// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import type {
  EvidenceReportPreview,
  EvidenceReportScope,
  EvidenceReportReviewInput,
  EvidenceReportAssembleInput,
  EvidenceReportSourceKind,
  EvidenceReportSourceRef,
} from '@vn-grc/shared';

import type {
  // Sprint 63.33.0 — an early-implementation record with its notice, dated on read
  PhasedObligationEarlyImplementationRow,
  // Sprint 63.33.0 (#594) — a standards pack installed on its own
  StandalonePackInstallManifest,
  StandalonePackUninstallResult,
  PdplAuthorityResponseClassification,
  // fix-sprint #431 — read-only cross-border state; opening is a person's POST
  CrossBorderCaseStatus,
  CrossBorderDossierOpenResult,
  BilingualText,
  // Sprint 62.13.0 — penalty exposure + enforcement cases
  PenaltyExposureInputs,
  FinePartyBasis,
  DeclaredExposureMetric,
  ExposureAreaMark,
  AreaExposureResult,
  AreaReadinessResult,
  AreaReadinessSummary,
  AuthorityDirection,
  DiscoveredDirectionKind,
  DiscoveredAuthorityEventKind,
  DeclaredConditionSet,
  ProductClassificationCatalogue,
  ProductClassificationRecord,
  ConformityCertificateRecord,
  LegalHold,
  LegalHoldListItem,
  HoldScopeCandidate,
  HoldScopeCandidateSource,
  RegulatedAccount,
  RegulatedAccountEvent,
  CredentialExpiryObligation,
  AccountLifecycleWindow,
  // Sprint 62.19.0 — sector licences and the marketing-claim gate
  DiscoveredLicenceKind,
  SectorLicence,
  LicenceObligation,
  BoundaryStatement,
  BoundaryGuidanceTag,
  MarketingClaimApproval,
  // Sprint 62.19.0 — investor protection
  InstalledFineArea,
  InvestorVerificationEvidence,
  AccountDataDisclosure,
  AccountDataDisclosureDecision,
  RegulatoryDisclosure,
  RegulatoryDisclosureKind,
  RegulatoryDisclosureStatus,
  // Sprint 62.18.0 — issued records (the student-record register)
  DiscoveredIssuedRecordKind,
  IssuedRecordGuidanceTag,
  IssuedRecordScopeDecision,
  IssuedRecord,
  IssuedRecordVersion,
  IssuedRecordVersionKind,
  IssuedRecordConsent,
  IssuedRecordSharingEvent,
  IssuedRecordSharingBasisKind,
  IssuedRecordEvidenceEntry,
  IssuedRecordEvidenceKind,
  IssuedRecordExportKind,
  // Sprint 62.17.0 — publishing (publications, the release gate, the appointed
  // -representative register and the AI-assisted publication assessment)
  DiscoveredPublicationGateKind,
  DiscoveredPublicationCategory,
  PublicationGuidanceTag,
  PublicationTiming,
  PublicationDepositTiming,
  Publication,
  PublicationStatus,
  PublicationRightsEvidence,
  PublicationDepositSubmission,
  PublicationReleaseAttempt,
  PublicationPrerequisiteRead,
  PublicationPrerequisiteRecord,
  RecordPublicationPrerequisitesInput,
  LegalRepresentativeEntry,
  AiSystemRegisterState,
  PublicationAssessment,
  PublicationAssessmentStatus,
  UnifiedExportFormat,
  // Sprint 65.1.0 — the compliance-operations work programme (AC7/AC8).
  ComplianceCoverageReport,
  ComplianceObligationInstance,
  DpoOversightRole,
  ObligationLineageClass,
  ObligationState,
  RenderableExportFormat,
  EnforcementCase,
  EnforcementCaseIntake,
  EnforcementCaseStage,
  EnforcementLinkedArea,
  EnforcementDecision,
  EnforcementLimitationClock,
  RightsRequestCase,
  CaseListItem,
  CaseNote,
  PaginatedResponse,
  IntakeResponse,
  LegalMatterRightOption,
  CaseCreatedResponse,
  PublicIntakePayload,
  AddNotePayload,
  UpdateCasePayload,
  AuditTrailResponse,
  EvidenceChecklistItem,
  EvidenceReference,
  ExportRecord,
  GrcSystem,
  DataMappingIndicatorReading,
  DataCategory,
  ProcessingPurpose,
  RetentionPolicy,
  DataFlow,
  ProcessingActivity,
  RopaRecord,
  EvidenceItem,
  DataMappingAuditEvent,
  DataMappingExport,
  AttestationCycle,
  AttestationDepartmentTask,
  GovernanceAssignment,
  CrossBorderRiskAssessment,
  // Sprint 41.5.0 G1 — Cross-Border Filing lifecycle
  CrossBorderFiling,
  CrossBorderFilingCreateInput,
  CrossBorderFilingStatusTransition,
  CrossBorderFilingStatus,
  CrossBorderFilingFormFence,
  GrcVendor,
  DiscoveredReachQuestion,
  VendorSubProcessor,
  VendorTransferMechanism,
  DriftEvent,
  ComplianceScore,
  DeletionPlaybook,
  DeletionExecution,
  ApprovalRecord,
  GovernanceAuditEvent,
  VendorOnboardingWorkflow,
  VendorDpaReview,
  VendorPeriodicReview,
  VendorOffboardingTask,
  ConsentPolicy,
  ConsentPolicyVersion,
  ConsentCatalogPurpose,
  ConsentCatalogLegalBase,
  ConsentWorkflow,
  ConsentCmpSnapshot,
  ChangeAwarenessEntry,
  ConsentAuditEvent,
  DpiaRecord,
  DpiaAutocompleteDraft,
  DpiaAutocompleteFieldKey,
  FormAutocompleteDraft,
  FormAutocompleteRequest,
  FormAutocompleteResolveResult,
  TiaAutocompleteDraft,
  TiaAutocompleteAcceptResult,
  TiaAutocompleteFieldKey,
  DpiaRisk,
  DpiaMitigation,
  TransferAssessment,
  CrossBorderSafeguard,
  AssessmentDashboard,
  AssessmentTriggerConfig,
  GapReport,
  AssessmentAuditEvent,
  TaxonomyDefinition,
  TaxonomyLevel,
  TaxonomyVersion,
  LabeledObject,
  LifecycleRecord,
  LifecycleEvent,
  ClassificationGap,
  ClassificationAuditEvent,
  MonitoringProgram,
  ProgramPeriod,
  TaskTemplate,
  TaskInstance,
  KpiSnapshot,
  RicCase,
  MonitoringAuditEvent,
  MonitoringChange,
  // Incidents (Sprint 1.8.0 / 21.1.0)
  Incident,
  IncidentTimelineEvent,
  IncidentDecision,
  IncidentTask,
  IncidentAuditEvent,
  BreachNotification,
  BreachNotificationForsetiDraft,
  BreachNotificationDraftResolveRequest,
  BreachNotificationDraftResolveResult,
  FilingChannelRefusal,
  IncidentSupplementRequest,
  RemediationAction,
  IncidentAuthorityRequest,
  DisclosureRegisterEntry,
  // Vendor Risk (Sprint 2.1.0 / 21.0.0)
  VendorRiskScore,
  QuestionnaireInstance,
  VendorRiskSignal,
  VendorReassessmentTask,
  VendorRiskAcceptance,
  ReviewRouting,
  // Program Governance (Sprint 2.5.0)
  MaturityModel,
  MaturityAssessment,
  TrendMetric,
  ReportingPeriod,
  ExecutiveSummary,
  AuditReadiness,
  EvidenceGap,
  // Sprint 65.8.0 — the audit engagement register and the findings raised inside it
  AuditEngagement,
  AuditEngagementState,
  AuditFinding,
  AuditFindingState,
  ProgramGovernanceAuditEvent,
  ObligationMapping,
  RaciAssignment,
  GovernanceDocument,
  DocumentAcknowledgment,
  RemediationPlan,
  StoredRemediationPlan,
  AssessmentCriteria,
  HandbookAssessment,
  HandbookAssessmentDefinition,
  HandbookAssessmentAnswerState,
  HandbookAssessmentEvidenceState,
  HandbookAssessmentReviewStatus,
  HandbookEvidenceCandidatesResponse,
  // Training (Sprint 65.6.0 — DT-01)
  TrainingAssignment,
  TrainingAssignmentSummary,
  TrainingAssignmentView,
  TrainingCourse,
  TrainingEvidenceKind,
  // Privacy office (Sprint 65.6.0 — DPO-01 and PUB-01)
  DpoAppointment,
  DpoAppointmentStatus,
  PrivacyNotice,
  PrivacyNoticeAudienceKind,
  PrivacyNoticeDeliveryChannel,
  PublicationTarget,
  // Deployment privacy (Sprint 62.58.0 Slice N)
  NoticeCoveragePage,
  AccessStatementPage,
  AccessStatementView,
  RecordAccessStatementInput,
  // Users / Auth (Sprint 3.0.0+)
  UserResponse,
  UserDirectoryResponse,
  UserDirectoryByIdsResponse,
  Role,
  RoleAssignment,
  Session,
  // API Keys / Service Accounts (Sprint 3.2.0)
  ApiKeyResponse,
  ApiKeyCreateResponse,
  ServiceAccountResponse,
  ServiceAccountCreateResponse,
  // MFA (Sprint 3.1.0)
  MfaEnrollmentResponse,
  MfaConfirmationResponse,
  MfaStatus,
  // MFA enforcement (Sprint 63.7.0)
  MfaEnforcementLevel,
  MfaAdminView,
  // SSO (Sprint 3.1.0)
  SsoConfigResponse,
  SsoEnforcementSettings,
  SsoPublicInfo,
  SsoProviderType,
  SsoRevokeSessionsResult,
  LogoutResponse,
  SsoVerificationReport,
  // Delegation (Sprint 3.2.0)
  DelegationResponse,
  // Break-Glass (Sprint 3.2.0)
  BreakGlassResponse,
  // Org Settings (Sprint 3.2.0)
  IpAllowlistSettings,
  SessionSettings,
  EvidencePackDispatchOrgSettings,
  // Cross-framework evidence packs (Sprint 62.2.5 consumes 62.2.1-62.2.4)
  EvidencePackPreview,
  FrameworkEvidencePack,
  FrameworkEvidencePackVersion,
  EvidencePackAssembleResult,
  EvidencePackExportResult,
  FrameworkEvidencePackApproval,
  FrameworkEvidencePackSubmission,
  // Vietnam AI governance operations (Sprint 62.3.0)
  AiGovernanceOptionsResponse,
  AiSystemProfile,
  AiEvidenceIndexEntry,
  AiBankingTriggerReading,
  AiSystemDatedObligation,
  InstalledScopeQualification,
  ScopeQualificationSurface,
  AiInventoryExtras,
  AiLegacyImportBatch,
  AiClassificationRun,
  AiObligationRecord,
  CreateAiSystemProfileRequest,
  UpdateAiSystemProfileRequest,
  ReviewAiClassificationRunRequest,
  UpdateAiObligationRecordRequest,
  UpdateAiGovernanceEvidenceRequest,
  ReviewAiGovernanceEvidenceRequest,
  AiGovernanceEvidenceField,
  AiGovernanceEvidenceFormCandidate,
  Sector,
  SectorOverlayInstallMode,
  FrameworkRelationship,
  // Access Review (Sprint 3.3.0)
  AccessReviewCampaign,
  RoleUsageStat,
  OverPrivilegedUser,
  TrimRecommendation,
  // Webhooks (Sprint 3.6.0)
  WebhookConfig,
  WebhookDelivery,
  // Domain (Sprint 3.6.0)
  DomainMapping,
  // Export Schedules (Sprint 3.6.0)
  ExportSchedule,
  ExportRun,
  // Sprint 3.3.0: Access Review analytics & SoD additional types
  AttestationScheduleResponse,
  SodRuleResponse,
  SodExceptionResponse,
  SodViolation,
  SodCheckResult,
  AccessReviewItemResponse,
  // Sprint 3.6.0: Email Log
  EmailLogEntry,
  // Sprint 4.1.0: Privacy Scanner
  PrivacyScannerConfig,
  PrivacyScanProgress,
  PrivacyScanResult,
  ScanComparisonResult,
  // Sprint 4.2.0 / Sprint 20.6.0: Consent CMP integration & governance
  CmpWebhookConfig,
  BulkSyncJob,
  CmpPurposeMapping,
  WebConsentRecord,
  WebConsentAnalytics,
  WithdrawalPropagationTask,
  MismatchReconciliationTask,
  CmpSyncEvent,
  // Sprint 20.4.0: DSR Fulfillment
  DepartmentFulfillmentTask,
  DsrResponseTemplate,
  DsrResponseRecord,
  ExtensionRequest,
  // Sprint 43.2.0 batch 1 — Org-level retention policy
  OrgRetentionPolicy,
  // Sprint 4.4.0: Module Config
  ModuleConfig,
  // Sprint 7.6.0 / Sprint 15.7.0: Forms & Dossier
  FormTemplate,
  FormInstance,
  FormInstanceDetail,
  // Sprint 62.58.0 (AC-A2) — the draft facts of a template's declaring frameworks
  TemplateFrameworkNotice,
  TemplateNotOwedFlag,
  OfficialLabel,
  ProvisionalArtefactReason,
  FormOutput,
  FormAuditEvent,
  FormCatalogEntryWithStatus,
  FormInstallStatusResponse,
  Dossier,
  PrepareNoticeDossierResult,
  UpdateDossierPayload,
  // Sprint 37.1.0 + fix-sprint-20260411-B F13: orchestration contracts
  CreateWithFormPayload,
  UpdateFormValuesPayload,
  UpdateFormValuesResponse,
  SubmitFormResponse,
  FinalizeFormResponse,
  // Sprint 15.7.0: Compliance Rules
  ComplianceRuleDocument,
  // Sprint 11.5.0+: Org Groups
  OrgGroup,
  OrgGroupMembership,
  GroupSharedVendor,
  GroupSharedFramework,
  OrgSettingsInheritance,
  GroupUserAssignment,
  UserOrgMembership,
  OrgSwitchResponse,
  GroupAuditEntry,
  GroupAuditSummary,
  GroupDashboardData,
  GroupDashboardWidget,
  GroupReport,
  // Sprint 17.4.0+: Task Management
  TaskRecord,
  TaskListItem,
  TaskComment,
  // Sprint 17.5.0: Workflows
  WorkflowDefinition,
  WorkflowInstance,
  WorkflowTemplate,
  // Sprint 17.6.0+: Dashboard widgets
  UserDashboardPreferences,
  UpdateDashboardPreferencesPayload,
  // Sprint 18.0.0: License & Package Management
  License,
  PackageInfo,
  PackageChangeResult,
  // Sprint 20.3.0: Dossier Filings
  DossierFiling,
  DossierFirstFilingGuidance,
  // Sprint 62.57.0 (Slice R): authority communications on a filing
  DossierAuthorityCommunication,
  // Sprint 62.58.0 (Slices F and C): the regulatory-update comparison and the briefing record
  RegulatoryUpdateComparisonView,
  ComparisonReview,
  CreateComparisonReviewPayload,
  FrameworkBriefing,
  FrameworkBriefingPage,
  RecordFrameworkBriefingInput,
  FrameworkSummaryExportFormat,
  ExportRedactionSummary,
  RecordDossierAuthorityCommunicationRequest,
  DossierHandlingAuthorityStatement,
  DossierHandlingStatementBasis,
  DossierReceiptEvent,
  // Sprint 62.57.0 (Slice D, D35) — a body a person chose for an update.
  UpdateAddresseeBodyChoice,
  // Sprint 40.0.0: Bulk Import
  ImportJob,
  // Sprint 41.0.0: Generic attachments
  AttachmentRef,
  GenericAttachment,
  // Sprint 41.2.0 E7 — Sub-Processor Register types
  SubProcessorRegisterRecord,
  SubProcessorRegisterChange,
  // Sprint 41.4.0 — Platform → Files Configuration & Browser
  AttachmentOwnerType,
  PlatformFileSettings,
  PlatformFileGlobalSettings,
  // Sprint 63.1.0 — the two retention registers and their sweep reports (#148).
  AbandonedAttachmentPosition,
  AbandonedAttachmentSweepReport,
  AccessLogRetentionPosition,
  AccessLogRetentionSweepReport,
  PlatformFileOwnerTypeOverride,
  PlatformFileEffectiveSettings,
  PlatformFileAuditEvent,
  PlatformFileAuditEventType,
  FileBrowserFilter,
  // Sprint 43.2.0 batch 4 (E5) — Breach-trigger records for the red
  // banner on TicketDetailPage.
  DataBreach,
  GovernanceModel,
  DataSubjectIdentifierKeyDeclaration,
  ReleasedAttachmentTombstone,
  AttachmentRetentionRelease,
  ReleaseAttachmentAtRetentionRequest,
  AttachmentReleasePreview,
  DataSubjectProfile,
  DataSubjectTimelineEntry,
  // Sprint 47.4.5 — Immutable Audit Trails frontend types
  ImmutableAuditTrailsOrgSettings,
  ProofLink,
  ProofReceipt,
  ProofUsageRecord,
  ProofAlert,
  ProofCreditPackage,
  IatCreditPackageCode,
  AdvisorSummary,
  // Sprint 47.5.8 — Advisor audit lineage strict typing.
  LineageQuery,
  // Sprint 47.9.7 — MCP connector
  McpApiKeyIssueRequest,
  McpApiKeyIssueResponse,
  McpApiKeyListItem,
  McpApiKeyRotateResponse,
  // Sprint 58.x.x — PDPL Evidence Pack (Phase 25)
  PdplEvidencePackAssembleResult,
  PdplEvidencePackAssemblyDoc,
  PdplEvidencePackEligibilityContext,
  PdplEmailDraftComposeResult,
  PdplEmailDraftDoc,
  PdplEmailSentProofDoc,
  PdplEmailSentProofUploadResult,
  PdplEmailSendRecord,
  PdplEmailProviderConfigRedacted,
  PdplEmailProviderCreateInput,
  PdplEmailProviderStatus,
  // Sprint 62.20.0 — linked schemes, screening inputs, placeholders
  AggregationPromptCatalogueResponse,
  ClassificationGuidanceTag,
  CreateRegulatoryPlaceholderRequest,
  PlaceholderKindCatalogueResponse,
  RecordAggregationReviewRequest,
  // Sprint 62.26.0 (AC7–AC9) — transitional windows between schemes.
  RecordTransitionalRetirementRequest,
  SubjectTransitionalWindowsResponse,
  RecordSchemeCrossMappingRequest,
  RegulatoryPlaceholderRecord,
  SetSubjectClassificationRequest,
  TransferScreeningInputsResponse,
  UpdateRegulatoryPlaceholderRequest,
  // Regulatory classification (Sprint 62.4.0)
  ClassificationAssessment,
  ClassificationSchemeCatalogueResponse,
  ClassificationReassessmentRow,
  ClassificationSubjectProfile,
  RegulatedOfferingObligationRequest,
  CreateClassificationAssessmentRequest,
  CreateClassificationSubjectProfileRequest,
  ProtectiveMeasureRecord,
  ProtectiveMeasureTest,
  RecordProtectiveMeasureTestRequest,
  RegulatedOfferingRegistration,
  ReviewClassificationAssessmentRequest,
  UpdateClassificationSubjectProfileRequest,
  UpdateProtectiveMeasureRequest,
  // Trust evidence — identity, trust services, electronic transactions
  // (Sprint 62.5.0). Mounted at `/api/v1/trust-evidence`.
  TrustRegime,
  TrustRegimeSource,
  IdentityProfile,
  IdentityAttribute,
  IdentityEvent,
  TrustServiceProvider,
  TrustCredential,
  TrustEvent,
  TrustEventKind,
  ElectronicSubmission,
  ElectronicAgreement,
  CreateIdentityProfileRequest,
  UpdateIdentityProfileRequest,
  CreateIdentityAttributeRequest,
  UpdateIdentityAttributeRequest,
  RecordIdentityEventRequest,
  CreateTrustProviderRequest,
  UpdateTrustProviderRequest,
  CreateTrustCredentialRequest,
  TransitionTrustCredentialRequest,
  RecordTrustVerificationRequest,
  CreateElectronicSubmissionRequest,
  UpdateElectronicSubmissionRequest,
  SubmitElectronicSubmissionRequest,
  RecordSubmissionReceiptRequest,
  RecordSubmissionAmendmentRequest,
  CreateElectronicAgreementRequest,
  UpdateElectronicAgreementRequest,
  RecordAgreementSyncRequest,
  RecordAgreementExceptionRequest,
  // Regulatory artefacts — what an artefact is, and who may see it
  // (Sprint 62.6.0). Mounted at `/api/v1/regulatory-artifacts`.
  RegulatoryArtifact,
  // Sprint 64.29.0 — the verification queue's rows carry a disposition, and its
  // envelope carries the install drift that explains the count.
  DispositionedArtifact,
  PackCurrencyReport,
  RegulatoryArtifactKind,
  ArtifactAccessTier,
  ArtifactAccessDecision,
  ArtifactAccessEvent,
  ArtifactAccessEventKind,
  ArtifactAccessGrant,
  ArtifactGrantStatus,
  ArtifactVerificationStatus,
  GrantArtifactAccessRequest,
  RequestArtifactAccessRequest,
  RestrictedArtifactsOrgSettings,
  RevokeArtifactAccessRequest,
  UpdateArtifactClassificationRequest,
  // Public data, payments and platform evidence (Sprint 62.7.0). Mounted at
  // `/api/v1/public-data`, `/api/v1/payments-evidence` and
  // `/api/v1/platform-evidence`.
  EvidenceRegime,
  EvidenceRegimeDiagnostic,
  ProtectedReferenceView,
  PublicDataset,
  PublicDatasetStatus,
  PublicDataDictionaryField,
  PublicDatasetDictionaryVersion,
  PublicDataSharingArrangement,
  SharingArrangementStatus,
  PublicDataShareReceipt,
  ShareReceiptStatus,
  OpenDataReleaseReadiness,
  ReleaseReadinessOutcome,
  OpenDataRelease,
  OpenDataReleaseStatus,
  PublicDataQualityIssue,
  QualityIssueStatus,
  PublicDataCorrection,
  CorrectionStatus,
  PaymentService,
  PaymentServiceStatus,
  PaymentObligationRequest,
  PaymentTransactionLogMapping,
  EkycVerification,
  EkycOutcome,
  RetentionState,
  PaymentDataSecurityRecord,
  CardEvidenceStatus,
  AmlDueDiligenceLink,
  CddLinkStatus,
  RetentionReconciliation,
  RetentionReconciliationStatus,
  PaymentEvidenceException,
  PaymentExceptionStatus,
  PlatformSeller,
  PlatformTransactionRecord,
  PlatformDocumentEvidence,
  ReconciliationRun,
  ReconciliationException,
  ReconciliationExceptionStatus,
  AuthorityCorrespondence,
  // ── Sprint 62.8.0 — manufacturing operations ──────────────────
  ManufacturingProduct,
  ManufacturingFoodSurfacesView,
  ManufacturingRecallTriggerSourceView,
  ManufacturingExportKind,
  ProductCommercialRole,
  ProductLifecycleState,
  ProductBatch,
  BatchGranularity,
  TraceIdentifier,
  StoredTraceEvent,
  ConformityRecord,
  ConformityState,
  CapaRecord,
  CapaState,
  RecallScope,
  EprScope,
  EprMaterialVolume,
  EprFulfilment,
  EprSubmission,
  EprReconciliationException,
  ChemicalSubstance,
  ProductComposition,
  ProductCompositionView,
  OtAssetProfile,
  OtAssetProfileView,
  OtIntegrationKind,
  RemoteAccessGrant,
  RemoteAccessGrantView,
  RemoteAccessGrantState,
  // ── Sprint 62.9.0 — logistics, customs, parcel and delivery ───
  Shipment,
  ShipmentDirection,
  ShipmentIdentifierScheme,
  ShipmentPartyRole,
  ShipmentStatus,
  CargoItem,
  CustomsRecord,
  CustomsRecordStatus,
  ClearanceEvent,
  ClearanceOutcome,
  ShipmentAuthorityCorrespondence,
  Parcel,
  ParcelStatus,
  ParcelEvent,
  ParcelEventKind,
  DeliveryProof,
  ParcelClaim,
  ClaimStatus,
  PostalAuthorisation,
  AuthorisationStatus,
  LogisticsExportKind,
  // Sprint 62.25.0 AC10/AC11 — the customs channel-discovery read.
  InstalledFilingChannelOption,
  FilingChannelDiscoveryDiagnostics,
  // Sprint 64.124.0 — the pending-write lifecycle, including `failed`, and the
  // list row the admin queue renders.
  McpPendingWriteStatus,
  McpPendingWriteListItem,
  // Sprint 63.23.0 — the MCP OAuth authorization server's view types (C.1).
  McpConnectionDetails,
  OAuthClientView,
  McpOAuthClientCreateRequest,
  McpOAuthClientCreateResponse,
  GrantView,
  McpOAuthConsentDetails,
  McpOAuthConsentDecision,
} from '@vn-grc/shared';
import type {
  SupervisoryReportingOverview,
  SupervisoryIndicatorView,
  SupervisoryLineageState,
  SupervisorySubmissionCheckView,
  SupervisoryInterruptionRow,
  SupervisoryDataCorrectionView,
  SupervisoryAccessApproval,
  SupervisoryDataDisclosed,
  SupervisoryFollowUp,
  SupervisoryInterruptionNoticeView,
  SupervisoryEventNoticeView,
  SupervisoryExportLocale,
  // Sprint 62.45.0 Slice H
  SupervisoryInspectionView,
  SupervisoryCalendar,
  SupervisoryCalendarRow,
  SupervisoryVocabularyChanges,
  SupervisoryUnrecordedInterruptionList,
  SupervisoryInterruptionDuty,
  SupervisoryRecommendationRow,
  SupervisoryRecommendationView,
  SupervisoryRecommendationKind,
  IdentityExchangeView,
  IdentityExchangeVocabulary,
  IdentityExchangeStatus,
  IdentityExchangeAttribute,
} from '@vn-grc/shared';
// Sprint 62.46.0 — the AML programme register. Every body type is the shared
// contract the route's `.strict()` schema asserts, re-used rather than restated.
import type {
  AmlProgrammeOverview,
  AmlProgrammeVocabulary,
  AmlScopingHistory,
  AmlScopingRecord,
  AmlScopingBody,
  AmlTwoPersonPolicyBody,
  AmlTwoPersonPolicyView,
  AmlRetentionScopeView,
  AmlRetentionScopeRow,
  AmlRetentionScopeCreateBody,
  AmlRetentionScopeUpdateBody,
  AmlIndicatorCoverageView,
  AmlIndicatorRow,
  AmlIndicatorRowBody,
  AmlEntityIndicatorCreateBody,
  AmlEntityIndicatorUpdateBody,
  AmlIndicatorConfigVersion,
  AmlIndicatorConfigVersionSummary,
  AmlConfigVersionApproveBody,
  AmlCaseList,
  AmlCaseStatus,
  AmlCase,
  AmlCaseDetail,
  AmlCaseCreateBody,
  AmlCaseUpdateBody,
  AmlCaseDeterminationBody,
  AmlCaseApprovalBody,
  AmlFilingRecordBody,
  AmlCaseCloseBody,
  AmlCddRefreshBody,
  AmlBeneficialOwnerList,
  AmlBeneficialOwnerRecordView,
  AmlBeneficialOwnerCreateBody,
  AmlBeneficialOwnerUpdateBody,
  AmlProductLaunchList,
  AmlProductLaunch,
  AmlProductLaunchView,
  AmlLaunchCreateBody,
  AmlLaunchScopeDeterminationBody,
  AmlLaunchAssessment,
  AmlLaunchAssessmentCreateBody,
  AmlLaunchAssessmentUpdateBody,
  AmlLaunchAssessmentDecisionBody,
  AmlLaunchApproveBody,
  AmlGuidanceTrackingView,
  AmlInspectionPackBody,
  AmlInspectionPackCounts,
  AmlIndicatorConfigVersionDetail,
  AmlIndicatorReviewTask,
  AmlIndicatorReviewTaskCompleteBody,
  AmlIndicatorReviewTaskCreateBody,
  AmlIndicatorReviewTaskRaise,
  AmlRetentionScopeApprovalBody,
  AmlCaseRelatedActionBody,
  AmlCaseRetentionStartBody,
  AmlLaunchPostLaunchReviewBody,
} from '@vn-grc/shared';
// Sprint 62.53.0 — organisation-profile statements and the applicability review.
import type {
  ApplicabilityReview,
  OrgProfileStatementInput,
  OrgProfileStatementView,
  RecordedOrgProfileAttribute,
} from '@vn-grc/shared';
import {
  AI_COMPONENT_KINDS,
  BACKGROUND_REQUEST_HEADER,
  AI_SUPPLIER_CLAUSE_KEYS,
  AI_WITHHELD_ITEM_CATEGORIES,
  isModuleTipAbsenceReason,
  type ModuleTipAbsenceReason,
} from '@vn-grc/shared';
// Sprint 62.48.0 wave 5 — the AI governance detail sections and registers.
import type {
  AiAttackCategory,
  AiAutonomyLevel,
  AiBankingFactCustomerClass,
  AiComplaintRecord,
  AiComplaintWriteSummary,
  AiComponentIntegrityRecord,
  AiComponentAdvisoryRecord,
  AiComponentRow,
  AiComponentSnapshot,
  AiDataProvenanceRecord,
  AiDecisionRecord,
  AiChannelRecord,
  AiDisclosureRecord,
  AiDutyHolderRow,
  AiDutyKind,
  AiEscalationRecord,
  AiImpactAssessmentRecord,
  AiIncidentRecord,
  AiInterventionKind,
  AiInterventionReading,
  AiInterventionRecord,
  AiInventoryReviewRecord,
  AiStaffBriefingRecord,
  AiInventoryReviewRow,
  AiMonitoringComparison,
  AiMonitoringEventKind,
  AiMonitoringEventReading,
  AiMonitoringEventRecord,
  AiMonitoringSource,
  AiPaymentAuthorityEvent,
  AiPaymentAuthorityRecord,
  AiPaymentAuthorityView,
  AiRegisterFigures,
  AiReleaseApprovalRecord,
  AiReadState,
  AiRevalidationDecisionInput,
  AiRiskFrameworkRecord,
  AiRollbackEvent,
  AiSecurityTestRecord,
  AiSecurityTestState,
  AiPeriodicTestReading,
  AiSeparationPolicy,
  AiSupplierArrangementRecord,
  AiSupplierArrangementReadings,
  AiSupplyChainReviewRecord,
  AiSystemVersion,
  AiThreeLineCode,
  AiThreeLineReadingEntry,
  AiThreeLinesMap,
  AiThreeLinesReading,
  AiThreeLinesSharedHolder,
  AiValidationRecord,
  AiReleaseGateReading,
  AiValidationTestArea,
  AiVersionChangeRecord,
  AiComplaintThresholdSetting,
  AiComplaintPatternResponse,
  AiComplaintExplanation,
  AiComplaintReasonReading,
  AiComplaintCount,
} from '@vn-grc/shared';
import type { TranslatableField, PdplRightType } from '@vn-grc/shared';
// Sprint 63.37.0 (E0, E3) — organisation identity and form prefill.
import type { OrgIdentity, UpdateOrgIdentityPayload, FormPrefillResult } from '@vn-grc/shared';
import type { UIAssistStatusResponse } from '@vn-grc/shared';
// Sprint 62.50.0 — supervision-information registers (work file C.1).
import type {
  PcfBranchRequirement,
  PcfChangeProposalRecord,
  PcfConnectionReadinessRecord,
  PcfCorrectionRecord,
  PcfDepositorEnquiry,
  PcfIncidentRecord,
  PcfInformationRequestRecord,
  PcfPublicityRecord,
  PcfPublicityRequirementView,
  PcfRegisterContext,
  PcfReportingCalendarEntry,
  PcfRequirementKind,
  PcfSharedInformationEntry,
  PcfStatedDeadline,
  PcfSupervisionExportLocale,
  PcfSupervisionExportSurfaceKey,
  PcfSupervisionSurfaceKey,
  PcfSystemOperationRecord,
  PcfToldDate,
  OrganisationRegulatoryDeadline,
  PcfWrittenRequestRecord,
} from '@vn-grc/shared';
// Sprint 62.51.0 — data-exploitation registers (work file C.1, S2).
import type {
  DataExploitationActivity,
  DataExploitationCertificateKind,
  DataExploitationDisposition,
  DataExploitationEligibleCase,
  DataExploitationExportLocale,
  DataExploitationExportSurfaceKey,
  DataExploitationForeignAssessment,
  DataExploitationGrantDispositionRow,
  DataExploitationIncubatorRecord,
  DataExploitationNotice,
  DataExploitationNoticeSent,
  DataExploitationOverview,
  DataExploitationRegisterContext,
  DataExploitationRequest,
  DataExploitationRequestDetailView,
  DataExploitationRequestState,
  DataExploitationSurfaceKey,
  DataExploitationTestingRecord,
  DataExploitationTestingCorrectableSubrecord,
} from '@vn-grc/shared';
// Sprint 62.52.0 — cybersecurity assurance index registers (work file C.1, S2).
import type {
  CybersecurityAssuranceIndexCatalogueSnapshot,
  CybersecurityAssuranceIndexClassificationSubject,
  CybersecurityAssuranceIndexContext,
  CybersecurityAssuranceIndexEntry,
  CybersecurityAssuranceIndexExportLocale,
  CybersecurityAssuranceIndexFigureComponent,
  CybersecurityAssuranceIndexItemRow,
  CybersecurityAssuranceIndexPeriod,
  CybersecurityAssuranceIndexProgress,
  CybersecurityAssuranceIndexRecordingState,
  CybersecurityAssuranceIndexStatistic,
  CybersecurityAssuranceIndexStatisticSummaryRow,
  CybersecurityAssuranceIndexSupplierRequest,
  CybersecurityAssuranceIndexSupplierEvidenceCitation,
  CybersecurityAssuranceIndexPeriodAccessEnded,
} from '@vn-grc/shared';
// Sprint 62.57.0 Slice E (stream F) — the processing-service certificate register and the vendor record.
import type {
  CertificateDecisionIssuedFacts,
  LegalReservation,
  CertificateProceduresByWindow,
  PersonalDataProcessingService,
  ServiceCertificateApplicationDetailView,
  ServiceCertificateApplicationListRow,
  ServiceCertificateAvailability,
  ServiceCertificateCommunicationKind,
  ServiceCertificateCommunicationView,
  ServiceCertificateDecisionOutcome,
  ServiceCertificateDetailView,
  ServiceCertificateDocument,
  ServiceCertificateDocumentKind,
  ServiceCertificateFormsHeld,
  ServiceCertificateHistoryEntry,
  ServiceCertificateIssuingAuthorityDeclaration,
  ServiceCertificateListRow,
  ServiceCertificateOutOfScopeItem,
  ServiceCertificatePartState,
  ServiceCertificateProcedureKind,
  ServiceCertificateReceiptEvent,
  ServiceCertificateRevocationDeclaration,
  ServiceCertificateValidityDeclaration,
  ServiceListItem,
  VendorCertificateEntry,
  VendorCertificateStatedPosition,
  VendorCertificateStatementSource,
  VendorProcessingServiceStatement,
  VendorProcessingServiceStatementState,
  VendorServiceCertificateView,
} from '@vn-grc/shared';
// Sprint 62.49.0 — food traceability registers (work file C.1, completed by S2b).
import type {
  FoodCertificate,
  FoodCertificateStatus,
  FoodComplaint,
  FoodCarriedRef,
  FoodCorrectionRequest,
  FoodCorrectionTargetKind,
  FoodCorrectiveNoticeDetermination,
  FoodReportCarrierKind,
  FoodDecision,
  FoodDecisionEventKind,
  FoodEstablishmentView,
  FoodGrant,
  FoodGrantReview,
  FoodGrantReviewDecision,
  FoodInspection,
  FoodLotResult,
  FoodNotice,
  FoodQuantityFigureKind,
  FoodRecordsRequestRow,
  FoodReportCopyResult,
  FoodRetentionStatement,
  FoodRoute,
  FoodRouteDirection,
  FoodSupplierCorrection,
  FoodTestingBodyDesignation,
  FoodTraceabilityContext,
  FoodTraceabilityExportLocale,
  FoodTraceabilityRegisterKey,
  FoodTraceabilityReportCopyFormat,
  FoodWarning,
  FoodWarningSource,
  FoodBatchCorrectionProposal,
  FoodProductCorrectionProposal,
  TraceEventCorrectionFields,
  ProductFoodFacts,
  BatchFoodInput,
  BatchFoodRecipient,
  MasterDataVersion,
  TraceChainUnresolvedRef,
  CapaFoodOwnReportSent,
} from '@vn-grc/shared';

/** `PUT /food-traceability/establishment` — no `state` key exists on the reference (AC2). */
export interface PutFoodEstablishmentBody {
  identity?: {
    name?: string | null;
    enterprise_code?: string | null;
    head_office_address?: string | null;
    site_addresses?: string[];
    legal_representative?: string | null;
    phone?: string | null;
    email?: string | null;
  };
  registration_document_attachment_ids?: string[];
  registration_document_kind?: string | null;
  food_safety_condition_ref?: string | null;
  internal_control_attachment_ids?: string[];
  registration_reference?: { reference: string; filed_on?: string | null } | null;
  certificate_lapse_window_days?: number | null;
  evidence_attachment_ids?: string[];
  notes?: string | null;
  expected_version?: number;
}
export interface CreateFoodCertificateBody {
  issuer: string;
  kind?: string | null;
  equivalence?: { claimed_kind_text: string; reason: string } | null;
  number: string;
  scope: string;
  issued_on: string;
  valid_until?: string | null;
  status: FoodCertificateStatus;
  notes?: string | null;
}
export interface PatchFoodCertificateBody {
  status?: FoodCertificateStatus;
  status_effective_at?: string;
  scope?: string;
  evidence_attachment_ids?: string[];
  notes?: string | null;
  expected_version: number;
}
export interface RenewFoodCertificateBody {
  issuer: string;
  kind?: string | null;
  number: string;
  scope: string;
  issued_on: string;
  valid_until?: string | null;
  evidence_attachment_ids?: string[];
  expected_version?: number;
}
export interface CreateFoodLotResultBody {
  batch_id: string;
  sampled_on: string;
  obtained_by: { kind: 'platform_user'; user_id: string } | { kind: 'external'; name: string };
  result_source?: string | null;
  laboratory_name: string;
  scope: string;
  result_as_recorded: string;
  supersedes_result_id?: string | null;
  notes?: string | null;
}
export interface PatchFoodLotResultBody {
  laboratory_name?: string;
  scope?: string;
  result_as_recorded?: string;
  evidence_attachment_ids?: string[];
  notes?: string | null;
  expected_version: number;
}
export interface FoodTestingBodyStatementBody {
  designation: FoodTestingBodyDesignation;
  basis_text?: string | null;
  expected_version?: number;
}
export interface CreateFoodInspectionBody {
  inspected_on: string;
  inspecting_body: string;
  scope: string;
  finding: string;
  notes?: string | null;
}
export interface PatchFoodInspectionBody {
  inspecting_body?: string;
  scope?: string;
  finding?: string;
  evidence_attachment_ids?: string[];
  notes?: string | null;
  expected_version: number;
}
export interface FoodInspectionResponseBody {
  response_text: string;
  responded_on: string;
  evidence_attachment_ids?: string[];
  expected_version?: number;
}
export interface CreateFoodWarningBody {
  source: FoodWarningSource;
  received_at: string;
  source_checked: boolean;
  source_checked_note?: string | null;
  description: string;
  notes?: string | null;
}
export type PatchFoodWarningBody = Partial<CreateFoodWarningBody> & {
  evidence_attachment_ids?: string[];
  expected_version: number;
};
export interface CreateFoodNoticeBody {
  capa_id?: string | null;
  recipient_vendor_id?: string | null;
  recipient_name?: string | null;
  kind: string;
  method: string;
  subject: string;
  notes?: string | null;
  /** Sprint 62.49.2 (AC2) — the lots and trace events the notice carried. */
  carried_refs?: FoodCarriedRef[];
}
export interface PatchFoodNoticeBody {
  method?: string;
  subject?: string;
  evidence_attachment_ids?: string[];
  notes?: string | null;
  /** Sprint 62.49.2 (AC2) — replaces the list whole. */
  carried_refs?: FoodCarriedRef[];
  expected_version: number;
}
/** Sprint 62.49.2 (AC6) — `POST /food-traceability/corrections/:id/impact-determinations`. */
export interface FoodImpactDeterminationBody {
  report_kind: FoodReportCarrierKind;
  report_id: string;
  determination: FoodCorrectiveNoticeDetermination;
  reason: string;
  recipient_as_stated?: string | null;
  by_when_as_stated?: string | null;
  expected_version?: number;
}
export interface FoodNoticeSentBody {
  sent_at: string;
  proof_attachment_ids: string[];
  expected_version?: number;
}
export interface FoodQuantityReportBody {
  figure_kind: FoodQuantityFigureKind;
  quantity: number;
  unit: string;
  source: string;
  reported_on: string;
  expected_version?: number;
}
/** No numeric sanction key exists (AC16). */
export interface CreateFoodDecisionBody {
  issuing_authority: string;
  decision_number: string;
  decided_on: string;
  violation_as_stated: string;
  basis_as_stated: string;
  sanction_as_stated: string;
  execution_deadline_as_stated?: string | null;
  notes?: string | null;
}
export interface PatchFoodDecisionBody {
  evidence_attachment_ids?: string[];
  notes?: string | null;
  expected_version: number;
}
export interface FoodDecisionEventBody {
  kind: FoodDecisionEventKind;
  occurred_on: string;
  statement: string;
  evidence_attachment_ids?: string[];
  expected_version?: number;
}
export interface CreateFoodCorrectionRequestBody {
  target_kind: FoodCorrectionTargetKind;
  target_id: string;
  /** Gate E12.5 (P3-4) — a product target proposes its food facts. */
  proposed:
    | TraceEventCorrectionFields
    | FoodBatchCorrectionProposal
    | FoodProductCorrectionProposal;
  reason: string;
}
export interface CreateFoodSupplierCorrectionBody {
  vendor_id: string;
  requested_on: string;
  request_text: string;
  reply_on?: string | null;
  correction_as_supplied?: string | null;
  notes?: string | null;
}
export interface PatchFoodSupplierCorrectionBody {
  request_text?: string;
  reply_on?: string | null;
  correction_as_supplied?: string | null;
  evidence_attachment_ids?: string[];
  notes?: string | null;
  expected_version: number;
}
export interface CreateFoodRouteBody {
  systems: string[];
  direction: FoodRouteDirection;
  data_scope: string;
  operator_vendor_id?: string | null;
  credential_holder_user_id?: string | null;
  notes?: string | null;
}
export type PatchFoodRouteBody = Partial<CreateFoodRouteBody> & {
  evidence_attachment_ids?: string[];
  expected_version: number;
};
export interface FoodRouteReceiptBody {
  reference_as_returned: string;
  received_on: string;
  evidence_attachment_ids?: string[];
  expected_version?: number;
}
/** `POST /food-traceability/grants` — the food permissions lent, until when, and why (follow-up 2026-10-28). */
export interface CreateFoodGrantBody {
  user_id: string;
  permissions: string[];
  ends_at: string;
  reason: string;
}
export interface CreateFoodComplaintBody {
  received_at: string;
  channel: string;
  product_id?: string | null;
  product_as_named?: string | null;
  batch_id?: string | null;
  lot_not_identified_reason?: string | null;
  description: string;
  contact_minimal?: string | null;
  notes?: string | null;
}
export interface PatchFoodComplaintBody {
  channel?: string;
  description?: string;
  contact_minimal?: string | null;
  evidence_attachment_ids?: string[];
  notes?: string | null;
  expected_version: number;
}
/** `PUT /manufacturing/capa-recall/:recordId/trigger` (AC13). `own_report_sent: null` clears it. */
export interface PutCapaFoodTriggerBody {
  source: string;
  received_at: string;
  authority_request_id?: string | null;
  warning_id?: string | null;
  own_report_sent?: Pick<
    CapaFoodOwnReportSent,
    'sent_on' | 'recipient_as_entered' | 'proof_attachment_id' | 'reference_returned'
  > | null;
}
/** `GET /manufacturing/{products,batches}/:id/as-of` (AC19). */
export interface MasterDataAsOf<T> {
  as_of: string;
  target_kind: 'product' | 'batch';
  target_id: string;
  existed: boolean;
  record: T | null;
  version_number: number | null;
}
// Sprint 62.55.0 — customs tax-administration registers (work file C.1).
import type {
  CtaAnchorEventView,
  CtaChannelDiscoveryState,
  CtaChannelOption,
  CtaClockListRow,
  CtaCustomsRecordNotice,
  CtaDebtInstrumentView,
  CtaDeductionView,
  CtaDifferenceClosure,
  CtaDossierFigureClosure,
  CtaDossierFigureComparison,
  CtaDossierView,
  CtaDutyFreeList,
  CtaExaminationStep,
  CtaExaminationView,
  CtaExportKind,
  CtaFigureComparison,
  CtaFigureSource,
  CtaFormNumbering,
  CtaGoverningObligation,
  CtaGoverningRulesReading,
  CtaListAmendment,
  CtaListResponse,
  CtaListStopEventView,
  CtaOfficeExamination,
  CtaPayment,
  CtaProcedureOption,
  CtaProjectTransferView,
  CtaReadingLineage,
  CtaReceivedDocument,
  CtaRefundRecordView,
  CtaRegisterContext,
  CtaRequirementAsOfReading,
  CtaRuleDetermination,
  CtaStatedFigure,
  CtaReviewableRecordType,
  CtaReviewResult,
  CtaTimelineEntryDeclaration,
  CtaClauseRelationshipDeclaration,
  CtaTransitionStatement,
  CtaUsageNotification,
  CtaUsageScheduleEntry,
  CustomsTaxSurfaceKey,
  OrgFinancialYearSettingsView,
  UpdateFinancialYearSettingsPayload,
} from '@vn-grc/shared';
// Sprint 62.56.0 — biometric capture components, chain of custody, readiness reviews.
import type {
  BiometricCaptureComponentDetail,
  BiometricCaptureComponentSummary,
  BiometricCaptureContext,
  BiometricCaptureExportBody,
  BiometricCaptureLinkView,
  BiometricComponentLifecycleState,
  BiometricCustodyListQuery,
  BiometricCustodyTransferView,
  BiometricDeletionRecordView,
  BiometricLifecycleBody,
  BiometricLinkTargetType,
  BiometricReadinessReview,
  BiometricScopeDetermination,
  BiometricSupervisionActView,
  BiometricTargetLinksResponse,
  ConfirmBiometricReadinessReviewBody,
  CreateBiometricComponentBody,
  CreateBiometricDeletionRecordBody,
  CreateBiometricSupervisionActBody,
  CreateBiometricCustodyTransferBody,
  CreateBiometricLinkBody,
  CreateBiometricReadinessReviewBody,
  CreateBiometricScopeDeterminationBody,
  PutBiometricCustodyReceiptBody,
  PutBiometricDeletionConfirmationBody,
  PutBiometricEvidenceAreaBody,
  PutBiometricSectionBody,
  UpdateBiometricComponentBody,
  UpdateBiometricCustodyTransferBody,
  UpdateBiometricSupervisionActBody,
  ReviewBiometricLinkBody,
  WithdrawBiometricCustodyTransferBody,
} from '@vn-grc/shared';
import type {
  SampleDataPackageDetail,
  SampleDataPackageInstallSummary,
  SampleDataPackageLocale,
  SampleDataPackageRemovalSummary,
  SampleDataPackageSummary,
} from '@vn-grc/shared';
import type {
  ControlledTestingCase,
  ControlledTestingClockOutput,
  ControlledTestingOutcomeFiling,
  ControlledTestingDeclaredSections,
  ControlledTestingApplicationFormOption,
  ControlledTestingLabel,
  ControlledTestingPersonnelRecordView,
  ControlledTestingPersonnelVocabulary,
  ControlledTestingRegistryRecordPanel,
  ControlledTestingRegistryRequirementView,
  ControlledTestingRegistrySource,
  ControlledTestingSecurityLevelLink,
  ControlledTestingSolutionPanel,
} from '@vn-grc/shared';
import { ApiError, statusToErrorCode } from './api-error.js';
import { logWarning } from '../utils/optional-operation-log.js';
import { MFA_STEP_UP_EXEMPT_PATHS, MFA_STEP_UP_ROUTE } from '../utils/mfa-step-up.js';
import { PASSWORD_CHANGE_REQUIRED_ROUTE } from '../utils/password-change-required.js';

/**
 * One channel's status (UC-VN-61 step 3), as `GET …/channel-status` returns it. Read only: the page offers no action on it.
 */
export interface AiChannelStatusRow {
  channel_id: string;
  name: string;
  supervisor_line: string;
  dual_control: boolean;
  current_state: 'running' | 'paused' | 'stopped' | 'emergency_stopped';
  state_intervention_id: string | null;
  state_reading: 'recorded' | 'requested' | 'confirmed' | null;
  disclosures_in_use: Array<{
    kind: string;
    product: string;
    channel: string;
    reading: 'in_use' | 'not_recorded';
    disclosure: AiDisclosureRecord | null;
  }>;
  disclosures_awaiting_approval: AiDisclosureRecord[];
  open_interventions: Array<{
    intervention_id: string;
    kind: string;
    reading: string;
    awaiting_approval: boolean;
    unanswered_escalations: number;
    recorded_at: string;
  }>;
}

/**
 * What a Module Guide lookup answers (idea-catalog #79): the record under
 * `data`, or — on the route's 404 — `data: null` with the typed reason there
 * is none. Any other failure still throws.
 */
export type ModuleTipLookup =
  | { data: unknown; absent?: undefined }
  | { data: null; absent: ModuleTipAbsenceReason };

/**
 * Sprint 62.5.0 — a party to an electronic agreement **as a client may state
 * it**: a role and exactly one reference, and no `label`.
 *
 * The shared `AgreementParty` carries a required `label`, which is resolved
 * server-side and which the route's `.strict()` schema refuses. See
 * `createElectronicAgreement` for why the difference matters.
 */
export interface TrustAgreementPartyInput {
  role: 'issuer' | 'counterparty' | 'witness';
  identity_profile_id?: string | null;
  vendor_id?: string | null;
  is_own_organisation?: boolean;
}

/*
 * Sprint 65.4.1 — the local `CommencementMomentView` declaration is gone; the
 * re-export below is the one spelling. Its docblock argued it belonged here
 * "because the anchor half is a backend read-model", which was true of where it
 * is PRODUCED and not of where the wire contract should be declared: it crosses
 * the wire, so both ends must read the same type or they are free to disagree
 * about it.
 *
 * fix-sprint-20260905-2135 F18 — the comment above said that a sprint before
 * the interface was actually deleted. It stayed, unreferenced anywhere in
 * `src/`, structurally identical to `OperatorRecordedMoment`, while
 * `docs/ui/materialised-view-inventory.json` still pinned it as a live view
 * shape — so `lint:view-shapes` reported "9 view type(s) checked" and passed
 * with one of the nine certifying a dead type against itself. Now deleted, and
 * the inventory regenerated.
 */

/**
 * Re-exported from the shared contract rather than restated — Sprint 65.4.1.
 * `scan-materialised-views` caught the duplicate; the wire has one shape.
 */
import type { CommencementView, CommencementAnchorView } from '@vn-grc/shared';
import type {
  AuditEvidencePack,
  AuditEvidencePackCoverage,
  AuditEvidencePackDrift,
  AuditEvidencePackItem,
  AuditEvidencePackSourceOutcome,
  AuditEvidencePackVersion,
} from '@vn-grc/shared';

/**
 * Sprint 63.25.0 — the strict body of `POST /audit/evidence-packs`
 * (`CreateAuditEvidencePackSchema`): an undeclared key is a 400.
 */
export interface AuditEvidencePackCreateBody {
  name: string;
  period_start: string;
  period_end: string;
  framework_code?: string | null;
  include_untagged: boolean;
  entity_types: string[];
  modules?: string[] | null;
}
import type {
  ControlledTestingChildDerived,
  ControlledTestingChildEligibility,
  ControlledTestingChildKind,
  ControlledTestingClosureView,
  ControlledTestingCompletionView,
  ControlledTestingDirectionKind,
  ControlledTestingDirectionKindOption,
  ControlledTestingDirectionRecordView,
  ControlledTestingStopView,
} from '@vn-grc/shared';
export type { CommencementView, CommencementAnchorView };

export interface CommencementEnvelope {
  data: CommencementView;
}

/**
 * The body the two commencement routes accept, and nothing more — the route's
 * schema is `.strict()`, because `recorded_by`, `recorded_at` and
 * `source_field` are the server's to decide.
 */
export interface RecordCommencementBody {
  /** An ISO 8601 instant carrying `Z` or a ±HH:MM offset. */
  occurred_at: string;
  /** Required when a moment already stands; refused when one does not. */
  correction_note?: string | null;
}

/*
 * Sprint 65.7.0 — the two moments a FILED dossier carries.
 *
 * Re-exported from the shared contract for the reason `CommencementView` is:
 * these shapes cross the wire, so both ends read one declaration or they are
 * free to disagree about it. `scan-materialised-views` treats a second local
 * copy as a finding, and 65.4.1 deleted exactly such a copy from this file.
 */
import type {
  DossierFirstFilingView,
  RegisteredParticularsChangeView,
  RecordDossierFirstFilingRequest,
  RecordRegisteredParticularsChangeRequest,
} from '@vn-grc/shared';
export type {
  DossierFirstFilingView,
  RegisteredParticularsChangeView,
  RecordDossierFirstFilingRequest,
  RecordRegisteredParticularsChangeRequest,
};
// Sprint 62.24.0 — consent evidence by recipient class, the credit-information
// supply register, the data-service provider record and the offering scoping
// decision. Wire shapes come from the shared contract for the reason above.
import type {
  DeclaredLegalMatterRight,
  ConsentEvidenceEntryView,
  ConsentEvidenceVocabulary,
  LawfulBasisVocabulary,
  TransferMechanismVocabulary,
  ConsentCoverState,
  CreditInformationSupplyEntryView,
  CreditInformationSupplyKinds,
  DataServiceProviderRecord,
  DataServiceProviderView,
  DataServiceProviderVocabulary,
  DeclarationAbsence,
  RecordOfferingScopingDecisionRequest,
} from '@vn-grc/shared';
// Sprint 62.54.0 — fee lines on applications and sender-name certificates.
import type {
  FeeApplicationDecisionStatus,
  FeeCompletenessRow,
  LicenceFeeApplicationView,
  RegulatoryFeeSchedule,
  SenderNameCertificateView,
  FeeLineOwnerType,
  FeeLinePeriodList,
  RegulatoryFeeLineExportLocale,
} from '@vn-grc/shared';
// Sprint 62.28.0 — the marketing-communications registers.
import type {
  AuthorityRegisterReconciliationState,
  AutomatedClassificationAssessment,
  EnforcementMethod,
  MarketingCommunicationsKinds,
  MarketingConsentEntryView,
  MarketingSuppressionEntryView,
  SenderIdentityRecord,
  SuppressionEnforcementCheckView,
  SuppressionScope,
} from '@vn-grc/shared';

// Sprint 63.28.0 — Persona Paths. The parts each item is built from are the
// shared contract; the items below name only how the onboarding routes compose
// them (`onboarding-maturity-service.ts` `getPersonaPaths`, `getPersonaPath`,
// `detectPersona`, `choosePersona`).
import type {
  PersonaText,
  PersonaJurisdiction,
  PersonaRoleFamily,
  PersonaSector,
  PersonaRelevance,
  PersonaDestinationLink,
  PersonaFrameworkSplit,
  PersonaFrameworkName,
  PersonaSource,
  PersonaDetectedBy,
} from '@vn-grc/shared';

/** The registry entry as `GET /onboarding/maturity/persona-paths` projects it. */
export interface PersonaPathBase {
  persona_id: string;
  /** English; the page resolves the reader's language from the `_i18n` fields. */
  persona_label: string;
  role_description: string;
  primary_modules: string[];
  quick_start_title: string;
  persona_label_i18n: PersonaText;
  role_description_i18n: PersonaText;
  quick_start_title_i18n: PersonaText;
  jurisdiction: PersonaJurisdiction;
  doc_slug: string;
  role_family: PersonaRoleFamily;
  sectors: PersonaSector[];
  primary_destinations: PersonaDestinationLink[];
}

/** One item of the paginated list: the projection plus the tenant's relevance. */
export interface PersonaPathListItem extends PersonaPathBase {
  /** Empty when the persona has no authored quick-start scenario. */
  quick_start_scenario_id: string;
  relevance: PersonaRelevance;
  /**
   * The frameworks naming the persona, each with its code so
   * the page resolves the reader's-language name from its own locale content;
   * read by the picker's search.
   */
  framework_names: PersonaFrameworkName[];
}

/** `GET /onboarding/maturity/persona-paths/:personaId`. */
export interface PersonaPathDetailItem extends PersonaPathListItem {
  recommended_tours_enriched: { module: string; title: string }[];
  frameworks: PersonaFrameworkSplit;
}

/** Sprint 63.35.0 (Q45) — `GET /onboarding/maturity/persona-text`. */
export interface PersonaTextResponse {
  personas: Record<string, { role_description: PersonaText; quick_start_title: PersonaText }>;
}

/** `GET /onboarding/maturity/detect-persona` — the saved persona, else the detected one. */
export interface DetectedPersonaResponse extends PersonaPathBase {
  persona_source: PersonaSource;
  /** Absent when `persona_source` is `chosen`. */
  detected_by?: PersonaDetectedBy;
  /** True when a saved persona is no longer available and detection answered. */
  chosen_unavailable?: boolean;
}

/** `PATCH /onboarding/maturity/progress/persona`. */
export interface ChosenPersonaResult {
  persona_id: string;
  persona_source: 'chosen';
}

/** The custody reference as recorded (62.7.0 shape); `restricted` is stated, never defaulted. */
export interface ConsentCustodyReferenceInput {
  kind_code: string;
  custodian: string;
  external_reference: string;
  restricted: boolean;
}
export interface CreateConsentEvidenceBody {
  subject_ref: string;
  artefact_attachment_id?: string | null;
  custody_reference?: ConsentCustodyReferenceInput | null;
  consent_format: string;
  consented_at: string;
  recipient_classes: string[];
  consent_workflow_id?: string | null;
  notes?: string | null;
  framework_code?: string | null;
}
export type UpdateConsentEvidenceBody = Partial<CreateConsentEvidenceBody>;
// ── Sprint 62.28.0: marketing-communications registers ──
export interface CreateMarketingConsentBody {
  subject_ref: string;
  channel: string;
  consent_source: string;
  consented_at: string;
  notice: {
    kind: string;
    policy_id?: string | null;
    version_label?: string | null;
    notice_uri?: string | null;
  };
  topic_refs?: string[];
  artefact_attachment_id?: string | null;
  custody_reference?: ConsentCustodyReferenceInput | null;
  consent_workflow_id?: string | null;
  notes?: string | null;
  framework_code?: string | null;
}
export type UpdateMarketingConsentBody = Partial<CreateMarketingConsentBody>;
export interface CreateMarketingSuppressionBody {
  subject_ref: string;
  scope: SuppressionScope;
  /** Null exactly when `scope` is `all_channels`; the server refuses the mismatch. */
  channel?: string | null;
  suppression_reason: string;
  requested_at: string;
  effective_from: string;
  marketing_consent_entry_id?: string | null;
  complaint_ref?: string | null;
  notes?: string | null;
  framework_code?: string | null;
}
export interface RecordSuppressionConfirmationBody {
  confirmation_channel: string;
  stops_at?: string | null;
  artefact_attachment_id?: string | null;
  note?: string | null;
}
export interface CreateSuppressionEnforcementCheckBody {
  campaign_ref: string;
  channel: string;
  performed_at: string;
  sender_identity?: SenderIdentityRecord | null;
  population_size: number;
  suppressed_count: number;
  authority_register_state: AuthorityRegisterReconciliationState;
  method: EnforcementMethod;
  /** Required when `method` is `automated`, refused when it is `manual`. */
  automated_classification?: AutomatedClassificationAssessment | null;
  artefact_attachment_id?: string | null;
  notes?: string | null;
  framework_code?: string | null;
}
export interface CreateCreditInformationSupplyBody {
  subject_ref: string;
  recipient_class: string;
  vendor_id?: string | null;
  supply_channel: string;
  supplied_at: string;
  data_category_ids?: string[];
  consent_evidence_id?: string | null;
  /** Required, no default — stated cover is the row's point (AC4). */
  consent_cover_state: ConsentCoverState;
  transfer_assessment_id?: string | null;
  notes?: string | null;
  framework_code?: string | null;
}
export type UpdateCreditInformationSupplyBody = Partial<CreateCreditInformationSupplyBody> & {
  /** AC6 — the organisation's determination; the server stamps who and when. */
  withdrawal_determination?: { determination: string } | null;
};
export interface CreateDataServiceProviderBody {
  name: string;
  offering_ids: string[];
  pre_existing_on_commencement: boolean;
  framework_code?: string | null;
  notes?: string | null;
}
export type UpdateDataServiceProviderBody = Partial<CreateDataServiceProviderBody>;

// ── Sprint 62.55.0 — customs tax-administration register bodies (work file C.1) ──
// Each mirrors a STRICT server schema (`schemas.ts`, `schemas-records.ts`): an
// undeclared key is a 400. Every update carries the `version` it read (409
// `CTA_VERSION_CONFLICT`). Select-valued keys are pack option values the page
// read from `GET /customs-tax-administration/context/:surfaceKey`.
type CtaUpdate<T> = Partial<T> & { version: number };
/** A list query: the R-23 bounds, plus the route's own filters. */
export type CtaListParams = Record<string, string | number | undefined> & {
  page?: number;
  page_size?: number;
};
export interface CreateCtaDossierBody {
  procedure_value: string;
  filed_template_schema_id?: string | null;
  customs_record_ids: string[];
  channel_code?: string | null;
  held_document_reference?: string | null;
  signed_off_by?: string | null;
  signed_off_on?: string | null;
  lodged_on?: string | null;
  confirmation_purpose?: string | null;
  notes?: string | null;
}
export type UpdateCtaDossierBody = CtaUpdate<
  Omit<CreateCtaDossierBody, 'procedure_value'> & {
    received_by_authority_on: string | null;
    confirmation_answer: string | null;
    confirmation_unfulfilled_declarations: string | null;
    confirmation_answer_document_id: string | null;
    confirmation_answered_on: string | null;
    reconfirmed_by_dossier_id: string | null;
    evidence_attachment_ids: string[];
  }
>;
export type CtaReceivedBinding = CtaReceivedDocument['bound_to'];
export interface CtaStatedFigureInput {
  figure_key: string;
  label?: string | null;
  stated_amount: number;
  stated_currency?: string | null;
}
export interface CreateCtaReceivedDocumentBody {
  document_kind: string;
  template_schema_id: string;
  document_number?: string | null;
  document_date?: string | null;
  received_on: string;
  issuing_body_as_printed?: string | null;
  bound_to: CtaReceivedBinding;
  stated_figures?: CtaStatedFigureInput[];
  stated_deadlines?: Array<{ label: string; stated_on: string }>;
  stated_late_interest_start_on?: string | null;
  notes?: string | null;
}
export type UpdateCtaReceivedDocumentBody = CtaUpdate<
  Omit<CreateCtaReceivedDocumentBody, 'document_kind' | 'template_schema_id' | 'bound_to'> & {
    replied_on: string | null;
    reply_dossier_id: string | null;
    evidence_attachment_ids: string[];
  }
>;
export type CtaPaysTarget = CtaPayment['pays'][number];
export interface CreateCtaPaymentBody {
  stated_amount: number;
  stated_currency?: string | null;
  payer_class: string;
  payer_name_as_stated?: string | null;
  paid_on: string;
  payment_date_basis: string;
  document_number?: string | null;
  pays: CtaPaysTarget[];
  notes?: string | null;
}
export type UpdateCtaPaymentBody = CtaUpdate<
  CreateCtaPaymentBody & { receipt_attachment_id: string | null }
>;
export interface CreateCtaDifferenceClosureBody {
  payment_id: string;
  received_document_id: string;
  figure_key: string;
  reason: string;
}
/** Gap R-2 (2026-10-26) — the figures a dossier states, replaced as a whole at the version read. */
export interface SetCtaDossierStatedFiguresBody {
  stated_figures: CtaStatedFigureInput[];
  version: number;
}
/** Gap R-2 — a person closes a difference between a dossier's figure and a received document's. */
export interface CreateCtaDossierFigureClosureBody {
  dossier_id: string;
  received_document_id: string;
  figure_key: string;
  reason: string;
}
export interface CreateCtaAnchorEventBody {
  customs_record_id: string;
  anchor_kind: string;
  occurred_on: string;
  recorded_by: string;
  source_received_document_id?: string | null;
  source_reference?: string | null;
  notes?: string | null;
}
export type UpdateCtaAnchorEventBody = CtaUpdate<
  Omit<CreateCtaAnchorEventBody, 'customs_record_id' | 'anchor_kind'> & {
    closed_by_payment_id: string | null;
  }
>;
export interface CreateCtaExportBody {
  kind: CtaExportKind;
  format: 'pdf' | 'csv' | 'xlsx';
  filters?: { procedure_id?: string; customs_record_id?: string };
  /** The language the page shows; the file prints pack labels in it (`en` or `vi`). */
  locale?: string;
}
export interface CtaListPartyInput {
  party_role: string;
  name_as_stated: string;
  tax_code_as_stated?: string | null;
}
export interface CreateCtaDutyFreeListBody {
  dossier_id: string;
  list_reference?: string | null;
  parties?: CtaListPartyInput[];
  customs_record_ids?: string[];
  notes?: string | null;
}
export type UpdateCtaDutyFreeListBody = CtaUpdate<
  Omit<CreateCtaDutyFreeListBody, 'dossier_id'> & {
    acknowledgement_document_id: string | null;
    acknowledged_on: string | null;
    imports_ended_on: string | null;
    expires_on: string | null;
    operation_started_on: string | null;
    /** O6 — every recorded completion, one per completion-notice kind. */
    completion_facts: Array<{ notice_kind: string; completed_on: string }>;
  }
>;
export interface CreateCtaListAmendmentBody {
  amended_on: string;
  description: string;
  customs_record_id?: string | null;
  received_document_id?: string | null;
  notes?: string | null;
}
export type UpdateCtaListAmendmentBody = CtaUpdate<CreateCtaListAmendmentBody>;
export interface CreateCtaListStopEventBody {
  stop_use_event: string;
  occurred_on: string;
  notes?: string | null;
}
export type UpdateCtaListStopEventBody = CtaUpdate<{
  declared_by_customs_record_id: string | null;
  declared_on: string | null;
  notes: string | null;
}>;
export interface CreateCtaDeductionBody {
  duty_free_list_id: string;
  customs_record_id: string;
  received_document_id?: string | null;
  system_reference?: string | null;
  goods_as_stated: string;
  unit_as_stated: string;
  stated_quantity_cleared: number;
  stated_quantity_remaining: number;
  /** As printed on the sheet (gate E12.5 C1-8); orders the ledger. */
  stated_row_date?: string | null;
  /** As printed on the sheet, an integer of 1 or more; orders the ledger before the date. */
  stated_row_sequence?: number | null;
  notes?: string | null;
}
export type UpdateCtaDeductionBody = CtaUpdate<
  Omit<CreateCtaDeductionBody, 'duty_free_list_id' | 'customs_record_id'>
>;
export interface CreateCtaUsageNotificationBody {
  duty_free_list_id: string;
  notice_kind: string;
  financial_year_ended_on?: string | null;
  notified_on: string;
  template_schema_id?: string | null;
  dossier_id?: string | null;
  notes?: string | null;
}
export type UpdateCtaUsageNotificationBody = CtaUpdate<
  Omit<CreateCtaUsageNotificationBody, 'duty_free_list_id' | 'notice_kind'>
>;
export interface CtaRefundFieldsBody {
  classification?: string | null;
  /** OQ-86-3 wiring: the case the classification notice states, an installed option value. */
  classification_case?: string | null;
  classification_document_id?: string | null;
  materials_report_template_schema_id?: string | null;
  norms_summary?: string | null;
  pre_refund_examination_id?: string | null;
  decision_document_id?: string | null;
  post_refund_examination_id?: string | null;
  recovery_decision_document_id?: string | null;
  /** Pack 1.16.4 (R-1): the received offset notice. */
  offset_notice_document_id?: string | null;
  self_discovered_on?: string | null;
  self_discovery_notice?: string | null;
  self_discovery_notified_on?: string | null;
  notes?: string | null;
}
export type CreateCtaRefundRecordBody = CtaRefundFieldsBody & { dossier_id: string };
export type UpdateCtaRefundRecordBody = CtaRefundFieldsBody & { version: number };
export interface CtaStatedFigureWithSourceInput extends CtaStatedFigureInput {
  source: CtaFigureSource;
}
export interface CtaGuaranteeInput {
  guarantor_as_stated: string;
  valid_from?: string | null;
  valid_until?: string | null;
  substituted_on?: string | null;
  substitution_note?: string | null;
}
export interface CtaDebtFieldsBody {
  dossier_id?: string | null;
  received_document_id?: string | null;
  ends_instrument_id?: string | null;
  effective_on?: string | null;
  instalment_band?: string | null;
  stated_debt_figure?: CtaStatedFigureWithSourceInput | null;
  /** Pack 1.16.4 (D-1): a freeze or write-off only — the case as the decision states it. */
  stated_case?: {
    text: string;
    source: { kind: 'received_document'; received_document_id: string };
    /** OQ-86-3 wiring: the installed cases a person matched the decision to. */
    choices?: Array<{ field_id: string; value: string }>;
  } | null;
  stated_case_citation?: string | null;
  approved_on?: string | null;
  /** OQ-86-3 wiring: the due date the notice or the written acceptance states. */
  stated_due_on?: string | null;
  guarantee?: CtaGuaranteeInput | null;
  notes?: string | null;
}
export type CreateCtaDebtInstrumentBody = CtaDebtFieldsBody & { instrument_kind: string };
export type UpdateCtaDebtInstrumentBody = CtaDebtFieldsBody & { version: number };
export type CtaExaminationSubject = CtaExaminationView['examination']['subject'];
export interface CreateCtaExaminationBody {
  purpose: string;
  subject: CtaExaminationSubject;
  decision_signed_on?: string | null;
  decision_sent_on?: string | null;
  announced_on?: string | null;
  re_examination_of?: string | null;
  /** Pack 1.16.1: the `examination_basis` option value and the two expert-opinion dates. */
  examination_basis?: string | null;
  expert_opinion_requested_on?: string | null;
  expert_opinion_reply_received_on?: string | null;
  notes?: string | null;
}
export type UpdateCtaExaminationBody = CtaUpdate<{
  decision_signed_on: string | null;
  decision_sent_on: string | null;
  announced_on: string | null;
  examination_basis: string | null;
  expert_opinion_requested_on: string | null;
  expert_opinion_reply_received_on: string | null;
  notes: string | null;
}>;
export interface CtaExaminationStepFieldsBody {
  template_schema_id?: string | null;
  received_document_id?: string | null;
  organisation_explanation?: string | null;
  refused_to_sign?: boolean;
  refusal_witness_as_stated?: string | null;
  suspension_requested_by_organisation?: boolean;
  suspension_reason?: string | null;
  suspension_from?: string | null;
  suspension_until?: string | null;
  notes?: string | null;
}
export type CreateCtaExaminationStepBody = CtaExaminationStepFieldsBody & {
  step_kind: string;
  occurred_on: string;
};
export type UpdateCtaExaminationStepBody = CtaExaminationStepFieldsBody & {
  occurred_on?: string;
  version: number;
};
export interface CreateCtaOfficeExaminationBody {
  authority_request_id: string;
  subject: CtaOfficeExamination['subject'];
  explanation?: string | null;
  explanation_sent_on?: string | null;
  outcome?: string | null;
  outcome_on?: string | null;
  notes?: string | null;
}
export type UpdateCtaOfficeExaminationBody = CtaUpdate<
  Omit<CreateCtaOfficeExaminationBody, 'authority_request_id' | 'subject'>
>;
export interface CreateCtaProjectTransferBody {
  transfer_role: string;
  counterparty_as_stated: string;
  transferred_on: string;
  /** Pack 1.16.1: the `transfer_case` option value. */
  transfer_case?: string | null;
  certificate_adjusted_on?: string | null;
  contract_on?: string | null;
  transferor_notice_dossier_id?: string | null;
  notes?: string | null;
}
export type UpdateCtaProjectTransferBody = CtaUpdate<
  Omit<CreateCtaProjectTransferBody, 'transfer_role' | 'transferred_on'> & {
    duty_assignee: string | null;
    new_declaration_customs_record_id: string | null;
    new_declaration_on: string | null;
  }
>;
export interface CreateCtaRuleDeterminationBody {
  record_type: 'dossier' | 'examination';
  record_id: string;
  outcome: 'earlier_rules' | 'current_rules';
  reason: string;
  authority_statement?: string | null;
  authority_statement_document_id?: string | null;
  notes?: string | null;
}
export type UpdateCtaRuleDeterminationBody = CtaUpdate<
  Omit<CreateCtaRuleDeterminationBody, 'record_type' | 'record_id'>
>;
/** One row of `GET /legal-timeline`. */
export interface CtaLegalTimelineRow {
  framework_code: string;
  /** The installed display names of every framework the row names, by code (62.55.0 close-out). */
  framework_names?: Record<string, BilingualText>;
  /**
   * The installed titles of every requirement the row names, by framework code then
   * requirement id (gate E12.5 Q1-7); an absent key means no title is installed.
   */
  requirement_titles?: Record<string, Record<string, BilingualText>>;
  entries: CtaTimelineEntryDeclaration[];
  relationships: CtaClauseRelationshipDeclaration[];
  lineage: CtaReadingLineage[];
}

// ── Sprint 62.50.0 — supervision-information register bodies (work file C.1) ──
// Every create is a strict schema on the server; every PATCH/PUT carries the
// `version` it read (409 `PCF_RECORD_VERSION_CONFLICT` on a stale one).
export interface CreatePcfBranchRequirementBody {
  reference: string;
  received_on: string;
  content: string;
  period_text?: string | null;
  stated_deadline: PcfStatedDeadline;
  /** What the requirement asks for; at least one kind. */
  asks_for: PcfRequirementKind[];
  framework_code?: string | null;
  notes?: string | null;
}
export type UpdatePcfBranchRequirementBody = Partial<CreatePcfBranchRequirementBody> & {
  /** Recording the publicity complete: a date and the person, together (publicity:write). */
  publicity_completed_on?: string | null;
  publicity_completed_by?: string | null;
  version: number;
};
export interface CreatePcfPublicityBody {
  requirement_id: string;
  channel: string;
  absence_statement: boolean;
  location_or_address?: string | null;
  published_on?: string | null;
  notice_text?: string | null;
  notes?: string | null;
}
/** A record's requirement is fixed at creation: the strict update schema refuses `requirement_id`. */
export type UpdatePcfPublicityBody = Partial<Omit<CreatePcfPublicityBody, 'requirement_id'>> & {
  evidence_attachment_ids?: string[];
  version: number;
};
export interface CreatePcfInformationRequestBody {
  requirement_id: string;
  information_requested: string;
  information_provided?: string | null;
  provided_on?: string | null;
  channel_used?: string | null;
  approved_by?: string | null;
  notes?: string | null;
}
/** A record's requirement is fixed at creation: the strict update schema refuses `requirement_id`. */
export type UpdatePcfInformationRequestBody = Partial<
  Omit<CreatePcfInformationRequestBody, 'requirement_id'>
> & {
  evidence_attachment_ids?: string[];
  version: number;
};
export interface CreatePcfDepositorEnquiryBody {
  depositor_reference: string;
  received_at: string;
  received_through: string;
  enquiry_summary: string;
  routing_outcome: string;
  handled_by: string;
  information_request_id?: string | null;
  notes?: string | null;
}
/** Only these four keys may change after the enquiry is logged (C.1). */
export interface UpdatePcfDepositorEnquiryBody {
  handled_by?: string;
  information_request_id?: string | null;
  enquiry_summary?: string;
  notes?: string | null;
  version: number;
}
export interface PcfPersonalDataMechanismBody {
  statement: string;
  recorded_by: string;
  recorded_on: string;
}
export interface CreatePcfSharedInformationBody {
  recipient: string;
  information_category: string;
  scope_limb: string;
  approval_reference?: string | null;
  connection_method: string;
  purpose_of_use: string;
  contains_personal_data: boolean;
  personal_data_mechanism?: PcfPersonalDataMechanismBody | null;
  recorded_by: string;
  recorded_on: string;
  notes?: string | null;
}
export type UpdatePcfSharedInformationBody = Partial<CreatePcfSharedInformationBody> & {
  version: number;
};
/**
 * Sprint 62.50.1 — register (a): a recipient's written request, and its answer
 * once a person records one (`CreateWrittenRequest`, strict). `answer`,
 * `answer_reference` and `answered_on` are recorded together or not at all.
 */
export interface CreatePcfWrittenRequestBody {
  recipient: string;
  sent_on: string;
  reference: string;
  information_requested: string;
  answer?: string | null;
  answer_reference?: string | null;
  answered_on?: string | null;
  related_entry_ids?: string[];
  recorded_by: string;
  recorded_on: string;
  notes?: string | null;
}
export type UpdatePcfWrittenRequestBody = Partial<CreatePcfWrittenRequestBody> & {
  version: number;
};
/** Sprint 62.50.1 — register (a): a recipient's proposal (`CreateChangeProposal`, strict). */
export interface CreatePcfChangeProposalBody {
  recipient: string;
  sent_on: string;
  reference: string;
  change_kinds: string[];
  subjects: string[];
  necessity: string;
  details: string;
  response_note?: string | null;
  related_entry_ids?: string[];
  recorded_by: string;
  recorded_on: string;
  notes?: string | null;
}
export type UpdatePcfChangeProposalBody = Partial<CreatePcfChangeProposalBody> & {
  version: number;
};
export interface CreatePcfReportingCalendarBody {
  list_reference: string;
  information_group: string;
  item_name: string;
  period_as_listed: string;
  deadline_as_listed: string;
  reporting_period: string;
  deadline_date: string;
  channel: string;
  submitted_on?: string | null;
  submission_reference?: string | null;
  notes?: string | null;
}
export type UpdatePcfReportingCalendarBody = Partial<CreatePcfReportingCalendarBody> & {
  version: number;
};
export interface CreatePcfIncidentBody {
  incident_started_at: string;
  incident_description: string;
  branch_notified_on?: string | null;
  branch_notice_reference?: string | null;
  sent_in_writing?: boolean | null;
  sent_through_system_function?: boolean | null;
  fixed_at?: string | null;
  fix_notice_reference?: string | null;
  resumed_at?: string | null;
  resumption_note?: string | null;
  notes?: string | null;
}
export type UpdatePcfIncidentBody = Partial<CreatePcfIncidentBody> & {
  evidence_attachment_ids?: string[];
  version: number;
};
export interface CreatePcfCorrectionBody {
  notice_type: string;
  report_concerned: string;
  notice_received_on: string;
  corrected_and_resent_on?: string | null;
  locked: boolean;
  unlock_request_reference?: string | null;
  unlock_request_reason?: string | null;
  branch_decision?: string | null;
  branch_decision_on?: string | null;
  notes?: string | null;
}
export type UpdatePcfCorrectionBody = Partial<CreatePcfCorrectionBody> & {
  evidence_attachment_ids?: string[];
  version: number;
};
export interface PutPcfSystemOperationBody {
  operates?: PcfToldDate | null;
  official_operation?: PcfToldDate | null;
  already_sends_through_system?: boolean | null;
  upgrade_completed_on?: string | null;
  upgrade_evidence?: string | null;
  evidence_attachment_ids?: string[];
  notes?: string | null;
  version?: number;
}
// ── Sprint 62.51.0 — data-exploitation request bodies (work file C.1) ──
// Strict on the server: every key here is one the route declares. A create
// carries no evidence list (a file is held against a record that exists); an
// edit and every sub-record carry the `version` they read.
export interface CreateDataExploitationActivityBody {
  activity_description: string;
  routes_used: string[];
  no_route: boolean;
  incentive_elected: string | null;
  decided_by: string;
  basis: string;
  decided_on: string;
  notes: string | null;
}
export type UpdateDataExploitationActivityBody = Partial<CreateDataExploitationActivityBody> & {
  version: number;
  evidence_attachment_ids?: string[];
};
export interface CreateDataExploitationRequestBody {
  state: DataExploitationRequestState;
  receiving_body: string | null;
  requester_identification: string | null;
  purpose: string | null;
  scope: string | null;
  data_type: string | null;
  requested_term: string | null;
  basis: string | null;
  data_protection_marking: string | null;
  data_protection_plan: string | null;
  sent_at: string | null;
  outcome: string | null;
  resolution_category: string | null;
  supplement_requested_on: string | null;
  resolution_extension_notified_on: string | null;
  resolution_extension_reasons: string | null;
  deciding_body_kind: string | null;
  deciding_body: string | null;
  decision_reference: string | null;
  decided_on: string | null;
  approved_datasets: string | null;
  approved_purpose_and_scope: string | null;
  approved_term_end: string | null;
  refusal_reasons: string | null;
  notes: string | null;
}
export type UpdateDataExploitationRequestBody = Partial<CreateDataExploitationRequestBody> & {
  version: number;
  evidence_attachment_ids?: string[];
};
export interface DataExploitationRightActionBody {
  version: number;
  right_action_kind: string;
  right_action_date: string;
  right_action_body: string;
  right_action_reference: string | null;
  right_action_reason: string | null;
  evidence_attachment_ids: string[];
  /**
   * Remediation Y: on an ending decision, the `action_id` of the standing
   * suspension, halt or revocation it ends, as the person names it from the
   * document; omitted or `null` → the server ends the earliest standing one of
   * a kind the option names.
   */
  ends_action_id?: string | null;
}
/** Remediation Y — the organisation's correction of an entry it recorded by mistake. */
export interface DataExploitationRecordedInErrorBody {
  version: number;
  /** Required, 1–2,000 characters after trimming. */
  reason: string;
}
export interface DataExploitationPurposeEndedBody {
  version: number;
  ended_on: string;
}
export interface CreateDataExploitationTestingRecordBody {
  case_id: string;
  related_request_id: string | null;
  mechanism_chosen: string;
  choice_reason: string;
  cooperation_contract_reference: string | null;
  foreign_participant: boolean | null;
  supervision_arrangements: string | null;
  logging_and_traces: string | null;
  approved_space_kinds: string[];
  approved_space: string | null;
  approved_term_start: string | null;
  approved_term_end: string | null;
  wider_application_decision_reference: string | null;
  notes: string | null;
}
export type UpdateDataExploitationTestingRecordBody = Partial<
  Omit<CreateDataExploitationTestingRecordBody, 'case_id'>
> & { version: number; evidence_attachment_ids?: string[] };
export interface DataExploitationRiskPlanVersionBody {
  version: number;
  plan_version: string;
  plan_adopted_on: string;
  plan_change_summary: string | null;
  evidence_attachment_ids: string[];
}
export interface DataExploitationExtensionBody {
  version: number;
  extension_end_date: string;
  extension_decision_reference: string;
  extension_decision_date: string;
  evidence_attachment_ids: string[];
}
export interface DataExploitationCentreActionBody {
  version: number;
  centre_action_kind: string;
  centre_action_date: string;
  centre_action_reference: string | null;
  centre_action_reason: string | null;
  evidence_attachment_ids: string[];
}
export interface DataExploitationCertificateBody {
  version: number;
  kind: DataExploitationCertificateKind;
  reference: string;
  dated_on: string;
  issuing_body: string;
  copy_source: 'authority_copy';
  evidence_attachment_ids: string[];
}
export interface DataExploitationReuseCheckBody {
  version: number;
  items: string;
  destination: string;
  authority_kind: string;
  authority_reference: string | null;
  checked_by: string;
  checked_on: string;
  evidence_attachment_ids: string[];
}
export interface CreateDataExploitationNoticeBody {
  kind: string;
  discovered_at: string;
  description: string;
  related_request_id: string | null;
  related_incident_id: string | null;
  notices: DataExploitationNoticeSent[];
  isolation_measures: string | null;
  remedial_measures: string | null;
  evidence_preserved: string | null;
  notes: string | null;
}
export type UpdateDataExploitationNoticeBody = Partial<CreateDataExploitationNoticeBody> & {
  version: number;
  evidence_attachment_ids?: string[];
};
export interface CreateDataExploitationDispositionBody {
  related_request_id: string;
  end_kind: string;
  ended_on: string;
  action: string;
  done_on: string;
  evidence: string | null;
  retention_basis: string | null;
  deletion_run_id: string | null;
  notes: string | null;
}
export type UpdateDataExploitationDispositionBody = Partial<
  Omit<CreateDataExploitationDispositionBody, 'related_request_id'>
> & { version: number; evidence_attachment_ids?: string[] };
export interface CreateDataExploitationIncubatorBody {
  programme: string;
  enrichment_performed: string;
  shared_with_centre: string;
  shared_on: string;
  listed_on: string | null;
  notes: string | null;
  /** Remediation W (U21): the completion certificate as it names itself; optional, nullable. */
  completion_certificate_reference?: string | null;
  completion_certificate_date?: string | null;
  completion_certificate_issuer?: string | null;
}
/** Remediation W (U21): one reliance on the liability-exemption section, recorded on an incubator record. */
export interface DataExploitationLiabilityRelianceBody {
  version: number;
  liability_case: string;
  liability_risk_and_facts: string;
  liability_measures_applied?: string | null;
  liability_conditions_basis: string;
  liability_decided_by: string;
  liability_decided_on: string;
  evidence_attachment_ids?: string[];
}
export type UpdateDataExploitationIncubatorBody = Partial<CreateDataExploitationIncubatorBody> & {
  version: number;
  evidence_attachment_ids?: string[];
};
export interface CreateDataExploitationForeignAssessmentBody {
  foreign_party_name: string;
  foreign_party_country: string;
  cooperation_kinds: string[];
  cooperation_description: string;
  deployed_on: string | null;
  related_request_id: string | null;
  related_testing_case_id: string | null;
  vendor_id: string | null;
  transfer_dossier_reference: string | null;
  assessed_by: string | null;
  assessed_on: string | null;
  assessment_reference: string | null;
  data_level: string | null;
  data_scope: string | null;
  data_nature: string | null;
  management_measures: string | null;
  supervision_measures: string | null;
  tracing_measures: string | null;
  access_control_measures: string | null;
  notes: string | null;
}
export type UpdateDataExploitationForeignAssessmentBody =
  Partial<CreateDataExploitationForeignAssessmentBody> & {
    version: number;
    evidence_attachment_ids?: string[];
  };
// ── Sprint 62.52.0 — cybersecurity assurance index request bodies (C.1) ──
// Every body is strict on the server: these are its keys and no others. A
// select-valued key is a pack option value, never a literal of the platform.
/** One piece of evidence of the period (D4 as revised): a file, a reference, or both. */
export interface CybersecurityAssuranceIndexEvidenceLinkBody {
  description: string;
  evidence_date: string;
  /** The field ids of the catalogue's evidence-date statements the person made. */
  date_statements?: string[];
  attachment_id?: string | null;
  evidence_reference?: string | null;
  /**
   * Remediation W (U12) — on an item entry only (the statistic and the
   * supplier's evidence refuse the key): the supplier request's returned
   * evidence this link cites. Its date and file must be the cited evidence's own.
   */
  supplier_request_evidence?: CybersecurityAssuranceIndexSupplierEvidenceCitation | null;
}
/**
 * E12.5 p1 (QA-12) — the citation's shape is the shared interface itself,
 * re-exported for the callers that read it from here.
 */
export type { CybersecurityAssuranceIndexSupplierEvidenceCitation };
/** The owner's assessment a participant takes part in; the keys are the period template's field ids. */
export interface CybersecurityAssuranceIndexOwnerAssessmentBody {
  owner_name?: string | null;
  owner_system_name?: string | null;
  participant_kind?: string | null;
  owner_request_reference?: string | null;
  owner_request_date?: string | null;
  participation_scope?: string | null;
  owner_procedures_reference?: string | null;
  owner_vendor_id?: string | null;
}
export interface CreateCybersecurityAssuranceIndexPeriodBody {
  title: string;
  period_start: string;
  period_end: string;
  assessor_role: string;
  guidance_reference?: string | null;
  contains_state_secrets?: string | null;
  owner_assessment?: CybersecurityAssuranceIndexOwnerAssessmentBody | null;
  members?: string[];
  notes?: string | null;
}
export type UpdateCybersecurityAssuranceIndexPeriodBody = Partial<
  Omit<CreateCybersecurityAssuranceIndexPeriodBody, 'members'>
> & { version: number };
export interface AddCybersecurityAssuranceIndexSystemBody {
  version: number;
  software_development: boolean;
  system_name?: string;
  classification_profile_id?: string | null;
  scheme_id?: string | null;
  system_level?: string | null;
  level_approval_reference?: string | null;
  operating_parties?: string | null;
}
export interface UpdateCybersecurityAssuranceIndexSystemBody {
  version: number;
  system_name?: string;
  system_level?: string;
  level_approval_reference?: string | null;
  software_development?: boolean;
  operating_parties?: string | null;
}
export interface CybersecurityAssuranceIndexParticipantBody {
  participant_kind: string;
  participant_name: string;
  vendor_id?: string | null;
  certification_basis?: {
    standard_as_entered: string;
    certification_reference?: string | null;
    recorded_judgement: string;
    judged_by: string;
  } | null;
}
export interface PutCybersecurityAssuranceIndexEntryBody {
  /** 0 for an item not yet recorded; the version read otherwise. */
  version: number;
  rating: string | null;
  excluded: boolean;
  explanation?: string | null;
  evidence?: CybersecurityAssuranceIndexEvidenceLinkBody[];
  exclusion_explanation?: string | null;
  participants?: CybersecurityAssuranceIndexParticipantBody[];
  entered_by?: string;
}
export interface CybersecurityAssuranceIndexAuthorityFigureBody {
  value: number;
  component?: CybersecurityAssuranceIndexFigureComponent;
}
export interface CreateCybersecurityAssuranceIndexStatisticBody {
  /** null — one statistic over the whole period (D9). */
  system_entry_id: string | null;
  criterion_code: string;
  numerator: number;
  denominator?: number | null;
  unit: string;
  evidence_source: string;
  calculation_version: string;
  authority_figure?: CybersecurityAssuranceIndexAuthorityFigureBody | null;
  evidence?: CybersecurityAssuranceIndexEvidenceLinkBody[];
  notes?: string | null;
}
export type UpdateCybersecurityAssuranceIndexStatisticBody = Partial<
  Omit<CreateCybersecurityAssuranceIndexStatisticBody, 'system_entry_id' | 'criterion_code'>
> & { version: number };
export interface CreateCybersecurityAssuranceIndexSupplierRequestBody {
  cybersecurity_assurance_index_period_id: string;
  supplier_name: string;
  supplier_vendor_id?: string | null;
  content_codes: string[];
  requested_evidence_kind: string;
  requested_on: string;
  requested_by: string;
  request_reference?: string | null;
  contract_reference?: string | null;
  answer_wanted_by?: string | null;
  response_received_on?: string | null;
  response_summary?: string | null;
  certification_reference?: string | null;
  response_evidence?: CybersecurityAssuranceIndexEvidenceLinkBody[];
  notes?: string | null;
}
export type UpdateCybersecurityAssuranceIndexSupplierRequestBody = Partial<
  Omit<
    CreateCybersecurityAssuranceIndexSupplierRequestBody,
    'cybersecurity_assurance_index_period_id'
  >
> & { version: number };
export interface PutPcfConnectionReadinessBody {
  standard_reference?: string | null;
  standard_evidence?: string | null;
  under_special_control?: boolean | null;
  unable_to_connect?: boolean | null;
  branch_notice_on?: string | null;
  branch_notice_reference?: string | null;
  /** A pack option value of the template's `notice_sent_by` field, or null. */
  notice_sent_by?: string | null;
  database_kept_evidence?: string | null;
  readiness_evidence?: string | null;
  evidence_attachment_ids?: string[];
  notes?: string | null;
  version?: number;
}

// ── Sprint 63.24.0 Slice A — controlled-testing case views and bodies (plan §2) ──
// The stored case is `ControlledTestingCase` (shared). `derived` is what the
// route computes on every read and never stores (case-service `view`).
export type ControlledTestingProcedureState =
  | 'declared'
  | 'lifecycle_absent'
  | 'lifecycle_malformed'
  | 'procedure_not_found'
  | 'framework_not_installed';
export type ControlledTestingClockSuppressedReason =
  | 'framework_not_operational'
  | 'procedure_not_declared'
  | 'deadline_not_in_force'
  | 'deadline_activation_unknown';
export type ControlledTestingTransitionalStatus =
  import('@vn-grc/shared').ControlledTestingTransitionalTag['status'];
export interface ControlledTestingDerived {
  dossier_checklist: import('@vn-grc/shared').ControlledTestingDossierChecklistView;
  results_report_guidance: import('@vn-grc/shared').ControlledTestingAnnexGuidanceView;
  applicant_kind: import('@vn-grc/shared').ControlledTestingApplicantKindView;
  dossier_track: import('@vn-grc/shared').ControlledTestingDossierTrackView;
  filing_requirements: {
    customer_group_required: boolean;
    results_report_required: boolean;
  } | null;
  results_report_held: boolean | null;
  transition_rules_declared: boolean;
  declared_transitions: import('@vn-grc/shared').ProcedureTransition[];
  declared_events: import('@vn-grc/shared').ProcedureLifecycleEvent[];
  lifecycle_event_verification: import('@vn-grc/shared').ControlledTestingLifecycleEventVerification[];
  recording_scope: {
    status: 'not_declared' | 'pending_anchor' | 'verified' | 'review_required' | 'conflict';
    anchor_date: string | null;
    source_ref: string | null;
  };
  procedure_state: ControlledTestingProcedureState;
  declared_state_ids: string[];
  state_declared: boolean | null;
  framework_operational: boolean;
  clock_suppressed_reason: ControlledTestingClockSuppressedReason | null;
  clocks: ControlledTestingClockOutput[];
  /** The application form's title as the installed record authors it; `null` when it authors none. */
  application_form_title: ControlledTestingLabel | null;
  transitional: import('@vn-grc/shared').ControlledTestingTransitionalTag;
  channel_declared: boolean | null;
  completeness_reasons: string[];
  as_of: string;
  /**
   * Sprint 62.51.0 (CL-3) — the organisation outcome filings the installed
   * procedure offers (`stop`, `completion`); absent on the procedure reads both.
   * A filing not listed is refused by the server (409 `OUTCOME_FILING_NOT_DECLARED`).
   * Optional here so a server older than the field reads as both.
   */
  outcome_filings_offered?: ControlledTestingOutcomeFiling[];
  /** Sprint 62.51.0 (CL-3) — the register surfaces where the outcome is recorded instead. */
  outcome_recorded_on?: string[];
  /**
   * Sprint 62.51.0 Remediation W (U1) — which case-page sections the installed
   * records declare for this case. Optional so a server older than the field
   * draws every section, as before.
   */
  declared_sections?: ControlledTestingDeclaredSections;
}
export interface ControlledTestingCaseView extends ControlledTestingCase {
  derived: ControlledTestingDerived;
  /** Slice E: the child's filing and sign-off reads. Present on a child case only. */
  child?: ControlledTestingChildDerived | null;
}
/** Slice E: the body an adjustment or an extension is opened with. The procedure is the server's, never the client's. */
/** One file held under a case for its stop or completion. The bytes are sent base64 encoded. */
export interface ControlledTestingOutcomeEvidenceBody {
  filename: string;
  mime_type: string;
  file_data_base64: string;
}
export interface ControlledTestingOutcomeEvidenceRef {
  file_id: string;
  filename: string;
  mime_type: string;
  size: number;
  uploaded_at: string;
}
export interface ControlledTestingStopFileBody {
  ground_code?: string;
  expected_version: number;
  application_file_id: string;
  results_report_file_id: string;
  stop_plan_file_id: string;
  stop_date: string;
}
export interface ControlledTestingRevocationBody {
  ground_code?: string | null;
  expected_version: number;
  decision_ref: string;
  decided_on: string;
  decision_text: string;
  evidence_file_ids: string[];
}
export interface ControlledTestingCompletionFileBody {
  expected_version: number;
  evaluation_report_file_id: string;
  results_report_file_id: string;
}
export interface ControlledTestingCertificateBody {
  expected_version: number;
  file_id: string;
  copy_source: 'authority_copy';
  copy_dated_on: string;
}
export interface ControlledTestingClosureBody {
  expected_version: number;
  notice?: { file_id: string; notified_on: string; recipients: string[]; channel: string };
  liquidation?: { file_id: string; recorded_on: string };
  /** The anchor dates a person enters, each with its evidence reference (owner ruling 2026-10-11). Nothing computed is sent. */
  anchors?: {
    stop_decision?: { date: string; evidence_ref: string };
    settlement_completion?: { date: string; evidence_ref: string };
  };
}
export interface ControlledTestingAnchorCorrectionBody {
  expected_version: number;
  value: { date: string; evidence_ref: string };
  reason: string;
}
export interface ControlledTestingDirectionRecordBody {
  expected_version: number;
  kind: ControlledTestingDirectionKind;
  direction_ref: string;
  authority_name: string;
  scope_description: string;
  received_at: string;
  receipt_evidence_refs: string[];
}

export interface OpenControlledTestingChildCaseBody {
  kind: ControlledTestingChildKind;
  application_form_id: string;
  notes?: string;
}
/** Slice E: the child list's R-23 envelope, with the eligibility answer for each kind. */
export interface ControlledTestingChildListResponse extends ControlledTestingCaseListResponse {
  eligibility: Record<ControlledTestingChildKind, ControlledTestingChildEligibility>;
}
export interface ControlledTestingCaseListResponse {
  data: ControlledTestingCaseView[];
  total: number;
  page: number;
  page_size: number;
}
/** One installed framework, as the open-case picker reads it. `can_open_cases` is the open route's own answer. */
export interface ControlledTestingFrameworkRow {
  framework_id: string;
  framework_code: string;
  display_name: ControlledTestingLabel | null;
  can_open_cases: boolean;
  /** The framework's own record declares a procedure with a lifecycle (a case can sit on it). */
  has_case_procedures?: boolean;
  applicant_kinds?: Array<{
    application_form_id: string;
    declaration: import('@vn-grc/shared').ControlledTestingApplicantKindView;
  }>;
}
export interface ControlledTestingFrameworkListResponse {
  data: ControlledTestingFrameworkRow[];
  total: number;
  page: number;
  page_size: number;
}
export interface OpenControlledTestingCaseBody {
  framework_code: string;
  procedure_id: string;
  application_form_id: string;
  applicant_name: string;
  applicant_kind?: string | null;
  notes?: string;
}
export interface UpdateControlledTestingCaseBody {
  expected_version: number;
  applicant_name?: string;
  applicant_kind?: string | null;
  notes?: string | null;
  channel_code?: string;
  usb_recorded?: boolean | null;
  received_on?: string | null;
}
export interface RecordControlledTestingTransitionBody {
  expected_version: number;
  to_state_id: string;
  entered_on: string;
  evidence_refs: string[];
}

export interface RecordControlledTestingEventBody {
  expected_version: number;
  event_id: string;
  occurred_on: string;
  evidence_refs: string[];
}

export interface RecordControlledTestingFilingBody {
  expected_version: number;
  filing_reference: string;
  submitted_on: string;
  customer_group: string;
}
export interface RecordControlledTestingChildFilingBody {
  expected_version: number;
  filing: {
    approval_id: string;
    filing_reference: string;
    submitted_on: string;
    customer_group: string;
  };
}

// ── Sprint 63.24.0 Slice B — personnel eligibility evidence (B1–B4) ──
// The stored record is `ControlledTestingPersonnelRecord` (shared); each read
// returns `ControlledTestingPersonnelRecordView`, which adds `derived` (role
// applicability, the currency answer). A write names the whole roles list, so a
// file a role keeps is named again (the server replaces the roles).
export interface ControlledTestingPersonnelRoleBody {
  role_code: string;
  qualification_basis_code?: string | null;
  from: string;
  to?: string | null;
}
export interface ControlledTestingPersonnelRoleWriteBody extends ControlledTestingPersonnelRoleBody {
  evidence_attachment_ids: string[];
}
export interface CreateControlledTestingPersonnelBody {
  person_name: string;
  statement_signed_on?: string | null;
  roles?: ControlledTestingPersonnelRoleBody[];
}
/**
 * The P2P conditions of the legal representative and the general director: one declared code
 * per condition, `null` when not recorded. The criminal-record condition was removed at pack
 * 1.0.8. A code stored under 1.0.7 is not named by a save, so the server keeps it; only an
 * explicit `conviction_code: null` clears it (the case dialog's clear control sends exactly that).
 */
export interface ControlledTestingLegalRepresentativeConditionsBody {
  nationality_code?: string | null;
  conviction_code?: string | null;
  sanction_code?: string | null;
}
export interface UpdateControlledTestingPersonnelBody {
  expected_version: number;
  condition_answers?: Record<
    string,
    { option_code: string | null; evidence_reference: string | null }
  >;
  person_name?: string;
  statement_signed_on?: string | null;
  roles?: ControlledTestingPersonnelRoleWriteBody[];
  criminal_record_outcome?: { outcome_code: string; outcome_on: string } | null;
  legal_representative_conditions?: ControlledTestingLegalRepresentativeConditionsBody | null;
}
export interface ControlledTestingPersonnelEvidenceBody {
  filename: string;
  mime_type: string;
  file_data_base64: string;
}
export interface ControlledTestingPersonnelEvidenceRef {
  file_id: string;
  filename: string;
  mime_type: string;
  size: number;
  uploaded_at: string;
}
export interface ControlledTestingPersonnelListResponse {
  data: ControlledTestingPersonnelRecordView[];
  total: number;
  page: number;
  page_size: number;
}

// Sprint 63.24.0 Slice C — the solution record. Every write names what it changes; the
// server checks each declared value. A create names no version; a change names `expected_version`.
export interface CreateControlledTestingSolutionBody {
  solution_type_code?: string | null;
  customer_group?: string | null;
  security_level?: ControlledTestingSecurityLevelLink | null;
}
export interface UpdateControlledTestingSolutionBody extends CreateControlledTestingSolutionBody {
  expected_version?: number;
  technical_statement_attachment_id?: string | null;
}
export interface ControlledTestingSolutionEvidenceBody {
  filename: string;
  mime_type: string;
  file_data_base64: string;
}
export interface ControlledTestingSolutionEvidenceRef {
  file_id: string;
  filename: string;
  mime_type: string;
  size: number;
  uploaded_at: string;
}

// Sprint 63.24.0 Slice D — the authoritative-source records. A create names the requirement;
// a change names `expected_version` and only what it changes. The server refuses any other key.
export interface CreateControlledTestingRegistryBody {
  /** The requirement's id as the installed pack spells it (the catalogue names it). */
  requirement_id: string;
  source?: ControlledTestingRegistrySource | null;
  personnel_record_id?: string | null;
}
export interface UpdateControlledTestingRegistryBody {
  outcome_data_correction?: { reason: string; reference: string };
  expected_version: number;
  personnel_record_id?: string | null;
  retrieved_on?: string | null;
  applicant_copy_attachment_id?: string | null;
  failure_follow_up?: {
    kind: 'authority_information_request' | 'applicant_supplement';
    requested_on: string;
    reference: string | null;
    attachment_id: string | null;
  };
  source?: ControlledTestingRegistrySource | null;
  outcome_code?: string | null;
  outcome_on?: string | null;
  supplement_on?: string | null;
  supplement_reference?: string | null;
  supplement_attachment_id?: string | null;
  decision_on?: string | null;
  decision_text?: string | null;
}
export interface ControlledTestingRegistryEvidenceBody {
  filename: string;
  mime_type: string;
  file_data_base64: string;
}
export interface ControlledTestingRegistryListResponse {
  data: ControlledTestingRegistryRecordPanel[];
  total: number;
  page: number;
  page_size: number;
  requirements: ControlledTestingRegistryRequirementView[];
}

// ── Sprint 62.38.0 — Supervisory Reporting register bodies ──
export interface CreateSupervisoryIndicatorBody {
  section: string;
  indicator_code: string;
  indicator_name: string;
  periodicity: string;
  commencement: string;
  source_system?: string | null;
  data_owner_user_id?: string | null;
  calculation_rule?: string | null;
  linked_registers?: string | null;
  evidence_reference?: string | null;
  approver_user_id?: string | null;
  last_reviewed_at?: string | null;
}
/**
 * fix-sprint-20260925-2123 F4 — the record's `updated_at` as the form loaded
 * it. The server refuses the PATCH with `SUPERVISORY_CONCURRENT_CHANGE` (409,
 * `details.current_updated_at`) when the record no longer carries it. Every
 * edit form sends it; the evidence-file dialog's list replacement does not.
 */
export interface SupervisoryExpectedVersion {
  expected_updated_at?: string;
}
export type UpdateSupervisoryIndicatorBody = Partial<
  Omit<CreateSupervisoryIndicatorBody, 'section' | 'indicator_code'>
> &
  SupervisoryExpectedVersion;
export interface CreateSupervisoryCheckBody {
  section: string;
  reporting_period: string;
  /**
   * Sprint 62.45.0 — optional: the server requires it only when no installed
   * deadline record clocks the check's submission period (400
   * `SUPERVISORY_DEADLINE_SOURCE_REQUIRED`).
   */
  deadline_source?: string | null;
  /** Sprint 62.45.0 — a `submission_period` option value in force on `period_end`. */
  submission_period?: string | null;
  period_end?: string | null;
  submitted_on?: string | null;
  receipt_reference?: string | null;
  signed_file_reference?: string | null;
  form_instance_ids?: string[];
  completeness_confirmed?: boolean;
  cross_indicator_checks?: string | null;
  register_reconciliation?: string | null;
  discrepancies?: string | null;
  reported_complaint_count?: number | null;
  register_complaint_count?: number | null;
  reported_fraud_count?: number | null;
  register_fraud_count?: number | null;
  reconciliation_explanation?: string | null;
  interruption_rows?: SupervisoryInterruptionRow[];
  /** Slice H (AC15) — a `report` option in force on `period_end`, filed by a held role. */
  report?: string | null;
  preparer_user_id?: string | null;
  signatory_name?: string | null;
  /** Slice H (AC15) — a `channel` option in force on `period_end`. */
  channel?: string | null;
  /** Slice H (AC15) — HH:MM, organisation time. */
  submitted_time?: string | null;
}
/**
 * Evidence files are bound by PATCH only (Slice H, AC23): a create body never
 * carries `*_attachment_ids`, and the server refuses one that does.
 */
export type UpdateSupervisoryCheckBody = Partial<Omit<CreateSupervisoryCheckBody, 'section'>> &
  SupervisoryExpectedVersion & {
    evidence_attachment_ids?: string[];
  };
/**
 * Slice H (AC16) — `POST /submission-checks/:checkId/submission`, on a draft
 * or an approved check. Exactly these keys: the approval, the counts and the
 * reconciliation are not reachable through it.
 */
export interface RecordSupervisorySubmissionBody {
  expected_updated_at?: string;
  submitted_on?: string | null;
  submitted_time?: string | null;
  receipt_reference?: string | null;
  signed_file_reference?: string | null;
  signatory_name?: string | null;
  channel?: string | null;
  evidence_attachment_ids?: string[];
}
/**
 * Sprint 62.38.0 — a data correction; Slice H (AC19) — also a supervision
 * unit's request. `section`, `defect_type` and `indicator_codes` are absent
 * (null) on a request whose kind concerns no supplied data.
 */
export interface CreateSupervisoryCorrectionBody {
  section?: string | null;
  indicator_codes?: string[];
  defect_type?: string | null;
  notified_on: string;
  root_cause?: string | null;
  correction?: string | null;
  supplied_on?: string | null;
  lineage_updated?: boolean;
  request_kind?: string | null;
  request_reference?: string | null;
  scope?: string | null;
  owner_user_id?: string | null;
  deadline_set_by_request?: string | null;
  response_summary?: string | null;
}
export type UpdateSupervisoryCorrectionBody = Partial<
  Omit<CreateSupervisoryCorrectionBody, 'section'>
> &
  SupervisoryExpectedVersion & { evidence_attachment_ids?: string[] };
export interface CreateSupervisoryInspectionBody {
  inspection_basis: string;
  notice_date?: string | null;
  scope: string;
  personnel_assigned?: string | null;
  equipment_provided?: string | null;
  access_approvals?: SupervisoryAccessApproval[];
  systems_examined?: string[];
  data_disclosed?: SupervisoryDataDisclosed[];
  handling_decision_reference?: string | null;
  /** Slice H (AC24) */
  decision_reference?: string | null;
  decision_received_on?: string | null;
  minutes_reference?: string | null;
  follow_up_actions?: SupervisoryFollowUp[];
}
export type UpdateSupervisoryInspectionBody = Partial<CreateSupervisoryInspectionBody> &
  SupervisoryExpectedVersion & {
    evidence_attachment_ids?: string[];
  };
/** Sprint 62.45.0 — the period evidence pack (`POST /submission-checks/export`). */
export interface SupervisoryPeriodExportBody {
  format: RenderableExportFormat;
  period_end: string;
  section?: string;
  /** Gate E12.5 pass 1 — the language the file is written in (EN or VI). */
  locale?: SupervisoryExportLocale;
}
/**
 * Sprint 62.45.0 — an interruption notice record. `notice_sent_by` is the
 * server's record of who recorded the send and is never sent; `incident_id`
 * identifies the record and is accepted on create only.
 */
export interface UpdateSupervisoryInterruptionNoticeBody {
  reporting_role?: string;
  affected_system_or_service?: string | null;
  /** An instant WITH an offset (`toIsoInstant`). */
  notice_sent_at?: string | null;
  notice_content?: string | null;
  notice_evidence_reference?: string | null;
  report_submitted_on?: string | null;
  report_evidence_reference?: string | null;
  /** Slice H (AC21) — filled forms (the incident report form) the notice links. */
  form_instance_ids?: string[];
}
export interface CreateSupervisoryInterruptionNoticeBody extends UpdateSupervisoryInterruptionNoticeBody {
  incident_id: string;
  reporting_role: string;
}
/** Slice H (AC23) — the evidence files, bound by PATCH only. */
export interface BindSupervisoryInterruptionEvidenceBody {
  notice_evidence_attachment_ids?: string[];
  report_evidence_attachment_ids?: string[];
}
/** Sprint 62.45.0 — a change or contract notice. */
export interface CreateSupervisoryEventNoticeBody {
  event_kind: string;
  description?: string | null;
  event_date: string;
  sent_on?: string | null;
  evidence_reference?: string | null;
}
export type UpdateSupervisoryEventNoticeBody = Partial<CreateSupervisoryEventNoticeBody> & {
  evidence_attachment_ids?: string[];
};
/** Slice H (AC22) — a recommendation a supervision letter makes (no inspection). */
export interface CreateSupervisoryRecommendationBody {
  source_reference: string;
  received_on: string;
  recommendation: string;
  owner_user_id?: string | null;
  deadline?: string | null;
  measures?: string | null;
  report_sent_on?: string | null;
  evidence_reference?: string | null;
  internal_closure_on?: string | null;
}
export type UpdateSupervisoryRecommendationBody = Partial<CreateSupervisoryRecommendationBody> & {
  evidence_attachment_ids?: string[];
};

// ── Sprint 62.39.0 — Cross-Border Identity Exchange register bodies ──
export interface CreateIdentityExchangeBody {
  identity_provider: string;
  identity_provider_country: string;
  relying_party: string;
  relying_party_country: string;
  purpose: string;
  direction: string;
  attributes?: IdentityExchangeAttribute[];
  minimisation_confirmed?: boolean;
  transfer_assessment_id?: string | null;
  authorisation_state?: string;
  authorisation_document_reference?: string | null;
  authorisation_issuing_authority?: string | null;
  authorisation_issued_on?: string | null;
  authentication_outcome_recording?: string | null;
  reconciliation_method?: string | null;
  reconciliation_result?: string | null;
  transaction_log_location?: string | null;
  retention_period?: string | null;
  identity_profile_id?: string | null;
  status?: 'modelled' | 'testing';
}
/**
 * fix-sprint-20260925-2123 F4 — `expected_updated_at` is required: the record's
 * `updated_at` as the edit form loaded it. A record changed since is refused
 * with `IDENTITY_EXCHANGE_CONCURRENT_CHANGE` (409, `details.current_updated_at`).
 */
export type UpdateIdentityExchangeBody = Partial<Omit<CreateIdentityExchangeBody, 'status'>> & {
  expected_updated_at: string;
};
export interface ProviderNotificationBody {
  channel: string;
  sent_at: string;
  receipt_reference?: string | null;
  notes?: string | null;
}
export interface ProviderPeriodicReportBody {
  reporting_year: number;
  channel: string;
  filed_at: string;
  receipt_reference?: string | null;
  notes?: string | null;
}
export interface ProviderAdHocReportBody {
  authority_request_id: string;
  requested_at: string;
  answered_at?: string | null;
  notes?: string | null;
}

/** Sprint 62.57.0 (Slice F, C.1) — `POST /dossier-filings/:id/receipt-evidence`. */
export interface RecordDossierReceiptEvidenceBody {
  event: DossierReceiptEvent;
  /** `YYYY-MM-DD`. */
  received_on: string;
  /** D19 — the postmark of a postal filing, `YYYY-MM-DD`. */
  postmarked_on?: string;
  communication_id?: string;
  correction_of?: string;
  correction_note?: string;
  /** The filing's `updated_at` as last read; a stale value answers 409. */
  expected_updated_at: string;
  /**
   * Sprint 62.57.0 (Slice C, D31) — required with `completed_dossier_received`
   * (the completion submission received), refused with any other event.
   */
  completion_submission_id?: string;
  /**
   * Sprint 62.57.0 Slice E (D54, L-13) — the certificate application's receipt
   * route only: *no deficiency notice*, with `complete_dossier_received`. The
   * filing route is never sent it (the dialog sets it only where its caller
   * offers the choice).
   */
  no_deficiency_notice?: true;
}

/** Sprint 62.57.0 (Slice A, C.1) — `POST /dossier-filings/:id/handling-authority-statements`. */
export interface RecordHandlingAuthorityStatementBody {
  basis: DossierHandlingStatementBasis;
  communication_id: string;
  /** A `may_transfer_to` option, or null when the person names another body. */
  option_id: string | null;
  body_name: string;
  correction_of?: string;
  correction_note?: string;
}

/** Sprint 62.57.0 (Slice B, C.1) — `POST /dossier-filings/:id/outcome`. */
export interface RecordDossierResultBody {
  result_option_id: string;
  /** `YYYY-MM-DD`, the date on the notice. */
  result_date: string;
  /** `YYYY-MM-DD`, the organisation's receipt of the notice. */
  notice_received_at: string;
  communication_id: string;
  notes: string;
  expected_updated_at: string;
  /**
   * E12.5 P3-10 — the `result_id` of the CURRENT result this one corrects.
   * Refused (409 `DOSSIER_RESULT_CORRECTION_NOT_OPEN`) once anything follows it.
   */
  correction_of?: string;
  /** Required with `correction_of` (1–2000 characters). */
  correction_note?: string;
  /**
   * Sprint 62.57.0 (Slice C, D25) — the person states the cited communication
   * asks the organisation to complete the dossier (default false).
   */
  completion_requested?: boolean;
}

/** Sprint 62.57.0 (Slice B, B-7) — `POST /dossier-filings/:id/result-supplement-requests`. */
export interface RecordResultSupplementRequestBody {
  communication_id: string;
  /** Each item the notice names; an item with an owner becomes a task (B-7). */
  requested_items: Array<{ text: string; owner_id: string | null }>;
  stated_period_text?: string;
  /** `YYYY-MM-DD`, only the notice's own date (`operator_stated`). */
  stated_deadline?: string;
  expected_updated_at: string;
}

/** Sprint 62.57.0 (Slice B, AC-B6) — `POST /dossier-filings/:id/failure-follow-up`. */
export interface RecordFailureFollowUpBody {
  reasons: string;
  remediation: {
    summary: string;
    actions: Array<{ title: string; owner_id: string; due_date: string }>;
  };
  enforcement_risk_note?: string;
  correction_note?: string;
  expected_updated_at: string;
}

/** Sprint 62.57.0 (Slice B, D20) — `POST /dossier-filings/:id/authority-stated-result-date`. */
export interface RecordAuthorityStatedResultDateBody {
  /** `YYYY-MM-DD`, as the communication states it. */
  stated_date: string;
  communication_id: string;
  correction_of?: string;
  correction_note?: string;
  expected_updated_at: string;
}

/** Sprint 62.57.0 (Slice C, D25, §11.4.3) — `POST /dossier-filings/:id/completion-requests`. */
export interface RecordCompletionRequestBody {
  /** A Slice R communication on the same filing. */
  communication_id: string;
  /** The standing completion request this corrects. */
  correction_of?: string;
  /** Required with `correction_of` (1–2,000 characters). */
  correction_note?: string;
  expected_updated_at: string;
}

/**
 * Gate E12.5 pass 1 (BE-5) — the same route withdraws a standing completion
 * request recorded in error: a correction that cites no communication, with a
 * required note. A withdrawal itself cannot be corrected (409).
 */
export interface WithdrawCompletionRequestBody {
  correction_of: string;
  /** 1–2,000 characters. */
  correction_note: string;
  withdrawn: true;
  expected_updated_at: string;
}

/** Sprint 62.57.0 (Slice C, D28) — `POST /dossier-filings/:id/supplement-request`. */
export interface RecordPreResultSupplementRequestBody {
  requested_items: string[];
  /** The communication that asks for the supplement; never sent with `received_at`. */
  communication_id?: string;
  /** D25 — the person states the communication asks for completion; needs `communication_id`. */
  completion_requested?: boolean;
  /** Legacy: a typed receipt date with no communication (opens nothing, D28). */
  received_at?: string;
  /** `YYYY-MM-DD`, only a date the communication itself states. */
  response_deadline?: string;
}

/** Sprint 62.57.0 (Slice C, D30) — `POST /dossier-filings/:id/submit-supplement` (strict). */
export interface SubmitDossierSupplementBody {
  response_notes?: string;
  /** `YYYY-MM-DD`; not in the future, not before the trigger's receipt. */
  submitted_on?: string;
  expected_updated_at?: string;
}

/** Sprint 62.57.0 (Slice D, D35) — the revision body of `change-filing` (unchanged). */
export interface ChangeFilingRevisionBody {
  title?: string;
  reason?: string;
}

/** Sprint 62.57.0 (Slice D, D35) — the update body of `change-filing`. */
export interface ChangeFilingUpdateBody {
  mode: 'update';
  /** The update form instance the page created first under the forms grant. */
  form_instance_id: string;
  title?: string;
  sent_to: UpdateAddresseeBodyChoice;
  competent_authority: UpdateAddresseeBodyChoice;
  /** 1–2,000; required when either body differs from the original's handling body. */
  difference_reason?: string;
}

/** Sprint 62.57.0 (Slice D, D36) — `PUT /dossier-filings/:id/update-addressee`. */
export interface UpdateAddresseeBody {
  sent_to: UpdateAddresseeBodyChoice;
  competent_authority: UpdateAddresseeBodyChoice;
  difference_reason?: string;
  /** Only while unset: a legacy update names the filing it updates. */
  updates_filing_id?: string;
  expected_updated_at: string;
}

/** Sprint 62.57.0 (AC-F5, D38) — one option of the list's *Governing text* filter. */
export interface GoverningTextFilterOption {
  window_id: string;
  label: { en: string; vi: string } | null;
  declaration_id?: string | null;
  framework_code?: string | null;
}

/** Sprint 62.57.0 (AC-F5, D38) — `GET /dossier-filings/requirement-links`. */
export interface DossierRequirementLink {
  requirement_id: string;
  filing_count: number;
}

export interface DossierFirstFilingEnvelope {
  data: DossierFirstFilingView;
}

export interface RegisteredParticularsChangeEnvelope {
  data: RegisteredParticularsChangeView;
}

/**
 * The body the first-filing routes accept, and nothing more.
 *
 * `RecordDossierFirstFilingSchema` is `.strict()` and deliberately does NOT
 * accept `correction_note`: the first filing is write-once, so there is never a
 * restatement for a note to explain. A body that carried one would be refused,
 * not ignored — which is why this alias exists rather than reusing
 * `RecordCommencementBody`, whose optional note would compile here and fail at
 * the server.
 */
export type RecordDossierFirstFilingBody = RecordDossierFirstFilingRequest;

/** The body the particulars-change routes accept. `change_description` is required. */
export type RecordRegisteredParticularsChangeBody = RecordRegisteredParticularsChangeRequest;

// ── Sprint 63.23.0 — MCP remote server: the C.1 contract ──────────────────
//
// The view types are Stream B's, from `@vn-grc/shared` (`types/mcp.ts`), so the
// page and the server read one definition. Lists are the flat
// `PaginatedResponse` every paginated route returns (R-23).

/** The `{ data }` envelope's payload; a bare payload passes through. */
function envelopeData<T>(res: unknown): T {
  if (res && typeof res === 'object' && 'data' in res) {
    return (res as { data: T }).data;
  }
  return res as T;
}

/** A paginated list response with `data` always an array (R-23). */
function normalisePage<T>(
  res: Partial<PaginatedResponse<T>> | null | undefined,
): PaginatedResponse<T> {
  const data = Array.isArray(res?.data) ? res!.data : [];
  return {
    data,
    total: typeof res?.total === 'number' ? res.total : data.length,
    page: typeof res?.page === 'number' ? res.page : 1,
    page_size: typeof res?.page_size === 'number' ? res.page_size : data.length,
  };
}

function pageQuery(params?: { page?: number; page_size?: number }): Record<string, unknown> {
  return { page: params?.page, page_size: params?.page_size };
}

/**
 * Keeps the first-run setup session for the tab, so a reload mid-wizard can still
 * finish setup. Returns false when storage is unavailable: the client then keeps
 * the session in memory for this page only.
 */
function persistSetupSession(session: string): boolean {
  try {
    sessionStorage.setItem(SETUP_SESSION_STORAGE_KEY, session);
    return true;
  } catch {
    return false;
  }
}
/** sessionStorage key of the first-run setup session (fix-sprint-20261102-0448 D1). */
const SETUP_SESSION_STORAGE_KEY = 'complianceone.setup_session';
const API_BASE = '/api/v1';

// ── Sprint 46.2.0 — sector overlay client types ───────────────────
export interface SectorOverlayCatalogEntry {
  pack_code: string;
  framework_code: string;
  display_name: string;
  description: string;
  version: string;
  released_at: string;
  /** The instrument's effective date as the pack manifest states it; empty when it states none. */
  effective_date?: string;
  /** The instrument's issue date as the pack manifest states it; empty when it states none. */
  issued_date?: string;
  /**
   * As the pack manifest states it: a string, or a locale map like the framework
   * records' `authority_name`. Read it through `extractLocale`.
   */
  authority_name: TranslatableField;
  jurisdiction_code: string;
  framework_type: string;
  sector?: Sector;
  applicability_basis?: string;
  direct_scope: string[];
  optional_pack: true;
  /**
   * The register selectors the pack DECLARES (deadline `event_type`s and form
   * `register_surface`s). The screens they open come from the frontend's
   * registry keyed by these values — a pack declares no routes.
   */
  register_surfaces?: string[];
  /**
   * The other declarations screens read off the pack: the evidence-regime
   * domains and trust regimes its framework records declare, and the fine
   * areas it authors with the module codes they name.
   */
  pack_declarations?: SectorOverlayPackDeclarations;
  installed: boolean;
  installed_as: SectorOverlayInstallMode | null;
  installed_at: string | null;
  suggested_mode: SectorOverlayInstallMode;
}

export interface SectorOverlayPackDeclarations {
  evidence_domains: string[];
  trust_regimes: boolean;
  fine_area_count: number;
  fine_area_modules: string[];
}

export interface InstalledSectorOverlay {
  framework_id: string;
  framework_code: string;
  display_name: string | Record<string, string>;
  sector: Sector;
  direct_scope: string[];
  /** The mode the platform RUNS the framework in (Sprint 62.25.0 gate E12.5 pass 1, P2-2). */
  installed_as: SectorOverlayInstallMode;
  installed_at: string;
  /**
   * The anchor framework(s) whose mode this framework follows; non-empty means
   * its mode is changed on an anchor, and a change here is refused (409
   * `INSTALL_MODE_FOLLOWS_ANCHOR`).
   */
  mode_follows_anchor_codes?: string[];
}

export interface InstalledRegulatoryOverlay extends Omit<InstalledSectorOverlay, 'sector'> {
  sector?: Sector;
}

/**
 * Sprint 63.43.0 (AC2) — how an installed optional overlay stands for this
 * organisation, as `GET /sector-overlays/navigation` reports it (read through
 * the backend's `OptionalOverlayPolicy`, never from `installed_as`). Only an
 * `operational` overlay brings views into the Sector Overlays menu.
 */
export type SectorOverlayNavigationState = 'operational' | 'reference' | 'superseded';

/** One installed optional overlay in the navigation projection (`navigation-service.ts`). */
export interface SectorOverlayNavigationEntry {
  pack_code: string;
  framework_code: string;
  /** The overlay's name as the catalogue states it; read it through `extractLocale`. */
  display_name: string;
  sector?: Sector;
  framework_type: string;
  state: SectorOverlayNavigationState;
  /** The register selectors the pack declares, as on the catalogue entry. */
  register_surfaces: string[];
  /** The other declarations screens read off the pack, as on the catalogue entry. */
  pack_declarations: SectorOverlayPackDeclarations;
}

/**
 * Sprint 63.43.0 (AC2) — the navigation projection. A plain object, NOT a
 * paginated envelope: read `overlays`, never `data` (R-23 does not apply).
 */
export interface SectorOverlayNavigation {
  /** Always `true` when this answers: the route is behind the Enterprise guard. */
  entitled: true;
  overlays: SectorOverlayNavigationEntry[];
}

// Sprint 46.3.0 — phased deadline engine client types
export interface PhasedObligationEarlyImplementationRecord {
  org_id: string;
  framework_code: string;
  deadline_code: string;
  effective_at: string;
  evidence_attachment_id?: string;
  reason_note?: string;
  actor_id: string;
  timestamp: string;
}

/** Helper: read a File into a base64 data URL body for upload. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result.replace(/^data:[^;]+;base64,/, ''));
      } else {
        reject(new Error('Unexpected FileReader result'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('File read error'));
    reader.readAsDataURL(file);
  });
}
const RETRYABLE_STATUSES = [408, 429, 502, 503, 504];
const IDEMPOTENT_METHODS = ['GET', 'HEAD', 'OPTIONS', 'PUT'];
const MAX_RETRIES = 3;
const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 8000;
const REQUEST_TIMEOUT_MS = 30_000;
/** In-flight GET request deduplication cache. */
const inflightGets = new Map<string, Promise<unknown>>();
function deduplicateGet<T>(key: string, executor: () => Promise<T>): Promise<T> {
  const existing = inflightGets.get(key);
  if (existing) return existing as Promise<T>;
  const promise = executor().finally(() => {
    inflightGets.delete(key);
  });
  inflightGets.set(key, promise);
  return promise;
}
/**
 * What a client may send when registering or amending a regulated offering.
 *
 * Everything on the record except `obligations`, whose entries carry the two
 * identifying fields only — the requirement title is read from the installed
 * framework by the server and rejected in the request body (gate E12.5 F11).
 */
type RegulatedOfferingWriteRequest = Partial<Omit<RegulatedOfferingRegistration, 'obligations'>> & {
  obligations?: RegulatedOfferingObligationRequest[];
};

/**
 * API client for the AesirX ComplianceOne backend.
 * Handles all HTTP communication with CSRF, retry, deduplication, and AbortController support.
 */
/**
 * Sprint 62.19.0 gate E12.5 pass 1, P2-16 — the two licence bodies, typed from
 * the shared record rather than `unknown`. Create takes what the operator
 * records; `issuing_authority` is optional because a declared kind takes the
 * pack's authority server-side and only an undeclared kind carries the
 * operator's own text. Update is the PATCH subset the detail page offers,
 * every field optional.
 */
// ── Sprint 62.54.0: applications, fee lines, decisions, sender-name certificates ──
export interface CreateLicenceFeeApplicationBody {
  /** A value of the installed fee schedule's procedure vocabulary (licence side). */
  procedure: string;
  application_reference: string;
  filed_on?: string | null;
  licence_id?: string | null;
  notes?: string | null;
}
export type UpdateLicenceFeeApplicationBody = Partial<CreateLicenceFeeApplicationBody> & {
  /** The `updated_at` the page read; a mismatch answers 409 `STALE_WRITE`. */
  expected_updated_at?: string;
};
/** The decision route's body (`sector_licence_decision:write`). */
export interface RecordLicenceFeeDecisionBody {
  status: FeeApplicationDecisionStatus;
  decided_on?: string | null;
  reference?: string | null;
  /** A file uploaded under `licence_fee_decision_document` against this application. */
  document_attachment_id?: string | null;
  expected_updated_at?: string;
}
/**
 * A fee (or charge) line. `amount_paid` is what a person entered; the
 * scheduled amount is derived server-side from the row and quantity.
 * `receipt_attachment_id` is refused at create and bound on PATCH to a file
 * uploaded against the saved line.
 */
export interface CreateFeeLineBody {
  row_value: string | null;
  quantity?: number;
  amount_paid: number | null;
  payer: string;
  paid_on: string | null;
  collecting_body_value?: string | null;
  collecting_body?: string | null;
  receipt_reference?: string | null;
  notes?: string | null;
}
export type UpdateFeeLineBody = Partial<CreateFeeLineBody> & {
  receipt_attachment_id?: string | null;
  expected_updated_at?: string;
};
export interface CreateSenderNameCertificateBody {
  sender_name: string;
  /** A value of the installed procedure vocabulary on the sender-identity side. */
  procedure: string;
  application_reference?: string | null;
  filed_on?: string | null;
  certificate_reference?: string | null;
  granted_on?: string | null;
  valid_until?: string | null;
  issuing_body?: string | null;
  notes?: string | null;
}
export type UpdateSenderNameCertificateBody = Partial<CreateSenderNameCertificateBody> & {
  expected_updated_at?: string;
};

export type CreateSectorLicenceBody = Pick<
  SectorLicence,
  | 'licence_kind'
  | 'reference_number'
  | 'scope_description'
  | 'covered_services'
  | 'issued_at'
  | 'expires_at'
  | 'status'
  | 'accountable_owner_user_id'
  | 'evidence_refs'
  | 'notes'
> & {
  issuing_authority?: string;
  framework_code?: string | null;
  pathway?: string | null;
  /** Sprint 62.24.0 (AC11) — which reading of the text the organisation relied on, and on whose advice. */
  reading_relied_on?: string | null;
  advice_source?: string | null;
};
export type UpdateSectorLicenceBody = Partial<
  Pick<
    SectorLicence,
    | 'licence_kind'
    | 'issuing_authority'
    | 'reference_number'
    | 'scope_description'
    | 'covered_services'
    | 'issued_at'
    | 'expires_at'
    | 'status'
    | 'accountable_owner_user_id'
    | 'evidence_refs'
    | 'notes'
    | 'framework_code'
    | 'pathway'
    | 'reading_relied_on'
    | 'advice_source'
  >
>;

/**
 * Sprint 65.1.0 — the compliance-operations coverage and export contracts.
 *
 * ## Why these are declared here and not imported
 *
 * `CoverageReport`, `CoverageRow`, `COVERAGE_READINESS_STATES` and
 * `COMPLIANCE_OPERATIONS_EXPORT_KINDS` are all exported from
 * `src/backend/src/modules/compliance-operations/`, and the frontend cannot
 * import from the backend workspace. The obligation, cadence and clock
 * contracts ARE in `@vn-grc/shared` and are imported above; the coverage
 * report is not, so a page that renders it has nothing to type-check against.
 *
 * Restated rather than typed `unknown`, because `unknown` on a rendered shape
 * moves every field access to a cast and puts the drift somewhere nobody is
 * looking. Recorded as a contract gap in the sprint report: the coverage report
 * belongs in `src/shared/src/types/compliance-operations.ts` beside the six
 * contracts that are already there, and when it moves, these declarations are
 * deleted in the same commit.
 */
export const COMPLIANCE_OPERATIONS_EXPORT_KIND_VALUES = ['work_programme', 'completeness'] as const;
export type ComplianceOperationsExportKind =
  (typeof COMPLIANCE_OPERATIONS_EXPORT_KIND_VALUES)[number];

/** Ordered as the backend orders them: nothing asked → everything recorded. */
/*
 * Sprint 65.1.0 — the coverage contract used to be RESTATED here.
 *
 * `coverage-service.ts` declared it and this file declared it again, so one
 * wire shape had two spellings with nothing to notice them drifting. That is
 * the `TaskSourceModule` defect this same sprint removed from
 * `task-management/schemas.ts`, where a hand-copied union meant a new member
 * type-checked everywhere while the API silently refused it.
 *
 * It now lives once, in `@vn-grc/shared`, imported by both ends.
 */

/**
 * Sprint 62.18.0 — the issued-record register's request and response shapes,
 * typed from the shared records rather than `unknown` (the 62.19.0 P2-16
 * lesson). The register never edits a version: there is no update body for
 * one, only the first-issue body and the supersede body that carries a
 * mandatory reason.
 */
export type CreateIssuedRecordScopeDecisionBody = Pick<
  IssuedRecordScopeDecision,
  | 'record_kind'
  | 'coverage'
  | 'organisation_name'
  | 'programmes'
  | 'roles'
  | 'foreign_curriculum_review'
  | 'legal_basis'
  | 'decided_by_user_id'
  | 'decided_at'
  | 'next_review'
>;
/** Current decision (`superseded_by_decision_id: null`) and the superseded ones, newest first. */
export interface IssuedRecordScopeDecisionsResponse {
  current: IssuedRecordScopeDecision | null;
  history: IssuedRecordScopeDecision[];
}
export type CreateIssuedRecordBody = Pick<
  IssuedRecord,
  'record_kind' | 'subject_identifier' | 'subject_display_ref' | 'transfer_source_institution'
> & {
  /** Sprint 63.31.0 (#415, Q3 (b)) — the Data Subject profile (`profile_id`) the record is for. */
  data_subject_profile_id?: string | null;
};
/** A register row — the record as stored; the list route computes nothing per row (R-26, reconciled). */
export type IssuedRecordListRow = IssuedRecord;
/** The detail read: the record, its current version and the counts behind the sections. */
export interface IssuedRecordDetail {
  record: IssuedRecord;
  current_version: IssuedRecordVersion | null;
  counts: { versions: number; consents: number; sharing_events: number; sharing_refusals: number };
}
/** The version fields the operator records; every instant MUST carry a zone (`toIsoInstant`). */
export type IssuedRecordVersionFields = Pick<
  IssuedRecordVersion,
  | 'period_label'
  | 'data_standard_version'
  | 'source_verification'
  | 'signatures'
  | 'issuance'
  | 'evidence_refs'
>;
export type IssueIssuedRecordVersionBody = IssuedRecordVersionFields;
export type SupersedeIssuedRecordVersionBody = IssuedRecordVersionFields & {
  version_kind: Exclude<IssuedRecordVersionKind, 'issued'>;
  reason: string;
};
export type CreateIssuedRecordConsentBody = Pick<
  IssuedRecordConsent,
  | 'subject_is_minor'
  | 'consenting_person'
  | 'representative_relationship'
  | 'representative_basis'
  | 'recipient'
  | 'purpose'
  | 'data_scope'
  | 'consent_given_at'
  | 'channel'
  | 'valid_until'
  | 'consent_workflow_id'
  | 'evidence_refs'
>;
/** `shared_at` MUST be an instant with a zone — pass `toIsoInstant(x) ?? x`. */
export type CreateIssuedRecordSharingEventBody = Pick<
  IssuedRecordSharingEvent,
  | 'shared_at'
  | 'recipient'
  | 'recipient_category'
  | 'purpose'
  | 'data_scope'
  | 'necessity_statement'
  | 'channel'
  | 'restrictions_acknowledged'
> & {
  basis_kind: IssuedRecordSharingBasisKind;
  basis_code: string | null;
  consent_id: string | null;
};
/** The newest entry of one evidence kind, with its age in whole days against org-calendar today. */
export interface IssuedRecordLatestEvidence {
  entry: IssuedRecordEvidenceEntry;
  age_days: number;
}
export interface IssuedRecordEvidenceResponse {
  latest: Record<IssuedRecordEvidenceKind, IssuedRecordLatestEvidence | null>;
  entries: IssuedRecordEvidenceEntry[];
}
export type CreateIssuedRecordEvidenceBody = Pick<
  IssuedRecordEvidenceEntry,
  | 'record_kind'
  | 'evidence_kind'
  | 'occurred_at'
  | 'owner_user_id'
  | 'attachment_ref'
  | 'summary'
  | 'result'
  | 'next_due'
>;
/**
 * One installed vendor-category pack template and, when answered for the
 * vendor, the newest instance bound to it — `null` states "not answered".
 * The shape is the backend's `VendorQuestionnaireRow` (R-26, reconciled).
 */
export interface IssuedRecordVendorQuestionnaire {
  /**
   * Sprint 62.58.0 gate E12.5 pass 2 (P2-1) — `template_not_owed` /
   * `template_not_owed_status` are the forms listing's draft flag for the
   * questionnaire's declaring framework; the panel chips the row from them.
   */
  template: TemplateNotOwedFlag & {
    template_id: string;
    schema_id: string;
    name: FormTemplate['title'];
    regulatory_framework_code: string;
    sections: FormTemplate['sections'];
  };
  instance: {
    instance_id: string;
    field_values: FormInstance['field_values'];
    status: FormInstance['status'];
    updated_at: string;
  } | null;
}

/**
 * Sprint 62.17.0 — the publishing module's request and response shapes,
 * reconciled field by field against `src/backend/src/modules/publishing/routes.ts`
 * (R-26). The register is mounted at `/publishing`; its list envelopes are
 * `{ data, total, page, page_size }` and its single reads `{ data }`.
 *
 * Only `/publications/:id/release-attempts` is PAGED. The deposit-submission
 * list, the discovery reads and the AI system register are unpaged and are
 * typed as bare `{ data: T[] }` so a caller cannot read a `total` the server
 * never sends.
 */
export type CreatePublicationBody = {
  publication_ref: string;
  title: string;
  format: string;
  gate_kind: string;
  planned_release_date?: string | null;
  accountable_owner_user_id: string;
  notes?: string | null;
  evidence_refs?: string[];
};
/** No `publication_ref` and no `gate_kind`: the route's `UpdatePublication` omits both. */
export type UpdatePublicationBody = {
  title: string;
  format: string;
  planned_release_date?: string | null;
  accountable_owner_user_id: string;
  notes?: string | null;
  evidence_refs?: string[];
};
export type RecordPublicationRightsEvidenceBody = Pick<
  PublicationRightsEvidence,
  'evidence_kind' | 'summary' | 'holder' | 'attachment_ref'
> & { reference?: string | null; valid_until?: string | null };
/**
 * `reason` is optional ON THE WIRE and mandatory in the SERVICE: the route
 * defaults an absent one to `''` so the refusal carries
 * `PUBLICATION_CATEGORY_REASON_REQUIRED` rather than a generic field error.
 */
export type ApplyPublicationCategoryBody = { category_code: string; reason?: string | null };
/** fix-sprint-20260904-2130-stream-remainders F1 (Q-SR1) — one recording of a declared step. */
export type RecordPublicationCategoryStepBody = {
  category_code: string;
  step_id: string;
  /** Calendar date (YYYY-MM-DD). */
  recorded_on: string;
  reason: string;
  evidence_attachment_ref?: string | null;
  evidence_reference?: string | null;
};
export type RecordPublicationDepositBody = {
  /** An instant WITH a zone offset — `PUBLICATION_INSTANT_WITHOUT_ZONE` otherwise. */
  submitted_at: string;
  receipt_reference: string;
  receipt_received_at?: string | null;
  receipt_evidence_refs?: string[];
  notes?: string | null;
};
export type RecordPublicationReceiptBody = {
  receipt_received_at: string;
  /** Required, not optional: a receipt with nothing attached is a claim (AC8). */
  receipt_evidence_refs: string[];
  receipt_reference?: string | null;
};
export type RecordPublicationReleaseBody = { actual_release_date: string };
export type WithdrawPublicationBody = { reason: string };
export type RecordLegalRepresentativeBody = Pick<
  LegalRepresentativeEntry,
  | 'subject_type'
  | 'representative_name'
  | 'contact_reference'
  | 'appointed_at'
  | 'scope_description'
  | 'evidence_refs'
> & {
  appointment_ends_at?: string | null;
  /** Mandatory for a superseding entry — the service raises `LEGAL_REPRESENTATIVE_REASON_REQUIRED`. */
  reason?: string | null;
  supersedes_entry_id?: string | null;
};
export type PublicationAssessmentBody = {
  ai_systems: Array<{ ai_system_ref: string; intended_use: string }>;
  content_source: string;
  rights_evidence_entry_ids?: string[];
  editorial_responsibility: string;
  output_traceability: string;
  evidence_refs?: string[];
  notes?: string | null;
};
export type CreatePublicationAssessmentBody = PublicationAssessmentBody & {
  publication_id: string;
};
/** Both optional on the wire; the service raises `PUBLICATION_ASSESSMENT_APPROVER_REQUIRED`. */
export type CompletePublicationAssessmentBody = {
  approver_user_id?: string | null;
  approver_name?: string | null;
};
/** One AI system profile the assessment may link to (`AiSystemOption`). */
export interface PublicationAiSystemOption {
  document_id: string;
  profile_id: string | null;
  name: string;
}
/**
 * The AI system register read, with its three states. `register_empty` is a
 * fact about a fresh tenant and never blocks; `register_unreadable` is a
 * failed read and is never rendered as the empty one.
 */
export interface PublicationAiSystemRegisterRead {
  state: AiSystemRegisterState;
  systems: PublicationAiSystemOption[];
  /**
   * The register holds more systems than this page carries. The count on
   * screen then says so instead of presenting the page as the whole register
   * (gate E12.5 pass 1, B4). Links on an assessment are resolved server-side
   * against the register itself, never against this page.
   */
  truncated: boolean;
}
/**
 * The scoping questionnaire read (AC5), with its four states — the backend's
 * `ScopeQuestionnaireRead`. `declaration` names the record that declared it;
 * `template` is renderable through `FormSectionRenderer`; `instance` is the
 * organisation's newest answer, or `null` when it has not answered.
 */
export interface PublicationScopeQuestionnaireRead {
  state: 'not_declared' | 'template_not_installed' | 'not_answered' | 'answered';
  declaration: {
    form_id: string;
    framework_code: string;
    reference: string | null;
    note: string | BilingualText | null;
    pack_code: string | null;
    timing: PublicationTiming;
    activation_date: string | null;
  } | null;
  template: {
    template_id: string;
    schema_id: string;
    title: FormTemplate['title'];
    lifecycle_status: FormTemplate['lifecycle_status'];
    sections: FormTemplate['sections'];
  } | null;
  instance: {
    instance_id: string;
    field_values: FormInstance['field_values'];
    status: FormInstance['status'];
    updated_at: string;
  } | null;
}

class ApiClient {
  private csrfToken: string | null = null;
  private csrfFetched = false;
  private onUnauthorized: (() => void) | null = null;
  private onMfaStepUpRequired: ((returnTo: string) => void) | null = null;
  /** Sprint 63.29.0 (#541) — see `setOnPasswordChangeRequired`. */
  private onPasswordChangeRequired: (() => void) | null = null;
  /** @deprecated No-op — auth tokens are now managed via httpOnly cookies. */
  setToken(_token: string) {
    // No-op: tokens are managed via httpOnly cookies set by the backend
  }
  /** @deprecated No-op — auth tokens are now managed via httpOnly cookies. */
  clearToken() {
    // No-op: cookies are cleared by backend logout endpoint
  }
  /** Register a callback for 401 auto-logout. */
  setOnUnauthorized(callback: () => void) {
    this.onUnauthorized = callback;
  }
  /**
   * Fire the 401 auto-logout hook, from anywhere that reads a response status.
   *
   * `request()` used to be the only place this happened, and the two **blob**
   * helpers below (`downloadGenericAttachmentChecked`,
   * `downloadPlatformFileChecked`) issue a raw `fetch` rather than going
   * through it. `AuthContext` wires `setOnUnauthorized` to clear the session
   * and drop the user to the login screen, so on an expired session a download
   * from any migrated surface threw `ApiError { status: 401 }`, the hook
   * toasted "Download failed (401)", and the app stayed in a stale
   * authenticated state — the operator retried and got the same toast with no
   * path to re-authenticate.
   *
   * That was a two-call-site exposure until 26 surfaces were pointed at these
   * helpers, filing-grade evidence-pack and case ZIP retrieval among them.
   */
  private notifyUnauthorized(status: number): void {
    if (status === 401 && this.onUnauthorized) {
      this.onUnauthorized();
    }
  }
  /**
   * Register the callback that takes a LIVE session held at the TOTP step-up to
   * the page where a code can be entered (Sprint 63.6.3, gate E12.5 pass 2).
   * The app registers one navigation (`MfaStepUpRedirector`); with none
   * registered a hold is just an error, as it was.
   */
  setOnMfaStepUpRequired(callback: ((returnTo: string) => void) | null) {
    this.onMfaStepUpRequired = callback;
  }
  /**
   * A 403 `MFA_REQUIRED` means the session exists and has not presented its
   * second factor (`authenticate` in the backend admits exactly three paths
   * while it is held). Without this, every page of the product failed with a
   * generic error and nothing in the SPA could enter a code: a person whose
   * session was returned to the code prompt (an administrator changed which
   * sign-in factors count, or the sign-in came through single sign-on without a
   * factor the organisation accepts) was stuck behind their own screens.
   *
   * Fires AT MOST ONE navigation per hold, and never from the paths that are
   * how a held session gets out of it:
   *  - `/mfa/verify`: the step-up page's own call (a refusal there is its error
   *    to show);
   *  - `/auth/logout`, `/auth/me`: always admitted, and the shell reads them
   *    before it knows there is a hold;
   *  - any request made while the window is already on the step-up page (its
   *    sibling requests failing in the same breath as the one that moved it).
   * `window.location` is read at call time and the registered navigation updates
   * it synchronously, so the burst of sibling 403s a page load produces sees the
   * step-up page and stops.
   */
  private notifyMfaStepUpRequired(status: number, serverCode: unknown, path: string): void {
    if (status !== 403 || serverCode !== 'MFA_REQUIRED') return;
    if (!this.onMfaStepUpRequired || typeof window === 'undefined') return;
    const route = path.split('?')[0].replace(/\/+$/, '');
    if (MFA_STEP_UP_EXEMPT_PATHS.includes(route)) return;
    if (window.location.pathname === MFA_STEP_UP_ROUTE) return;
    this.onMfaStepUpRequired(`${window.location.pathname}${window.location.search}`);
  }
  /**
   * Sprint 63.29.0 (#541) — register the navigation that takes a session whose
   * user must change their password to the change-password step. The app
   * registers one (`PasswordChangeRedirector`).
   */
  setOnPasswordChangeRequired(callback: (() => void) | null) {
    this.onPasswordChangeRequired = callback;
  }
  /**
   * A 403 `PASSWORD_CHANGE_REQUIRED` on ANY request means an administrator
   * required a new password, possibly while this person was already signed in
   * (the server sets the hold on their live sessions). Central, so no page has
   * to know: the first refused request moves the window, and none does once it
   * is already on the step.
   */
  private notifyPasswordChangeRequired(status: number, serverCode: unknown): void {
    if (status !== 403 || serverCode !== 'PASSWORD_CHANGE_REQUIRED') return;
    if (!this.onPasswordChangeRequired || typeof window === 'undefined') return;
    if (window.location.pathname === PASSWORD_CHANGE_REQUIRED_ROUTE) return;
    this.onPasswordChangeRequired();
  }
  /** Fetch CSRF token from backend (called once on init, or forced on refresh). */
  private async fetchCsrfToken(): Promise<void> {
    if (this.csrfFetched) return;
    try {
      const res = await fetch(`${API_BASE}/csrf-token`, { credentials: 'same-origin' });
      if (!res.ok) {
        logWarning('api.csrf.endpoint_unavailable');
        return;
      }
      const data = await res.json();
      if (typeof data?.csrf_token !== 'string' || data.csrf_token.length === 0) {
        logWarning('api.csrf.invalid_response');
        return;
      }
      this.csrfToken = data.csrf_token;
      this.csrfFetched = true;
    } catch {
      // Leave acquisition retryable; server-side CSRF refusal remains authoritative.
      logWarning('api.csrf.acquisition_failed');
    }
  }
  /** Force-refresh the CSRF token (e.g. after a stale-token 403). */
  private async refreshCsrfToken(): Promise<void> {
    this.csrfFetched = false;
    this.csrfToken = null;
    await this.fetchCsrfToken();
  }
  /**
   * Sprint 47.7.4.1 AC-1 — public CSRF token accessor for the SSE
   * streaming path. `postSse` (`utils/sse-fetch.ts`) is a lower-level
   * fetch helper that bypasses `apiClient.request()`, so the SSE
   * callsites (ChatInput, future workflow streaming) need to fetch
   * the token themselves and pass it as the `csrfToken` option. This
   * method is idempotent and shares the cached token with the regular
   * mutation path; if the token gets invalidated by a `csrf_failed`
   * response on the regular path, the next call here will trigger a
   * fresh fetch via the existing `refreshCsrfToken()` sequence.
   */
  async getCsrfToken(): Promise<string | null> {
    await this.fetchCsrfToken();
    return this.csrfToken;
  }
  /**
   * Builds a query string from a record, stripping invalid/empty values.
   */
  private buildQuery(params?: Record<string, any>): string {
    if (!params) return '';
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '' && value !== 'undefined') {
        query.set(key, String(value));
      }
    }
    const qString = query.toString();
    return qString ? `?${qString}` : '';
  }
  // ── Public endpoints ──────────────────────────────────────────
  async submitIntake(payload: PublicIntakePayload, intakeKey?: string): Promise<IntakeResponse> {
    const query = intakeKey ? `?key=${encodeURIComponent(intakeKey)}` : '';
    return this.post(`/intake${query}`, payload, false);
  }
  // Phase 9 Extension: Public intake config resolution
  async getPublicIntakeConfig(key: string): Promise<{
    org_name: string;
    enabled_request_types: string[];
    default_language: string;
    /** Sprint 63.31.0 (OD-E) — the rights a request handled in Legal Matters may name. */
    legal_matter_rights?: Record<string, LegalMatterRightOption[]>;
    [key: string]: unknown;
  }> {
    return this.get(`/organizations/public/intake/config?key=${encodeURIComponent(key)}`);
  }
  // Returns org name + enabled request types when exactly one active org exists.
  // Returns null when multiple orgs exist (multi-tenant — key required).
  async getDefaultPublicIntakeConfig(): Promise<{
    org_name: string;
    enabled_request_types: string[];
    default_language: string;
    /** Sprint 63.31.0 (OD-E) — the rights a request handled in Legal Matters may name. */
    legal_matter_rights?: Record<string, LegalMatterRightOption[]>;
    [key: string]: unknown;
  } | null> {
    const res = await fetch('/api/v1/organizations/public/intake/default-config');
    if (res.status === 204) return null;
    if (!res.ok) return null;
    return res.json();
  }
  // ── Admin endpoints ───────────────────────────────────────────
  async listCases(
    params?: Record<string, string | undefined>,
  ): Promise<PaginatedResponse<CaseListItem>> {
    return this.get(`/cases${this.buildQuery(params)}`);
  }
  async exportCasesCSV(params?: Record<string, string | undefined>): Promise<Blob> {
    const response = await fetch(`${API_BASE}/cases/export/csv${this.buildQuery(params)}`, {
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }
  /**
   * Sprint 43.2.0 batch 2 (E3) — bulk case export.
   *
   * Backs the TicketListPage ExportFormatMenu with three formats:
   *   - `csv`  → delegates to the existing CSV exporter and persists
   *              through the File Component.
   *   - `pdf`  → aggregated summary table of the filtered cases.
   *   - `zip`  → "ZIP of ZIPs" — each filtered case's evidence pack
   *              bundled with an `index.json` cover sheet.
   *
   * Returns the persisted AttachmentRef augmented with `case_count`,
   * `format`, and an optional `warning` (e.g. truncation notice or
   * large-bundle heads-up).
   */
  async exportCasesBulk(
    filter: Record<string, unknown>,
    format: 'pdf' | 'zip' | 'csv',
  ): Promise<{
    file_id: string;
    filename: string;
    mime_type: string;
    size: number;
    storage_path: string;
    uploaded_at: string;
    case_count: number;
    format: 'pdf' | 'zip' | 'csv';
    warning?: string;
  }> {
    const res = await this.post<{
      data: {
        file_id: string;
        filename: string;
        mime_type: string;
        size: number;
        storage_path: string;
        uploaded_at: string;
        case_count: number;
        format: 'pdf' | 'zip' | 'csv';
        warning?: string;
      };
    }>(`/cases/export/bulk`, { filter, format });
    return res.data;
  }
  async getCaseDetail(casePublicId: string): Promise<RightsRequestCase> {
    return this.get(`/cases/${casePublicId}`);
  }
  async updateCase(casePublicId: string, payload: UpdateCasePayload): Promise<RightsRequestCase> {
    return this.patch(`/cases/${casePublicId}`, payload);
  }
  async createCase(payload: Record<string, unknown>): Promise<CaseCreatedResponse> {
    return this.post('/cases', payload);
  }
  /**
   * fix-sprint #447 W9 (owner ruling OR3) — whether a processor or third party
   * must act. A change re-dates the case from receipt; the case comes back with
   * `third_party_redating` naming the old and the new due date.
   */
  async setCaseThirdPartyInvolvement(
    casePublicId: string,
    thirdPartyInvolved: boolean,
  ): Promise<RightsRequestCase> {
    return this.put(`/cases/${encodeURIComponent(casePublicId)}/third-party-involvement`, {
      third_party_involved: thirdPartyInvolved,
    });
  }
  // ── Sprint 40.0.0: Rights Request Case Bulk Import ────────
  async importCases(csvData: string, filename: string): Promise<ImportJob> {
    const res: any = await this.post('/cases/import', { csv: csvData, filename });
    return res.data ?? res;
  }
  async getCaseImportHistory(): Promise<ImportJob[]> {
    const res: any = await this.get('/cases/import/history');
    return res.data ?? res;
  }
  async getCaseNotes(casePublicId: string): Promise<{ data: CaseNote[] }> {
    return this.get(`/cases/${casePublicId}/notes`);
  }
  async addNote(casePublicId: string, payload: AddNotePayload): Promise<CaseNote> {
    return this.post(`/cases/${casePublicId}/notes`, payload);
  }
  async getCaseAudit(
    casePublicId: string,
    params?: Record<string, string>,
  ): Promise<AuditTrailResponse> {
    return this.get(`/cases/${casePublicId}/audit${this.buildQuery(params)}`);
  }
  async getGlobalAudit(params?: Record<string, string>): Promise<AuditTrailResponse> {
    return this.get(`/audit${this.buildQuery(params)}`);
  }
  async getSettings(): Promise<Record<string, unknown>> {
    return this.get('/settings');
  }
  async listStatutoryCalendars(params: { page: string; page_size: string }): Promise<{
    data: {
      data: import('@vn-grc/shared').StatutoryCalendarRead[];
      pagination: import('@vn-grc/shared').StatutoryCalendarHistory['pagination'];
    };
  }> {
    return this.get(`/settings/statutory-calendars${this.buildQuery(params)}`);
  }
  async getStatutoryCalendar(
    key: string,
  ): Promise<{ data: import('@vn-grc/shared').StatutoryCalendarRead }> {
    return this.get(`/settings/statutory-calendars/${encodeURIComponent(key)}`);
  }
  async updateStatutoryCalendar(
    key: string,
    body: import('@vn-grc/shared').UpdateStatutoryCalendarInput,
  ): Promise<{ data: import('@vn-grc/shared').StatutoryCalendarRead }> {
    return this.patch(`/settings/statutory-calendars/${encodeURIComponent(key)}`, body);
  }
  async listStatutoryCalendarHistory(
    key: string,
    page: number,
    pageSize: number,
  ): Promise<{ data: import('@vn-grc/shared').StatutoryCalendarHistory }> {
    return this.get(
      `/settings/statutory-calendars/${encodeURIComponent(key)}/history?page=${page}&page_size=${pageSize}`,
    );
  }
  async updateNotificationSettings(
    settings: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch('/settings/notifications', settings);
  }
  async updateIntakeSettings(settings: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.patch('/settings/intake', settings);
  }
  /**
   * Sprint 62.45.0 (AC17) — save the organisation's working days and holidays.
   * The whole calendar is sent, with the `updated_at` the editor read; a save
   * over a newer calendar is a 409 (`CALENDAR_SETTINGS_CHANGED`).
   */
  async updateCalendarSettings(
    payload: import('@vn-grc/shared').UpdateCalendarSettingsPayload,
  ): Promise<import('@vn-grc/shared').OrgCalendarSettingsView> {
    return this.patch('/settings/calendar', payload);
  }
  // ── Sprint 41.0.0 G0.6 / 41.2.0 E1: Generic attachments (provider-agnostic) ──
  //
  // Sprint 41.2.0 E1 deleted the legacy rights-request-scoped
  // `uploadAttachment`/`listAttachments`/`deleteAttachment`/`downloadAttachment`
  // methods. Rights Requests pages now call the generic methods below
  // with `owner_type='rights_request_attachment'` and `owner_id=<caseDoc._id>`.
  async listGenericAttachments(
    ownerType: string,
    ownerId: string,
    /** A page of the owner's files; absent, the route's own default page. */
    paging?: { page: number; page_size: number },
  ): Promise<{
    data: GenericAttachment[];
    total: number;
    /**
     * Sprint 63.31.0 (#410, AC7) — the owner's files released at the end of
     * their retention (or for an erasure request), page 1 only. Their metadata
     * rows are gone; these say what happened. Absent from an older server.
     */
    released?: ReleasedAttachmentTombstone[];
  }> {
    const qs =
      `?owner_type=${encodeURIComponent(ownerType)}&owner_id=${encodeURIComponent(ownerId)}` +
      (paging ? `&page=${paging.page}&page_size=${paging.page_size}` : '');
    return this.get(`/attachments${qs}`);
  }

  /**
   * Sprint 63.31.0 (#410, AC7) — `GET /attachments/releases?file_ids=a,b`
   * (1–100 ids, `attachments:download`): which of the ids a register row names
   * were released, and why. `{ data: ReleasedAttachmentTombstone[] }`, absent
   * for an id never released.
   */
  async listAttachmentReleases(
    fileIds: readonly string[],
  ): Promise<{ data: ReleasedAttachmentTombstone[] }> {
    const ids = Array.from(new Set(fileIds.filter((id) => id.trim() !== ''))).slice(0, 100);
    return this.get(`/attachments/releases?file_ids=${encodeURIComponent(ids.join(','))}`);
  }

  /**
   * Sprint 63.31.0 (#410, owner rulings Q4 (a), OD-C) — release a file an
   * evidence entry rests on at the end of its retention period (or for an
   * erasure request), under `attachments:retention:release`. Strict body.
   * `201 { data: AttachmentRetentionRelease }`.
   */
  async releaseAttachmentAtRetention(
    fileId: string,
    body: ReleaseAttachmentAtRetentionRequest,
  ): Promise<{ data: AttachmentRetentionRelease }> {
    return this.post(`/attachments/${encodeURIComponent(fileId)}/release-at-retention`, body);
  }

  /**
   * Sprint 63.31.0 gate E12.5 pass 1 (P1-L1, P3-C3) — what releasing the file
   * would rest on (`GET /attachments/:fileId/release-at-retention`, same grant
   * and owner gates as the release): the installed retention end, and the
   * erasure requests the release would accept. `{ data: AttachmentReleasePreview }`.
   */
  async getAttachmentReleasePreview(fileId: string): Promise<{ data: AttachmentReleasePreview }> {
    return this.get(`/attachments/${encodeURIComponent(fileId)}/release-at-retention`);
  }
  async uploadGenericAttachment(
    ownerType: string,
    ownerId: string | null,
    file: File,
    metadata?: Record<string, unknown>,
  ): Promise<AttachmentRef> {
    const base64 = await fileToBase64(file);
    return this.post<AttachmentRef>('/attachments', {
      owner_type: ownerType,
      owner_id: ownerId,
      filename: file.name,
      mime_type: file.type || 'application/octet-stream',
      size: file.size,
      file_data_base64: base64,
      ...(metadata ? { metadata } : {}),
    });
  }
  downloadGenericAttachment(fileId: string, filename: string): void {
    const url = `${API_BASE}/attachments/${fileId}/download`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  /**
   * Sprint 62.9.0 gate E12.5 pass-1 — awaitable variant of
   * `downloadGenericAttachment`, for surfaces that must SURFACE a denial.
   *
   * ## Why this exists rather than `downloadPlatformFileChecked`
   *
   * The 64.0.0 owner-type download policy gates **two** routes, and they are not
   * interchangeable:
   *
   * - `GET /platform/files/:fileId/download` — `platform:files:detail:read`,
   *   which `seed-roles.ts` grants to platform_admin, compliance_owner and
   *   case_manager only. `downloadPlatformFileChecked` targets this route, and
   *   64.0.0 recorded, measured, that the 62.2.5 retrieve button using it was
   *   *already 403ing silently for ordinary pack readers*.
   * - `GET /attachments/:fileId/download` — `attachments:download`, the grant an
   *   ordinary operator actually holds, then `assertOwnerTypeDownloadPermitted`
   *   on the file's own owner type.
   *
   * A courier or customs clerk holds the second and not the first. Pointing an
   * operational evidence surface at the platform-files route would reproduce
   * exactly the defect 64.0.0 closed — a control that looks retrievable and
   * denies everyone but three admin roles.
   *
   * What `downloadPlatformFileChecked` contributes is not its URL but its
   * shape: it **awaits** the response, throws a typed `ApiError` on any non-OK
   * status (so `extractErrorMessage` and a toast work), and only then hands the
   * blob to the browser. The anchor-click helper above cannot observe a
   * response at all, so `owner_type_download_denied` renders as a dead click.
   * This method is that shape over the route an operator can use.
   *
   * Deliberately additive: the shipped `downloadGenericAttachment` call sites
   * keep their behaviour; denial-capable surfaces opt in.
   */
  async downloadGenericAttachmentChecked(fileId: string, filename: string): Promise<void> {
    const url = `${API_BASE}/attachments/${encodeURIComponent(fileId)}/download`;
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) {
      let body: { error?: string; message?: string; details?: Record<string, unknown> } = {};
      try {
        body = await res.json();
      } catch {
        // non-JSON error body — fall through to the status-bucket error
      }
      // A raw `fetch` skips `request()`, which is where the 401 auto-logout
      // lives. Without this the operator is left in a stale authenticated
      // state with a toast and no way back to the login screen.
      this.notifyUnauthorized(res.status);
      this.notifyMfaStepUpRequired(res.status, body.error, '/attachments/download');
      this.notifyPasswordChangeRequired(res.status, body.error);
      throw new ApiError({
        code: statusToErrorCode(res.status),
        message: body.message || `Download failed (${res.status})`,
        status: res.status,
        serverCode: body.error,
        details: body.details,
      });
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }
  async deleteGenericAttachment(fileId: string): Promise<void> {
    return this.del(`/attachments/${fileId}`);
  }

  // ── Sprint 41.4.0 F8: Platform → Files Configuration & Browser ──
  //
  // 15 typed methods backing the FilesConfigPage, FilesBrowserPage,
  // PlatformFileDetailModal, and FilesDashboardPage. Every method
  // routes through one of the F6/F7 backend endpoints. Return types
  // come from the shared types in `platform-file-settings.ts`. No
  // `any` casts; no `langKey` patterns.

  // ── Sprint 63.1.0: abandoned uploads (idea-catalog #148) ──

  /**
   * GET /api/v1/platform/files/abandoned — files no save ever claimed, past the
   * grace window. Gated on `platform:files:browse` alone; it destroys nothing.
   */
  async getAbandonedUploads(params?: { as_of?: string }): Promise<AbandonedAttachmentPosition> {
    return this.get(`/platform/files/abandoned${this.buildQuery(params)}`);
  }

  /**
   * POST /api/v1/platform/files/abandoned/sweep — soft-deletes them.
   *
   * Soft, never hard: the rows keep their bytes, emit `ATTACHMENT_DELETED`, and
   * are restorable through the existing restore route. The clock is the input
   * most likely to be wrong, so the irreversible verb is not the one it controls.
   */
  async sweepAbandonedUploads(): Promise<AbandonedAttachmentSweepReport> {
    return this.post('/platform/files/abandoned/sweep', {});
  }

  /** GET /api/v1/platform/files/settings — fetch the org's settings doc. */
  async getPlatformFileSettings(): Promise<{
    global: PlatformFileGlobalSettings | null;
    per_owner_type: Partial<Record<AttachmentOwnerType, PlatformFileOwnerTypeOverride>>;
    version: number;
    updated_at: string | null;
    updated_by: string | null;
    empty: boolean;
  }> {
    return this.get('/platform/files/settings');
  }

  /** PUT /api/v1/platform/files/settings/global — update org-level defaults. */
  async updatePlatformFileSettingsGlobal(
    patch: Partial<PlatformFileGlobalSettings>,
    expectedVersion: number,
  ): Promise<PlatformFileSettings> {
    return this.put('/platform/files/settings/global', {
      expected_version: expectedVersion,
      patch,
    });
  }

  /** PUT /api/v1/platform/files/settings/owner-type/:ownerType — add/update override. */
  async updatePlatformFileOwnerTypeOverride(
    ownerType: AttachmentOwnerType,
    patch: PlatformFileOwnerTypeOverride,
    expectedVersion: number,
  ): Promise<PlatformFileSettings> {
    return this.put(`/platform/files/settings/owner-type/${encodeURIComponent(ownerType)}`, {
      expected_version: expectedVersion,
      patch,
    });
  }

  /** DELETE /api/v1/platform/files/settings/owner-type/:ownerType — remove an override. */
  async deletePlatformFileOwnerTypeOverride(
    ownerType: AttachmentOwnerType,
    expectedVersion: number,
  ): Promise<PlatformFileSettings> {
    // Use request() directly because the `del` helper doesn't accept
    // a body and this endpoint requires `expected_version` for
    // optimistic locking.
    return this.request<PlatformFileSettings>(
      'DELETE',
      `/platform/files/settings/owner-type/${encodeURIComponent(ownerType)}`,
      { expected_version: expectedVersion },
      true,
    );
  }

  /** GET /api/v1/platform/files/settings/owner-type/:ownerType/effective — preview merged settings. */
  async getPlatformFileOwnerTypeEffective(
    ownerType: AttachmentOwnerType,
  ): Promise<PlatformFileEffectiveSettings> {
    return this.get(
      `/platform/files/settings/owner-type/${encodeURIComponent(ownerType)}/effective`,
    );
  }

  /** GET /api/v1/platform/files — paginated cross-owner-type list with optional sort. */
  async listPlatformFiles(
    filter: FileBrowserFilter,
    page: number = 1,
    size: number = 50,
    sort?: { field: string; dir: 'asc' | 'desc' },
  ): Promise<{
    data: GenericAttachment[];
    total: number;
    page: number;
    size: number;
    sort_field: string;
    sort_dir: 'asc' | 'desc';
  }> {
    const params: Record<string, unknown> = { page, size };
    if (filter.owner_type && filter.owner_type.length > 0) {
      params.owner_type = filter.owner_type.join(',');
    }
    if (filter.owner_id) params.owner_id = filter.owner_id;
    if (filter.uploader_user_id) params.uploader_user_id = filter.uploader_user_id;
    if (filter.uploaded_after) params.uploaded_after = filter.uploaded_after;
    if (filter.uploaded_before) params.uploaded_before = filter.uploaded_before;
    if (filter.size_min !== undefined) params.size_min = filter.size_min;
    if (filter.size_max !== undefined) params.size_max = filter.size_max;
    if (filter.mime_type && filter.mime_type.length > 0) {
      params.mime_type = filter.mime_type.join(',');
    }
    if (filter.deleted) params.deleted = filter.deleted;
    if (filter.orphaned) params.orphaned = 'true';
    if (filter.search) params.search = filter.search;
    if (sort) {
      params.sort_field = sort.field;
      params.sort_dir = sort.dir;
    }
    return this.get(`/platform/files${this.buildQuery(params)}`);
  }

  /** GET /api/v1/platform/files/:fileId — file detail with audit trail and effective settings. */
  async getPlatformFileDetail(fileId: string): Promise<{
    attachment: GenericAttachment;
    audit: PlatformFileAuditEvent[];
    download_count: number;
    last_downloaded_at: string | null;
    owner_entity_exists: boolean | null;
    effective_settings: PlatformFileEffectiveSettings;
  }> {
    return this.get(`/platform/files/${encodeURIComponent(fileId)}`);
  }

  /** POST /api/v1/platform/files/:fileId/soft-delete — soft delete with mandatory reason. */
  async softDeletePlatformFile(
    fileId: string,
    reason: string,
  ): Promise<{ ok: true; file_id: string }> {
    return this.post(`/platform/files/${encodeURIComponent(fileId)}/soft-delete`, { reason });
  }

  /** POST /api/v1/platform/files/:fileId/restore — restore soft-deleted file. */
  async restorePlatformFile(
    fileId: string,
    reason: string,
  ): Promise<{ ok: true; file_id: string }> {
    return this.post(`/platform/files/${encodeURIComponent(fileId)}/restore`, { reason });
  }

  /** DELETE /api/v1/platform/files/:fileId — hard delete with reason and optional legal-hold override. */
  async hardDeletePlatformFile(
    fileId: string,
    reason: string,
    legalHoldOverride?: boolean,
  ): Promise<{ ok: true; file_id: string }> {
    // Use request() directly because the `del` helper doesn't accept
    // a body and this endpoint requires `reason` (>= 20 chars) and
    // optional `legal_hold_override`.
    return this.request<{ ok: true; file_id: string }>(
      'DELETE',
      `/platform/files/${encodeURIComponent(fileId)}`,
      { reason, legal_hold_override: legalHoldOverride === true },
      true,
    );
  }

  /** POST /api/v1/platform/files/bulk/soft-delete — bulk soft delete with per-id status. */
  async bulkSoftDeletePlatformFiles(
    fileIds: string[],
    reason: string,
  ): Promise<{
    results: Array<{
      file_id: string;
      status: 'deleted' | 'already_deleted' | 'not_found' | 'has_dependent_evidence';
      /** Only on `has_dependent_evidence` (Sprint 62.25.0 AC15): the refusal and the entries. */
      error?: string;
      message?: string;
      dependents?: Array<{ register: string; entry_id: string }>;
    }>;
  }> {
    return this.post('/platform/files/bulk/soft-delete', { file_ids: fileIds, reason });
  }

  /**
   * Trigger an operator-side download of a platform file. The browser
   * navigates to the URL directly so the response stream becomes a
   * file save dialog and the audit event is recorded server-side.
   */
  downloadPlatformFile(fileId: string, filename: string): void {
    const url = `${API_BASE}/platform/files/${encodeURIComponent(fileId)}/download`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /**
   * Sprint 64.0.0 — awaitable variant of `downloadPlatformFile` for
   * surfaces that must SURFACE a denial instead of dying silently.
   *
   * The anchor-click helper above is fire-and-forget: it cannot observe
   * the response, so a 403 from the owner-type-aware download route
   * (`owner_type_download_denied`) renders as a dead click. This variant
   * fetches the bytes, throws a typed `ApiError` on any non-OK response
   * (so `extractErrorMessage` and toasts work), and only then hands the
   * blob to the browser as a download.
   *
   * Deliberately NOT a global replacement: the 152 existing
   * `downloadPlatformFile` call sites keep their shipped behaviour; new
   * denial-capable surfaces (62.2.5's evidence-pack version-history
   * retrieve button) opt in.
   */
  async downloadPlatformFileChecked(fileId: string, filename: string): Promise<void> {
    const url = `${API_BASE}/platform/files/${encodeURIComponent(fileId)}/download`;
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) {
      let body: { error?: string; message?: string; details?: Record<string, unknown> } = {};
      try {
        body = await res.json();
      } catch {
        // non-JSON error body — fall through to the status-bucket error
      }
      // A raw `fetch` skips `request()`, which is where the 401 auto-logout
      // lives. Without this the operator is left in a stale authenticated
      // state with a toast and no way back to the login screen.
      this.notifyUnauthorized(res.status);
      this.notifyMfaStepUpRequired(res.status, body.error, '/platform/files/download');
      this.notifyPasswordChangeRequired(res.status, body.error);
      throw new ApiError({
        code: statusToErrorCode(res.status),
        message: body.message || `Download failed (${res.status})`,
        status: res.status,
        serverCode: body.error,
        details: body.details,
      });
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  /** GET /api/v1/platform/files/metrics — dashboard payload (60s server cache per org). */
  async getPlatformFileMetrics(): Promise<{
    total_count: number;
    total_size_bytes: number;
    by_owner_type: Record<AttachmentOwnerType, { count: number; size_bytes: number }>;
    growth_30d: Array<{ date: string; count: number; size_bytes: number }>;
    orphan_count: number;
    near_quota: { attachments_pct: number; size_pct: number };
  }> {
    return this.get('/platform/files/metrics');
  }

  /** GET /api/v1/platform/files/audit — list operational audit events. */
  async listPlatformFileAudit(
    filter: {
      event_type?: PlatformFileAuditEventType[];
      actor_user_id?: string;
      target_file_id?: string;
      target_owner_type?: AttachmentOwnerType;
      from?: string;
      to?: string;
    } = {},
    page: number = 1,
    size: number = 50,
  ): Promise<{
    data: PlatformFileAuditEvent[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const params: Record<string, unknown> = { page, size };
    if (filter.event_type && filter.event_type.length > 0) {
      params.event_type = filter.event_type.join(',');
    }
    if (filter.actor_user_id) params.actor_user_id = filter.actor_user_id;
    if (filter.target_file_id) params.target_file_id = filter.target_file_id;
    if (filter.target_owner_type) params.target_owner_type = filter.target_owner_type;
    if (filter.from) params.from = filter.from;
    if (filter.to) params.to = filter.to;
    return this.get(`/platform/files/audit${this.buildQuery(params)}`);
  }

  // ── Rights Requests attachment helpers (Sprint 41.2.0 E1) ─────────
  //
  // Rights Requests pages identify cases by public ID (e.g. "DSR-001"),
  // but the generic attachment endpoint keys on the internal ULID. These
  // helpers resolve the public ID to a ULID once, then call the generic
  // endpoint. They exist so page code stays concise.
  async listRightsRequestAttachments(
    casePublicId: string,
  ): Promise<{ data: GenericAttachment[]; total: number }> {
    const detail = await this.getCaseDetail(casePublicId);
    return this.listGenericAttachments('rights_request_attachment', detail._id);
  }
  async uploadRightsRequestAttachment(
    casePublicId: string,
    file: File,
    metadata?: Record<string, unknown>,
  ): Promise<AttachmentRef> {
    const detail = await this.getCaseDetail(casePublicId);
    return this.uploadGenericAttachment('rights_request_attachment', detail._id, file, metadata);
  }
  // ── Sprint 1.1.0: Evidence Checklist & References ──────────────
  async getChecklist(
    casePublicId: string,
  ): Promise<{ items: EvidenceChecklistItem[]; pct: number }> {
    return this.get(`/cases/${casePublicId}/checklist`);
  }
  async toggleChecklistItem(
    casePublicId: string,
    itemId: string,
    checked: boolean,
  ): Promise<{ items: EvidenceChecklistItem[] }> {
    return this.patch(`/cases/${casePublicId}/checklist`, { item_id: itemId, checked });
  }
  async addReference(
    casePublicId: string,
    payload: Record<string, unknown>,
  ): Promise<EvidenceReference> {
    return this.post(`/cases/${casePublicId}/references`, payload);
  }
  async listReferences(casePublicId: string): Promise<{ data: EvidenceReference[] }> {
    return this.get(`/cases/${casePublicId}/references`);
  }
  // ── Sprint 1.1.0: Identity Verification ────────────────────────
  async verifyIdentity(
    casePublicId: string,
    payload: { verification_method: string; verification_notes?: string },
  ): Promise<RightsRequestCase> {
    return this.post(`/cases/${casePublicId}/verify-identity`, payload);
  }
  // Sprint 43.5.1 consolidation — the Sprint 1.1.0 Response Templates API
  // (`/api/v1/templates/*`) and its `ResponseTemplate` type have been
  // removed. Use the DsrResponseTemplate methods further down in this
  // file (`listDsrResponseTemplates`, `generateDsrResponse`, CRUD set)
  // which are backed by the canonical `grc_dsr_response_templates`
  // collection seeded at org creation (20 templates: 10 types × vi/en).

  // ── Sprint 1.1.0: Evidence Pack Export ─────────────────────────
  async exportPDF(casePublicId: string): Promise<ExportRecord> {
    return this.post(`/cases/${casePublicId}/export/pdf`);
  }
  async exportZIP(casePublicId: string): Promise<ExportRecord> {
    return this.post(`/cases/${casePublicId}/export/zip`);
  }
  async listExports(casePublicId: string): Promise<{ data: ExportRecord[] }> {
    return this.get(`/cases/${casePublicId}/exports`);
  }
  // ── Sprint 1.1.0: SLA Config ──────────────────────────────────
  async getSlaConfig(): Promise<Record<string, unknown>> {
    return this.get('/sla/config');
  }
  async updateSlaConfig(config: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.patch('/sla/config', config);
  }
  // Mirrors the backend `SlaEvaluation` (sla/service.ts): since PR #2928 a case
  // holding no due date answers `no_due_date` with the four figures null; a
  // case in a terminal status answers `stopped` (`stopped_reason: 'case_closed'`).
  async getCaseSla(casePublicId: string): Promise<{
    status: 'on_track' | 'due_soon' | 'overdue' | 'breached' | 'no_due_date' | 'stopped';
    stopped_reason?: 'case_closed';
    breached_before_close?: boolean;
    days_remaining: number | null;
    days_elapsed: number | null;
    deadline_days: number | null;
    extended_deadline_days: number;
    pct_elapsed: number | null;
  }> {
    return this.get(`/cases/${casePublicId}/sla`);
  }
  // ── Sprint 1.1.0: Audit Export ─────────────────────────────────
  // Sprint 42.2.3 E2 — returns AttachmentRef (was Blob). Caller should
  // call downloadPlatformFile(ref.file_id, ref.filename) to download.
  // The CSV is also persisted in grc_attachments (visible in Files Browser).
  async exportAuditCSV(params?: Record<string, string>): Promise<AttachmentRef> {
    return this.get(`/audit/export${this.buildQuery(params)}`);
  }
  /** Sprint 43.5.5 — Audit export now supports XLSX alongside CSV. */
  async exportAudit(
    format: 'csv' | 'xlsx',
    params?: Record<string, string>,
  ): Promise<AttachmentRef> {
    const q = this.buildQuery({ ...(params ?? {}), format });
    return this.get(`/audit/export${q}`);
  }
  // ── Sprint 1.2.0: Data Mapping -- Systems ────────────────────
  async listSystems(params?: Record<string, string>): Promise<PaginatedResponse<GrcSystem>> {
    return this.get(`/data-mapping/systems${this.buildQuery(params)}`);
  }
  /**
   * Sprint 62.55.0 (AC-F3) — the electronic indicator sets a system or a flow
   * may name, their fields with the pack's personal-data flag, and the guidance
   * the installed packs relate to Data Mapping.
   */
  async getDataMappingIndicatorSets(): Promise<{ data: DataMappingIndicatorReading }> {
    return this.get('/data-mapping/indicator-sets');
  }
  async getSystem(systemId: string): Promise<GrcSystem> {
    return this.get(`/data-mapping/systems/${systemId}`);
  }
  async createSystem(payload: Record<string, unknown>): Promise<GrcSystem> {
    return this.post('/data-mapping/systems', payload);
  }
  async updateSystem(systemId: string, payload: Record<string, unknown>): Promise<GrcSystem> {
    return this.patch(`/data-mapping/systems/${systemId}`, payload);
  }
  async archiveSystem(systemId: string): Promise<GrcSystem> {
    return this.post(`/data-mapping/systems/${systemId}/archive`, {});
  }
  // ── Sprint 1.2.0: Data Mapping -- Categories ────────────────
  async listCategories(params?: Record<string, string>): Promise<PaginatedResponse<DataCategory>> {
    return this.get(`/data-mapping/categories${this.buildQuery(params)}`);
  }
  async createCategory(payload: Record<string, unknown>): Promise<DataCategory> {
    return this.post('/data-mapping/categories', payload);
  }
  async updateCategory(
    categoryId: string,
    payload: Record<string, unknown>,
  ): Promise<DataCategory> {
    return this.patch(`/data-mapping/categories/${categoryId}`, payload);
  }
  async archiveCategory(categoryId: string): Promise<DataCategory> {
    return this.post(`/data-mapping/categories/${categoryId}/archive`, {});
  }
  // ── Sprint 45.4.0: Data Mapping -- Data Registry ──────────────
  async listRegistryItems(
    params?: Record<string, string>,
  ): Promise<import('@vn-grc/shared').RegistryListResult> {
    return this.get(`/data-mapping/registry${this.buildQuery(params)}`);
  }
  async getRegistryItem(
    registryItemId: string,
  ): Promise<import('@vn-grc/shared').DataRegistryItemView> {
    return this.get(`/data-mapping/registry/${registryItemId}`);
  }
  async getRegistryItemLineage(
    registryItemId: string,
  ): Promise<import('@vn-grc/shared').RegistryLineage> {
    return this.get(`/data-mapping/registry/${registryItemId}/lineage`);
  }
  async createRegistryItem(
    payload: Record<string, unknown>,
  ): Promise<import('@vn-grc/shared').DataRegistryItemView> {
    return this.post('/data-mapping/registry', payload);
  }
  async updateRegistryItem(
    registryItemId: string,
    payload: Record<string, unknown>,
  ): Promise<import('@vn-grc/shared').DataRegistryItemView> {
    return this.put(`/data-mapping/registry/${registryItemId}`, payload);
  }
  async patchRegistryItem(
    registryItemId: string,
    payload: Record<string, unknown>,
  ): Promise<import('@vn-grc/shared').DataRegistryItemView> {
    return this.patch(`/data-mapping/registry/${registryItemId}`, payload);
  }
  async archiveRegistryItem(
    registryItemId: string,
  ): Promise<import('@vn-grc/shared').DataRegistryItemView> {
    return this.post(`/data-mapping/registry/${registryItemId}/archive`, {});
  }
  async restoreRegistryItem(
    registryItemId: string,
  ): Promise<import('@vn-grc/shared').DataRegistryItemView> {
    return this.post(`/data-mapping/registry/${registryItemId}/restore`, {});
  }
  async importRegistryFromForm(payload: {
    form_submission_id: string;
    dry_run: boolean;
  }): Promise<import('@vn-grc/shared').RegistryFormImportResult> {
    return this.post('/data-mapping/registry/import-from-form', payload);
  }
  async importRegistryHistory(payload: { template_ids?: string[]; dry_run: boolean }): Promise<{
    aggregate: {
      created: number;
      updated: number;
      skipped_archived: number;
      conflicts: number;
    };
    results: import('@vn-grc/shared').RegistryFormImportResult[];
  }> {
    return this.post('/data-mapping/registry/import-history', payload);
  }
  async listMappedRegistryTemplates(): Promise<{
    templates: import('@vn-grc/shared').MappedTemplateDescriptor[];
  }> {
    return this.get('/data-mapping/registry/mapped-templates');
  }
  async getRegistryHygiene(includeInformational: boolean): Promise<{ misses: unknown[] }> {
    return this.get(`/data-mapping/registry/hygiene?include_informational=${includeInformational}`);
  }
  /**
   * Sprint 62.35.0 (AC6) — the authorisation bases installed packs declare for
   * a registry item's `provenance.authorisation_basis`, with each declaration's
   * lifecycle. Empty `declarations` = no installed pack declares the surface.
   */
  async getRegistryProvenanceVocabulary(): Promise<
    import('@vn-grc/shared').DataRegistryProvenanceVocabulary
  > {
    return this.get('/data-mapping/registry/provenance-vocabulary');
  }
  /**
   * Sprint 45.4.0 hotfix-3 — Registry export via UnifiedExportService.
   *
   * Returns an `AttachmentRef` the caller feeds into
   * `downloadPlatformFile(ref.file_id, ref.filename)` (the same pattern
   * AuditTrailPage / RopaCompletenessPage / every other admin export
   * already uses). Previously the RegistryPage constructed a raw URL
   * and called `window.open(...)`, which bypassed the platform file
   * browser, skipped the auth header, and opened a JSON AttachmentRef
   * response as a raw JSON blob in a new tab instead of downloading the
   * generated file.
   */
  async exportRegistryItems(params: Record<string, string>): Promise<AttachmentRef> {
    return this.get(`/data-mapping/registry/export${this.buildQuery(params)}`);
  }
  // ── Sprint 1.2.0: Data Mapping -- Purposes ──────────────────
  async listPurposes(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ProcessingPurpose>> {
    return this.get(`/data-mapping/purposes${this.buildQuery(params)}`);
  }
  async createPurpose(payload: Record<string, unknown>): Promise<ProcessingPurpose> {
    return this.post('/data-mapping/purposes', payload);
  }
  async updatePurpose(
    purposeId: string,
    payload: Record<string, unknown>,
  ): Promise<ProcessingPurpose> {
    return this.patch(`/data-mapping/purposes/${purposeId}`, payload);
  }
  async archivePurpose(purposeId: string): Promise<ProcessingPurpose> {
    return this.post(`/data-mapping/purposes/${purposeId}/archive`, {});
  }
  // ── Sprint 1.2.0: Data Mapping -- Retention Policies ────────
  async listRetentionPolicies(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<RetentionPolicy>> {
    return this.get(`/data-mapping/retention-policies${this.buildQuery(params)}`);
  }
  async createRetentionPolicy(payload: Record<string, unknown>): Promise<RetentionPolicy> {
    return this.post('/data-mapping/retention-policies', payload);
  }
  async updateRetentionPolicy(
    retentionId: string,
    payload: Record<string, unknown>,
  ): Promise<RetentionPolicy> {
    return this.patch(`/data-mapping/retention-policies/${retentionId}`, payload);
  }
  async archiveRetentionPolicy(retentionId: string): Promise<RetentionPolicy> {
    return this.post(`/data-mapping/retention-policies/${retentionId}/archive`, {});
  }
  // ── Sprint 1.2.0: Data Mapping -- Data Flows ────────────────
  async listFlows(params?: Record<string, string>): Promise<PaginatedResponse<DataFlow>> {
    return this.get(`/data-mapping/flows${this.buildQuery(params)}`);
  }
  async getFlow(flowId: string): Promise<DataFlow> {
    return this.get(`/data-mapping/flows/${flowId}`);
  }
  async createFlow(payload: Record<string, unknown>): Promise<DataFlow> {
    return this.post('/data-mapping/flows', payload);
  }
  async updateFlow(flowId: string, payload: Record<string, unknown>): Promise<DataFlow> {
    return this.patch(`/data-mapping/flows/${flowId}`, payload);
  }
  async archiveFlow(flowId: string): Promise<DataFlow> {
    return this.post(`/data-mapping/flows/${flowId}/archive`, {});
  }
  async listCrossBorderFlows(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<DataFlow>> {
    return this.get(`/data-mapping/flows/cross-border${this.buildQuery(params)}`);
  }
  // ── Sprint 1.2.0: Data Mapping -- Processing Activities ─────
  async listActivities(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ProcessingActivity>> {
    return this.get(`/data-mapping/activities${this.buildQuery(params)}`);
  }
  async getActivity(activityId: string): Promise<ProcessingActivity> {
    return this.get(`/data-mapping/activities/${activityId}`);
  }
  async createActivity(payload: Record<string, unknown>): Promise<ProcessingActivity> {
    return this.post('/data-mapping/activities', payload);
  }
  async updateActivity(
    activityId: string,
    payload: Record<string, unknown>,
  ): Promise<ProcessingActivity> {
    return this.patch(`/data-mapping/activities/${activityId}`, payload);
  }
  async archiveActivity(activityId: string): Promise<ProcessingActivity> {
    return this.post(`/data-mapping/activities/${activityId}/archive`, {});
  }
  /**
   * Sprint 62.58.0 (Slice N, AC-N2) — for each data subject of the activity,
   * the linked notices whose audience names it (R-23, paged over the data
   * subjects). Every row reads `not_visible` with no notice for a reader
   * without `privacy_notice:read` (`notices_visible: false`).
   */
  async getActivityNoticeCoverage(
    activityId: string,
    paging: { page: number; page_size: number },
  ): Promise<NoticeCoveragePage> {
    return this.get(
      `/data-mapping/activities/${encodeURIComponent(activityId)}/notice-coverage${this.buildQuery({
        page: String(paging.page),
        page_size: String(paging.page_size),
      })}`,
    );
  }
  /**
   * Sprint 62.58.0 (Slice N, AC-N3) — the revisions of the activity's
   * deployment access statement, latest first (R-23).
   */
  async listAccessStatements(
    activityId: string,
    paging: { page: number; page_size: number },
  ): Promise<AccessStatementPage> {
    return this.get(
      `/data-mapping/activities/${encodeURIComponent(activityId)}/access-statements${this.buildQuery(
        {
          page: String(paging.page),
          page_size: String(paging.page_size),
        },
      )}`,
    );
  }
  /**
   * Sprint 62.58.0 (Slice N, AC-N3) — record a revision (append-only).
   * `replaces_version` is the current version as read (absent for the first);
   * a stale one answers 409 `ACCESS_STATEMENT_VERSION_CONFLICT` and nothing is
   * written. `dm:activity:write`.
   */
  async recordAccessStatement(
    activityId: string,
    body: RecordAccessStatementInput,
  ): Promise<{ data: AccessStatementView }> {
    return this.post(
      `/data-mapping/activities/${encodeURIComponent(activityId)}/access-statements`,
      body,
    );
  }
  /** Sprint 62.58.0 (Slice N, AC-N3) — one revision of an access statement. */
  async getAccessStatement(statementId: string): Promise<{ data: AccessStatementView }> {
    return this.get(`/data-mapping/access-statements/${encodeURIComponent(statementId)}`);
  }
  /**
   * Sprint 64.71.0 — set which registry items this activity is linked to.
   *
   * Its own call rather than a field on `updateActivity`, because it writes a
   * different collection: `DataRegistryItem.processing_activity_ids` is where
   * this edge is stored, and `ProcessingActivity.data_registry_item_ids` is a
   * derived read. Sending it on the activity PATCH would be sending a field the
   * server does not store.
   */
  async setActivityRegistryItems(
    activityId: string,
    itemIds: string[],
  ): Promise<{ data_registry_item_ids: string[] }> {
    return this.put(`/data-mapping/activities/${activityId}/registry-items`, {
      item_ids: itemIds,
    });
  }
  // ── Sprint 46.0.0: ROPA Completeness Report ─────────────────
  async getRopaCompleteness(
    params: Record<string, string>,
  ): Promise<import('@vn-grc/shared').RopaCompletenessReport> {
    return this.get(`/data-mapping/ropa-completeness${this.buildQuery(params)}`);
  }
  async exportRopaCompleteness(params: Record<string, string>): Promise<AttachmentRef> {
    return this.get(`/data-mapping/ropa-completeness/export${this.buildQuery(params)}`);
  }
  // Sprint 46.0.1 — list frameworks installed for the requesting org
  async getInstalledRopaFrameworks(): Promise<{
    frameworks: import('@vn-grc/shared').RopaCompletenessFramework[];
  }> {
    return this.get('/data-mapping/ropa-completeness/installed-frameworks');
  }
  // ── Sprint 1.2.0: Data Mapping -- RoPA ──────────────────────
  async listRopaRecords(params?: Record<string, string>): Promise<{ data: RopaRecord[] }> {
    return this.get(`/data-mapping/ropa${this.buildQuery(params)}`);
  }
  async getRopaRecord(ropaRecordId: string): Promise<RopaRecord> {
    return this.get(`/data-mapping/ropa/${ropaRecordId}`);
  }
  async generateRopaRecord(activityId: string): Promise<RopaRecord> {
    return this.post(`/data-mapping/ropa/generate/${activityId}`);
  }
  // ── Sprint 1.2.0: Data Mapping -- Evidence ──────────────────
  async listDmEvidence(params?: Record<string, string>): Promise<PaginatedResponse<EvidenceItem>> {
    return this.get(`/data-mapping/evidence${this.buildQuery(params)}`);
  }
  async getDmEvidence(evidenceId: string): Promise<EvidenceItem> {
    return this.get(`/data-mapping/evidence/${evidenceId}`);
  }
  async createDmEvidence(payload: Record<string, unknown>): Promise<EvidenceItem> {
    return this.post('/data-mapping/evidence', payload);
  }
  async updateDmEvidence(
    evidenceId: string,
    payload: Record<string, unknown>,
  ): Promise<EvidenceItem> {
    return this.patch(`/data-mapping/evidence/${evidenceId}`, payload);
  }
  async deleteDmEvidence(evidenceId: string): Promise<void> {
    return this.del(`/data-mapping/evidence/${evidenceId}`);
  }
  async linkDmEvidence(
    evidenceId: string,
    entityType: string,
    entityId: string,
  ): Promise<EvidenceItem> {
    return this.post(`/data-mapping/evidence/${evidenceId}/link`, {
      entity_type: entityType,
      entity_id: entityId,
    });
  }
  async unlinkDmEvidence(evidenceId: string, entityType: string, entityId: string): Promise<void> {
    return this.del(`/data-mapping/evidence/${evidenceId}/link/${entityType}/${entityId}`);
  }
  // ── Sprint 1.2.0: Data Mapping -- Exports ───────────────────
  // Sprint 42.2.3 E2 — returns AttachmentRef (was Blob).
  async exportDmCSV(exportType: string): Promise<AttachmentRef> {
    return this.post(`/data-mapping/export/csv/${exportType}`);
  }
  /**
   * Sprint 42.1.0 E4 + 44.8.0 — legacy PDF endpoint now returns an
   * AttachmentRef (JSON), not a Blob. The frontend should call
   * `downloadPlatformFile(ref.file_id, ref.filename)` after this
   * returns to trigger the actual download via the authenticated
   * platform files endpoint.
   *
   * Prior 42.1.0 migration returned an AttachmentRef on the backend
   * but the client still used `response.blob()`, which produced a
   * JSON-as-blob that appeared to be a "broken PDF" when opened.
   */
  async exportDmPDF(exportType: string): Promise<AttachmentRef> {
    return this.post(`/data-mapping/export/pdf/${exportType}`);
  }
  /**
   * Sprint 44.8.0 — Unified multi-format DM export.
   * format ∈ 'pdf' | 'csv' | 'xlsx' | 'docx'.
   */
  async exportDataMapping(
    exportType: string,
    format: 'pdf' | 'csv' | 'xlsx' | 'docx',
  ): Promise<AttachmentRef> {
    return this.post(`/data-mapping/export/${exportType}/${format}`);
  }
  // ── Sprint 1.2.0: Data Mapping -- Audit ─────────────────────
  async getDmAudit(params?: Record<string, string>): Promise<{ data: DataMappingAuditEvent[] }> {
    return this.get(`/data-mapping/audit${this.buildQuery(params)}`);
  }
  async getDmEntityAudit(
    entityType: string,
    entityId: string,
    params?: Record<string, string>,
  ): Promise<{ data: DataMappingAuditEvent[] }> {
    return this.get(`/data-mapping/audit/${entityType}/${entityId}${this.buildQuery(params)}`);
  }
  // ── Sprint 20.5.0: Data Mapping -- Attestation Cycles ───────
  async listAttestationCycles(
    params?: Record<string, string>,
  ): Promise<{ data: AttestationCycle[] }> {
    return this.get(`/data-mapping/attestation/cycles${this.buildQuery(params)}`);
  }
  async getAttestationCycle(cycleId: string): Promise<AttestationCycle> {
    return this.get(`/data-mapping/attestation/cycles/${cycleId}`);
  }
  async createAttestationCycle(data: Record<string, unknown>): Promise<AttestationCycle> {
    return this.post('/data-mapping/attestation/cycles', data);
  }
  async updateAttestationCycle(
    cycleId: string,
    data: Record<string, unknown>,
  ): Promise<AttestationCycle> {
    return this.patch(`/data-mapping/attestation/cycles/${cycleId}`, data);
  }
  async assignAttestationTasks(
    cycleId: string,
    assignments: Record<string, unknown>[],
  ): Promise<{ data: AttestationDepartmentTask[] }> {
    return this.post(`/data-mapping/attestation/cycles/${cycleId}/assign`, { assignments });
  }
  async updateAttestationTask(
    taskId: string,
    updates: Record<string, unknown>,
  ): Promise<AttestationDepartmentTask> {
    return this.patch(`/data-mapping/attestation/tasks/${taskId}`, updates);
  }
  async completeAttestationTask(taskId: string): Promise<AttestationDepartmentTask> {
    return this.post(`/data-mapping/attestation/tasks/${taskId}/complete`);
  }
  async getAttestationSummary(
    cycleId: string,
  ): Promise<{ tasks: Record<string, unknown>[]; [key: string]: unknown }> {
    return this.get(`/data-mapping/attestation/cycles/${cycleId}/summary`);
  }
  async detectStaleEntries(cycleId: string): Promise<{
    stale_systems: Array<Record<string, unknown>>;
    stale_flows: Array<Record<string, unknown>>;
  }> {
    return this.get(`/data-mapping/attestation/cycles/${cycleId}/stale`);
  }
  // ── Sprint 20.5.0: Data Mapping -- Governance Ownership ────
  async assignGovernanceOwnership(data: Record<string, unknown>): Promise<GovernanceAssignment> {
    return this.post('/data-mapping/ownership', data);
  }
  async listGovernanceOwnership(
    params?: Record<string, string>,
  ): Promise<{ data: GovernanceAssignment[] }> {
    return this.get(`/data-mapping/ownership${this.buildQuery(params)}`);
  }
  async removeGovernanceOwnership(assignmentId: string): Promise<void> {
    return this.del(`/data-mapping/ownership/${assignmentId}`);
  }
  // ── Sprint 20.5.0: Data Mapping -- Cross-Border Risk Assessment ─
  async listCrossBorderAssessments(
    flowId?: string,
  ): Promise<{ data: CrossBorderRiskAssessment[] }> {
    const params = flowId ? `?flow_id=${flowId}` : '';
    return this.get(`/data-mapping/risk-assessments${params}`);
  }
  async updateCrossBorderAssessment(
    assessmentId: string,
    data: Record<string, unknown>,
  ): Promise<CrossBorderRiskAssessment> {
    return this.patch(`/data-mapping/risk-assessments/${assessmentId}`, data);
  }
  async approveCrossBorderAssessment(
    assessmentId: string,
    decision: Record<string, unknown>,
  ): Promise<CrossBorderRiskAssessment> {
    return this.post(`/data-mapping/risk-assessments/${assessmentId}/decide`, decision);
  }
  // ── Sprint 20.5.0: Data Mapping -- Export & Evidence ────────
  async generateAttestationBundle(cycleId: string): Promise<DataMappingExport> {
    return this.post(`/data-mapping/attestation/cycles/${cycleId}/export`);
  }
  async getEntityEvidence(entityType: string, entityId: string): Promise<{ data: EvidenceItem[] }> {
    return this.get(`/data-mapping/evidence/lookup/${entityType}/${entityId}`);
  }
  // ── Sprint 1.3.0: Governance -- Vendors ─────────────────────
  async listVendors(params?: Record<string, string>): Promise<PaginatedResponse<GrcVendor>> {
    return this.get(`/governance/vendors${this.buildQuery(params)}`);
  }
  /**
   * Sprint 43.5.5 — Convenience helper for vendor-name lookup tables
   * (Approval Gates, Risk Scoring, Vendor Reviews, Reassessments,
   * Risk Signals, Risk Acceptance, Review Routing, Sub-Processor
   * Register, Questionnaires, etc). Always requests a large page so
   * lookups resolve to names instead of raw UUIDs for vendors beyond
   * the default 25-row first page.
   *
   * Sprint 43.5.6 — also returns `_id` so callers can resolve name
   * lookups whether their upstream service stores the business
   * `vendor_id` or the Mongo `_id` (seed data + legacy records are
   * inconsistent on this across modules).
   */
  async listAllVendorsForLookup(): Promise<
    Array<{ _id: string; vendor_id: string; name: string }>
  > {
    const raw = await this.listVendors({ page_size: '500' });
    const arr = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as unknown as { data?: unknown[] })?.data)
        ? (raw as unknown as { data: GrcVendor[] }).data
        : [];
    return (arr as GrcVendor[]).map((v) => ({
      _id: v._id,
      vendor_id: v.vendor_id,
      name: v.name,
    }));
  }
  async getVendor(vendorId: string): Promise<GrcVendor> {
    return this.get(`/governance/vendors/${vendorId}`);
  }
  async createVendor(payload: Record<string, unknown>): Promise<GrcVendor> {
    return this.post('/governance/vendors', payload);
  }
  async updateVendor(vendorId: string, payload: Record<string, unknown>): Promise<GrcVendor> {
    return this.patch(`/governance/vendors/${vendorId}`, payload);
  }
  async offboardVendor(vendorId: string, reason: string): Promise<GrcVendor> {
    return this.post(`/governance/vendors/${vendorId}/offboard`, { reason });
  }
  /** fix-sprint-20260925-0900 F1 (#353) — the foreign-reach questions installed frameworks declare. */
  async listVendorReachQuestions(): Promise<PaginatedResponse<DiscoveredReachQuestion>> {
    return this.get('/governance/vendors/reach-questions');
  }
  /** fix-sprint-20260925-0900 F1 (#353) — a person's foreign-reach determination; 409 when stale. */
  async recordVendorReachDetermination(
    vendorId: string,
    body: {
      framework_code: string;
      reach_id: string;
      within_reach: boolean;
      rationale: string;
      citation?: string | null;
      expected_updated_at: string;
    },
  ): Promise<GrcVendor> {
    return this.post(`/governance/vendors/${vendorId}/reach-determinations`, body);
  }
  async getVendorStats(): Promise<Record<string, unknown>> {
    return this.get('/governance/vendors/stats');
  }
  // ── Sprint 1.3.0: Governance -- Sub-Processors ────────────
  async listSubProcessors(vendorId: string): Promise<{ data: VendorSubProcessor[] }> {
    return this.get(`/governance/vendors/${vendorId}/subprocessors`);
  }
  async createSubProcessor(
    vendorId: string,
    payload: Record<string, unknown>,
  ): Promise<VendorSubProcessor> {
    return this.post(`/governance/vendors/${vendorId}/subprocessors`, payload);
  }
  async updateSubProcessor(
    relationshipId: string,
    payload: Record<string, unknown>,
  ): Promise<VendorSubProcessor> {
    return this.patch(`/governance/subprocessors/${relationshipId}`, payload);
  }
  async terminateSubProcessor(relationshipId: string): Promise<void> {
    return this.del(`/governance/subprocessors/${relationshipId}`);
  }
  // ── Sprint 1.3.0: Governance -- Transfer Mechanisms ───────
  async listTransferMechanisms(vendorId: string): Promise<{ data: VendorTransferMechanism[] }> {
    return this.get(`/governance/vendors/${vendorId}/transfer-mechanisms`);
  }
  async createTransferMechanism(
    vendorId: string,
    payload: Record<string, unknown>,
  ): Promise<VendorTransferMechanism> {
    return this.post(`/governance/vendors/${vendorId}/transfer-mechanisms`, payload);
  }
  async updateTransferMechanism(
    mechanismId: string,
    payload: Record<string, unknown>,
  ): Promise<VendorTransferMechanism> {
    return this.patch(`/governance/transfer-mechanisms/${mechanismId}`, payload);
  }
  // ── Sprint 1.3.0: Governance -- Cross-Border ──────────────
  async getCrossBorderReport(): Promise<Record<string, unknown>> {
    return this.get('/governance/cross-border/report');
  }
  /**
   * Sprint 41.5.0 G3.1 — return type changed from DataMappingExport
   * (which was a fake placeholder shape) to AttachmentRef. Callers
   * should pass the returned `file_id` + `filename` to
   * `downloadPlatformFile()` to trigger the actual download.
   */
  async exportCrossBorderPDF(
    jurisdiction?: string,
    format: 'pdf' | 'docx' = 'pdf',
  ): Promise<AttachmentRef> {
    return this.post('/governance/cross-border/report/export', { jurisdiction, format });
  }
  /** Sprint 41.5.0 G3.2 — Board Report PDF export.
   *  Sprint 43.5.5 — supports DOCX via optional `format`. */
  async exportBoardReportPDF(payload: {
    date_from: string;
    date_to: string;
    language?: 'vi' | 'en';
    format?: 'pdf' | 'docx';
  }): Promise<AttachmentRef> {
    return this.post('/governance/board-report/export', payload);
  }
  /** Sprint 41.5.0 G3.3 — Governance Evidence bundle PDF export. */
  async exportGovernanceEvidenceBundle(payload: {
    evidence_ids: string[];
    bundle_title?: string;
  }): Promise<AttachmentRef> {
    return this.post('/governance/evidence/bundle/export', payload);
  }
  // Sprint 42.2.2 — incident reports list PDF export (unified File Component)
  async exportIncidentReportsPDF(
    params?: Record<string, string | undefined>,
  ): Promise<AttachmentRef> {
    const qs = params
      ? new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][],
        ).toString()
      : '';
    return this.post(`/incidents/reports/export${qs ? `?${qs}` : ''}`);
  }
  // Sprint 42.2.2 — rights request response PDF export (unified File Component)
  async exportRightsRequestResponsePDF(
    casePublicId: string,
    payload: {
      responses: Array<{ subject?: string; body?: string; response_id?: string | null }>;
      request_type?: string;
    },
  ): Promise<AttachmentRef> {
    return this.post(`/cases/${encodeURIComponent(casePublicId)}/responses/export-pdf`, payload);
  }
  // ── Sprint 41.5.0 G1 — Cross-Border Filings lifecycle ─────
  async listCrossBorderFilings(params?: {
    status?: CrossBorderFilingStatus;
    jurisdiction?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<CrossBorderFiling>> {
    const qp: Record<string, string> = {};
    if (params?.status) qp.status = params.status;
    if (params?.jurisdiction) qp.jurisdiction = params.jurisdiction;
    if (params?.page !== undefined) qp.page = String(params.page);
    if (params?.page_size !== undefined) qp.page_size = String(params.page_size);
    return this.get(`/governance/cross-border/filings${this.buildQuery(qp)}`);
  }
  async createCrossBorderFiling(payload: CrossBorderFilingCreateInput): Promise<CrossBorderFiling> {
    return this.post('/governance/cross-border/filings', payload);
  }
  async getCrossBorderFiling(filingId: string): Promise<CrossBorderFiling> {
    return this.get(`/governance/cross-border/filings/${filingId}`);
  }
  async updateCrossBorderFilingStatus(
    filingId: string,
    payload: CrossBorderFilingStatusTransition,
  ): Promise<CrossBorderFiling> {
    return this.patch(`/governance/cross-border/filings/${filingId}/status`, payload);
  }
  async attachCrossBorderFilingEvidence(
    filingId: string,
    payload: AttachmentRef,
  ): Promise<CrossBorderFiling> {
    return this.post(`/governance/cross-border/filings/${filingId}/evidence`, payload);
  }
  async archiveCrossBorderFiling(filingId: string): Promise<CrossBorderFiling> {
    return this.post(`/governance/cross-border/filings/${filingId}/archive`, {});
  }
  /** OQ-86-6 (owner, 2026-10-26) — the installed forms another register owns (the picker turns them away). */
  async getCrossBorderFilingFormFence(): Promise<CrossBorderFilingFormFence> {
    return this.get('/governance/cross-border/filing-form-fence');
  }
  // ── Sprint 1.3.0: Governance -- Drift Events ─────────────
  async listDriftEvents(params?: Record<string, string>): Promise<PaginatedResponse<DriftEvent>> {
    return this.get(`/governance/drift${this.buildQuery(params)}`);
  }
  async getDriftEvent(eventId: string): Promise<DriftEvent> {
    return this.get(`/governance/drift/${eventId}`);
  }
  async updateDriftStatus(eventId: string, status: string, notes?: string): Promise<DriftEvent> {
    return this.patch(`/governance/drift/${eventId}/status`, { status, resolution_notes: notes });
  }
  async assignDriftEvent(eventId: string, assignedTo: string): Promise<DriftEvent> {
    return this.patch(`/governance/drift/${eventId}/assign`, { assigned_to: assignedTo });
  }
  async getDriftDashboard(): Promise<Record<string, unknown>> {
    return this.get('/governance/drift/dashboard');
  }
  // ── Sprint 1.3.0: Governance -- Compliance Scores ─────────
  async getOrgScore(): Promise<ComplianceScore> {
    return this.get('/governance/scores/org');
  }
  async getSystemScore(systemId: string): Promise<ComplianceScore> {
    return this.get(`/governance/scores/systems/${systemId}`);
  }
  async getVendorScore(vendorId: string): Promise<ComplianceScore> {
    return this.get(`/governance/scores/vendors/${vendorId}`);
  }
  // ── Sprint 1.3.0: Governance -- Deletion Playbooks ────────
  async listPlaybooks(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<DeletionPlaybook>> {
    return this.get(`/governance/playbooks${this.buildQuery(params)}`);
  }
  async getPlaybook(playbookId: string): Promise<DeletionPlaybook> {
    return this.get(`/governance/playbooks/${playbookId}`);
  }
  async createPlaybook(payload: Record<string, unknown>): Promise<DeletionPlaybook> {
    return this.post('/governance/playbooks', payload);
  }
  async updatePlaybook(
    playbookId: string,
    payload: Record<string, unknown>,
  ): Promise<DeletionPlaybook> {
    return this.patch(`/governance/playbooks/${playbookId}`, payload);
  }
  async archivePlaybook(playbookId: string): Promise<void> {
    return this.del(`/governance/playbooks/${playbookId}`);
  }
  // ── Sprint 1.3.0: Governance -- Deletion Executions ───────
  async initiateExecution(payload: Record<string, unknown>): Promise<DeletionExecution> {
    return this.post('/governance/executions', payload);
  }
  // Sprint 41.0.0 G3 — list active executions for the org
  async listGovernanceExecutions(
    status?: string,
  ): Promise<{ data: DeletionExecution[]; total: number }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.get(`/governance/executions${qs}`);
  }
  async getExecution(executionId: string): Promise<DeletionExecution> {
    return this.get(`/governance/executions/${executionId}`);
  }
  async updateExecutionStep(
    executionId: string,
    stepId: string,
    payload: Record<string, unknown>,
  ): Promise<DeletionExecution> {
    return this.patch(`/governance/executions/${executionId}/steps/${stepId}`, payload);
  }
  async attachStepEvidence(
    executionId: string,
    stepId: string,
    evidenceId: string,
  ): Promise<DeletionExecution> {
    return this.post(`/governance/executions/${executionId}/steps/${stepId}/evidence`, {
      evidence_id: evidenceId,
    });
  }
  async listTicketExecutions(ticketId: string): Promise<{ data: DeletionExecution[] }> {
    return this.get(`/rights-requests/${ticketId}/executions`);
  }
  // ── Sprint 1.3.0: Governance -- Approvals ─────────────────
  async listApprovals(params?: Record<string, string>): Promise<PaginatedResponse<ApprovalRecord>> {
    return this.get(`/governance/approvals${this.buildQuery(params)}`);
  }
  async listPendingApprovals(): Promise<{ data: ApprovalRecord[] }> {
    return this.get('/governance/approvals/pending');
  }
  async getApproval(approvalId: string): Promise<ApprovalRecord> {
    return this.get(`/governance/approvals/${approvalId}`);
  }
  async approveRequest(
    approvalId: string,
    notes: string,
    evidenceIds?: string[],
  ): Promise<ApprovalRecord> {
    return this.post(`/governance/approvals/${approvalId}/approve`, {
      decision_notes: notes,
      evidence_ids: evidenceIds ?? undefined,
    });
  }
  async rejectRequest(
    approvalId: string,
    notes: string,
    evidenceIds?: string[],
  ): Promise<ApprovalRecord> {
    return this.post(`/governance/approvals/${approvalId}/reject`, {
      decision_notes: notes,
      evidence_ids: evidenceIds ?? undefined,
    });
  }
  async getPendingApprovalCount(): Promise<Record<string, unknown>> {
    return this.get('/governance/approvals/count');
  }
  // ── Sprint 1.3.0: Governance -- Audit ─────────────────────
  // Sprint 41.2.0 E6 — paginated shape.
  async getGovernanceAudit(
    params?: Record<string, string>,
  ): Promise<{ data: GovernanceAuditEvent[]; total: number; page: number; page_size: number }> {
    return this.get(`/governance/audit${this.buildQuery(params)}`);
  }
  async getGovernanceEntityAudit(
    entityType: string,
    entityId: string,
  ): Promise<{ data: GovernanceAuditEvent[] }> {
    return this.get(`/governance/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 1.3.0: Governance -- Evidence ──────────────────
  async listGovernanceEvidence(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    return this.get(`/governance/evidence${this.buildQuery(params)}`);
  }
  async createGovernanceEvidence(
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.post('/governance/evidence', payload);
  }
  async linkGovernanceEvidence(
    evidenceId: string,
    entityType: string,
    entityId: string,
  ): Promise<Record<string, unknown>> {
    return this.post(`/governance/evidence/${evidenceId}/link`, {
      entity_type: entityType,
      entity_id: entityId,
    });
  }
  async deleteGovernanceEvidence(evidenceId: string): Promise<void> {
    return this.del(`/governance/evidence/${evidenceId}`);
  }
  // ── Sprint 20.9.0: Vendor Governance Maturity ───────────────
  async createOnboardingWorkflow(
    payload: Record<string, unknown>,
  ): Promise<VendorOnboardingWorkflow> {
    return this.post('/governance/onboarding', payload);
  }
  // Sprint 41.2.0 E4 — backend now returns paginated shape
  async listOnboardingWorkflows(params?: Record<string, string>): Promise<{
    data: VendorOnboardingWorkflow[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/governance/onboarding${this.buildQuery(params)}`);
  }
  async getOnboardingStatus(workflowId: string): Promise<VendorOnboardingWorkflow> {
    return this.get(`/governance/onboarding/${workflowId}`);
  }
  async advanceOnboardingStage(
    workflowId: string,
    notes: string,
  ): Promise<VendorOnboardingWorkflow> {
    return this.post(`/governance/onboarding/${workflowId}/advance`, { notes });
  }
  async createDpaReview(payload: Record<string, unknown>): Promise<VendorDpaReview> {
    return this.post('/governance/dpa-reviews', payload);
  }
  // Sprint 41.0.0 G6 — list DPA reviews (optional vendor filter)
  // Sprint 41.2.0 E4 — backend now returns paginated shape
  async listDpaReviews(params?: Record<string, string>): Promise<{
    data: VendorDpaReview[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.get(`/governance/dpa-reviews${query}`);
  }
  async completeDpaReview(
    reviewId: string,
    data: Record<string, unknown>,
  ): Promise<VendorDpaReview> {
    return this.post(`/governance/dpa-reviews/${reviewId}/complete`, data);
  }
  async linkSecurityAssessment(reviewId: string, assessmentId: string): Promise<VendorDpaReview> {
    return this.post(`/governance/dpa-reviews/${reviewId}/link-assessment`, {
      assessment_id: assessmentId,
    });
  }
  async getCombinedDpaAssessment(vendorId: string): Promise<VendorDpaReview> {
    return this.get(`/governance/dpa-reviews/vendor/${vendorId}`);
  }
  async createPeriodicReview(payload: Record<string, unknown>): Promise<VendorPeriodicReview> {
    return this.post('/governance/periodic-reviews', payload);
  }
  // Sprint 41.2.0 E4 — backend now returns paginated shape
  async listPeriodicReviews(params?: Record<string, string>): Promise<{
    data: VendorPeriodicReview[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/governance/periodic-reviews${this.buildQuery(params)}`);
  }
  async completePeriodicReview(
    reviewId: string,
    data: Record<string, unknown>,
  ): Promise<VendorPeriodicReview> {
    return this.post(`/governance/periodic-reviews/${reviewId}/complete`, data);
  }
  async triggerReassessment(vendorId: string, reason: string): Promise<Record<string, unknown>> {
    return this.post(`/governance/reassessment/${vendorId}`, { reason });
  }
  async initiateOffboarding(
    vendorId: string,
    data: Record<string, unknown>,
  ): Promise<VendorOffboardingTask> {
    return this.post(`/governance/offboarding/${vendorId}`, data);
  }
  async getOffboardingStatus(taskId: string): Promise<VendorOffboardingTask> {
    return this.get(`/governance/offboarding/${taskId}/status`);
  }
  async verifyDataReturn(
    taskId: string,
    data: Record<string, unknown>,
  ): Promise<VendorOffboardingTask> {
    return this.post(`/governance/offboarding/${taskId}/verify-data`, data);
  }
  async signOffOffboarding(taskId: string, notes: string): Promise<VendorOffboardingTask> {
    return this.post(`/governance/offboarding/${taskId}/sign-off`, { notes });
  }
  // ── Sprint 1.4.0: Consent -- Policies ──────────────────────
  async listConsentPolicies(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentPolicy>> {
    return this.get(`/consent/policies${this.buildQuery(params)}`);
  }
  async getConsentPolicy(policyId: string): Promise<ConsentPolicy> {
    return this.get(`/consent/policies/${policyId}`);
  }
  async createConsentPolicy(payload: Record<string, unknown>): Promise<ConsentPolicy> {
    return this.post('/consent/policies', payload);
  }
  async updateConsentPolicy(
    policyId: string,
    payload: Record<string, unknown>,
  ): Promise<ConsentPolicy> {
    return this.patch(`/consent/policies/${policyId}`, payload);
  }
  async submitPolicyForReview(policyId: string): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/submit-review`);
  }
  /**
   * Sprint 63.30.0, gate E12.5 pass 1 (C-P3(8)) — end a review round that
   * cannot finish: the round's decisions are archived and the policy is a
   * draft again. The reason is required (1–2000 characters); 409
   * `POLICY_NOT_IN_REVIEW` / `POLICY_CHANGED_CONCURRENTLY`.
   */
  async returnConsentPolicyToDraft(policyId: string, reason: string): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/return-to-draft`, { reason });
  }
  async approvePolicyReview(
    policyId: string,
    decision: string,
    comments?: string,
  ): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/approve`, { decision, comments });
  }
  async activatePolicy(policyId: string, body?: Record<string, unknown>): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/activate`, body);
  }
  async suspendPolicy(policyId: string, reason?: string): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/suspend`, reason ? { reason } : undefined);
  }
  async retirePolicy(policyId: string): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/retire`);
  }
  async getPolicyVersions(policyId: string): Promise<ConsentPolicyVersion[]> {
    return this.get(`/consent/policies/${policyId}/versions`);
  }
  // ── Sprint 1.4.0: Consent -- Purposes ─────────────────────
  async listConsentPurposes(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentCatalogPurpose>> {
    return this.get(`/consent/purposes${this.buildQuery(params)}`);
  }
  async createConsentPurpose(payload: Record<string, unknown>): Promise<ConsentCatalogPurpose> {
    return this.post('/consent/purposes', payload);
  }
  async updateConsentPurpose(
    purposeId: string,
    payload: Record<string, unknown>,
  ): Promise<ConsentCatalogPurpose> {
    return this.patch(`/consent/purposes/${purposeId}`, payload);
  }
  async deprecateConsentPurpose(purposeId: string): Promise<ConsentCatalogPurpose> {
    return this.post(`/consent/purposes/${purposeId}/deprecate`);
  }
  // ── Sprint 1.4.0: Consent -- Legal Bases ──────────────────
  async listConsentLegalBases(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentCatalogLegalBase>> {
    return this.get(`/consent/legal-bases${this.buildQuery(params)}`);
  }
  async createConsentLegalBase(payload: Record<string, unknown>): Promise<ConsentCatalogLegalBase> {
    return this.post('/consent/legal-bases', payload);
  }
  // ── Sprint 1.4.0: Consent -- Workflows ────────────────────
  async listConsentWorkflows(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentWorkflow>> {
    return this.get(`/consent/workflows${this.buildQuery(params)}`);
  }
  /**
   * Sprint 63.18.0 (#377) — the server-side search behind the Withdrawal
   * Propagation picker: `GET /consent/workflows?search=`. The route matches the
   * text, case-insensitively and as a literal (never a pattern), against the
   * workflow's type, data subject reference, channel and id, and refuses a
   * `search` over 200 characters with a 400 — so a longer paste is cut here.
   * An empty term sends no `search`. Other filters ride in `params`.
   */
  async searchConsentWorkflows(
    search: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentWorkflow>> {
    const query: Record<string, string> = { ...params };
    const term = search.trim().slice(0, 200);
    if (term) query.search = term;
    return this.get(`/consent/workflows${this.buildQuery(query)}`);
  }
  async createConsentWorkflow(payload: Record<string, unknown>): Promise<ConsentWorkflow> {
    return this.post('/consent/workflows', payload);
  }
  async getConsentWorkflow(workflowId: string): Promise<ConsentWorkflow> {
    return this.get(`/consent/workflows/${workflowId}`);
  }
  async updateConsentWorkflow(
    workflowId: string,
    payload: Record<string, unknown>,
  ): Promise<ConsentWorkflow> {
    return this.patch(`/consent/workflows/${workflowId}`, payload);
  }
  async updateWorkflowStatus(workflowId: string, status: string): Promise<ConsentWorkflow> {
    return this.patch(`/consent/workflows/${workflowId}/status`, { status });
  }
  async updateWorkflowAssessment(
    workflowId: string,
    outcome: string,
    justification?: string,
  ): Promise<ConsentWorkflow> {
    return this.patch(`/consent/workflows/${workflowId}/assessment`, { outcome, justification });
  }
  async addWorkflowAction(
    workflowId: string,
    payload: Record<string, unknown>,
  ): Promise<ConsentWorkflow> {
    return this.post(`/consent/workflows/${workflowId}/actions`, payload);
  }
  async updateWorkflowAction(
    workflowId: string,
    actionId: string,
    payload: Record<string, unknown>,
  ): Promise<ConsentWorkflow> {
    return this.patch(`/consent/workflows/${workflowId}/actions/${actionId}`, payload);
  }
  async getWorkflowDashboard(): Promise<Record<string, unknown>> {
    return this.get('/consent/workflows/dashboard');
  }
  // ── Sprint 40.0.0 / 44.4.0: Consent Workflow Bulk Import ───────────
  async importConsentWorkflows(
    csvData: string,
    filename: string,
    options?: { createProfiles?: boolean },
  ): Promise<ImportJob> {
    const createProfiles = options?.createProfiles;
    const query =
      typeof createProfiles === 'boolean'
        ? this.buildQuery({ create_profiles: String(createProfiles) })
        : '';
    return this.post(`/consent/workflows/import${query}`, { csv: csvData, filename });
  }
  async getConsentImportHistory(): Promise<ImportJob[]> {
    const res: any = await this.get('/consent/workflows/import/history');
    return res.data ?? res;
  }
  // ── Sprint 1.4.0: Consent -- CMP Snapshots ───────────────
  async listCmpSnapshots(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentCmpSnapshot>> {
    return this.get(`/consent/snapshots${this.buildQuery(params)}`);
  }
  async createCmpSnapshot(payload: Record<string, unknown>): Promise<ConsentCmpSnapshot> {
    return this.post('/consent/snapshots', payload);
  }
  async getCmpSnapshot(snapshotId: string): Promise<ConsentCmpSnapshot> {
    return this.get(`/consent/snapshots/${snapshotId}`);
  }
  async compareCmpSnapshots(
    snapshotId1: string,
    snapshotId2: string,
  ): Promise<Record<string, unknown>> {
    return this.get(
      `/consent/snapshots/compare?snapshot_id_1=${snapshotId1}&snapshot_id_2=${snapshotId2}`,
    );
  }
  // ── Sprint 1.4.0: Consent -- Change Awareness ────────────
  async listChangeAwareness(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ChangeAwarenessEntry>> {
    return this.get(`/consent/change-awareness${this.buildQuery(params)}`);
  }
  async createChangeAwareness(payload: Record<string, unknown>): Promise<ChangeAwarenessEntry> {
    return this.post('/consent/change-awareness', payload);
  }
  async updateChangeAwarenessStatus(
    entryId: string,
    status: string,
    notes?: string,
  ): Promise<ChangeAwarenessEntry> {
    return this.patch(`/consent/change-awareness/${entryId}/status`, {
      status,
      resolution_notes: notes,
    });
  }
  async getChangeAwarenessDashboard(): Promise<Record<string, unknown>> {
    return this.get('/consent/change-awareness/dashboard');
  }
  // ── Sprint 1.4.0: Consent -- Audit ────────────────────────
  async getConsentAudit(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentAuditEvent>> {
    return this.get(`/consent/audit${this.buildQuery(params)}`);
  }
  async getConsentEntityAudit(entityType: string, entityId: string): Promise<ConsentAuditEvent[]> {
    return this.get(`/consent/audit/${entityType}/${entityId}`);
  }
  // Sprint 42.2.3 E2 — returns AttachmentRef (was Blob).
  async exportConsentAuditCSV(params?: Record<string, string>): Promise<AttachmentRef> {
    return this.get(`/consent/audit/export${this.buildQuery(params)}`);
  }
  // ── Sprint 62.35.0 (Slice C): pre-collection protection assessments ──
  async listPreCollectionAssessments(params?: {
    page?: number;
    page_size?: number;
    status?: string;
  }): Promise<{
    data: import('@vn-grc/shared').PreCollectionAssessmentView[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/assessments/pre-collection-assessments${this.buildQuery(params)}`);
  }
  async createPreCollectionAssessment(
    payload: import('@vn-grc/shared').CreatePreCollectionAssessmentBody,
  ): Promise<{ data: import('@vn-grc/shared').PreCollectionAssessmentView }> {
    return this.post('/assessments/pre-collection-assessments', payload);
  }
  async updatePreCollectionAssessment(
    id: string,
    payload: import('@vn-grc/shared').UpdatePreCollectionAssessmentBody,
  ): Promise<{ data: import('@vn-grc/shared').PreCollectionAssessmentView }> {
    return this.patch(`/assessments/pre-collection-assessments/${encodeURIComponent(id)}`, payload);
  }
  // ── Sprint 1.5.0: Assessments -- DPIAs ─────────────────────
  /**
   * `ids` — comma-separated, deduplicated, 1–100 record ids: the list
   * restricted to those records (fix-sprint-20261003-0545 F3). A label read
   * names exactly the records a linking record cites, never a first page.
   */
  async listDpias(
    params?: Record<string, string> & { ids?: string },
  ): Promise<PaginatedResponse<DpiaRecord>> {
    return this.get(`/assessments/dpias${this.buildQuery(params)}`);
  }
  async createDpia(payload: Record<string, unknown>): Promise<DpiaRecord> {
    return this.post('/assessments/dpias', payload);
  }
  async getDpia(dpiaId: string): Promise<DpiaRecord> {
    return this.get(`/assessments/dpias/${dpiaId}`);
  }
  async updateDpia(dpiaId: string, payload: Record<string, unknown>): Promise<DpiaRecord> {
    return this.patch(`/assessments/dpias/${dpiaId}`, payload);
  }
  async submitDpiaForReview(dpiaId: string): Promise<DpiaRecord> {
    return this.post(`/assessments/dpias/${dpiaId}/submit-review`);
  }
  async withdrawDpiaFromReview(dpiaId: string): Promise<DpiaRecord> {
    return this.post(`/assessments/dpias/${dpiaId}/withdraw-review`, {});
  }
  async approveDpia(dpiaId: string, decision: string, comments?: string): Promise<DpiaRecord> {
    return this.post(`/assessments/dpias/${dpiaId}/approve`, { decision, comments });
  }
  /** Sprint 63.17.0 — a rejected DPIA comes back as a draft; its round is archived. */
  async reopenDpia(dpiaId: string): Promise<DpiaRecord> {
    return this.post(`/assessments/dpias/${dpiaId}/reopen`);
  }
  async deprecateDpia(dpiaId: string): Promise<DpiaRecord> {
    return this.post(`/assessments/dpias/${dpiaId}/deprecate`);
  }
  async getDpiaApprovalLog(dpiaId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/assessments/dpias/${dpiaId}/approval-log`);
  }

  // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
  //
  // Two records, one contract. The GET exists so a surface can say "undated,
  // and why" with the absence reason coming from the server rather than being
  // re-derived in the browser — there is exactly one declaration of what an
  // absent commencement means, and it is not here.
  async getDpiaCommencement(dpiaId: string): Promise<CommencementEnvelope> {
    return this.get(`/assessments/dpias/${dpiaId}/commencement`);
  }
  async recordDpiaCommencement(
    dpiaId: string,
    payload: RecordCommencementBody,
  ): Promise<CommencementEnvelope> {
    return this.patch(`/assessments/dpias/${dpiaId}/commencement`, payload);
  }
  async getTransferCommencement(assessmentId: string): Promise<CommencementEnvelope> {
    return this.get(`/assessments/transfers/${assessmentId}/commencement`);
  }
  async recordTransferCommencement(
    assessmentId: string,
    payload: RecordCommencementBody,
  ): Promise<CommencementEnvelope> {
    return this.patch(`/assessments/transfers/${assessmentId}/commencement`, payload);
  }

  // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──────────
  //
  // Eight routes, traced from `modules/assessments/routes.ts` (R-26): the
  // quartet at `/dpias/:dpiaId/…` and its mirror at `/transfers/:assessmentId/…`,
  // each a GET beside a PATCH. The GET exists so a surface can say "no filing
  // recorded, and why" without mutating anything, with the absence reason coming
  // from the server rather than re-derived here.
  //
  // The verbs are the routes' own: **PATCH**, not POST. A first filing is
  // write-once at the service, so a POST-shaped client method would suggest a
  // second one is creatable.
  async getDpiaDossierFirstFiling(dpiaId: string): Promise<DossierFirstFilingEnvelope> {
    return this.get(`/assessments/dpias/${encodeURIComponent(dpiaId)}/dossier-first-filing`);
  }
  async recordDpiaDossierFirstFiling(
    dpiaId: string,
    payload: RecordDossierFirstFilingBody,
  ): Promise<DossierFirstFilingEnvelope> {
    return this.patch(
      `/assessments/dpias/${encodeURIComponent(dpiaId)}/dossier-first-filing`,
      payload,
    );
  }
  async getTransferDossierFirstFiling(assessmentId: string): Promise<DossierFirstFilingEnvelope> {
    return this.get(
      `/assessments/transfers/${encodeURIComponent(assessmentId)}/dossier-first-filing`,
    );
  }
  async recordTransferDossierFirstFiling(
    assessmentId: string,
    payload: RecordDossierFirstFilingBody,
  ): Promise<DossierFirstFilingEnvelope> {
    return this.patch(
      `/assessments/transfers/${encodeURIComponent(assessmentId)}/dossier-first-filing`,
      payload,
    );
  }
  async getDpiaRegisteredParticularsChange(
    dpiaId: string,
  ): Promise<RegisteredParticularsChangeEnvelope> {
    return this.get(
      `/assessments/dpias/${encodeURIComponent(dpiaId)}/registered-particulars-change`,
    );
  }
  async recordDpiaRegisteredParticularsChange(
    dpiaId: string,
    payload: RecordRegisteredParticularsChangeBody,
  ): Promise<RegisteredParticularsChangeEnvelope> {
    return this.patch(
      `/assessments/dpias/${encodeURIComponent(dpiaId)}/registered-particulars-change`,
      payload,
    );
  }
  async getTransferRegisteredParticularsChange(
    assessmentId: string,
  ): Promise<RegisteredParticularsChangeEnvelope> {
    return this.get(
      `/assessments/transfers/${encodeURIComponent(assessmentId)}/registered-particulars-change`,
    );
  }
  async recordTransferRegisteredParticularsChange(
    assessmentId: string,
    payload: RecordRegisteredParticularsChangeBody,
  ): Promise<RegisteredParticularsChangeEnvelope> {
    return this.patch(
      `/assessments/transfers/${encodeURIComponent(assessmentId)}/registered-particulars-change`,
      payload,
    );
  }

  // Sprint 47.9.6 — Forseti DPIA autocomplete cross-module workflow exemplar.
  // Sprint 48.1.0 — added optional `frameworkVariant` to drive the
  //                 per-framework templates + per-framework regulatory
  //                 pack search scope. Defaults server-side to 'gdpr'.
  async draftDpiaAutocomplete(
    dpiaId: string,
    activityId: string,
    frameworkVariant?: 'gdpr' | 'pdpl' | 'nis2' | 'iso-27001' | 'soc2',
    locale?: string,
  ): Promise<
    DpiaAutocompleteDraft & {
      framework_variant?: 'gdpr' | 'pdpl' | 'nis2' | 'iso-27001' | 'soc2';
      cross_framework_conflict_ids?: string[];
    }
  > {
    return this.post(`/assessments/dpias/${dpiaId}/autocomplete`, {
      activity_id: activityId,
      framework_variant: frameworkVariant,
      // Pass the active UI locale so drafted prose matches the on-screen
      // language (the backend injects an output-language directive).
      locale,
    });
  }
  async acceptDpiaAutocomplete(
    dpiaId: string,
    payload: {
      draft_id: string;
      accepted_fields: DpiaAutocompleteFieldKey[];
      rejected_fields?: DpiaAutocompleteFieldKey[];
    },
  ): Promise<{ accepted: DpiaAutocompleteFieldKey[]; rejected: DpiaAutocompleteFieldKey[] }> {
    return this.post(`/assessments/dpias/${dpiaId}/autocomplete/accept`, payload);
  }
  // Sprint 61.1.1 — template-agnostic Forseti compliance form-draft engine.
  async draftComplianceForm(request: FormAutocompleteRequest): Promise<FormAutocompleteDraft> {
    return this.post(`/ai-advisor/form-drafts`, request);
  }
  async resolveComplianceFormDraft(
    draftId: string,
    payload: { accepted_fields: string[]; rejected_fields: string[] },
  ): Promise<FormAutocompleteResolveResult> {
    return this.post(`/ai-advisor/form-drafts/${draftId}/resolve`, payload);
  }
  // Sprint 48.1.5 — Breach notification timeline.
  async computeBreachTimeline(
    incidentId: string,
    payload?: { installed_frameworks?: string[] },
  ): Promise<unknown> {
    return this.post(`/incidents/${incidentId}/breach-timeline`, payload ?? {});
  }
  async acknowledgeBreachTimeline(
    timelineId: string,
    payload?: { note?: string },
  ): Promise<{ status: string }> {
    return this.post(`/incidents/breach-timelines/${timelineId}/acknowledge`, payload ?? {});
  }
  // Sprint 48.1.7 — Privacy scanner anomaly explanation.
  async explainPrivacyScannerAnomalies(scanId: string): Promise<unknown> {
    return this.post(`/privacy-scanner/scans/${scanId}/explain-anomalies`, {});
  }
  async resolvePrivacyScannerAnomalyExplanation(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string; remediation_task_ids: string[] }> {
    return this.post(`/privacy-scanner/anomaly-explanations/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  // Sprint 48.1.8 — Per-cookie / per-tracker AI insight.
  async producePrivacyScannerCookieInsight(scanId: string): Promise<unknown> {
    return this.post(`/privacy-scanner/scans/${scanId}/cookie-insight`, {});
  }
  async resolvePrivacyScannerCookieInsight(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/privacy-scanner/cookie-insights/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  // Sprint 48.2.7 — Remediation plan generator from compliance gaps.
  async generateRemediationPlanFromGaps(
    sourceModule: string,
    sourceParentId: string,
    gaps: Array<{
      id: string;
      severity: 'critical' | 'high' | 'medium' | 'low';
      description: string;
      theme?: 'technical' | 'process' | 'training' | 'governance';
      source_module?: string;
    }>,
  ): Promise<unknown> {
    return this.post(`/remediation-plans/generate-from-gaps`, {
      source_module: sourceModule,
      source_parent_id: sourceParentId,
      gaps,
    });
  }
  // Sprint 48.2.7 fix — cross-module gap-set ingestion. Returns accepted
  // gap-sets from consent / policy / vendor draft collections in a unified
  // UpstreamGap[] shape for the RemediationPlanGeneratorPanel.
  async loadUpstreamRemediationGaps(
    source?: 'consent' | 'policy' | 'vendor',
    limit?: number,
  ): Promise<{
    data: Array<{
      id: string;
      source: 'consent' | 'policy' | 'vendor';
      source_record_id: string;
      gap_id: string;
      severity: 'critical' | 'high' | 'medium' | 'low';
      description: string;
      theme?: 'technical' | 'process' | 'training' | 'governance';
      framework_codes?: string[];
    }>;
  }> {
    const params: Record<string, string | number> = {};
    if (source) params.source = source;
    if (typeof limit === 'number') params.limit = limit;
    return this.get(`/remediation-plans/upstream-gaps${this.buildQuery(params)}`);
  }
  async resolveRemediationPlanDraft(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string; created_task_ids: string[] }> {
    return this.post(`/remediation-plans/generate/${draftId}/resolve`, { decision, note });
  }
  async exportRemediationPlanDocx(draftId: string): Promise<AttachmentRef> {
    return this.post<AttachmentRef>(`/remediation-plans/generate/${draftId}/export-docx`, {});
  }
  // Sprint 48.2.6 — Governance document policy gap detection.
  async runPolicyGapAnalysis(documentId: string): Promise<unknown> {
    return this.post(`/governance-documents/${documentId}/gap-analysis`, {});
  }
  async resolvePolicyGapAnalysis(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/governance-documents/gap-analysis/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  async exportPolicyGapAnalysisDocx(draftId: string): Promise<AttachmentRef> {
    return this.post<AttachmentRef>(
      `/governance-documents/gap-analysis/${draftId}/export-docx`,
      {},
    );
  }
  // Sprint 48.2.5 — Maturity assessment AI gap walkthrough.
  async runMaturityWalkthrough(assessmentId: string): Promise<unknown> {
    return this.post(`/maturity-assessments/${assessmentId}/walkthrough`, {});
  }
  async resolveMaturityWalkthrough(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/maturity-assessments/walkthrough/${draftId}/resolve`, { decision, note });
  }
  async exportMaturityWalkthroughDocx(draftId: string): Promise<AttachmentRef> {
    return this.post<AttachmentRef>(`/maturity-assessments/walkthrough/${draftId}/export-docx`, {});
  }
  // Sprint 48.2.4 — Risk acceptance reasoning generator.
  async draftRiskAcceptanceReasoning(acceptanceId: string): Promise<unknown> {
    return this.post(`/risk-acceptances/${acceptanceId}/draft-reasoning`, {});
  }
  async resolveRiskAcceptanceReasoning(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/risk-acceptances/reasoning/${draftId}/resolve`, { decision, note });
  }
  // Sprint 48.2.3 — Contract redline assistant + clause library.
  async runContractRedline(
    contractId: string,
    inlineText?: string,
    jurisdiction?: string,
  ): Promise<unknown> {
    return this.post(`/contracts/${contractId}/redline-assistant`, {
      inline_text: inlineText,
      jurisdiction: jurisdiction || undefined,
    });
  }
  async resolveContractRedline(
    draftId: string,
    decision: 'accepted' | 'rejected' | 'partially_accepted',
    appliedSuggestionIds?: string[],
    note?: string,
  ): Promise<{ status: string; applied_count: number }> {
    return this.post(`/contracts/redline/${draftId}/resolve`, {
      decision,
      applied_suggestion_ids: appliedSuggestionIds,
      note,
    });
  }
  async listClauseLibrary(jurisdiction?: string): Promise<unknown> {
    const qs = jurisdiction ? `?jurisdiction=${encodeURIComponent(jurisdiction)}` : '';
    return this.get(`/contracts/clause-library${qs}`);
  }
  async createClauseLibraryEntry(input: {
    slug: string;
    title: string;
    body: string;
    jurisdiction?: string;
    tags?: string[];
    must_contain?: string[];
    severity_when_missing?: 'critical' | 'high' | 'medium' | 'low';
  }): Promise<unknown> {
    return this.post(`/contracts/clause-library`, input);
  }
  async updateClauseLibraryEntry(
    slug: string,
    input: {
      title?: string;
      body?: string;
      jurisdiction?: string;
      tags?: string[];
      must_contain?: string[];
      severity_when_missing?: 'critical' | 'high' | 'medium' | 'low';
    },
  ): Promise<unknown> {
    return this.put(`/contracts/clause-library/${slug}`, input);
  }
  async deleteClauseLibraryEntry(slug: string): Promise<unknown> {
    return this.del(`/contracts/clause-library/${slug}`);
  }
  // Sprint 48.2.2 — Contract obligation extraction.
  async extractContractObligations(contractId: string, inlineText?: string): Promise<unknown> {
    return this.post(`/contracts/${contractId}/extract-obligations`, {
      inline_text: inlineText,
    });
  }
  async resolveContractObligations(
    draftId: string,
    decision: 'accepted' | 'rejected',
    overrides?: Record<string, { summary?: string; due_date?: string }>,
    note?: string,
  ): Promise<{ status: string; created_task_ids: string[] }> {
    return this.post(`/contracts/obligations/${draftId}/resolve`, {
      decision,
      overrides,
      note,
    });
  }
  // Sprint 48.2.1 — Sub-processor change impact.
  async assessSubprocessorChange(changeId: string): Promise<unknown> {
    return this.post(`/vendor-risk/subprocessor-changes/${changeId}/assess`, {});
  }
  async resolveSubprocessorImpact(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string; notification_task_id: string | null }> {
    return this.post(`/vendor-risk/subprocessor-impact/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  // Sprint 48.2.0 — Vendor onboarding evidence collector.
  async collectVendorOnboardingEvidence(caseId: string): Promise<unknown> {
    return this.post(`/vendor-risk/onboarding-cases/${caseId}/collect-evidence`, {});
  }
  async resolveVendorOnboardingEvidence(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string; created_task_ids: string[] }> {
    return this.post(`/vendor-risk/onboarding-evidence/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  // Sprint 48.1.9 — Dossier filing pre-flight + auto-assembly.
  async runDossierPreflight(filingId: string): Promise<unknown> {
    return this.post(`/assessments/dossier-filings/${filingId}/preflight`, {});
  }
  async resolveDossierPreflight(
    draftId: string,
    decision: 'accepted' | 'rejected' | 'overridden',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/assessments/dossier-filings/preflight/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  async assembleDossierPackage(
    filingId: string,
    preflightDraftId?: string,
    forceThroughBlock?: boolean,
  ): Promise<unknown> {
    return this.post(`/assessments/dossier-filings/${filingId}/auto-assemble`, {
      preflight_draft_id: preflightDraftId,
      force_through_block: forceThroughBlock === true,
    });
  }
  // Sprint 48.1.6 — Authority request response draft.
  async draftAuthorityResponse(incidentId: string, authorityRequestId: string): Promise<unknown> {
    return this.post(
      `/incidents/${incidentId}/authority-requests/${authorityRequestId}/draft-response`,
      {},
    );
  }
  async resolveAuthorityResponseDraft(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/incidents/authority-response-drafts/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  /** Decide one drafted answer; the draft itself resolves once every answer is decided. */
  async resolveAuthorityResponseQuestion(
    draftId: string,
    questionId: string,
    decision: 'accepted' | 'rejected' | 'needs_counsel',
  ): Promise<{ draft_status: string; question_decision: string }> {
    return this.post(
      `/incidents/authority-response-drafts/${encodeURIComponent(draftId)}/questions/${encodeURIComponent(questionId)}/resolve`,
      { decision },
    );
  }
  // Sprint 48.1.4 — Consent gap analysis Forseti workflow.
  async runConsentGapAnalysis(payload: {
    framework_codes?: Array<'gdpr' | 'pdpl' | 'soc2'>;
    org_flags?: Record<string, boolean>;
  }): Promise<unknown> {
    return this.post('/consent/gap-analysis', payload);
  }
  async triageConsentGap(
    analysisId: string,
    payload: {
      row_index: number;
      decision: 'dismissed_not_applicable' | 'resolved';
      note?: string;
    },
  ): Promise<{ status: string }> {
    return this.post(`/consent/gap-analysis/${analysisId}/triage`, payload);
  }
  /**
   * Sprint 62.27.0 gate E12.5 pass 2 — the consent families the organisation
   * has installed, each with the installed instruments that answer for it. The
   * gap analysis page offers only these.
   */
  async getConsentGapAnalysisFamilies(): Promise<{
    families: Array<{ family: 'gdpr' | 'pdpl' | 'soc2'; framework_codes: string[] }>;
  }> {
    return this.get('/consent/gap-analysis/families');
  }
  /**
   * Sprint 62.27.0 gate E12.5 pass 2 (OD40) — a person acknowledges a
   * purpose's sensitive-data ground review. Who and when are stamped by the
   * server; a machine actor is refused.
   */
  async acknowledgeSensitiveDataGroundReview(
    purposeId: string,
    payload: { note: string },
  ): Promise<{
    data: {
      _id: string;
      purpose_id: string;
      note: string;
      acknowledged_by: string;
      acknowledged_at: string;
    };
  }> {
    return this.post(
      `/consent/purposes/${encodeURIComponent(purposeId)}/sensitive-data-ground-review/acknowledgements`,
      payload,
    );
  }
  // Sprint 48.1.3 — Cross-border TIA Forseti autocomplete.
  async draftTiaAutocomplete(transferId: string, locale?: string): Promise<TiaAutocompleteDraft> {
    // Pass the active UI locale so drafted prose matches the on-screen
    // language (the backend injects an output-language directive).
    return this.post(`/assessments/transfers/${transferId}/tia-autocomplete`, { locale });
  }
  async acceptTiaAutocomplete(
    transferId: string,
    payload: {
      draft_id: string;
      accepted_fields: TiaAutocompleteFieldKey[];
      rejected_fields?: TiaAutocompleteFieldKey[];
    },
  ): Promise<TiaAutocompleteAcceptResult> {
    return this.post(`/assessments/transfers/${transferId}/tia-autocomplete/accept`, payload);
  }
  // Sprint 47.5.8.4a — cross-module Forseti advisor-summary endpoints.
  async getDpiaAdvisorSummary(dpiaId: string): Promise<AdvisorSummary> {
    return this.get(`/assessments/dpias/${dpiaId}/advisor-summary`);
  }
  async getDossierFilingAdvisorSummary(filingId: string): Promise<AdvisorSummary> {
    return this.get(`/assessments/dossier-filings/${filingId}/advisor-summary`);
  }
  async getTransferAssessmentAdvisorSummary(assessmentId: string): Promise<AdvisorSummary> {
    return this.get(`/assessments/transfers/${assessmentId}/advisor-summary`);
  }
  async getActivityAdvisorSummary(activityId: string): Promise<AdvisorSummary> {
    return this.get(`/data-mapping/activities/${activityId}/advisor-summary`);
  }
  async getRightsRequestAdvisorSummary(casePublicId: string): Promise<AdvisorSummary> {
    return this.get(`/cases/${casePublicId}/advisor-summary`);
  }
  // Sprint 47.5.8.4b — Risk + Vendor lane advisor-summary endpoints.
  async getVendorAdvisorSummary(vendorId: string): Promise<AdvisorSummary> {
    return this.get(`/governance/vendors/${vendorId}/advisor-summary`);
  }
  async getContractAdvisorSummary(contractId: string): Promise<AdvisorSummary> {
    return this.get(`/contracts/${contractId}/advisor-summary`);
  }
  async getVendorRiskAcceptanceAdvisorSummary(acceptanceId: string): Promise<AdvisorSummary> {
    return this.get(`/vendor-risk/acceptances/${acceptanceId}/advisor-summary`);
  }
  async getPlaybookActivationAdvisorSummary(activationId: string): Promise<AdvisorSummary> {
    return this.get(`/incident-ops/activations/${activationId}/advisor-summary`);
  }
  async getBreachNotificationAdvisorSummary(notificationId: string): Promise<AdvisorSummary> {
    return this.get(`/incidents/breach-notifications/${notificationId}/advisor-summary`);
  }
  async getAuthorityRequestAdvisorSummary(requestId: string): Promise<AdvisorSummary> {
    return this.get(`/incidents/authority-requests/${requestId}/advisor-summary`);
  }
  async getSupplementRequestAdvisorSummary(supplementId: string): Promise<AdvisorSummary> {
    return this.get(`/incidents/supplements/${supplementId}/advisor-summary`);
  }
  // Sprint 47.5.8.4c — Governance lane advisor-summary endpoints.
  async getCrossBorderFilingAdvisorSummary(filingId: string): Promise<AdvisorSummary> {
    return this.get(`/governance/cross-border/filings/${filingId}/advisor-summary`);
  }
  async getMaturityAssessmentAdvisorSummary(assessmentId: string): Promise<AdvisorSummary> {
    return this.get(`/program-governance/assessments/${assessmentId}/advisor-summary`);
  }
  async getRemediationPlanAdvisorSummary(planId: string): Promise<AdvisorSummary> {
    return this.get(`/program-governance/remediation-plans/${planId}/advisor-summary`);
  }
  /** Sprint 64.167.0 — the official handbook evidence checklist. */
  async getHandbookAssessmentAdvisorSummary(assessmentId: string): Promise<AdvisorSummary> {
    return this.get(`/program-governance/handbook-assessments/${assessmentId}/advisor-summary`);
  }
  async getGovernanceDocumentAdvisorSummary(docId: string): Promise<AdvisorSummary> {
    return this.get(`/program-governance/governance-documents/${docId}/advisor-summary`);
  }
  // ── Sprint 62.10.0: the ten 62.x sector-domain advisor-summary endpoints ──
  //
  // Each path is its module's own record detail path with `/advisor-summary`
  // appended — derived from that router, never from a sibling's convention, and
  // each served on the READ permission the module's own detail route already
  // requires. The response is returned **bare** (`res.json(result)`, no
  // `{ data }` envelope), exactly like the sixteen above, so `this.get()`
  // consumes it directly: R-23's `unwrapResponse` does not apply here and adding
  // it would read `.data` off a payload that has none.
  async getAiSystemProfileAdvisorSummary(profileId: string): Promise<AdvisorSummary> {
    return this.get(`/ai-governance/systems/${profileId}/advisor-summary`);
  }
  async getOpenDataReleaseAdvisorSummary(releaseId: string): Promise<AdvisorSummary> {
    return this.get(`/public-data/releases/${releaseId}/advisor-summary`);
  }
  async getRetentionReconciliationAdvisorSummary(
    reconciliationId: string,
  ): Promise<AdvisorSummary> {
    return this.get(`/payments-evidence/reconciliations/${reconciliationId}/advisor-summary`);
  }
  async getPlatformCorrespondenceAdvisorSummary(correspondenceId: string): Promise<AdvisorSummary> {
    return this.get(`/platform-evidence/correspondence/${correspondenceId}/advisor-summary`);
  }
  async getConformityRecordAdvisorSummary(recordId: string): Promise<AdvisorSummary> {
    return this.get(`/manufacturing/conformity/${recordId}/advisor-summary`);
  }
  async getCapaRecordAdvisorSummary(recordId: string): Promise<AdvisorSummary> {
    return this.get(`/manufacturing/capa-recall/${recordId}/advisor-summary`);
  }
  async getEprSubmissionAdvisorSummary(submissionId: string): Promise<AdvisorSummary> {
    return this.get(`/manufacturing/epr/submissions/${submissionId}/advisor-summary`);
  }
  async getRemoteAccessGrantAdvisorSummary(grantId: string): Promise<AdvisorSummary> {
    return this.get(`/manufacturing/ot-assets/remote-access/grants/${grantId}/advisor-summary`);
  }
  async getParcelClaimAdvisorSummary(claimId: string): Promise<AdvisorSummary> {
    return this.get(`/logistics/claims/${claimId}/advisor-summary`);
  }
  async getPostalAuthorisationAdvisorSummary(authorisationId: string): Promise<AdvisorSummary> {
    return this.get(`/logistics/authorisations/${authorisationId}/advisor-summary`);
  }
  // Sprint 62.10.0, second pass — the three records Gate B0's census missed.
  //
  // The trust-evidence module composes its sub-routers with three bare
  // router.use(...) calls and no sub-prefix (routes.ts:142-144), so the two
  // trust paths below sit directly under the module mount rather than under a
  // per-sub-router segment. Checked against that router, not assumed from the
  // file names.
  //
  // No example path is written in this comment, and that is deliberate. An
  // earlier draft quoted two of them inside backticks; `route-coverage.test.ts`
  // pairs a method name with the FIRST backticked trust-evidence path in its
  // chunk, so it read the prose as code and reported the sibling logistics
  // method as targeting a credentials route that does not exist. A regex cannot
  // tell a path in a sentence from a template literal — the same shape as this
  // sprint's own Gate −1 correction, where a grep counted comments as data.
  async getCustomsRecordAdvisorSummary(customsRecordId: string): Promise<AdvisorSummary> {
    return this.get(`/logistics/customs-records/${customsRecordId}/advisor-summary`);
  }
  async getElectronicSubmissionAdvisorSummary(submissionId: string): Promise<AdvisorSummary> {
    return this.get(`/trust-evidence/submissions/${submissionId}/advisor-summary`);
  }
  async getTrustCredentialAdvisorSummary(credentialId: string): Promise<AdvisorSummary> {
    return this.get(`/trust-evidence/credentials/${credentialId}/advisor-summary`);
  }
  // ── Sprint 63.22.0 (OD-A5): the ten 62.13+ registers with a detail view ──
  // Each endpoint sits beside its module's detail GET under the same read
  // permission and returns a bare `AdvisorSummary` (no `{ data }` envelope).
  async getEnforcementCaseAdvisorSummary(id: string): Promise<AdvisorSummary> {
    return this.get(`/penalty-exposure/cases/${encodeURIComponent(id)}/advisor-summary`);
  }
  async getAuthorityDirectionAdvisorSummary(id: string): Promise<AdvisorSummary> {
    return this.get(`/authority-operations/directions/${encodeURIComponent(id)}/advisor-summary`);
  }
  async getRegulatedAccountAdvisorSummary(id: string): Promise<AdvisorSummary> {
    return this.get(`/authority-operations/accounts/${encodeURIComponent(id)}/advisor-summary`);
  }
  async getRegulatoryPlaceholderAdvisorSummary(id: string): Promise<AdvisorSummary> {
    return this.get(`/regulatory-placeholders/${encodeURIComponent(id)}/advisor-summary`);
  }
  async getSectorLicenceAdvisorSummary(id: string): Promise<AdvisorSummary> {
    return this.get(`/sector-licensing/licences/${encodeURIComponent(id)}/advisor-summary`);
  }
  async getCreditInformationSupplyAdvisorSummary(id: string): Promise<AdvisorSummary> {
    return this.get(
      `/credit-information-supply/supplies/${encodeURIComponent(id)}/advisor-summary`,
    );
  }
  async getDataServiceProviderAdvisorSummary(id: string): Promise<AdvisorSummary> {
    return this.get(`/data-services/providers/${encodeURIComponent(id)}/advisor-summary`);
  }
  async getRegulatoryDisclosureAdvisorSummary(id: string): Promise<AdvisorSummary> {
    return this.get(`/investor-protection/disclosures/${encodeURIComponent(id)}/advisor-summary`);
  }
  async getIssuedRecordAdvisorSummary(id: string): Promise<AdvisorSummary> {
    return this.get(`/issued-records/records/${encodeURIComponent(id)}/advisor-summary`);
  }
  async getPublicationAdvisorSummary(id: string): Promise<AdvisorSummary> {
    return this.get(`/publishing/publications/${encodeURIComponent(id)}/advisor-summary`);
  }
  /**
   * Record-type → advisor-summary dispatcher. Lets a generic consumer
   * (e.g. the row-action `AskForsetiModal` launchpad, which only knows
   * `{ recordType, recordId }`) fetch the one-paragraph case summary +
   * suggested persona without each list page wiring its own per-module
   * call. Best-effort: returns `null` for record types that have no
   * summary endpoint, and swallows transport errors so a slow/absent
   * summary never blocks the launchpad from rendering.
   *
   * Sprint 63.29.0 (Q5, #540): called only when a person asks for the summary
   * (`useAdvisorSummaryOnRequest`) or starts a chat about the record; every
   * call is an access-log read on the server. `rethrow` lets the *Show summary*
   * control say a read failed instead of reading a failure as "no summary".
   */
  async getAdvisorSummaryForRecord(
    recordType: import('@vn-grc/shared').ParentRecordRef['type'],
    recordId: string,
    // No object-literal type here: route-coverage tests find the body by its first brace.
    options?: Partial<Record<'rethrow', boolean>>,
  ): Promise<AdvisorSummary | null> {
    try {
      switch (recordType) {
        case 'dpia':
          return await this.getDpiaAdvisorSummary(recordId);
        case 'dossier_filing':
          return await this.getDossierFilingAdvisorSummary(recordId);
        case 'transfer_assessment':
          return await this.getTransferAssessmentAdvisorSummary(recordId);
        case 'data_mapping_activity':
          return await this.getActivityAdvisorSummary(recordId);
        case 'rights_request':
          return await this.getRightsRequestAdvisorSummary(recordId);
        case 'vendor':
          return await this.getVendorAdvisorSummary(recordId);
        case 'contract':
          return await this.getContractAdvisorSummary(recordId);
        case 'vendor_acceptance':
          return await this.getVendorRiskAcceptanceAdvisorSummary(recordId);
        case 'incident_op':
          return await this.getPlaybookActivationAdvisorSummary(recordId);
        case 'breach_notification':
          return await this.getBreachNotificationAdvisorSummary(recordId);
        case 'authority_request':
          return await this.getAuthorityRequestAdvisorSummary(recordId);
        case 'supplement_request':
          return await this.getSupplementRequestAdvisorSummary(recordId);
        case 'cross_border_filing':
          return await this.getCrossBorderFilingAdvisorSummary(recordId);
        case 'remediation_plan':
          return await this.getRemediationPlanAdvisorSummary(recordId);
        case 'governance_document':
          return await this.getGovernanceDocumentAdvisorSummary(recordId);
        case 'program':
          // Maturity assessments reuse the `program` ParentRecordType
          // (there is no dedicated `maturity_assessment` value), and they
          // are the only consumer of `program` in the Forseti row action,
          // so `recordId` is the assessment id.
          return await this.getMaturityAssessmentAdvisorSummary(recordId);
        case 'classification_subject_profile':
          // Sprint 62.20.0 — the record that carries a subject's levels under
          // EVERY installed scheme. One type, because the three
          // classifications are three fields of one record; adding a type per
          // scheme kind would put the merge this sprint exists to prevent
          // into the registry itself.
          return await this.getClassificationSubjectProfileAdvisorSummary(recordId);
        case 'handbook_assessment':
          // Sprint 64.167.0 — a DIFFERENT record from `program`. The two live
          // in different collections and only one of them carries a score.
          return await this.getHandbookAssessmentAdvisorSummary(recordId);
        // ── Sprint 62.10.0: the ten 62.x sector-domain records ──────
        case 'ai_system_profile':
          return await this.getAiSystemProfileAdvisorSummary(recordId);
        case 'open_data_release':
          return await this.getOpenDataReleaseAdvisorSummary(recordId);
        case 'retention_reconciliation':
          return await this.getRetentionReconciliationAdvisorSummary(recordId);
        case 'platform_correspondence':
          return await this.getPlatformCorrespondenceAdvisorSummary(recordId);
        case 'conformity_record':
          return await this.getConformityRecordAdvisorSummary(recordId);
        case 'capa_record':
          return await this.getCapaRecordAdvisorSummary(recordId);
        case 'epr_submission':
          return await this.getEprSubmissionAdvisorSummary(recordId);
        case 'remote_access_grant':
          return await this.getRemoteAccessGrantAdvisorSummary(recordId);
        case 'parcel_claim':
          return await this.getParcelClaimAdvisorSummary(recordId);
        case 'postal_authorisation':
          return await this.getPostalAuthorisationAdvisorSummary(recordId);
        // ── Sprint 62.10.0, second pass ─────────────────────────────
        case 'customs_record':
          return await this.getCustomsRecordAdvisorSummary(recordId);
        case 'electronic_submission':
          return await this.getElectronicSubmissionAdvisorSummary(recordId);
        case 'trust_credential':
          return await this.getTrustCredentialAdvisorSummary(recordId);
        // ── Sprint 63.22.0: the ten 62.13+ registers with a detail view ──
        case 'enforcement_case':
          return await this.getEnforcementCaseAdvisorSummary(recordId);
        case 'authority_direction':
          return await this.getAuthorityDirectionAdvisorSummary(recordId);
        case 'regulated_account':
          return await this.getRegulatedAccountAdvisorSummary(recordId);
        case 'regulatory_placeholder':
          return await this.getRegulatoryPlaceholderAdvisorSummary(recordId);
        case 'sector_licence':
          return await this.getSectorLicenceAdvisorSummary(recordId);
        case 'credit_information_supply':
          return await this.getCreditInformationSupplyAdvisorSummary(recordId);
        case 'data_service_provider':
          return await this.getDataServiceProviderAdvisorSummary(recordId);
        case 'regulatory_disclosure':
          return await this.getRegulatoryDisclosureAdvisorSummary(recordId);
        case 'issued_record':
          return await this.getIssuedRecordAdvisorSummary(recordId);
        case 'publication':
          return await this.getPublicationAdvisorSummary(recordId);
        default:
          // FIVE record types have no advisor-summary endpoint and render the
          // launchpad without a summary line (the preset persona + 3 actions
          // still apply): `project`, `vendor_contract`, `incident`, `task`,
          // `compliance_rule`.
          //
          // This comment used to name four. `project` was absent from both the
          // comment and the dispatcher test, so the census of what this `default`
          // absorbs was itself narrower than the `switch` it described — the
          // exact class of defect the 62.10.0 work file's C.1 trace calls
          // registry 5. It is corrected here and pinned by
          // `ADVISOR_SUMMARY_EXEMPT_RECORD_TYPES` in
          // `__tests__/api-client-advisor-summary-dispatcher.test.ts`, which
          // fails both when a type is in neither the switch nor the list AND when
          // a listed type gains an endpoint, so the list cannot rot in either
          // direction.
          return null;
      }
    } catch (err) {
      if (options?.rethrow) throw err;
      return null;
    }
  }
  // ── Sprint 1.5.0: Assessments -- DPIA Risks ──────────────
  async listDpiaRisks(dpiaId: string): Promise<{ data: DpiaRisk[] }> {
    return this.get(`/assessments/dpias/${dpiaId}/risks`);
  }
  async createDpiaRisk(dpiaId: string, payload: Record<string, unknown>): Promise<DpiaRisk> {
    return this.post(`/assessments/dpias/${dpiaId}/risks`, payload);
  }
  async updateDpiaRisk(riskId: string, payload: Record<string, unknown>): Promise<DpiaRisk> {
    return this.patch(`/assessments/risks/${riskId}`, payload);
  }
  async deleteDpiaRisk(riskId: string): Promise<{ message: string }> {
    return this.del(`/assessments/risks/${riskId}`);
  }
  async updateResidualRisk(
    riskId: string,
    residualLikelihood: number,
    residualImpact: number,
  ): Promise<DpiaRisk> {
    return this.patch(`/assessments/risks/${riskId}/residual`, {
      residual_likelihood: residualLikelihood,
      residual_impact: residualImpact,
    });
  }
  // ── Sprint 1.5.0: Assessments -- DPIA Mitigations ────────
  async listDpiaMitigations(dpiaId: string): Promise<{ data: DpiaMitigation[] }> {
    return this.get(`/assessments/dpias/${dpiaId}/mitigations`);
  }
  async createDpiaMitigation(
    dpiaId: string,
    payload: Record<string, unknown>,
  ): Promise<DpiaMitigation> {
    return this.post(`/assessments/dpias/${dpiaId}/mitigations`, payload);
  }
  async updateDpiaMitigation(
    mitigationId: string,
    payload: Record<string, unknown>,
  ): Promise<DpiaMitigation> {
    return this.patch(`/assessments/mitigations/${mitigationId}`, payload);
  }
  async updateDpiaMitigationStatus(mitigationId: string, status: string): Promise<DpiaMitigation> {
    return this.patch(`/assessments/mitigations/${mitigationId}/status`, { status });
  }
  // ── Sprint 1.5.0: Assessments -- Transfer Assessments ─────
  async listTransferAssessments(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<TransferAssessment>> {
    return this.get(`/assessments/transfers${this.buildQuery(params)}`);
  }
  async createTransferAssessment(payload: Record<string, unknown>): Promise<TransferAssessment> {
    return this.post('/assessments/transfers', payload);
  }
  async getTransferAssessment(assessmentId: string): Promise<TransferAssessment> {
    return this.get(`/assessments/transfers/${assessmentId}`);
  }
  async updateTransferAssessment(
    assessmentId: string,
    payload: Record<string, unknown>,
  ): Promise<TransferAssessment> {
    return this.patch(`/assessments/transfers/${assessmentId}`, payload);
  }
  /**
   * fix-sprint-20261016-2029 F11 — re-determine a queued transfer mechanism on a
   * record the edit route refuses (in review or approved). Mechanism only.
   */
  async redetermineTransferMechanism(
    assessmentId: string,
    transferMechanism: string,
  ): Promise<TransferAssessment> {
    return this.post(`/assessments/transfers/${assessmentId}/mechanism-redetermination`, {
      transfer_mechanism: transferMechanism,
    });
  }
  async submitTransferForReview(assessmentId: string): Promise<TransferAssessment> {
    return this.post(`/assessments/transfers/${assessmentId}/submit-review`);
  }
  async approveTransfer(
    assessmentId: string,
    decision: string,
    comments?: string,
  ): Promise<TransferAssessment> {
    return this.post(`/assessments/transfers/${assessmentId}/approve`, { decision, comments });
  }
  /** Sprint 63.17.0 — a rejected transfer assessment comes back as a draft; its round is archived. */
  async reopenTransfer(assessmentId: string): Promise<TransferAssessment> {
    return this.post(`/assessments/transfers/${assessmentId}/reopen`);
  }
  /**
   * Fix sprint 20261016-1956 (Finding 15) — take a transfer assessment out of review
   * (in_review -> draft) without a decision; the round is archived as withdrawn.
   */
  async withdrawTransferFromReview(assessmentId: string): Promise<TransferAssessment> {
    return this.post(`/assessments/transfers/${assessmentId}/withdraw-review`, {});
  }
  async deprecateTransfer(assessmentId: string): Promise<TransferAssessment> {
    return this.post(`/assessments/transfers/${assessmentId}/deprecate`);
  }
  async evaluateTransferRisk(
    assessmentId: string,
    evaluation: Record<string, unknown>,
  ): Promise<TransferAssessment> {
    return this.patch(`/assessments/transfers/${assessmentId}/risk-evaluation`, evaluation);
  }
  // ── Sprint 1.5.0: Assessments -- Safeguards ──────────────
  async listSafeguards(assessmentId: string): Promise<{ data: CrossBorderSafeguard[] }> {
    return this.get(`/assessments/transfers/${assessmentId}/safeguards`);
  }
  async createSafeguard(
    assessmentId: string,
    payload: Record<string, unknown>,
  ): Promise<CrossBorderSafeguard> {
    return this.post(`/assessments/transfers/${assessmentId}/safeguards`, payload);
  }
  async updateSafeguard(
    safeguardId: string,
    payload: Record<string, unknown>,
  ): Promise<CrossBorderSafeguard> {
    return this.patch(`/assessments/safeguards/${safeguardId}`, payload);
  }
  async updateSafeguardStatus(safeguardId: string, status: string): Promise<CrossBorderSafeguard> {
    return this.patch(`/assessments/safeguards/${safeguardId}/status`, { status });
  }
  // ── Sprint 1.5.0: Assessments -- Dashboard & Gap Report ──
  async getAssessmentDashboard(params?: Record<string, string>): Promise<AssessmentDashboard> {
    return this.get(`/assessments/dashboard${this.buildQuery(params)}`);
  }
  async getGapReport(params?: Record<string, string>): Promise<GapReport> {
    return this.get(`/assessments/gap-report${this.buildQuery(params)}`);
  }
  async getAssessmentAudit(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<AssessmentAuditEvent>> {
    return this.get(`/assessments/audit${this.buildQuery(params)}`);
  }
  // ── Sprint 1.6.0: Classification -- Taxonomies ──────────────
  async listTaxonomies(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<TaxonomyDefinition>> {
    return this.get(`/classification/taxonomies${this.buildQuery(params)}`);
  }
  async getTaxonomy(taxonomyId: string): Promise<TaxonomyDefinition> {
    return this.get(`/classification/taxonomies/${taxonomyId}`);
  }
  async createTaxonomy(payload: Record<string, unknown>): Promise<TaxonomyDefinition> {
    return this.post('/classification/taxonomies', payload);
  }
  async updateTaxonomy(
    taxonomyId: string,
    payload: Record<string, unknown>,
  ): Promise<TaxonomyDefinition> {
    return this.patch(`/classification/taxonomies/${taxonomyId}`, payload);
  }
  async deleteTaxonomy(taxonomyId: string): Promise<void> {
    return this.del(`/classification/taxonomies/${taxonomyId}`);
  }
  async activateTaxonomy(taxonomyId: string): Promise<TaxonomyDefinition> {
    return this.post(`/classification/taxonomies/${taxonomyId}/activate`);
  }
  async deprecateTaxonomy(taxonomyId: string): Promise<TaxonomyDefinition> {
    return this.post(`/classification/taxonomies/${taxonomyId}/deprecate`);
  }
  async seedDefaultTaxonomies(): Promise<Record<string, unknown>> {
    return this.post('/classification/taxonomies/seed-defaults');
  }
  // ── Sprint 1.6.0: Classification -- Taxonomy Levels ────────
  async listTaxonomyLevels(taxonomyId: string): Promise<TaxonomyLevel[]> {
    return this.get(`/classification/taxonomies/${taxonomyId}/levels`);
  }
  async createTaxonomyLevel(
    taxonomyId: string,
    payload: Record<string, unknown>,
  ): Promise<TaxonomyLevel> {
    return this.post(`/classification/taxonomies/${taxonomyId}/levels`, payload);
  }
  async updateTaxonomyLevel(
    levelId: string,
    payload: Record<string, unknown>,
  ): Promise<TaxonomyLevel> {
    return this.patch(`/classification/levels/${levelId}`, payload);
  }
  async deleteTaxonomyLevel(levelId: string): Promise<void> {
    return this.del(`/classification/levels/${levelId}`);
  }
  async reorderTaxonomyLevels(taxonomyId: string, levelIds: string[]): Promise<TaxonomyLevel[]> {
    return this.post(`/classification/taxonomies/${taxonomyId}/levels/reorder`, {
      level_ids: levelIds,
    });
  }
  // ── Sprint 1.6.0: Classification -- Taxonomy Versions ──────
  async listTaxonomyVersions(taxonomyId: string): Promise<TaxonomyVersion[]> {
    return this.get(`/classification/taxonomies/${taxonomyId}/versions`);
  }
  async getTaxonomyVersion(versionId: string): Promise<TaxonomyVersion> {
    return this.get(`/classification/versions/${versionId}`);
  }
  // ── Sprint 1.6.0: Classification -- Labels ─────────────────
  async listLabels(params?: Record<string, string>): Promise<PaginatedResponse<LabeledObject>> {
    return this.get(`/classification/labels${this.buildQuery(params)}`);
  }
  async getLabel(labelId: string): Promise<LabeledObject> {
    return this.get(`/classification/labels/${labelId}`);
  }
  async assignLabel(payload: Record<string, unknown>): Promise<LabeledObject> {
    return this.post('/classification/labels', payload);
  }
  async updateLabel(labelId: string, payload: Record<string, unknown>): Promise<LabeledObject> {
    return this.patch(`/classification/labels/${labelId}`, payload);
  }
  async deleteLabel(labelId: string): Promise<void> {
    return this.del(`/classification/labels/${labelId}`);
  }
  async bulkAssignLabels(payload: Record<string, unknown>): Promise<LabeledObject[]> {
    return this.post('/classification/labels/bulk', payload);
  }
  async reviewLabel(labelId: string, payload?: Record<string, unknown>): Promise<LabeledObject> {
    return this.post(`/classification/labels/${labelId}/review`, payload);
  }
  async getStaleLabels(days?: number): Promise<LabeledObject[]> {
    const query = days ? `?days=${days}` : '';
    return this.get(`/classification/labels/stale${query}`);
  }
  async getLabelConflicts(): Promise<Record<string, unknown>[]> {
    return this.get('/classification/labels/conflicts');
  }
  // ── Sprint 1.6.0: Classification -- Lifecycle ──────────────
  async listLifecycleRecords(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<LifecycleRecord>> {
    return this.get(`/classification/lifecycle${this.buildQuery(params)}`);
  }
  async getLifecycleRecord(recordId: string): Promise<LifecycleRecord> {
    return this.get(`/classification/lifecycle/${recordId}`);
  }
  async createLifecycleRecord(payload: Record<string, unknown>): Promise<LifecycleRecord> {
    return this.post('/classification/lifecycle', payload);
  }
  async updateLifecycleRecord(
    recordId: string,
    payload: Record<string, unknown>,
  ): Promise<LifecycleRecord> {
    return this.patch(`/classification/lifecycle/${recordId}`, payload);
  }
  async transitionLifecycleStage(
    recordId: string,
    toStage: string,
    reason?: string,
  ): Promise<LifecycleRecord> {
    // The route reads `reason`; a `description` key was dropped and the typed reason never stored.
    return this.post(`/classification/lifecycle/${recordId}/transition`, {
      to_stage: toStage,
      reason,
    });
  }
  async placeLifecycleHold(recordId: string, reason: string): Promise<LifecycleRecord> {
    return this.post(`/classification/lifecycle/${recordId}/hold`, { hold_reason: reason });
  }
  async releaseLifecycleHold(recordId: string): Promise<LifecycleRecord> {
    return this.post(`/classification/lifecycle/${recordId}/release-hold`);
  }
  async listLifecycleEvents(
    recordId: string,
    params?: Record<string, string>,
  ): Promise<LifecycleEvent[]> {
    return this.get(`/classification/lifecycle/${recordId}/events${this.buildQuery(params)}`);
  }
  async createLifecycleEvent(
    recordId: string,
    payload: Record<string, unknown>,
  ): Promise<LifecycleEvent> {
    return this.post(`/classification/lifecycle/${recordId}/events`, payload);
  }
  async getExpiredRetentions(): Promise<LifecycleRecord[]> {
    return this.get('/classification/lifecycle/expired');
  }
  // ── Sprint 1.6.0: Classification -- Gaps ───────────────────
  async listClassificationGaps(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ClassificationGap>> {
    return this.get(`/classification/gaps${this.buildQuery(params)}`);
  }
  async getClassificationGap(gapId: string): Promise<ClassificationGap> {
    return this.get(`/classification/gaps/${gapId}`);
  }
  async acknowledgeGap(gapId: string): Promise<ClassificationGap> {
    return this.post(`/classification/gaps/${gapId}/acknowledge`);
  }
  async remediateGap(gapId: string, notes?: string): Promise<ClassificationGap> {
    return this.post(`/classification/gaps/${gapId}/remediate`, { notes });
  }
  async acceptGapRisk(gapId: string, justification: string): Promise<ClassificationGap> {
    return this.post(`/classification/gaps/${gapId}/accept-risk`, { justification });
  }
  async getGapSummary(): Promise<Record<string, unknown>> {
    return this.get('/classification/gaps/summary');
  }
  async detectGaps(): Promise<Record<string, unknown>> {
    return this.post('/classification/gaps/detect');
  }
  // ── Sprint 1.6.0: Classification -- Audit ──────────────────
  async getClassificationAudit(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ClassificationAuditEvent>> {
    return this.get(`/classification/audit${this.buildQuery(params)}`);
  }
  async getClassificationEntityAudit(
    entityType: string,
    entityId: string,
  ): Promise<ClassificationAuditEvent[]> {
    return this.get(`/classification/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 1.6.0: Classification -- Exports ────────────────
  // Sprint 42.2.3 E2 — returns AttachmentRef (was Blob).
  async exportClassificationCSV(exportType: string): Promise<AttachmentRef> {
    return this.post(`/classification/exports/csv/${exportType}`);
  }
  async exportClassificationPDF(exportType: string): Promise<Blob> {
    await this.fetchCsrfToken();
    const headers: Record<string, string> = {};
    if (this.csrfToken) headers['X-CSRF-Token'] = this.csrfToken;
    const response = await fetch(`${API_BASE}/classification/exports/pdf/${exportType}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers,
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }
  /*
   * Sprint 63.8.2 gate E12.5 remediation A — the classification discovery-cycle
   * register (`grc_classification_discovery_cycles`, mounted at
   * `/classification/discovery-cycles`). `classification/DiscoveryCyclesPage`
   * reads and writes THIS register: a cycle scoped by `scope_systems`, a label
   * delta, a schedule. It used to call the Discovery module's `/discovery/cycles`
   * (a different register, whose create needs `scope_type`/`scope_ids`, so every
   * create was refused) and two `/discovery/cycles/:id/{delta,schedule}` paths
   * that no backend route serves.
   */
  async listClassificationDiscoveryCycles(params?: Record<string, string>) {
    return this.get(`/classification/discovery-cycles${this.buildQuery(params)}`);
  }
  async createClassificationDiscoveryCycle(data: {
    name: string;
    description: string;
    scope_systems?: string[];
  }) {
    return this.post('/classification/discovery-cycles', data);
  }
  async runClassificationDeltaDetection(cycleId: string) {
    return this.post(`/classification/discovery-cycles/${cycleId}/delta`, {});
  }
  async scheduleClassificationDiscoveryCycle(
    cycleId: string,
    data: { interval_days: number; enabled: boolean },
  ): Promise<unknown> {
    return this.post(`/classification/discovery-cycles/${cycleId}/schedule`, data);
  }
  // ── Sprint 20.8.0: Classification Maturity ──────────────────
  async submitLabelForReview(labelId: string) {
    return this.post(`/classification/labels/${labelId}/submit-review`, {});
  }
  async approveLabel(labelId: string) {
    return this.post(`/classification/labels/${labelId}/approve`, {});
  }
  async deprecateLabel(labelId: string) {
    return this.post(`/classification/labels/${labelId}/deprecate`, {});
  }
  async listReviewTasks(params?: Record<string, string>) {
    return this.get(`/classification/review-tasks${this.buildQuery(params)}`);
  }
  async resolveReviewTask(taskId: string, data: { resolution: string; notes: string }) {
    return this.post(`/classification/review-tasks/${taskId}/resolve`, data);
  }
  async listDmReviewTasks(params?: Record<string, string>) {
    return this.get(`/data-mapping/review-tasks${this.buildQuery(params)}`);
  }
  async resolveDmReviewTask(taskId: string, data: { resolution: string; notes: string }) {
    return this.post(`/data-mapping/review-tasks/${taskId}/resolve`, data);
  }
  async bulkDmReviewTasks(taskIds: string[], action: 'approve' | 'reject') {
    return this.post('/data-mapping/review-tasks/bulk', { task_ids: taskIds, action });
  }
  async createReviewTask(data: Record<string, any>) {
    return this.post('/classification/review-tasks', data);
  }
  async getEscalatedTasks() {
    return this.get('/classification/review-tasks/escalated');
  }
  /* Sprint 65.18.0 — continuity plans and exercises (toolkit row IRP-01). */
  async listContinuityPlans() {
    return this.get('/continuity/plans');
  }
  async createContinuityPlan(payload: unknown) {
    return this.post('/continuity/plans', payload);
  }
  async listContinuityExercises() {
    return this.get('/continuity/exercises');
  }
  async scheduleContinuityExercise(payload: unknown) {
    return this.post('/continuity/exercises', payload);
  }
  /** The transition IRP-01's fact watches. */
  async completeContinuityExercise(exerciseId: string, payload: unknown) {
    return this.post(`/continuity/exercises/${exerciseId}/completion`, payload);
  }
  /**
   * Cancelling an exercise that will not be held.
   *
   * Separate from completion, and it raises no duty: an exercise that never
   * happened is not an exercise with a bad outcome.
   */
  async cancelContinuityExercise(exerciseId: string, payload: unknown) {
    return this.post(`/continuity/exercises/${exerciseId}/cancellation`, payload);
  }
  async retestContinuityAction(exerciseId: string, actionId: string, payload: unknown) {
    return this.post(`/continuity/exercises/${exerciseId}/actions/${actionId}/retest`, payload);
  }

  // ── Sprint 65.19.0 — LEG-01 legal matters ──────────────────────────
  /**
   * Sprint 62.30.0 — an instrument's articles with coverage derived.
   *
   * Typed on the response rather than `unknown`, because the panel branches on
   * `provision_structure` and on `articles.length`, and those two branches are
   * the difference between "correctly has no articles", "not mapped yet" and
   * "the read failed".
   */
  async getFrameworkArticles(frameworkCode: string): Promise<{
    data: import('@vn-grc/shared').FrameworkArticleCoverageReport;
  }> {
    return this.get(`/localization/frameworks/${encodeURIComponent(frameworkCode)}/articles`);
  }

  /**
   * Sprint 63.27.0 — one article's RECORDED text (the signed instrument's words
   * from its committed transcript), bounded; pass `next_offset` back to continue.
   * `article` is the article identity, `6` or `6a`.
   */
  async getFrameworkArticleText(
    frameworkCode: string,
    article: string,
    offset = 0,
  ): Promise<{ data: import('@vn-grc/shared').LegalArticleText }> {
    const query = offset > 0 ? `?offset=${offset}` : '';
    return this.get(
      `/localization/frameworks/${encodeURIComponent(frameworkCode)}/articles/${encodeURIComponent(article)}/text${query}`,
    );
  }

  /** Sprint 63.27.0 — a segment of a framework's whole recorded source (the Source tab). */
  async getFrameworkSourceText(
    frameworkCode: string,
    options: { sourceId?: string; offset?: number } = {},
  ): Promise<{ data: import('@vn-grc/shared').LegalSourceText }> {
    const params = new URLSearchParams();
    if (options.sourceId) params.set('source_id', options.sourceId);
    if (options.offset && options.offset > 0) params.set('offset', String(options.offset));
    const query = params.toString();
    return this.get(
      `/localization/frameworks/${encodeURIComponent(frameworkCode)}/source-text${query ? `?${query}` : ''}`,
    );
  }

  // ── Sprint 65.21.0 — credit-information uses ──
  /**
   * The purposes this organisation's installed frameworks declare.
   *
   * Typed on the response rather than `unknown`, because the page branches on
   * `purposes.length === 0` and that branch is the difference between "this
   * organisation has none" and a read that failed.
   */
  async listCreditInformationPurposes(): Promise<{
    data: {
      purposes: { value: string; label: { en: string; vi: string } }[];
      declared_by_framework_codes: string[];
    };
  }> {
    return this.get('/credit-information/purposes');
  }
  async listCreditInformationUses(params?: Record<string, string>) {
    return this.get(`/credit-information/uses${this.buildQuery(params)}`);
  }
  async getCreditInformationUse(useId: string) {
    return this.get(`/credit-information/uses/${useId}`);
  }
  /** Recording a use — or being refused one the instrument forbids. */
  async recordCreditInformationUse(payload: unknown) {
    return this.post('/credit-information/uses', payload);
  }
  /** The consent a use rested on has been withdrawn. The use is not deleted. */
  async recordCreditInformationConsentWithdrawal(useId: string, payload: unknown) {
    return this.post(`/credit-information/uses/${useId}/consent-withdrawal`, payload);
  }
  /**
   * fix-sprint-20260922-2142 F13 — the boundary statements the installed
   * frameworks that declare this register author for it. `data: []` means no
   * declaring framework authors one; a failed read rejects.
   */
  async listCreditInformationUseBoundaryStatements(): Promise<{ data: BoundaryStatement[] }> {
    return this.get('/credit-information/boundary-statements');
  }

  /**
   * fix-sprint-20260922-2142 F9 — the rights a `statutory_right` matter may
   * rest on, read from the organisation's installed frameworks (the list the
   * server validates against). `data: []` is a real answer — no installed
   * framework declares a right — and is distinct from a rejected read.
   */
  async listLegalMatterRights(): Promise<{ data: DeclaredLegalMatterRight[] }> {
    return this.get('/legal-matters/declared-rights');
  }

  async listLegalMatters(params?: Record<string, string>) {
    return this.get(`/legal-matters/matters${this.buildQuery(params)}`);
  }
  async getLegalMatter(matterId: string) {
    return this.get(`/legal-matters/matters/${matterId}`);
  }
  /** Receiving a matter. The intake the platform had no surface for. */
  async receiveLegalMatter(payload: unknown) {
    return this.post('/legal-matters/matters', payload);
  }
  /** Telling the raiser you have it. A dated act, and it happens once. */
  async acknowledgeLegalMatter(matterId: string, payload: unknown) {
    return this.post(`/legal-matters/matters/${matterId}/acknowledgement`, payload);
  }
  /** Resolving. `legal_matters:close`, and the transition the fact watches. */
  async resolveLegalMatter(matterId: string, payload: unknown) {
    return this.post(`/legal-matters/matters/${matterId}/resolution`, payload);
  }
  /** Withdrawing, when the raiser took it back. Appends no fact. */
  async withdrawLegalMatter(matterId: string, payload: unknown) {
    return this.post(`/legal-matters/matters/${matterId}/withdrawal`, payload);
  }
  /** Sprint 63.31.0 (gate E12.5 pass 2, P2-L2) — record the right an intake left unnamed. */
  async recordLegalMatterRight(matterId: string, payload: { right_code: string }) {
    return this.post(`/legal-matters/matters/${encodeURIComponent(matterId)}/right`, payload);
  }
  /** Sprint 63.31.0 (gate E12.5 pass 2, P3-L2) — accept or dismiss a suggested link. */
  async decideLegalMatterSuggestedLink(
    matterId: string,
    suggestedMatterId: string,
    payload: { decision: 'accepted' | 'dismissed' },
  ) {
    return this.post(
      `/legal-matters/matters/${encodeURIComponent(matterId)}/suggested-links/${encodeURIComponent(suggestedMatterId)}/decision`,
      payload,
    );
  }

  /* Sprint 65.17.0 — de-identification runs (toolkit row ANON-01). */
  async listDeidentificationRuns(params?: Record<string, string>) {
    return this.get(`/deidentification/runs${this.buildQuery(params)}`);
  }
  async getDeidentificationRun(runId: string) {
    return this.get(`/deidentification/runs/${runId}`);
  }
  async createDeidentificationRun(payload: unknown) {
    return this.post('/deidentification/runs', payload);
  }
  async updateDeidentificationRun(runId: string, payload: unknown) {
    return this.patch(`/deidentification/runs/${runId}`, payload);
  }
  /** Approve or reject. The only path to the state ANON-01's fact watches. */
  async decideDeidentificationRun(runId: string, payload: unknown) {
    return this.post(`/deidentification/runs/${runId}/decision`, payload);
  }

  async createClassificationException(data: Record<string, any>) {
    return this.post('/classification/exceptions', data);
  }
  async listClassificationExceptions(params?: Record<string, string>) {
    return this.get(`/classification/exceptions${this.buildQuery(params)}`);
  }
  async resolveClassificationException(
    exceptionId: string,
    notes: string,
    resolution: 'resolved' | 'accepted_risk',
  ) {
    return this.post(`/classification/exceptions/${exceptionId}/resolve`, {
      resolution,
      resolution_notes: notes,
    });
  }
  async getExceptionDashboard() {
    return this.get('/classification/exceptions/dashboard');
  }
  async getEnterpriseInventory() {
    return this.get('/classification/inventory');
  }
  async getInventorySummary() {
    return this.get('/classification/inventory/summary');
  }
  async listDiscoveryCycles(params?: Record<string, string>) {
    return this.get(`/discovery/cycles${this.buildQuery(params)}`);
  }
  async createDiscoveryCycle(data: Record<string, any>) {
    return this.post('/discovery/cycles', data);
  }
  // ── Sprint 21.4.0: Discovery Cycle Orchestration ──────────────
  async advanceCycleStatus(cycleId: string, status: string) {
    return this.patch(`/discovery/cycles/${cycleId}/status`, { status });
  }
  async compareCycles(currentId: string, previousId: string) {
    return this.get(
      `/discovery/cycles/compare?current_cycle_id=${currentId}&previous_cycle_id=${previousId}`,
    );
  }
  async configureScheduledScan(data: Record<string, any>) {
    return this.post('/discovery/cycles/schedule', data);
  }
  async routeStagedRecordToOwner(recordId: string, ownerId: string) {
    return this.post('/discovery/review-tasks', { record_id: recordId, owner_id: ownerId });
  }
  async listOwnerReviewTasks(params?: Record<string, string>) {
    return this.get(`/discovery/review-tasks/owner/${(params as any)?.owner_id ?? 'me'}`);
  }
  async resolveOwnerReviewTask(taskId: string, decision: string, notes?: string) {
    return this.post(`/discovery/review-tasks/${taskId}/resolve`, { decision, notes });
  }
  async bulkResolveReviewTasks(taskIds: string[], decision: string, notes?: string) {
    return this.post('/discovery/review-tasks/bulk-resolve', {
      task_ids: taskIds,
      decision,
      notes,
    });
  }
  async listExceptionItems(params?: Record<string, string>) {
    return this.get(`/discovery/review-tasks/exceptions${this.buildQuery(params)}`);
  }
  async linkSuggestionToLabel(suggestionId: string, proposedLabelId: string) {
    return this.post('/discovery/lineage/link-suggestion', {
      suggestion_id: suggestionId,
      proposed_label_id: proposedLabelId,
    });
  }
  async getDiscoveryLineage(recordId: string) {
    return this.get(`/discovery/lineage/${recordId}`);
  }
  // ── Sprint 1.7.0: Monitoring -- Programs ─────────────────────
  async listPrograms(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<MonitoringProgram>> {
    return this.get(`/monitoring/programs${this.buildQuery(params)}`);
  }
  async getProgram(programId: string): Promise<MonitoringProgram> {
    return this.get(`/monitoring/programs/${programId}`);
  }
  async createProgram(payload: Record<string, unknown>): Promise<MonitoringProgram> {
    return this.post('/monitoring/programs', payload);
  }
  async updateProgram(
    programId: string,
    payload: Record<string, unknown>,
  ): Promise<MonitoringProgram> {
    return this.patch(`/monitoring/programs/${programId}`, payload);
  }
  async activateProgram(programId: string): Promise<MonitoringProgram> {
    return this.post(`/monitoring/programs/${programId}/activate`);
  }
  async pauseProgram(programId: string): Promise<MonitoringProgram> {
    return this.post(`/monitoring/programs/${programId}/pause`);
  }
  async completeProgram(programId: string): Promise<MonitoringProgram> {
    return this.post(`/monitoring/programs/${programId}/complete`);
  }
  async archiveProgram(programId: string): Promise<MonitoringProgram> {
    return this.post(`/monitoring/programs/${programId}/archive`);
  }
  // ── Sprint 1.7.0: Monitoring -- Periods ─────────────────────
  async listProgramPeriods(
    programId: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ProgramPeriod>> {
    return this.get(`/monitoring/programs/${programId}/periods${this.buildQuery(params)}`);
  }
  async getPeriod(periodId: string): Promise<ProgramPeriod> {
    return this.get(`/monitoring/periods/${periodId}`);
  }
  async createPeriod(programId: string, payload: Record<string, unknown>): Promise<ProgramPeriod> {
    return this.post(`/monitoring/programs/${programId}/periods`, payload);
  }
  async autoGeneratePeriods(programId: string): Promise<ProgramPeriod[]> {
    return this.post(`/monitoring/programs/${programId}/periods/auto-generate`);
  }
  async startPeriod(periodId: string): Promise<ProgramPeriod> {
    return this.post(`/monitoring/periods/${periodId}/start`);
  }
  async closePeriod(periodId: string): Promise<ProgramPeriod> {
    return this.post(`/monitoring/periods/${periodId}/close`);
  }
  async updatePeriodReviewers(periodId: string, reviewerIds: string[]): Promise<ProgramPeriod> {
    return this.patch(`/monitoring/periods/${periodId}/reviewers`, { reviewer_ids: reviewerIds });
  }
  // ── Sprint 1.7.0: Monitoring -- Task Templates ──────────────
  async listTaskTemplates(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<TaskTemplate>> {
    return this.get(`/monitoring/task-templates${this.buildQuery(params)}`);
  }
  async getTaskTemplate(templateId: string): Promise<TaskTemplate> {
    return this.get(`/monitoring/task-templates/${templateId}`);
  }
  async createTaskTemplate(payload: Record<string, unknown>): Promise<TaskTemplate> {
    return this.post('/monitoring/task-templates', payload);
  }
  async updateTaskTemplate(
    templateId: string,
    payload: Record<string, unknown>,
  ): Promise<TaskTemplate> {
    return this.patch(`/monitoring/task-templates/${templateId}`, payload);
  }
  async deleteTaskTemplate(templateId: string): Promise<void> {
    return this.del(`/monitoring/task-templates/${templateId}`);
  }
  async seedTaskTemplateDefaults(): Promise<Record<string, unknown>> {
    return this.post('/monitoring/task-templates/seed-defaults');
  }
  // ── Sprint 1.7.0: Monitoring -- Task Instances ──────────────
  async listTaskInstances(
    periodId: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<TaskInstance>> {
    return this.get(`/monitoring/periods/${periodId}/tasks${this.buildQuery(params)}`);
  }
  async getTaskInstance(taskId: string): Promise<TaskInstance> {
    return this.get(`/monitoring/tasks/${taskId}`);
  }
  async createTaskInstance(
    periodId: string,
    payload: Record<string, unknown>,
  ): Promise<TaskInstance> {
    return this.post(`/monitoring/periods/${periodId}/tasks`, payload);
  }
  async instantiateTasksFromTemplates(periodId: string): Promise<TaskInstance[]> {
    return this.post(`/monitoring/periods/${periodId}/tasks/instantiate`);
  }
  async updateTaskStatus(taskId: string, status: string): Promise<TaskInstance> {
    return this.patch(`/monitoring/tasks/${taskId}/status`, { status });
  }
  async updateTaskChecklist(
    taskId: string,
    itemId: string,
    checked: boolean,
  ): Promise<TaskInstance> {
    return this.patch(`/monitoring/tasks/${taskId}/checklist`, { item_id: itemId, checked });
  }
  async setTaskChecklist(
    taskId: string,
    checklistItems: Array<{ id?: string; label: string; description?: string; required?: boolean }>,
  ): Promise<TaskInstance> {
    return this.put(`/monitoring/tasks/${taskId}/checklist-items`, {
      checklist_items: checklistItems,
    });
  }
  async deferTask(taskId: string, reason: string): Promise<TaskInstance> {
    return this.post(`/monitoring/tasks/${taskId}/defer`, { reason });
  }
  async blockTask(taskId: string): Promise<TaskInstance> {
    return this.post(`/monitoring/tasks/${taskId}/block`);
  }
  async completeTask(taskId: string): Promise<TaskInstance> {
    return this.post(`/monitoring/tasks/${taskId}/complete`);
  }
  async overrideCompleteTask(taskId: string, justification: string): Promise<TaskInstance> {
    return this.post(`/monitoring/tasks/${taskId}/override-complete`, {
      justification,
    });
  }
  async assignTask(taskId: string, assignedTo: string): Promise<TaskInstance> {
    return this.patch(`/monitoring/tasks/${taskId}/assign`, { assigned_to: assignedTo });
  }
  async addTaskEvidence(taskId: string, evidenceId: string): Promise<TaskInstance> {
    return this.post(`/monitoring/tasks/${taskId}/evidence`, { evidence_id: evidenceId });
  }
  async addTaskChangeRef(
    taskId: string,
    changeRef: Record<string, unknown>,
  ): Promise<TaskInstance> {
    return this.post(`/monitoring/tasks/${taskId}/change-ref`, changeRef);
  }
  // ── Sprint 1.7.0: Monitoring -- Change Detection ────────────
  async detectProgramChanges(programId: string): Promise<MonitoringChange[]> {
    return this.post(`/monitoring/programs/${programId}/detect-changes`);
  }
  async getPeriodChangeDigest(periodId: string): Promise<Record<string, unknown>> {
    return this.get(`/monitoring/periods/${periodId}/change-digest`);
  }
  // ── Sprint 1.7.0: Monitoring -- KPIs ────────────────────────
  async listKpiSnapshots(
    programId: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<KpiSnapshot>> {
    return this.get(`/monitoring/programs/${programId}/kpis${this.buildQuery(params)}`);
  }
  async getLatestKpiSnapshot(programId: string): Promise<KpiSnapshot> {
    return this.get(`/monitoring/programs/${programId}/kpis/latest`);
  }
  async createKpiSnapshot(programId: string): Promise<KpiSnapshot> {
    return this.post(`/monitoring/programs/${programId}/kpis/snapshot`);
  }
  async getKpiTrends(programId: string): Promise<Record<string, unknown>> {
    return this.get(`/monitoring/programs/${programId}/kpis/trends`);
  }
  // ── Sprint 1.7.0: Monitoring -- RIC Cases ───────────────────
  async listRicCases(params?: Record<string, string>): Promise<PaginatedResponse<RicCase>> {
    return this.get(`/monitoring/ric${this.buildQuery(params)}`);
  }
  async getRicCase(ricId: string): Promise<RicCase> {
    return this.get(`/monitoring/ric/${ricId}`);
  }
  async createRicCase(payload: Record<string, unknown>): Promise<RicCase> {
    return this.post('/monitoring/ric', payload);
  }
  async updateRicCase(ricId: string, payload: Record<string, unknown>): Promise<RicCase> {
    return this.patch(`/monitoring/ric/${ricId}`, payload);
  }
  async transitionRicStatus(ricId: string, status: string): Promise<RicCase> {
    return this.post(`/monitoring/ric/${ricId}/transition`, { status });
  }
  async addRicFollowUp(ricId: string, question: string): Promise<RicCase> {
    return this.post(`/monitoring/ric/${ricId}/follow-up`, { question });
  }
  async respondToRicFollowUp(ricId: string, itemId: string, response: string): Promise<RicCase> {
    return this.post(`/monitoring/ric/${ricId}/follow-up/${itemId}/respond`, { response });
  }
  async linkRicEvidence(ricId: string, evidenceId: string): Promise<RicCase> {
    return this.post(`/monitoring/ric/${ricId}/link-evidence`, { evidence_id: evidenceId });
  }
  async addRicEvidence(ricId: string, evidenceId: string): Promise<RicCase> {
    return this.post(`/monitoring/ric/${ricId}/evidence`, { evidence_id: evidenceId });
  }
  async getRicTemplates(): Promise<Record<string, unknown>[]> {
    return this.get('/monitoring/ric/templates');
  }
  // ── Sprint 1.7.0: Monitoring -- Audit ───────────────────────
  async getMonitoringAudit(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<MonitoringAuditEvent>> {
    return this.get(`/monitoring/audit${this.buildQuery(params)}`);
  }
  async getMonitoringEntityAudit(
    entityType: string,
    entityId: string,
  ): Promise<MonitoringAuditEvent[]> {
    return this.get(`/monitoring/audit/${entityType}/${entityId}`);
  }
  // ── Monitoring Maturity (Sprint 21.3.0) ─────────────────────
  async listFrameworkLinks(params?: Record<string, any>) {
    return this.get(`/monitoring/framework-links${this.buildQuery(params)}`);
  }
  async createFrameworkLink(programId: string, frameworkCode: string) {
    return this.post('/monitoring/framework-links', {
      program_id: programId,
      framework_code: frameworkCode,
    });
  }
  async deleteFrameworkLink(linkId: string) {
    return this.del(`/monitoring/framework-links/${linkId}`);
  }
  async seedMonitoringDefaults() {
    return this.post('/monitoring/framework-links/seed-defaults', {});
  }
  async getReadinessScore(linkId: string) {
    return this.get(`/monitoring/framework-links/${encodeURIComponent(linkId)}/readiness`);
  }
  async listAuthorityReadiness(params?: Record<string, any>) {
    return this.get(`/monitoring/authority-readiness${this.buildQuery(params)}`);
  }
  async createAuthorityReadiness(data: Record<string, any>) {
    return this.post('/monitoring/authority-readiness', data);
  }
  async updateReadinessStatus(entryId: string, status: string) {
    return this.patch(`/monitoring/authority-readiness/${encodeURIComponent(entryId)}/status`, {
      readiness_status: status,
    });
  }
  async runReadinessDrill(entryId: string) {
    return this.post(`/monitoring/authority-readiness/${encodeURIComponent(entryId)}/drill`, {});
  }
  async listMonitoringChanges(params?: Record<string, any>) {
    return this.get(`/monitoring/changes${this.buildQuery(params)}`);
  }
  async listChangeReviews(params?: Record<string, any>) {
    return this.get(`/monitoring/change-reviews${this.buildQuery(params)}`);
  }
  async createChangeReview(data: Record<string, any>) {
    return this.post('/monitoring/change-reviews', data);
  }
  async updateChangeReviewDecision(reviewId: string, decision: string) {
    return this.patch(`/monitoring/change-reviews/${encodeURIComponent(reviewId)}/decision`, {
      decision,
    });
  }
  async configureAutoCycle(data: Record<string, any>) {
    return this.post('/monitoring/change-reviews/auto-cycle', data);
  }
  /**
   * Sprint 43.7.3 Wave 3 — R-33 list-export migration for Monitoring.
   *
   * Each method routes the page's "export list" action through the
   * backend UnifiedExportService (csv / xlsx / pdf / docx) and returns
   * an AttachmentRef. The caller is expected to invoke
   * `downloadPlatformFile(ref.file_id, ref.filename)` to trigger the
   * browser download. These replace in-browser `downloadClientExport()`
   * usages on 9 of the 10 Monitoring list/dashboard pages (the 10th,
   * GovernanceRaciPage, reuses `exportRaciMatrix` under
   * /program-governance/raci/export).
   */
  async exportMonitoringProgramsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/monitoring/programs/export${this.buildQuery(params)}`);
  }
  async exportMonitoringPeriodsList(
    programId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(
      `/monitoring/programs/${encodeURIComponent(programId)}/periods/export${this.buildQuery(params)}`,
    );
  }
  async exportMonitoringTaskTemplatesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/monitoring/task-templates/export${this.buildQuery(params)}`);
  }
  async exportMonitoringTasksList(
    periodId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(
      `/monitoring/periods/${encodeURIComponent(periodId)}/tasks/export${this.buildQuery(params)}`,
    );
  }
  async exportMonitoringKpi(
    programId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(
      `/monitoring/programs/${encodeURIComponent(programId)}/kpis/export${this.buildQuery(params)}`,
    );
  }
  async exportMonitoringRicCasesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/monitoring/ric/export${this.buildQuery(params)}`);
  }
  async exportMonitoringFrameworkLinksList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/monitoring/framework-links/export${this.buildQuery(params)}`);
  }
  async exportMonitoringAuthorityReadinessList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/monitoring/authority-readiness/export${this.buildQuery(params)}`);
  }
  async exportMonitoringChangeReviewsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/monitoring/change-reviews/export${this.buildQuery(params)}`);
  }
  // ── Sprint 17.8.0: UI Assist ─────────────────────────────────
  // Sprint 63.22.0 decision 13: the shared type, so the field name the backend
  // sends and the one the provider reads cannot diverge (they had: relayUrl vs wsUrl).
  async getUIAssistStatus(): Promise<UIAssistStatusResponse> {
    return this.get('/ui-assist/status');
  }
  async listUIAssistSessions(): Promise<{ sessions: any[] }> {
    return this.get('/ui-assist/sessions');
  }
  async getUIAssistSession(sessionId: string): Promise<Record<string, unknown>> {
    return this.get(`/ui-assist/sessions/${encodeURIComponent(sessionId)}`);
  }
  async appendUIAssistMessage(
    sessionId: string,
    message: { role: string; content: string; timestamp?: string; messageIndex?: number },
    elementLabel?: string,
  ): Promise<void> {
    return this.post(`/ui-assist/sessions/${encodeURIComponent(sessionId)}/messages`, {
      ...message,
      ...(elementLabel ? { elementLabel } : {}),
    });
  }
  async updateUIAssistSessionStatus(
    sessionId: string,
    status: 'complete' | 'error',
  ): Promise<void> {
    return this.patch(`/ui-assist/sessions/${encodeURIComponent(sessionId)}/status`, { status });
  }
  async deleteUIAssistSession(sessionId: string): Promise<void> {
    return this.del(`/ui-assist/sessions/${encodeURIComponent(sessionId)}`);
  }

  // ── Agent Lobby (Multi-Session Coordination) ─────────────────

  async listLobbies(): Promise<{ lobbies: import('@vn-grc/shared').LobbySession[] }> {
    return this.get('/agent-lobby/lobbies');
  }
  async getActiveLobby(): Promise<{ lobby: import('@vn-grc/shared').LobbySession | null }> {
    return this.get('/agent-lobby/lobbies/active');
  }
  async createLobby(name: string): Promise<{ lobby: import('@vn-grc/shared').LobbySession }> {
    return this.post('/agent-lobby/lobbies', { name });
  }
  async getLobby(lobbyId: string): Promise<{ lobby: import('@vn-grc/shared').LobbySession }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}`);
  }
  async updateLobbyStatus(
    lobbyId: string,
    status: 'active' | 'paused' | 'closed',
  ): Promise<{ ok: boolean }> {
    return this.patch(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/status`, { status });
  }
  async getLobbyAgents(
    lobbyId: string,
  ): Promise<{ agents: import('@vn-grc/shared').LobbyAgent[] }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/agents`);
  }
  async getLobbyMessages(
    lobbyId: string,
    limit = 100,
  ): Promise<{ messages: import('@vn-grc/shared').LobbyMessage[] }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/messages?limit=${limit}`);
  }
  async sendLobbyMessage(
    lobbyId: string,
    content: string,
    toAgentId = 'all',
  ): Promise<{ message: import('@vn-grc/shared').LobbyMessage }> {
    return this.post(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/messages`, {
      content,
      to_agent_id: toAgentId,
    });
  }
  async getLobbyRequests(
    lobbyId: string,
  ): Promise<{ requests: import('@vn-grc/shared').LobbyRequest[] }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/requests`);
  }
  async getLobbyPendingRequests(
    lobbyId: string,
  ): Promise<{ requests: import('@vn-grc/shared').LobbyRequest[]; count: number }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/requests/pending`);
  }
  async appointBossAgent(
    lobbyId: string,
    agentId: string,
  ): Promise<{ ok: boolean; boss_agent_id: string }> {
    return this.post(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/boss-agent`, {
      agent_id: agentId,
    });
  }
  async decideLobbyRequest(
    lobbyId: string,
    requestId: string,
    status: 'approved' | 'rejected' | 'deferred',
    decisionNotes?: string,
  ): Promise<{ request: import('@vn-grc/shared').LobbyRequest }> {
    return this.post(
      `/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/requests/${encodeURIComponent(requestId)}/decide`,
      { status, decision_notes: decisionNotes },
    );
  }

  // ── Lobby Tasks (Sprint 35.5.0) ──
  async getLobbyTasks(lobbyId: string): Promise<{ tasks: import('@vn-grc/shared').LobbyTask[] }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/tasks`);
  }
  async createLobbyTask(
    lobbyId: string,
    payload: import('@vn-grc/shared').CreateLobbyTaskPayload,
  ): Promise<{ task: import('@vn-grc/shared').LobbyTask }> {
    return this.post(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/tasks`, payload);
  }
  async deleteLobbyTask(lobbyId: string, taskId: string): Promise<{ ok: boolean }> {
    return this.del(
      `/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/tasks/${encodeURIComponent(taskId)}`,
    );
  }

  // ── Lobby join tokens (fix-sprint-20260411-0657-followup F1) ──
  // The credential a relay (agent scope) or the Agent OS Panel (operator scope) presents on the
  // agent socket. The token is in the issue response once; the list carries metadata only.
  async issueLobbyJoinToken(
    lobbyId: string,
    payload: {
      scope: import('@vn-grc/shared').LobbyJoinTokenScope;
      label?: string;
      ttl_hours?: number;
    },
  ): Promise<import('@vn-grc/shared').IssueLobbyJoinTokenResponse> {
    return this.post(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/join-tokens`, payload);
  }
  async listLobbyJoinTokens(
    lobbyId: string,
  ): Promise<{ join_tokens: import('@vn-grc/shared').LobbyJoinTokenInfo[] }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/join-tokens`);
  }
  async revokeLobbyJoinToken(
    lobbyId: string,
    tokenId: string,
  ): Promise<{
    join_token: import('@vn-grc/shared').LobbyJoinTokenInfo;
    connections_closed: number;
  }> {
    return this.del(
      `/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/join-tokens/${encodeURIComponent(tokenId)}`,
    );
  }

  // ── Compliance operations — the DPO work programme (Sprint 65.1.0) ──
  //
  // Four reads and one export, matching `modules/compliance-operations/
  // routes.ts` exactly. Written against the routes, not against the sprint's
  // C.1 table: the router mounts two `PATCH`es the table does not list, and it
  // mounts `GET /coverage` and `POST /export` BEFORE `/obligations/:id`, which
  // is why `getComplianceCoverage` is not reachable as an obligation id.
  //
  // The list is the R-23 OFFSET envelope — `{ data, total, page, page_size }` —
  // so a caller unwraps it with `unwrapResponse` and `extractPaginationMeta`.
  // It is deliberately not cursor-paginated; see the sprint's Deviations table.
  //
  // The two `PATCH`es (`/obligations/:id/accountability`, `/obligations/:id/
  // cadence`) exist on the router and are NOT bound here: this sprint's
  // work-programme surface is read-and-navigate (spec C.2 — "this sprint ships
  // no authoring form"), so a client method with no caller would be a helper
  // nobody has ever observed to work. They are bound by the slice that mounts
  // the editing surface.
  async listComplianceObligations(params?: {
    page?: number;
    page_size?: number;
    state?: ObligationState;
    toolkit_row_code?: string;
    binding_id?: string;
    dpo_oversight?: DpoOversightRole;
    lineage_class?: ObligationLineageClass;
    /** `statutory` | `cadence` | `undated`. There is no "due date" filter (AC4). */
    timing?: 'statutory' | 'cadence' | 'undated';
    due_before?: string;
    overdue?: boolean;
  }): Promise<PaginatedResponse<ComplianceObligationInstance>> {
    return this.get(`/compliance-operations/obligations${this.buildQuery(params)}`);
  }
  async getComplianceObligation(
    obligationId: string,
  ): Promise<{ data: ComplianceObligationInstance }> {
    return this.get(`/compliance-operations/obligations/${encodeURIComponent(obligationId)}`);
  }
  /**
   * Programme coverage, computed per read.
   *
   * The response shape is declared structurally here rather than imported: the
   * backend owns `CoverageReport` in `modules/compliance-operations/
   * coverage-service.ts` and it is not in `@vn-grc/shared`, so the frontend
   * cannot import it. Recorded as a contract gap rather than papered over with
   * `unknown` — a shape a page renders is a shape the page must be able to
   * type-check against.
   */
  async getComplianceCoverage(): Promise<{ data: ComplianceCoverageReport }> {
    return this.get('/compliance-operations/coverage');
  }
  async exportComplianceOperations(body: {
    kind: ComplianceOperationsExportKind;
    format: RenderableExportFormat;
  }): Promise<{ data: AttachmentRef }> {
    return this.post('/compliance-operations/export', body);
  }

  // ── HTTP helpers ──────────────────────────────────────────────
  private async get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request('GET', path, undefined, true, signal);
  }
  /**
   * fix-sprint-20261016-2029 F2 — a GET the client issues on a timer, not
   * on a person's action. It carries `X-Background-Request: 1`, and the server
   * checks the session's idle clock without renewing it, so an unattended open
   * tab is idle-expired at the organisation's configured timeout.
   */
  private async getInBackground<T>(path: string): Promise<T> {
    return this.request('GET', path, undefined, true, undefined, undefined, {
      [BACKGROUND_REQUEST_HEADER]: '1',
    });
  }
  private async post<T>(
    path: string,
    body?: unknown,
    auth = true,
    signal?: AbortSignal,
    timeoutMs?: number,
  ): Promise<T> {
    return this.request('POST', path, body, auth, signal, timeoutMs);
  }
  private async patch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request('PATCH', path, body, true, signal);
  }
  private async put<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request('PUT', path, body, true, signal);
  }
  private async del<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request('DELETE', path, body, true, signal);
  }
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    _auth = true,
    signal?: AbortSignal,
    timeoutMs?: number,
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    const url = `${API_BASE}${path}`;
    const isIdempotent = IDEMPOTENT_METHODS.includes(method);
    const maxRetries = isIdempotent ? MAX_RETRIES : 0;
    const doRequest = async (): Promise<T> => {
      // Fetch CSRF token for mutation requests
      if (['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
        await this.fetchCsrfToken();
      }
      let csrfRetried = false;
      let lastError: unknown;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(extraHeaders ?? {}),
          };
          // Attach CSRF token for mutations
          if (this.csrfToken && ['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
            headers['X-CSRF-Token'] = this.csrfToken;
          }
          // Combine caller signal with a per-request timeout so fetch never hangs indefinitely.
          // Callers may override the default for slow server-side operations (e.g. the
          // document-review version upload, which extracts PDF/DOCX text synchronously).
          const timeoutSignal = AbortSignal.timeout(timeoutMs ?? REQUEST_TIMEOUT_MS);
          const effectiveSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
          const response = await fetch(url, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
            signal: effectiveSignal,
            credentials: 'same-origin',
          });
          // 204 No Content — success with no body (e.g. DELETE)
          if (response.status === 204) return undefined as T;
          // Detect HTML responses (e.g. SPA fallback when backend is unavailable)
          const contentType = response.headers.get('content-type') || '';
          if (!contentType.includes('application/json')) {
            throw new ApiError({
              code: 'SERVER_ERROR',
              message: 'Backend API is not connected. Please ensure the backend server is running.',
              status: 0,
            });
          }
          if (!response.ok) {
            const errorData = await response
              .json()
              .catch(() => ({ error: 'unknown', message: 'Request failed' }));
            // CSRF token expired — refresh and retry once (without consuming a retry attempt)
            if (response.status === 403 && errorData.error === 'csrf_failed' && !csrfRetried) {
              csrfRetried = true;
              await this.refreshCsrfToken();
              attempt--;
              continue;
            }
            // 401 → auto-logout — except a WRONG CODE at the step-up: `/mfa/verify`
            // answers 401 `INVALID_CODE` for a code that does not match, while the
            // session itself is perfectly good. Treating that as an expired session
            // would sign the person out of the UI for mistyping a digit.
            // Gate E12.5 pass 1 (frontend P2-1) — likewise a wrong CURRENT
            // password on the change-password step (401 INVALID_CREDENTIALS):
            // the session is good, and signing the UI out would strand a person
            // held for a password change on the login page.
            if (
              !(path === '/mfa/verify' && errorData.error === 'INVALID_CODE') &&
              !(path === '/auth/change-password' && errorData.error === 'INVALID_CREDENTIALS')
            ) {
              this.notifyUnauthorized(response.status);
            }
            this.notifyMfaStepUpRequired(response.status, errorData.error, path);
            this.notifyPasswordChangeRequired(response.status, errorData.error);
            // Retry on transient failures (idempotent methods only)
            if (RETRYABLE_STATUSES.includes(response.status) && attempt < maxRetries) {
              const retryAfter = response.headers.get('Retry-After');
              const baseDelay = retryAfter
                ? parseInt(retryAfter, 10) * 1000
                : Math.min(RETRY_BASE_MS * Math.pow(2, attempt), RETRY_MAX_MS);
              const jitter = baseDelay * (0.8 + Math.random() * 0.4);
              await new Promise((r) => setTimeout(r, jitter));
              continue;
            }
            throw new ApiError({
              code: statusToErrorCode(response.status),
              message: errorData.message || 'Request failed',
              status: response.status,
              details: errorData.details,
              // Preserve the backend's semantic code (`error` field) so call
              // sites can localize by it — the HTTP-bucket `code` above cannot
              // tell e.g. invoice_package_mismatch from invoice_not_paid.
              serverCode: typeof errorData.error === 'string' ? errorData.error : undefined,
            });
          }
          if (response.status === 204) return undefined as T;
          return response.json();
        } catch (err: any) {
          // Don't retry aborted requests
          if (err.name === 'AbortError') throw err;
          // Don't retry typed ApiErrors (non-transient)
          if (err instanceof ApiError) throw err;
          // Retry network errors (idempotent only)
          if (attempt < maxRetries) {
            lastError = err;
            const baseDelay = Math.min(RETRY_BASE_MS * Math.pow(2, attempt), RETRY_MAX_MS);
            const jitter = baseDelay * (0.8 + Math.random() * 0.4);
            await new Promise((r) => setTimeout(r, jitter));
            continue;
          }
          throw new ApiError({
            code: 'NETWORK_ERROR',
            message: err.message || 'Network request failed',
          });
        }
      }
      throw lastError || new ApiError({ code: 'UNKNOWN', message: 'Request failed after retries' });
    };
    // Deduplicate in-flight GET requests
    if (method === 'GET') {
      return deduplicateGet<T>(url, doRequest);
    }
    return doRequest();
  }
  // ── Sprint 1.8.0: Incidents -- Core ──────────────────────────
  async listIncidents(params?: Record<string, any>): Promise<{ data: Incident[] }> {
    return this.get(`/incidents/incidents${this.buildQuery(params)}`);
  }
  async getIncident(id: string): Promise<Incident> {
    return this.get(`/incidents/incidents/${id}`);
  }
  async createIncident(payload: Record<string, any>): Promise<Incident> {
    return this.post('/incidents/incidents', payload);
  }
  async updateIncident(id: string, payload: Record<string, any>): Promise<Incident> {
    return this.patch(`/incidents/incidents/${id}`, payload);
  }
  async transitionIncidentStatus(id: string, toStatus: string): Promise<Incident> {
    return this.post(`/incidents/incidents/${id}/transition`, { to_status: toStatus });
  }
  async completeTriage(id: string, triageData: Record<string, any>): Promise<Incident> {
    return this.post(`/incidents/incidents/${id}/triage`, triageData);
  }
  async updateImpactAssessment(id: string, data: Record<string, any>): Promise<Incident> {
    return this.post(`/incidents/incidents/${id}/impact-assessment`, data);
  }
  // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — every write that replaces an
  // incident's notification requirements states the `requirements_revision`
  // the caller READ (0 where the incident carries none). The server refuses a
  // write whose revision has moved since with 409 INCIDENT_REQUIREMENTS_CHANGED
  // rather than letting it overwrite another user's save.
  async updateBreachAssessment(
    id: string,
    data: Record<string, any>,
    expectedRequirementsRevision: number,
  ): Promise<Incident> {
    return this.post(`/incidents/incidents/${id}/breach-assessment`, {
      ...data,
      expected_requirements_revision: expectedRequirementsRevision,
    });
  }
  async updateNotificationStatus(
    id: string,
    jurisdiction: string,
    field: string,
    status: string,
    expectedRequirementsRevision: number,
  ): Promise<Incident> {
    return this.patch(`/incidents/incidents/${id}/notification-status`, {
      jurisdiction,
      field,
      status,
      expected_requirements_revision: expectedRequirementsRevision,
    });
  }
  // Sprint 62.35.0 (AC9) — affected-party notification under a requirement.
  // The jurisdiction is a path segment, so it is encoded: it is free text.
  async addAffectedPartyNotification(
    id: string,
    jurisdiction: string,
    data: import('@vn-grc/shared').AffectedPartyNotificationInput,
    expectedRequirementsRevision: number,
  ): Promise<Incident> {
    return this.post(
      `/incidents/incidents/${id}/notification-requirements/${encodeURIComponent(jurisdiction)}/affected-parties`,
      { ...data, expected_requirements_revision: expectedRequirementsRevision },
    );
  }
  async updateAffectedPartyNotification(
    id: string,
    jurisdiction: string,
    partyId: string,
    data: Partial<import('@vn-grc/shared').AffectedPartyNotificationInput>,
    expectedRequirementsRevision: number,
  ): Promise<Incident> {
    return this.patch(
      `/incidents/incidents/${id}/notification-requirements/${encodeURIComponent(jurisdiction)}/affected-parties/${encodeURIComponent(partyId)}`,
      { ...data, expected_requirements_revision: expectedRequirementsRevision },
    );
  }
  // A DELETE carries no body, so the revision travels in the query string.
  async removeAffectedPartyNotification(
    id: string,
    jurisdiction: string,
    partyId: string,
    expectedRequirementsRevision: number,
  ): Promise<Incident> {
    return this.del(
      `/incidents/incidents/${id}/notification-requirements/${encodeURIComponent(jurisdiction)}/affected-parties/${encodeURIComponent(partyId)}?expected_requirements_revision=${encodeURIComponent(String(expectedRequirementsRevision))}`,
    );
  }
  async addIncidentEvidence(id: string, evidenceId: string): Promise<Incident> {
    return this.post(`/incidents/incidents/${id}/evidence`, { evidence_id: evidenceId });
  }
  async closeIncident(id: string, justification: string): Promise<Incident> {
    return this.post(`/incidents/incidents/${id}/close`, { justification });
  }
  // ── Sprint 1.8.0: Incidents -- Timeline ─────────────────────
  async listTimelineEvents(
    incidentId: string,
    params?: Record<string, any>,
  ): Promise<{ data: IncidentTimelineEvent[] }> {
    return this.get(`/incidents/incidents/${incidentId}/timeline${this.buildQuery(params)}`);
  }
  async createTimelineEvent(
    incidentId: string,
    payload: Record<string, any>,
  ): Promise<IncidentTimelineEvent> {
    return this.post(`/incidents/incidents/${incidentId}/timeline`, payload);
  }
  // ── Sprint 1.8.0: Incidents -- Decisions ────────────────────
  async listDecisions(
    incidentId: string,
    params?: Record<string, any>,
  ): Promise<{ data: IncidentDecision[] }> {
    return this.get(`/incidents/incidents/${incidentId}/decisions${this.buildQuery(params)}`);
  }
  async createDecision(
    incidentId: string,
    payload: Record<string, any>,
  ): Promise<IncidentDecision> {
    return this.post(`/incidents/incidents/${incidentId}/decisions`, payload);
  }
  // ── Sprint 1.8.0: Incidents -- Tasks ────────────────────────
  async listIncidentTasks(
    incidentId: string,
    params?: Record<string, any>,
  ): Promise<{ data: IncidentTask[] }> {
    return this.get(`/incidents/incidents/${incidentId}/tasks${this.buildQuery(params)}`);
  }
  async getIncidentTask(taskId: string): Promise<IncidentTask> {
    return this.get(`/incidents/tasks/${taskId}`);
  }
  async createIncidentTask(
    incidentId: string,
    payload: Record<string, any>,
  ): Promise<IncidentTask> {
    return this.post(`/incidents/incidents/${incidentId}/tasks`, payload);
  }
  async updateIncidentTaskStatus(taskId: string, status: string): Promise<IncidentTask> {
    return this.patch(`/incidents/tasks/${taskId}/status`, { status });
  }
  async updateIncidentTaskChecklist(
    taskId: string,
    itemId: string,
    checked: boolean,
  ): Promise<IncidentTask> {
    return this.patch(`/incidents/tasks/${taskId}/checklist`, { item_id: itemId, checked });
  }
  async assignIncidentTask(taskId: string, assignedTo: string): Promise<IncidentTask> {
    return this.patch(`/incidents/tasks/${taskId}/assign`, { assigned_to: assignedTo });
  }
  async addIncidentTaskEvidence(taskId: string, evidenceId: string): Promise<IncidentTask> {
    return this.post(`/incidents/tasks/${taskId}/evidence`, { evidence_id: evidenceId });
  }
  async escalateIncidentTask(taskId: string): Promise<IncidentTask> {
    return this.post(`/incidents/tasks/${taskId}/escalate`, {});
  }
  async getIncidentTaskTemplates(): Promise<{ data: Record<string, unknown>[] }> {
    return this.get('/incidents/task-templates');
  }
  // ── Sprint 21.1.0: Incident Response Maturity ──────────────
  async createBreachNotification(
    incidentId: string,
    payload: Record<string, any>,
  ): Promise<BreachNotification> {
    return this.post('/incidents/breach-notifications', { incident_id: incidentId, ...payload });
  }
  async listBreachNotifications(
    params?: Record<string, any>,
  ): Promise<{ data: BreachNotification[] }> {
    return this.get(`/incidents/breach-notifications${this.buildQuery(params)}`);
  }
  async getBreachNotification(id: string): Promise<BreachNotification> {
    return this.get(`/incidents/breach-notifications/${id}`);
  }
  async draftBreachNotification(id: string): Promise<BreachNotificationForsetiDraft> {
    return this.post(`/incidents/breach-notifications/${encodeURIComponent(id)}/forseti-draft`, {});
  }
  async resolveBreachNotificationDraft(
    draftId: string,
    payload: BreachNotificationDraftResolveRequest,
  ): Promise<BreachNotificationDraftResolveResult> {
    return this.post(
      `/incidents/breach-notification-drafts/${encodeURIComponent(draftId)}/resolve`,
      payload,
    );
  }
  async transitionNotificationStatus(id: string, toStatus: string): Promise<BreachNotification> {
    return this.post(`/incidents/breach-notifications/${id}/transition`, { to_status: toStatus });
  }
  async makeNotificationDecision(
    id: string,
    decision: string,
    rationale: string,
  ): Promise<BreachNotification> {
    return this.post(`/incidents/breach-notifications/${id}/decision`, { decision, rationale });
  }
  async submitNotificationToAuthority(
    id: string,
    channel: string,
    confirmation: string,
  ): Promise<BreachNotification> {
    return this.post(`/incidents/breach-notifications/${id}/submit`, { channel, confirmation });
  }
  /**
   * Fix sprint #447 W1 — the channels `/submit` accepts, read from the filing
   * framework's declaration; `refusal` states why there are none.
   */
  async listBreachNotificationFilingChannels(notificationId?: string): Promise<{
    framework_code: string | null;
    channels: Array<{ code: string; name: Record<string, string> }>;
    refusal: FilingChannelRefusal | null;
  }> {
    // fix-sprint-20260808-2111 (#36) — for one notification, the channels of
    // the framework its `/submit` will validate against.
    return this.get(
      `/incidents/breach-notifications/filing-channels${this.buildQuery(
        notificationId ? { notification_id: notificationId } : {},
      )}`,
    );
  }
  async generateForm08(id: string): Promise<Record<string, unknown>> {
    return this.get(`/incidents/breach-notifications/${id}/form08`);
  }
  async createSupplementRequest(
    notificationId: string,
    requestDetails: string,
    deadline: string | null,
  ): Promise<IncidentSupplementRequest> {
    return this.post(`/incidents/breach-notifications/${notificationId}/supplements`, {
      request_details: requestDetails,
      deadline,
    });
  }
  async listSupplements(notificationId: string): Promise<{ data: IncidentSupplementRequest[] }> {
    return this.get(`/incidents/breach-notifications/${notificationId}/supplements`);
  }
  async submitSupplementResponse(
    supplementId: string,
    responsePackage: string,
  ): Promise<IncidentSupplementRequest> {
    return this.post(`/incidents/supplements/${supplementId}/submit`, {
      response_package: responsePackage,
    });
  }
  async createRemediationAction(
    incidentId: string,
    payload: Record<string, any>,
  ): Promise<RemediationAction> {
    return this.post('/incidents/remediation-actions', { incident_id: incidentId, ...payload });
  }
  async listRemediationActions(
    incidentId: string,
    params?: Record<string, any>,
  ): Promise<{ data: RemediationAction[] }> {
    return this.get(`/incidents/incidents/${incidentId}/remediation${this.buildQuery(params)}`);
  }
  async updateRemediationStatus(actionId: string, status: string): Promise<RemediationAction> {
    return this.patch(`/incidents/remediation-actions/${actionId}/status`, { status });
  }
  async verifyRemediation(actionId: string): Promise<RemediationAction> {
    return this.post(`/incidents/remediation-actions/${actionId}/verify`, {});
  }
  /**
   * Sprint 62.49.0 (AC28) — `received_at` (an instant with its offset, optional;
   * `null` = not recorded) is the anchor a records clock is raised from. It is
   * never defaulted from the record's creation.
   */
  async createAuthorityRequest(
    payload: Record<string, any> & { received_at?: string | null },
  ): Promise<IncidentAuthorityRequest> {
    return this.post('/incidents/authority-requests', payload);
  }
  /**
   * The authority-request register — one page of it.
   *
   * Sprint 64.39.0: this declared `{ data: IncidentAuthorityRequest[] }` and the
   * route returned a **bare array**, unbounded, with no count. The declaration
   * had been wrong since it was written, which is why
   * `loadAuthorityRequestOptions` in the logistics module read `res?.data ?? []`
   * and got an empty picker on every render — the type said `.data` was there
   * and nothing checked.
   *
   * The route now emits R-23's envelope with a server-enforced bound. Callers
   * that already unwrap through `unwrapResponse` were unaffected; the one that
   * reached for `.data` directly is repaired in the same sprint.
   */
  async listAuthorityRequests(params?: Record<string, any>): Promise<{
    data: IncidentAuthorityRequest[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/incidents/authority-requests${this.buildQuery(params)}`);
  }
  async submitAuthorityResponse(
    requestId: string,
    responsePackage: string,
    channel: string,
    confirmation: string,
    opts?: {
      response_context?:
        | 'authority_supplement_response'
        | 'authority_information_request_response'
        | 'authority_inspection_response';
      regulatory_framework_code?: string;
      linked_form_instance_id?: string;
    },
  ): Promise<IncidentAuthorityRequest> {
    return this.post(`/incidents/authority-requests/${requestId}/submit`, {
      response_package: responsePackage,
      channel,
      confirmation,
      ...(opts?.response_context ? { response_context: opts.response_context } : {}),
      ...(opts?.regulatory_framework_code
        ? { regulatory_framework_code: opts.regulatory_framework_code }
        : {}),
      ...(opts?.linked_form_instance_id
        ? { linked_form_instance_id: opts.linked_form_instance_id }
        : {}),
    });
  }
  // Sprint 44.5.0 — retry + status for authority-response materialisation
  async retryAuthorityResponseMaterialisation(requestId: string): Promise<{
    request_id: string;
    submission_dossier_id: string;
    materialisation_status: 'pending' | 'success' | 'failed';
    created: boolean;
  }> {
    return this.post(`/incidents/authority-requests/${requestId}/retry-materialisation`);
  }
  async getAuthorityResponseMaterialisationStatus(requestId: string): Promise<{
    filing_id: string;
    status: 'pending' | 'success' | 'failed' | null;
    dossier_id: string | null;
    failure_reason: string | null;
    last_attempt_at: string | null;
    failed_at: string | null;
    retry_count: number;
    eligible_to_retry: boolean;
  }> {
    return this.get(`/incidents/authority-requests/${requestId}/materialisation-status`);
  }
  /**
   * Sprint 62.49.0 gate E12.5 pass 1 (P3) — correct the receipt instant and the
   * framework a request is made under, after it was recorded
   * (`PATCH /incidents/authority-requests/:id/receipt`, held to
   * `incident_authority_request:write`). An absent key keeps a field; `null`
   * clears the framework. Pass 2 (P2-4): a reason is required, a recorded
   * receipt is never cleared, and it is not moved later once a response is
   * recorded or a clock it anchors is live (each refused with its own code).
   */
  async updateAuthorityRequest(
    requestId: string,
    body: {
      received_at?: string | null;
      regulatory_framework_code?: string | null;
      /** Why, as the person writes it: at least `AUTHORITY_REQUEST_RECEIPT_REASON_MIN_LENGTH` characters. */
      reason: string;
    },
  ): Promise<IncidentAuthorityRequest> {
    return this.patch(
      `/incidents/authority-requests/${encodeURIComponent(requestId)}/receipt`,
      body,
    );
  }
  async transitionAuthorityRequestStatus(
    requestId: string,
    status: string,
  ): Promise<IncidentAuthorityRequest> {
    return this.patch(`/incidents/authority-requests/${requestId}/status`, { status });
  }
  async updateAuthorityRequestChecklist(
    requestId: string,
    checklist: { item: string; checked: boolean }[],
  ): Promise<IncidentAuthorityRequest> {
    return this.patch(`/incidents/authority-requests/${requestId}/checklist`, { checklist });
  }
  async listDisclosureRegister(
    params?: Record<string, any>,
  ): Promise<{ data: DisclosureRegisterEntry[] }> {
    return this.get(`/incidents/disclosure-register${this.buildQuery(params)}`);
  }
  async createDisclosureEntry(payload: Record<string, any>): Promise<DisclosureRegisterEntry> {
    return this.post('/incidents/disclosure-register', payload);
  }
  // ── Sprint 27.2.1: Incident Export ────────────────────────
  // Sprint 42.2.3 E2 — returns AttachmentRef (was Blob).
  async exportIncidentsCsv(params?: Record<string, string>): Promise<AttachmentRef> {
    return this.get(`/incidents/export/csv${this.buildQuery(params)}`);
  }
  async exportIncidentDetailPdf(incidentId: string): Promise<Record<string, unknown>> {
    return this.get(`/incidents/export/pdf/${incidentId}`);
  }
  // ── Sprint 1.8.0: Incidents -- Audit ────────────────────────
  async listIncidentAudit(params?: Record<string, any>): Promise<{ data: IncidentAuditEvent[] }> {
    return this.get(`/incidents/audit${this.buildQuery(params)}`);
  }
  async listIncidentAuditForIncident(incidentId: string): Promise<{ data: IncidentAuditEvent[] }> {
    return this.get(`/incidents/audit/incident/${incidentId}`);
  }
  // ── Sprint 1.9.0: Onboarding -- Setup Wizard ──────────────
  async getWizard() {
    return this.get('/onboarding/wizard');
  }
  async initializeWizard() {
    return this.post('/onboarding/wizard/initialize', {});
  }
  async completeWizardStep(stepId: string, data: Record<string, any>) {
    return this.post(`/onboarding/wizard/steps/${stepId}/complete`, data);
  }
  async completeWizard() {
    return this.post('/onboarding/wizard/complete', {});
  }
  async skipWizard() {
    return this.post('/onboarding/wizard/skip', {});
  }
  // Public setup wizard (no auth required)
  //
  // fix-sprint-20261102-0448 (verifier D1): once `/setup/account/create` has
  // created the organisation, the server lets only this browser finish setup.
  // It answered with a setup session, which every later setup write presents.
  // Kept for the tab's lifetime so a reload mid-wizard can still finish.
  private setupSession: string | null = null;
  private setupSessionHeaders(): Record<string, string> | undefined {
    let session = this.setupSession;
    if (!session) {
      try {
        session = sessionStorage.getItem(SETUP_SESSION_STORAGE_KEY);
      } catch {
        session = null;
      }
    }
    return session ? { 'X-Setup-Session': session } : undefined;
  }
  private setupPost<T>(path: string, body: unknown): Promise<T> {
    return this.request('POST', path, body, true, undefined, undefined, this.setupSessionHeaders());
  }
  async validateLicensePublic(license_key: string): Promise<Record<string, unknown>> {
    return this.setupPost('/setup/license/validate', { license_key });
  }
  async getSetupWizard() {
    return this.get('/setup/wizard');
  }
  async initializeSetupWizard() {
    return this.setupPost('/setup/wizard/initialize', {});
  }
  async completeSetupWizardStep(stepId: string, data: Record<string, any>) {
    return this.setupPost(`/setup/wizard/steps/${stepId}/complete`, data);
  }
  async completeSetupWizard() {
    return this.setupPost('/setup/wizard/complete', {});
  }
  async skipSetupWizard() {
    return this.setupPost('/setup/wizard/skip', {});
  }
  async createSetupAccount(data: {
    name: string;
    email: string;
    password: string;
    org_name: string;
  }) {
    const result = await this.setupPost<{
      success?: boolean;
      org_id?: string;
      setup_session?: string;
    }>('/setup/account/create', data);
    if (result && typeof result.setup_session === 'string') {
      this.setupSession = result.setup_session;
      persistSetupSession(result.setup_session);
    }
    return result;
  }
  // ── Sprint 1.9.0: Onboarding -- Tours ─────────────────────
  async getTourDefinitions() {
    return this.get('/onboarding/tours/definitions');
  }
  async getTourProgress() {
    return this.get('/onboarding/tours/progress');
  }
  async getTourProgressForModule(module: string) {
    return this.get(`/onboarding/tours/progress/${module}`);
  }
  async startTour(module: string) {
    return this.post(`/onboarding/tours/${module}/start`, {});
  }
  async advanceTour(module: string, stepIndex: number, expectedRevision?: string) {
    return this.post(`/onboarding/tours/${module}/advance`, {
      step_index: stepIndex,
      expected_revision: expectedRevision,
    });
  }
  async completeTour(module: string, expectedRevision?: string) {
    return this.post(`/onboarding/tours/${module}/complete`, {
      expected_revision: expectedRevision,
    });
  }
  async dismissTour(module: string) {
    return this.post(`/onboarding/tours/${module}/dismiss`, {});
  }
  async resetTour(module: string) {
    return this.post(`/onboarding/tours/${module}/reset`, {});
  }
  // ── Sprint 1.9.0: Onboarding -- Help Center ──────────────
  async listHelpArticles(params?: Record<string, any>) {
    return this.get(`/onboarding/help/articles${this.buildQuery(params)}`);
  }
  async searchHelpArticles(q: string, locale?: string) {
    const params = `q=${encodeURIComponent(q)}${locale ? `&locale=${locale}` : ''}`;
    return this.get(`/onboarding/help/articles/search?${params}`);
  }
  async getHelpCategories() {
    return this.get('/onboarding/help/categories');
  }
  async getHelpModules() {
    return this.get('/onboarding/help/modules');
  }
  async getHelpTooltips() {
    return this.get('/onboarding/help/tooltips');
  }
  async getHelpArticleBySlug(slug: string) {
    return this.get(`/onboarding/help/articles/by-slug/${slug}`);
  }
  async getHelpArticle(id: string) {
    return this.get(`/onboarding/help/articles/${id}`);
  }
  async createHelpArticle(payload: Record<string, any>) {
    return this.post('/onboarding/help/articles', payload);
  }
  async updateHelpArticle(id: string, payload: Record<string, any>) {
    return this.patch(`/onboarding/help/articles/${id}`, payload);
  }
  async deleteHelpArticle(id: string) {
    return this.del(`/onboarding/help/articles/${id}`);
  }
  // ── Sprint 1.9.0: Onboarding -- Sample Data ──────────────
  async listSampleDataOperations() {
    return this.get('/onboarding/sample-data/operations');
  }
  async getSampleDataSummary() {
    return this.get('/onboarding/sample-data/summary');
  }
  async getForsetiEvaluation() {
    return this.get('/onboarding/sample-data/forseti-evaluation');
  }
  async loadForsetiEvaluation() {
    return this.post('/onboarding/sample-data/forseti-evaluation/load', {});
  }
  async clearForsetiEvaluation() {
    return this.post('/onboarding/sample-data/forseti-evaluation/clear', {});
  }
  /** Sprint 63.37.0 (B6) — `locale`: the language the cross-industry content installs in. */
  async loadSampleData(modules?: string[], locale?: SampleDataPackageLocale) {
    return this.post('/onboarding/sample-data/load', {
      ...(modules ? { modules } : {}),
      ...(locale ? { locale } : {}),
    });
  }
  async clearSampleData(modules?: string[]) {
    return this.post('/onboarding/sample-data/clear', modules ? { modules } : {});
  }
  // ── Sprint 63.26.0: Onboarding -- Industry sample-data packages ──
  async listSampleDataPackages() {
    return this.get<{
      data: SampleDataPackageSummary[];
      total: number;
      page: number;
      page_size: number;
    }>('/onboarding/sample-data/packages');
  }
  async getSampleDataPackage(key: string) {
    return this.get<SampleDataPackageDetail>(
      `/onboarding/sample-data/packages/${encodeURIComponent(key)}`,
    );
  }
  async installSampleDataPackage(
    key: string,
    body: { locale: SampleDataPackageLocale; branches?: string[] },
  ) {
    return this.post<SampleDataPackageInstallSummary>(
      `/onboarding/sample-data/packages/${encodeURIComponent(key)}/install`,
      body,
    );
  }
  async removeSampleDataPackage(key: string) {
    return this.post<SampleDataPackageRemovalSummary>(
      `/onboarding/sample-data/packages/${encodeURIComponent(key)}/remove`,
      {},
    );
  }
  // ── Sprint 1.9.0: Onboarding -- Audit ─────────────────────
  async listOnboardingAudit(params?: Record<string, any>) {
    return this.get(`/onboarding/audit${this.buildQuery(params)}`);
  }
  // ── Sprint 22.4.0: Onboarding Maturity ─────────────────────
  /**
   * Sprint 62.0.6 — returns the R-23 envelope `{ data, total, page, page_size }`.
   * Callers read `.data` (via `unwrapResponse`), never the response object.
   */
  async getPersonaPaths(params?: { page?: number; page_size?: number }): Promise<{
    data: PersonaPathListItem[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/onboarding/maturity/persona-paths${this.buildQuery(params)}`);
  }
  /** 404 for an id that is no persona; 403 `PERSONA_NOT_AVAILABLE` for one this org cannot have. */
  /**
   * Sprint 63.35.0 (Q45) — `GET /onboarding/maturity/persona-text`: every
   * persona's role description and quick-start title, EN and VI, from the
   * versioned pack `packs/personas/`. One object keyed by persona id.
   */
  async getPersonaText(): Promise<PersonaTextResponse> {
    return this.get('/onboarding/maturity/persona-text');
  }

  async getPersonaPath(personaId: string): Promise<{ data: PersonaPathDetailItem }> {
    return this.get(`/onboarding/maturity/persona-paths/${encodeURIComponent(personaId)}`);
  }
  /**
   * Sprint 62.0.6 — persona and use-case IDs this organisation may see.
   * Unpaginated: the help-centre badge renderer needs the whole set to decide
   * whether each code in article prose becomes a link.
   */
  async getPersonaAvailability() {
    return this.get('/onboarding/maturity/persona-availability');
  }
  /** A read: the saved persona when one is still available, else the detected one. */
  async detectPersona(): Promise<{ data: DetectedPersonaResponse }> {
    return this.get('/onboarding/maturity/detect-persona');
  }
  /**
   * Sprint 63.28.0 (D1) — "Use as my onboarding persona". Writes the caller's
   * own progress record only. 400 for an unknown id; 403 `PERSONA_NOT_AVAILABLE`
   * for a persona the organisation's market packs do not make available.
   */
  async choosePersona(personaId: string): Promise<{ data: ChosenPersonaResult }> {
    return this.patch('/onboarding/maturity/progress/persona', { persona_id: personaId });
  }
  async getTierModuleConfig(tier: string) {
    return this.get(`/onboarding/maturity/tier-config/${tier}`);
  }
  async getFrameworkSetupOptions(params?: { page?: number; page_size?: number }) {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/onboarding/maturity/framework-options${suffix}`);
  }
  async selectFrameworks(frameworkCodes: string[]) {
    return this.post('/onboarding/maturity/select-frameworks', { framework_codes: frameworkCodes });
  }
  async getQuickStartScenarios(personaId?: string) {
    const q = personaId ? `?persona_id=${personaId}` : '';
    return this.get(`/onboarding/maturity/quick-starts${q}`);
  }
  async startQuickStart(scenarioId: string) {
    return this.post(`/onboarding/maturity/quick-starts/${scenarioId}/start`, {});
  }
  async advanceQuickStart(scenarioId: string, stepIndex: number) {
    return this.post(`/onboarding/maturity/quick-starts/${scenarioId}/advance`, {
      step_index: stepIndex,
    });
  }
  async completeQuickStart(scenarioId: string) {
    return this.post(`/onboarding/maturity/quick-starts/${scenarioId}/complete`, {});
  }
  async resetQuickStart(scenarioId: string) {
    return this.post(`/onboarding/maturity/quick-starts/${scenarioId}/reset`, {});
  }
  async getOnboardingProgress() {
    return this.get('/onboarding/maturity/progress');
  }
  async getQuickStartProgress() {
    return this.get('/onboarding/maturity/quick-starts/progress');
  }
  async initializeOnboardingProgress(payload: Record<string, any>) {
    return this.post('/onboarding/maturity/progress/initialize', payload);
  }
  // ── Sprint 2.0.0: Connectors -- Definitions ────────────────
  async listConnectorDefinitions(params?: Record<string, any>) {
    return this.get(`/connectors/definitions${this.buildQuery(params)}`);
  }
  async getConnectorDefinition(connectorId: string) {
    return this.get(`/connectors/definitions/${connectorId}`);
  }
  async createConnectorDefinition(payload: Record<string, any>) {
    return this.post('/connectors/definitions', payload);
  }
  async updateConnectorDefinition(connectorId: string, payload: Record<string, any>) {
    return this.patch(`/connectors/definitions/${connectorId}`, payload);
  }
  // ── Sprint 2.0.0: Connectors -- Instances ─────────────────
  async listConnectorInstances(params?: Record<string, any>) {
    return this.get(`/connectors/instances${this.buildQuery(params)}`);
  }
  async getConnectorInstance(id: string) {
    return this.get(`/connectors/instances/${id}`);
  }
  async createConnectorInstance(payload: Record<string, any>) {
    return this.post('/connectors/instances', payload);
  }
  async updateConnectorInstance(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/instances/${id}`, payload);
  }
  async updateConnectorInstanceStatus(id: string, status: string) {
    return this.post(`/connectors/instances/${id}/status`, { status });
  }
  async testConnectorInstance(id: string) {
    return this.post(`/connectors/instances/${id}/test`, {});
  }
  async deleteConnectorInstance(id: string) {
    return this.del(`/connectors/instances/${id}`);
  }
  // ── Sprint 2.0.0: Connectors -- Sync History ─────────────
  async listInstanceSyncHistory(instanceId: string, params?: Record<string, any>) {
    return this.get(`/connectors/instances/${instanceId}/sync-history${this.buildQuery(params)}`);
  }
  async getSyncHistoryEntry(id: string) {
    return this.get(`/connectors/sync-history/${id}`);
  }
  async triggerManualSync(instanceId: string) {
    return this.post(`/connectors/instances/${instanceId}/sync`, {});
  }
  /**
   * Sprint 63.30.0 (#583) — record a sync run's outcome: `{ status:
   * 'success'|'partial'|'failure', records_processed?, records_created?,
   * records_failed? }`. 409 `SYNC_ALREADY_COMPLETED` when already recorded.
   */
  async completeConnectorSync(
    instanceId: string,
    syncId: string,
    payload: Record<string, unknown>,
  ) {
    return this.post(`/connectors/instances/${instanceId}/sync/${syncId}/complete`, payload);
  }
  // ── Sprint 2.0.0: Connectors -- Credentials ──────────────
  async listCredentials(params?: Record<string, any>) {
    return this.get(`/connectors/credentials${this.buildQuery(params)}`);
  }
  async getCredential(id: string) {
    return this.get(`/connectors/credentials/${id}`);
  }
  async createCredential(payload: Record<string, any>) {
    return this.post('/connectors/credentials', payload);
  }
  async updateCredential(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/credentials/${id}`, payload);
  }
  async rotateCredential(id: string, value: string) {
    return this.post(`/connectors/credentials/${id}/rotate`, { value });
  }
  async deleteCredential(id: string) {
    return this.del(`/connectors/credentials/${id}`);
  }
  async getCredentialHealth() {
    return this.get('/connectors/credentials/health');
  }
  async getCredentialUsage(id: string) {
    return this.get(`/connectors/credentials/${id}/usage`);
  }
  async listOrphanedCredentials() {
    return this.get('/connectors/credentials/orphans');
  }
  // ── Sprint 2.0.0: Connectors -- Event Bus Events ─────────
  async listEventBusEvents(params?: Record<string, any>) {
    return this.get(`/connectors/events${this.buildQuery(params)}`);
  }
  async getEventBusEvent(id: string) {
    return this.get(`/connectors/events/${id}`);
  }
  async publishEvent(payload: Record<string, any>) {
    return this.post('/connectors/events', payload);
  }
  // ── Sprint 2.0.0: Connectors -- Event Bus Subscriptions ──
  async listEventBusSubscriptions(params?: Record<string, any>) {
    return this.get(`/connectors/subscriptions${this.buildQuery(params)}`);
  }
  async getEventBusSubscription(id: string) {
    return this.get(`/connectors/subscriptions/${id}`);
  }
  async createEventBusSubscription(payload: Record<string, any>) {
    return this.post('/connectors/subscriptions', payload);
  }
  async updateEventBusSubscription(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/subscriptions/${id}`, payload);
  }
  async toggleEventBusSubscription(id: string, enabled: boolean) {
    return this.post(`/connectors/subscriptions/${id}/toggle`, { enabled });
  }
  async deleteEventBusSubscription(id: string) {
    return this.del(`/connectors/subscriptions/${id}`);
  }
  // ── Sprint 2.0.0: Connectors -- DLQ ──────────────────────
  async listDlqEntries(params?: Record<string, any>) {
    return this.get(`/connectors/dlq${this.buildQuery(params)}`);
  }
  async retryDlqEntry(id: string) {
    return this.post(`/connectors/dlq/${id}/retry`, {});
  }
  async dismissDlqEntry(id: string) {
    return this.post(`/connectors/dlq/${id}/dismiss`, {});
  }
  // ── Sprint 2.0.0: Connectors -- Audit ────────────────────
  async listConnectorAudit(params?: Record<string, any>) {
    return this.get(`/connectors/audit${this.buildQuery(params)}`);
  }
  async getConnectorEntityAudit(entityType: string, entityId: string) {
    return this.get(`/connectors/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 21.6.0: Connectors -- BRD Capture & Decision Records ──
  async listConnectorBrds(params?: Record<string, any>) {
    return this.get(`/connectors/brds${this.buildQuery(params)}`);
  }
  async getConnectorBrd(id: string) {
    return this.get(`/connectors/brds/${id}`);
  }
  async createConnectorBrd(payload: Record<string, any>) {
    return this.post('/connectors/brds', payload);
  }
  async updateConnectorBrdStatus(id: string, status: string) {
    return this.patch(`/connectors/brds/${id}/status`, { status });
  }
  async listConnectorDecisionRecords(params?: Record<string, any>) {
    return this.get(`/connectors/decision-records${this.buildQuery(params)}`);
  }
  async createConnectorDecisionRecord(payload: Record<string, any>) {
    return this.post('/connectors/decision-records', payload);
  }
  // ── Sprint 21.6.0: Connectors -- Control Mapping & Security Review ──
  async createConnectorControlMapping(payload: Record<string, any>) {
    return this.post('/connectors/control-mappings', payload);
  }
  async listConnectorControlMappings(connectorId: string) {
    return this.get(`/connectors/connectors/${connectorId}/control-mappings`);
  }
  async listAllConnectorControlMappings(params?: Record<string, any>) {
    return this.get(`/connectors/control-mappings${this.buildQuery(params)}`);
  }
  async listConnectorSecurityReviews(params?: Record<string, any>) {
    return this.get(`/connectors/security-reviews${this.buildQuery(params)}`);
  }
  async createConnectorSecurityReview(payload: Record<string, any>) {
    return this.post('/connectors/security-reviews', payload);
  }
  async updateConnectorSecurityReviewStatus(id: string, status: string) {
    return this.patch(`/connectors/security-reviews/${id}/status`, { status });
  }
  // ── Sprint 21.6.0: Connectors -- POC Scorecards & Health ──
  async listConnectorPocScorecards(params?: Record<string, any>) {
    return this.get(`/connectors/poc-scorecards${this.buildQuery(params)}`);
  }
  async createConnectorPocScorecard(payload: Record<string, any>) {
    return this.post('/connectors/poc-scorecards', payload);
  }
  async getConnectorPocScorecard(id: string) {
    return this.get(`/connectors/poc-scorecards/${id}`);
  }
  async getConnectorHealthMetrics(instanceId: string) {
    return this.get(`/connectors/connectors/${instanceId}/health-metrics`);
  }
  // ── Sprint 2.1.0: Vendor Risk -- Configuration ─────────────
  async getVendorRiskConfig() {
    return this.get('/vendor-risk/config');
  }
  async updateVendorRiskConfig(payload: Record<string, any>) {
    return this.patch('/vendor-risk/config', payload);
  }
  async updateGateCriteria(criteria: Record<string, any>[]) {
    return this.put('/vendor-risk/config/gate-criteria', { criteria });
  }
  // ── Sprint 2.1.0: Vendor Risk -- Scores ───────────────────
  async listVendorRiskScores(params?: Record<string, any>) {
    return this.get(`/vendor-risk/scores${this.buildQuery(params)}`);
  }
  async calculateVendorRiskScore(payload: Record<string, any>) {
    return this.post('/vendor-risk/scores', payload);
  }
  async getVendorRiskDistribution() {
    return this.get('/vendor-risk/scores/distribution');
  }
  async getVendorRiskTierChanges(params?: Record<string, any>) {
    return this.get(`/vendor-risk/scores/tier-changes${this.buildQuery(params)}`);
  }
  async getVendorScoreHistory(vendorId: string, params?: Record<string, any>) {
    return this.get(`/vendor-risk/scores/vendor/${vendorId}${this.buildQuery(params)}`);
  }
  async getVendorLatestScore(vendorId: string) {
    return this.get(`/vendor-risk/scores/vendor/${vendorId}/latest`);
  }
  async getVendorRiskScore(scoreId: string) {
    return this.get(`/vendor-risk/scores/${scoreId}`);
  }
  async compareVendorRiskScores(scoreId1: string, scoreId2: string) {
    return this.get(`/vendor-risk/scores/compare/${scoreId1}/${scoreId2}`);
  }
  async batchRecalculateScores(vendorIds: string[]) {
    return this.post('/vendor-risk/scores/batch-recalculate', { vendor_ids: vendorIds });
  }
  // ── Sprint 2.1.0: Vendor Risk -- Questionnaire Templates ──
  async listQuestionnaireTemplates(params?: Record<string, any>) {
    return this.get(`/vendor-risk/questionnaire-templates${this.buildQuery(params)}`);
  }
  async getQuestionnaireTemplate(templateId: string) {
    return this.get(`/vendor-risk/questionnaire-templates/${templateId}`);
  }
  async createQuestionnaireTemplate(payload: Record<string, any>) {
    return this.post('/vendor-risk/questionnaire-templates', payload);
  }
  async updateQuestionnaireTemplate(templateId: string, payload: Record<string, any>) {
    return this.patch(`/vendor-risk/questionnaire-templates/${templateId}`, payload);
  }
  async updateQuestionnaireTemplateStatus(templateId: string, status: string) {
    return this.post(`/vendor-risk/questionnaire-templates/${templateId}/status`, { status });
  }
  // ── Sprint 2.1.0: Vendor Risk -- Questionnaire Instances ──
  async listQuestionnaires(params?: Record<string, any>) {
    return this.get(`/vendor-risk/questionnaires${this.buildQuery(params)}`);
  }
  async getQuestionnaire(instanceId: string) {
    return this.get(`/vendor-risk/questionnaires/${instanceId}`);
  }
  async initiateQuestionnaire(payload: Record<string, any>) {
    return this.post('/vendor-risk/questionnaires', payload);
  }
  async updateQuestionnaireResponses(instanceId: string, responses: Record<string, any>[]) {
    return this.patch(`/vendor-risk/questionnaires/${instanceId}/responses`, { responses });
  }
  async submitQuestionnaire(instanceId: string) {
    return this.post(`/vendor-risk/questionnaires/${instanceId}/submit`, {});
  }
  async reviewQuestionnaire(instanceId: string, payload: Record<string, any>) {
    return this.post(`/vendor-risk/questionnaires/${instanceId}/review`, payload);
  }
  async resubmitQuestionnaire(instanceId: string) {
    return this.post(`/vendor-risk/questionnaires/${instanceId}/resubmit`, {});
  }
  /**
   * Sprint 43.6.0 W4 — single-instance export (PDF / DOCX audit record
   * or CSV / XLSX analytics). Returns AttachmentRef persisted via
   * UnifiedExportService; caller triggers the download via
   * downloadPlatformFile(ref.file_id, ref.filename).
   */
  async exportQuestionnaireInstance(args: {
    id: string;
    format: 'pdf' | 'docx' | 'csv' | 'xlsx';
    /** Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the document's language. */
    locale?: string;
  }): Promise<AttachmentRef> {
    const query: Record<string, string> = { format: args.format };
    if (args.locale) query.locale = args.locale;
    return this.get(`/vendor-risk/questionnaires/${args.id}/export${this.buildQuery(query)}`);
  }
  /**
   * Sprint 43.6.0 W4 — portfolio-level CSV / XLSX export. Filters match
   * the listQuestionnaires endpoint. Returns AttachmentRef via File
   * Component; caller invokes downloadPlatformFile to trigger download.
   */
  async exportQuestionnaireList(args: {
    filters?: Record<string, string | undefined>;
    format: 'csv' | 'xlsx';
    /** Sprint 62.58.0 gate E12.5 pass 1 remainder — the document's language. */
    locale?: string;
  }): Promise<AttachmentRef> {
    const params: Record<string, string> = { format: args.format };
    if (args.locale) params.locale = args.locale;
    if (args.filters) {
      for (const [k, v] of Object.entries(args.filters)) {
        if (typeof v === 'string' && v.length > 0) params[k] = v;
      }
    }
    return this.get(`/vendor-risk/questionnaires/export${this.buildQuery(params)}`);
  }
  // ── Sprint 47.5.8.1: Contracts (first-class module) ───────
  async listContracts(params?: Record<string, any>) {
    return this.get(`/contracts${this.buildQuery(params)}`);
  }
  async getExpiringContracts(withinDays?: number) {
    const query = withinDays ? `?within_days=${withinDays}` : '';
    return this.get(`/contracts/expiring${query}`);
  }
  async getContractCoverage() {
    return this.get('/contracts/coverage');
  }
  async getContract(contractId: string) {
    return this.get(`/contracts/${contractId}`);
  }
  async createContract(payload: Record<string, any>) {
    return this.post('/contracts', payload);
  }
  async updateContract(contractId: string, payload: Record<string, any>) {
    return this.patch(`/contracts/${contractId}`, payload);
  }
  /**
   * Sprint 65.5.0 AC3 — `reason` is required by the SERVER for a transition
   * that removes the contract from force (`active → expired|terminated|
   * superseded`), and is omitted from the body when absent so the `.strict()`
   * schema sees the same shape it saw before.
   */
  async updateContractStatus(contractId: string, status: string, reason?: string) {
    return this.post(`/contracts/${contractId}/status`, reason ? { status, reason } : { status });
  }
  async recordContractSignature(contractId: string, payload: Record<string, unknown>) {
    return this.post(`/contracts/${contractId}/signatures`, payload);
  }
  async deleteContract(contractId: string) {
    return this.del(`/contracts/${contractId}`);
  }
  // ── Sprint 2.1.0: Vendor Risk -- Gate Evaluations ─────────
  async evaluateVendorGate(vendorId: string, approvalId: string) {
    return this.post('/vendor-risk/gates/evaluate', {
      vendor_id: vendorId,
      approval_id: approvalId,
    });
  }
  async listGateEvaluations(params?: Record<string, any>) {
    return this.get(`/vendor-risk/gates${this.buildQuery(params)}`);
  }
  async getVendorLatestGate(vendorId: string) {
    return this.get(`/vendor-risk/gates/vendor/${vendorId}/latest`);
  }
  async getGateEvaluation(evaluationId: string) {
    return this.get(`/vendor-risk/gates/${evaluationId}`);
  }
  async overrideGateEvaluation(evaluationId: string, justification: string) {
    return this.post(`/vendor-risk/gates/${evaluationId}/override`, { justification });
  }
  // ── Sprint 2.1.0: Vendor Risk -- Reviews ──────────────────
  async listVendorReviews(params?: Record<string, any>) {
    return this.get(`/vendor-risk/reviews${this.buildQuery(params)}`);
  }
  async getVendorReviewKpis() {
    return this.get('/vendor-risk/reviews/kpis');
  }
  async getVendorReviewHistory(vendorId: string, params?: Record<string, any>) {
    return this.get(`/vendor-risk/reviews/vendor/${vendorId}${this.buildQuery(params)}`);
  }
  async getVendorReview(reviewId: string) {
    return this.get(`/vendor-risk/reviews/${reviewId}`);
  }
  async createVendorReview(payload: Record<string, any>) {
    return this.post('/vendor-risk/reviews', payload);
  }
  async updateVendorReview(reviewId: string, payload: Record<string, any>) {
    return this.patch(`/vendor-risk/reviews/${reviewId}`, payload);
  }
  // ── Sprint 2.1.0: Vendor Risk -- Audit ────────────────────
  async listVendorRiskAudit(params?: Record<string, any>) {
    return this.get(`/vendor-risk/audit${this.buildQuery(params)}`);
  }
  async getVendorRiskEntityAudit(entityType: string, entityId: string) {
    return this.get(`/vendor-risk/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 21.0.0: Vendor Risk Maturity ──────────────────────
  async createRiskSignal(payload: Record<string, unknown>): Promise<VendorRiskSignal> {
    return this.post('/vendor-risk/signals', payload);
  }
  async listRiskSignals(params?: Record<string, string>): Promise<VendorRiskSignal[]> {
    return this.get(`/vendor-risk/signals${this.buildQuery(params)}`);
  }
  async triggerVendorReassessment(
    payload: Record<string, unknown>,
  ): Promise<VendorReassessmentTask> {
    return this.post('/vendor-risk/reassessments', payload);
  }
  /**
   * Sprint 63.30.0 (#591) — `GET /vendor-risk/reassessments` answers a PLAIN
   * ARRAY (`listReassessmentTasks` returns `VendorReassessmentTask[]`), not a
   * paginated envelope; the type said otherwise.
   */
  async listReassessmentTasks(params?: Record<string, string>): Promise<VendorReassessmentTask[]> {
    return this.get(`/vendor-risk/reassessments${this.buildQuery(params)}`);
  }
  async completeVendorReassessment(
    taskId: string,
    data: Record<string, unknown>,
  ): Promise<VendorReassessmentTask> {
    return this.post(`/vendor-risk/reassessments/${taskId}/complete`, data);
  }
  async getVendorRiskTrend(vendorId: string): Promise<VendorRiskScore[]> {
    return this.get(`/vendor-risk/vendors/${vendorId}/risk-trend`);
  }
  async getVendorRiskPortfolio(): Promise<Record<string, unknown>> {
    return this.get('/vendor-risk/portfolio/dashboard');
  }
  async getVendorRiskTrendById(vendorId: string): Promise<VendorRiskScore[]> {
    return this.get(`/vendor-risk/portfolio/vendor-trend/${vendorId}`);
  }
  async listOutstandingQuestionnaires(): Promise<QuestionnaireInstance[]> {
    return this.get('/vendor-risk/portfolio/outstanding-questionnaires');
  }
  async listOverdueAssessments(): Promise<Record<string, unknown>[]> {
    return this.get('/vendor-risk/portfolio/overdue-assessments');
  }
  async routeToVendorReviewers(payload: Record<string, unknown>): Promise<ReviewRouting> {
    return this.post('/vendor-risk/review-routing', payload);
  }
  async getVendorReviewRoutingStatus(questionnaireId: string): Promise<ReviewRouting> {
    return this.get(`/vendor-risk/review-routing/${questionnaireId}`);
  }
  async createRiskAcceptance(payload: Record<string, unknown>): Promise<VendorRiskAcceptance> {
    return this.post('/vendor-risk/acceptances', payload);
  }
  async listRiskAcceptances(params?: Record<string, string>): Promise<VendorRiskAcceptance[]> {
    return this.get(`/vendor-risk/acceptances${this.buildQuery(params)}`);
  }
  async reviewAcceptanceCondition(
    acceptanceId: string,
    conditionIndex: number,
    data: Record<string, unknown>,
  ): Promise<VendorRiskAcceptance> {
    return this.post(`/vendor-risk/acceptances/${acceptanceId}/conditions/${conditionIndex}`, data);
  }
  async exportAcceptanceRegister(): Promise<Record<string, unknown>> {
    return this.get('/vendor-risk/acceptances/export');
  }
  /**
   * Sprint 43.5.5 — Unified export for Risk Acceptance Register.
   * Returns AttachmentRef persisted via UnifiedExportService (File
   * Component). Call downloadPlatformFile() to trigger the download.
   */
  async exportAcceptanceRegisterUnified(format: 'csv' | 'xlsx' | 'json'): Promise<AttachmentRef> {
    return this.get(`/vendor-risk/acceptances/export${this.buildQuery({ format })}`);
  }
  /**
   * Sprint 43.5.5 — Unified export for Sub-Processor Register.
   * Supports CSV / XLSX / JSON, persisted in File Component.
   */
  async exportSubProcessorRegisterUnified(format: 'csv' | 'xlsx' | 'json'): Promise<AttachmentRef> {
    return this.get(`/vendor-risk/sub-processor-register/export${this.buildQuery({ format })}`);
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Runs ───────────
  async listOrchestrationRuns(params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/runs${this.buildQuery(params)}`);
  }
  async getOrchestrationRun(runId: string) {
    return this.get(`/deletion-orchestration/runs/${runId}`);
  }
  async createOrchestrationRun(payload: Record<string, any>) {
    return this.post('/deletion-orchestration/runs', payload);
  }
  async updateOrchestrationRunTargets(runId: string, targets: Record<string, any>) {
    return this.patch(`/deletion-orchestration/runs/${runId}/targets`, targets);
  }
  async startOrchestrationRun(runId: string) {
    return this.post(`/deletion-orchestration/runs/${runId}/start`, {});
  }
  /**
   * Sprint 63.30.0 (E10, #589) — cancel a run that is not executing; needs
   * `deletion_orchestration:cancel`. An `in_progress` run answers 409
   * `RUN_EXECUTING` (pause it first).
   */
  async cancelOrchestrationRun(runId: string, reason?: string) {
    return this.post(
      `/deletion-orchestration/runs/${runId}/cancel`,
      reason !== undefined ? { reason } : {},
    );
  }
  async updateOrchestrationRunStatus(runId: string, status: string) {
    return this.patch(`/deletion-orchestration/runs/${runId}/status`, { status });
  }
  async getOrchestrationDashboard() {
    return this.get('/deletion-orchestration/runs/dashboard');
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Execution Tasks ─
  async listExecutionTasks(runId: string, params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/runs/${runId}/tasks${this.buildQuery(params)}`);
  }
  async getExecutionTask(taskId: string) {
    return this.get(`/deletion-orchestration/tasks/${taskId}`);
  }
  async createExecutionTasks(runId: string, payload: Record<string, any>) {
    return this.post(`/deletion-orchestration/runs/${runId}/tasks`, payload);
  }
  async assignExecutionTask(taskId: string, assigneeId: string) {
    return this.patch(`/deletion-orchestration/tasks/${taskId}/assign`, {
      assigned_to: assigneeId,
    });
  }
  async updateExecutionTaskStatus(taskId: string, status: string, opts?: Record<string, any>) {
    return this.patch(`/deletion-orchestration/tasks/${taskId}/status`, { status, ...opts });
  }
  async updateDeletionExecutionStep(taskId: string, stepId: string, updates: Record<string, any>) {
    return this.patch(`/deletion-orchestration/tasks/${taskId}/steps/${stepId}`, updates);
  }
  async verifyExecutionTask(taskId: string) {
    return this.post(`/deletion-orchestration/tasks/${taskId}/verify`, {});
  }
  async getOverdueExecutionTasks() {
    return this.get('/deletion-orchestration/tasks/overdue');
  }
  async getMyExecutionTasks(params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/tasks/my-tasks${this.buildQuery(params)}`);
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Checklist Templates
  async listChecklistTemplates(params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/templates${this.buildQuery(params)}`);
  }
  async getChecklistTemplate(templateId: string) {
    return this.get(`/deletion-orchestration/templates/${templateId}`);
  }
  async createChecklistTemplate(payload: Record<string, any>) {
    return this.post('/deletion-orchestration/templates', payload);
  }
  async updateChecklistTemplate(templateId: string, payload: Record<string, any>) {
    return this.patch(`/deletion-orchestration/templates/${templateId}`, payload);
  }
  async updateChecklistTemplateSteps(templateId: string, steps: Record<string, any>[]) {
    return this.put(`/deletion-orchestration/templates/${templateId}/steps`, { steps });
  }
  async cloneChecklistTemplate(templateId: string, newName: string) {
    return this.post(`/deletion-orchestration/templates/${templateId}/clone`, { name: newName });
  }
  async archiveChecklistTemplate(templateId: string) {
    return this.post(`/deletion-orchestration/templates/${templateId}/archive`, {});
  }
  async activateChecklistTemplate(templateId: string) {
    return this.post(`/deletion-orchestration/templates/${templateId}/activate`, {});
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Vendor Deletion Requests
  async listVendorDeletionRequests(params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/vendor-requests${this.buildQuery(params)}`);
  }
  async getVendorDeletionRequest(requestId: string) {
    return this.get(`/deletion-orchestration/vendor-requests/${requestId}`);
  }
  async createVendorDeletionRequest(payload: Record<string, any>) {
    return this.post('/deletion-orchestration/vendor-requests', payload);
  }
  async updateVendorDeletionRequestStatus(requestId: string, status: string) {
    return this.patch(`/deletion-orchestration/vendor-requests/${requestId}/status`, { status });
  }
  async addVendorDeletionCommunication(requestId: string, entry: Record<string, any>) {
    return this.post(`/deletion-orchestration/vendor-requests/${requestId}/communication`, entry);
  }
  async recordVendorDeletionConfirmation(requestId: string, confirmation: Record<string, any>) {
    return this.post(`/deletion-orchestration/vendor-requests/${requestId}/confirm`, confirmation);
  }
  async recordVendorDeletionDispute(requestId: string, dispute: Record<string, any>) {
    return this.post(`/deletion-orchestration/vendor-requests/${requestId}/dispute`, dispute);
  }
  async getOverdueVendorDeletionRequests() {
    return this.get('/deletion-orchestration/vendor-requests/overdue');
  }
  async getVendorDeletionMetrics(vendorId?: string) {
    const query = vendorId ? `?vendor_id=${vendorId}` : '';
    return this.get(`/deletion-orchestration/vendor-requests/metrics${query}`);
  }
  async getVendorDeletionDashboard() {
    return this.get('/deletion-orchestration/vendor-requests/dashboard');
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Proof Records ──
  async listProofRecords(runId: string) {
    return this.get(`/deletion-orchestration/runs/${runId}/proof`);
  }
  async verifyProofChain(runId: string) {
    return this.post(`/deletion-orchestration/runs/${runId}/proof/verify`, {});
  }
  async generateAttestation(runId: string, payload: Record<string, any>) {
    return this.post(`/deletion-orchestration/runs/${runId}/proof/attestation`, payload);
  }
  /**
   * Sprint 62.35.0 (AC8) — append a destruction record's identification to a
   * run's proof chain. Every key is sent; an unrecorded optional one is `null`.
   */
  async recordDestructionIdentification(
    runId: string,
    payload: import('@vn-grc/shared').DestructionIdentification,
  ) {
    return this.post(
      `/deletion-orchestration/runs/${encodeURIComponent(runId)}/proof/destruction-record`,
      payload,
    );
  }
  /**
   * Sprint 44.0.4 Wave 2 — R-33 backend export for the three
   * Deletion Orchestration pages that previously bypassed
   * UnifiedExportService via `downloadClientExport`. Each returns an
   * AttachmentRef; caller invokes `downloadPlatformFile(ref.file_id,
   * ref.filename)` to trigger the browser download.
   */
  async exportDeletionExecutionTasks(
    runId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/deletion-orchestration/runs/${runId}/tasks/export${this.buildQuery(params)}`);
  }
  async exportDeletionVendorRequests(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/deletion-orchestration/vendor-requests/export${this.buildQuery(params)}`);
  }
  async exportDeletionProofChain(
    runId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/deletion-orchestration/runs/${runId}/proof/export${this.buildQuery(params)}`);
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Reports ────────
  async generateCompletionReport(runId: string) {
    return this.post(`/deletion-orchestration/runs/${runId}/report`, {});
  }
  async generateExceptionReport(runId: string) {
    return this.post(`/deletion-orchestration/runs/${runId}/exception-report`, {});
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Audit ──────────
  async listDeletionOrchestrationAudit(params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/audit${this.buildQuery(params)}`);
  }
  async getDeletionOrchestrationEntityAudit(entityType: string, entityId: string) {
    return this.get(`/deletion-orchestration/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 21.5.0: Deletion Orchestration Maturity ─────────
  async listDsrRuns(params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/dsr-runs${this.buildQuery(params)}`);
  }
  async createDsrDeletionRun(data: Record<string, any>) {
    return this.post('/deletion-orchestration/dsr-runs', data);
  }
  async getDsrDeletionStatus(dsrCaseId: string) {
    // Sprint 63.30.0 (#589) — the route is `GET /dsr-runs/:dsrCaseId`; this
    // client read `/dsr-runs/:id/status`, which only the PATCH serves.
    return this.get(`/deletion-orchestration/dsr-runs/${encodeURIComponent(dsrCaseId)}`);
  }
  async updateDsrRunStatus(dsrRunId: string, status: string) {
    return this.patch(`/deletion-orchestration/dsr-runs/${dsrRunId}/status`, { status });
  }
  async routeTaskToDepartment(taskId: string, departmentOwnerId: string) {
    return this.post(`/deletion-orchestration/tasks/${taskId}/route`, {
      department_owner_id: departmentOwnerId,
    });
  }
  async confirmDepartmentDeletion(taskId: string, proofData: Record<string, any>) {
    return this.post(`/deletion-orchestration/tasks/${taskId}/confirm`, proofData);
  }
  async createVendorFollowup(data: Record<string, any>) {
    return this.post('/deletion-orchestration/vendor-followups', data);
  }
  async recordVendorConfirmation(followupId: string, data: Record<string, any>) {
    return this.post(`/deletion-orchestration/vendor-followups/${followupId}/confirm`, data);
  }
  async listOverdueVendorDeletions(params?: Record<string, string>) {
    return this.get(`/deletion-orchestration/vendor-followups/overdue${this.buildQuery(params)}`);
  }
  async listVendorFollowups(params?: Record<string, string>) {
    return this.get(`/deletion-orchestration/vendor-followups${this.buildQuery(params)}`);
  }
  async escalateVendorDeletion(followupId: string) {
    return this.post(`/deletion-orchestration/vendor-followups/${followupId}/escalate`, {});
  }
  async getDeletionCompleteness(dataSubjectId: string) {
    return this.get(`/deletion-orchestration/completeness/${dataSubjectId}`);
  }
  async checkRetentionConflicts(data: Record<string, any>) {
    return this.post('/deletion-orchestration/retention-conflicts/check', data);
  }
  async resolveRetentionConflict(conflictId: string, resolution: string) {
    return this.post(`/deletion-orchestration/retention-conflicts/${conflictId}/resolve`, {
      resolution,
    });
  }
  // ── Sprint 2.3.0: Discovery & Classification Automation ───
  // Discovery Scans
  async getDiscoveryDashboard() {
    return this.get('/discovery/scans/dashboard');
  }
  async listDiscoveryScans(params?: Record<string, any>) {
    return this.get(`/discovery/scans${this.buildQuery(params)}`);
  }
  async getDiscoveryScan(scanId: string) {
    return this.get(`/discovery/scans/${scanId}`);
  }
  async createDiscoveryScan(payload: any) {
    return this.post('/discovery/scans', payload);
  }
  async updateDiscoveryScanStatus(scanId: string, status: string) {
    return this.patch(`/discovery/scans/${scanId}/status`, { status });
  }
  async updateDiscoveryScanProgress(scanId: string, progress: any) {
    return this.patch(`/discovery/scans/${scanId}/progress`, progress);
  }
  // Staged Records
  async listStagedRecords(params?: Record<string, any>) {
    return this.get(`/discovery/staged-records${this.buildQuery(params)}`);
  }
  async getStagedRecord(recordId: string) {
    return this.get(`/discovery/staged-records/${recordId}`);
  }
  async createStagedRecord(payload: any) {
    return this.post('/discovery/staged-records', payload);
  }
  async reviewStagedRecord(recordId: string) {
    return this.post(`/discovery/staged-records/${recordId}/review`, {});
  }
  async promoteStagedRecord(recordId: string) {
    return this.post(`/discovery/staged-records/${recordId}/promote`, {});
  }
  async rejectStagedRecord(recordId: string, reason: string) {
    return this.post(`/discovery/staged-records/${recordId}/reject`, { reason });
  }
  async mergeStagedRecord(recordId: string, targetInventoryId: string) {
    return this.post(`/discovery/staged-records/${recordId}/merge`, {
      target_inventory_id: targetInventoryId,
    });
  }
  async bulkPromoteStagedRecords(ids: string[]) {
    return this.post('/discovery/staged-records/bulk-promote', { ids });
  }
  async bulkRejectStagedRecords(ids: string[], reason: string) {
    return this.post('/discovery/staged-records/bulk-reject', { ids, reason });
  }
  // Sprint 45.5.0 — Discovery → Registry promotion
  async promoteStagedRecordToRegistry(
    recordId: string,
    payload: {
      category_id: string;
      name?: string;
      field_identifiers?: string[];
      proposed_sensitivity?: 'standard' | 'sensitive' | 'special_category';
    },
  ): Promise<{ staged_record: unknown; registry_item_id: string }> {
    return this.post(`/discovery/staged-records/${recordId}/promote-to-registry`, payload);
  }
  async bulkPromoteStagedRecordsToRegistry(
    items: Array<{
      staged_record_id: string;
      category_id: string;
      name?: string;
      field_identifiers?: string[];
      proposed_sensitivity?: 'standard' | 'sensitive' | 'special_category';
    }>,
  ): Promise<{
    results: Array<{
      staged_record_id: string;
      status: 'promoted' | 'error';
      registry_item_id?: string;
      error?: string;
    }>;
  }> {
    return this.post('/discovery/staged-records/bulk-promote-to-registry', { items });
  }
  // Dedup Rules
  async listDedupRules(params?: Record<string, any>) {
    return this.get(`/discovery/dedup-rules${this.buildQuery(params)}`);
  }
  async getDedupRule(ruleId: string) {
    return this.get(`/discovery/dedup-rules/${ruleId}`);
  }
  async createDedupRule(payload: any) {
    return this.post('/discovery/dedup-rules', payload);
  }
  async updateDedupRule(ruleId: string, payload: any) {
    return this.put(`/discovery/dedup-rules/${ruleId}`, payload);
  }
  async deleteDedupRule(ruleId: string) {
    return this.del(`/discovery/dedup-rules/${ruleId}`);
  }
  async toggleDedupRule(ruleId: string, enabled: boolean) {
    return this.patch(`/discovery/dedup-rules/${ruleId}/toggle`, { enabled });
  }
  async seedDedupDefaults() {
    return this.post('/discovery/dedup-rules/seed-defaults', {});
  }
  // Classification Rules
  async listClassificationRules(params?: Record<string, any>) {
    return this.get(`/discovery/classification-rules${this.buildQuery(params)}`);
  }
  async getClassificationRule(ruleId: string) {
    return this.get(`/discovery/classification-rules/${ruleId}`);
  }
  async createClassificationRule(payload: any) {
    return this.post('/discovery/classification-rules', payload);
  }
  async updateClassificationRule(ruleId: string, payload: any) {
    return this.put(`/discovery/classification-rules/${ruleId}`, payload);
  }
  async deleteClassificationRule(ruleId: string) {
    return this.del(`/discovery/classification-rules/${ruleId}`);
  }
  async toggleClassificationRule(ruleId: string, enabled: boolean) {
    return this.patch(`/discovery/classification-rules/${ruleId}/toggle`, { enabled });
  }
  async seedClassificationRuleDefaults() {
    return this.post('/discovery/classification-rules/seed-defaults', {});
  }
  async runClassificationRules(): Promise<{ created: number; skipped: number }> {
    return this.post('/discovery/classification-rules/run', {});
  }
  async getClassificationRuleEffectiveness() {
    return this.get('/discovery/classification-rules/effectiveness');
  }
  // Classification Suggestions
  async listClassificationSuggestions(params?: Record<string, any>) {
    return this.get(`/discovery/suggestions${this.buildQuery(params)}`);
  }
  async getClassificationSuggestion(suggestionId: string) {
    return this.get(`/discovery/suggestions/${suggestionId}`);
  }
  async createClassificationSuggestion(payload: any) {
    return this.post('/discovery/suggestions', payload);
  }
  async approveSuggestion(suggestionId: string, appliedLevelId?: string) {
    return this.post(`/discovery/suggestions/${suggestionId}/approve`, {
      applied_level_id: appliedLevelId,
    });
  }
  async rejectSuggestion(suggestionId: string, reason: string) {
    return this.post(`/discovery/suggestions/${suggestionId}/reject`, { reason });
  }
  async modifyAndApproveSuggestion(suggestionId: string, newLevelId: string, reason?: string) {
    return this.post(`/discovery/suggestions/${suggestionId}/modify-approve`, {
      new_level_id: newLevelId,
      reason,
    });
  }
  async delegateSuggestion(suggestionId: string, targetUserId: string) {
    return this.post(`/discovery/suggestions/${suggestionId}/delegate`, {
      target_user_id: targetUserId,
    });
  }
  async escalateSuggestion(suggestionId: string) {
    return this.post(`/discovery/suggestions/${suggestionId}/escalate`, {});
  }
  async bulkApproveSuggestions(ids: string[]) {
    return this.post('/discovery/suggestions/bulk-approve', { ids });
  }
  async bulkRejectSuggestions(ids: string[], reason: string) {
    return this.post('/discovery/suggestions/bulk-reject', { ids, reason });
  }
  async getDiscoverySuggestionAnalytics() {
    return this.get('/discovery/suggestions/analytics');
  }
  // Import Templates
  async listImportTemplates(params?: Record<string, any>) {
    return this.get(`/discovery/import-templates${this.buildQuery(params)}`);
  }
  async getImportTemplate(templateId: string) {
    return this.get(`/discovery/import-templates/${templateId}`);
  }
  async createImportTemplate(payload: any) {
    return this.post('/discovery/import-templates', payload);
  }
  async updateImportTemplate(templateId: string, payload: any) {
    return this.put(`/discovery/import-templates/${templateId}`, payload);
  }
  async deleteImportTemplate(templateId: string) {
    return this.del(`/discovery/import-templates/${templateId}`);
  }
  // Discovery Audit
  async listDiscoveryAudit(params?: Record<string, any>) {
    return this.get(`/discovery/audit${this.buildQuery(params)}`);
  }
  async getDiscoveryEntityAudit(entityType: string, entityId: string) {
    return this.get(`/discovery/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 2.4.0: Incident Operations Level 2 ──────────────
  // Playbook Templates
  async listPlaybookTemplates(params?: Record<string, any>) {
    return this.get(`/incident-ops/playbooks${this.buildQuery(params)}`);
  }
  async getPlaybookTemplate(playbookId: string) {
    return this.get(`/incident-ops/playbooks/${playbookId}`);
  }
  async createPlaybookTemplate(payload: Record<string, any>) {
    return this.post('/incident-ops/playbooks', payload);
  }
  async updatePlaybookTemplate(playbookId: string, payload: Record<string, any>) {
    return this.patch(`/incident-ops/playbooks/${playbookId}`, payload);
  }
  async updatePlaybookTemplateStatus(playbookId: string, status: string) {
    return this.post(`/incident-ops/playbooks/${playbookId}/status`, { status });
  }
  async clonePlaybookTemplate(playbookId: string, name: string) {
    return this.post(`/incident-ops/playbooks/${playbookId}/clone`, { name });
  }
  async seedPlaybookDefaults() {
    return this.post('/incident-ops/playbooks/seed-defaults', {});
  }
  // Playbook Activations
  async listPlaybookActivations(params?: Record<string, any>) {
    return this.get(`/incident-ops/activations${this.buildQuery(params)}`);
  }
  async getPlaybookActivation(activationId: string) {
    return this.get(`/incident-ops/activations/${activationId}`);
  }
  async activatePlaybook(payload: Record<string, any>) {
    return this.post('/incident-ops/activations', payload);
  }
  async deactivatePlaybook(activationId: string, reason: string) {
    return this.post(`/incident-ops/activations/${activationId}/deactivate`, { reason });
  }
  async completePlaybookActivation(activationId: string, metrics?: Record<string, any>) {
    return this.post(`/incident-ops/activations/${activationId}/complete`, { metrics });
  }
  async updatePlaybookPhaseStatus(activationId: string, phaseId: string, status: string) {
    return this.patch(`/incident-ops/activations/${activationId}/phases/${phaseId}`, { status });
  }
  // SLA Definitions
  async listIncidentSlaDefinitions(params?: Record<string, any>) {
    return this.get(`/incident-ops/sla-definitions${this.buildQuery(params)}`);
  }
  async getIncidentSlaDefinition(slaId: string) {
    return this.get(`/incident-ops/sla-definitions/${slaId}`);
  }
  async createIncidentSlaDefinition(payload: Record<string, any>) {
    return this.post('/incident-ops/sla-definitions', payload);
  }
  async updateIncidentSlaDefinition(slaId: string, payload: Record<string, any>) {
    return this.patch(`/incident-ops/sla-definitions/${slaId}`, payload);
  }
  async seedIncidentSlaDefaults() {
    return this.post('/incident-ops/sla-definitions/seed-defaults', {});
  }
  // SLA Records
  async listIncidentSlaRecords(params?: Record<string, any>) {
    return this.get(`/incident-ops/sla-records${this.buildQuery(params)}`);
  }
  async getIncidentSlaDashboard() {
    return this.get('/incident-ops/sla-records/dashboard');
  }
  async getIncidentSlaRecord(recordId: string) {
    return this.get(`/incident-ops/sla-records/${recordId}`);
  }
  async createIncidentSlaRecord(payload: Record<string, any>) {
    return this.post('/incident-ops/sla-records', payload);
  }
  async updateIncidentSlaRecordStatus(recordId: string, status: string) {
    return this.patch(`/incident-ops/sla-records/${recordId}/status`, { status });
  }
  async pauseIncidentSlaClock(recordId: string) {
    return this.post(`/incident-ops/sla-records/${recordId}/pause`, {});
  }
  async resumeIncidentSlaClock(recordId: string) {
    return this.post(`/incident-ops/sla-records/${recordId}/resume`, {});
  }
  async stopIncidentSlaClock(recordId: string) {
    return this.post(`/incident-ops/sla-records/${recordId}/stop`, {});
  }
  // Escalations
  async listIncidentEscalations(params?: Record<string, any>) {
    return this.get(`/incident-ops/escalations${this.buildQuery(params)}`);
  }
  async getIncidentEscalation(escalationId: string) {
    return this.get(`/incident-ops/escalations/${escalationId}`);
  }
  async createIncidentEscalation(payload: Record<string, any>) {
    return this.post('/incident-ops/escalations', payload);
  }
  async resolveIncidentEscalation(escalationId: string) {
    return this.post(`/incident-ops/escalations/${escalationId}/resolve`, {});
  }
  // Regulator Exports
  async listRegulatorExports(params?: Record<string, any>) {
    return this.get(`/incident-ops/regulator-exports${this.buildQuery(params)}`);
  }
  async getRegulatorExport(exportId: string) {
    return this.get(`/incident-ops/regulator-exports/${exportId}`);
  }
  async createRegulatorExport(payload: Record<string, any>) {
    return this.post('/incident-ops/regulator-exports', payload);
  }
  async updateRegulatorExportSubmissionStatus(exportId: string, status: string) {
    return this.patch(`/incident-ops/regulator-exports/${exportId}/submission-status`, { status });
  }
  // Vendor Coordination
  async listVendorCoordination(params?: Record<string, any>) {
    return this.get(`/incident-ops/vendor-coordination${this.buildQuery(params)}`);
  }
  async getVendorCoordination(coordinationId: string) {
    return this.get(`/incident-ops/vendor-coordination/${coordinationId}`);
  }
  async createVendorCoordination(payload: Record<string, any>) {
    return this.post('/incident-ops/vendor-coordination', payload);
  }
  async updateVendorCoordinationStatus(coordinationId: string, status: string) {
    return this.patch(`/incident-ops/vendor-coordination/${coordinationId}/status`, { status });
  }
  async addVendorCoordinationCommunication(coordinationId: string, data: Record<string, any>) {
    return this.post(`/incident-ops/vendor-coordination/${coordinationId}/communications`, data);
  }
  async addVendorCoordinationInfoRequest(coordinationId: string, data: Record<string, any>) {
    return this.post(`/incident-ops/vendor-coordination/${coordinationId}/info-requests`, data);
  }
  async updateVendorInfoRequestStatus(coordinationId: string, requestId: string, status: string) {
    return this.patch(
      `/incident-ops/vendor-coordination/${coordinationId}/info-requests/${requestId}/status`,
      { status },
    );
  }
  async addVendorCoordinationActionRequest(coordinationId: string, data: Record<string, any>) {
    return this.post(`/incident-ops/vendor-coordination/${coordinationId}/action-requests`, data);
  }
  async updateVendorActionRequestStatus(coordinationId: string, requestId: string, status: string) {
    return this.patch(
      `/incident-ops/vendor-coordination/${coordinationId}/action-requests/${requestId}/status`,
      { status },
    );
  }
  async getCrossVendorTimeline(incidentId: string) {
    return this.get(`/incident-ops/vendor-coordination/cross-timeline/${incidentId}`);
  }
  /**
   * Sprint 44.0.5 Wave 2 — R-33 backend exports for the six Incident
   * Operations list/dashboard pages that previously bypassed
   * UnifiedExportService via `downloadClientExport`. Each returns an
   * AttachmentRef; caller invokes `downloadPlatformFile(ref.file_id,
   * ref.filename)` to trigger the browser download. Two of the
   * endpoints (dashboard-export and metrics-export) share the
   * `/sla-records` backend service method but emit distinct
   * AttachmentOwnerType values so the File Component audit trail
   * distinguishes which page emitted each artefact.
   */
  async exportIncidentOpsPlaybookTemplates(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/incident-ops/playbooks/export${this.buildQuery(params)}`);
  }
  async exportIncidentOpsRegulatorExports(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/incident-ops/regulator-exports/list-export${this.buildQuery(params)}`);
  }
  async exportIncidentOpsResponseMetrics(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/incident-ops/sla-records/metrics-export${this.buildQuery(params)}`);
  }
  async exportIncidentOpsSlaDashboard(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/incident-ops/sla-records/dashboard-export${this.buildQuery(params)}`);
  }
  async exportIncidentOpsSlaDefinitions(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/incident-ops/sla-definitions/export${this.buildQuery(params)}`);
  }
  async exportIncidentOpsVendorCoordination(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/incident-ops/vendor-coordination/export${this.buildQuery(params)}`);
  }
  // Incident Ops Audit
  async listIncidentOpsAudit(params?: Record<string, any>) {
    return this.get(`/incident-ops/audit${this.buildQuery(params)}`);
  }
  async getIncidentOpsEntityAudit(entityType: string, entityId: string) {
    return this.get(`/incident-ops/audit/${entityType}/${entityId}`);
  }
  // ── Incident Ops Maturity (Sprint 21.2.0) ─────────────────
  async listPlaybookNotificationLinks(params?: Record<string, any>) {
    return this.get(`/incident-ops/playbook-links${this.buildQuery(params)}`);
  }
  async createPlaybookNotificationLink(activationId: string, notificationId: string) {
    return this.post('/incident-ops/playbook-links', {
      activation_id: activationId,
      notification_id: notificationId,
    });
  }
  async seedPdplPlaybooks() {
    return this.post('/incident-ops/playbook-links/seed-pdpl', {});
  }
  async listRegulatorySlaMapping(params?: Record<string, any>) {
    return this.get(`/incident-ops/regulatory-sla${this.buildQuery(params)}`);
  }
  async createRegulatorySlaMapping(data: Record<string, any>) {
    return this.post('/incident-ops/regulatory-sla', data);
  }
  async seedPdplSlaDefaults() {
    return this.post('/incident-ops/regulatory-sla/seed-pdpl', {});
  }
  async listCoordinationTeams(incidentId: string) {
    return this.get(`/incident-ops/coordination-teams/${encodeURIComponent(incidentId)}`);
  }
  async createCoordinationTeam(incidentId: string, data: Record<string, any>) {
    return this.post(`/incident-ops/coordination-teams/${encodeURIComponent(incidentId)}`, data);
  }
  async updateCoordinationTeam(incidentId: string, teamId: string, data: Record<string, any>) {
    return this.patch(
      `/incident-ops/coordination-teams/${encodeURIComponent(incidentId)}/${encodeURIComponent(teamId)}`,
      data,
    );
  }
  async listEvidenceChain(incidentId: string) {
    return this.get(`/incident-ops/evidence-chain/${encodeURIComponent(incidentId)}`);
  }
  async createEvidenceChainEntry(data: Record<string, any>) {
    return this.post('/incident-ops/evidence-chain', data);
  }
  async getPostIncidentReview(incidentId: string) {
    return this.get(`/incident-ops/post-reviews/${encodeURIComponent(incidentId)}`);
  }
  async createPostIncidentReview(incidentId: string, data: Record<string, any>) {
    return this.post(`/incident-ops/post-reviews/${encodeURIComponent(incidentId)}`, data);
  }
  async updatePostIncidentReview(incidentId: string, reviewId: string, data: Record<string, any>) {
    return this.patch(
      `/incident-ops/post-reviews/${encodeURIComponent(incidentId)}/${encodeURIComponent(reviewId)}`,
      data,
    );
  }
  async signOffPostIncidentReview(incidentId: string, reviewId: string) {
    return this.post(
      `/incident-ops/post-reviews/${encodeURIComponent(incidentId)}/${encodeURIComponent(reviewId)}/sign-off`,
      {},
    );
  }
  // ── Program Governance (Sprint 2.5.0) ─────────────────────
  // Maturity Models
  async listMaturityModels(params?: Record<string, any>): Promise<{ data: MaturityModel[] }> {
    return this.get(`/program-governance/models${this.buildQuery(params)}`);
  }
  async createMaturityModel(data: Record<string, any>): Promise<MaturityModel> {
    return this.post('/program-governance/models', data);
  }
  async getMaturityModel(modelId: string): Promise<MaturityModel> {
    return this.get(`/program-governance/models/${modelId}`);
  }
  async updateMaturityModel(modelId: string, data: Record<string, any>): Promise<MaturityModel> {
    return this.put(`/program-governance/models/${modelId}`, data);
  }
  async updateMaturityModelStatus(
    modelId: string,
    data: Record<string, any>,
  ): Promise<MaturityModel> {
    return this.patch(`/program-governance/models/${modelId}/status`, data);
  }
  async seedMaturityModelDefaults(): Promise<Record<string, unknown>> {
    return this.post('/program-governance/models/seed-defaults', {});
  }
  // Maturity Assessments
  async listMaturityAssessments(
    params?: Record<string, any>,
  ): Promise<{ data: MaturityAssessment[] }> {
    return this.get(`/program-governance/assessments${this.buildQuery(params)}`);
  }
  async createMaturityAssessment(data: Record<string, any>): Promise<MaturityAssessment> {
    return this.post('/program-governance/assessments', data);
  }
  async getMaturityAssessment(assessmentId: string): Promise<MaturityAssessment> {
    return this.get(`/program-governance/assessments/${assessmentId}`);
  }
  async updateMaturityAssessment(
    assessmentId: string,
    data: Record<string, any>,
  ): Promise<MaturityAssessment> {
    return this.put(`/program-governance/assessments/${assessmentId}`, data);
  }
  async evaluateDomain(
    assessmentId: string,
    domainId: string,
    data: Record<string, any>,
  ): Promise<MaturityAssessment> {
    return this.put(`/program-governance/assessments/${assessmentId}/domains/${domainId}`, data);
  }
  async submitAssessmentForReview(assessmentId: string): Promise<MaturityAssessment> {
    return this.post(`/program-governance/assessments/${assessmentId}/submit`, {});
  }
  async approveAssessmentDomain(
    assessmentId: string,
    domainId: string,
    data: Record<string, any>,
  ): Promise<MaturityAssessment> {
    return this.post(
      `/program-governance/assessments/${assessmentId}/domains/${domainId}/approve`,
      data,
    );
  }
  async finalizeAssessment(assessmentId: string): Promise<MaturityAssessment> {
    return this.post(`/program-governance/assessments/${assessmentId}/finalize`, {});
  }
  async updateAssessmentStatus(assessmentId: string, status: string): Promise<MaturityAssessment> {
    return this.patch(`/program-governance/assessments/${assessmentId}/status`, { status });
  }
  /*
   * ── Sprint 65.6.0 (AC3) — DT-01 training ────────────────────────
   *
   * Nine methods, one per act, rather than a generic status PATCH. The three
   * closing acts are not one act with a parameter: a completion cites evidence,
   * an exemption cites a reason and is attributed, a cancellation is neither.
   * 65.5.0 found the opposite shape shipped — a modal calling a generic PATCH,
   * so a legality table added to the status endpoint changed nothing a user
   * does.
   */
  async listTrainingCourses(params?: {
    active?: 'true' | 'false';
    page?: number;
    page_size?: number;
  }): Promise<{ data: TrainingCourse[]; total: number; page: number; page_size: number }> {
    return this.get(`/training/courses${this.buildQuery(params)}`);
  }
  async createTrainingCourse(data: {
    title: string;
    description: string;
    /** An id from the organisation's own installed templates — never a literal (R-46). */
    evidence_form_schema_id: string | null;
    recurrence_months: number | null;
  }): Promise<{ data: TrainingCourse }> {
    return this.post('/training/courses', data);
  }
  async listTrainingAssignments(
    params?: Record<string, string>,
  ): Promise<{ data: TrainingAssignmentView[]; total: number; page: number; page_size: number }> {
    return this.get(`/training/assignments${this.buildQuery(params)}`);
  }
  async getTrainingSummary(): Promise<{ data: TrainingAssignmentSummary }> {
    return this.get('/training/assignments/summary');
  }
  async getTrainingAssignment(
    assignmentId: string,
  ): Promise<{ data: TrainingAssignmentView | null }> {
    return this.get(`/training/assignments/${assignmentId}`);
  }
  async assignTraining(data: {
    course_id: string;
    assignee_ids: string[];
    due_at: string | null;
  }): Promise<{ data: TrainingAssignment[] }> {
    return this.post('/training/assignments', data);
  }
  async startTrainingAssignment(assignmentId: string): Promise<{ data: TrainingAssignment }> {
    return this.post(`/training/assignments/${assignmentId}/start`, {});
  }
  async recordTrainingCompletion(
    assignmentId: string,
    data: { kind: TrainingEvidenceKind; reference_id: string },
  ): Promise<{ data: TrainingAssignment }> {
    return this.post(`/training/assignments/${assignmentId}/completion`, data);
  }
  async recordTrainingExemption(
    assignmentId: string,
    data: { reason: string },
  ): Promise<{ data: TrainingAssignment }> {
    return this.post(`/training/assignments/${assignmentId}/exemption`, data);
  }
  async cancelTrainingAssignment(
    assignmentId: string,
    data: { reason: string },
  ): Promise<{ data: TrainingAssignment }> {
    return this.post(`/training/assignments/${assignmentId}/cancel`, data);
  }

  /*
   * ── Sprint 65.6.0 (AC8) — DPO-01 and PUB-01 ─────────────────────
   *
   * R-26: every path, verb and body shape below was read off
   * `src/backend/src/modules/privacy-office/routes.ts` and its `schemas.ts`,
   * not guessed. The router is mounted at `/api/v1/privacy-office` twice — once
   * for the compliance-proof routes and once for these — so the prefix here is
   * `/privacy-office` and the segment (`appointments` / `notices`) is what
   * separates the two registers.
   *
   * Eleven methods, one per act, and deliberately not a generic status PATCH:
   * bringing an appointment into effect cites a moment, publishing cites
   * targets and a publication moment, ending cites a reason. The backend
   * declares them as separate POSTs for exactly that reason, and a client that
   * collapsed them would put the legality table back out of reach of the
   * surface — 65.5.0's contract-status defect.
   */
  async listDpoAppointments(
    params?: Record<string, string>,
  ): Promise<{ data: DpoAppointment[]; total: number; page: number; page_size: number }> {
    return this.get(`/privacy-office/appointments${this.buildQuery(params)}`);
  }
  async getDpoAppointment(appointmentId: string): Promise<{ data: DpoAppointment }> {
    return this.get(`/privacy-office/appointments/${appointmentId}`);
  }
  async recordDpoAppointment(data: {
    appointee: {
      full_name: string;
      position_title: string;
      contact_email: string;
      contact_phone: string | null;
    };
    appointing_decision: {
      decided_by: string;
      decided_on: string;
      decision_reference: string | null;
    };
    takes_effect_on: string | null;
    ends_on: string | null;
    evidence_form_instance_ids: string[];
    supersedes_appointment_id: string | null;
  }): Promise<{ data: DpoAppointment }> {
    return this.post('/privacy-office/appointments', data);
  }
  async takeDpoAppointmentEffect(
    appointmentId: string,
    data: { takes_effect_on: string | null; correction_note: string | null },
  ): Promise<{ data: DpoAppointment }> {
    return this.post(`/privacy-office/appointments/${appointmentId}/take-effect`, data);
  }
  async publishDpoAppointment(
    appointmentId: string,
    data: { targets: PublicationTarget[]; published_on: string },
  ): Promise<{ data: DpoAppointment }> {
    return this.post(`/privacy-office/appointments/${appointmentId}/publish`, data);
  }
  async endDpoAppointment(
    appointmentId: string,
    data: { to_status: DpoAppointmentStatus; reason: string; ends_on: string | null },
  ): Promise<{ data: DpoAppointment }> {
    return this.post(`/privacy-office/appointments/${appointmentId}/end`, data);
  }
  async listPrivacyNotices(
    params?: Record<string, string>,
  ): Promise<{ data: PrivacyNotice[]; total: number; page: number; page_size: number }> {
    return this.get(`/privacy-office/notices${this.buildQuery(params)}`);
  }
  async getPrivacyNotice(noticeId: string): Promise<{ data: PrivacyNotice }> {
    return this.get(`/privacy-office/notices/${noticeId}`);
  }
  /**
   * Sprint 62.58.0 (Slice N, AC-N1) — a revision states whom it is given to,
   * how (`channels`, at least one; `channel_note` with `other`), from which day,
   * and the processing activities and data-map systems it is given for. Linking
   * needs `dm:activity:read` (403 `PRIVACY_NOTICE_LINK_TARGET_GRANT_REQUIRED`);
   * an id of no activity or system of this organisation is 400
   * `PRIVACY_NOTICE_LINK_TARGET_NOT_FOUND`, and a named data subject outside the
   * linked activities' own is 400 `PRIVACY_NOTICE_AUDIENCE_INVALID` (both with
   * `field_errors`).
   */
  async draftPrivacyNotice(data: {
    title: string;
    revision: number;
    content_url: string;
    locales: string[];
    supersedes_notice_id: string | null;
    audience_kind: PrivacyNoticeAudienceKind;
    audience_data_subjects: string[];
    channels: PrivacyNoticeDeliveryChannel[];
    channel_note: string | null;
    effective_from: string | null;
    processing_activity_ids: string[];
    data_map_system_ids: string[];
  }): Promise<{ data: PrivacyNotice }> {
    return this.post('/privacy-office/notices', data);
  }
  async approvePrivacyNotice(
    noticeId: string,
    data: { approved_by: string | null; approval_reference: string | null },
  ): Promise<{ data: PrivacyNotice }> {
    return this.post(`/privacy-office/notices/${noticeId}/approve`, data);
  }
  async publishPrivacyNotice(
    noticeId: string,
    data: { targets: PublicationTarget[]; published_on: string },
  ): Promise<{ data: PrivacyNotice }> {
    return this.post(`/privacy-office/notices/${noticeId}/publish`, data);
  }
  async withdrawPrivacyNotice(
    noticeId: string,
    data: { reason: string },
  ): Promise<{ data: PrivacyNotice }> {
    return this.post(`/privacy-office/notices/${noticeId}/withdraw`, data);
  }
  async supersedePrivacyNotice(
    noticeId: string,
    data: { reason: string },
  ): Promise<{ data: PrivacyNotice }> {
    return this.post(`/privacy-office/notices/${noticeId}/supersede`, data);
  }

  // Source-locked official-handbook evidence assessments.
  async getHandbookAssessmentDefinition(): Promise<HandbookAssessmentDefinition> {
    return this.get('/program-governance/handbook-assessments/definition');
  }
  async listHandbookAssessments(params?: {
    cursor?: string;
    page_size?: number;
    status?: HandbookAssessment['status'];
  }): Promise<{ data: HandbookAssessment[]; next_cursor: string | null; has_more: boolean }> {
    return this.get(`/program-governance/handbook-assessments${this.buildQuery(params)}`);
  }
  async createHandbookAssessment(data: { title?: string } = {}): Promise<HandbookAssessment> {
    return this.post('/program-governance/handbook-assessments', data);
  }
  async getHandbookAssessment(assessmentId: string): Promise<HandbookAssessment> {
    return this.get(`/program-governance/handbook-assessments/${assessmentId}`);
  }
  async updateHandbookAssessment(
    assessmentId: string,
    data: { title?: string; status?: 'in_progress' | 'under_review' },
  ): Promise<HandbookAssessment> {
    return this.patch(`/program-governance/handbook-assessments/${assessmentId}`, data);
  }
  async reviewHandbookAssessment(
    assessmentId: string,
    data: {
      status?: 'in_progress' | 'reviewed' | 'archived';
      legal_review_status: HandbookAssessmentReviewStatus;
    },
  ): Promise<HandbookAssessment> {
    return this.post(`/program-governance/handbook-assessments/${assessmentId}/review`, data);
  }
  async updateHandbookQuestion(
    assessmentId: string,
    questionId: string,
    data: {
      answer_state?: HandbookAssessmentAnswerState;
      evidence_state?: HandbookAssessmentEvidenceState;
      notes?: string;
    },
  ): Promise<HandbookAssessment> {
    return this.patch(
      `/program-governance/handbook-assessments/${assessmentId}/questions/${questionId}`,
      data,
    );
  }
  async reviewHandbookQuestion(
    assessmentId: string,
    questionId: string,
    legalReviewStatus: HandbookAssessmentReviewStatus,
  ): Promise<HandbookAssessment> {
    return this.post(
      `/program-governance/handbook-assessments/${assessmentId}/questions/${questionId}/review`,
      { legal_review_status: legalReviewStatus },
    );
  }
  /**
   * Sprint 64.166.0 — which platform records could satisfy this question.
   * Read-only; the API returns the same set `resolve-evidence` picks from.
   */
  async listHandbookEvidenceCandidates(
    assessmentId: string,
    questionId: string,
  ): Promise<HandbookEvidenceCandidatesResponse> {
    return this.get(
      `/program-governance/handbook-assessments/${assessmentId}/questions/${questionId}/evidence-candidates`,
    );
  }
  async resolveHandbookQuestionEvidence(
    assessmentId: string,
    questionId: string,
  ): Promise<HandbookAssessment> {
    return this.post(
      `/program-governance/handbook-assessments/${assessmentId}/questions/${questionId}/resolve-evidence`,
      {},
    );
  }
  async linkHandbookEvidence(
    assessmentId: string,
    questionId: string,
    data: Record<string, unknown>,
  ): Promise<HandbookAssessment> {
    return this.post(
      `/program-governance/handbook-assessments/${assessmentId}/questions/${questionId}/evidence-links`,
      data,
    );
  }
  async createHandbookRemediation(
    assessmentId: string,
    questionId: string,
    data: {
      severity: 'critical' | 'high' | 'medium' | 'low';
      assigned_to?: string;
      target_date?: string;
      guidance?: string;
    },
  ): Promise<Record<string, unknown>> {
    return this.post(
      `/program-governance/handbook-assessments/${assessmentId}/questions/${questionId}/remediation`,
      data,
    );
  }
  async exportHandbookAssessment(
    assessmentId: string,
    data: { format: 'pdf' | 'docx'; language: 'en' | 'vi' | 'bilingual' },
  ): Promise<AttachmentRef> {
    return this.post(`/program-governance/handbook-assessments/${assessmentId}/export`, data);
  }
  // Trend Metrics
  async listTrendMetrics(params?: Record<string, any>): Promise<{ data: TrendMetric[] }> {
    return this.get(`/program-governance/trends${this.buildQuery(params)}`);
  }
  async createTrendMetric(data: Record<string, any>): Promise<TrendMetric> {
    return this.post('/program-governance/trends', data);
  }
  async createTrendMetricBatch(data: Record<string, any>): Promise<{ data: TrendMetric[] }> {
    return this.post('/program-governance/trends/batch', data);
  }
  async getTrendMetricSummary(): Promise<{ data: TrendMetric[]; total: number }> {
    return this.get('/program-governance/trends/summary');
  }
  async getTrendMetricByName(
    metricName: string,
    params?: Record<string, any>,
  ): Promise<{ data: TrendMetric[] }> {
    return this.get(
      `/program-governance/trends/${encodeURIComponent(metricName)}${this.buildQuery(params)}`,
    );
  }
  // Reporting Periods
  async listReportingPeriods(params?: Record<string, any>): Promise<{ data: ReportingPeriod[] }> {
    return this.get(`/program-governance/reporting-periods${this.buildQuery(params)}`);
  }
  async createReportingPeriod(data: Record<string, any>): Promise<ReportingPeriod> {
    return this.post('/program-governance/reporting-periods', data);
  }
  async getReportingPeriod(periodId: string): Promise<ReportingPeriod> {
    return this.get(`/program-governance/reporting-periods/${periodId}`);
  }
  async updateReportingPeriod(
    periodId: string,
    data: Record<string, any>,
  ): Promise<ReportingPeriod> {
    return this.put(`/program-governance/reporting-periods/${periodId}`, data);
  }
  async updateReportingPeriodStatus(
    periodId: string,
    data: Record<string, any>,
  ): Promise<ReportingPeriod> {
    return this.patch(`/program-governance/reporting-periods/${periodId}/status`, data);
  }
  async autoGenerateReportingPeriods(
    data: Record<string, any>,
  ): Promise<{ data: ReportingPeriod[] }> {
    return this.post('/program-governance/reporting-periods/auto-generate', data);
  }
  // Executive Summaries
  async listExecutiveSummaries(
    params?: Record<string, any>,
  ): Promise<{ data: ExecutiveSummary[] }> {
    return this.get(`/program-governance/summaries${this.buildQuery(params)}`);
  }
  async generateExecutiveSummary(data: Record<string, any>): Promise<ExecutiveSummary> {
    return this.post('/program-governance/summaries/generate', data);
  }
  async getExecutiveSummary(summaryId: string): Promise<ExecutiveSummary> {
    return this.get(`/program-governance/summaries/${summaryId}`);
  }
  async editExecutiveSummary(
    summaryId: string,
    data: Record<string, any>,
  ): Promise<ExecutiveSummary> {
    return this.put(`/program-governance/summaries/${summaryId}`, data);
  }
  // Audit Readiness
  async listAuditReadiness(params?: Record<string, any>): Promise<{ data: AuditReadiness[] }> {
    return this.get(`/program-governance/readiness${this.buildQuery(params)}`);
  }
  async getLatestAuditReadiness(): Promise<AuditReadiness> {
    return this.get('/program-governance/readiness/latest');
  }
  async calculateAuditReadiness(data: Record<string, any>): Promise<AuditReadiness> {
    return this.post('/program-governance/readiness/calculate', data);
  }
  // Evidence Gaps
  async listEvidenceGaps(params?: Record<string, any>): Promise<{ data: EvidenceGap[] }> {
    return this.get(`/program-governance/evidence-gaps${this.buildQuery(params)}`);
  }
  async createEvidenceGap(data: Record<string, any>): Promise<EvidenceGap> {
    return this.post('/program-governance/evidence-gaps', data);
  }
  async getEvidenceGapStats(): Promise<Record<string, unknown>> {
    return this.get('/program-governance/evidence-gaps/stats');
  }
  async getEvidenceGap(gapId: string): Promise<EvidenceGap> {
    return this.get(`/program-governance/evidence-gaps/${gapId}`);
  }
  async updateEvidenceGap(gapId: string, data: Record<string, any>): Promise<EvidenceGap> {
    return this.put(`/program-governance/evidence-gaps/${gapId}`, data);
  }
  async assignEvidenceGap(gapId: string, data: Record<string, any>): Promise<EvidenceGap> {
    return this.post(`/program-governance/evidence-gaps/${gapId}/assign`, data);
  }
  async updateEvidenceGapStatus(gapId: string, data: Record<string, any>): Promise<EvidenceGap> {
    return this.patch(`/program-governance/evidence-gaps/${gapId}/status`, data);
  }
  /*
   * ── Sprint 65.8.0 — Audit engagements and the findings inside them ──
   *
   * Paths and permissions READ from `program-governance/routes.ts` (R-26), not
   * guessed: all ten are mounted under `/program-governance`, the reads are
   * gated on `AUDIT_READINESS_READ` and the writes on `AUDIT_READINESS_WRITE`
   * — the module's existing audit pair, which the remediation register beside
   * them already rides on. The transition endpoints are `POST .../transition`
   * with the target state in the body, NOT a PATCH on a `state` field: the
   * lifecycle gates live in the service, and a client that could set `state`
   * directly would be a client that could skip them.
   *
   * Both list routes answer `PaginatedResponse<T>` (`{ data, total, page,
   * page_size }`) — the shared envelope `extractPaginationMeta` reads.
   */
  async listAuditEngagements(
    params?: Record<string, unknown>,
  ): Promise<PaginatedResponse<AuditEngagement>> {
    return this.get(`/program-governance/audit-engagements${this.buildQuery(params)}`);
  }
  async getAuditEngagement(engagementId: string): Promise<AuditEngagement> {
    return this.get(`/program-governance/audit-engagements/${encodeURIComponent(engagementId)}`);
  }
  async createAuditEngagement(data: Record<string, unknown>): Promise<AuditEngagement> {
    return this.post('/program-governance/audit-engagements', data);
  }
  async updateAuditEngagement(
    engagementId: string,
    data: Record<string, unknown>,
  ): Promise<AuditEngagement> {
    return this.patch(
      `/program-governance/audit-engagements/${encodeURIComponent(engagementId)}`,
      data,
    );
  }
  /**
   * `reason` is MANDATORY server-side for `cancelled` and accepted for every
   * other target. It is omitted from the body rather than sent as an empty
   * string when the caller has none: the schema is `.strict()` and the service
   * trims, so `''` and absent are the same refusal — but an absent key says
   * "no reason was given" where `''` says "a reason was given and it was
   * empty".
   */
  async transitionAuditEngagement(
    engagementId: string,
    state: AuditEngagementState,
    reason?: string,
  ): Promise<AuditEngagement> {
    return this.post(
      `/program-governance/audit-engagements/${encodeURIComponent(engagementId)}/transition`,
      reason ? { state, reason } : { state },
    );
  }
  async listAuditFindings(
    params?: Record<string, unknown>,
  ): Promise<PaginatedResponse<AuditFinding>> {
    return this.get(`/program-governance/audit-findings${this.buildQuery(params)}`);
  }
  async getAuditFinding(findingId: string): Promise<AuditFinding> {
    return this.get(`/program-governance/audit-findings/${encodeURIComponent(findingId)}`);
  }
  async createAuditFinding(data: Record<string, unknown>): Promise<AuditFinding> {
    return this.post('/program-governance/audit-findings', data);
  }
  async updateAuditFinding(
    findingId: string,
    data: Record<string, unknown>,
  ): Promise<AuditFinding> {
    return this.patch(`/program-governance/audit-findings/${encodeURIComponent(findingId)}`, data);
  }
  /** `reason` is MANDATORY server-side for `rejected`. See the note above. */
  async transitionAuditFinding(
    findingId: string,
    state: AuditFindingState,
    reason?: string,
  ): Promise<AuditFinding> {
    return this.post(
      `/program-governance/audit-findings/${encodeURIComponent(findingId)}/transition`,
      reason ? { state, reason } : { state },
    );
  }
  // Program Governance Audit
  async listProgramGovernanceAudit(
    params?: Record<string, any>,
  ): Promise<{ data: ProgramGovernanceAuditEvent[] }> {
    return this.get(`/program-governance/audit${this.buildQuery(params)}`);
  }
  async getProgramGovernanceEntityAudit(
    entityType: string,
    entityId: string,
  ): Promise<{ data: ProgramGovernanceAuditEvent[] }> {
    return this.get(`/program-governance/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 21.7.0: Program Governance -- Obligation Mappings & RACI ──
  async listObligationMappings(
    params?: Record<string, any>,
  ): Promise<{ data: ObligationMapping[] }> {
    return this.get(`/program-governance/obligation-mappings${this.buildQuery(params)}`);
  }
  async createObligationMapping(data: Record<string, any>): Promise<ObligationMapping> {
    return this.post('/program-governance/obligation-mappings', data);
  }
  async updateObligationMapping(
    mappingId: string,
    data: Record<string, any>,
  ): Promise<ObligationMapping> {
    return this.patch(`/program-governance/obligation-mappings/${mappingId}`, data);
  }
  async deleteObligationMapping(mappingId: string): Promise<void> {
    return this.del(`/program-governance/obligation-mappings/${mappingId}`);
  }
  async listRaciAssignments(params?: Record<string, any>): Promise<{ data: RaciAssignment[] }> {
    return this.get(`/program-governance/raci-assignments${this.buildQuery(params)}`);
  }
  async createRaciAssignment(data: Record<string, any>): Promise<RaciAssignment> {
    return this.post('/program-governance/raci-assignments', data);
  }
  async updateRaciAssignment(
    assignmentId: string,
    data: Record<string, any>,
  ): Promise<RaciAssignment> {
    return this.patch(`/program-governance/raci-assignments/${assignmentId}`, data);
  }
  async deleteRaciAssignment(assignmentId: string): Promise<void> {
    return this.del(`/program-governance/raci-assignments/${assignmentId}`);
  }
  async getProgramStaleness(programId: string): Promise<any> {
    return this.get(`/monitoring/programs/${programId}/staleness`);
  }
  // ── Sprint 21.7.0: Program Governance -- Governance Documents ──
  async listGovernanceDocuments(
    params?: Record<string, any>,
  ): Promise<{ data: GovernanceDocument[] }> {
    return this.get(`/program-governance/governance-documents${this.buildQuery(params)}`);
  }
  async createGovernanceDocument(data: Record<string, any>): Promise<GovernanceDocument> {
    return this.post('/program-governance/governance-documents', data);
  }
  async updateGovernanceDocumentStatus(docId: string, status: string): Promise<GovernanceDocument> {
    return this.patch(`/program-governance/governance-documents/${docId}/status`, { status });
  }
  async acknowledgeGovernanceDocument(
    docId: string,
    department?: string,
  ): Promise<DocumentAcknowledgment> {
    return this.post(`/program-governance/governance-documents/${docId}/acknowledge`, {
      department: department || '',
    });
  }
  // ── Sprint 27.5.1: Governance Model & Document Issuance (UC-VN-17) ──
  async listGovernanceModels(
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<GovernanceModel>> {
    return this.get(`/program-governance/governance-models${this.buildQuery(params)}`);
  }
  async createGovernanceModel(data: Record<string, any>): Promise<GovernanceModel> {
    return this.post('/program-governance/governance-models', data);
  }
  async updateGovernanceModel(
    modelId: string,
    data: Record<string, any>,
  ): Promise<GovernanceModel> {
    return this.patch(`/program-governance/governance-models/${modelId}`, data);
  }
  async deleteGovernanceModel(modelId: string): Promise<void> {
    return this.del(`/program-governance/governance-models/${modelId}`);
  }
  /**
   * Sprint 43.7.0 Wave 5 — per-model export. When `format` is a binary
   * format (csv / xlsx / pdf / docx) returns an AttachmentRef persisted
   * through UnifiedExportService (call `downloadPlatformFile()` to pull
   * the bytes). When `format === 'json'` the backend returns the JSON
   * payload inline (also as an AttachmentRef when UnifiedExport is
   * wired). Consumers should pass the ref through the File Component
   * download helper.
   */
  async exportGovernanceModel(
    modelId: string,
    format: 'csv' | 'xlsx' | 'json' | 'pdf' | 'docx',
  ): Promise<any> {
    return this.get(
      `/program-governance/governance-models/${modelId}/export${this.buildQuery({ format })}`,
    );
  }
  /**
   * Sprint 43.7.2 Wave 3 — R-33 list-export migration.
   *
   * Each method routes the page's "export list" action through the
   * backend UnifiedExportService (csv / xlsx / pdf / docx) and returns
   * an AttachmentRef. The caller is expected to invoke
   * `downloadPlatformFile(ref.file_id, ref.filename)` to trigger the
   * browser download. These replace in-browser `downloadClientExport()`
   * usages on the 7 Program Governance list/dashboard pages.
   */
  async exportGovernanceDocumentsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/governance-documents/export${this.buildQuery(params)}`);
  }
  async exportDocumentIssuanceList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/document-issuance/export${this.buildQuery(params)}`);
  }
  async exportDocumentChangeLog(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/document-change-log/export${this.buildQuery(params)}`);
  }
  async exportMaturityModelsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/models/export${this.buildQuery(params)}`);
  }
  async exportRemediationPlansList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/remediation-plans/export${this.buildQuery(params)}`);
  }
  async exportReportingPeriodsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/reporting-periods/export${this.buildQuery(params)}`);
  }
  async exportTrendMetricsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/trends/export${this.buildQuery(params)}`);
  }
  async exportAuditReadiness(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/readiness/export${this.buildQuery(params)}`);
  }
  /**
   * Sprint 43.7.2 Wave 3 residual — R-33 closeout exports for the 4
   * program-governance pages missed by the initial audit. Each returns
   * an AttachmentRef; the caller invokes
   * `downloadPlatformFile(ref.file_id, ref.filename)` to download.
   */
  async exportAcknowledgmentTracking(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/acknowledgments/export${this.buildQuery(params)}`);
  }
  async exportDocumentDraftingList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(
      `/program-governance/governance-documents/drafting/export${this.buildQuery(params)}`,
    );
  }
  async exportProgramHealth(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/program-health/export${this.buildQuery(params)}`);
  }
  async exportRaciMatrix(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/raci/export${this.buildQuery(params)}`);
  }
  async assignDocumentDrafter(docId: string, data: Record<string, any>): Promise<any> {
    return this.post(`/program-governance/governance-documents/${docId}/assign-drafter`, data);
  }
  async submitDocumentReview(docId: string, data: Record<string, any>): Promise<any> {
    return this.post(`/program-governance/governance-documents/${docId}/review`, data);
  }
  async approveGovernanceDocument(docId: string, data: Record<string, any>): Promise<any> {
    return this.post(`/program-governance/governance-documents/${docId}/approve`, data);
  }
  async issueGovernanceDocument(docId: string, data: Record<string, any>): Promise<any> {
    return this.post(`/program-governance/governance-documents/${docId}/issue`, data);
  }
  async supersedeGovernanceDocument(docId: string, data: Record<string, any>): Promise<any> {
    return this.post(`/program-governance/governance-documents/${docId}/supersede`, data);
  }
  async listDocumentAcknowledgments(params?: Record<string, any>): Promise<{ data: any[] }> {
    return this.get(`/program-governance/acknowledgments${this.buildQuery(params)}`);
  }
  /**
   * Fix sprint #447 W3 (owner ruling OR1) — notifies every recipient who has
   * not acknowledged; answers how many were pending and how many were notified.
   */
  async sendAcknowledgmentReminder(docId: string): Promise<{
    document_id: string;
    pending_count: number;
    recipient_count: number;
    notified_user_ids: string[];
    skipped_user_ids: string[];
    reminded_at: string;
  }> {
    return this.post(`/program-governance/governance-documents/${docId}/send-reminder`, {});
  }
  /** Fix sprint #447 W3 — the people who may be recorded as a document's recipients. */
  async listAcknowledgmentRecipientCandidates(): Promise<{
    data: Array<{ _id: string; name: string; email: string }>;
    total: number;
  }> {
    return this.get('/program-governance/acknowledgments/recipient-candidates');
  }
  /** Fix sprint #447 W3 — record the people asked to acknowledge a document. */
  async setGovernanceDocumentRecipients(docId: string, recipientIds: string[]): Promise<any> {
    return this.put(`/program-governance/governance-documents/${docId}/recipients`, {
      recipient_ids: recipientIds,
    });
  }
  async listDocumentChangeLog(params?: Record<string, any>): Promise<{ data: any[] }> {
    return this.get(`/program-governance/document-change-log${this.buildQuery(params)}`);
  }
  async listReviewCadences(params?: Record<string, any>): Promise<{ data: any[] }> {
    return this.get(`/program-governance/review-cadences${this.buildQuery(params)}`);
  }
  async createReviewCadence(data: Record<string, any>): Promise<any> {
    return this.post('/program-governance/review-cadences', data);
  }
  async updateReviewCadence(cadenceId: string, data: Record<string, any>): Promise<any> {
    return this.patch(`/program-governance/review-cadences/${cadenceId}`, data);
  }
  // ── Sprint 21.7.0: Program Governance -- Remediation Plans ──
  /*
   * Sprint 65.8.0 (AC3) — reads are `StoredRemediationPlan`, writes are
   * `RemediationPlan`.
   *
   * The two differ in exactly one field and the difference is the point: a row
   * this client CREATES always carries a `source`, and a row it READS BACK from
   * a tenant that existed before 65.8.0 may not, because the migration leaves an
   * unclassifiable anchor provisional rather than inventing one. A page that
   * reads `plan.source.id` off the list type without that distinction would be
   * asserting a field the data may lack.
   */
  async listRemediationPlans(
    params?: Record<string, any>,
  ): Promise<{ data: StoredRemediationPlan[] }> {
    return this.get(`/program-governance/remediation-plans${this.buildQuery(params)}`);
  }
  async createRemediationPlan(data: Record<string, any>): Promise<RemediationPlan> {
    return this.post('/program-governance/remediation-plans', data);
  }
  async updateRemediationPlanStatus(
    planId: string,
    status: string,
  ): Promise<StoredRemediationPlan> {
    return this.patch(`/program-governance/remediation-plans/${planId}/status`, { status });
  }
  // ── Sprint 21.7.0: Program Governance -- Cross-Framework Assessment ──
  async getCrossFrameworkAssessment(): Promise<Record<string, unknown>> {
    return this.get('/program-governance/cross-framework-assessment');
  }
  async getConsolidatedGapRegister(): Promise<Record<string, unknown>> {
    return this.get('/program-governance/consolidated-gap-register');
  }
  async createAssessmentCriteria(data: Record<string, any>): Promise<AssessmentCriteria> {
    return this.post('/program-governance/assessment-criteria', data);
  }
  // ── Sprint 42.2.3 E5b: Generic client-side export pass-through ──
  // Used by `utils/csv-export.ts → downloadClientExport()`. Accepts
  // either pre-built CSV string OR row data + format choice (CSV/XLSX).
  // Persists in File Component → FilesBrowserPage.
  async exportClientCSV(payload: {
    csv?: string;
    data?: Record<string, unknown>[];
    format?: 'csv' | 'xlsx';
    filename_base: string;
    source_module: string;
    source_page?: string;
  }): Promise<AttachmentRef> {
    return this.post('/attachments/client-csv-export', payload);
  }

  // ── Sprint 42.2.3 E3: Access Accountability -- generic CSV pass-through ──
  // Pages that previously built CSV in-browser now POST it here for
  // unified File Component persistence.
  async exportAccessAccountabilityCSV(payload: {
    export_type: string;
    csv: string;
    filename_base: string;
  }): Promise<AttachmentRef> {
    return this.post('/access-accountability/exports/csv', payload);
  }

  // ── Sprint 44.0.6 Wave 3: Access Accountability — multi-format list exports ──
  //
  // R-33 migration replaces the 1-format CSV pass-through above with
  // 4-format (csv/xlsx/pdf/docx) backend routes that call
  // UnifiedExportService.generateFromDefinition. Each method returns an
  // `AttachmentRef` that can be handed to `downloadPlatformFile`.
  async exportAccessSessionEventsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/sessions/export${this.buildQuery(params)}`);
  }
  async exportAccessAnchoringProofsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/proofs/export${this.buildQuery(params)}`);
  }
  async exportAccessJustificationsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/justifications/export${this.buildQuery(params)}`);
  }
  async exportAccessInvestigationsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/investigations/export-list${this.buildQuery(params)}`);
  }
  async exportAccessInvestigationCase(
    investigationId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/access-accountability/investigations/${encodeURIComponent(investigationId)}/export-file${this.buildQuery(params)}`,
    );
  }
  async exportAccessLogList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/access-log/export-list${this.buildQuery(params)}`);
  }
  async exportAccessPoliciesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/policies/export${this.buildQuery(params)}`);
  }
  async exportAccessReviewCampaignsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/review-campaigns/export${this.buildQuery(params)}`);
  }
  async exportAccessEvidencePackagesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/access-accountability/evidence-packages/export-list${this.buildQuery(params)}`,
    );
  }
  async exportAccessEvidencePackageDetail(
    packageId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/access-accountability/evidence-packages/${encodeURIComponent(packageId)}/export-file${this.buildQuery(params)}`,
    );
  }

  // ── Sprint 63.1.0: access-log retention (idea-catalog #148) ──
  //
  // Two calls, and the split between them is the design: the REGISTER is a read
  // gated on `access_accountability:log:read` and destroys nothing; the SWEEP
  // has its own permission because it is the module's one destructive verb.
  // Every sweep is answerable before it is acted on.

  /** GET /access-accountability/access-log/retention-position — a read. */
  async getAccessLogRetentionPosition(params?: {
    as_of?: string;
  }): Promise<AccessLogRetentionPosition> {
    return this.get(
      `/access-accountability/access-log/retention-position${this.buildQuery(params)}`,
    );
  }

  /** POST /access-accountability/access-log/retention-sweep — honours the window. */
  async runAccessLogRetentionSweep(): Promise<AccessLogRetentionSweepReport> {
    return this.post('/access-accountability/access-log/retention-sweep', {});
  }

  // ── Sprint 2.6.0: Access Accountability -- Session Events ──
  async listSessionEvents(params?: Record<string, any>) {
    return this.get(`/access-accountability/sessions${this.buildQuery(params)}`);
  }
  async getSessionEvent(id: string) {
    return this.get(`/access-accountability/sessions/${id}`);
  }
  async getActiveSessions(params?: Record<string, any>) {
    return this.get(`/access-accountability/sessions/active${this.buildQuery(params)}`);
  }
  async getSessionAnalytics(params?: Record<string, any>) {
    return this.get(`/access-accountability/sessions/analytics${this.buildQuery(params)}`);
  }
  async createSessionEvent(payload: Record<string, any>) {
    return this.post('/access-accountability/sessions', payload);
  }
  async revokeSession(sessionId: string, reason: string) {
    return this.post(`/access-accountability/sessions/${sessionId}/revoke`, { reason });
  }
  // ── Sprint 2.6.0: Access Accountability -- Access Log ─────
  async listAccessLog(params?: Record<string, any>) {
    return this.get(`/access-accountability/access-log${this.buildQuery(params)}`);
  }
  async getAccessLogEntry(id: string) {
    return this.get(`/access-accountability/access-log/${id}`);
  }
  async getAccessLogByResource(
    resourceType: string,
    resourceId: string,
    params?: Record<string, any>,
  ) {
    return this.get(
      `/access-accountability/access-log/resource/${resourceType}/${resourceId}${this.buildQuery(params)}`,
    );
  }
  async getUserAccessSummary(userId: string, params?: Record<string, any>) {
    return this.get(
      `/access-accountability/access-log/user/${userId}/summary${this.buildQuery(params)}`,
    );
  }
  async exportAccessLog(params?: Record<string, any>) {
    return this.get(`/access-accountability/access-log/export${this.buildQuery(params)}`);
  }
  async createAccessLogEntry(payload: Record<string, any>) {
    return this.post('/access-accountability/access-log', payload);
  }
  // ── Sprint 2.6.0: Access Accountability -- Justifications ──
  async listJustifications(params?: Record<string, any>) {
    return this.get(`/access-accountability/justifications${this.buildQuery(params)}`);
  }
  async getJustification(id: string) {
    return this.get(`/access-accountability/justifications/${id}`);
  }
  async getJustificationByAccessId(accessId: string) {
    return this.get(`/access-accountability/justifications/access/${accessId}`);
  }
  async getJustificationReviewQueue(params?: Record<string, any>) {
    return this.get(`/access-accountability/justifications/review-queue${this.buildQuery(params)}`);
  }
  async createJustification(payload: Record<string, any>) {
    return this.post('/access-accountability/justifications', payload);
  }
  async reviewJustification(id: string, payload: Record<string, any>) {
    return this.post(`/access-accountability/justifications/${id}/review`, payload);
  }
  async declineAccess(id: string, reason: string) {
    return this.post(`/access-accountability/justifications/${id}/decline`, { reason });
  }
  // ── Sprint 2.6.0: Access Accountability -- Investigations ──
  async listInvestigations(params?: Record<string, any>) {
    return this.get(`/access-accountability/investigations${this.buildQuery(params)}`);
  }
  async getInvestigation(investigationId: string) {
    return this.get(`/access-accountability/investigations/${investigationId}`);
  }
  async createInvestigation(payload: Record<string, any>) {
    return this.post('/access-accountability/investigations', payload);
  }
  async updateInvestigation(investigationId: string, payload: Record<string, any>) {
    return this.patch(`/access-accountability/investigations/${investigationId}`, payload);
  }
  async addInvestigationTimeline(investigationId: string, entry: Record<string, any>) {
    return this.post(`/access-accountability/investigations/${investigationId}/timeline`, entry);
  }
  async linkInvestigationAccessIds(investigationId: string, accessIds: string[]) {
    return this.post(`/access-accountability/investigations/${investigationId}/link-access`, {
      access_ids: accessIds,
    });
  }
  async linkInvestigationJustificationIds(investigationId: string, justificationIds: string[]) {
    return this.post(
      `/access-accountability/investigations/${investigationId}/link-justifications`,
      { justification_ids: justificationIds },
    );
  }
  async closeInvestigation(investigationId: string, payload: Record<string, any>) {
    return this.post(`/access-accountability/investigations/${investigationId}/close`, payload);
  }
  async exportInvestigation(investigationId: string) {
    return this.get(`/access-accountability/investigations/${investigationId}/export`);
  }
  async getPatternAnalysis(userId: string, params?: Record<string, any>) {
    return this.get(
      `/access-accountability/investigations/pattern/${userId}${this.buildQuery(params)}`,
    );
  }
  // ── Sprint 2.6.0: Access Accountability -- Proofs ──────────
  async listAccessProofs(params?: Record<string, any>) {
    return this.get(`/access-accountability/proofs${this.buildQuery(params)}`);
  }
  /** Access-log entries with no proof yet — option source for the anchoring picker. */
  async listUnanchoredAccessLogs(params?: Record<string, any>) {
    return this.get(`/access-accountability/proofs/unanchored${this.buildQuery(params)}`);
  }
  async getAccessProof(id: string) {
    return this.get(`/access-accountability/proofs/${id}`);
  }
  async getAccessProofByAccessId(accessId: string) {
    return this.get(`/access-accountability/proofs/access/${accessId}`);
  }
  async getAnchoringStatus() {
    return this.get('/access-accountability/proofs/status');
  }
  async anchorAccessProof(accessId: string) {
    return this.post('/access-accountability/proofs/anchor', { access_id: accessId });
  }
  async batchAnchorAccessProofs(accessIds: string[]) {
    return this.post('/access-accountability/proofs/batch-anchor', { access_ids: accessIds });
  }
  async verifyAccessProofChain(params?: Record<string, any>) {
    return this.post('/access-accountability/proofs/verify', params || {});
  }
  // ── Sprint 2.6.0: Access Accountability -- Config ──────────
  async getAccessAccountabilityConfig() {
    return this.get('/access-accountability/config');
  }
  async upsertAccessAccountabilityConfig(payload: Record<string, any>) {
    return this.put('/access-accountability/config', payload);
  }
  // ── Sprint 2.6.0: Access Accountability -- Audit ───────────
  async listAccessAccountabilityAudit(params?: Record<string, any>) {
    return this.get(`/access-accountability/audit${this.buildQuery(params)}`);
  }
  async getAccessAccountabilityEntityAudit(entityType: string, entityId: string) {
    return this.get(`/access-accountability/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 21.8.0: Access Accountability -- Access Policies ──
  async listAccessPolicies(params?: Record<string, any>) {
    return this.get(`/access-accountability/policies${this.buildQuery(params)}`);
  }
  async createAccessPolicy(data: Record<string, any>) {
    return this.post('/access-accountability/policies', data);
  }
  // ── Sprint 21.8.0: Access Accountability -- Review Campaigns ──
  async listAcctbltyReviewCampaigns(params?: Record<string, any>) {
    return this.get(`/access-accountability/review-campaigns${this.buildQuery(params)}`);
  }
  async createAcctbltyReviewCampaign(data: Record<string, any>) {
    return this.post('/access-accountability/review-campaigns', data);
  }
  async certifyAccessReview(campaignId: string, data: Record<string, any>) {
    return this.post(`/access-accountability/review-campaigns/${campaignId}/certify`, data);
  }
  // ── Sprint 21.8.0: Access Accountability -- Investigation Workflow ──
  async updateInvestigationWorkflow(investigationId: string, status: string) {
    return this.patch(`/access-accountability/investigations/${investigationId}/workflow`, {
      status,
    });
  }
  async linkInvestigationToRemediation(investigationId: string, remediationId: string) {
    return this.post(`/access-accountability/investigations/${investigationId}/remediation`, {
      remediation_id: remediationId,
    });
  }
  async generateAuthorityEvidencePackage(investigationId: string) {
    return this.post(
      `/access-accountability/investigations/${investigationId}/authority-evidence`,
      {},
    );
  }
  // ── Sprint 21.8.0: Access Accountability -- Evidence Packages ──
  async generateAccessEvidencePackage(data: Record<string, any>) {
    return this.post('/access-accountability/evidence-packages', data);
  }
  async listAccessEvidencePackages(params?: Record<string, any>) {
    return this.get(`/access-accountability/evidence-packages${this.buildQuery(params)}`);
  }
  async getEvidencePackageCompleteness(packageId: string) {
    return this.get(`/access-accountability/evidence-packages/${packageId}/completeness`);
  }
  async getEvidencePackageArtifacts(packageId: string) {
    return this.get(`/access-accountability/evidence-packages/${packageId}/artifacts`);
  }
  async signOffEvidencePackage(packageId: string, data: Record<string, any>) {
    return this.post(`/access-accountability/evidence-packages/${packageId}/sign-off`, data);
  }
  async closeEvidencePackage(packageId: string, data: Record<string, any>) {
    return this.post(`/access-accountability/evidence-packages/${packageId}/close`, data);
  }
  async exportEvidencePackage(packageId: string) {
    return this.post(`/access-accountability/evidence-packages/${packageId}/export`, {});
  }
  async getAccessCertificationHistory(params?: Record<string, any>) {
    return this.get(`/access-accountability/certification-history${this.buildQuery(params)}`);
  }
  // ── Sprint 2.7.0: Consent Properties -- Multi-Property Governance ──
  // Properties
  async listConsentProperties(params?: Record<string, any>) {
    return this.get(`/consent-properties/properties${this.buildQuery(params)}`);
  }
  async getConsentProperty(propertyId: string) {
    return this.get(`/consent-properties/properties/${propertyId}`);
  }
  async createConsentProperty(payload: Record<string, any>) {
    return this.post('/consent-properties/properties', payload);
  }
  async updateConsentProperty(propertyId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/properties/${propertyId}`, payload);
  }
  async decommissionConsentProperty(propertyId: string) {
    return this.post(`/consent-properties/properties/${propertyId}/decommission`, {});
  }
  // Sprint 28.2.1: Sensitive Data Review
  async submitSensitiveDataReview(
    propertyId: string,
    payload: { action: 'approved' | 'rejected'; notes: string },
  ) {
    return this.post(`/consent-properties/properties/${propertyId}/sensitive-review`, payload);
  }
  // Sprint 28.2.1: Withdrawal Propagation
  async propagatePropertyWithdrawal(propertyId: string, payload: Record<string, any>) {
    return this.post(`/consent-properties/properties/${propertyId}/propagate-withdrawal`, payload);
  }
  async listPropertyWithdrawals(propertyId: string, params?: Record<string, any>) {
    return this.get(
      `/consent-properties/properties/${propertyId}/withdrawals${this.buildQuery(params)}`,
    );
  }
  // Groups
  async listConsentPropertyGroups(params?: Record<string, any>) {
    return this.get(`/consent-properties/groups${this.buildQuery(params)}`);
  }
  async getConsentPropertyGroup(groupId: string) {
    return this.get(`/consent-properties/groups/${groupId}`);
  }
  async createConsentPropertyGroup(payload: Record<string, any>) {
    return this.post('/consent-properties/groups', payload);
  }
  async updateConsentPropertyGroup(groupId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/groups/${groupId}`, payload);
  }
  async deleteConsentPropertyGroup(groupId: string) {
    return this.del(`/consent-properties/groups/${groupId}`);
  }
  async getConsentPropertyGroupDashboard(groupId: string) {
    return this.get(`/consent-properties/groups/${groupId}/dashboard`);
  }
  // Bindings
  async listConsentPropertyBindings(params?: Record<string, any>) {
    return this.get(`/consent-properties/bindings${this.buildQuery(params)}`);
  }
  async getConsentPropertyBindingsByProperty(propertyId: string) {
    return this.get(`/consent-properties/bindings/property/${propertyId}`);
  }
  async getConsentPropertyBindingMatrix() {
    return this.get('/consent-properties/bindings/matrix');
  }
  async createConsentPropertyBinding(payload: Record<string, any>) {
    return this.post('/consent-properties/bindings', payload);
  }
  async bulkCreateConsentPropertyBindings(payload: Record<string, any>) {
    return this.post('/consent-properties/bindings/bulk', payload);
  }
  async deleteConsentPropertyBinding(bindingId: string) {
    return this.del(`/consent-properties/bindings/${bindingId}`);
  }
  async updateConsentPropertyBindingStatus(bindingId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/bindings/${bindingId}/status`, payload);
  }
  // Change Requests
  async listConsentChangeRequests(params?: Record<string, any>) {
    return this.get(`/consent-properties/change-requests${this.buildQuery(params)}`);
  }
  async getConsentChangeRequestPipeline() {
    return this.get('/consent-properties/change-requests/pipeline');
  }
  async getConsentChangeRequest(crId: string) {
    return this.get(`/consent-properties/change-requests/${crId}`);
  }
  async getConsentChangeRequestImpact(crId: string) {
    return this.get(`/consent-properties/change-requests/${crId}/impact`);
  }
  async createConsentChangeRequest(payload: Record<string, any>) {
    return this.post('/consent-properties/change-requests', payload);
  }
  async updateConsentChangeRequest(crId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/change-requests/${crId}`, payload);
  }
  async submitConsentChangeRequest(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/submit`, {});
  }
  async approveConsentChangeRequest(crId: string, payload: Record<string, any>) {
    return this.post(`/consent-properties/change-requests/${crId}/approve`, payload);
  }
  async scheduleConsentChangeRequest(crId: string, payload: Record<string, any>) {
    return this.post(`/consent-properties/change-requests/${crId}/schedule`, payload);
  }
  async deployConsentChangeRequest(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/deploy`, {});
  }
  async advanceConsentChangeRequestStage(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/advance-stage`, {});
  }
  async completeConsentChangeRequestDeployment(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/complete-deployment`, {});
  }
  async pauseConsentChangeRequest(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/pause`, {});
  }
  async rollbackConsentChangeRequest(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/rollback`, {});
  }
  async cancelConsentChangeRequest(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/cancel`, {});
  }
  // Comparison
  async runConsentComparison(payload: Record<string, any>) {
    return this.post('/consent-properties/comparison/compare', payload);
  }
  async listConsentComparisonReports(params?: Record<string, any>) {
    return this.get(`/consent-properties/comparison/reports${this.buildQuery(params)}`);
  }
  async getConsentComparisonReport(reportId: string) {
    return this.get(`/consent-properties/comparison/reports/${reportId}`);
  }
  async saveConsentComparisonNotes(reportId: string, notes: string) {
    return this.patch(`/consent-properties/comparison/reports/${reportId}/notes`, {
      analyst_notes: notes,
    });
  }
  async runConsentInconsistencyScan() {
    return this.post('/consent-properties/comparison/scan', {});
  }
  // Inconsistencies
  async listConsentInconsistencies(params?: Record<string, any>) {
    return this.get(`/consent-properties/inconsistencies${this.buildQuery(params)}`);
  }
  async getConsentInconsistency(id: string) {
    return this.get(`/consent-properties/inconsistencies/${id}`);
  }
  async getConsentInconsistencyTrend() {
    return this.get('/consent-properties/inconsistencies/trend');
  }
  async acknowledgeConsentInconsistency(id: string) {
    return this.post(`/consent-properties/inconsistencies/${id}/acknowledge`, {});
  }
  async resolveConsentInconsistency(id: string, changeRequestId: string) {
    return this.post(`/consent-properties/inconsistencies/${id}/resolve`, {
      change_request_id: changeRequestId,
    });
  }
  async suppressConsentInconsistency(id: string) {
    return this.post(`/consent-properties/inconsistencies/${id}/suppress`, {});
  }
  // Sprint 28.2.1: Owner/DPO-Scoped Review
  async listMyInconsistencyReview(params?: Record<string, any>) {
    return this.get(`/consent-properties/inconsistencies/my-review${this.buildQuery(params)}`);
  }
  async listDpoInconsistencyReview(params?: Record<string, any>) {
    return this.get(`/consent-properties/inconsistencies/dpo-review${this.buildQuery(params)}`);
  }
  async listMyChangeRequestReview(params?: Record<string, any>) {
    return this.get(`/consent-properties/change-requests/my-review${this.buildQuery(params)}`);
  }
  // Snapshot Schedules
  async listConsentSnapshotSchedules(params?: Record<string, any>) {
    return this.get(`/consent-properties/snapshot-schedules${this.buildQuery(params)}`);
  }
  async getConsentSnapshotSchedule(scheduleId: string) {
    return this.get(`/consent-properties/snapshot-schedules/${scheduleId}`);
  }
  async createConsentSnapshotSchedule(payload: Record<string, any>) {
    return this.post('/consent-properties/snapshot-schedules', payload);
  }
  async updateConsentSnapshotSchedule(scheduleId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/snapshot-schedules/${scheduleId}`, payload);
  }
  async pauseConsentSnapshotSchedule(scheduleId: string) {
    return this.post(`/consent-properties/snapshot-schedules/${scheduleId}/pause`, {});
  }
  async resumeConsentSnapshotSchedule(scheduleId: string) {
    return this.post(`/consent-properties/snapshot-schedules/${scheduleId}/resume`, {});
  }
  async disableConsentSnapshotSchedule(scheduleId: string) {
    return this.post(`/consent-properties/snapshot-schedules/${scheduleId}/disable`, {});
  }
  async executeConsentSnapshotSchedule(scheduleId: string) {
    return this.post(`/consent-properties/snapshot-schedules/${scheduleId}/execute`, {});
  }
  // Golden Templates
  async listConsentGoldenTemplates(params?: Record<string, any>) {
    return this.get(`/consent-properties/golden-templates${this.buildQuery(params)}`);
  }
  async getConsentGoldenTemplate(templateId: string) {
    return this.get(`/consent-properties/golden-templates/${templateId}`);
  }
  async createConsentGoldenTemplate(payload: Record<string, any>) {
    return this.post('/consent-properties/golden-templates', payload);
  }
  async updateConsentGoldenTemplate(templateId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/golden-templates/${templateId}`, payload);
  }
  async updateConsentGoldenTemplateStatus(templateId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/golden-templates/${templateId}/status`, payload);
  }
  // Sprint 20.7.0 — Golden Template Application & Rollout
  async applyTemplateToProperty(templateId: string, propertyId: string) {
    return this.post(`/consent-properties/golden-templates/${templateId}/apply-property`, {
      property_id: propertyId,
    });
  }
  async applyTemplateToGroup(templateId: string, groupId: string) {
    return this.post(`/consent-properties/golden-templates/${templateId}/apply-group`, {
      group_id: groupId,
    });
  }
  async getTemplateApplicationHistory(templateId: string) {
    return this.get(`/consent-properties/golden-templates/${templateId}/applications`);
  }
  async listTemplateRollouts(params?: Record<string, any>) {
    return this.get(`/consent-properties/rollouts${this.buildQuery(params)}`);
  }
  async confirmRolloutProperty(rolloutId: string, propertyId: string) {
    return this.post(`/consent-properties/rollouts/${rolloutId}/confirm/${propertyId}`, {});
  }
  async rejectRolloutProperty(rolloutId: string, propertyId: string, reason?: string) {
    return this.post(`/consent-properties/rollouts/${rolloutId}/reject/${propertyId}`, { reason });
  }
  async getRolloutSummary(rolloutId: string) {
    return this.get(`/consent-properties/rollouts/${rolloutId}/summary`);
  }
  // Sprint 20.7.0 — Property Consent Analytics
  async getPropertyAnalytics(propertyId: string) {
    return this.get(`/consent-properties/analytics/property/${propertyId}`);
  }
  async getPropertyComplianceScore(propertyId: string) {
    return this.get(`/consent-properties/analytics/property/${propertyId}/compliance`);
  }
  async getNonCompliantProperties() {
    return this.get('/consent-properties/analytics/non-compliant');
  }
  // Evidence Packs
  async generateConsentEvidencePack(payload: Record<string, any>) {
    return this.post('/consent-properties/evidence-packs/generate', payload);
  }
  async listConsentEvidencePacks(params?: Record<string, any>) {
    return this.get(`/consent-properties/evidence-packs${this.buildQuery(params)}`);
  }
  async exportConsentEvidencePack(packId: string): Promise<AttachmentRef> {
    return this.post(`/consent-properties/evidence-packs/${encodeURIComponent(packId)}/export`, {});
  }
  /**
   * Sprint 44.0.1 Wave 3 — R-33 list-export migration for Consent
   * Properties. Both methods route the page's "export list" action
   * through the backend UnifiedExportService (csv / xlsx / pdf / docx)
   * and return an AttachmentRef. Caller invokes
   * `downloadPlatformFile(ref.file_id, ref.filename)` to trigger the
   * browser download. These replace in-browser `downloadClientExport()`
   * usages on BindingMatrixPage and PropertyRegistryPage.
   */
  async exportConsentPropertyBindings(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/consent-properties/bindings/export${this.buildQuery(params)}`);
  }
  async exportConsentPropertyRegistry(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/consent-properties/properties/export${this.buildQuery(params)}`);
  }
  async exportConsentPropertyChangeRequests(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/consent-properties/change-requests/export${this.buildQuery(params)}`);
  }
  // Dashboard
  async getConsentPropertyDashboard() {
    return this.get('/consent-properties/dashboard');
  }
  // Audit
  async listConsentPropertyAudit(params?: Record<string, any>) {
    return this.get(`/consent-properties/audit${this.buildQuery(params)}`);
  }
  async getConsentPropertyEntityAudit(entityType: string, entityId: string) {
    return this.get(`/consent-properties/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 2.8.0: International Packaging and Localization ──
  // Market Packs
  async listMarketPacks(params?: Record<string, any>) {
    return this.get(`/localization/market-packs${this.buildQuery(params)}`);
  }
  async getMarketPack(packId: string) {
    return this.get(`/localization/market-packs/${packId}`);
  }
  // Sprint 64.149.0 — `createMarketPack` removed with `POST
  // /localization/market-packs`. Market packs are installed, not created; the
  // route could never succeed and this method had no caller. A client method
  // for a route that does not exist is the same defect one layer out.
  async updateMarketPack(packId: string, data: any) {
    return this.patch(`/localization/market-packs/${packId}`, data);
  }
  async updateMarketPackStatus(packId: string, status: string) {
    return this.post(`/localization/market-packs/${packId}/status`, { status });
  }
  async installMarketPack(packId: string) {
    return this.post(`/localization/market-packs/${packId}/install`, {});
  }
  async uninstallMarketPack(packId: string) {
    return this.post(`/localization/market-packs/${packId}/uninstall`, {});
  }
  // Sprint 45.8.0 — Help-Content Pack admin
  async getHelpContentPacks() {
    return this.get('/localization/help-content-packs');
  }
  async getHelpContentPackCatalog() {
    return this.get('/localization/help-content-packs/catalog');
  }
  async reinstallHelpContentPack() {
    return this.post('/localization/help-content-packs/reinstall', {});
  }
  async uninstallHelpContentPack(packCode: string) {
    return this.post(`/localization/help-content-packs/${packCode}/uninstall`, {});
  }
  // Pack Artifacts
  async listPackArtifacts(params?: Record<string, any>) {
    return this.get(`/localization/artifacts${this.buildQuery(params)}`);
  }
  async getPackArtifact(artifactId: string) {
    return this.get(`/localization/artifacts/${artifactId}`);
  }
  async listPackArtifactsByPack(packId: string, params?: Record<string, any>) {
    return this.get(`/localization/artifacts/pack/${packId}${this.buildQuery(params)}`);
  }
  async createPackArtifact(data: any) {
    return this.post('/localization/artifacts', data);
  }
  async updatePackArtifact(artifactId: string, data: any) {
    return this.patch(`/localization/artifacts/${artifactId}`, data);
  }
  async deletePackArtifact(artifactId: string) {
    return this.del(`/localization/artifacts/${artifactId}`);
  }
  // Language Packs
  async listLanguagePacks(params?: Record<string, any>) {
    return this.get(`/localization/language-packs${this.buildQuery(params)}`);
  }
  async getLanguagePack(packId: string) {
    return this.get(`/localization/language-packs/${packId}`);
  }
  async getLanguagePackCompleteness(packId: string) {
    return this.get(`/localization/language-packs/${packId}/completeness`);
  }
  async createLanguagePack(data: any) {
    return this.post('/localization/language-packs', data);
  }
  async updateLanguagePack(packId: string, data: any) {
    return this.patch(`/localization/language-packs/${packId}`, data);
  }
  async activateLanguagePack(packId: string) {
    return this.post(`/localization/language-packs/${packId}/activate`, {});
  }
  async deactivateLanguagePack(packId: string) {
    return this.post(`/localization/language-packs/${packId}/deactivate`, {});
  }
  async uninstallLanguagePack(packId: string) {
    return this.del(`/localization/language-packs/${packId}`);
  }
  async listAvailableLanguagePacks() {
    return this.get('/localization/language-packs/available');
  }
  /** Sprint 26.4.2: Public endpoint — installed languages for UI selectors (no auth required). */
  async listInstalledLanguages(): Promise<{
    data: Array<{ locale: string; display_name: string }>;
    total: number;
  }> {
    return this.get('/localization/public/installed-languages') as any;
  }
  async installLanguagePackFromRegistry(locale: string) {
    return this.post('/localization/language-packs/install', { locale });
  }
  // Regulatory Frameworks
  /**
   * Sprint 64.67.0 — pass `{ view: 'summary' }` unless the caller reads a
   * framework's **content**.
   *
   * The default response is the whole document, which over the installed corpus
   * is 11.71 MB across 199 rows — 87% of it `requirements`, `procedures` and
   * `fines_risks`. The summary view returns the same rows projected to identity
   * and lifecycle fields: 96.7 KB, measured 2026-08-18 — 124x smaller.
   *
   * `view` accepts `'full'` and `'summary'` only; anything else is a 400, so a
   * typo is loud rather than merely expensive. Keep the default where a caller
   * genuinely reads content off the list — `RegulatoryFrameworksPage` renders a
   * requirement count from it.
   */
  async listRegulatoryFrameworks(params?: Record<string, any>) {
    return this.get(`/localization/frameworks${this.buildQuery(params)}`);
  }
  async getRegulatoryFramework(frameworkId: string) {
    return this.get(`/localization/frameworks/${frameworkId}`);
  }
  async getFrameworkRequirements(frameworkId: string) {
    return this.get(`/localization/frameworks/${frameworkId}/requirements`);
  }
  async createRegulatoryFramework(data: any) {
    return this.post('/localization/frameworks', data);
  }
  async updateRegulatoryFramework(frameworkId: string, data: any) {
    return this.patch(`/localization/frameworks/${frameworkId}`, data);
  }
  async updateRegulatoryFrameworkStatus(frameworkId: string, status: string) {
    return this.post(`/localization/frameworks/${frameworkId}/status`, { status });
  }
  async addFrameworkRequirement(frameworkId: string, data: any) {
    return this.post(`/localization/frameworks/${frameworkId}/requirements`, data);
  }
  async removeFrameworkRequirement(frameworkId: string, requirementId: string) {
    return this.del(`/localization/frameworks/${frameworkId}/requirements/${requirementId}`);
  }
  // Sprint 25.5.0: Framework detail endpoints
  async getFrameworkProcedures(
    frameworkId: string,
    page = 1,
    pageSize = 50,
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    return this.get(
      `/localization/frameworks/${frameworkId}/procedures?page=${page}&page_size=${pageSize}`,
    );
  }
  async getFrameworkFinesRisks(
    frameworkId: string,
    page = 1,
    pageSize = 50,
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    return this.get(
      `/localization/frameworks/${frameworkId}/fines-risks?page=${page}&page_size=${pageSize}`,
    );
  }
  // Sprint 46.9.0 — list regulatory guidances for a framework. The route
  // takes the framework_code (not the framework_id) for symmetry with how
  // pack-installer + the guidance service key records.
  async getFrameworkGuidances(
    frameworkCode: string,
    params: {
      type?: string;
      category?: string;
      module?: string;
      status?: string;
      search?: string;
      page?: number;
      page_size?: number;
      sort?: string;
    } = {},
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&');
    return this.get(
      `/localization/frameworks/${encodeURIComponent(frameworkCode)}/guidances${qs ? `?${qs}` : ''}`,
    );
  }
  async recordGuidanceViewed(frameworkCode: string, guidanceId: string): Promise<void> {
    return this.post(
      `/localization/frameworks/${encodeURIComponent(frameworkCode)}/guidances/${encodeURIComponent(guidanceId)}/viewed`,
    );
  }
  // Sprint 26.4.0.3: Get framework legislative stack
  async getFrameworkStack(frameworkId: string): Promise<Record<string, unknown>> {
    return this.get(`/localization/frameworks/${frameworkId}/stack`);
  }
  // Sprint 25.5.0.2: Update stack layer status
  async updateStackLayerStatus(
    frameworkId: string,
    layerCode: string,
    status: string,
  ): Promise<Record<string, unknown>> {
    return this.patch(`/localization/frameworks/${frameworkId}/stack/${layerCode}/status`, {
      status,
    });
  }
  // Regulatory Mappings
  async listRegulatoryMappings(params?: Record<string, any>) {
    return this.get(`/localization/mappings${this.buildQuery(params)}`);
  }
  async getRegulatoryMapping(mappingId: string) {
    return this.get(`/localization/mappings/${mappingId}`);
  }
  async listMappingsByFramework(frameworkId: string) {
    return this.get(`/localization/mappings/framework/${frameworkId}`);
  }
  async listMappingsByModule(module: string) {
    return this.get(`/localization/mappings/module/${module}`);
  }
  async seedRegulatoryMappings() {
    return this.post('/localization/mappings/seed', {});
  }
  async createRegulatoryMapping(data: any) {
    return this.post('/localization/mappings', data);
  }
  async updateRegulatoryMapping(mappingId: string, data: any) {
    return this.patch(`/localization/mappings/${mappingId}`, data);
  }
  async deleteRegulatoryMapping(mappingId: string) {
    return this.del(`/localization/mappings/${mappingId}`);
  }
  // Pack Submissions
  async listPackSubmissions(params?: Record<string, any>) {
    return this.get(`/localization/submissions${this.buildQuery(params)}`);
  }
  async getPackSubmission(submissionId: string) {
    return this.get(`/localization/submissions/${submissionId}`);
  }
  async createPackSubmission(data: any) {
    return this.post('/localization/submissions', data);
  }
  async startPackSubmissionReview(submissionId: string, reviewerIds: string[]) {
    return this.post(`/localization/submissions/${submissionId}/review`, {
      reviewer_ids: reviewerIds,
    });
  }
  async addPackSubmissionComment(submissionId: string, data: any) {
    return this.post(`/localization/submissions/${submissionId}/comment`, data);
  }
  async approvePackSubmission(submissionId: string) {
    return this.post(`/localization/submissions/${submissionId}/approve`, {});
  }
  async installPackSubmission(submissionId: string) {
    return this.post(`/localization/submissions/${submissionId}/install`, {});
  }
  async rejectPackSubmission(submissionId: string, reason: string) {
    return this.post(`/localization/submissions/${submissionId}/reject`, { reason });
  }
  // Pack install from zip (Sprint 26.7.0 Round 4) — removed by owner ruling
  // 2026-10-21 (OR-3): the route refuses every unsigned zip and no screen offers one.
  async listAllInstalledPacks() {
    return this.get('/localization/submissions/installed-all');
  }
  // Localization Audit
  async listLocalizationAudit(params?: Record<string, any>) {
    return this.get(`/localization/audit${this.buildQuery(params)}`);
  }
  async getLocalizationEntityAudit(entityType: string, entityId: string) {
    return this.get(`/localization/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 7.5.0: Pack Catalog & Install Lifecycle ───────────
  // Pack Catalog
  async listPackCatalog(params?: Record<string, any>) {
    return this.get(`/localization/catalog${this.buildQuery(params)}`);
  }
  async getPackCatalogEntry(marketCode: string) {
    return this.get(`/localization/catalog/${marketCode}`);
  }
  async listPackCatalogClusters() {
    return this.get('/localization/catalog/clusters');
  }
  async getPackInstallStatus() {
    return this.get('/localization/install-status');
  }
  async installPackFromCatalog(marketCode: string, autoInstallDependencies: boolean = false) {
    return this.post(`/localization/catalog/${marketCode}/install`, {
      auto_install_dependencies: autoInstallDependencies,
    });
  }
  async uninstallPackFromCatalog(marketCode: string, force: boolean = false) {
    return this.post(`/localization/catalog/${marketCode}/uninstall`, { force });
  }
  async installCluster(cluster: string) {
    return this.post(`/localization/catalog/clusters/${cluster}/install`, {});
  }
  /**
   * Sprint 63.33.0 (#594) — install ONE standards pack (a catalogue row marked
   * `installable_alone: true`). `localization/routes.ts`:
   * `POST /regulatory/packs/:pack_code/install`, body a strict `{}`, 201 with the
   * install result; 409 `PACK_ALREADY_INSTALLED`, 400 `PACK_NOT_INSTALLABLE_ALONE`,
   * 404 `PACK_NOT_FOUND`, 403 without `regulatory_framework:write`.
   */
  async installStandalonePack(packCode: string): Promise<StandalonePackInstallManifest> {
    return this.post(`/localization/regulatory/packs/${encodeURIComponent(packCode)}/install`, {});
  }
  /**
   * Sprint 63.33.0 (#594), security review P3-4 — remove a standards pack the
   * organisation installed on its own. `DELETE /regulatory/packs/:pack_code/install`,
   * no body, 200 with what was removed; 404 `PACK_NOT_INSTALLED` / `PACK_NOT_FOUND`,
   * 400 `PACK_NOT_INSTALLABLE_ALONE`, 403 without `regulatory_framework:write`.
   */
  async uninstallStandalonePack(packCode: string): Promise<StandalonePackUninstallResult> {
    return this.del(`/localization/regulatory/packs/${encodeURIComponent(packCode)}/install`);
  }
  // ── Sprint 2.9.0: Partner Marketplace ────────────────────────
  // Partner Organizations
  async listPartnerOrgs(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/partner-orgs${this.buildQuery(params)}`);
  }
  /**
   * Sprint 64.169.3 — the tenants the caller's partner orgs manage, as
   * `{ org_id, name }`, for the two "Client org" pickers.
   *
   * Returns the full R-23 envelope, and its values are truthful rather than
   * decorative: the set is the union of `managed_org_ids` over the caller's own
   * partner orgs, so it is bounded by how many tenants they manage and there is
   * no page 2. `page` is 1 and `page_size` equals `total`, which is what "one
   * page holding everything" looks like — so `extractPaginationMeta` reads it
   * correctly and a truncation notice computed from it renders nothing.
   *
   * This is the id space `org_id` on a ticket or a deliverable is supposed to
   * hold. `listPartnerOrgs` returns a DIFFERENT id space (`partner_id`), and
   * feeding a client-tenant field from it is the defect this sprint fixes — so
   * do not substitute one for the other when this call returns empty.
   */
  async listManagedTenants() {
    return this.get('/partner-marketplace/partner-orgs/managed-tenants');
  }
  /**
   * Sprint 64.169.0 — tenant-wide partner counts, aggregated server-side.
   *
   * Returns `{ total_partner_orgs, total_managed_tenants }` — a summary object
   * with **no** pagination envelope, so callers must not put it through
   * `unwrapResponse`. It replaces summing `managed_org_ids.length` over a
   * listed page, which made a card labelled "Total" a function of pagination.
   *
   * Sprint 64.169.1 added `health_score`, `health_band` and
   * `health_excluded_factors` to the same body. The first two are `null` when
   * there is nothing to score or when the score's own read failed; the third is
   * always present, because a card showing `--` must still be able to say what
   * the number would not have covered.
   */
  async getPartnerOrgSummary() {
    return this.get('/partner-marketplace/partner-orgs/summary');
  }

  /**
   * Sprint 64.169.1 — the programme score with its factor breakdown.
   *
   * Not `getPartnerOrgHealth(partnerId)`, which is what the spec named: nothing
   * on a ticket or a deliverable identifies which partner organisation it
   * belongs to, so a per-partner-org score cannot be computed today. See
   * `partner-health-service.ts` and `sprint-64.169.3.md`.
   */
  async getPartnerProgrammeHealth() {
    return this.get('/partner-marketplace/partner-orgs/health');
  }
  async getPartnerOrg(partnerId: string) {
    return this.get(`/partner-marketplace/partner-orgs/${partnerId}`);
  }
  async createPartnerOrg(data: any) {
    return this.post('/partner-marketplace/partner-orgs', data);
  }
  async updatePartnerOrg(partnerId: string, data: any) {
    return this.patch(`/partner-marketplace/partner-orgs/${partnerId}`, data);
  }
  async updatePartnerOrgStatus(partnerId: string, status: string) {
    return this.patch(`/partner-marketplace/partner-orgs/${partnerId}/status`, { status });
  }
  async addManagedOrg(partnerId: string, orgId: string) {
    return this.post(`/partner-marketplace/partner-orgs/${partnerId}/managed-orgs`, {
      org_id: orgId,
    });
  }
  async removeManagedOrg(partnerId: string, orgId: string) {
    return this.del(`/partner-marketplace/partner-orgs/${partnerId}/managed-orgs/${orgId}`);
  }
  // Service Tiers
  async listServiceTiers(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/service-tiers${this.buildQuery(params)}`);
  }
  async getServiceTier(tierId: string) {
    return this.get(`/partner-marketplace/service-tiers/${tierId}`);
  }
  async compareServiceTiers(tierIds: string[]) {
    return this.get(`/partner-marketplace/service-tiers/compare?tier_ids=${tierIds.join(',')}`);
  }
  async createServiceTier(data: any) {
    return this.post('/partner-marketplace/service-tiers', data);
  }
  async updateServiceTier(tierId: string, data: any) {
    return this.patch(`/partner-marketplace/service-tiers/${tierId}`, data);
  }
  async updateServiceTierStatus(tierId: string, status: string) {
    return this.patch(`/partner-marketplace/service-tiers/${tierId}/status`, { status });
  }
  // Deliverable Templates
  async listDeliverableTemplates(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/deliverable-templates${this.buildQuery(params)}`);
  }
  async getDeliverableTemplate(templateId: string) {
    return this.get(`/partner-marketplace/deliverable-templates/${templateId}`);
  }
  async createDeliverableTemplate(data: any) {
    return this.post('/partner-marketplace/deliverable-templates', data);
  }
  async updateDeliverableTemplate(templateId: string, data: any) {
    return this.patch(`/partner-marketplace/deliverable-templates/${templateId}`, data);
  }
  // Deliverable Instances
  async listDeliverables(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/deliverables${this.buildQuery(params)}`);
  }
  async getDeliverable(instanceId: string) {
    return this.get(`/partner-marketplace/deliverables/${instanceId}`);
  }
  async createDeliverable(data: any) {
    return this.post('/partner-marketplace/deliverables', data);
  }
  async updateDeliverable(instanceId: string, data: any) {
    return this.patch(`/partner-marketplace/deliverables/${instanceId}`, data);
  }
  async updateDeliverableStatus(instanceId: string, status: string) {
    return this.patch(`/partner-marketplace/deliverables/${instanceId}/status`, { status });
  }
  async bulkUpdateDeliverableStatus(instanceIds: string[], status: string) {
    return this.post('/partner-marketplace/deliverables/bulk-status', {
      instance_ids: instanceIds,
      status,
    });
  }
  async generateDeliverableSchedule(orgId: string, tierId: string) {
    return this.post('/partner-marketplace/deliverables/generate-schedule', {
      org_id: orgId,
      tier_id: tierId,
    });
  }
  // Brand Profiles
  async listBrandProfiles(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/brand-profiles${this.buildQuery(params)}`);
  }
  async getBrandProfile(brandId: string) {
    return this.get(`/partner-marketplace/brand-profiles/${brandId}`);
  }
  async createBrandProfile(data: any) {
    return this.post('/partner-marketplace/brand-profiles', data);
  }
  async updateBrandProfile(brandId: string, data: any) {
    return this.patch(`/partner-marketplace/brand-profiles/${brandId}`, data);
  }
  async setBrandProfileDefault(brandId: string) {
    return this.post(`/partner-marketplace/brand-profiles/${brandId}/set-default`, {});
  }
  // Support Tickets
  async listSupportTickets(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/support-tickets${this.buildQuery(params)}`);
  }
  async getSupportTicket(ticketId: string) {
    return this.get(`/partner-marketplace/support-tickets/${ticketId}`);
  }
  async createSupportTicket(data: any) {
    return this.post('/partner-marketplace/support-tickets', data);
  }
  async updateSupportTicket(ticketId: string, data: any) {
    return this.patch(`/partner-marketplace/support-tickets/${ticketId}`, data);
  }
  async updateSupportTicketStatus(ticketId: string, status: string) {
    return this.patch(`/partner-marketplace/support-tickets/${ticketId}/status`, { status });
  }
  async addTicketNote(ticketId: string, note: { type: string; content: string }) {
    return this.post(`/partner-marketplace/support-tickets/${ticketId}/notes`, note);
  }
  async addTicketAttachment(ticketId: string, attachment: any) {
    return this.post(`/partner-marketplace/support-tickets/${ticketId}/attachments`, attachment);
  }
  async escalateTicket(ticketId: string, toTier: string, reason: string) {
    return this.post(`/partner-marketplace/support-tickets/${ticketId}/escalate`, {
      to_tier: toTier,
      reason,
    });
  }
  async deescalateTicket(ticketId: string, toTier: string, reason: string) {
    return this.post(`/partner-marketplace/support-tickets/${ticketId}/deescalate`, {
      to_tier: toTier,
      reason,
    });
  }
  // Escalation
  async getEscalationDashboard() {
    return this.get('/partner-marketplace/escalation/dashboard');
  }
  async getEscalatedTickets(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/escalation/tickets${this.buildQuery(params)}`);
  }
  async evaluateAutoEscalation() {
    return this.post('/partner-marketplace/escalation/auto-evaluate', {});
  }
  // Workflow Templates
  async listWorkflowTemplates(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/workflow-templates${this.buildQuery(params)}`);
  }
  async getWorkflowTemplate(templateId: string) {
    return this.get(`/partner-marketplace/workflow-templates/${templateId}`);
  }
  async createWorkflowTemplate(data: any) {
    return this.post('/partner-marketplace/workflow-templates', data);
  }
  async updateWorkflowTemplate(templateId: string, data: any) {
    return this.patch(`/partner-marketplace/workflow-templates/${templateId}`, data);
  }
  // Workflow Instances
  async listWorkflowInstances(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/workflows${this.buildQuery(params)}`);
  }
  async getWorkflowInstance(instanceId: string) {
    return this.get(`/partner-marketplace/workflows/${instanceId}`);
  }
  async createWorkflowInstance(data: any) {
    return this.post('/partner-marketplace/workflows', data);
  }
  async updateWorkflowInstanceStatus(instanceId: string, status: string) {
    return this.patch(`/partner-marketplace/workflows/${instanceId}/status`, { status });
  }
  async advanceWorkflowStep(instanceId: string, stepNumber: number, data?: any) {
    return this.post(`/partner-marketplace/workflows/${instanceId}/advance`, {
      step_number: stepNumber,
      ...data,
    });
  }
  async cancelWorkflowInstance(instanceId: string) {
    return this.post(`/partner-marketplace/workflows/${instanceId}/cancel`, {});
  }
  // Reports
  async listPartnerReports(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/reports${this.buildQuery(params)}`);
  }
  async getPartnerReport(reportId: string) {
    return this.get(`/partner-marketplace/reports/${reportId}`);
  }
  async generatePartnerReport(data: any) {
    return this.post('/partner-marketplace/reports/generate', data);
  }
  // Partner Audit
  async listPartnerAudit(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/audit${this.buildQuery(params)}`);
  }
  async getPartnerAuditEvent(eventId: string) {
    return this.get(`/partner-marketplace/audit/${eventId}`);
  }
  async exportPartnerAudit(params?: Record<string, any>) {
    return this.post(`/partner-marketplace/audit/export${this.buildQuery(params)}`, {});
  }
  // ── Sprint 44.0.8 Wave 3 — 14 Partner Workspace list/detail exports ─
  //
  // 4-format (csv/xlsx/pdf/docx) backend routes via UnifiedExportService.
  // Each returns an AttachmentRef ({ file_id, filename, ... }) consumed
  // by `downloadPlatformFile(file_id, filename)`. The pre-existing
  // `exportPartnerAudit` (POST /audit/export, 1-format JSON metadata
  // response, Sprint 42.0.0 F1) is kept for programmatic consumers.
  async exportPartnerOrgsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/partner-orgs/export${this.buildQuery(params)}`);
  }
  async exportPartnerOrgDetail(
    partnerId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(
      `/partner-marketplace/partner-orgs/${partnerId}/export${this.buildQuery(params)}`,
    );
  }
  async exportPartnerDashboard(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/dashboard/export${this.buildQuery(params)}`);
  }
  async exportPartnerReportsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/reports/export${this.buildQuery(params)}`);
  }
  async exportPartnerAuditList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/audit/export-list${this.buildQuery(params)}`);
  }
  async exportPartnerWorkflowTemplatesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/workflow-templates/export${this.buildQuery(params)}`);
  }
  async exportPartnerWorkflowInstancesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/workflows/export${this.buildQuery(params)}`);
  }
  async exportPartnerDeliverablesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/deliverables/export${this.buildQuery(params)}`);
  }
  async exportPartnerDeliverableTemplatesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/deliverable-templates/export${this.buildQuery(params)}`);
  }
  async exportPartnerSupportTicketsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/support-tickets/export${this.buildQuery(params)}`);
  }
  async exportPartnerTicketDetail(
    ticketId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(
      `/partner-marketplace/support-tickets/${ticketId}/export${this.buildQuery(params)}`,
    );
  }
  async exportPartnerEscalationDashboard(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/escalation/export${this.buildQuery(params)}`);
  }
  async exportPartnerServiceTiersList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/service-tiers/export${this.buildQuery(params)}`);
  }
  async exportPartnerBrandProfilesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/brand-profiles/export${this.buildQuery(params)}`);
  }
  // ── Dashboard ───────────────────────────────────────────────
  async getDashboardSummary(params?: {
    framework_code?: string;
  }): Promise<Record<string, unknown>> {
    return this.get(`/dashboard/summary${this.buildQuery(params)}`);
  }
  // ── Sprint 3.0.0: Auth ──────────────────────────────────────
  async login(payload: {
    email: string;
    password: string;
  }): Promise<{ token: string; refresh_token: string; user: Record<string, unknown> }> {
    return this.post('/auth/login', payload, false);
  }
  /**
   * Sprint 63.6.3 — the platform session is ended before this answers. The
   * response says whether the session arrived through SSO and, for an OIDC
   * provider that declares an end-session endpoint, where to send the browser
   * to end the provider's session too.
   */
  async logoutApi(): Promise<LogoutResponse> {
    return this.post('/auth/logout', {});
  }
  async getMe(): Promise<UserResponse> {
    return this.get('/auth/me');
  }
  async changePassword(payload: { current_password: string; new_password: string }): Promise<void> {
    return this.post('/auth/change-password', payload);
  }
  // ── Sprint 26.0.0: Module Tips ─────────────────────────────────
  /**
   * The Module Guide record for `moduleId`, or its typed absence.
   *
   * The route answers a miss with a 404 whose `details.absent` says WHICH miss
   * (`module_not_registered` vs `no_record_for_registered_module`). That 404 is
   * an answer, not a failure, so it resolves here; a 404 that does not carry a
   * reason — or any other error — is still thrown.
   */
  async getModuleTip(moduleId: string): Promise<ModuleTipLookup> {
    try {
      // Sprint 62.51.0 (Remediation W, U23) — a typed absence may also come back
      // as a 200 `{ data: null, absent }`, so a page with no record logs no failed
      // request; the 404 form below is still read for a server older than that.
      const res = await this.get<{ data: unknown; absent?: unknown }>(
        `/onboarding/module-tips/${encodeURIComponent(moduleId)}`,
      );
      if (res && res.data == null && isModuleTipAbsenceReason(res.absent)) {
        return { data: null, absent: res.absent };
      }
      return { data: res?.data };
    } catch (err) {
      const absent = err instanceof ApiError && err.status === 404 ? err.details?.absent : null;
      if (isModuleTipAbsenceReason(absent)) return { data: null, absent };
      throw err;
    }
  }
  async getModuleTips(params?: Record<string, string>): Promise<Record<string, unknown>> {
    return this.get(`/onboarding/module-tips${this.buildQuery(params)}`);
  }
  // ── Sprint 26.0.0: User Preferences ────────────────────────────
  async getPreferences(): Promise<Record<string, unknown>> {
    return this.get('/auth/preferences');
  }
  async updatePreferences(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.patch('/auth/preferences', payload);
  }
  // ── Sprint 26.0.0: Self-service Profile ────────────────────────
  async updateMyProfile(payload: {
    display_name?: string;
    phone?: string | null;
    department?: string | null;
    title?: string | null;
  }): Promise<UserResponse> {
    return this.patch('/auth/me', payload);
  }
  // ── Sprint 3.1.0: Token Refresh ────────────────────────────────────
  async refreshToken(payload: {
    refresh_token: string;
  }): Promise<{ token: string; refresh_token: string }> {
    return this.post('/auth/refresh', payload, false);
  }
  // ── Sprint 3.1.0: Password Reset ──────────────────────────────
  async forgotPassword(payload: { email: string }): Promise<void> {
    return this.post('/auth/forgot-password', payload, false);
  }
  async resetPassword(payload: { token: string; new_password: string }): Promise<void> {
    return this.post('/auth/reset-password', payload, false);
  }
  // ── Sprint 3.1.0: Email Verification ──────────────────────────
  async verifyEmail(payload: { token: string }): Promise<void> {
    return this.post('/auth/verify-email', payload, false);
  }
  async requestEmailVerification(payload: { email: string }): Promise<void> {
    return this.post('/auth/request-email-verification', payload);
  }
  // ── Sprint 3.1.0: MFA ─────────────────────────────────────────
  async enrollMfa(): Promise<MfaEnrollmentResponse> {
    return this.post('/mfa/enroll', {});
  }
  async confirmMfa(payload: { code: string }): Promise<MfaConfirmationResponse> {
    return this.post('/mfa/confirm', payload);
  }
  async verifyMfa(payload: { code: string }): Promise<{ success: boolean }> {
    return this.post('/mfa/verify', payload);
  }
  async getMfaStatus(): Promise<MfaStatus> {
    return this.get('/mfa/status');
  }
  async disableMfa(): Promise<void> {
    return this.del('/mfa');
  }
  async regenerateBackupCodes(): Promise<{ backup_codes: string[] }> {
    return this.post('/mfa/backup-codes/regenerate', {});
  }
  // ── Sprint 63.7.0: organisation-level MFA enforcement ─────────
  /**
   * The organisation's level, plus what each level would cost.
   *
   * All three counts come back, not just the current level's, because the
   * operator is choosing BETWEEN levels — a screen that can only tell them the
   * consequence of the option they already picked makes them pick twice.
   */
  async getOrgMfaPolicy(): Promise<{
    enforcement_level: MfaEnforcementLevel;
    unenrolled_counts: Record<MfaEnforcementLevel, number>;
  }> {
    return this.get('/org-settings/mfa-policy');
  }
  /**
   * `acknowledged_unenrolled_count` is the number the operator was SHOWN.
   *
   * The server recounts and returns 409 `UNENROLLED_COUNT_CHANGED` if it has
   * moved. That is not ceremony: the window between opening the dialog and
   * confirming it is exactly when somebody else adds eleven users, or when a
   * screen left open since yesterday is confirmed.
   */
  async updateOrgMfaPolicy(payload: {
    enforcement_level: MfaEnforcementLevel;
    acknowledged_unenrolled_count: number;
  }): Promise<{ enforcement_level: MfaEnforcementLevel; unenrolled_count: number }> {
    return this.put('/org-settings/mfa-policy', payload);
  }
  // ── Sprint 63.7.0: administrator operations on another user's MFA ──
  /**
   * None of these returns a secret, an otpauth URI, a QR image or a backup
   * code. An administrator can see WHETHER somebody is enrolled and can take
   * the factor away; they can never see or set what it is.
   */
  async getUserMfaState(userId: string): Promise<MfaAdminView> {
    return this.get(`/users/${encodeURIComponent(userId)}/mfa`);
  }
  async resetUserMfa(userId: string, payload: { reason: string }): Promise<{ action: string }> {
    return this.post(`/users/${encodeURIComponent(userId)}/mfa/reset`, payload);
  }
  async disableUserMfa(userId: string, payload: { reason: string }): Promise<{ action: string }> {
    return this.del(`/users/${encodeURIComponent(userId)}/mfa`, payload);
  }
  async revokeUserBackupCodes(
    userId: string,
    payload: { reason: string },
  ): Promise<{ action: string }> {
    return this.post(`/users/${encodeURIComponent(userId)}/mfa/backup-codes/revoke`, payload);
  }
  // ── Sprint 63.7.0: #199 — turning IP-allowlist enforcement on ──
  /**
   * fix-sprint-20260921-2110 FINDING 4 — read-only, and read BEFORE the confirm.
   *
   * `caller_covered` is `null` when the question could not be settled at all
   * (no entries to be covered by, an address the deployment cannot vouch for,
   * or an IPv6 caller no saveable list could ever cover). The screen says which
   * of the three it has; it never states coverage it has not been told.
   */
  async getIpAllowlistEnforcementReadiness(): Promise<{
    would_block_observations_30d: number;
    caller_covered: boolean | null;
    caller_ip: string | null;
    caller_reason: string | null;
  }> {
    return this.get('/org-settings/ip-allowlist/enforcement-readiness');
  }
  async confirmIpAllowlistEnforcement(): Promise<{
    enforcement_confirmed_at: string | null;
    enforcement_confirmed_by: string | null;
    would_block_observations_30d: number;
  }> {
    return this.post('/org-settings/ip-allowlist/confirm-enforcement', {});
  }
  async withdrawIpAllowlistEnforcement(): Promise<{
    enforcement_confirmed_at: string | null;
  }> {
    return this.post('/org-settings/ip-allowlist/withdraw-enforcement', {});
  }
  // ── Sprint 3.1.0: SSO Config ──────────────────────────────────
  /**
   * What the login screen may know about an organisation before anybody signs
   * in.
   *
   * Sprint 63.6.0 corrected the declared return type. It said
   * `SsoConfigResponse` — a whole administration record including the attribute
   * map, the role map and every trust field — for an endpoint that has never
   * returned one and is unauthenticated. `SsoPublicInfo` is what it answers.
   */
  async getSsoPublicInfo(orgId: string): Promise<SsoPublicInfo> {
    return this.get(`/sso/public-info?org_id=${encodeURIComponent(orgId)}`);
  }
  async listSsoConfigs(): Promise<SsoConfigResponse[]> {
    return this.get('/sso/configs');
  }
  async getSsoConfig(configId: string): Promise<SsoConfigResponse> {
    return this.get(`/sso/configs/${configId}`);
  }
  async createSsoConfig(payload: Record<string, unknown>): Promise<SsoConfigResponse> {
    return this.post('/sso/configs', payload);
  }
  async updateSsoConfig(
    configId: string,
    payload: Record<string, unknown>,
  ): Promise<SsoConfigResponse> {
    return this.patch(`/sso/configs/${configId}`, payload);
  }
  /**
   * fix-sprint-20261016-1956 Finding 8 — `endSessions: true` also ends the
   * provider's sessions in the same request (after the configuration stops
   * accepting sign-ins) and answers the two counts; without it the answer is
   * empty (204), as before.
   */
  async deleteSsoConfig(
    configId: string,
    options: { endSessions?: boolean } = {},
  ): Promise<SsoRevokeSessionsResult | void> {
    const query = options.endSessions ? '?end_sessions=true' : '';
    return this.del(`/sso/configs/${encodeURIComponent(configId)}${query}`);
  }

  /**
   * fix-sprint-20261016-1956 Finding 8 — the deleted configurations, so their
   * sessions can still be ended. The server answers only a holder of
   * `platform:sso:write`.
   */
  async listDeletedSsoConfigs(): Promise<SsoConfigResponse[]> {
    return this.get('/sso/configs/deleted');
  }

  /**
   * Sprint 63.6.3 (#335) — end exactly one configuration's sessions. The answer
   * counts what was ended and, separately, the federated sessions that pre-date
   * `Session.sso_config_id` and so could not be attributed to any one
   * configuration (those are ended by revoking any configuration).
   */
  async revokeSsoConfigSessions(configId: string): Promise<SsoRevokeSessionsResult> {
    return this.post(`/sso/configs/${encodeURIComponent(configId)}/revoke-sessions`, {});
  }

  // ── Sprint 63.6.0: the SSO login runtime ─────────────────────
  /**
   * Where to send the browser to start a federated login.
   *
   * This is a URL the caller navigates to, not a request this client makes: the
   * round trip leaves the application and comes back through the assertion
   * consumer. It is built here so the one place that knows the API's base path
   * stays the one place that knows it.
   */
  ssoLoginUrl(configId: string, next?: string, providerType?: SsoProviderType | null): string {
    const query = next ? `?next=${encodeURIComponent(next)}` : '';
    /*
     * Sprint 63.6.1 — the protocol is a PARAMETER now, and that is the real
     * narrowing this sprint removed.
     *
     * 63.6.0's spec described the login page as gated on `provider_type`, and
     * it was not: the page offered the control for any configuration and this
     * method hard-coded `/sso/saml/`, so a tenant holding an `oidc` record was
     * shown a button that led to a refusal. `provider_type` has been on
     * `SsoPublicInfo` since 63.6.0 and was read by nobody.
     *
     * It defaults to SAML so that a caller who does not know the protocol keeps
     * the previous behaviour — and the SAML start route now redirects an `oidc`
     * configuration to the OIDC one, so that default is a slower path rather
     * than a wrong one.
     */
    const protocol = providerType === 'oidc' ? 'oidc' : 'saml';
    return `${API_BASE}/sso/${protocol}/${encodeURIComponent(configId)}/login${query}`;
  }

  /** Exchange the one-time handoff code for the session cookie pair. */
  async exchangeSsoHandoff(code: string): Promise<{
    token: string;
    refresh_token: string;
    user: Record<string, unknown>;
    expires_at: string;
    mfa_required: boolean;
    /**
     * The organisation requires a second factor and this account has none: the
     * session is held at the enrolment interstitial (`issueSessionPair` always
     * returns it; it was missing from this type).
     */
    mfa_enrolment_required?: boolean;
    redirect_to: string;
  }> {
    return this.post('/sso/handoff/exchange', { code });
  }

  /**
   * Start a verification run. Returns where to send the administrator.
   *
   * Sprint 63.6.1 — takes the protocol for the same reason `ssoLoginUrl` does.
   * The OIDC verification returns a `post_body` of `null` always: an
   * authorization request is a redirect with query parameters and has no form
   * to post, and the field is kept so the caller has one shape to handle.
   */
  async startSsoVerification(
    configId: string,
    providerType?: SsoProviderType | null,
  ): Promise<{ url: string; post_body: { SAMLRequest: string; RelayState: string } | null }> {
    const protocol = providerType === 'oidc' ? 'oidc' : 'saml';
    return this.get(`/sso/${protocol}/${encodeURIComponent(configId)}/verify`);
  }

  /** Read a verification run's per-check report. */
  async getSsoVerificationReport(stateId: string): Promise<SsoVerificationReport> {
    return this.get(`/sso/verification/${encodeURIComponent(stateId)}`);
  }

  async addSsoCertificate(configId: string, pem: string): Promise<SsoConfigResponse> {
    return this.post(`/sso/configs/${configId}/certificates`, { pem });
  }

  async removeSsoCertificate(
    configId: string,
    fingerprintSha256: string,
  ): Promise<SsoConfigResponse> {
    return this.del(`/sso/configs/${configId}/certificates/${fingerprintSha256}`);
  }

  async getSsoEnforcement(): Promise<SsoEnforcementSettings> {
    return this.get('/sso/enforcement');
  }

  async activateSsoEnforcement(payload: {
    config_id: string;
    exempt_user_ids: string[];
  }): Promise<SsoEnforcementSettings> {
    return this.post('/sso/enforcement', payload);
  }

  async deactivateSsoEnforcement(): Promise<SsoEnforcementSettings> {
    return this.del('/sso/enforcement');
  }
  // ── Sprint 3.0.0: Users ─────────────────────────────────────
  async listUsers(params?: Record<string, string>): Promise<PaginatedResponse<UserResponse>> {
    return this.get(`/users${this.buildQuery(params)}`);
  }
  /**
   * Sprint 63.14.0 — `{_id, name, email}` for the organisation and a `total`.
   * Read by `useUserDirectory` alone; a register that needs the full user
   * document still has {@link listUsers}.
   */
  async listUserDirectory(params?: Record<string, string>): Promise<UserDirectoryResponse> {
    return this.get(`/users/directory${this.buildQuery(params)}`);
  }
  /**
   * Sprint 63.14.1 — the named members of the caller's organisation, under the
   * directory's grant. At most `MAX_DIRECTORY_IDS` ids per call; the server
   * refuses more rather than truncating, and `useUserDirectory` splits.
   *
   * The query is built here, not through `buildQuery`, which drops `''` and
   * `'undefined'`: that would turn this read into the bare first-page read and
   * answer 500 members as if they were the ones asked for (gate E12.5 pass 5).
   * An empty list asks for nothing and is refused before any request.
   */
  async listUserDirectoryByIds(ids: readonly string[]): Promise<UserDirectoryByIdsResponse> {
    if (ids.length === 0) throw new Error('listUserDirectoryByIds: no ids to read');
    return this.get(`/users/directory?${new URLSearchParams({ ids: ids.join(',') }).toString()}`);
  }
  async getUser(userId: string): Promise<UserResponse> {
    return this.get(`/users/${userId}`);
  }
  async createUser(payload: Record<string, unknown>): Promise<UserResponse> {
    return this.post('/users', payload);
  }
  async updateUser(userId: string, payload: Record<string, unknown>): Promise<UserResponse> {
    return this.patch(`/users/${userId}`, payload);
  }
  async deactivateUser(userId: string): Promise<UserResponse> {
    return this.patch(`/users/${userId}/deactivate`, {});
  }
  async reactivateUser(userId: string): Promise<UserResponse> {
    return this.patch(`/users/${userId}/reactivate`, {});
  }
  async removeUser(userId: string): Promise<UserResponse> {
    return this.patch(`/users/${userId}/remove`, {});
  }
  async unlockUser(userId: string): Promise<UserResponse> {
    return this.patch(`/users/${userId}/unlock`, {});
  }
  /**
   * Sprint 63.29.0 (#541) — PATCH /users/:id/require-password-change. The user,
   * and every session they hold, can reach nothing but the change-password step
   * until they change it.
   */
  async requireUserPasswordChange(userId: string): Promise<UserResponse> {
    return this.patch(`/users/${userId}/require-password-change`, {});
  }
  async getUserRoles(userId: string): Promise<RoleAssignment[]> {
    return this.get(`/users/${userId}/roles`);
  }
  async assignUserRole(userId: string, payload: Record<string, unknown>): Promise<RoleAssignment> {
    return this.post(`/users/${userId}/roles`, payload);
  }
  async revokeUserRole(userId: string, assignmentId: string): Promise<void> {
    return this.del(`/users/${userId}/roles/${assignmentId}`);
  }
  // ── Sprint 3.0.0: Roles ─────────────────────────────────────
  async listRoles(params?: Record<string, string>): Promise<PaginatedResponse<Role>> {
    return this.get(`/roles${this.buildQuery(params)}`);
  }
  async getRole(roleId: string): Promise<Role> {
    return this.get(`/roles/${roleId}`);
  }
  async createRole(payload: Record<string, unknown>): Promise<Role> {
    return this.post('/roles', payload);
  }
  async updateRole(roleId: string, payload: Record<string, unknown>): Promise<Role> {
    return this.patch(`/roles/${roleId}`, payload);
  }
  async deleteRole(roleId: string): Promise<void> {
    return this.del(`/roles/${roleId}`);
  }
  // ── Sprint 3.2.0: API Keys ─────────────────────────────────────
  async listApiKeys(params?: Record<string, string>): Promise<PaginatedResponse<ApiKeyResponse>> {
    return this.get(`/api-keys${this.buildQuery(params)}`);
  }
  async createApiKey(payload: Record<string, unknown>): Promise<ApiKeyCreateResponse> {
    return this.post('/api-keys', payload);
  }
  async getApiKey(keyId: string): Promise<ApiKeyResponse> {
    return this.get(`/api-keys/${keyId}`);
  }
  async updateApiKey(keyId: string, payload: Record<string, unknown>): Promise<ApiKeyResponse> {
    return this.patch(`/api-keys/${keyId}`, payload);
  }
  async revokeApiKey(keyId: string): Promise<ApiKeyResponse> {
    return this.del(`/api-keys/${keyId}`);
  }
  async rotateApiKey(keyId: string): Promise<ApiKeyCreateResponse> {
    return this.post(`/api-keys/${keyId}/rotate`, {});
  }
  // ── Sprint 3.2.0: Service Accounts ─────────────────────────────
  async listServiceAccounts(params?: Record<string, string>): Promise<ServiceAccountResponse[]> {
    return this.get(`/service-accounts${this.buildQuery(params)}`);
  }
  async createServiceAccount(
    payload: Record<string, unknown>,
  ): Promise<ServiceAccountCreateResponse> {
    return this.post('/service-accounts', payload);
  }
  async getServiceAccount(id: string): Promise<ServiceAccountResponse> {
    return this.get(`/service-accounts/${id}`);
  }
  async deactivateServiceAccount(id: string): Promise<ServiceAccountResponse> {
    return this.patch(`/service-accounts/${id}/deactivate`, {});
  }
  // ── Sprint 3.2.0: Delegations ──────────────────────────────────
  async listDelegations(params?: Record<string, string>): Promise<DelegationResponse[]> {
    return this.get(`/delegations${this.buildQuery(params)}`);
  }
  async createDelegation(payload: Record<string, unknown>): Promise<DelegationResponse> {
    return this.post('/delegations', payload);
  }
  async getDelegation(delegationId: string): Promise<DelegationResponse> {
    return this.get(`/delegations/${delegationId}`);
  }
  async revokeDelegation(delegationId: string): Promise<DelegationResponse> {
    return this.del(`/delegations/${delegationId}`);
  }
  // ── Sprint 3.2.0: Break-Glass ──────────────────────────────────
  async requestBreakGlass(payload: Record<string, unknown>): Promise<BreakGlassResponse> {
    return this.post('/breakglass/request', payload);
  }
  async listBreakGlassRequests(params?: Record<string, string>): Promise<BreakGlassResponse[]> {
    return this.get(`/breakglass${this.buildQuery(params)}`);
  }
  /** The caller's own break-glass requests (needs only the request permission). */
  async listMyBreakGlassRequests(params?: Record<string, string>): Promise<BreakGlassResponse[]> {
    return this.get(`/breakglass/mine${this.buildQuery(params)}`);
  }
  async getBreakGlassRequest(requestId: string): Promise<BreakGlassResponse> {
    return this.get(`/breakglass/${requestId}`);
  }
  async approveBreakGlass(requestId: string): Promise<BreakGlassResponse> {
    return this.post(`/breakglass/${requestId}/approve`, {});
  }
  async denyBreakGlass(requestId: string, reason: string): Promise<BreakGlassResponse> {
    return this.post(`/breakglass/${requestId}/deny`, { reason });
  }
  // ── Sprint 3.2.0: Org Settings (IP Allowlist + Session) ────────
  async getOrgIpAllowlist(): Promise<IpAllowlistSettings> {
    return this.get('/org-settings/ip-allowlist');
  }
  async updateOrgIpAllowlist(payload: Record<string, unknown>): Promise<IpAllowlistSettings> {
    return this.put('/org-settings/ip-allowlist', payload);
  }
  async getOrgSessionSettings(): Promise<SessionSettings> {
    return this.get('/org-settings/sessions');
  }
  async updateOrgSessionSettings(payload: Record<string, unknown>): Promise<SessionSettings> {
    return this.put('/org-settings/sessions', payload);
  }
  // ── Sprint 46.2.0: Org sector self-identification ──────────────
  async getOrgSectors(): Promise<{ sectors: string[] }> {
    return this.get('/org-settings/sectors');
  }
  async updateOrgSectors(sectors: string[]): Promise<{ sectors: string[]; updated?: string }> {
    return this.patch('/org-settings/sectors', { sectors });
  }
  // ── Sprint 62.53.0: Organization profile statements ────────────
  // Routes: `src/backend/src/modules/org-settings/routes.ts`
  // (`mountProfileStatementRoutes`), mounted at `/api/v1/org-settings`. Every
  // route answers `{ data }`; these methods return the unwrapped `data`.
  /** `GET /profile-statements/applicability-review` (platform security read). */
  async getOrgProfileApplicabilityReview(): Promise<ApplicabilityReview> {
    const res = await this.get<{ data: ApplicabilityReview }>(
      '/org-settings/profile-statements/applicability-review',
    );
    return res.data;
  }
  /** `GET /profile-statements/:attribute` — current statement, history, evidence, used-by. */
  async getOrgProfileStatement(
    attribute: RecordedOrgProfileAttribute,
  ): Promise<OrgProfileStatementView> {
    const res = await this.get<{ data: OrgProfileStatementView }>(
      `/org-settings/profile-statements/${encodeURIComponent(attribute)}`,
    );
    return res.data;
  }
  /**
   * `PATCH /profile-statements/:attribute` (platform security write). The body
   * is strict on the server: `values` only with `state: 'selected'`, and
   * `expected_revision` always (0 when no statement exists). 409
   * `PROFILE_STATEMENT_REVISION_CONFLICT` carries `details.{expected_revision,
   * current_revision}`; 422 `PROFILE_EVIDENCE_NOT_HELD` carries `details.file_ids`.
   */
  async recordOrgProfileStatement(
    attribute: RecordedOrgProfileAttribute,
    input: OrgProfileStatementInput,
  ): Promise<OrgProfileStatementView> {
    const res = await this.patch<{ data: OrgProfileStatementView }>(
      `/org-settings/profile-statements/${encodeURIComponent(attribute)}`,
      input,
    );
    return res.data;
  }
  /**
   * `POST /profile-statements/:attribute/evidence` — the `AttachmentField`
   * `upload` override. Strict body `{ filename, mime_type, file_data_base64 }`;
   * the server measures the size from the payload. 201 `{ data: AttachmentRef }`.
   */
  async uploadOrgProfileStatementEvidence(
    attribute: RecordedOrgProfileAttribute,
    file: File,
  ): Promise<AttachmentRef> {
    const base64 = await fileToBase64(file);
    const res = await this.post<{ data: AttachmentRef }>(
      `/org-settings/profile-statements/${encodeURIComponent(attribute)}/evidence`,
      {
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        file_data_base64: base64,
      },
    );
    return res.data;
  }
  // ── Sprint 62.2.5: Cross-framework evidence packs ──────────────
  /**
   * The generic evidence-pack surface, shipped by 62.2.1–62.2.4.
   *
   * ## Two envelopes, deliberately not one
   *
   * Every method below returns the **generic** envelope: `{ data }` for a single
   * entity, `{ data, total, page, page_size }` for a list. The **PDPL** methods
   * further up this file do not — `getPdplEvidencePack` returns the bare
   * document and `listPdplEmailProviders` returns `{ items }`. 62.2.3 pinned
   * that difference by test precisely so this sprint would not apply one unwrap
   * across both and break the shipped PDPL pages.
   *
   * Paths re-read from `modules/framework-evidence-packs/routes.ts` at kickoff
   * (R-26), never inferred; the trace is in the sprint work file.
   */
  async previewFrameworkEvidencePack(
    rootFrameworkCode: string,
  ): Promise<{ data: EvidencePackPreview }> {
    return this.post('/framework-evidence-packs/preview', {
      root_framework_code: rootFrameworkCode,
    });
  }

  async downloadEvidenceReportArtifactChecked(
    packId: string,
    version: number,
    artifactPath: string,
    filename: string,
  ): Promise<void> {
    const path = `/framework-evidence-packs/${encodeURIComponent(packId)}/versions/${version}/artifact?path=${encodeURIComponent(artifactPath)}`;
    const response = await fetch(`${API_BASE}${path}`, { credentials: 'same-origin' });
    if (!response.ok) {
      let body: { error?: string; message?: string; details?: Record<string, unknown> } = {};
      try {
        body = await response.json();
      } catch {
        /* A non-JSON failure remains a typed refusal. */
        body = {};
      }
      this.notifyUnauthorized(response.status);
      this.notifyMfaStepUpRequired(response.status, body.error, path);
      this.notifyPasswordChangeRequired(response.status, body.error);
      throw new ApiError({
        code: statusToErrorCode(response.status),
        status: response.status,
        serverCode: body.error,
        message: body.message || `Download failed (${response.status})`,
        details: body.details,
      });
    }
    const objectUrl = URL.createObjectURL(await response.blob());
    try {
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }
  async previewEvidenceReport(input: {
    root_framework_code: string;
    report_scope: EvidenceReportScope;
    review?: EvidenceReportReviewInput;
  }): Promise<{ data: EvidenceReportPreview }> {
    return this.post('/framework-evidence-packs/preview', input);
  }

  async assembleEvidenceReport(
    input: EvidenceReportAssembleInput,
  ): Promise<{ data: EvidencePackAssembleResult }> {
    // Capture renders report/official outputs and copies admitted bytes synchronously.
    // Keep the response window beyond the capture lease; POST recovery remains keyed by the caller.
    return this.post('/framework-evidence-packs', input, true, undefined, 180_000);
  }

  async searchEvidenceReportSources(params: {
    kind: EvidenceReportSourceKind;
    root_framework_code?: string;
    purpose?: EvidenceReportScope['purpose'];
    assessment_kind?: EvidenceReportScope['assessment_kind'];
    applicant_kind?: EvidenceReportScope['applicant_kind'];
    query?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: Array<{ source_ref: EvidenceReportSourceRef; title: string; state: string }>;
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/framework-evidence-packs/report-sources${this.buildQuery(params)}`);
  }

  async listFrameworkEvidencePacks(params?: {
    page?: number;
    page_size?: number;
    status?: string;
    root_framework_code?: string;
  }): Promise<{
    data: FrameworkEvidencePack[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/framework-evidence-packs${this.buildQuery(params)}`);
  }

  async getFrameworkEvidencePack(packId: string): Promise<{ data: FrameworkEvidencePack }> {
    return this.get(`/framework-evidence-packs/${encodeURIComponent(packId)}`);
  }

  async assembleFrameworkEvidencePack(input: {
    root_framework_code: string;
    title?: string;
  }): Promise<{ data: EvidencePackAssembleResult }> {
    return this.post('/framework-evidence-packs', input);
  }

  /**
   * Sprint 62.2.0 close-out (AC6) — paginated. The route used to return every
   * version with `page_size` equal to the count; it now takes `page` and
   * `page_size` (at most 100 rows a page) like the submissions list, newest
   * first, so the pack's current version is always on page 1.
   */
  async listFrameworkEvidencePackVersions(
    packId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<{
    data: FrameworkEvidencePackVersion[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(
      `/framework-evidence-packs/${encodeURIComponent(packId)}/versions${this.buildQuery(params)}`,
    );
  }

  async exportFrameworkEvidencePack(packId: string): Promise<{ data: EvidencePackExportResult }> {
    return this.post(`/framework-evidence-packs/${encodeURIComponent(packId)}/export`, {});
  }

  async approveFrameworkEvidencePack(
    packId: string,
    input: { version: number; note?: string },
  ): Promise<{ data: FrameworkEvidencePackApproval }> {
    return this.post(`/framework-evidence-packs/${encodeURIComponent(packId)}/approve`, input);
  }

  /**
   * `idempotency_key` is **required** by the server (min 8 characters) and is
   * what makes a double-clicked Send exactly one dispatch: 62.2.4 inserts the
   * submission row carrying this key *before* calling the mailer, so a second
   * request is refused by a unique index rather than emailing the regulator
   * twice. The caller must generate it **once per composed message**, not once
   * per click — a fresh key per click would defeat the whole mechanism.
   */
  async sendFrameworkEvidencePack(
    packId: string,
    input: {
      idempotency_key: string;
      provider_config_id: string;
      recipients: string[];
      recipient_framework_code: string;
      responsible_user_id: string;
      subject: string;
      body: string;
    },
  ): Promise<{ data: FrameworkEvidencePackSubmission }> {
    return this.post(`/framework-evidence-packs/${encodeURIComponent(packId)}/send`, input);
  }

  /**
   * `idempotency_key` is **required** by the server, exactly as on
   * `sendFrameworkEvidencePack` (62.2.0 close-out, AC7): a retried record
   * carrying the same key is refused 409 `duplicate_dispatch` by the same
   * unique index, rather than recording the same paper twice. Generate it once
   * per composed record and keep it across retries.
   */
  async recordFrameworkEvidencePackHardCopy(
    packId: string,
    input: {
      idempotency_key: string;
      channel: 'postal' | 'in_person';
      recipient_framework_code: string;
      responsible_user_id: string;
      dispatched_at: string;
      hard_copy: {
        recipient_name: string;
        recipient_address: string;
        courier: string;
        tracking_number: string;
        proof: AttachmentRef;
      };
    },
  ): Promise<{ data: FrameworkEvidencePackSubmission }> {
    return this.post(`/framework-evidence-packs/${encodeURIComponent(packId)}/hard-copy`, input);
  }

  /**
   * Sprint 64.33.0 — the proof BYTES, through the pack.
   *
   * `AttachmentField` posts to the generic `POST /attachments`, which admits
   * any allowlisted owner type on `attachments:upload`. That made proof of a
   * physical filing producible by a caller who may not file. This route is
   * gated on `framework_evidence_pack:send`, matching the dispatch record the
   * proof is about to be attached to, and takes neither an owner type nor an
   * owner id — the route fixes the first and the path carries the second.
   *
   * No `size` is sent: the backend measures the payload it is holding.
   */
  async uploadFrameworkEvidencePackHardCopyProof(
    packId: string,
    file: File,
  ): Promise<AttachmentRef> {
    const base64 = await fileToBase64(file);
    const res = await this.post<{ data: AttachmentRef }>(
      `/framework-evidence-packs/${encodeURIComponent(packId)}/hard-copy-proof`,
      {
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        file_data_base64: base64,
      },
    );
    // This module wraps in `{ data }`; the generic upload route does not. The
    // unwrap happens here so `AttachmentField` keeps one return type.
    return res.data;
  }

  async listFrameworkEvidencePackSubmissions(
    packId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<{
    data: FrameworkEvidencePackSubmission[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(
      `/framework-evidence-packs/${encodeURIComponent(packId)}/submissions${this.buildQuery(params)}`,
    );
  }

  // ── Sprint 62.3.0: Vietnam AI governance operations ────────────

  /**
   * The AI system register.
   *
   * Paths re-read from `modules/ai-governance/routes.ts` at kickoff (R-26),
   * never inferred; the trace is in the sprint work file. Every method returns
   * the **generic** envelope — `{ data }` for one entity, `{ data, total, page,
   * page_size }` for a list — matching what the routes emit. Not PDPL's
   * `{ items }`; R-23 says never infer a shape from a module name.
   *
   * Note the review path: it is `/classifications/:runId/review`, not nested
   * under the profile. A review acts on a run, and routing it through the
   * profile would give a handler two sources for one fact.
   */
  async getAiGovernanceOptions(params?: {
    sector?: string;
    /**
     * Narrow the response to one publishing framework — F3. Omitted, the server
     * selects the only one when there is one and selects none when there are
     * several.
     */
    framework_code?: string;
  }): Promise<{ data: AiGovernanceOptionsResponse }> {
    return this.get(`/ai-governance/options${this.buildQuery(params)}`);
  }

  async listAiSystemProfiles(params?: {
    page?: number;
    page_size?: number;
    risk_tier?: string;
    lifecycle_stage?: string;
    status?: string;
  }): Promise<
    {
      data: AiSystemProfile[];
      total: number;
      page: number;
      page_size: number;
    } & Partial<AiInventoryExtras>
  > {
    return this.get(`/ai-governance/systems${this.buildQuery(params)}`);
  }

  /**
   * Sprint 62.48.0, cluster G (G33) — a legacy import of the bank's existing inventory. One row per entry; the
   * answer is the batch as recorded, with every row not taken and why.
   */
  async importAiLegacyInventory(input: {
    source_label?: string | null;
    rows: Array<{
      name?: string | null;
      description?: string | null;
      legacy_reference?: string | null;
      owner_as_recorded?: string | null;
      status_as_recorded?: string | null;
    }>;
  }): Promise<{ data: AiLegacyImportBatch }> {
    return this.post('/ai-governance/systems', { kind: 'legacy_import', ...input });
  }

  /** Sprint 62.48.0, cluster G (G36) — the bank-wide inventory as a file, through UnifiedExportService. */
  async exportAiInventory(
    params: { format?: 'csv' | 'xlsx' | 'pdf' } = {},
  ): Promise<AttachmentRef> {
    return this.get(`/ai-governance/systems${this.buildQuery({ format: 'csv', ...params })}`);
  }

  /** Sprint 62.48.0, cluster G (G35) — the detail carries the regulator evidence index beside the profile. */
  async getAiSystemProfile(profileId: string): Promise<{
    data: AiSystemProfile & {
      evidence_index?: AiEvidenceIndexEntry[];
      banking_trigger_reading?: AiBankingTriggerReading | null;
      dated_obligations?: AiSystemDatedObligation[];
      /** fix-sprint-20261031-1200 F15 — `unavailable` when the dated obligations could not be read. */
      dated_obligations_state?: AiReadState;
    };
  }> {
    return this.get(`/ai-governance/systems/${encodeURIComponent(profileId)}`);
  }

  /** Fix sprint #602 — the scope qualifications the installed frameworks declare for one surface. */
  async listAiScopeQualifications(
    surface: ScopeQualificationSurface,
  ): Promise<{ data: { qualifications: InstalledScopeQualification[]; unreadable: number } }> {
    return this.get(`/ai-governance/scope-qualifications?surface=${encodeURIComponent(surface)}`);
  }

  async createAiSystemProfile(
    input: CreateAiSystemProfileRequest,
  ): Promise<{ data: AiSystemProfile }> {
    return this.post('/ai-governance/systems', input);
  }

  async updateAiSystemProfile(
    profileId: string,
    input: UpdateAiSystemProfileRequest,
  ): Promise<{ data: AiSystemProfile }> {
    return this.patch(`/ai-governance/systems/${encodeURIComponent(profileId)}`, input);
  }

  async listAiClassificationRuns(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<{ data: AiClassificationRun[]; total: number; page: number; page_size: number }> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/classifications${this.buildQuery(bounds)}`,
    );
  }

  async createAiClassificationRun(
    profileId: string,
    input: {
      declared_category_codes: string[];
      trigger_reason: string;
      /**
       * Which installed framework's list to screen against — F3. Required in
       * effect whenever the organisation has more than one publisher; the
       * server answers `ai_framework_selection_required` rather than choosing.
       */
      framework_code?: string;
    },
  ): Promise<{ data: AiClassificationRun }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/classifications`,
      input,
    );
  }

  async reviewAiClassificationRun(
    runId: string,
    input: ReviewAiClassificationRunRequest,
  ): Promise<{
    data: {
      run: AiClassificationRun;
      obligations: { created: number; existing: number; not_applicable: number } | null;
    };
  }> {
    return this.post(`/ai-governance/classifications/${encodeURIComponent(runId)}/review`, input);
  }

  async listAiObligationRecords(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<{ data: AiObligationRecord[]; total: number; page: number; page_size: number }> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/obligations${this.buildQuery(bounds)}`,
    );
  }

  async updateAiObligationRecord(
    recordId: string,
    input: UpdateAiObligationRecordRequest,
  ): Promise<{ data: AiObligationRecord }> {
    return this.patch(`/ai-governance/obligations/${encodeURIComponent(recordId)}`, input);
  }

  // Sprint 62.29.0 — governance evidence fields.
  async recordAiGovernanceEvidence(
    profileId: string,
    field: AiGovernanceEvidenceField,
    input: UpdateAiGovernanceEvidenceRequest,
  ): Promise<{ data: AiSystemProfile }> {
    return this.put(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/governance-evidence/${encodeURIComponent(field)}`,
      input,
    );
  }

  async reviewAiGovernanceEvidence(
    profileId: string,
    field: AiGovernanceEvidenceField,
    input: ReviewAiGovernanceEvidenceRequest,
  ): Promise<{ data: AiSystemProfile }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/governance-evidence/${encodeURIComponent(field)}/review`,
      input,
    );
  }

  async listAiGovernanceEvidenceFormCandidates(
    profileId: string,
    field: AiGovernanceEvidenceField,
  ): Promise<{ data: AiGovernanceEvidenceFormCandidate[]; total: number }> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/governance-evidence/${encodeURIComponent(field)}/form-candidates`,
    );
  }

  async exportAiSystemEvidence(profileId: string): Promise<{
    data: {
      profile_id: string;
      root_framework_code: string;
      classification_run_id: string;
      risk_tier: string;
    };
  }> {
    return this.post(`/ai-governance/systems/${encodeURIComponent(profileId)}/export`, {});
  }

  // ── Sprint 62.48.0 wave 5: AI governance detail sections and registers ──
  //
  // Paths and bodies read from `modules/ai-governance/routes.ts` (R-26). A
  // list returns the paged envelope `{ data, total, page, page_size }`; a
  // single record returns `{ data }`. Refusals come back as `{ error, message,
  // details }`, so `details.missing_items` and `details.ceiling` are the
  // server's own words.

  async listAiInventoryReviews(params: {
    period?: string;
  }): Promise<PaginatedResponse<AiInventoryReviewRow>> {
    return this.get(`/ai-governance/inventory-reviews${this.buildQuery(params)}`);
  }

  async confirmAiInventoryReview(
    profileId: string,
    input: { period: string; outcome: 'confirmed' | 'changed'; change_summary?: string },
  ): Promise<{ data: AiInventoryReviewRecord }> {
    return this.post(
      `/ai-governance/inventory-reviews/${encodeURIComponent(profileId)}/confirm`,
      input,
    );
  }

  async listAiPaymentAuthorities(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiPaymentAuthorityView>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/payment-authorities${this.buildQuery(bounds)}`,
    );
  }

  async createAiPaymentAuthority(
    profileId: string,
    input: {
      customer_class?: AiBankingFactCustomerClass | null;
      agreement_reference?: string | null;
      automated_transaction_scope?: string[];
      per_transaction_limit_vnd?: number | null;
      per_day_limit_vnd?: number | null;
      valid_from?: string | null;
      valid_until?: string | null;
      suspension_conditions?: string | null;
      customer_termination_clause_reference?: string | null;
      identity_verification_statement?: string | null;
      pause_or_cancel_tool_reference?: string | null;
      electronic_lending?: { classification: string; lending_rule_reference: string } | null;
      payments_lead_user_id?: string | null;
      ai_execution_reference?: string | null;
      automation_conditions_statement?: string | null;
    },
  ): Promise<{ data: AiPaymentAuthorityRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/payment-authorities`,
      input,
    );
  }

  /**
   * Sprint 62.48.0 AC3, relief part — a change to an authority's terms, written as a pending version that a second
   * person approves (the writer is refused). Used for the statements a declared confirmation relief rests on.
   */
  async updateAiPaymentAuthority(
    authorityId: string,
    patch: {
      ai_execution_reference?: string | null;
      automation_conditions_statement?: string | null;
    },
  ): Promise<{ data: AiPaymentAuthorityView }> {
    return this.patch(
      `/ai-governance/payment-authorities/${encodeURIComponent(authorityId)}`,
      patch,
    );
  }

  async recordAiPaymentAuthorityEvent(
    authorityId: string,
    input:
      | { kind: 'activate' }
      | {
          kind: 'suspend' | 'revoke';
          reported_at: string;
          implemented_at?: string | null;
          reason: string;
        }
      | { kind: 'implement'; target_event_id: string; implemented_at: string }
      | {
          kind: 'execution_summary';
          execution: {
            customer_ref: string;
            executed_on: string;
            transaction_count: number;
            largest_transaction_vnd: number;
            largest_transaction_ref: string;
            daily_total_vnd: number;
          };
        }
      | { kind: 'relief_reliance'; relief_code: string; reason: string },
  ): Promise<{ data: AiPaymentAuthorityEvent }> {
    return this.post(
      `/ai-governance/payment-authorities/${encodeURIComponent(authorityId)}/events`,
      input,
    );
  }

  /** UC-VN-60 step 1 — a second person approves a pending write of an authority (the writer is refused, 403). */
  async approveAiPaymentAuthorityVersion(
    authorityId: string,
    versionId: string,
  ): Promise<{ data: AiPaymentAuthorityView }> {
    return this.post(
      `/ai-governance/payment-authorities/${encodeURIComponent(authorityId)}/versions/${encodeURIComponent(versionId)}/approve`,
      {},
    );
  }

  /** UC-VN-60 step 9 — the authority history, versions, breach flags and stops, as an internal evidence file. */
  async exportAiPaymentAuthorities(
    profileId: string,
    format: 'csv' | 'xlsx' | 'pdf' = 'csv',
  ): Promise<AttachmentRef> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/payment-authorities/export?format=${format}`,
    );
  }

  async listAiInterventions(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiInterventionRecord & { reading: AiInterventionReading }>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/interventions${this.buildQuery(bounds)}`,
    );
  }

  /** The request time is not sent: the platform records it when the request is saved (UC-VN-61 step 4). */
  async recordAiIntervention(
    profileId: string,
    input: {
      kind: AiInterventionKind;
      content_changed: string;
      reason: string;
      channel_id: string;
      decision_id?: string | null;
      resumes_intervention_id?: string | null;
      confirmed_at?: string | null;
      incident_id?: string | null;
    },
  ): Promise<{ data: AiInterventionRecord & { reading: AiInterventionReading } }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/interventions`,
      input,
    );
  }

  /** The channels of one system, each with the supervisor's line over it (UC-VN-61 step 1). */
  async listAiChannels(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiChannelRecord>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/channels${this.buildQuery(bounds)}`,
    );
  }

  /** `dual_control` (UC-VN-61 step 5): a pause, stop and resume on this channel wait for a second person. */
  async createAiChannel(
    profileId: string,
    input: { name: string; supervisor_line: string; dual_control?: boolean },
  ): Promise<{ data: AiChannelRecord }> {
    return this.post(`/ai-governance/systems/${encodeURIComponent(profileId)}/channels`, input);
  }

  /** Each channel's state, disclosures in use, versions awaiting approval and open interventions (UC-VN-61 step 3). Read only. */
  async listAiChannelStatus(
    profileId: string,
    params: { on?: string } = {},
  ): Promise<PaginatedResponse<AiChannelStatusRow>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/channel-status${this.buildQuery(params)}`,
    );
  }

  /** The pauses, stops, emergency stops and resumes of the system for a period, with their escalation responses (UC-VN-61 export 3). */
  async exportAiPauseStopRegister(
    profileId: string,
    params: { from?: string; to?: string; format?: 'csv' | 'xlsx' | 'pdf' } = {},
  ): Promise<AttachmentRef> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/pause-stop-register/export${this.buildQuery({ format: 'csv', ...params })}`,
    );
  }

  /** One channel's intervention log and disclosure history for a period (UC-VN-61 step 8). */
  async exportAiChannel(
    profileId: string,
    channelId: string,
    params: { from?: string; to?: string; format?: 'csv' | 'xlsx' | 'pdf' } = {},
  ): Promise<AttachmentRef> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/channels/${encodeURIComponent(channelId)}/export${this.buildQuery({ format: 'csv', ...params })}`,
    );
  }

  async listAiEscalations(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiEscalationRecord>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/escalations${this.buildQuery(bounds)}`,
    );
  }

  async respondAiEscalation(
    escalationId: string,
    input: { response_text: string },
  ): Promise<{ data: AiEscalationRecord }> {
    return this.post(
      `/ai-governance/escalations/${encodeURIComponent(escalationId)}/response`,
      input,
    );
  }

  async listAiStaffBriefings(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiStaffBriefingRecord>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/staff-briefings${this.buildQuery(bounds)}`,
    );
  }

  async recordAiStaffBriefing(
    profileId: string,
    input: {
      briefed_on: string;
      limits_briefed: string;
      procedure_version: string;
      attendee_user_ids: string[];
      training_reference?: string | null;
    },
  ): Promise<{ data: AiStaffBriefingRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/staff-briefings`,
      input,
    );
  }

  async confirmAiIntervention(
    profileId: string,
    interventionId: string,
    input: { confirmed_at: string },
  ): Promise<{ data: AiInterventionRecord & { reading: AiInterventionReading } }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/interventions/${encodeURIComponent(interventionId)}/confirmation`,
      input,
    );
  }

  /** A second person approves a dual-control pause, stop or resume (UC-VN-61 step 5). The recorder is refused (403). */
  async approveAiIntervention(
    profileId: string,
    interventionId: string,
  ): Promise<{ data: AiInterventionRecord & { reading: AiInterventionReading } }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/interventions/${encodeURIComponent(interventionId)}/approval`,
      {},
    );
  }

  async listAiImpactAssessments(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiImpactAssessmentRecord>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/impact-assessments${this.buildQuery(bounds)}`,
    );
  }

  async recordAiImpactAssessment(
    profileId: string,
    input: {
      purpose: string;
      scope: string;
      mechanism: { description: string; human_oversight_and_intervention: string };
      risk_analysis: {
        fairness: string;
        transparency: string;
        explainability: string;
        discrimination_risk: string;
      };
      personal_data_assessment: {
        dpia_record_id: string | null;
        not_applicable_reason: string | null;
      };
      controls: string;
      monitoring_plan: string;
      incident_response: string;
    },
  ): Promise<{ data: AiImpactAssessmentRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/impact-assessments`,
      input,
    );
  }

  async approveAiImpactAssessment(
    profileId: string,
    assessmentId: string,
  ): Promise<{ data: AiImpactAssessmentRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/impact-assessments/${encodeURIComponent(assessmentId)}/approval`,
      {},
    );
  }

  async recordAiFairnessSection(
    profileId: string,
    assessmentId: string,
    input: { fairness_assessment: string; bias_controls: string; vulnerable_groups: string },
  ): Promise<{ data: AiImpactAssessmentRecord }> {
    return this.put(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/impact-assessments/${encodeURIComponent(assessmentId)}/fairness`,
      input,
    );
  }

  async listAiSystemVersions(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiSystemVersion>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/versions${this.buildQuery(bounds)}`,
    );
  }

  async createAiSystemVersion(
    profileId: string,
    input: {
      model_version: string;
      prompt_reference?: string | null;
      reference_data_reference?: string | null;
      integration_reference?: string | null;
      autonomy_level?: AiAutonomyLevel | null;
      date_applied?: string;
      scope_affected?: string | null;
      configuration_reference?: string | null;
      test_result_reference?: string | null;
      listed_developer_user_ids?: string[];
      revalidation?: AiRevalidationDecisionInput;
    },
  ): Promise<{ data: AiSystemVersion }> {
    return this.post(`/ai-governance/systems/${encodeURIComponent(profileId)}/versions`, input);
  }

  async listAiVersionChanges(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiVersionChangeRecord>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/version-changes${this.buildQuery(bounds)}`,
    );
  }

  async recordAiVersionChange(
    profileId: string,
    versionId: string,
    input: {
      change_summary: string;
      changed_fields?: string[];
      revalidation?: AiRevalidationDecisionInput;
      /** fix-sprint-20261031-1200 F1 — a person's answer, with its basis; null is not recorded. */
      significant_change?: boolean | null;
      significance_basis?: string | null;
    },
  ): Promise<{ data: AiVersionChangeRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/versions/${encodeURIComponent(versionId)}/changes`,
      input,
    );
  }

  /**
   * Gate E12.5 CR-P2-3 — the significance answer, with its basis, on a change recorded without one. Answered once
   * (409 `ai_version_significance_already_recorded`); the basis is required (400). The person answering is the one
   * signed in.
   */
  async recordAiVersionChangeSignificance(
    profileId: string,
    changeId: string,
    input: { significant_change: boolean; significance_basis: string | null },
  ): Promise<{ data: AiVersionChangeRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/version-changes/${encodeURIComponent(changeId)}/significance`,
      input,
    );
  }

  async setAiRollbackPlan(
    profileId: string,
    versionId: string,
    input: {
      last_known_good_version_id: string;
      steps: string;
      owner_user_id: string;
      last_rehearsal_date?: string | null;
    },
  ): Promise<{ data: AiSystemVersion }> {
    return this.put(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/versions/${encodeURIComponent(versionId)}/rollback-plan`,
      input,
    );
  }

  async listAiRollbacks(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiRollbackEvent>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/rollbacks${this.buildQuery(bounds)}`,
    );
  }

  async recordAiRollback(
    profileId: string,
    input: {
      restored_version_id?: string | null;
      reason: string;
      linked_incident_id?: string | null;
    },
  ): Promise<{ data: AiRollbackEvent }> {
    return this.post(`/ai-governance/systems/${encodeURIComponent(profileId)}/rollbacks`, input);
  }

  async listAiReleaseApprovals(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiReleaseApprovalRecord>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/release-approvals${this.buildQuery(bounds)}`,
    );
  }

  async approveAiRelease(
    profileId: string,
    versionId: string,
    input: {
      go_live_date: string;
      senior_management_body?: string | null;
      senior_management_decided_on?: string | null;
    },
  ): Promise<{ data: AiReleaseApprovalRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/versions/${encodeURIComponent(versionId)}/release-approvals`,
      input,
    );
  }

  /** The records of the current version, and the periodic reading of its schedule (cluster B, G7). */
  async listAiSecurityTests(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiSecurityTestRecord> & { periodic_test?: AiPeriodicTestReading }> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/security-tests${this.buildQuery(bounds)}`,
    );
  }

  /** Cluster B (UC-VN-59 step 8): the period and last test date of the current version's schedule. */
  async setAiSecurityTestSchedule(
    profileId: string,
    input: { period_months: number; last_test_date: string },
  ): Promise<{ data: AiPeriodicTestReading }> {
    return this.put(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/security-test-schedule`,
      input,
    );
  }

  async recordAiSecurityTest(
    profileId: string,
    input: {
      category: AiAttackCategory;
      state: AiSecurityTestState;
      owner_user_id?: string | null;
      separation_policy?: AiSeparationPolicy;
      separation_basis?: string | null;
      method?: string | null;
      tester?: string | null;
      test_date?: string | null;
      result?: string | null;
      artefact_reference?: string | null;
      personal_data_held?: boolean | null;
      not_applicable_reason?: string | null;
      not_mandatory_reason?: string | null;
    },
  ): Promise<{ data: AiSecurityTestRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/security-tests`,
      input,
    );
  }

  async recordAiSecurityTestState(
    profileId: string,
    category: AiAttackCategory,
    input: {
      state: AiSecurityTestState;
      owner_user_id?: string | null;
      separation_policy?: AiSeparationPolicy;
      separation_basis?: string | null;
      method?: string | null;
      tester?: string | null;
      test_date?: string | null;
      result?: string | null;
      artefact_reference?: string | null;
      personal_data_held?: boolean | null;
      not_applicable_reason?: string | null;
      not_mandatory_reason?: string | null;
      finding_actions?: Array<Record<string, unknown>>;
    },
  ): Promise<{ data: AiSecurityTestRecord }> {
    return this.put(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/security-tests/${encodeURIComponent(category)}`,
      input,
    );
  }

  async listAiValidations(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiValidationRecord>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/validations${this.buildQuery(bounds)}`,
    );
  }

  /** Opens a validation (G6). Nothing is signed here: the record is `open` until `signAiValidation`. */
  async openAiValidation(
    profileId: string,
    input: {
      version_id?: string | null;
      scope: string;
      tests?: Array<{
        test_area: AiValidationTestArea;
        name: string;
        method: string;
        data: string;
        result: string;
      }>;
      validator_user_id: string;
      validator_unit: string;
      independence_statement: string;
    },
  ): Promise<{ data: AiValidationRecord }> {
    return this.post(`/ai-governance/systems/${encodeURIComponent(profileId)}/validations`, input);
  }

  /** Signs an open validation. Refused with no test on the record. */
  async signAiValidation(
    profileId: string,
    validationId: string,
    input: {
      tests?: Array<{
        test_area: AiValidationTestArea;
        name: string;
        method: string;
        data: string;
        result: string;
      }>;
    } = {},
  ): Promise<{ data: AiValidationRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/validations/${encodeURIComponent(validationId)}/sign`,
      input,
    );
  }

  /** The finding's owner writes the response (a write, not a validation). */
  async respondAiValidationFinding(
    profileId: string,
    validationId: string,
    findingId: string,
    input: { response: string },
  ): Promise<{ data: AiValidationRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/validations/${encodeURIComponent(validationId)}/findings/${encodeURIComponent(findingId)}/response`,
      input,
    );
  }

  /** The release gate for one version, read without writing. */
  async getAiReleaseGate(
    profileId: string,
    versionId: string,
  ): Promise<{ data: AiReleaseGateReading }> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/versions/${encodeURIComponent(versionId)}/release-gate`,
    );
  }

  async recordAiValidationFinding(
    profileId: string,
    validationId: string,
    input:
      | { action: 'open'; description: string; owner_user_id: string; due_date: string }
      | { action: 'close'; finding_id: string; retest_date?: string | null; retest_result: string },
  ): Promise<{ data: AiValidationRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/validations/${encodeURIComponent(validationId)}/findings`,
      input,
    );
  }

  async setAiMonitoringThresholds(
    profileId: string,
    input: { thresholds: Array<{ metric_code: string; max_absolute_deviation: number }> },
  ): Promise<{ data: unknown }> {
    return this.put(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/monitoring-thresholds`,
      input,
    );
  }

  async listAiMonitoringComparisons(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiMonitoringComparison>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/monitoring-comparisons${this.buildQuery(bounds)}`,
    );
  }

  async recordAiMonitoringComparison(
    profileId: string,
    input: {
      metric_code: string;
      measured_on: string;
      predicted_value: number;
      actual_value: number;
      source: AiMonitoringSource;
    },
  ): Promise<{ data: unknown }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/monitoring-comparisons`,
      input,
    );
  }

  async listAiMonitoringEvents(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiMonitoringEventRecord & { reading: AiMonitoringEventReading }>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/monitoring-events${this.buildQuery(bounds)}`,
    );
  }

  async recordAiMonitoringEvent(
    profileId: string,
    input: { kind: AiMonitoringEventKind; description: string; metric_code?: string | null },
  ): Promise<{ data: AiMonitoringEventRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/monitoring-events`,
      input,
    );
  }

  async closeAiMonitoringEvent(
    profileId: string,
    eventId: string,
    input: { response: string },
  ): Promise<{ data: AiMonitoringEventRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/monitoring-events/${encodeURIComponent(eventId)}/close`,
      input,
    );
  }

  async listAiDutyHolders(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiDutyHolderRow>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/duty-holders${this.buildQuery(bounds)}`,
    );
  }

  async addAiDutyHolder(
    profileId: string,
    input: { user_id: string; duty: AiDutyKind; training_assignment_id?: string | null },
  ): Promise<{ data: unknown }> {
    return this.post(`/ai-governance/systems/${encodeURIComponent(profileId)}/duty-holders`, input);
  }

  async recordAiCompetenceReview(
    profileId: string,
    dutyHolderId: string,
    input: { reviewed_on: string; summary: string },
  ): Promise<{ data: unknown }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/duty-holders/${encodeURIComponent(dutyHolderId)}/competence-reviews`,
      input,
    );
  }

  async recordAiCompetenceCapacity(
    profileId: string,
    input: {
      basis: 'unit_builds_or_runs' | 'outsourced';
      capacity_statement?: string | null;
      supplier_name?: string | null;
      supplier_capacity_statement?: string | null;
      supplier_assessed_on?: string | null;
    },
  ): Promise<{ data: unknown }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/competence-capacity`,
      input,
    );
  }

  /** fix-sprint-20261031-1200 F8 — one system's serious-incident records (needs `incident:read` as well). */
  async listAiIncidentRecords(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<
    PaginatedResponse<{
      _id: string;
      record_id: string;
      incident_id: string;
      reference_number: string | null;
      incident_title: string | null;
      serious: boolean | null;
      recorded_at: string;
    }>
  > {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/incident-records${this.buildQuery(bounds)}`,
    );
  }

  async getAiIncidentRecord(incidentId: string): Promise<{ data: AiIncidentRecord | null }> {
    return this.get(`/ai-governance/incidents/${encodeURIComponent(incidentId)}/ai-record`);
  }

  async recordAiIncidentRecord(
    incidentId: string,
    input: {
      serious?: boolean;
      serious_basis?: string;
      remediation?: { description: string };
      completion_report?: {
        cause: string;
        damage: string;
        remediation: string;
        completed_on: string;
      };
      state_report?: {
        authority: 'competent_state_authority' | 'state_bank_it_department';
        reported_on: string;
        reference: string;
      };
    },
  ): Promise<{ data: AiIncidentRecord }> {
    return this.put(`/ai-governance/incidents/${encodeURIComponent(incidentId)}/ai-record`, input);
  }

  async listAiDisclosures(
    profileId: string,
    params?: { on?: string; kind?: string; product?: string; channel_id?: string },
  ): Promise<PaginatedResponse<AiDisclosureRecord> | { data: AiDisclosureRecord | null }> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/disclosures${this.buildQuery(params)}`,
    );
  }

  /** The versions awaiting a second person's approval (UC-VN-61 step 2). */
  async listAiPendingDisclosures(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiDisclosureRecord>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/disclosures/pending${this.buildQuery(bounds)}`,
    );
  }

  /** A second person approves a disclosure version; it is in use from its go-live day (UC-VN-61 step 2). The recorder is refused (403). */
  async approveAiDisclosure(
    profileId: string,
    disclosureId: string,
  ): Promise<{ data: AiDisclosureRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/disclosures/${encodeURIComponent(disclosureId)}/approve`,
      {},
    );
  }

  async recordAiDisclosure(
    profileId: string,
    input: {
      kind: 'pre_service_information' | 'ai_interaction_statement' | 'internal_user_information';
      product: string;
      channel_id: string;
      go_live_date: string;
      same_day_reason?: string | null;
      text?: string | null;
      internal_user_information?: {
        purpose: string;
        scope: string;
        limits: string;
        interpretation_guidance: string;
        human_intervention_route: string;
        feedback_channel: string;
      } | null;
    },
  ): Promise<{ data: AiDisclosureRecord }> {
    return this.put(`/ai-governance/systems/${encodeURIComponent(profileId)}/disclosures`, input);
  }

  /**
   * Each complaint with its decision's basis reading, derived on read (UC-VN-62 step 2); its reason (F8, `not_recorded`
   * on an older row) and its explanations with their stages (F37).
   */
  async listAiComplaints(): Promise<
    PaginatedResponse<
      Omit<AiComplaintRecord, 'complaint_reason' | 'explanations'> & {
        basis_reading: string | null;
        complaint_reason: AiComplaintReasonReading;
        explanations: AiComplaintExplanation[];
      }
    >
  > {
    return this.get('/ai-governance/complaints');
  }

  /**
   * F8 — complaints by system and product over a period (`YYYY-MM`; default the six months ending this month), each
   * against the threshold over its window, with the organisation's pattern responses.
   */
  async listAiComplaintCounts(period: { from?: string; to?: string } = {}): Promise<
    PaginatedResponse<AiComplaintCount> & {
      period: { from: string; to: string };
      pattern_responses: AiComplaintPatternResponse[];
    }
  > {
    return this.get(`/ai-governance/complaints/counts${this.buildQuery(period)}`);
  }

  /** F8 — the complaint trend by month, through UnifiedExportService. Download with `downloadPlatformFile`. */
  async exportAiComplaintTrend(
    params: { from?: string; to?: string; format?: 'csv' | 'xlsx' | 'pdf' } = {},
  ): Promise<AttachmentRef> {
    return this.get(
      `/ai-governance/complaints/counts${this.buildQuery({ format: 'csv', ...params })}`,
    );
  }

  /** F8 — the organisation's response to a pattern of complaints, by one of the threshold task's addressees. */
  async recordAiComplaintPatternResponse(input: {
    ai_system_profile_id: string;
    product: string;
    response_kind: AiComplaintPatternResponse['response_kind'];
    linked_record_id?: string | null;
    response_summary: string;
    responded_on: string;
  }): Promise<{ data: AiComplaintPatternResponse }> {
    return this.post('/ai-governance/complaints/pattern-responses', input);
  }

  /** F37 — an explanation delivered after the re-review, by the routed reviewer, before closure. */
  async recordAiComplaintExplanation(
    complaintId: string,
    input: { text: string; delivered_on: string; channel: string },
  ): Promise<{ data: AiComplaintRecord | AiComplaintWriteSummary }> {
    return this.post(
      `/ai-governance/complaints/${encodeURIComponent(complaintId)}/explanations`,
      input,
    );
  }

  /**
   * fix-sprint-20261031-1200 F23 — the complaint writes answer the full record only to an `ai_governance:read`
   * holder; anyone else gets `AiComplaintWriteSummary`. The register pages reload the register after a write.
   */
  async createAiComplaint(input: {
    ai_system_profile_id?: string | null;
    decision_record_id?: string | null;
    product: string;
    decision_category: string;
    /** F8 — required: one of `AI_COMPLAINT_REASONS`. */
    complaint_reason: string;
    received_on: string;
    complaint_summary: string;
    decision_explanation?: string | null;
    decision_explanation_delivered_on?: string | null;
    decision_explanation_channel?: string | null;
    stated_reason?: string | null;
  }): Promise<{ data: AiComplaintRecord | AiComplaintWriteSummary }> {
    return this.post('/ai-governance/complaints', input);
  }

  async routeAiComplaint(
    complaintId: string,
    input: { reviewer_user_id: string },
  ): Promise<{ data: AiComplaintRecord | AiComplaintWriteSummary }> {
    return this.post(`/ai-governance/complaints/${encodeURIComponent(complaintId)}/route`, input);
  }

  async withholdAiComplaintItem(
    complaintId: string,
    input: {
      item: string;
      reason: string;
      category: (typeof AI_WITHHELD_ITEM_CATEGORIES)[number];
    },
  ): Promise<{ data: AiComplaintRecord | AiComplaintWriteSummary }> {
    return this.post(
      `/ai-governance/complaints/${encodeURIComponent(complaintId)}/withheld-items`,
      input,
    );
  }

  async reReviewAiComplaint(
    complaintId: string,
    input: { outcome_summary?: string | null; stated_reason?: string | null },
  ): Promise<{ data: AiComplaintRecord | AiComplaintWriteSummary }> {
    return this.post(
      `/ai-governance/complaints/${encodeURIComponent(complaintId)}/re-review`,
      input,
    );
  }

  /** Closes a re-reviewed complaint, with the customer's notification method and day (UC-VN-62 step 6). */
  async closeAiComplaint(
    complaintId: string,
    input: { customer_notification_method: string; customer_notified_on: string },
  ): Promise<{ data: AiComplaintRecord | AiComplaintWriteSummary }> {
    return this.post(`/ai-governance/complaints/${encodeURIComponent(complaintId)}/close`, input);
  }

  /** The complaint file through UnifiedExportService (UC-VN-62 step 8). Download with `downloadPlatformFile`. */
  async exportAiComplaintFile(
    complaintId: string,
    params: { format?: 'csv' | 'xlsx' | 'pdf' } = {},
  ): Promise<AttachmentRef> {
    return this.get(
      `/ai-governance/complaints/${encodeURIComponent(complaintId)}/export${this.buildQuery({ format: 'csv', ...params })}`,
    );
  }

  async getAiComplaintThreshold(): Promise<{ data: AiComplaintThresholdSetting | null }> {
    return this.get('/ai-governance/complaint-threshold');
  }

  async setAiComplaintThreshold(input: {
    count: number;
    second_line_user_id?: string | null;
    /** F8 — the window in months (1–12); null counts every complaint recorded. */
    window_months?: number | null;
  }): Promise<{ data: AiComplaintThresholdSetting }> {
    return this.put('/ai-governance/complaint-threshold', input);
  }

  async listAiDecisions(
    profileId: string,
    bounds?: { page: string; page_size: string },
  ): Promise<PaginatedResponse<AiDecisionRecord>> {
    return this.get(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/decisions${this.buildQuery(bounds)}`,
    );
  }

  async recordAiDecision(
    profileId: string,
    input: Record<string, unknown>,
  ): Promise<{ data: AiDecisionRecord }> {
    return this.post(`/ai-governance/systems/${encodeURIComponent(profileId)}/decisions`, input);
  }

  async listAiSupplierArrangements(
    vendorId: string,
    params?: { period?: string },
  ): Promise<PaginatedResponse<AiSupplierArrangementRecord & AiSupplierArrangementReadings>> {
    return this.get(
      `/ai-governance/suppliers/${encodeURIComponent(vendorId)}/arrangements${this.buildQuery(params)}`,
    );
  }

  /**
   * Fix-sprint-20261031-1200 F7 — the supplier record and components for one period (`YYYY` or `YYYY-MM`), through
   * UnifiedExportService. The same GET as the list, with `format`. Download with `downloadPlatformFile`.
   */
  async exportAiSupplierRecord(
    vendorId: string,
    params: { period: string; format?: 'csv' | 'xlsx' | 'pdf' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/ai-governance/suppliers/${encodeURIComponent(vendorId)}/arrangements${this.buildQuery({ format: 'csv', ...params })}`,
    );
  }

  async recordAiSupplierArrangement(
    vendorId: string,
    input: {
      ai_system_profile_ids: string[];
      period_start: string;
      period_end: string;
      clauses?: Partial<
        Record<
          (typeof AI_SUPPLIER_CLAUSE_KEYS)[number],
          { state?: 'present' | 'absent' | 'pending' | null; contract_reference?: string | null }
        >
      >;
      assurance_report?: {
        reference: string;
        period_start: string;
        period_end: string;
        expires_on: string;
        warn_days_before?: number;
      } | null;
      training_data?: {
        position: 'commitment' | 'consent' | 'not_recorded';
        scope?: string | null;
        /** Fix-sprint-20261031-1200 F35 — required for a consent. */
        written_consent_reference?: string | null;
        consent_basis?: string | null;
      };
      vendor_governance_user_id?: string | null;
      review_cycle_months?: number | null;
    },
  ): Promise<{ data: AiSupplierArrangementRecord }> {
    return this.post(
      `/ai-governance/suppliers/${encodeURIComponent(vendorId)}/arrangements`,
      input,
    );
  }

  /**
   * Walk cluster F: one event of an arrangement's log. The route is the same for every kind; a body with no `kind`
   * is a model-version report, as before.
   */
  async recordAiSupplierEvent(
    vendorId: string,
    input:
      | {
          kind: 'model_version';
          arrangement_id: string;
          model_version: string;
          supplier_reported_on: string;
          affected_categories?: AiAttackCategory[];
          revalidation?: AiRevalidationDecisionInput;
        }
      | {
          kind: 'sub_processor_change';
          arrangement_id: string;
          sub_processor: string;
          component_ref?: string | null;
          changed_on: string;
          summary: string;
        }
      | { kind: 'termination'; arrangement_id: string; effective_on: string; reason: string }
      | {
          kind: 'regulator_request';
          arrangement_id: string;
          request_reference: string;
          received_on: string;
          summary: string;
        }
      | {
          kind: 'regulator_contact';
          arrangement_id: string;
          contact_user_id: string;
          route: string;
        }
      | { kind: 'continuity_test'; arrangement_id: string; tested_on: string; outcome: string }
      | {
          kind: 'monitoring_access_review';
          arrangement_id: string;
          reviewed_on: string;
          outcome: string;
        }
      | { kind: 'review'; arrangement_id: string; reviewed_on: string; outcome: string },
  ): Promise<{ data: unknown }> {
    return this.post(
      `/ai-governance/suppliers/${encodeURIComponent(vendorId)}/version-reports`,
      input,
    );
  }

  async getAiComponentRegister(profileId: string): Promise<{
    data: {
      snapshot_no: number;
      entered_on: string | null;
      rows: Array<
        AiComponentRow & { missing_fields: string[]; reading: 'complete' | 'incomplete' }
      >;
      incomplete_count: number;
      integrity_records: AiComponentIntegrityRecord[];
      advisories: AiComponentAdvisoryRecord[];
      supply_chain_review: {
        reading: 'open' | 'overdue' | 'cadence_not_declared' | 'not_required' | 'not_judged';
        anchor_date: string | null;
        due_on: string | null;
      };
    };
  }> {
    return this.get(`/ai-governance/systems/${encodeURIComponent(profileId)}/components`);
  }

  async recordAiComponentRegister(
    profileId: string,
    input: {
      rows: Array<{
        kind: (typeof AI_COMPONENT_KINDS)[number];
        name?: string | null;
        supplier_or_developer?: string | null;
        version?: string | null;
        licence?: string | null;
        integrity_check_value?: string | null;
        origin?: string | null;
        affected_attack_categories?: AiAttackCategory[];
      }>;
    },
  ): Promise<{ data: AiComponentSnapshot }> {
    return this.put(`/ai-governance/systems/${encodeURIComponent(profileId)}/components`, input);
  }

  async recordAiComponentIntegrity(
    profileId: string,
    input: {
      component_id: string;
      method: string;
      integrity_check_value: string;
      checked_on: string;
    },
  ): Promise<{ data: AiComponentIntegrityRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/components/integrity`,
      input,
    );
  }

  async recordAiComponentAdvisory(
    profileId: string,
    input: {
      advisory_ref: string;
      received_on: string;
      risk_decision: string;
      component_id?: string | null;
    },
  ): Promise<{ data: AiComponentAdvisoryRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/components/advisories`,
      input,
    );
  }

  async recordAiSupplyChainReview(
    profileId: string,
    input: { summary: string },
  ): Promise<{ data: AiSupplyChainReviewRecord }> {
    return this.post(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/supply-chain-reviews`,
      input,
    );
  }

  async getAiDataProvenance(profileId: string): Promise<{ data: AiDataProvenanceRecord | null }> {
    return this.get(`/ai-governance/systems/${encodeURIComponent(profileId)}/data-provenance`);
  }

  async recordAiDataProvenance(
    profileId: string,
    input: Record<string, unknown>,
  ): Promise<{ data: AiDataProvenanceRecord }> {
    return this.put(
      `/ai-governance/systems/${encodeURIComponent(profileId)}/data-provenance`,
      input,
    );
  }

  async listAiFrameworkRecords(): Promise<PaginatedResponse<AiRiskFrameworkRecord>> {
    return this.get('/ai-governance/framework-records');
  }

  async recordAiFrameworkRecord(input: {
    title: string;
    approver_body: string;
    approver_user_id: string;
    approved_on: string;
    risk_types: Array<{
      risk_type_code: string;
      definition: string;
      thresholds: string;
      controls: string;
    }>;
  }): Promise<{ data: AiRiskFrameworkRecord }> {
    return this.post('/ai-governance/framework-records', input);
  }

  async listAiThreeLines(): Promise<
    PaginatedResponse<
      AiThreeLinesMap & {
        status: AiThreeLinesReading;
        incomplete_lines: AiThreeLineCode[];
        /** Fix-sprint-20261031-1200 F6 — each line's reading, and each person named on two or more lines. */
        line_readings?: AiThreeLineReadingEntry[];
        shared_holders?: AiThreeLinesSharedHolder[];
      }
    >
  > {
    return this.get('/ai-governance/three-lines');
  }

  /** Fix-sprint-20261031-1200 F6 — the map records units and holders only; each line is attested on its own route. */
  async recordAiThreeLines(input: {
    lines: Array<{
      line: AiThreeLineCode;
      unit: string;
      holders: Array<{
        user_id: string;
        role: string;
        persona?: 'P-VN-42' | 'P-VN-07' | null;
      }>;
    }>;
  }): Promise<{ data: AiThreeLinesMap & { status: AiThreeLinesReading } }> {
    return this.post('/ai-governance/three-lines', input);
  }

  /** Fix-sprint-20261031-1200 F6 — one line of the current map, attested by one of its own holders. */
  async attestAiThreeLine(
    mapId: string,
    line: AiThreeLineCode,
    statement: string,
  ): Promise<{ data: AiThreeLinesMap & { status: AiThreeLinesReading } }> {
    return this.post(
      `/ai-governance/three-lines/${encodeURIComponent(mapId)}/lines/${encodeURIComponent(line)}/attestation`,
      { statement },
    );
  }

  /**
   * The AI figures the board report carries (its `ai_governance` block), read as
   * of now by a read-only route. Not `POST /governance/board-report`: that
   * gathers every module's metrics and writes a report-generated audit entry, so
   * reading it to show the page recorded a report nobody drew.
   */
  /** fix-sprint-20261031-1200 F1 — `window` (calendar days) is the report's period; absent, the server's default. */
  async readAiBoardFigures(window?: {
    from: string;
    to: string;
  }): Promise<{ data?: AiRegisterFigures; as_of?: string }> {
    return this.get(`/ai-governance/board-figures${window ? this.buildQuery(window) : ''}`);
  }

  // ── Sprint 62.4.0: regulatory classification ───────────────────

  /**
   * Data-asset and information-system classification.
   *
   * Every path below was traced from `modules/regulatory-classification/
   * routes.ts` at kickoff (R-26) and the table is in the sprint work file. The
   * envelopes are the generic pair — `{ data }` for one entity, `{ data, total,
   * page, page_size }` for a list — because that is what the routes emit, not
   * because the module name suggested it (R-23).
   *
   * **`getClassificationSchemeCatalogue` is the exception, and deliberately
   * so.** It returns one object holding two arrays, not a page of schemes:
   * a catalogue carries its own diagnostics and has no `page` that would mean
   * anything. A caller must unwrap it with `unwrapSingleResponse`;
   * `unwrapResponse` would return `[]` for every tenant, because `data` is an
   * object.
   *
   * Note the review path: `/assessments/:assessmentId/review`, not nested under
   * the profile. A review acts on an assessment, and routing it through the
   * profile would give the handler two sources for one fact.
   */
  async getClassificationSchemeCatalogue(params?: { kind?: string }): Promise<{
    data: ClassificationSchemeCatalogueResponse & {
      diagnostics: Array<{
        framework_code: string;
        scheme_id: string | null;
        reason: string;
        detail: string;
      }>;
    };
  }> {
    return this.get(`/regulatory-classification/schemes${this.buildQuery(params)}`);
  }

  async listClassificationProfiles(params?: {
    page?: number;
    page_size?: number;
    subject_kind?: string;
    current_tier_code?: string;
    status?: string;
  }): Promise<{
    data: ClassificationSubjectProfile[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/regulatory-classification/profiles${this.buildQuery(params)}`);
  }

  /**
   * Subjects whose classification in force was decided against a list version
   * that is no longer installed.
   *
   * Unpaginated by design — it is a work queue whose length is the answer, and a
   * page boundary through it makes "how much is outstanding?" unanswerable from
   * one request. The envelope is still `{ data, total, page, page_size }` so
   * `extractPaginationMeta` reads it like every other list.
   */
  async listClassificationReassessments(): Promise<{
    data: ClassificationReassessmentRow[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get('/regulatory-classification/reassessments');
  }

  async createClassificationProfile(
    input: CreateClassificationSubjectProfileRequest,
  ): Promise<{ data: ClassificationSubjectProfile }> {
    return this.post('/regulatory-classification/profiles', input);
  }

  async getClassificationProfile(
    profileId: string,
  ): Promise<{ data: ClassificationSubjectProfile }> {
    return this.get(`/regulatory-classification/profiles/${encodeURIComponent(profileId)}`);
  }

  async updateClassificationProfile(
    profileId: string,
    input: UpdateClassificationSubjectProfileRequest,
  ): Promise<{ data: ClassificationSubjectProfile }> {
    return this.patch(
      `/regulatory-classification/profiles/${encodeURIComponent(profileId)}`,
      input,
    );
  }

  async listClassificationAssessments(profileId: string): Promise<{
    data: ClassificationAssessment[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(
      `/regulatory-classification/profiles/${encodeURIComponent(profileId)}/assessments`,
    );
  }

  async createClassificationAssessment(
    profileId: string,
    input: CreateClassificationAssessmentRequest,
  ): Promise<{ data: ClassificationAssessment }> {
    return this.post(
      `/regulatory-classification/profiles/${encodeURIComponent(profileId)}/assessments`,
      input,
    );
  }

  async getClassificationAssessment(
    assessmentId: string,
  ): Promise<{ data: ClassificationAssessment }> {
    return this.get(`/regulatory-classification/assessments/${encodeURIComponent(assessmentId)}`);
  }

  async reviewClassificationAssessment(
    assessmentId: string,
    input: ReviewClassificationAssessmentRequest,
  ): Promise<{
    data: {
      assessment: ClassificationAssessment;
      profile: ClassificationSubjectProfile;
      measures: {
        created: number;
        existing: number;
        not_applicable: number;
        undeclared_categories: string[];
      } | null;
      legacy_data_tier_written: string | null;
      superseded_by_race: string | null;
    };
  }> {
    return this.post(
      `/regulatory-classification/assessments/${encodeURIComponent(assessmentId)}/review`,
      input,
    );
  }

  async listProtectiveMeasures(profileId: string): Promise<{
    data: ProtectiveMeasureRecord[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(
      `/regulatory-classification/profiles/${encodeURIComponent(profileId)}/measures`,
    );
  }

  async updateProtectiveMeasure(
    measureId: string,
    input: UpdateProtectiveMeasureRequest,
  ): Promise<{ data: ProtectiveMeasureRecord }> {
    return this.patch(
      `/regulatory-classification/measures/${encodeURIComponent(measureId)}`,
      input,
    );
  }

  /**
   * The tests recorded against one protective measure — Sprint 65.7.0.
   *
   * Traced from `modules/regulatory-classification/routes.ts` (R-26): the POST
   * answers `201` and the GET is **unpaginated by design**, because a page
   * boundary through one control's history makes "is this the whole history?"
   * unanswerable from one request. The envelope is still the list pair, so
   * `extractPaginationMeta` reads it like every other list (R-23).
   *
   * `performed_by_user_id` is absent from the request type on purpose: the
   * server takes the actor from the session, so a test cannot be attributed to
   * someone who did not perform it.
   */
  async listProtectiveMeasureTests(measureId: string): Promise<{
    data: ProtectiveMeasureTest[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/regulatory-classification/measures/${encodeURIComponent(measureId)}/tests`);
  }

  async recordProtectiveMeasureTest(
    measureId: string,
    input: RecordProtectiveMeasureTestRequest,
  ): Promise<{ data: ProtectiveMeasureTest }> {
    return this.post(
      `/regulatory-classification/measures/${encodeURIComponent(measureId)}/tests`,
      input,
    );
  }

  async listRegulatedOfferings(params?: {
    page?: number;
    page_size?: number;
    kind?: string;
    status?: string;
  }): Promise<{
    data: RegulatedOfferingRegistration[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/regulatory-classification/offerings${this.buildQuery(params)}`);
  }

  /**
   * `obligations` carries the two identifying fields only.
   *
   * Gate E12.5 F11 — `requirement_title` is derived server-side from the
   * installed framework's own requirement and is **rejected** in the request
   * body, so a caller that still sends one gets a 400 rather than believing it
   * set the text an offering displays as its obligation.
   */
  async createRegulatedOffering(
    input: RegulatedOfferingWriteRequest,
  ): Promise<{ data: RegulatedOfferingRegistration }> {
    return this.post('/regulatory-classification/offerings', input);
  }

  async getRegulatedOffering(offeringId: string): Promise<{ data: RegulatedOfferingRegistration }> {
    return this.get(`/regulatory-classification/offerings/${encodeURIComponent(offeringId)}`);
  }

  async updateRegulatedOffering(
    offeringId: string,
    input: RegulatedOfferingWriteRequest,
  ): Promise<{ data: RegulatedOfferingRegistration }> {
    return this.patch(
      `/regulatory-classification/offerings/${encodeURIComponent(offeringId)}`,
      input,
    );
  }

  /**
   * Assemble the joined evidence for one classified subject.
   *
   * Requires `regulatory_classification:read` **and**
   * `framework_evidence_pack:create` — the route uses `requireAllPermissions`,
   * because it composes 62.2.2's assembler and mints an immutable hash-bearing
   * artefact. The page gates the control on the second permission for that
   * reason.
   */
  async exportClassificationEvidence(profileId: string): Promise<{
    data: {
      subject_profile_id: string;
      root_framework_code: string;
      scheme_id: string | null;
      assessment_id: string | null;
      tier_code: string | null;
    };
  }> {
    return this.post(
      `/regulatory-classification/profiles/${encodeURIComponent(profileId)}/export`,
      {},
    );
  }

  // ── Sprint 62.20.0: linked classification schemes, screening inputs,
  //    placeholders ────────────────────────────────────────────────────
  /**
   * Every path below was traced from the module routers at kickoff (R-26).
   *
   * `setClassificationSchemeEntry` is a SECOND typed body over the existing
   * profile PATCH rather than a widening of `updateClassificationProfile`.
   * The two writes mean different things: one edits the subject record, the
   * other adds or removes ONE scheme's entry and leaves every other scheme's
   * untouched. Sharing one loose body would let a caller send both in a
   * single request and make "which write happened" unanswerable from the
   * audit entry.
   *
   * **It carries no tier, and cannot.** A tier in force is written by review;
   * a `:write`-only patch that could set one would make the review gate
   * decorative, and the route refuses such a body by name.
   */
  async setClassificationSchemeEntry(
    profileId: string,
    classification: SetSubjectClassificationRequest,
  ): Promise<{ data: ClassificationSubjectProfile }> {
    return this.patch(`/regulatory-classification/profiles/${encodeURIComponent(profileId)}`, {
      classification,
    });
  }

  /**
   * Record the correspondence a person states between the levels.
   *
   * `PUT`, not `POST`: the mapping is one block on the profile and a second
   * statement replaces the first rather than accumulating beside it. The
   * server stamps `recorded_at` and `recorded_by`; sending them from here
   * would let a client date its own record.
   */
  async putClassificationCrossMapping(
    profileId: string,
    input: RecordSchemeCrossMappingRequest,
  ): Promise<{ data: ClassificationSubjectProfile }> {
    return this.put(
      `/regulatory-classification/profiles/${encodeURIComponent(profileId)}/cross-mapping`,
      input,
    );
  }

  /**
   * The installed frameworks' own statements about draft status and about
   * what a scheme's levels are NOT a determination of (AC2).
   *
   * The tag is a member of the module's own declared set, checked server-side,
   * so the platform reads exactly the statements it declared it would read and
   * never an arbitrary tag query.
   */
  async listClassificationBoundaryStatements(
    tag: ClassificationGuidanceTag,
  ): Promise<{ data: BoundaryStatement[] }> {
    return this.get(
      `/regulatory-classification/boundary-statements?tag=${encodeURIComponent(tag)}`,
    );
  }

  /**
   * The upward-reclassification prompts the installed content declares.
   *
   * `[]` is the honest answer when nothing declares one — never a coded
   * fallback prompt, which would put a platform-authored reclassification
   * question in front of an operator with no instrument behind it.
   */
  async listClassificationAggregationPrompts(): Promise<{
    data: AggregationPromptCatalogueResponse;
    declaration_absence?: DeclarationAbsence | null;
  }> {
    return this.get('/regulatory-classification/aggregation-prompts');
  }

  /**
   * Record the human decision on a prompt. The level moves only on
   * `accepted`, and only the server moves it. `:review`, not `:write`.
   */
  async recordClassificationAggregationReview(
    profileId: string,
    input: RecordAggregationReviewRequest,
  ): Promise<{ data: ClassificationSubjectProfile }> {
    return this.post(
      `/regulatory-classification/profiles/${encodeURIComponent(profileId)}/aggregation-review`,
      input,
    );
  }

  /**
   * Sprint 62.26.0 (AC7–AC9) — the pack-declared transitional windows this
   * subject's classifications sit in, each with what the server read to date
   * it. `:read`. The server raises the duty a window asks for on this read,
   * idempotently, and writes nothing to the profile.
   */
  async getClassificationTransitionalWindows(
    profileId: string,
  ): Promise<{ data: SubjectTransitionalWindowsResponse }> {
    return this.get(
      `/regulatory-classification/profiles/${encodeURIComponent(profileId)}/transitional-windows`,
    );
  }

  /**
   * Sprint 62.26.0 (AC9) — a person retires a transitional classification
   * entry with a reason and a date. `:write`. The server stamps who and when;
   * the entry itself stays on the profile, readable and exportable.
   */
  async recordClassificationTransitionalRetirement(
    profileId: string,
    input: RecordTransitionalRetirementRequest,
  ): Promise<{ data: ClassificationSubjectProfile }> {
    return this.post(
      `/regulatory-classification/profiles/${encodeURIComponent(profileId)}/transitional-retirement`,
      input,
    );
  }

  async getClassificationSubjectProfileAdvisorSummary(profileId: string): Promise<AdvisorSummary> {
    return this.get(
      `/regulatory-classification/profiles/${encodeURIComponent(profileId)}/advisor-summary`,
    );
  }

  /**
   * The inputs a risk-tiered cross-border control would read, as the
   * installed content declares them, plus that content's own statement.
   *
   * `control_active` is carried rather than assumed: the screen states what
   * the server says, so the sentence cannot go stale against a later sprint
   * that turns a rule on.
   */
  async getTransferScreeningInputs(): Promise<{
    data: TransferScreeningInputsResponse;
    declaration_absence?: DeclarationAbsence | null;
  }> {
    return this.get('/assessments/transfers/screening-inputs');
  }

  /** The kinds of placeholder the installed content declares. `[]` is honest. */
  async listRegulatoryPlaceholderKinds(): Promise<{
    data: PlaceholderKindCatalogueResponse;
    declaration_absence?: DeclarationAbsence | null;
    /**
     * Gate E12.5 pass 3 (P2-1) — `data`'s shape over EVERY installed declaration,
     * whatever its install mode. Label a record that exists from it; offer only
     * from `data` (reference mode takes away application, never content).
     */
    content?: PlaceholderKindCatalogueResponse;
  }> {
    return this.get('/regulatory-placeholders/kinds');
  }

  async listRegulatoryPlaceholders(params?: {
    page?: number;
    page_size?: number;
    kind?: string;
  }): Promise<{
    data: RegulatoryPlaceholderRecord[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/regulatory-placeholders${this.buildQuery(params)}`);
  }

  async createRegulatoryPlaceholder(
    input: CreateRegulatoryPlaceholderRequest,
  ): Promise<{ data: RegulatoryPlaceholderRecord }> {
    return this.post('/regulatory-placeholders', input);
  }

  async getRegulatoryPlaceholder(id: string): Promise<{ data: RegulatoryPlaceholderRecord }> {
    return this.get(`/regulatory-placeholders/${encodeURIComponent(id)}`);
  }

  async updateRegulatoryPlaceholder(
    id: string,
    input: UpdateRegulatoryPlaceholderRequest,
  ): Promise<{ data: RegulatoryPlaceholderRecord }> {
    return this.patch(`/regulatory-placeholders/${encodeURIComponent(id)}`, input);
  }

  // ── Sprint 62.13.0: penalty exposure + enforcement cases ───────────
  /**
   * Every path below was traced from `modules/penalty-exposure/routes.ts`
   * (R-26). Envelopes: `{ data }` single, `{ data, total, page, page_size }`
   * for the case list; `computeExposure` returns `{ data, inputs_present,
   * schedule_diagnostics }` because the diagnostics are part of the honest
   * answer, not an error.
   */
  async getPenaltyExposureInputs(): Promise<{ data: PenaltyExposureInputs | null }> {
    return this.get('/penalty-exposure/inputs');
  }

  /**
   * `expected_updated_at` — the `updated_at` of the inputs document the page
   * read, or `null` when it read none. A save over a document that moved since
   * is refused with 409 `PENALTY_EXPOSURE_INPUTS_CONFLICT` (gate E12.5 pass 2).
   */
  async upsertPenaltyExposureInputs(input: {
    entity_basis: FinePartyBasis;
    declared_metrics: DeclaredExposureMetric[];
    area_marks: ExposureAreaMark[];
    expected_updated_at: string | null;
  }): Promise<{ data: PenaltyExposureInputs }> {
    return this.put('/penalty-exposure/inputs', input);
  }

  async computePenaltyExposure(): Promise<{
    data: AreaExposureResult[];
    inputs_present: boolean;
    /** The metric vocabulary the installed schedules consume (gate E12.5 finding 3). */
    metric_codes: string[];
    schedule_diagnostics: Array<{ framework_code: string; area_id: string; reason: string }>;
    declaration_absence?: DeclarationAbsence | null;
  }> {
    return this.get('/penalty-exposure/exposure');
  }

  /**
   * Sprint 62.20.0 (AC6) — the response carries a `summary` beside the rows,
   * and `summary.obligation_areas` is the DENOMINATOR.
   *
   * Typed because the page used to compute coverage over `data.length`, which
   * counts a not-yet-binding instrument's fine areas as areas an organisation
   * must be ready for. `summary` is optional so a client running against a
   * server that predates it renders no total rather than a wrong one — never
   * `data.length` silently standing in for it.
   *
   * Gate E12.5 pass 1 — the shape was an inline literal here and a third copy
   * inside `PenaltyReadinessPage`'s `useState`, beside the shared
   * `AreaReadinessSummary` the server already builds against. They had drifted:
   * the page's copy had lost `total_areas`. Each copy type-checked against
   * itself, so nothing reported it. One declaration, imported.
   */
  async computePenaltyReadiness(): Promise<{
    data: AreaReadinessResult[];
    summary?: AreaReadinessSummary;
    declaration_absence?: DeclarationAbsence | null;
  }> {
    return this.get('/penalty-exposure/readiness');
  }

  /*
   * fix-sprint-20260825-2124 F24 — typed as `{ data: AttachmentRef }`, not
   * `{ data: unknown }`. The route replies `201 { data: ref }` and the ref is
   * the ONLY route to the generated file, but `unknown` made discarding it a
   * silent no-op rather than a type error — which is exactly what all five
   * calling pages did: await, throw the ref away, and toast success for a file
   * the click never delivered. With the shape declared, a caller that does not
   * reach for `file_id` is at least visibly choosing to.
   */
  async exportPenaltyExposure(
    format: 'csv' | 'xlsx' | 'pdf' | 'docx',
  ): Promise<{ data: AttachmentRef }> {
    return this.post('/penalty-exposure/export', { format });
  }

  async listEnforcementCases(params?: {
    page?: number;
    page_size?: number;
    stage?: EnforcementCaseStage;
  }): Promise<{ data: EnforcementCase[]; total: number; page: number; page_size: number }> {
    return this.get(`/penalty-exposure/cases${this.buildQuery(params)}`);
  }

  async createEnforcementCase(input: {
    case_ref: string;
    framework_code: string;
    intake: EnforcementCaseIntake;
    linked_areas?: EnforcementLinkedArea[];
    case_metrics?: DeclaredExposureMetric[];
    lex_mitior_note?: string | null;
  }): Promise<{ data: EnforcementCase }> {
    return this.post('/penalty-exposure/cases', input);
  }

  async getEnforcementCase(id: string): Promise<{ data: EnforcementCase }> {
    return this.get(`/penalty-exposure/cases/${encodeURIComponent(id)}`);
  }

  async updateEnforcementCase(
    id: string,
    input: {
      case_ref?: string;
      linked_areas?: EnforcementLinkedArea[];
      case_metrics?: DeclaredExposureMetric[];
      lex_mitior_note?: string | null;
      decision?: EnforcementDecision | null;
    },
  ): Promise<{ data: EnforcementCase }> {
    return this.patch(`/penalty-exposure/cases/${encodeURIComponent(id)}`, input);
  }

  async transitionEnforcementCase(
    id: string,
    to: EnforcementCaseStage,
  ): Promise<{ data: EnforcementCase }> {
    return this.post(`/penalty-exposure/cases/${encodeURIComponent(id)}/transition`, { to });
  }

  async recordEnforcementCaseMeasure(
    id: string,
    input: { description: string; executed_at: string; evidence_refs: string[] },
  ): Promise<{ data: EnforcementCase }> {
    return this.post(`/penalty-exposure/cases/${encodeURIComponent(id)}/measures`, input);
  }

  async closeEnforcementCase(
    id: string,
    input: { outcome: string | null; note: string | null },
  ): Promise<{ data: EnforcementCase }> {
    return this.post(`/penalty-exposure/cases/${encodeURIComponent(id)}/close`, input);
  }

  async getEnforcementCaseLimitation(id: string): Promise<{ data: EnforcementLimitationClock }> {
    return this.get(`/penalty-exposure/cases/${encodeURIComponent(id)}/limitation`);
  }

  async getEnforcementCaseExposure(
    id: string,
  ): Promise<{ data: AreaExposureResult[]; declaration_absence?: DeclarationAbsence | null }> {
    return this.get(`/penalty-exposure/cases/${encodeURIComponent(id)}/exposure`);
  }

  /*
   * fix-sprint-20260825-2124 F24 — typed as `{ data: AttachmentRef }`, not
   * `{ data: unknown }`. The route replies `201 { data: ref }` and the ref is
   * the ONLY route to the generated file, but `unknown` made discarding it a
   * silent no-op rather than a type error — which is exactly what all five
   * calling pages did: await, throw the ref away, and toast success for a file
   * the click never delivered. With the shape declared, a caller that does not
   * reach for `file_id` is at least visibly choosing to.
   */
  async exportEnforcementCases(
    format: 'csv' | 'xlsx' | 'pdf' | 'docx',
  ): Promise<{ data: AttachmentRef }> {
    return this.post('/penalty-exposure/cases/export', { format });
  }

  // ── Sprint 62.14.0: authority directions, legal holds, regulated accounts ──
  async discoverDirectionKinds(): Promise<{
    data: DiscoveredDirectionKind[];
    declaration_absence?: DeclarationAbsence | null;
  }> {
    return this.get('/authority-operations/directions/kinds');
  }
  /**
   * Remediation W (U2, 62.51.0) — each of the three reads below takes an
   * optional direction kind: given one, the server answers only what the
   * frameworks declaring that kind declare, so a direction of one framework is
   * not framed with another framework's classifications or conditions.
   */
  private directionKindQuery(directionKind?: string): string {
    return directionKind ? `?direction_kind=${encodeURIComponent(directionKind)}` : '';
  }
  async discoverAuthorityEventKinds(directionKind?: string): Promise<{
    data: DiscoveredAuthorityEventKind[];
    declaration_absence?: DeclarationAbsence | null;
  }> {
    return this.get(
      `/authority-operations/directions/classification-kinds${this.directionKindQuery(directionKind)}`,
    );
  }
  async listAuthorityConditionSets(directionKind?: string): Promise<{
    data: DeclaredConditionSet[];
    /** Existing attestation labels only; offers and writes use data. */
    content?: DeclaredConditionSet[];
    declaration_absence?: DeclarationAbsence | null;
  }> {
    return this.get(
      `/authority-operations/directions/condition-sets${this.directionKindQuery(directionKind)}`,
    );
  }
  async listAuthorityBoundaryStatements(
    directionKind?: string,
  ): Promise<{ data: BoundaryStatement[] }> {
    return this.get(
      `/authority-operations/directions/boundary-statements${this.directionKindQuery(directionKind)}`,
    );
  }
  async listAuthorityDirections(params?: {
    page?: number;
    page_size?: number;
    status?: string;
  }): Promise<{
    data: AuthorityDirection[];
    total: number;
    page: number;
    page_size: number;
    /** Sprint 63.33.0 — the organisation's IANA timezone, to show instants in. */
    calendar_timezone?: string;
  }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/authority-operations/directions${suffix}`);
  }
  async getAuthorityDirection(id: string): Promise<{ data: AuthorityDirection }> {
    return this.get(`/authority-operations/directions/${encodeURIComponent(id)}`);
  }
  async getAuthorityDirectionTrail(id: string): Promise<{
    data: {
      direction: AuthorityDirection;
      holds: LegalHold[];
      account_events: RegulatedAccountEvent[];
      /** Sprint 63.33.0 — the organisation's IANA timezone, to show instants in. */
      calendar_timezone?: string;
    };
  }> {
    return this.get(`/authority-operations/directions/${encodeURIComponent(id)}/trail`);
  }
  async createAuthorityDirection(body: unknown): Promise<{ data: AuthorityDirection }> {
    return this.post('/authority-operations/directions', body);
  }
  async updateAuthorityDirection(id: string, body: unknown): Promise<{ data: AuthorityDirection }> {
    return this.patch(`/authority-operations/directions/${encodeURIComponent(id)}`, body);
  }
  async addAuthorityDirectionResponse(
    id: string,
    body: unknown,
  ): Promise<{ data: AuthorityDirection }> {
    return this.post(`/authority-operations/directions/${encodeURIComponent(id)}/responses`, body);
  }
  async classifyAuthorityDirection(
    id: string,
    body: unknown,
  ): Promise<{ data: AuthorityDirection }> {
    return this.post(
      `/authority-operations/directions/${encodeURIComponent(id)}/classification`,
      body,
    );
  }
  async attestAuthorityDirectionCondition(
    id: string,
    body: unknown,
  ): Promise<{ data: AuthorityDirection }> {
    return this.post(
      `/authority-operations/directions/${encodeURIComponent(id)}/condition-attestations`,
      body,
    );
  }
  async addAuthorityDirectionRemediationPlan(
    id: string,
    body: unknown,
  ): Promise<{ data: AuthorityDirection }> {
    return this.post(
      `/authority-operations/directions/${encodeURIComponent(id)}/remediation-plans`,
      body,
    );
  }
  async addAuthorityDirectionOutcomeReport(
    id: string,
    body: unknown,
  ): Promise<{ data: AuthorityDirection }> {
    return this.post(
      `/authority-operations/directions/${encodeURIComponent(id)}/outcome-reports`,
      body,
    );
  }
  async addAuthorityDirectionLegalReview(
    id: string,
    body: unknown,
  ): Promise<{ data: AuthorityDirection }> {
    return this.post(
      `/authority-operations/directions/${encodeURIComponent(id)}/legal-reviews`,
      body,
    );
  }
  async releaseAuthorityDirectionData(
    id: string,
    body: unknown,
  ): Promise<{ data: AuthorityDirection }> {
    return this.post(
      `/authority-operations/directions/${encodeURIComponent(id)}/data-releases`,
      body,
    );
  }

  // Sprint 62.22.0 — pack-fed product classification and conformity records.
  async getProductClassificationCatalogue(): Promise<{
    data: { schemes: ProductClassificationCatalogue[] };
  }> {
    return this.get('/product-assurance/classification-catalogue');
  }
  async listProductClassifications(): Promise<{
    data: ProductClassificationRecord[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get('/product-assurance/classifications');
  }
  async createProductClassification(body: unknown): Promise<{ data: ProductClassificationRecord }> {
    return this.post('/product-assurance/classifications', body);
  }
  async getProductAssuranceBoundaryStatements(): Promise<{ data: BoundaryStatement[] }> {
    return this.get('/product-assurance/boundary-statements');
  }
  async getProductConformityCatalogue(): Promise<{
    data: {
      templates: Array<{
        framework_code: string;
        template_id: string;
        title: BilingualText;
        description: BilingualText;
        legal_references: string[];
      }>;
    };
  }> {
    return this.get('/product-assurance/conformity-catalogue');
  }
  async listProductConformities(): Promise<{
    data: ConformityCertificateRecord[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get('/product-assurance/conformities');
  }
  async createProductConformity(body: unknown): Promise<{ data: ConformityCertificateRecord }> {
    return this.post('/product-assurance/conformities', body);
  }
  async closeAuthorityDirection(id: string, body: unknown): Promise<{ data: AuthorityDirection }> {
    return this.post(`/authority-operations/directions/${encodeURIComponent(id)}/close`, body);
  }
  /**
   * Sprint 62.26.0 (OD11) — record the due date the AUTHORITY stated in its
   * request, with the document it was stated in. The server accepts it only
   * where the clock resolves to `no_numeric_clock_declared` and refuses every
   * other state with 409 `AUTHORITY_DIRECTION_STATED_DUE_NOT_ACCEPTED`.
   */
  async recordAuthorityDirectionStatedDue(
    id: string,
    body: { due_at: string; evidence_refs: string[] },
  ): Promise<{ data: AuthorityDirection }> {
    return this.post(
      `/authority-operations/directions/${encodeURIComponent(id)}/authority-stated-due`,
      body,
    );
  }
  /*
   * fix-sprint-20260825-2124 F24 — typed as `{ data: AttachmentRef }`, not
   * `{ data: unknown }`. The route replies `201 { data: ref }` and the ref is
   * the ONLY route to the generated file, but `unknown` made discarding it a
   * silent no-op rather than a type error — which is exactly what all five
   * calling pages did: await, throw the ref away, and toast success for a file
   * the click never delivered. With the shape declared, a caller that does not
   * reach for `file_id` is at least visibly choosing to.
   */
  async exportAuthorityDirectionTrail(
    id: string,
    format: 'csv' | 'xlsx' | 'pdf' | 'docx',
  ): Promise<{ data: AttachmentRef }> {
    return this.post(`/authority-operations/directions/${encodeURIComponent(id)}/export`, {
      format,
    });
  }
  async listLegalHolds(params?: {
    page?: number;
    page_size?: number;
    status?: string;
  }): Promise<{ data: LegalHoldListItem[]; total: number; page: number; page_size: number }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/authority-operations/holds${suffix}`);
  }
  /**
   * The place-a-hold dialog's pickers: items a hold can be placed on, searched by
   * what the operator recognises them by. An empty `search` returns the most
   * recent. Gated on `legal_hold:place`.
   */
  async searchHoldScopeCandidates(
    source: HoldScopeCandidateSource,
    search: string,
    limit?: number,
  ): Promise<{ data: HoldScopeCandidate[]; total: number; page: number; page_size: number }> {
    const qs = new URLSearchParams({ source });
    if (search.trim() !== '') qs.set('search', search.trim());
    if (limit !== undefined) qs.set('limit', String(limit));
    return this.get(`/authority-operations/holds/scope-candidates?${qs.toString()}`);
  }
  async placeLegalHold(body: unknown): Promise<{ data: LegalHold }> {
    return this.post('/authority-operations/holds', body);
  }
  async releaseLegalHold(id: string, body: unknown): Promise<{ data: LegalHold }> {
    return this.post(`/authority-operations/holds/${encodeURIComponent(id)}/release`, body);
  }
  /*
   * fix-sprint-20260825-2124 F24 — typed as `{ data: AttachmentRef }`, not
   * `{ data: unknown }`. The route replies `201 { data: ref }` and the ref is
   * the ONLY route to the generated file, but `unknown` made discarding it a
   * silent no-op rather than a type error — which is exactly what all five
   * calling pages did: await, throw the ref away, and toast success for a file
   * the click never delivered. With the shape declared, a caller that does not
   * reach for `file_id` is at least visibly choosing to.
   */
  async exportLegalHolds(
    format: 'csv' | 'xlsx' | 'pdf' | 'docx',
  ): Promise<{ data: AttachmentRef }> {
    return this.post('/authority-operations/holds/export', { format });
  }
  async listRegulatedAccounts(params?: {
    page?: number;
    page_size?: number;
  }): Promise<{ data: RegulatedAccount[]; total: number; page: number; page_size: number }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/authority-operations/accounts${suffix}`);
  }
  async getRegulatedAccount(id: string): Promise<{ data: RegulatedAccount }> {
    return this.get(`/authority-operations/accounts/${encodeURIComponent(id)}`);
  }
  async createRegulatedAccount(body: unknown): Promise<{ data: RegulatedAccount }> {
    return this.post('/authority-operations/accounts', body);
  }
  async updateRegulatedAccount(id: string, body: unknown): Promise<{ data: RegulatedAccount }> {
    return this.patch(`/authority-operations/accounts/${encodeURIComponent(id)}`, body);
  }
  async listRegulatedAccountEvents(
    id: string,
    params?: { page?: number; page_size?: number; include_superseded?: boolean },
  ): Promise<{ data: RegulatedAccountEvent[]; total: number; page: number; page_size: number }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.include_superseded) qs.set('include_superseded', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/authority-operations/accounts/${encodeURIComponent(id)}/events${suffix}`);
  }
  async appendRegulatedAccountEvent(
    id: string,
    body: unknown,
  ): Promise<{ data: RegulatedAccountEvent }> {
    return this.post(`/authority-operations/accounts/${encodeURIComponent(id)}/events`, body);
  }
  async appendRegulatedAccountEventCorrection(
    eventId: string,
    body: unknown,
  ): Promise<{ data: RegulatedAccountEvent }> {
    return this.post(
      `/authority-operations/account-events/${encodeURIComponent(eventId)}/corrections`,
      body,
    );
  }
  async listRegulatedAccountExpiries(): Promise<{ data: CredentialExpiryObligation[] }> {
    return this.get('/authority-operations/accounts/expiries');
  }
  async listAccountLifecycleWindows(): Promise<{
    data: AccountLifecycleWindow[];
    declaration_absence?: DeclarationAbsence | null;
  }> {
    return this.get('/authority-operations/accounts/windows');
  }
  /*
   * fix-sprint-20260825-2124 F24 — typed as `{ data: AttachmentRef }`, not
   * `{ data: unknown }`. The route replies `201 { data: ref }` and the ref is
   * the ONLY route to the generated file, but `unknown` made discarding it a
   * silent no-op rather than a type error — which is exactly what all five
   * calling pages did: await, throw the ref away, and toast success for a file
   * the click never delivered. With the shape declared, a caller that does not
   * reach for `file_id` is at least visibly choosing to.
   */
  async exportRegulatedAccounts(
    format: 'csv' | 'xlsx' | 'pdf' | 'docx',
  ): Promise<{ data: AttachmentRef }> {
    return this.post('/authority-operations/accounts/export', { format });
  }

  // ── Sprint 62.19.0: sector licences, obligations, boundary statements, marketing-claim gate ──
  async discoverLicenceKinds(): Promise<{
    data: DiscoveredLicenceKind[];
    declaration_absence?: DeclarationAbsence | null;
    /**
     * Gate E12.5 pass 3 (P2-1) — `data`'s shape over EVERY installed declaration,
     * whatever its install mode. Label a record that exists from it; offer only
     * from `data` (reference mode takes away application, never content).
     */
    content?: DiscoveredLicenceKind[];
  }> {
    return this.get('/sector-licensing/licences/kinds');
  }
  /** One row per licence × declared window; `licence_id` narrows to one licence (detail page). */
  async listLicenceObligations(params?: {
    licence_id?: string;
  }): Promise<{ data: LicenceObligation[]; declaration_absence?: DeclarationAbsence | null }> {
    const qs = new URLSearchParams();
    if (params?.licence_id) qs.set('licence_id', params.licence_id);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/sector-licensing/licences/obligations${suffix}`);
  }
  /** Pack guidance selected by one of `BOUNDARY_GUIDANCE_TAGS`; any other tag is a 400. */
  async listBoundaryStatements(tag: BoundaryGuidanceTag): Promise<{ data: BoundaryStatement[] }> {
    return this.get(`/sector-licensing/boundary-statements?tag=${encodeURIComponent(tag)}`);
  }
  async listSectorLicences(params?: {
    page?: number;
    page_size?: number;
    status?: string;
    licence_kind?: string;
    /** Comma-separated, deduplicated, 1–100 record ids (fix-sprint-20261003-0545 F3). */
    ids?: string;
  }): Promise<{ data: SectorLicence[]; total: number; page: number; page_size: number }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.status) qs.set('status', params.status);
    if (params?.licence_kind) qs.set('licence_kind', params.licence_kind);
    if (params?.ids) qs.set('ids', params.ids);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/sector-licensing/licences${suffix}`);
  }
  async createSectorLicence(body: CreateSectorLicenceBody): Promise<{ data: SectorLicence }> {
    return this.post('/sector-licensing/licences', body);
  }
  async getSectorLicence(id: string): Promise<{ data: SectorLicence }> {
    return this.get(`/sector-licensing/licences/${encodeURIComponent(id)}`);
  }
  async updateSectorLicence(
    id: string,
    body: UpdateSectorLicenceBody,
  ): Promise<{ data: SectorLicence }> {
    return this.patch(`/sector-licensing/licences/${encodeURIComponent(id)}`, body);
  }
  // ── Sprint 62.54.0: the fee schedule, applications, fee lines, decisions ──
  /** The installed fee schedule; `declaration_absence` says why it is empty when it is (AC1). */
  async getLicenceFeeSchedule(): Promise<{
    data: RegulatoryFeeSchedule;
    declaration_absence: DeclarationAbsence | null;
  }> {
    return this.get('/sector-licensing/fee-schedule');
  }
  async listLicenceFeeApplications(params?: {
    page?: number;
    page_size?: number;
    procedure?: string;
    licence_id?: string;
    decision?: FeeApplicationDecisionStatus;
  }): Promise<{
    data: LicenceFeeApplicationView[];
    total: number;
    page: number;
    page_size: number;
    declaration_absence: DeclarationAbsence | null;
  }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.procedure) qs.set('procedure', params.procedure);
    if (params?.licence_id) qs.set('licence_id', params.licence_id);
    if (params?.decision) qs.set('decision', params.decision);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/sector-licensing/applications${suffix}`);
  }
  /** Applications owed on filing with no fee line — a missing payment record, never a missing licence (AC5). */
  async listLicenceFeeCompleteness(params?: { page?: number; page_size?: number }): Promise<{
    data: FeeCompletenessRow[];
    total: number;
    page: number;
    page_size: number;
    declaration_absence: DeclarationAbsence | null;
    scan_capped: boolean;
  }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/sector-licensing/applications/completeness${suffix}`);
  }
  async getLicenceFeeApplication(id: string): Promise<{
    data: LicenceFeeApplicationView;
    declaration_absence: DeclarationAbsence | null;
  }> {
    return this.get(`/sector-licensing/applications/${encodeURIComponent(id)}`);
  }
  async createLicenceFeeApplication(
    body: CreateLicenceFeeApplicationBody,
  ): Promise<{ data: LicenceFeeApplicationView }> {
    return this.post('/sector-licensing/applications', body);
  }
  async updateLicenceFeeApplication(
    id: string,
    body: UpdateLicenceFeeApplicationBody,
  ): Promise<{ data: LicenceFeeApplicationView }> {
    return this.patch(`/sector-licensing/applications/${encodeURIComponent(id)}`, body);
  }
  /** The decision route (AC6): `sector_licence_decision:write`. */
  async recordLicenceFeeDecision(
    id: string,
    body: RecordLicenceFeeDecisionBody,
  ): Promise<{ data: LicenceFeeApplicationView }> {
    return this.put(`/sector-licensing/applications/${encodeURIComponent(id)}/decision`, body);
  }
  async createLicenceFeeLine(
    applicationId: string,
    body: CreateFeeLineBody,
  ): Promise<{ data: LicenceFeeApplicationView }> {
    return this.post(
      `/sector-licensing/applications/${encodeURIComponent(applicationId)}/fee-lines`,
      body,
    );
  }
  async updateLicenceFeeLine(
    applicationId: string,
    lineId: string,
    body: UpdateFeeLineBody,
  ): Promise<{ data: LicenceFeeApplicationView }> {
    return this.patch(
      `/sector-licensing/applications/${encodeURIComponent(applicationId)}/fee-lines/${encodeURIComponent(lineId)}`,
      body,
    );
  }
  /**
   * `expected_updated_at` is the line's own `updated_at` as read, sent as a
   * query parameter (the route parses `DeleteFeeLineQuerySchema` off the
   * query): a line that changed since is refused (409 `STALE_WRITE`), and a
   * line holding a receipt file is refused (409 `FEE_LINE_HAS_RECEIPT`).
   * `receipt_files_left` names files the deleted line still owned that no
   * record names now (a receipt taken off it whose file was not deleted):
   * they stay in the Files browser, unattached (gate E12.5 pass 2).
   */
  async deleteLicenceFeeLine(
    applicationId: string,
    lineId: string,
    guard: { expected_updated_at: string },
  ): Promise<{ data: LicenceFeeApplicationView; receipt_files_left?: string[] }> {
    const qs = new URLSearchParams({ expected_updated_at: guard.expected_updated_at });
    return this.del(
      `/sector-licensing/applications/${encodeURIComponent(applicationId)}/fee-lines/${encodeURIComponent(lineId)}?${qs.toString()}`,
    );
  }
  // ── Sprint 62.54.0: sender-name certificates (Marketing Communications, sender-identity side) ──
  /** The installed schedule narrowed to the sender-identity side. */
  async getSenderNameFeeSchedule(): Promise<{
    data: RegulatoryFeeSchedule;
    declaration_absence: DeclarationAbsence | null;
  }> {
    return this.get('/marketing-communications/sender-name-certificates/schedule');
  }
  async listSenderNameCertificates(params?: { page?: number; page_size?: number }): Promise<{
    data: SenderNameCertificateView[];
    total: number;
    page: number;
    page_size: number;
    declaration_absence: DeclarationAbsence | null;
  }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/marketing-communications/sender-name-certificates${suffix}`);
  }
  async getSenderNameCertificate(id: string): Promise<{
    data: SenderNameCertificateView;
    declaration_absence: DeclarationAbsence | null;
  }> {
    return this.get(`/marketing-communications/sender-name-certificates/${encodeURIComponent(id)}`);
  }
  async createSenderNameCertificate(
    body: CreateSenderNameCertificateBody,
  ): Promise<{ data: SenderNameCertificateView }> {
    return this.post('/marketing-communications/sender-name-certificates', body);
  }
  async updateSenderNameCertificate(
    id: string,
    body: UpdateSenderNameCertificateBody,
  ): Promise<{ data: SenderNameCertificateView }> {
    return this.patch(
      `/marketing-communications/sender-name-certificates/${encodeURIComponent(id)}`,
      body,
    );
  }
  async deleteSenderNameCertificate(id: string): Promise<void> {
    return this.del(`/marketing-communications/sender-name-certificates/${encodeURIComponent(id)}`);
  }
  async createSenderNameChargeLine(
    certificateId: string,
    body: CreateFeeLineBody,
  ): Promise<{ data: SenderNameCertificateView }> {
    return this.post(
      `/marketing-communications/sender-name-certificates/${encodeURIComponent(certificateId)}/charge-lines`,
      body,
    );
  }
  async updateSenderNameChargeLine(
    certificateId: string,
    lineId: string,
    body: UpdateFeeLineBody,
  ): Promise<{ data: SenderNameCertificateView }> {
    return this.patch(
      `/marketing-communications/sender-name-certificates/${encodeURIComponent(certificateId)}/charge-lines/${encodeURIComponent(lineId)}`,
      body,
    );
  }
  /** As `deleteLicenceFeeLine`: the line's `updated_at` as read guards the delete (query). */
  async deleteSenderNameChargeLine(
    certificateId: string,
    lineId: string,
    guard: { expected_updated_at: string },
  ): Promise<{ data: SenderNameCertificateView; receipt_files_left?: string[] }> {
    const qs = new URLSearchParams({ expected_updated_at: guard.expected_updated_at });
    return this.del(
      `/marketing-communications/sender-name-certificates/${encodeURIComponent(certificateId)}/charge-lines/${encodeURIComponent(lineId)}?${qs.toString()}`,
    );
  }
  // ── Sprint 62.55.0 (UC-VN-109 F-1, F-3) — fee lines across both registers ──
  /** The lines paid in one month (`YYYY-MM`), each named by its record. Never a total. */
  async listFeeLinesByPeriod(params: {
    period: string;
    owner_type?: FeeLineOwnerType;
    page?: number;
    page_size?: number;
  }): Promise<FeeLinePeriodList> {
    const qs = new URLSearchParams({ period: params.period });
    if (params.owner_type) qs.set('owner_type', params.owner_type);
    if (params.page !== undefined) qs.set('page', String(params.page));
    if (params.page_size !== undefined) qs.set('page_size', String(params.page_size));
    return this.get(`/regulatory-fees/fee-lines?${qs.toString()}`);
  }
  /** One month's lines, and the decisions of the applications listed, through UnifiedExportService. */
  async exportFeeLinesForPeriod(
    period: string,
    format: RenderableExportFormat,
    locale: RegulatoryFeeLineExportLocale,
  ): Promise<{ data: AttachmentRef }> {
    const qs = new URLSearchParams({ period, format, locale });
    return this.get(`/regulatory-fees/fee-lines/export?${qs.toString()}`);
  }
  /** One application's lines and decision, or one certificate's charge lines. */
  async exportFeeLinesForOwner(
    ownerType: FeeLineOwnerType,
    ownerId: string,
    format: RenderableExportFormat,
    locale: RegulatoryFeeLineExportLocale,
  ): Promise<{ data: AttachmentRef }> {
    const qs = new URLSearchParams({ format, locale });
    return this.get(
      `/regulatory-fees/owners/${encodeURIComponent(ownerType)}/${encodeURIComponent(ownerId)}/export?${qs.toString()}`,
    );
  }
  // ── Sprint 62.24.0: consent evidence (AC2/AC3) on the consent router ──
  /**
   * OD42 (fix-sprint-20261003-0625 F28) — the transfer mechanisms the org's
   * installed frameworks declare.
   *
   * As with the lawful-basis vocabulary below, the response names every
   * instrument of the declaring stacks, including those contributing no
   * options, so a page can tell a verified negative ("declares none") from an
   * instrument not yet verified. An options list alone cannot.
   */
  async getTransferMechanismVocabulary(): Promise<{ data: TransferMechanismVocabulary }> {
    return this.get('/localization/transfer-mechanism-vocabulary');
  }

  /**
   * Sprint 62.27.0 — the lawful bases the org's installed frameworks declare.
   *
   * The response names every expanded instrument, including those contributing
   * no options, so a page can say which instrument was checked and which was
   * not. An options list alone cannot tell a verified negative from an
   * unanswered question.
   */
  async getLawfulBasisVocabulary(): Promise<{ data: LawfulBasisVocabulary }> {
    return this.get('/localization/lawful-basis-vocabulary');
  }

  async getConsentEvidenceVocabulary(): Promise<{ data: ConsentEvidenceVocabulary }> {
    return this.get('/consent/evidence/vocabulary');
  }
  async listConsentEvidence(params?: {
    page?: number;
    page_size?: number;
    subject_ref?: string;
    recipient_class?: string;
  }): Promise<PaginatedResponse<ConsentEvidenceEntryView>> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.subject_ref) qs.set('subject_ref', params.subject_ref);
    if (params?.recipient_class) qs.set('recipient_class', params.recipient_class);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/consent/evidence${suffix}`);
  }
  async createConsentEvidence(
    body: CreateConsentEvidenceBody,
  ): Promise<{ data: ConsentEvidenceEntryView }> {
    return this.post('/consent/evidence', body);
  }
  async getConsentEvidence(evidenceId: string): Promise<{ data: ConsentEvidenceEntryView }> {
    return this.get(`/consent/evidence/${encodeURIComponent(evidenceId)}`);
  }
  async updateConsentEvidence(
    evidenceId: string,
    body: UpdateConsentEvidenceBody,
  ): Promise<{ data: ConsentEvidenceEntryView }> {
    return this.patch(`/consent/evidence/${encodeURIComponent(evidenceId)}`, body);
  }
  // ── Sprint 62.28.0: marketing-communications consent, suppression, enforcement ──
  async getMarketingCommunicationsKinds(): Promise<{
    data: MarketingCommunicationsKinds;
    declaration_absence?: DeclarationAbsence | null;
  }> {
    return this.get('/marketing-communications/kinds');
  }
  async getMarketingBoundaryStatements(tag: string): Promise<{ data: BoundaryStatement[] }> {
    return this.get(`/marketing-communications/boundary-statements?tag=${encodeURIComponent(tag)}`);
  }
  async listMarketingConsents(params?: {
    page?: number;
    page_size?: number;
    subject_ref?: string;
    channel?: string;
  }): Promise<PaginatedResponse<MarketingConsentEntryView>> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.subject_ref) qs.set('subject_ref', params.subject_ref);
    if (params?.channel) qs.set('channel', params.channel);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/marketing-communications/consents${suffix}`);
  }
  async createMarketingConsent(
    body: CreateMarketingConsentBody,
  ): Promise<{ data: MarketingConsentEntryView }> {
    return this.post('/marketing-communications/consents', body);
  }
  async updateMarketingConsent(
    consentId: string,
    body: UpdateMarketingConsentBody,
  ): Promise<{ data: MarketingConsentEntryView }> {
    return this.patch(`/marketing-communications/consents/${encodeURIComponent(consentId)}`, body);
  }
  async listMarketingSuppressions(params?: {
    page?: number;
    page_size?: number;
    subject_ref?: string;
    channel?: string;
    standing_only?: boolean;
  }): Promise<PaginatedResponse<MarketingSuppressionEntryView>> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.subject_ref) qs.set('subject_ref', params.subject_ref);
    if (params?.channel) qs.set('channel', params.channel);
    if (params?.standing_only !== undefined) {
      qs.set('standing_only', params.standing_only ? 'true' : 'false');
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/marketing-communications/suppressions${suffix}`);
  }
  async createMarketingSuppression(
    body: CreateMarketingSuppressionBody,
  ): Promise<{ data: MarketingSuppressionEntryView }> {
    return this.post('/marketing-communications/suppressions', body);
  }
  /** The confirmation the recipient is owed — the server stamps who recorded it and when. */
  async recordSuppressionConfirmation(
    suppressionId: string,
    body: RecordSuppressionConfirmationBody,
  ): Promise<{ data: MarketingSuppressionEntryView }> {
    return this.post(
      `/marketing-communications/suppressions/${encodeURIComponent(suppressionId)}/confirmation`,
      body,
    );
  }
  /** Lifting resumes sending to someone who asked it to stop; its own grant guards the route. */
  async liftMarketingSuppression(
    suppressionId: string,
    body: { reason: string },
  ): Promise<{ data: MarketingSuppressionEntryView }> {
    return this.post(
      `/marketing-communications/suppressions/${encodeURIComponent(suppressionId)}/lift`,
      body,
    );
  }
  async listSuppressionEnforcementChecks(params?: {
    page?: number;
    page_size?: number;
    campaign_ref?: string;
    channel?: string;
  }): Promise<PaginatedResponse<SuppressionEnforcementCheckView>> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.campaign_ref) qs.set('campaign_ref', params.campaign_ref);
    if (params?.channel) qs.set('channel', params.channel);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/marketing-communications/enforcement-checks${suffix}`);
  }
  async createSuppressionEnforcementCheck(
    body: CreateSuppressionEnforcementCheckBody,
  ): Promise<{ data: SuppressionEnforcementCheckView }> {
    return this.post('/marketing-communications/enforcement-checks', body);
  }
  /** Records that the consent was reproduced on demand — a person's act; the server stamps who and when. */
  async demonstrateConsentEvidence(
    evidenceId: string,
    body: { note?: string | null },
  ): Promise<{ data: ConsentEvidenceEntryView }> {
    return this.post(`/consent/evidence/${encodeURIComponent(evidenceId)}/demonstrate`, body);
  }

  // ── Sprint 62.24.0: credit-information supply register (AC4–AC7) ──
  async getCreditInformationSupplyKinds(): Promise<{
    data: CreditInformationSupplyKinds;
    declaration_absence?: DeclarationAbsence | null;
  }> {
    return this.get('/credit-information-supply/supplies/kinds');
  }
  async listCreditInformationSupplyBoundaryStatements(
    tag: string,
  ): Promise<{ data: BoundaryStatement[] }> {
    return this.get(
      `/credit-information-supply/boundary-statements?tag=${encodeURIComponent(tag)}`,
    );
  }
  async listCreditInformationSupplies(params?: {
    page?: number;
    page_size?: number;
    recipient_class?: string;
    consent_cover_state?: ConsentCoverState;
    subject_ref?: string;
  }): Promise<PaginatedResponse<CreditInformationSupplyEntryView>> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.recipient_class) qs.set('recipient_class', params.recipient_class);
    if (params?.consent_cover_state) qs.set('consent_cover_state', params.consent_cover_state);
    if (params?.subject_ref) qs.set('subject_ref', params.subject_ref);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/credit-information-supply/supplies${suffix}`);
  }
  async createCreditInformationSupply(
    body: CreateCreditInformationSupplyBody,
  ): Promise<{ data: CreditInformationSupplyEntryView }> {
    return this.post('/credit-information-supply/supplies', body);
  }
  async getCreditInformationSupply(
    supplyId: string,
  ): Promise<{ data: CreditInformationSupplyEntryView }> {
    return this.get(`/credit-information-supply/supplies/${encodeURIComponent(supplyId)}`);
  }
  async updateCreditInformationSupply(
    supplyId: string,
    body: UpdateCreditInformationSupplyBody,
  ): Promise<{ data: CreditInformationSupplyEntryView }> {
    return this.patch(`/credit-information-supply/supplies/${encodeURIComponent(supplyId)}`, body);
  }
  async exportCreditInformationSupply(
    supplyId: string,
    format: RenderableExportFormat,
  ): Promise<{ data: AttachmentRef }> {
    return this.get(
      `/credit-information-supply/supplies/${encodeURIComponent(supplyId)}/export?format=${encodeURIComponent(format)}`,
    );
  }

  // ── Sprint 62.24.0: data-service provider records (AC9/AC10/AC12/AC13) ──
  async getDataServiceProviderVocabulary(): Promise<{
    data: DataServiceProviderVocabulary;
    declaration_absence?: DeclarationAbsence | null;
  }> {
    return this.get('/data-services/providers/kinds');
  }
  async listDataServiceProviderBoundaryStatements(
    tag: string,
  ): Promise<{ data: BoundaryStatement[] }> {
    return this.get(`/data-services/boundary-statements?tag=${encodeURIComponent(tag)}`);
  }
  async listDataServiceProviders(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<DataServiceProviderRecord>> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/data-services/providers${suffix}`);
  }
  async createDataServiceProvider(
    body: CreateDataServiceProviderBody,
  ): Promise<{ data: DataServiceProviderRecord }> {
    return this.post('/data-services/providers', body);
  }
  /** The view: the record plus its computed notification window and report years. */
  async getDataServiceProvider(providerId: string): Promise<{ data: DataServiceProviderView }> {
    return this.get(`/data-services/providers/${encodeURIComponent(providerId)}`);
  }
  async updateDataServiceProvider(
    providerId: string,
    body: UpdateDataServiceProviderBody,
  ): Promise<{ data: DataServiceProviderRecord }> {
    return this.patch(`/data-services/providers/${encodeURIComponent(providerId)}`, body);
  }
  async recordDataServiceProviderNotification(
    providerId: string,
    body: ProviderNotificationBody,
  ): Promise<{ data: DataServiceProviderRecord }> {
    return this.post(
      `/data-services/providers/${encodeURIComponent(providerId)}/notifications`,
      body,
    );
  }
  async recordDataServiceProviderPeriodicReport(
    providerId: string,
    body: ProviderPeriodicReportBody,
  ): Promise<{ data: DataServiceProviderRecord }> {
    return this.post(
      `/data-services/providers/${encodeURIComponent(providerId)}/periodic-reports`,
      body,
    );
  }
  async recordDataServiceProviderAdHocReport(
    providerId: string,
    body: ProviderAdHocReportBody,
  ): Promise<{ data: DataServiceProviderRecord }> {
    return this.post(
      `/data-services/providers/${encodeURIComponent(providerId)}/ad-hoc-reports`,
      body,
    );
  }
  async exportDataServiceProvider(
    providerId: string,
    format: RenderableExportFormat,
  ): Promise<{ data: AttachmentRef }> {
    return this.get(
      `/data-services/providers/${encodeURIComponent(providerId)}/export?format=${encodeURIComponent(format)}`,
    );
  }

  // ── Sprint 62.50.0 — supervision-information registers (work file C.1) ──
  // Mounted at `/pcf-supervision`. Lists are the R-23 envelope; pages unwrap
  // `res.data ?? []`. The platform sends nothing anywhere: every route here
  // reads or writes the organisation's own records.
  private pcfQuery(params?: Record<string, string | number | undefined>): string {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    const text = qs.toString();
    return text ? `?${text}` : '';
  }
  async getPcfRegisterContext(
    surfaceKey: PcfSupervisionSurfaceKey,
  ): Promise<{ data: PcfRegisterContext }> {
    return this.get(`/pcf-supervision/context/${encodeURIComponent(surfaceKey)}`);
  }
  async listPcfBranchRequirements(params?: {
    page?: number;
    page_size?: number;
    /** Only the requirements that ask for this kind. */
    asks_for?: PcfRequirementKind;
  }): Promise<PaginatedResponse<PcfBranchRequirement>> {
    return this.get(`/pcf-supervision/branch-requirements${this.pcfQuery(params)}`);
  }
  /** One requirement as stored now — the re-read after a 409 adopts its version. */
  async getPcfBranchRequirement(id: string): Promise<{ data: PcfBranchRequirement }> {
    return this.get(`/pcf-supervision/branch-requirements/${encodeURIComponent(id)}`);
  }
  async createPcfBranchRequirement(
    body: CreatePcfBranchRequirementBody,
  ): Promise<{ data: PcfBranchRequirement }> {
    return this.post('/pcf-supervision/branch-requirements', body);
  }
  async updatePcfBranchRequirement(
    id: string,
    body: UpdatePcfBranchRequirementBody,
  ): Promise<{ data: PcfBranchRequirement }> {
    return this.patch(`/pcf-supervision/branch-requirements/${encodeURIComponent(id)}`, body);
  }
  async listPcfPublicityRequirements(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<PcfPublicityRequirementView>> {
    return this.get(`/pcf-supervision/publicity/requirements${this.pcfQuery(params)}`);
  }
  async listPcfPublicityRecords(params?: {
    requirement_id?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<PcfPublicityRecord>> {
    return this.get(`/pcf-supervision/publicity${this.pcfQuery(params)}`);
  }
  async createPcfPublicityRecord(
    body: CreatePcfPublicityBody,
  ): Promise<{ data: PcfPublicityRecord }> {
    return this.post('/pcf-supervision/publicity', body);
  }
  /** One publicity record, read by id (the re-read after a conflict, #683 gate E12.5). */
  async getPcfPublicityRecord(id: string): Promise<{ data: PcfPublicityRecord }> {
    return this.get(`/pcf-supervision/publicity/${encodeURIComponent(id)}`);
  }
  async updatePcfPublicityRecord(
    id: string,
    body: UpdatePcfPublicityBody,
  ): Promise<{ data: PcfPublicityRecord }> {
    return this.patch(`/pcf-supervision/publicity/${encodeURIComponent(id)}`, body);
  }
  async listPcfInformationRequests(params?: {
    requirement_id?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<PcfInformationRequestRecord>> {
    return this.get(`/pcf-supervision/information-requests${this.pcfQuery(params)}`);
  }
  async createPcfInformationRequest(
    body: CreatePcfInformationRequestBody,
  ): Promise<{ data: PcfInformationRequestRecord }> {
    return this.post('/pcf-supervision/information-requests', body);
  }
  /** One information request, read by id (the re-read after a conflict, #683 gate E12.5). */
  async getPcfInformationRequest(id: string): Promise<{ data: PcfInformationRequestRecord }> {
    return this.get(`/pcf-supervision/information-requests/${encodeURIComponent(id)}`);
  }
  async updatePcfInformationRequest(
    id: string,
    body: UpdatePcfInformationRequestBody,
  ): Promise<{ data: PcfInformationRequestRecord }> {
    return this.patch(`/pcf-supervision/information-requests/${encodeURIComponent(id)}`, body);
  }
  async listPcfDepositorEnquiries(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<PcfDepositorEnquiry>> {
    return this.get(`/pcf-supervision/depositor-enquiries${this.pcfQuery(params)}`);
  }
  async createPcfDepositorEnquiry(
    body: CreatePcfDepositorEnquiryBody,
  ): Promise<{ data: PcfDepositorEnquiry }> {
    return this.post('/pcf-supervision/depositor-enquiries', body);
  }
  /** One depositor enquiry, read by id (the re-read after a conflict, #683 gate E12.5). */
  async getPcfDepositorEnquiry(id: string): Promise<{ data: PcfDepositorEnquiry }> {
    return this.get(`/pcf-supervision/depositor-enquiries/${encodeURIComponent(id)}`);
  }
  async updatePcfDepositorEnquiry(
    id: string,
    body: UpdatePcfDepositorEnquiryBody,
  ): Promise<{ data: PcfDepositorEnquiry }> {
    return this.patch(`/pcf-supervision/depositor-enquiries/${encodeURIComponent(id)}`, body);
  }
  async listPcfSharedInformation(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<PcfSharedInformationEntry>> {
    return this.get(`/pcf-supervision/shared-information${this.pcfQuery(params)}`);
  }
  async createPcfSharedInformation(
    body: CreatePcfSharedInformationBody,
  ): Promise<{ data: PcfSharedInformationEntry }> {
    return this.post('/pcf-supervision/shared-information', body);
  }
  /** One shared-information entry, read by id (the re-read after a conflict, #683 gate E12.5). */
  async getPcfSharedInformation(id: string): Promise<{ data: PcfSharedInformationEntry }> {
    return this.get(`/pcf-supervision/shared-information/${encodeURIComponent(id)}`);
  }
  async updatePcfSharedInformation(
    id: string,
    body: UpdatePcfSharedInformationBody,
  ): Promise<{ data: PcfSharedInformationEntry }> {
    return this.patch(`/pcf-supervision/shared-information/${encodeURIComponent(id)}`, body);
  }
  async listPcfReportingCalendar(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<PcfReportingCalendarEntry>> {
    return this.get(`/pcf-supervision/reporting-calendar${this.pcfQuery(params)}`);
  }
  async createPcfReportingCalendarEntry(
    body: CreatePcfReportingCalendarBody,
  ): Promise<{ data: PcfReportingCalendarEntry }> {
    return this.post('/pcf-supervision/reporting-calendar', body);
  }
  async updatePcfReportingCalendarEntry(
    id: string,
    body: UpdatePcfReportingCalendarBody,
  ): Promise<{ data: PcfReportingCalendarEntry }> {
    return this.patch(`/pcf-supervision/reporting-calendar/${encodeURIComponent(id)}`, body);
  }
  async listPcfIncidents(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<PcfIncidentRecord>> {
    return this.get(`/pcf-supervision/incidents${this.pcfQuery(params)}`);
  }
  async createPcfIncident(body: CreatePcfIncidentBody): Promise<{ data: PcfIncidentRecord }> {
    return this.post('/pcf-supervision/incidents', body);
  }
  async updatePcfIncident(
    id: string,
    body: UpdatePcfIncidentBody,
  ): Promise<{ data: PcfIncidentRecord }> {
    return this.patch(`/pcf-supervision/incidents/${encodeURIComponent(id)}`, body);
  }
  async listPcfCorrections(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<PcfCorrectionRecord>> {
    return this.get(`/pcf-supervision/corrections${this.pcfQuery(params)}`);
  }
  async createPcfCorrection(body: CreatePcfCorrectionBody): Promise<{ data: PcfCorrectionRecord }> {
    return this.post('/pcf-supervision/corrections', body);
  }
  async updatePcfCorrection(
    id: string,
    body: UpdatePcfCorrectionBody,
  ): Promise<{ data: PcfCorrectionRecord }> {
    return this.patch(`/pcf-supervision/corrections/${encodeURIComponent(id)}`, body);
  }
  async getPcfSystemOperation(): Promise<{ data: PcfSystemOperationRecord | null }> {
    return this.get('/pcf-supervision/system-operation');
  }
  async putPcfSystemOperation(
    body: PutPcfSystemOperationBody,
  ): Promise<{ data: PcfSystemOperationRecord | null }> {
    return this.put('/pcf-supervision/system-operation', body);
  }
  async getPcfConnectionReadiness(): Promise<{ data: PcfConnectionReadinessRecord | null }> {
    return this.get('/pcf-supervision/connection-readiness');
  }
  async putPcfConnectionReadiness(
    body: PutPcfConnectionReadinessBody,
  ): Promise<{ data: PcfConnectionReadinessRecord | null }> {
    return this.put('/pcf-supervision/connection-readiness', body);
  }
  // ── Sprint 62.50.1 — register (a)'s written requests and proposals ──
  async listPcfWrittenRequests(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<PcfWrittenRequestRecord>> {
    return this.get(`/pcf-supervision/written-requests${this.pcfQuery(params)}`);
  }
  async getPcfWrittenRequest(id: string): Promise<{ data: PcfWrittenRequestRecord }> {
    return this.get(`/pcf-supervision/written-requests/${encodeURIComponent(id)}`);
  }
  async createPcfWrittenRequest(
    body: CreatePcfWrittenRequestBody,
  ): Promise<{ data: PcfWrittenRequestRecord }> {
    return this.post('/pcf-supervision/written-requests', body);
  }
  async updatePcfWrittenRequest(
    id: string,
    body: UpdatePcfWrittenRequestBody,
  ): Promise<{ data: PcfWrittenRequestRecord }> {
    return this.patch(`/pcf-supervision/written-requests/${encodeURIComponent(id)}`, body);
  }
  async listPcfChangeProposals(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<PcfChangeProposalRecord>> {
    return this.get(`/pcf-supervision/change-proposals${this.pcfQuery(params)}`);
  }
  async getPcfChangeProposal(id: string): Promise<{ data: PcfChangeProposalRecord }> {
    return this.get(`/pcf-supervision/change-proposals/${encodeURIComponent(id)}`);
  }
  async createPcfChangeProposal(
    body: CreatePcfChangeProposalBody,
  ): Promise<{ data: PcfChangeProposalRecord }> {
    return this.post('/pcf-supervision/change-proposals', body);
  }
  async updatePcfChangeProposal(
    id: string,
    body: UpdatePcfChangeProposalBody,
  ): Promise<{ data: PcfChangeProposalRecord }> {
    return this.patch(`/pcf-supervision/change-proposals/${encodeURIComponent(id)}`, body);
  }
  /**
   * Sprint 62.50.1 — one register as PDF, CSV, DOCX or XLSX through
   * `UnifiedExportService` (R-33), in EN or VI. The route answers
   * `{ data: ExportedArtefactRef }` (D1); the page downloads by `file_id` in
   * the same handler. 422 `PCF_EXPORT_TOO_LARGE` above the record cap.
   */
  async exportPcfSupervisionRegister(
    surfaceKey: PcfSupervisionExportSurfaceKey,
    format: RenderableExportFormat,
    locale: PcfSupervisionExportLocale,
  ): Promise<{ data: AttachmentRef }> {
    return this.get(
      `/pcf-supervision/export/${encodeURIComponent(surfaceKey)}${this.pcfQuery({ format, locale })}`,
    );
  }

  // ── Sprint 62.51.0 — data-exploitation registers (work file C.1, S2) ──
  // Mounted at `/data-exploitation`. Lists are the R-23 envelope; pages unwrap
  // `res.data ?? []`. Nothing here sends, submits or connects to anything: every
  // route reads or writes the organisation's own records.
  private deQuery(params?: Record<string, string | number | boolean | undefined>): string {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    const text = qs.toString();
    return text ? `?${text}` : '';
  }
  private deId(id: string): string {
    return encodeURIComponent(id);
  }
  async getDataExploitationOverview(): Promise<{ data: DataExploitationOverview }> {
    return this.get('/data-exploitation/overview');
  }
  async getDataExploitationContext(
    surfaceKey: DataExploitationSurfaceKey,
  ): Promise<{ data: DataExploitationRegisterContext }> {
    return this.get(`/data-exploitation/context/${encodeURIComponent(surfaceKey)}`);
  }
  async exportDataExploitationRegister(
    surfaceKey: DataExploitationExportSurfaceKey,
    format: RenderableExportFormat,
    locale: DataExploitationExportLocale,
  ): Promise<{ data: AttachmentRef }> {
    return this.get(
      `/data-exploitation/export/${encodeURIComponent(surfaceKey)}${this.deQuery({ format, locale })}`,
    );
  }
  async exportDataExploitationGrant(
    requestId: string,
    format: RenderableExportFormat,
    locale: DataExploitationExportLocale,
  ): Promise<{ data: AttachmentRef }> {
    return this.get(
      `/data-exploitation/requests/${this.deId(requestId)}/export${this.deQuery({ format, locale })}`,
    );
  }
  // Slice A — activities
  async listDataExploitationActivities(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<DataExploitationActivity>> {
    return this.get(`/data-exploitation/activities${this.deQuery(params)}`);
  }
  async getDataExploitationActivity(id: string): Promise<{ data: DataExploitationActivity }> {
    return this.get(`/data-exploitation/activities/${this.deId(id)}`);
  }
  async createDataExploitationActivity(
    body: CreateDataExploitationActivityBody,
  ): Promise<{ data: DataExploitationActivity }> {
    return this.post('/data-exploitation/activities', body);
  }
  async updateDataExploitationActivity(
    id: string,
    body: UpdateDataExploitationActivityBody,
  ): Promise<{ data: DataExploitationActivity }> {
    return this.patch(`/data-exploitation/activities/${this.deId(id)}`, body);
  }
  // Slice B — requests and grants
  async listDataExploitationRequests(params?: {
    page?: number;
    page_size?: number;
    grants_only?: boolean;
  }): Promise<PaginatedResponse<DataExploitationRequest>> {
    return this.get(`/data-exploitation/requests${this.deQuery(params)}`);
  }
  async getDataExploitationRequest(
    id: string,
  ): Promise<{ data: DataExploitationRequestDetailView }> {
    return this.get(`/data-exploitation/requests/${this.deId(id)}`);
  }
  async createDataExploitationRequest(
    body: CreateDataExploitationRequestBody,
  ): Promise<{ data: DataExploitationRequest }> {
    return this.post('/data-exploitation/requests', body);
  }
  async updateDataExploitationRequest(
    id: string,
    body: UpdateDataExploitationRequestBody,
  ): Promise<{ data: DataExploitationRequest }> {
    return this.patch(`/data-exploitation/requests/${this.deId(id)}`, body);
  }
  async addDataExploitationRightAction(
    id: string,
    body: DataExploitationRightActionBody,
  ): Promise<{ data: DataExploitationRequest }> {
    return this.post(`/data-exploitation/requests/${this.deId(id)}/right-actions`, body);
  }
  async markDataExploitationPurposeEnded(
    id: string,
    body: DataExploitationPurposeEndedBody,
  ): Promise<{ data: DataExploitationRequest }> {
    return this.post(`/data-exploitation/requests/${this.deId(id)}/purpose-ended`, body);
  }
  // Remediation Y — an entry recorded in error (the organisation's correction).
  async markDataExploitationRightActionRecordedInError(
    id: string,
    actionId: string,
    body: DataExploitationRecordedInErrorBody,
  ): Promise<{ data: DataExploitationRequest }> {
    return this.post(
      `/data-exploitation/requests/${this.deId(id)}/right-actions/${this.deId(actionId)}/recorded-in-error`,
      body,
    );
  }
  async markDataExploitationPurposeEndedRecordedInError(
    id: string,
    body: DataExploitationRecordedInErrorBody,
  ): Promise<{ data: DataExploitationRequest }> {
    return this.post(
      `/data-exploitation/requests/${this.deId(id)}/purpose-ended/recorded-in-error`,
      body,
    );
  }
  async markDataExploitationTestingSubrecordRecordedInError(
    id: string,
    sub: DataExploitationTestingCorrectableSubrecord,
    subId: string,
    body: DataExploitationRecordedInErrorBody,
  ): Promise<{ data: DataExploitationTestingRecord }> {
    return this.post(
      `/data-exploitation/testing-records/${this.deId(id)}/${sub}/${this.deId(subId)}/recorded-in-error`,
      body,
    );
  }
  async markDataExploitationLiabilityRelianceRecordedInError(
    id: string,
    relianceId: string,
    body: DataExploitationRecordedInErrorBody,
  ): Promise<{ data: DataExploitationIncubatorRecord }> {
    return this.post(
      `/data-exploitation/incubator-records/${this.deId(id)}/liability-reliance/${this.deId(relianceId)}/recorded-in-error`,
      body,
    );
  }
  // Slice C — controlled-testing records
  async listDataExploitationEligibleCases(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<DataExploitationEligibleCase>> {
    return this.get(`/data-exploitation/testing/eligible-cases${this.deQuery(params)}`);
  }
  async listDataExploitationTestingRecords(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<DataExploitationTestingRecord>> {
    return this.get(`/data-exploitation/testing-records${this.deQuery(params)}`);
  }
  async getDataExploitationTestingRecord(
    id: string,
  ): Promise<{ data: DataExploitationTestingRecord }> {
    return this.get(`/data-exploitation/testing-records/${this.deId(id)}`);
  }
  async createDataExploitationTestingRecord(
    body: CreateDataExploitationTestingRecordBody,
  ): Promise<{ data: DataExploitationTestingRecord }> {
    return this.post('/data-exploitation/testing-records', body);
  }
  async updateDataExploitationTestingRecord(
    id: string,
    body: UpdateDataExploitationTestingRecordBody,
  ): Promise<{ data: DataExploitationTestingRecord }> {
    return this.patch(`/data-exploitation/testing-records/${this.deId(id)}`, body);
  }
  async addDataExploitationRiskPlanVersion(
    id: string,
    body: DataExploitationRiskPlanVersionBody,
  ): Promise<{ data: DataExploitationTestingRecord }> {
    return this.post(
      `/data-exploitation/testing-records/${this.deId(id)}/risk-plan-versions`,
      body,
    );
  }
  async recordDataExploitationExtension(
    id: string,
    body: DataExploitationExtensionBody,
  ): Promise<{ data: DataExploitationTestingRecord }> {
    return this.post(`/data-exploitation/testing-records/${this.deId(id)}/extension`, body);
  }
  async addDataExploitationLiabilityReliance(
    id: string,
    body: DataExploitationLiabilityRelianceBody,
  ): Promise<{ data: DataExploitationIncubatorRecord }> {
    return this.post(
      `/data-exploitation/incubator-records/${this.deId(id)}/liability-reliance`,
      body,
    );
  }
  async addDataExploitationCentreAction(
    id: string,
    body: DataExploitationCentreActionBody,
  ): Promise<{ data: DataExploitationTestingRecord }> {
    return this.post(`/data-exploitation/testing-records/${this.deId(id)}/centre-actions`, body);
  }
  async recordDataExploitationCertificate(
    id: string,
    body: DataExploitationCertificateBody,
  ): Promise<{ data: DataExploitationTestingRecord }> {
    return this.post(`/data-exploitation/testing-records/${this.deId(id)}/certificates`, body);
  }
  async addDataExploitationReuseCheck(
    id: string,
    body: DataExploitationReuseCheckBody,
  ): Promise<{ data: DataExploitationTestingRecord }> {
    return this.post(`/data-exploitation/testing-records/${this.deId(id)}/reuse-checks`, body);
  }
  // Slice D — notices
  async listDataExploitationNotices(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<DataExploitationNotice>> {
    return this.get(`/data-exploitation/notices${this.deQuery(params)}`);
  }
  async getDataExploitationNotice(id: string): Promise<{ data: DataExploitationNotice }> {
    return this.get(`/data-exploitation/notices/${this.deId(id)}`);
  }
  async createDataExploitationNotice(
    body: CreateDataExploitationNoticeBody,
  ): Promise<{ data: DataExploitationNotice }> {
    return this.post('/data-exploitation/notices', body);
  }
  async updateDataExploitationNotice(
    id: string,
    body: UpdateDataExploitationNoticeBody,
  ): Promise<{ data: DataExploitationNotice }> {
    return this.patch(`/data-exploitation/notices/${this.deId(id)}`, body);
  }
  // Slice E — dispositions
  async listDataExploitationDispositionGrants(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<DataExploitationGrantDispositionRow>> {
    return this.get(`/data-exploitation/dispositions/grants${this.deQuery(params)}`);
  }
  async listDataExploitationDispositions(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<DataExploitationDisposition>> {
    return this.get(`/data-exploitation/dispositions${this.deQuery(params)}`);
  }
  async getDataExploitationDisposition(id: string): Promise<{ data: DataExploitationDisposition }> {
    return this.get(`/data-exploitation/dispositions/${this.deId(id)}`);
  }
  async createDataExploitationDisposition(
    body: CreateDataExploitationDispositionBody,
  ): Promise<{ data: DataExploitationDisposition }> {
    return this.post('/data-exploitation/dispositions', body);
  }
  async updateDataExploitationDisposition(
    id: string,
    body: UpdateDataExploitationDispositionBody,
  ): Promise<{ data: DataExploitationDisposition }> {
    return this.patch(`/data-exploitation/dispositions/${this.deId(id)}`, body);
  }
  // Slice F — incubator records
  async listDataExploitationIncubatorRecords(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<DataExploitationIncubatorRecord>> {
    return this.get(`/data-exploitation/incubator-records${this.deQuery(params)}`);
  }
  async getDataExploitationIncubatorRecord(
    id: string,
  ): Promise<{ data: DataExploitationIncubatorRecord }> {
    return this.get(`/data-exploitation/incubator-records/${this.deId(id)}`);
  }
  async createDataExploitationIncubatorRecord(
    body: CreateDataExploitationIncubatorBody,
  ): Promise<{ data: DataExploitationIncubatorRecord }> {
    return this.post('/data-exploitation/incubator-records', body);
  }
  async updateDataExploitationIncubatorRecord(
    id: string,
    body: UpdateDataExploitationIncubatorBody,
  ): Promise<{ data: DataExploitationIncubatorRecord }> {
    return this.patch(`/data-exploitation/incubator-records/${this.deId(id)}`, body);
  }
  // Slice H — foreign-cooperation assessments
  async listDataExploitationForeignAssessments(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<DataExploitationForeignAssessment>> {
    return this.get(`/data-exploitation/foreign-assessments${this.deQuery(params)}`);
  }
  async getDataExploitationForeignAssessment(
    id: string,
  ): Promise<{ data: DataExploitationForeignAssessment }> {
    return this.get(`/data-exploitation/foreign-assessments/${this.deId(id)}`);
  }
  async createDataExploitationForeignAssessment(
    body: CreateDataExploitationForeignAssessmentBody,
  ): Promise<{ data: DataExploitationForeignAssessment }> {
    return this.post('/data-exploitation/foreign-assessments', body);
  }
  async updateDataExploitationForeignAssessment(
    id: string,
    body: UpdateDataExploitationForeignAssessmentBody,
  ): Promise<{ data: DataExploitationForeignAssessment }> {
    return this.patch(`/data-exploitation/foreign-assessments/${this.deId(id)}`, body);
  }

  // ── Sprint 62.52.0 — the cybersecurity assurance index registers (work file C.1) ──
  // Mounted at `/cybersecurity-assurance-index` (app.ts), behind authenticate,
  // enforceOrgScope and the admin rate limiter; every GET on `:read`, every write
  // on `:write`, then the period's read gate (membership or `:restricted_read`).
  // Lists are the R-23 envelope `{ data, total, page, page_size }`; pages unwrap
  // `res.data ?? []`. One entity is `{ data }`. Nothing here submits, transmits or
  // reports to any authority, and nothing computes an index figure.
  private csaiPath(...segments: string[]): string {
    return `/cybersecurity-assurance-index/${segments.map((s) => encodeURIComponent(s)).join('/')}`;
  }
  async getCybersecurityAssuranceIndexContext(): Promise<{
    data: CybersecurityAssuranceIndexContext;
  }> {
    return this.get('/cybersecurity-assurance-index/context');
  }
  async listCybersecurityAssuranceIndexClassificationSubjects(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<CybersecurityAssuranceIndexClassificationSubject>> {
    return this.get(
      `/cybersecurity-assurance-index/classification-subjects${this.buildQuery(params)}`,
    );
  }
  async listCybersecurityAssuranceIndexPeriods(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<CybersecurityAssuranceIndexPeriod>> {
    return this.get(`/cybersecurity-assurance-index/periods${this.buildQuery(params)}`);
  }
  async createCybersecurityAssuranceIndexPeriod(
    body: CreateCybersecurityAssuranceIndexPeriodBody,
  ): Promise<{ data: CybersecurityAssuranceIndexPeriod }> {
    return this.post('/cybersecurity-assurance-index/periods', body);
  }
  async getCybersecurityAssuranceIndexPeriod(
    periodId: string,
  ): Promise<{ data: CybersecurityAssuranceIndexPeriod }> {
    return this.get(this.csaiPath('periods', periodId));
  }
  async updateCybersecurityAssuranceIndexPeriod(
    periodId: string,
    body: UpdateCybersecurityAssuranceIndexPeriodBody,
  ): Promise<{ data: CybersecurityAssuranceIndexPeriod }> {
    return this.patch(this.csaiPath('periods', periodId), body);
  }
  async putCybersecurityAssuranceIndexPeriodMembers(
    periodId: string,
    body: { version: number; members: string[] },
  ): Promise<{
    /**
     * E12.5 p1 (BE-17): the period, or — the caller removed themselves and
     * holds no restricted read — only its id, its new version and
     * `viewer_access: 'none'`.
     */
    data: CybersecurityAssuranceIndexPeriod | CybersecurityAssuranceIndexPeriodAccessEnded;
  }> {
    return this.put(this.csaiPath('periods', periodId, 'members'), body);
  }
  async closeCybersecurityAssuranceIndexPeriod(
    periodId: string,
    body: { version: number },
  ): Promise<{ data: CybersecurityAssuranceIndexPeriod }> {
    return this.post(this.csaiPath('periods', periodId, 'close'), body);
  }
  async getCybersecurityAssuranceIndexPeriodCatalogue(
    periodId: string,
  ): Promise<{ data: CybersecurityAssuranceIndexCatalogueSnapshot }> {
    return this.get(this.csaiPath('periods', periodId, 'catalogue'));
  }
  async getCybersecurityAssuranceIndexPeriodProgress(
    periodId: string,
  ): Promise<{ data: CybersecurityAssuranceIndexProgress }> {
    return this.get(this.csaiPath('periods', periodId, 'progress'));
  }
  async addCybersecurityAssuranceIndexSystem(
    periodId: string,
    body: AddCybersecurityAssuranceIndexSystemBody,
  ): Promise<{ data: CybersecurityAssuranceIndexPeriod }> {
    return this.post(this.csaiPath('periods', periodId, 'systems'), body);
  }
  async updateCybersecurityAssuranceIndexSystem(
    periodId: string,
    systemEntryId: string,
    body: UpdateCybersecurityAssuranceIndexSystemBody,
  ): Promise<{ data: CybersecurityAssuranceIndexPeriod }> {
    return this.patch(this.csaiPath('periods', periodId, 'systems', systemEntryId), body);
  }
  async rereadCybersecurityAssuranceIndexClassification(
    periodId: string,
    systemEntryId: string,
    body: { version: number; scheme_id?: string | null },
  ): Promise<{ data: CybersecurityAssuranceIndexPeriod }> {
    return this.post(
      this.csaiPath('periods', periodId, 'systems', systemEntryId, 'reread-classification'),
      body,
    );
  }
  async listCybersecurityAssuranceIndexEntries(
    periodId: string,
    systemEntryId: string,
    params?: {
      page?: number;
      page_size?: number;
      criterion_code?: string;
      recording_state?: CybersecurityAssuranceIndexRecordingState;
      /** OQ-3 — the items at the scale's floor. No other Mức filter exists. */
      at_floor?: 'true' | 'false';
      include_not_offered?: 'true' | 'false';
      /** E12.5 p2 — a read by item: comma-separated content codes (at most 100). */
      ids?: string;
    },
  ): Promise<PaginatedResponse<CybersecurityAssuranceIndexItemRow>> {
    return this.get(
      `${this.csaiPath('periods', periodId, 'systems', systemEntryId, 'entries')}${this.buildQuery(params)}`,
    );
  }
  async putCybersecurityAssuranceIndexEntry(
    periodId: string,
    systemEntryId: string,
    contentCode: string,
    body: PutCybersecurityAssuranceIndexEntryBody,
  ): Promise<{ data: CybersecurityAssuranceIndexEntry }> {
    return this.put(
      this.csaiPath('periods', periodId, 'systems', systemEntryId, 'entries', contentCode),
      body,
    );
  }
  async listCybersecurityAssuranceIndexStatistics(params: {
    cybersecurity_assurance_index_period_id: string;
    page?: number;
    page_size?: number;
    /** E12.5 p2 — a read by id within the period: comma-separated ids (at most 100). */
    ids?: string;
  }): Promise<PaginatedResponse<CybersecurityAssuranceIndexStatistic>> {
    return this.get(`/cybersecurity-assurance-index/statistics${this.buildQuery(params)}`);
  }
  async getCybersecurityAssuranceIndexStatisticsSummary(
    periodId: string,
  ): Promise<{ data: CybersecurityAssuranceIndexStatisticSummaryRow[] }> {
    return this.get(this.csaiPath('periods', periodId, 'statistics', 'summary'));
  }
  async createCybersecurityAssuranceIndexStatistic(
    periodId: string,
    body: CreateCybersecurityAssuranceIndexStatisticBody,
  ): Promise<{ data: CybersecurityAssuranceIndexStatistic }> {
    return this.post(this.csaiPath('periods', periodId, 'statistics'), body);
  }
  async updateCybersecurityAssuranceIndexStatistic(
    statisticId: string,
    body: UpdateCybersecurityAssuranceIndexStatisticBody,
  ): Promise<{ data: CybersecurityAssuranceIndexStatistic }> {
    return this.patch(this.csaiPath('statistics', statisticId), body);
  }
  async listCybersecurityAssuranceIndexSupplierRequests(params?: {
    cybersecurity_assurance_index_period_id?: string;
    page?: number;
    page_size?: number;
    /** E12.5 p2 — a read by id within the list's scope: comma-separated ids (at most 100). */
    ids?: string;
  }): Promise<PaginatedResponse<CybersecurityAssuranceIndexSupplierRequest>> {
    return this.get(`/cybersecurity-assurance-index/supplier-requests${this.buildQuery(params)}`);
  }
  async createCybersecurityAssuranceIndexSupplierRequest(
    body: CreateCybersecurityAssuranceIndexSupplierRequestBody,
  ): Promise<{ data: CybersecurityAssuranceIndexSupplierRequest }> {
    return this.post('/cybersecurity-assurance-index/supplier-requests', body);
  }
  async updateCybersecurityAssuranceIndexSupplierRequest(
    requestId: string,
    body: UpdateCybersecurityAssuranceIndexSupplierRequestBody,
  ): Promise<{ data: CybersecurityAssuranceIndexSupplierRequest }> {
    return this.patch(this.csaiPath('supplier-requests', requestId), body);
  }
  /**
   * `POST /periods/:periodId/evidence` — a file is uploaded to the PERIOD through
   * the module's own route (its read gate, OQ-2), then bound by naming its
   * `file_id` as `attachment_id` in a record's evidence list.
   */
  async uploadCybersecurityAssuranceIndexEvidence(
    periodId: string,
    file: File,
  ): Promise<{ data: AttachmentRef }> {
    const base64 = await fileToBase64(file);
    return this.post(this.csaiPath('periods', periodId, 'evidence'), {
      filename: file.name,
      mime_type: file.type || 'application/octet-stream',
      size: file.size,
      file_data_base64: base64,
    });
  }
  /**
   * `GET /periods/:periodId/evidence/:fileId` — an evidence file or an export of
   * THIS period, through the period's read gate. A member without
   * `:restricted_read` cannot use the generic attachment route for these, so
   * this is the one download the pages use. Awaited and checked, like
   * `downloadGenericAttachmentChecked`: a refusal is thrown as an `ApiError`.
   */
  async downloadCybersecurityAssuranceIndexFileChecked(
    periodId: string,
    fileId: string,
    filename: string,
  ): Promise<void> {
    const url = `${API_BASE}${this.csaiPath('periods', periodId, 'evidence', fileId)}`;
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) {
      let body: { error?: string; message?: string; details?: Record<string, unknown> } = {};
      try {
        body = await res.json();
      } catch {
        // A non-JSON error body is read as no body: the refusal below is then
        // the status bucket's, with the status in its message.
        body = {};
      }
      this.notifyUnauthorized(res.status);
      this.notifyMfaStepUpRequired(
        res.status,
        body.error,
        '/cybersecurity-assurance-index/evidence/download',
      );
      this.notifyPasswordChangeRequired(res.status, body.error);
      throw new ApiError({
        code: statusToErrorCode(res.status),
        message: body.message || `Download failed (${res.status})`,
        status: res.status,
        serverCode: body.error,
        details: body.details,
      });
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }
  /** `GET /periods/:periodId/export` — the artefact is downloaded through the period's own route. */
  async exportCybersecurityAssuranceIndexPeriod(
    periodId: string,
    format: RenderableExportFormat,
    locale: CybersecurityAssuranceIndexExportLocale,
  ): Promise<{ data: AttachmentRef }> {
    return this.get(
      `${this.csaiPath('periods', periodId, 'export')}${this.buildQuery({ format, locale })}`,
    );
  }

  // ── Sprint 62.49.0 — the food traceability registers (work file C.1, completed by S2b) ──
  // Mounted at `/food-traceability` (app.ts), behind authenticate, enforceOrgScope
  // and the admin rate limiter; every GET on `food_traceability:read`, every
  // register write on `:write`, every second-person approval on `:approve`, and
  // grant requests, removals and reviews on `:grant`. Lists are the R-23 envelope
  // `{ data, total, page, page_size }`; pages unwrap `res.data ?? []`. One entity
  // is `{ data }`. Nothing here registers, submits, sends or connects to anything:
  // every route reads or writes the organisation's own records.
  private ftPath(...segments: string[]): string {
    return `/food-traceability/${segments.map((s) => encodeURIComponent(s)).join('/')}`;
  }
  private ftQuery(params?: Record<string, string | number | boolean | undefined>): string {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    const text = qs.toString();
    return text ? `?${text}` : '';
  }
  async getFoodTraceabilityContext(): Promise<{ data: FoodTraceabilityContext }> {
    return this.get('/food-traceability/context');
  }
  /** One register as PDF, CSV, DOCX or XLSX (R-33); a complaint export never carries the contact (AC27). */
  async exportFoodTraceabilityRegister(
    registerKey: FoodTraceabilityRegisterKey,
    format: RenderableExportFormat,
    locale: FoodTraceabilityExportLocale,
  ): Promise<{ data: { attachment_id: string; file_name: string } }> {
    return this.post(this.ftPath('export', registerKey), { format, locale });
  }
  // Slice A — establishment and certificates
  async getFoodEstablishment(): Promise<{ data: FoodEstablishmentView }> {
    return this.get('/food-traceability/establishment');
  }
  async putFoodEstablishment(
    body: PutFoodEstablishmentBody,
  ): Promise<{ data: FoodEstablishmentView }> {
    return this.put('/food-traceability/establishment', body);
  }
  async listFoodCertificates(params?: {
    as_of?: string;
    lapsing?: 'true' | 'false';
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodCertificate>> {
    return this.get(`/food-traceability/certificates${this.ftQuery(params)}`);
  }
  async getFoodCertificate(id: string, asOf?: string): Promise<{ data: FoodCertificate }> {
    return this.get(`${this.ftPath('certificates', id)}${this.ftQuery({ as_of: asOf })}`);
  }
  async createFoodCertificate(body: CreateFoodCertificateBody): Promise<{ data: FoodCertificate }> {
    return this.post('/food-traceability/certificates', body);
  }
  async updateFoodCertificate(
    id: string,
    body: PatchFoodCertificateBody,
  ): Promise<{ data: FoodCertificate }> {
    return this.patch(this.ftPath('certificates', id), body);
  }
  async renewFoodCertificate(
    id: string,
    body: RenewFoodCertificateBody,
  ): Promise<{ data: FoodCertificate }> {
    return this.post(this.ftPath('certificates', id, 'renewals'), body);
  }
  async approveFoodCertificateEquivalence(
    id: string,
    note: string | null,
  ): Promise<{ data: FoodCertificate }> {
    return this.post(this.ftPath('certificates', id, 'equivalence', 'approve'), { note });
  }
  // Slice B — lot results, inspections, warnings
  async listFoodLotResults(params?: {
    batch_id?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodLotResult>> {
    return this.get(`/food-traceability/lot-results${this.ftQuery(params)}`);
  }
  async getFoodLotResult(id: string): Promise<{ data: FoodLotResult }> {
    return this.get(this.ftPath('lot-results', id));
  }
  async createFoodLotResult(body: CreateFoodLotResultBody): Promise<{ data: FoodLotResult }> {
    return this.post('/food-traceability/lot-results', body);
  }
  async updateFoodLotResult(
    id: string,
    body: PatchFoodLotResultBody,
  ): Promise<{ data: FoodLotResult }> {
    return this.patch(this.ftPath('lot-results', id), body);
  }
  async recordFoodTestingBodyStatement(
    id: string,
    body: FoodTestingBodyStatementBody,
  ): Promise<{ data: FoodLotResult }> {
    return this.post(this.ftPath('lot-results', id, 'testing-body-statement'), body);
  }
  async approveFoodTestingBodyStatement(
    id: string,
    note: string | null,
  ): Promise<{ data: FoodLotResult }> {
    return this.post(this.ftPath('lot-results', id, 'testing-body-statement', 'approve'), { note });
  }
  async listFoodInspections(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodInspection>> {
    return this.get(`/food-traceability/inspections${this.ftQuery(params)}`);
  }
  async getFoodInspection(id: string): Promise<{ data: FoodInspection }> {
    return this.get(this.ftPath('inspections', id));
  }
  async createFoodInspection(body: CreateFoodInspectionBody): Promise<{ data: FoodInspection }> {
    return this.post('/food-traceability/inspections', body);
  }
  async updateFoodInspection(
    id: string,
    body: PatchFoodInspectionBody,
  ): Promise<{ data: FoodInspection }> {
    return this.patch(this.ftPath('inspections', id), body);
  }
  async recordFoodInspectionResponse(
    id: string,
    body: FoodInspectionResponseBody,
  ): Promise<{ data: FoodInspection }> {
    return this.post(this.ftPath('inspections', id, 'response'), body);
  }
  async approveFoodInspectionResponse(
    id: string,
    note: string | null,
  ): Promise<{ data: FoodInspection }> {
    return this.post(this.ftPath('inspections', id, 'response', 'approve'), { note });
  }
  async listFoodWarnings(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodWarning>> {
    return this.get(`/food-traceability/warnings${this.ftQuery(params)}`);
  }
  async getFoodWarning(id: string): Promise<{ data: FoodWarning }> {
    return this.get(this.ftPath('warnings', id));
  }
  async createFoodWarning(body: CreateFoodWarningBody): Promise<{ data: FoodWarning }> {
    return this.post('/food-traceability/warnings', body);
  }
  async updateFoodWarning(id: string, body: PatchFoodWarningBody): Promise<{ data: FoodWarning }> {
    return this.patch(this.ftPath('warnings', id), body);
  }
  // Slice C — the internal report copy, notices, decisions
  /**
   * AC14 — the recall case's internal copy of the installed report layout,
   * rendered from a COMPLETED form instance filled in through Forms (named
   * here, or the one already linked to the case). Labelled internal, no
   * official id, the recipient left empty. Nothing is sent.
   */
  async renderFoodReportCopy(
    capaId: string,
    body: {
      form_instance_id?: string | null;
      format: FoodTraceabilityReportCopyFormat;
      locale?: FoodTraceabilityExportLocale;
      /** Sprint 62.49.2 (AC3) — non-empty: needs the food write grant; the copy is stored with them. */
      carried_refs?: FoodCarriedRef[];
    },
  ): Promise<{ data: FoodReportCopyResult }> {
    return this.post(this.ftPath('recall-cases', capaId, 'annex-copy'), body);
  }
  async listFoodNotices(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodNotice>> {
    return this.get(`/food-traceability/notices${this.ftQuery(params)}`);
  }
  async getFoodNotice(id: string): Promise<{ data: FoodNotice }> {
    return this.get(this.ftPath('notices', id));
  }
  async createFoodNotice(body: CreateFoodNoticeBody): Promise<{ data: FoodNotice }> {
    return this.post('/food-traceability/notices', body);
  }
  async updateFoodNotice(id: string, body: PatchFoodNoticeBody): Promise<{ data: FoodNotice }> {
    return this.patch(this.ftPath('notices', id), body);
  }
  /** AC15 — *sent* only with a proof (an empty list is refused 422 `…_PROOF_REQUIRED`). */
  async markFoodNoticeSent(id: string, body: FoodNoticeSentBody): Promise<{ data: FoodNotice }> {
    return this.post(this.ftPath('notices', id, 'sent'), body);
  }
  async addFoodQuantityReport(
    id: string,
    body: FoodQuantityReportBody,
  ): Promise<{ data: FoodNotice }> {
    return this.post(this.ftPath('notices', id, 'quantity-reports'), body);
  }
  async listFoodDecisions(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodDecision>> {
    return this.get(`/food-traceability/decisions${this.ftQuery(params)}`);
  }
  async getFoodDecision(id: string): Promise<{ data: FoodDecision }> {
    return this.get(this.ftPath('decisions', id));
  }
  async createFoodDecision(body: CreateFoodDecisionBody): Promise<{ data: FoodDecision }> {
    return this.post('/food-traceability/decisions', body);
  }
  async updateFoodDecision(
    id: string,
    body: PatchFoodDecisionBody,
  ): Promise<{ data: FoodDecision }> {
    return this.patch(this.ftPath('decisions', id), body);
  }
  async addFoodDecisionEvent(
    id: string,
    body: FoodDecisionEventBody,
  ): Promise<{ data: FoodDecision }> {
    return this.post(this.ftPath('decisions', id, 'events'), body);
  }
  async setFoodDecisionCorrectiveCapas(
    id: string,
    body: { capa_ids: string[]; expected_version?: number },
  ): Promise<{ data: FoodDecision }> {
    return this.put(this.ftPath('decisions', id, 'corrective-capas'), body);
  }
  // Slice D — corrections, supplier corrections, retention, routes
  async listFoodCorrectionRequests(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodCorrectionRequest>> {
    return this.get(`/food-traceability/corrections${this.ftQuery(params)}`);
  }
  async getFoodCorrectionRequest(id: string): Promise<{ data: FoodCorrectionRequest }> {
    return this.get(this.ftPath('corrections', id));
  }
  async createFoodCorrectionRequest(
    body: CreateFoodCorrectionRequestBody,
  ): Promise<{ data: FoodCorrectionRequest }> {
    return this.post('/food-traceability/corrections', body);
  }
  /** AC18 — applied under the approver; 403 `…_SELF_APPROVAL` for the requester. */
  async approveFoodCorrectionRequest(
    id: string,
    note: string | null,
  ): Promise<{ data: FoodCorrectionRequest }> {
    return this.post(this.ftPath('corrections', id, 'approve'), { note });
  }
  async rejectFoodCorrectionRequest(
    id: string,
    note: string | null,
  ): Promise<{ data: FoodCorrectionRequest }> {
    return this.post(this.ftPath('corrections', id, 'reject'), { note });
  }
  /**
   * Sprint 62.49.2 (AC6) — a person records the establishment's determination about one report the
   * applied correction lists. Nothing is sent.
   */
  async addFoodCorrectionImpactDetermination(
    id: string,
    body: FoodImpactDeterminationBody,
  ): Promise<{ data: FoodCorrectionRequest }> {
    return this.post(this.ftPath('corrections', id, 'impact-determinations'), body);
  }
  async listFoodSupplierCorrections(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodSupplierCorrection>> {
    return this.get(`/food-traceability/supplier-corrections${this.ftQuery(params)}`);
  }
  async getFoodSupplierCorrection(id: string): Promise<{ data: FoodSupplierCorrection }> {
    return this.get(this.ftPath('supplier-corrections', id));
  }
  async createFoodSupplierCorrection(
    body: CreateFoodSupplierCorrectionBody,
  ): Promise<{ data: FoodSupplierCorrection }> {
    return this.post('/food-traceability/supplier-corrections', body);
  }
  async updateFoodSupplierCorrection(
    id: string,
    body: PatchFoodSupplierCorrectionBody,
  ): Promise<{ data: FoodSupplierCorrection }> {
    return this.patch(this.ftPath('supplier-corrections', id), body);
  }
  /** AC21 — each lot's retention end from the installed rows; `batch_ids` is a comma list. */
  async listFoodRetention(params: {
    batch_ids: string[];
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodRetentionStatement>> {
    return this.get(
      `/food-traceability/retention${this.ftQuery({
        batch_ids: params.batch_ids.join(','),
        page: params.page,
        page_size: params.page_size,
      })}`,
    );
  }
  async listFoodRoutes(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodRoute>> {
    return this.get(`/food-traceability/routes${this.ftQuery(params)}`);
  }
  async getFoodRoute(id: string): Promise<{ data: FoodRoute }> {
    return this.get(this.ftPath('routes', id));
  }
  async createFoodRoute(body: CreateFoodRouteBody): Promise<{ data: FoodRoute }> {
    return this.post('/food-traceability/routes', body);
  }
  async updateFoodRoute(id: string, body: PatchFoodRouteBody): Promise<{ data: FoodRoute }> {
    return this.patch(this.ftPath('routes', id), body);
  }
  async addFoodRouteReceipt(id: string, body: FoodRouteReceiptBody): Promise<{ data: FoodRoute }> {
    return this.post(this.ftPath('routes', id, 'receipts'), body);
  }
  // Slice E — grants, reviews, complaints
  async listFoodGrants(params?: {
    as_of?: string;
    user_id?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodGrant>> {
    return this.get(`/food-traceability/grants${this.ftQuery(params)}`);
  }
  async getFoodGrant(id: string): Promise<{ data: FoodGrant }> {
    return this.get(this.ftPath('grants', id));
  }
  async createFoodGrant(body: CreateFoodGrantBody): Promise<{ data: FoodGrant }> {
    return this.post('/food-traceability/grants', body);
  }
  async approveFoodGrant(id: string, note: string | null): Promise<{ data: FoodGrant }> {
    return this.post(this.ftPath('grants', id, 'approve'), { note });
  }
  async recordFoodGrantLeaver(
    id: string,
    body: { left_on: string; expected_version?: number },
  ): Promise<{ data: FoodGrant }> {
    return this.post(this.ftPath('grants', id, 'leaver'), body);
  }
  async removeFoodGrant(
    id: string,
    body: { reason: string; expected_version?: number },
  ): Promise<{ data: FoodGrant }> {
    return this.post(this.ftPath('grants', id, 'remove'), body);
  }
  async listFoodGrantReviews(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodGrantReview>> {
    return this.get(`/food-traceability/grant-reviews${this.ftQuery(params)}`);
  }
  async getFoodGrantReview(id: string): Promise<{ data: FoodGrantReview }> {
    return this.get(this.ftPath('grant-reviews', id));
  }
  async createFoodGrantReview(body: {
    review_on: string;
    grant_ids: string[];
  }): Promise<{ data: FoodGrantReview }> {
    return this.post('/food-traceability/grant-reviews', body);
  }
  async decideFoodGrantReview(
    id: string,
    body: { grant_id: string; decision: FoodGrantReviewDecision; reason: string },
  ): Promise<{ data: FoodGrantReview }> {
    return this.post(this.ftPath('grant-reviews', id, 'decisions'), body);
  }
  async listFoodComplaints(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodComplaint>> {
    return this.get(`/food-traceability/complaints${this.ftQuery(params)}`);
  }
  async getFoodComplaint(id: string): Promise<{ data: FoodComplaint }> {
    return this.get(this.ftPath('complaints', id));
  }
  async createFoodComplaint(body: CreateFoodComplaintBody): Promise<{ data: FoodComplaint }> {
    return this.post('/food-traceability/complaints', body);
  }
  async updateFoodComplaint(
    id: string,
    body: PatchFoodComplaintBody,
  ): Promise<{ data: FoodComplaint }> {
    return this.patch(this.ftPath('complaints', id), body);
  }
  async decideFoodComplaintInvestigation(
    id: string,
    body: { investigate: boolean; reason: string; expected_version?: number },
  ): Promise<{ data: FoodComplaint }> {
    return this.post(this.ftPath('complaints', id, 'investigation-decision'), body);
  }
  async closeFoodComplaint(
    id: string,
    body: { reason: string; expected_version?: number },
  ): Promise<{ data: FoodComplaint }> {
    return this.post(this.ftPath('complaints', id, 'close'), body);
  }
  // Slice F — the records-request clock view (AC28)
  async listFoodRecordsRequests(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<FoodRecordsRequestRow>> {
    return this.get(`/food-traceability/records-requests${this.ftQuery(params)}`);
  }
  /**
   * Sprint 62.49.2 (AC4) — the lots and trace events the answer to an authority request carried,
   * replaced whole. Needs the food write and the authority-request write grants.
   */
  /**
   * Gate E12.5 pass 1 (P2-3) — `expectedVersion` is the row's `answer_carried_refs_version` as
   * read (0: never written); a stale one is 409 `VERSION_CONFLICT` and nothing is written.
   */
  async setFoodRecordsRequestCarriedRefs(
    id: string,
    carriedRefs: FoodCarriedRef[],
    expectedVersion: number,
  ): Promise<{ data: FoodRecordsRequestRow }> {
    return this.put(this.ftPath('records-requests', id, 'carried-refs'), {
      carried_refs: carriedRefs,
      expected_version: expectedVersion,
    });
  }
  // Sprint 62.49.0 — manufacturing extensions (S2c): the as-of read and version rows (AC19),
  // and the food recall trigger (AC13).
  async getManufacturingProductAsOf(
    productId: string,
    date: string,
  ): Promise<{ data: MasterDataAsOf<ManufacturingProduct> }> {
    return this.get(
      `/manufacturing/products/${encodeURIComponent(productId)}/as-of${this.ftQuery({ date })}`,
    );
  }
  async getProductBatchAsOf(
    batchId: string,
    date: string,
  ): Promise<{ data: MasterDataAsOf<ProductBatch> }> {
    return this.get(
      `/manufacturing/batches/${encodeURIComponent(batchId)}/as-of${this.ftQuery({ date })}`,
    );
  }
  async listManufacturingProductVersions(
    productId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<PaginatedResponse<MasterDataVersion>> {
    return this.get(
      `/manufacturing/products/${encodeURIComponent(productId)}/versions${this.ftQuery(params)}`,
    );
  }
  async listProductBatchVersions(
    batchId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<PaginatedResponse<MasterDataVersion>> {
    return this.get(
      `/manufacturing/batches/${encodeURIComponent(batchId)}/versions${this.ftQuery(params)}`,
    );
  }
  /** AC13 — what started a food recall case; the source is a declared option of the installed recall template. */
  async putCapaFoodTrigger(
    recordId: string,
    body: PutCapaFoodTriggerBody,
  ): Promise<{ data: CapaRecord }> {
    return this.put(`/manufacturing/capa-recall/${encodeURIComponent(recordId)}/trigger`, body);
  }

  // ── Sprint 62.55.0 — customs tax-administration registers (work file C.1) ──
  // Mounted at `/customs-tax-administration` (app.ts), behind authenticate, enforceOrgScope and the
  // admin rate limiter. Lists are the R-23 envelope `{ data, total, page,
  // page_size }`; pages unwrap `res.data ?? []`. One entity is `{ data }`.
  // Nothing here files, pays, transmits or connects to any customs system:
  // every route reads or writes the organization's own records.
  private ctaQuery(params?: Record<string, string | number | undefined>): string {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    const text = qs.toString();
    return text ? `?${text}` : '';
  }
  private ctaId(id: string): string {
    return encodeURIComponent(id);
  }
  // Core (routes.ts, B1)
  async getCtaRegisterContext(
    surfaceKey: CustomsTaxSurfaceKey,
  ): Promise<{ data: CtaRegisterContext }> {
    return this.get(`/customs-tax-administration/context/${encodeURIComponent(surfaceKey)}`);
  }
  async listCtaProcedures(params?: CtaListParams): Promise<CtaListResponse<CtaProcedureOption>> {
    return this.get(`/customs-tax-administration/procedures${this.ctaQuery(params)}`);
  }
  /** The channels the framework declares; `diagnostics.state` says why a list is empty (AC-A1). */
  async listCtaChannels(params: CtaListParams & { framework_code: string }): Promise<
    CtaListResponse<CtaChannelOption> & {
      diagnostics: { framework_code: string; state: CtaChannelDiscoveryState };
    }
  > {
    return this.get(`/customs-tax-administration/channels${this.ctaQuery(params)}`);
  }
  async listCtaFormNumbering(
    params?: CtaListParams & { procedure_id?: string },
  ): Promise<CtaListResponse<CtaFormNumbering>> {
    return this.get(`/customs-tax-administration/form-numbering${this.ctaQuery(params)}`);
  }
  async listCtaDossiers(
    params?: CtaListParams & {
      register_role?: string;
      procedure_id?: string;
      customs_record_id?: string;
    },
  ): Promise<CtaListResponse<CtaDossierView>> {
    return this.get(`/customs-tax-administration/dossiers${this.ctaQuery(params)}`);
  }
  async getCtaDossier(id: string): Promise<{ data: CtaDossierView }> {
    return this.get(`/customs-tax-administration/dossiers/${this.ctaId(id)}`);
  }
  async createCtaDossier(body: CreateCtaDossierBody): Promise<{ data: CtaDossierView }> {
    return this.post(`/customs-tax-administration/dossiers`, body);
  }
  async updateCtaDossier(
    id: string,
    body: UpdateCtaDossierBody,
  ): Promise<{ data: CtaDossierView }> {
    return this.patch(`/customs-tax-administration/dossiers/${this.ctaId(id)}`, body);
  }
  async listCtaReceivedDocuments(
    params?: CtaListParams & {
      bound_kind?: CtaReceivedBinding['kind'];
      bound_id?: string;
      received_role?: string;
      /** Several received roles, comma-separated; exclusive with `received_role` (gate E12.5 Q1-15). */
      received_roles?: string;
    },
  ): Promise<CtaListResponse<CtaReceivedDocument>> {
    return this.get(`/customs-tax-administration/received-documents${this.ctaQuery(params)}`);
  }
  async getCtaReceivedDocument(id: string): Promise<{ data: CtaReceivedDocument }> {
    return this.get(`/customs-tax-administration/received-documents/${this.ctaId(id)}`);
  }
  async createCtaReceivedDocument(
    body: CreateCtaReceivedDocumentBody,
  ): Promise<{ data: CtaReceivedDocument }> {
    return this.post(`/customs-tax-administration/received-documents`, body);
  }
  async updateCtaReceivedDocument(
    id: string,
    body: UpdateCtaReceivedDocumentBody,
  ): Promise<{ data: CtaReceivedDocument }> {
    return this.patch(`/customs-tax-administration/received-documents/${this.ctaId(id)}`, body);
  }
  /** AC-A7 / AC-C2 notices on one customs record (read by Customs Evidence; nothing is blocked). */
  async getCtaCustomsRecordNotices(
    customsRecordId: string,
  ): Promise<{ data: CtaCustomsRecordNotice[] }> {
    return this.get(
      `/customs-tax-administration/customs-records/${this.ctaId(customsRecordId)}/notices`,
    );
  }
  async listCtaClocks(
    params?: CtaListParams & { owner_side?: 'organization' | 'authority'; state?: string },
  ): Promise<CtaListResponse<CtaClockListRow>> {
    return this.get(`/customs-tax-administration/clocks${this.ctaQuery(params)}`);
  }
  // Payments, difference closures, anchor events (routes-payments.ts, B1)
  async listCtaPayments(
    params?: CtaListParams & { pays_kind?: CtaPaysTarget['kind']; pays_id?: string },
  ): Promise<CtaListResponse<CtaPayment>> {
    return this.get(`/customs-tax-administration/payments${this.ctaQuery(params)}`);
  }
  async getCtaPayment(id: string): Promise<{ data: CtaPayment }> {
    return this.get(`/customs-tax-administration/payments/${this.ctaId(id)}`);
  }
  async createCtaPayment(body: CreateCtaPaymentBody): Promise<{ data: CtaPayment }> {
    return this.post(`/customs-tax-administration/payments`, body);
  }
  async updateCtaPayment(id: string, body: UpdateCtaPaymentBody): Promise<{ data: CtaPayment }> {
    return this.patch(`/customs-tax-administration/payments/${this.ctaId(id)}`, body);
  }
  /** AC-B2: both stated figures, side by side; no subtracted number. */
  async getCtaPaymentComparisons(id: string): Promise<{ data: CtaFigureComparison[] }> {
    return this.get(`/customs-tax-administration/payments/${this.ctaId(id)}/comparisons`);
  }
  async createCtaDifferenceClosure(
    body: CreateCtaDifferenceClosureBody,
  ): Promise<{ data: CtaDifferenceClosure }> {
    return this.post(`/customs-tax-administration/difference-closures`, body);
  }
  // Gap R-2 (2026-10-26) — a dossier's stated figures beside its received documents' (routes-dossier-figures.ts)
  /** Two stated values side by side, paired by key; no subtracted number. */
  async getCtaDossierFigureComparisons(
    dossierId: string,
  ): Promise<{ data: CtaDossierFigureComparison[] }> {
    return this.get(
      `/customs-tax-administration/dossiers/${this.ctaId(dossierId)}/figure-comparisons`,
    );
  }
  async setCtaDossierStatedFigures(
    dossierId: string,
    body: SetCtaDossierStatedFiguresBody,
  ): Promise<{ data: CtaStatedFigure[] }> {
    return this.put(
      `/customs-tax-administration/dossiers/${this.ctaId(dossierId)}/stated-figures`,
      body,
    );
  }
  async createCtaDossierFigureClosure(
    body: CreateCtaDossierFigureClosureBody,
  ): Promise<{ data: CtaDossierFigureClosure }> {
    return this.post(`/customs-tax-administration/dossier-figure-closures`, body);
  }
  async listCtaAnchorEvents(
    params?: CtaListParams & { customs_record_id?: string },
  ): Promise<CtaListResponse<CtaAnchorEventView>> {
    return this.get(`/customs-tax-administration/anchor-events${this.ctaQuery(params)}`);
  }
  async getCtaAnchorEvent(id: string): Promise<{ data: CtaAnchorEventView }> {
    return this.get(`/customs-tax-administration/anchor-events/${this.ctaId(id)}`);
  }
  async createCtaAnchorEvent(
    body: CreateCtaAnchorEventBody,
  ): Promise<{ data: CtaAnchorEventView }> {
    return this.post(`/customs-tax-administration/anchor-events`, body);
  }
  async updateCtaAnchorEvent(
    id: string,
    body: UpdateCtaAnchorEventBody,
  ): Promise<{ data: CtaAnchorEventView }> {
    return this.patch(`/customs-tax-administration/anchor-events/${this.ctaId(id)}`, body);
  }
  // Exports (routes-exports.ts, B1; R-33 through UnifiedExportService)
  async exportCtaRegister(body: CreateCtaExportBody): Promise<{ data: AttachmentRef }> {
    return this.post(`/customs-tax-administration/exports`, body);
  }
  // Duty-free lists, amendments, stop events, deductions, notifications (routes-duty-free.ts, B2)
  async listCtaDutyFreeLists(
    params?: CtaListParams & { dossier_id?: string },
  ): Promise<CtaListResponse<CtaDutyFreeList>> {
    return this.get(`/customs-tax-administration/duty-free-lists${this.ctaQuery(params)}`);
  }
  async getCtaDutyFreeList(id: string): Promise<{ data: CtaDutyFreeList }> {
    return this.get(`/customs-tax-administration/duty-free-lists/${this.ctaId(id)}`);
  }
  async createCtaDutyFreeList(body: CreateCtaDutyFreeListBody): Promise<{ data: CtaDutyFreeList }> {
    return this.post(`/customs-tax-administration/duty-free-lists`, body);
  }
  async updateCtaDutyFreeList(
    id: string,
    body: UpdateCtaDutyFreeListBody,
  ): Promise<{ data: CtaDutyFreeList }> {
    return this.patch(`/customs-tax-administration/duty-free-lists/${this.ctaId(id)}`, body);
  }
  async listCtaListAmendments(
    listId: string,
    params?: CtaListParams,
  ): Promise<CtaListResponse<CtaListAmendment>> {
    return this.get(
      `/customs-tax-administration/duty-free-lists/${this.ctaId(listId)}/amendments${this.ctaQuery(params)}`,
    );
  }
  async createCtaListAmendment(
    listId: string,
    body: CreateCtaListAmendmentBody,
  ): Promise<{ data: CtaListAmendment }> {
    return this.post(
      `/customs-tax-administration/duty-free-lists/${this.ctaId(listId)}/amendments`,
      body,
    );
  }
  async updateCtaListAmendment(
    id: string,
    body: UpdateCtaListAmendmentBody,
  ): Promise<{ data: CtaListAmendment }> {
    return this.patch(`/customs-tax-administration/list-amendments/${this.ctaId(id)}`, body);
  }
  async listCtaListStopEvents(
    listId: string,
    params?: CtaListParams,
  ): Promise<CtaListResponse<CtaListStopEventView>> {
    return this.get(
      `/customs-tax-administration/duty-free-lists/${this.ctaId(listId)}/stop-events${this.ctaQuery(params)}`,
    );
  }
  async createCtaListStopEvent(
    listId: string,
    body: CreateCtaListStopEventBody,
  ): Promise<{ data: CtaListStopEventView }> {
    return this.post(
      `/customs-tax-administration/duty-free-lists/${this.ctaId(listId)}/stop-events`,
      body,
    );
  }
  async updateCtaListStopEvent(
    id: string,
    body: UpdateCtaListStopEventBody,
  ): Promise<{ data: CtaListStopEventView }> {
    return this.patch(`/customs-tax-administration/list-stop-events/${this.ctaId(id)}`, body);
  }
  async listCtaDeductions(
    params?: CtaListParams & { duty_free_list_id?: string },
  ): Promise<CtaListResponse<CtaDeductionView>> {
    return this.get(`/customs-tax-administration/deductions${this.ctaQuery(params)}`);
  }
  async createCtaDeduction(body: CreateCtaDeductionBody): Promise<{ data: CtaDeductionView }> {
    return this.post(`/customs-tax-administration/deductions`, body);
  }
  async updateCtaDeduction(
    id: string,
    body: UpdateCtaDeductionBody,
  ): Promise<{ data: CtaDeductionView }> {
    return this.patch(`/customs-tax-administration/deductions/${this.ctaId(id)}`, body);
  }
  async listCtaUsageNotifications(
    params?: CtaListParams & { duty_free_list_id?: string },
  ): Promise<CtaListResponse<CtaUsageNotification>> {
    return this.get(`/customs-tax-administration/usage-notifications${this.ctaQuery(params)}`);
  }
  async createCtaUsageNotification(
    body: CreateCtaUsageNotificationBody,
  ): Promise<{ data: CtaUsageNotification }> {
    return this.post(`/customs-tax-administration/usage-notifications`, body);
  }
  async updateCtaUsageNotification(
    id: string,
    body: UpdateCtaUsageNotificationBody,
  ): Promise<{ data: CtaUsageNotification }> {
    return this.patch(`/customs-tax-administration/usage-notifications/${this.ctaId(id)}`, body);
  }
  /** The usage schedule, derived on read and stored nowhere (AC-C4, AC-E3, AC-G3). */
  async getCtaUsageSchedule(
    params?: CtaListParams & { duty_free_list_id?: string },
  ): Promise<CtaListResponse<CtaUsageScheduleEntry>> {
    return this.get(`/customs-tax-administration/usage-schedule${this.ctaQuery(params)}`);
  }
  // Refunds, debt instruments, examinations (routes-records.ts, B2)
  async listCtaRefundRecords(
    params?: CtaListParams,
  ): Promise<CtaListResponse<CtaRefundRecordView>> {
    return this.get(`/customs-tax-administration/refund-records${this.ctaQuery(params)}`);
  }
  async getCtaRefundRecord(id: string): Promise<{ data: CtaRefundRecordView }> {
    return this.get(`/customs-tax-administration/refund-records/${this.ctaId(id)}`);
  }
  async createCtaRefundRecord(
    body: CreateCtaRefundRecordBody,
  ): Promise<{ data: CtaRefundRecordView }> {
    return this.post(`/customs-tax-administration/refund-records`, body);
  }
  async updateCtaRefundRecord(
    id: string,
    body: UpdateCtaRefundRecordBody,
  ): Promise<{ data: CtaRefundRecordView }> {
    return this.patch(`/customs-tax-administration/refund-records/${this.ctaId(id)}`, body);
  }
  /** Needs the dossier write grant AND the manufacturing grant (403 `CTA_NORMS_CONFIRMATION_GRANT_REQUIRED`). */
  async confirmCtaRefundNorms(
    id: string,
    body: { confirmed_on: string; version: number },
  ): Promise<{ data: CtaRefundRecordView }> {
    return this.post(
      `/customs-tax-administration/refund-records/${this.ctaId(id)}/norms-confirmation`,
      body,
    );
  }
  async listCtaDebtInstruments(
    params?: CtaListParams,
  ): Promise<CtaListResponse<CtaDebtInstrumentView>> {
    return this.get(`/customs-tax-administration/debt-instruments${this.ctaQuery(params)}`);
  }
  async getCtaDebtInstrument(id: string): Promise<{ data: CtaDebtInstrumentView }> {
    return this.get(`/customs-tax-administration/debt-instruments/${this.ctaId(id)}`);
  }
  async createCtaDebtInstrument(
    body: CreateCtaDebtInstrumentBody,
  ): Promise<{ data: CtaDebtInstrumentView }> {
    return this.post(`/customs-tax-administration/debt-instruments`, body);
  }
  async updateCtaDebtInstrument(
    id: string,
    body: UpdateCtaDebtInstrumentBody,
  ): Promise<{ data: CtaDebtInstrumentView }> {
    return this.patch(`/customs-tax-administration/debt-instruments/${this.ctaId(id)}`, body);
  }
  async listCtaExaminations(params?: CtaListParams): Promise<CtaListResponse<CtaExaminationView>> {
    return this.get(`/customs-tax-administration/examinations${this.ctaQuery(params)}`);
  }
  async getCtaExamination(id: string): Promise<{ data: CtaExaminationView }> {
    return this.get(`/customs-tax-administration/examinations/${this.ctaId(id)}`);
  }
  async createCtaExamination(
    body: CreateCtaExaminationBody,
  ): Promise<{ data: CtaExaminationView }> {
    return this.post(`/customs-tax-administration/examinations`, body);
  }
  async updateCtaExamination(
    id: string,
    body: UpdateCtaExaminationBody,
  ): Promise<{ data: CtaExaminationView }> {
    return this.patch(`/customs-tax-administration/examinations/${this.ctaId(id)}`, body);
  }
  async listCtaExaminationSteps(
    examinationId: string,
    params?: CtaListParams,
  ): Promise<CtaListResponse<CtaExaminationStep>> {
    return this.get(
      `/customs-tax-administration/examinations/${this.ctaId(examinationId)}/steps${this.ctaQuery(params)}`,
    );
  }
  async createCtaExaminationStep(
    examinationId: string,
    body: CreateCtaExaminationStepBody,
  ): Promise<{ data: CtaExaminationStep }> {
    return this.post(
      `/customs-tax-administration/examinations/${this.ctaId(examinationId)}/steps`,
      body,
    );
  }
  async updateCtaExaminationStep(
    id: string,
    body: UpdateCtaExaminationStepBody,
  ): Promise<{ data: CtaExaminationStep }> {
    return this.patch(`/customs-tax-administration/examination-steps/${this.ctaId(id)}`, body);
  }
  async listCtaOfficeExaminations(
    params?: CtaListParams,
  ): Promise<CtaListResponse<CtaOfficeExamination>> {
    return this.get(`/customs-tax-administration/office-examinations${this.ctaQuery(params)}`);
  }
  async createCtaOfficeExamination(
    body: CreateCtaOfficeExaminationBody,
  ): Promise<{ data: CtaOfficeExamination }> {
    return this.post(`/customs-tax-administration/office-examinations`, body);
  }
  async updateCtaOfficeExamination(
    id: string,
    body: UpdateCtaOfficeExaminationBody,
  ): Promise<{ data: CtaOfficeExamination }> {
    return this.patch(`/customs-tax-administration/office-examinations/${this.ctaId(id)}`, body);
  }
  // Transfers, readings, determinations, statement (routes-transition.ts, B2)
  async listCtaProjectTransfers(
    params?: CtaListParams,
  ): Promise<CtaListResponse<CtaProjectTransferView>> {
    return this.get(`/customs-tax-administration/project-transfers${this.ctaQuery(params)}`);
  }
  async getCtaProjectTransfer(id: string): Promise<{ data: CtaProjectTransferView }> {
    return this.get(`/customs-tax-administration/project-transfers/${this.ctaId(id)}`);
  }
  async createCtaProjectTransfer(
    body: CreateCtaProjectTransferBody,
  ): Promise<{ data: CtaProjectTransferView }> {
    return this.post(`/customs-tax-administration/project-transfers`, body);
  }
  async updateCtaProjectTransfer(
    id: string,
    body: UpdateCtaProjectTransferBody,
  ): Promise<{ data: CtaProjectTransferView }> {
    return this.patch(`/customs-tax-administration/project-transfers/${this.ctaId(id)}`, body);
  }
  async getCtaGoverningRules(
    recordType: 'dossier' | 'examination',
    recordId: string,
  ): Promise<{ data: CtaGoverningRulesReading }> {
    return this.get(
      `/customs-tax-administration/governing-rules/${recordType}/${this.ctaId(recordId)}`,
    );
  }
  async listCtaRuleDeterminations(
    params?: CtaListParams & { record_type?: 'dossier' | 'examination'; record_id?: string },
  ): Promise<CtaListResponse<CtaRuleDetermination>> {
    return this.get(`/customs-tax-administration/rule-determinations${this.ctaQuery(params)}`);
  }
  async createCtaRuleDetermination(
    body: CreateCtaRuleDeterminationBody,
  ): Promise<{ data: CtaRuleDetermination }> {
    return this.post(`/customs-tax-administration/rule-determinations`, body);
  }
  async updateCtaRuleDetermination(
    id: string,
    body: UpdateCtaRuleDeterminationBody,
  ): Promise<{ data: CtaRuleDetermination }> {
    return this.patch(`/customs-tax-administration/rule-determinations/${this.ctaId(id)}`, body);
  }
  /** O7 (owner, 2026-10-26) — a record's optional review approval, as anyone who can read it sees it. */
  async getCtaReviewApproval(
    recordType: CtaReviewableRecordType,
    recordId: string,
  ): Promise<{ data: CtaReviewResult }> {
    return this.get(
      `/customs-tax-administration/reviews/${recordType}/${this.ctaId(recordId)}/approval`,
    );
  }
  /** O7 — approve the record as it stands at `version`, as a person other than its recorder. */
  async approveCtaRecord(
    recordType: CtaReviewableRecordType,
    recordId: string,
    body: { version: number; note?: string | null },
  ): Promise<{ data: CtaReviewResult }> {
    return this.post(
      `/customs-tax-administration/reviews/${recordType}/${this.ctaId(recordId)}/approval`,
      body,
    );
  }
  /** O7 — the approver withdraws their own approval. */
  async withdrawCtaApproval(
    recordType: CtaReviewableRecordType,
    recordId: string,
  ): Promise<{ data: CtaReviewResult }> {
    return this.del(
      `/customs-tax-administration/reviews/${recordType}/${this.ctaId(recordId)}/approval`,
    );
  }
  /** AC-E4: a requirement as of an assessed period (strict query; no paging). */
  async getCtaRequirementAsOf(params: {
    framework_code: string;
    requirement_id: string;
    period_from: string;
    period_until: string;
  }): Promise<{ data: CtaRequirementAsOfReading }> {
    return this.get(`/customs-tax-administration/requirements-as-of${this.ctaQuery(params)}`);
  }
  async listCtaGoverningObligations(
    params: CtaListParams & { on: string },
  ): Promise<CtaListResponse<CtaGoverningObligation>> {
    return this.get(`/customs-tax-administration/governing-obligations${this.ctaQuery(params)}`);
  }
  async getCtaLegalTimeline(
    params?: CtaListParams & { framework_code?: string },
  ): Promise<CtaListResponse<CtaLegalTimelineRow>> {
    return this.get(`/customs-tax-administration/legal-timeline${this.ctaQuery(params)}`);
  }
  /** AC-E5: reads in reference mode too. */
  async getCtaTransitionStatement(): Promise<{ data: CtaTransitionStatement }> {
    return this.get(`/customs-tax-administration/transition-statement`);
  }
  /**
   * Sprint 62.55.0 (AC-C1) — the organization's financial-year end, beside the
   * calendar and under the same grant. A save over a newer value is a 409
   * (`FINANCIAL_YEAR_SETTINGS_CHANGED`); the response is the view, unwrapped.
   */
  async updateFinancialYearSettings(
    payload: UpdateFinancialYearSettingsPayload,
  ): Promise<OrgFinancialYearSettingsView> {
    return this.patch('/settings/financial-year', payload);
  }

  // ── Sprint 62.56.0 — biometric capture registers (spec C.1) ──
  // Mounted at `/biometric-capture`. Lists are the R-23 envelope
  // `{ data, total, page, page_size }`; pages unwrap `res.data ?? []`. A single
  // record is `{ data }`. A template's own fields travel in a `values` map keyed
  // by the installed template's field ids. Nothing here sends anything to any
  // system: every route reads or writes the organisation's own records.
  private biometricQuery(params?: object): string {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {}) as Array<[string, unknown]>) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    }
    const text = qs.toString();
    return text ? `?${text}` : '';
  }
  async getBiometricCaptureContext(): Promise<{ data: BiometricCaptureContext }> {
    return this.get('/biometric-capture/context');
  }
  async listBiometricCaptureComponents(params?: {
    page?: number;
    page_size?: number;
    framework_code?: string;
    component_type?: string;
    lifecycle_state?: BiometricComponentLifecycleState;
    organisation_role?: string;
    scope_value?: string;
  }): Promise<PaginatedResponse<BiometricCaptureComponentSummary>> {
    return this.get(`/biometric-capture/components${this.biometricQuery(params)}`);
  }
  async createBiometricCaptureComponent(
    body: CreateBiometricComponentBody,
  ): Promise<{ data: BiometricCaptureComponentDetail }> {
    return this.post('/biometric-capture/components', body);
  }
  async getBiometricCaptureComponent(
    id: string,
  ): Promise<{ data: BiometricCaptureComponentDetail }> {
    return this.get(`/biometric-capture/components/${encodeURIComponent(id)}`);
  }
  async updateBiometricCaptureComponent(
    id: string,
    body: UpdateBiometricComponentBody,
  ): Promise<{ data: BiometricCaptureComponentDetail }> {
    return this.patch(`/biometric-capture/components/${encodeURIComponent(id)}`, body);
  }
  async putBiometricSecurityCheck(
    id: string,
    body: PutBiometricSectionBody,
  ): Promise<{ data: BiometricCaptureComponentDetail }> {
    return this.put(`/biometric-capture/components/${encodeURIComponent(id)}/security-check`, body);
  }
  async putBiometricSoftwareConformity(
    id: string,
    body: PutBiometricSectionBody,
  ): Promise<{ data: BiometricCaptureComponentDetail }> {
    return this.put(
      `/biometric-capture/components/${encodeURIComponent(id)}/software-conformity`,
      body,
    );
  }
  async putBiometricEvidenceArea(
    id: string,
    areaId: string,
    body: PutBiometricEvidenceAreaBody,
  ): Promise<{ data: BiometricCaptureComponentDetail }> {
    return this.put(
      `/biometric-capture/components/${encodeURIComponent(id)}/evidence/${encodeURIComponent(areaId)}`,
      body,
    );
  }
  async transitionBiometricCaptureComponent(
    id: string,
    body: BiometricLifecycleBody,
  ): Promise<{ data: BiometricCaptureComponentDetail }> {
    return this.post(`/biometric-capture/components/${encodeURIComponent(id)}/lifecycle`, body);
  }
  async listBiometricScopeDeterminations(
    componentId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<PaginatedResponse<BiometricScopeDetermination>> {
    return this.get(
      `/biometric-capture/components/${encodeURIComponent(componentId)}/scope-determinations${this.biometricQuery(params)}`,
    );
  }
  async createBiometricScopeDetermination(
    componentId: string,
    body: CreateBiometricScopeDeterminationBody,
  ): Promise<{ data: BiometricScopeDetermination }> {
    return this.post(
      `/biometric-capture/components/${encodeURIComponent(componentId)}/scope-determinations`,
      body,
    );
  }
  async listBiometricComponentLinks(
    componentId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<PaginatedResponse<BiometricCaptureLinkView>> {
    return this.get(
      `/biometric-capture/components/${encodeURIComponent(componentId)}/links${this.biometricQuery(params)}`,
    );
  }
  async createBiometricComponentLink(
    componentId: string,
    body: CreateBiometricLinkBody,
  ): Promise<{ data: BiometricCaptureLinkView }> {
    return this.post(
      `/biometric-capture/components/${encodeURIComponent(componentId)}/links`,
      body,
    );
  }
  async deleteBiometricComponentLink(componentId: string, linkId: string): Promise<void> {
    return this.del(
      `/biometric-capture/components/${encodeURIComponent(componentId)}/links/${encodeURIComponent(linkId)}`,
    );
  }
  /** The components linked to one existing record (the PDPL-side view), with the boundary statements. */
  async listBiometricLinksForTarget(params: {
    target_type: BiometricLinkTargetType;
    target_id: string;
    page?: number;
    page_size?: number;
  }): Promise<BiometricTargetLinksResponse> {
    return this.get(`/biometric-capture/links${this.biometricQuery(params)}`);
  }
  /**
   * Gate E12.5 pass 2 — `sent_from` / `sent_to` (`YYYY-MM-DD`, inclusive) narrow
   * the register by sending date beside the framework, component and state
   * filters; `custody_state: 'withdrawn'` lists the withdrawn transfers.
   */
  async listBiometricCustodyTransfers(
    params?: BiometricCustodyListQuery,
  ): Promise<PaginatedResponse<BiometricCustodyTransferView>> {
    return this.get(`/biometric-capture/custody-transfers${this.biometricQuery(params)}`);
  }
  async createBiometricCustodyTransfer(
    body: CreateBiometricCustodyTransferBody,
  ): Promise<{ data: BiometricCustodyTransferView }> {
    return this.post('/biometric-capture/custody-transfers', body);
  }
  async getBiometricCustodyTransfer(id: string): Promise<{ data: BiometricCustodyTransferView }> {
    return this.get(`/biometric-capture/custody-transfers/${encodeURIComponent(id)}`);
  }
  async updateBiometricCustodyTransfer(
    id: string,
    body: UpdateBiometricCustodyTransferBody,
  ): Promise<{ data: BiometricCustodyTransferView }> {
    return this.patch(`/biometric-capture/custody-transfers/${encodeURIComponent(id)}`, body);
  }
  /**
   * Gate E12.5 pass 2 (owner decision 2026-10-26) — withdraw a transfer, with a
   * reason, by a person who recorded neither half. It stays in the history,
   * reads *Withdrawn* and takes no further write; a corrected transfer is
   * recorded new with `replaces_transfer_id`.
   */
  async withdrawBiometricCustodyTransfer(
    id: string,
    body: WithdrawBiometricCustodyTransferBody,
  ): Promise<{ data: BiometricCustodyTransferView }> {
    return this.post(
      `/biometric-capture/custody-transfers/${encodeURIComponent(id)}/withdraw`,
      body,
    );
  }
  async putBiometricCustodyReceipt(
    id: string,
    body: PutBiometricCustodyReceiptBody,
  ): Promise<{ data: BiometricCustodyTransferView }> {
    return this.put(`/biometric-capture/custody-transfers/${encodeURIComponent(id)}/receipt`, body);
  }
  async listBiometricReadinessReviews(params?: {
    page?: number;
    page_size?: number;
    framework_code?: string;
  }): Promise<PaginatedResponse<BiometricReadinessReview>> {
    return this.get(`/biometric-capture/readiness-reviews${this.biometricQuery(params)}`);
  }
  async createBiometricReadinessReview(
    body: CreateBiometricReadinessReviewBody,
  ): Promise<{ data: BiometricReadinessReview }> {
    return this.post('/biometric-capture/readiness-reviews', body);
  }
  async getBiometricReadinessReview(id: string): Promise<{ data: BiometricReadinessReview }> {
    return this.get(`/biometric-capture/readiness-reviews/${encodeURIComponent(id)}`);
  }
  /** R-1b — once, by a person who neither recorded the review nor is its named reviewer. */
  async confirmBiometricReadinessReview(
    id: string,
    body: ConfirmBiometricReadinessReviewBody,
  ): Promise<{ data: BiometricReadinessReview }> {
    return this.post(
      `/biometric-capture/readiness-reviews/${encodeURIComponent(id)}/confirm`,
      body,
    );
  }
  /**
   * P-2a — records that the linked record was reviewed against the component
   * as it now stands. `component_version` is the version the person reviewed
   * against (the link view's `current_component_version`); 409
   * `BIOMETRIC_RECORD_VERSION_CONFLICT` when the component moved since
   * (gate E12.5 pass 1).
   */
  async reviewBiometricLink(
    linkId: string,
    body: ReviewBiometricLinkBody,
  ): Promise<{ data: BiometricCaptureLinkView }> {
    return this.post(`/biometric-capture/links/${encodeURIComponent(linkId)}/review`, body);
  }
  // Round 2 (O-3a) — deletion records on a component: the execution half, then a
  // second person's confirmation with its evidence.
  async listBiometricDeletionRecords(
    componentId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<PaginatedResponse<BiometricDeletionRecordView>> {
    return this.get(
      `/biometric-capture/components/${encodeURIComponent(componentId)}/deletion-records${this.biometricQuery(params)}`,
    );
  }
  async createBiometricDeletionRecord(
    componentId: string,
    body: CreateBiometricDeletionRecordBody,
  ): Promise<{ data: BiometricDeletionRecordView }> {
    return this.post(
      `/biometric-capture/components/${encodeURIComponent(componentId)}/deletion-records`,
      body,
    );
  }
  async getBiometricDeletionRecord(id: string): Promise<{ data: BiometricDeletionRecordView }> {
    return this.get(`/biometric-capture/deletion-records/${encodeURIComponent(id)}`);
  }
  async putBiometricDeletionConfirmation(
    id: string,
    body: PutBiometricDeletionConfirmationBody,
  ): Promise<{ data: BiometricDeletionRecordView }> {
    return this.put(
      `/biometric-capture/deletion-records/${encodeURIComponent(id)}/confirmation`,
      body,
    );
  }
  // Round 2 (PU-4) — supervision and inspection acts.
  async listBiometricSupervisionActs(params?: {
    page?: number;
    page_size?: number;
    framework_code?: string;
    act_type?: string;
    component_id?: string;
  }): Promise<PaginatedResponse<BiometricSupervisionActView>> {
    return this.get(`/biometric-capture/supervision-acts${this.biometricQuery(params)}`);
  }
  async createBiometricSupervisionAct(
    body: CreateBiometricSupervisionActBody,
  ): Promise<{ data: BiometricSupervisionActView }> {
    return this.post('/biometric-capture/supervision-acts', body);
  }
  async getBiometricSupervisionAct(id: string): Promise<{ data: BiometricSupervisionActView }> {
    return this.get(`/biometric-capture/supervision-acts/${encodeURIComponent(id)}`);
  }
  async updateBiometricSupervisionAct(
    id: string,
    body: UpdateBiometricSupervisionActBody,
  ): Promise<{ data: BiometricSupervisionActView }> {
    return this.patch(`/biometric-capture/supervision-acts/${encodeURIComponent(id)}`, body);
  }
  /** The inspection evidence pack, through `UnifiedExportService`; resolves with the file ref. */
  async exportBiometricCaptureInspectionPack(
    body: BiometricCaptureExportBody,
  ): Promise<AttachmentRef> {
    return this.post('/biometric-capture/export', body);
  }

  // ── Sprint 63.24.0 Slice A — controlled-testing cases (plan §2) ──
  // The list is the R-23 envelope; the page unwraps it with `.data ?? []`.
  // The procedure picker is `getFrameworkProcedures` above (no route of its own).
  async listControlledTestingCases(params?: {
    page?: number;
    page_size?: number;
    framework_code?: string;
    procedure_id?: string;
    state_id?: string;
  }): Promise<ControlledTestingCaseListResponse> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/controlled-testing/cases${suffix}`);
  }
  /** The installed frameworks, each with whether a case can open on it (R-23 envelope). */
  async listControlledTestingFrameworks(params?: {
    page?: number;
    page_size?: number;
  }): Promise<ControlledTestingFrameworkListResponse> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/controlled-testing/frameworks${suffix}`);
  }
  /**
   * Sprint 62.51.0 Remediation W (U15) — the forms a procedure lists, each with
   * the title its installed form record authors, read before a case exists.
   */
  async listControlledTestingApplicationForms(params: {
    framework_code: string;
    procedure_id: string;
  }): Promise<{ data: ControlledTestingApplicationFormOption[] }> {
    const qs = new URLSearchParams({
      framework_code: params.framework_code,
      procedure_id: params.procedure_id,
    });
    return this.get(`/controlled-testing/application-forms?${qs.toString()}`);
  }
  async openControlledTestingCase(
    body: OpenControlledTestingCaseBody,
  ): Promise<{ data: ControlledTestingCaseView }> {
    return this.post('/controlled-testing/cases', body);
  }
  async getControlledTestingCase(caseId: string): Promise<{ data: ControlledTestingCaseView }> {
    return this.get(`/controlled-testing/cases/${encodeURIComponent(caseId)}`);
  }
  async updateControlledTestingCase(
    caseId: string,
    body: UpdateControlledTestingCaseBody,
  ): Promise<{ data: ControlledTestingCaseView }> {
    return this.patch(`/controlled-testing/cases/${encodeURIComponent(caseId)}`, body);
  }
  async recordControlledTestingTransition(
    caseId: string,
    body: RecordControlledTestingTransitionBody,
  ): Promise<{ data: ControlledTestingCaseView }> {
    return this.post(`/controlled-testing/cases/${encodeURIComponent(caseId)}/transitions`, body);
  }

  // ── Sprint 63.24.0 Slice B — personnel eligibility evidence (plan §B) ──
  // Every read is `{ data }`; the list is the R-23 envelope, unwrapped by the page
  // with `.data ?? []`. Every write names `expected_version` (a stale one is 409).
  async getControlledTestingPersonnelVocabulary(
    caseId: string,
  ): Promise<{ data: ControlledTestingPersonnelVocabulary }> {
    return this.get(`/controlled-testing/cases/${encodeURIComponent(caseId)}/personnel-vocabulary`);
  }
  async listControlledTestingPersonnelRecords(
    caseId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<ControlledTestingPersonnelListResponse> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/personnel-records${suffix}`,
    );
  }
  async createControlledTestingPersonnel(
    caseId: string,
    body: CreateControlledTestingPersonnelBody,
  ): Promise<{ data: ControlledTestingPersonnelRecordView }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/personnel-records`,
      body,
    );
  }
  async getControlledTestingPersonnel(
    caseId: string,
    recordId: string,
  ): Promise<{ data: ControlledTestingPersonnelRecordView }> {
    return this.get(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/personnel-records/${encodeURIComponent(recordId)}`,
    );
  }
  async updateControlledTestingPersonnel(
    caseId: string,
    recordId: string,
    body: UpdateControlledTestingPersonnelBody,
  ): Promise<{ data: ControlledTestingPersonnelRecordView }> {
    return this.patch(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/personnel-records/${encodeURIComponent(recordId)}`,
      body,
    );
  }
  async uploadControlledTestingPersonnelEvidence(
    caseId: string,
    recordId: string,
    body: ControlledTestingPersonnelEvidenceBody,
  ): Promise<{ data: ControlledTestingPersonnelEvidenceRef }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/personnel-records/${encodeURIComponent(recordId)}/evidence`,
      body,
    );
  }

  // ── Sprint 63.24.0 Slice C — solution evidence (plan C1–C3) ──
  // One record per case. Reads return the panel (the stored record, or none, and what the
  // installed record declares now); writes are `:manage` on the server.
  async getControlledTestingSolution(
    caseId: string,
  ): Promise<{ data: ControlledTestingSolutionPanel }> {
    return this.get(`/controlled-testing/cases/${encodeURIComponent(caseId)}/solution`);
  }
  async createControlledTestingSolution(
    caseId: string,
    body: CreateControlledTestingSolutionBody,
  ): Promise<{ data: ControlledTestingSolutionPanel }> {
    return this.post(`/controlled-testing/cases/${encodeURIComponent(caseId)}/solution`, body);
  }
  async updateControlledTestingSolution(
    caseId: string,
    body: UpdateControlledTestingSolutionBody,
  ): Promise<{ data: ControlledTestingSolutionPanel }> {
    return this.patch(`/controlled-testing/cases/${encodeURIComponent(caseId)}/solution`, body);
  }
  async uploadControlledTestingSolutionEvidence(
    caseId: string,
    body: ControlledTestingSolutionEvidenceBody,
  ): Promise<{ data: ControlledTestingSolutionEvidenceRef }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/solution/evidence`,
      body,
    );
  }

  // ── Sprint 63.24.0 Slice D — authoritative-source records (D1–D4) ──
  // One row per registry document per case. Reads are `:read`; every write is `:manage`. No
  // method here calls a registry: the rows hold the State Bank's answer, as recorded.
  async listControlledTestingRegistryRecords(
    caseId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<ControlledTestingRegistryListResponse> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/registry-records${suffix}`,
    );
  }
  async createControlledTestingRegistryRecord(
    caseId: string,
    body: CreateControlledTestingRegistryBody,
  ): Promise<{ data: ControlledTestingRegistryRecordPanel }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/registry-records`,
      body,
    );
  }
  async getControlledTestingRegistryRecord(
    caseId: string,
    recordId: string,
  ): Promise<{ data: ControlledTestingRegistryRecordPanel }> {
    return this.get(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/registry-records/${encodeURIComponent(recordId)}`,
    );
  }
  async updateControlledTestingRegistryRecord(
    caseId: string,
    recordId: string,
    body: UpdateControlledTestingRegistryBody,
  ): Promise<{ data: ControlledTestingRegistryRecordPanel }> {
    return this.patch(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/registry-records/${encodeURIComponent(recordId)}`,
      body,
    );
  }
  async uploadControlledTestingRegistryEvidence(
    caseId: string,
    recordId: string,
    body: ControlledTestingRegistryEvidenceBody,
  ): Promise<{ data: ControlledTestingSolutionEvidenceRef }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/registry-records/${encodeURIComponent(recordId)}/evidence`,
      body,
    );
  }

  // ── Sprint 63.24.0 Slice E — child cases: an adjustment or an extension of an approved case ──
  // The list is the R-23 envelope plus the eligibility answer for each kind. A child is read and
  // written only under its parent. No method approves, determines or files.
  async listControlledTestingChildCases(
    caseId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<ControlledTestingChildListResponse> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== undefined) qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/controlled-testing/cases/${encodeURIComponent(caseId)}/child-cases${suffix}`);
  }
  async getControlledTestingParentCopyStatement(
    parentCaseId: string,
    childCaseId: string,
  ): Promise<{ data: import('@vn-grc/shared').ControlledTestingParentCopyStatementView }> {
    return this.get(
      `/controlled-testing/cases/${encodeURIComponent(parentCaseId)}/child-cases/${encodeURIComponent(childCaseId)}/parent-solution/technical-statement`,
    );
  }
  async listControlledTestingChildPublications(
    parentCaseId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<{
    data: import('@vn-grc/shared').ControlledTestingChildPublicationView[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(
      `/controlled-testing/cases/${encodeURIComponent(parentCaseId)}/child-publications${this.buildQuery(params)}`,
    );
  }
  async resumeControlledTestingChildPublication(
    parentCaseId: string,
    operationId: string,
  ): Promise<{ data: import('@vn-grc/shared').ControlledTestingChildPublicationView }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(parentCaseId)}/child-publications/${encodeURIComponent(operationId)}/resume`,
      {},
    );
  }
  /** The route derives immutable file/source/version lineage; no caller file ID or server path is followed. */
  async downloadControlledTestingParentCopyChecked(
    parentCaseId: string,
    childCaseId: string,
    filename: string,
  ): Promise<void> {
    const path = `/controlled-testing/cases/${encodeURIComponent(parentCaseId)}/child-cases/${encodeURIComponent(childCaseId)}/parent-solution/technical-statement/download`;
    const response = await fetch(`${API_BASE}${path}`, { credentials: 'same-origin' });
    if (!response.ok) {
      let body: { error?: string; message?: string; details?: Record<string, unknown> } = {};
      try {
        body = await response.json();
      } catch {
        /* A non-JSON failure remains a typed refusal. */
        body = {};
      }
      this.notifyUnauthorized(response.status);
      this.notifyMfaStepUpRequired(response.status, body.error, path);
      this.notifyPasswordChangeRequired(response.status, body.error);
      throw new ApiError({
        code: statusToErrorCode(response.status),
        status: response.status,
        serverCode: body.error,
        message: body.message || `Download failed (${response.status})`,
        details: body.details,
      });
    }
    const objectUrl = URL.createObjectURL(await response.blob());
    try {
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }
  async openControlledTestingChildCase(
    caseId: string,
    body: OpenControlledTestingChildCaseBody,
  ): Promise<{ data: ControlledTestingCaseView }> {
    return this.post(`/controlled-testing/cases/${encodeURIComponent(caseId)}/child-cases`, body);
  }
  async getControlledTestingChildCase(
    caseId: string,
    childId: string,
  ): Promise<{ data: ControlledTestingCaseView }> {
    return this.get(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/child-cases/${encodeURIComponent(childId)}`,
    );
  }
  async recordControlledTestingChildTransition(
    caseId: string,
    childId: string,
    body: RecordControlledTestingTransitionBody,
  ): Promise<{ data: ControlledTestingCaseView }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/child-cases/${encodeURIComponent(childId)}/transitions`,
      body,
    );
  }

  async recordControlledTestingEvent(
    caseId: string,
    body: RecordControlledTestingEventBody,
  ): Promise<{ data: ControlledTestingCaseView }> {
    return this.post(`/controlled-testing/cases/${encodeURIComponent(caseId)}/events`, body);
  }

  async recordControlledTestingChildEvent(
    caseId: string,
    childId: string,
    body: RecordControlledTestingEventBody,
  ): Promise<{ data: ControlledTestingCaseView }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/child-cases/${encodeURIComponent(childId)}/events`,
      body,
    );
  }

  async recordControlledTestingFiling(
    caseId: string,
    body: RecordControlledTestingFilingBody,
  ): Promise<{ data: ControlledTestingCaseView }> {
    return this.post(`/controlled-testing/cases/${encodeURIComponent(caseId)}/filing`, body);
  }

  async recordControlledTestingChildFiling(
    caseId: string,
    childId: string,
    body: RecordControlledTestingChildFilingBody,
  ): Promise<{ data: ControlledTestingCaseView }> {
    return this.patch(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/child-cases/${encodeURIComponent(childId)}`,
      body,
    );
  }

  async bindControlledTestingChildResultsReport(
    caseId: string,
    childId: string,
    body: { expected_version: number; results_report_file_id: string | null },
  ): Promise<{ data: ControlledTestingCaseView }> {
    return this.patch(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/child-cases/${encodeURIComponent(childId)}`,
      body,
    );
  }

  async uploadControlledTestingChildResultsReport(
    caseId: string,
    childId: string,
    body: ControlledTestingOutcomeEvidenceBody,
  ): Promise<{ data: ControlledTestingOutcomeEvidenceRef }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/child-cases/${encodeURIComponent(childId)}/results-report`,
      body,
    );
  }

  // ── Sprint 63.24.0 Slice F — stop, completion, closure and direction records (F1–F6) ──
  // Reads need the case read grant; every write needs the manage grant. The platform records what
  // a person enters and what the authority gives; no method decides, issues or certifies.
  async getControlledTestingStop(caseId: string): Promise<{ data: ControlledTestingStopView }> {
    return this.get(`/controlled-testing/cases/${encodeURIComponent(caseId)}/stop`);
  }
  async uploadControlledTestingStopEvidence(
    caseId: string,
    body: ControlledTestingOutcomeEvidenceBody,
  ): Promise<{ data: ControlledTestingOutcomeEvidenceRef }> {
    return this.post(`/controlled-testing/cases/${encodeURIComponent(caseId)}/stop/evidence`, body);
  }
  async fileControlledTestingStop(
    caseId: string,
    body: ControlledTestingStopFileBody,
  ): Promise<{ data: ControlledTestingStopView }> {
    return this.post(`/controlled-testing/cases/${encodeURIComponent(caseId)}/stop`, body);
  }
  async recordControlledTestingRevocation(
    caseId: string,
    body: ControlledTestingRevocationBody,
  ): Promise<{ data: ControlledTestingStopView }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/stop/revocation`,
      body,
    );
  }
  async recordControlledTestingStopReceipt(
    caseId: string,
    body: { expected_version: number; received_on: string; evidence_refs: string[] },
  ): Promise<{ data: ControlledTestingStopView }> {
    return this.post(`/controlled-testing/cases/${encodeURIComponent(caseId)}/stop/receipt`, body);
  }
  async recordControlledTestingStopEvent(
    caseId: string,
    body: RecordControlledTestingEventBody,
  ): Promise<{ data: ControlledTestingStopView }> {
    return this.post(`/controlled-testing/cases/${encodeURIComponent(caseId)}/stop/events`, body);
  }
  async recordControlledTestingStopTransition(
    caseId: string,
    body: RecordControlledTestingTransitionBody,
  ): Promise<{ data: ControlledTestingStopView }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/stop/transitions`,
      body,
    );
  }
  async recordControlledTestingAuthorityTermination(
    caseId: string,
    body: ControlledTestingRevocationBody,
  ): Promise<{ data: ControlledTestingStopView }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/authority-termination`,
      body,
    );
  }
  async getControlledTestingCompletion(
    caseId: string,
  ): Promise<{ data: ControlledTestingCompletionView }> {
    return this.get(`/controlled-testing/cases/${encodeURIComponent(caseId)}/completion`);
  }
  async uploadControlledTestingCompletionEvidence(
    caseId: string,
    body: ControlledTestingOutcomeEvidenceBody,
  ): Promise<{ data: ControlledTestingOutcomeEvidenceRef }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/completion/evidence`,
      body,
    );
  }
  async fileControlledTestingCompletion(
    caseId: string,
    body: ControlledTestingCompletionFileBody,
  ): Promise<{ data: ControlledTestingCompletionView }> {
    return this.post(`/controlled-testing/cases/${encodeURIComponent(caseId)}/completion`, body);
  }
  async recordControlledTestingCertificate(
    caseId: string,
    body: ControlledTestingCertificateBody,
  ): Promise<{ data: ControlledTestingCompletionView }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/completion/certificate`,
      body,
    );
  }
  async getControlledTestingClosure(
    caseId: string,
  ): Promise<{ data: ControlledTestingClosureView }> {
    return this.get(`/controlled-testing/cases/${encodeURIComponent(caseId)}/closure`);
  }
  async recordControlledTestingClosure(
    caseId: string,
    body: ControlledTestingClosureBody,
  ): Promise<{ data: ControlledTestingClosureView }> {
    return this.post(`/controlled-testing/cases/${encodeURIComponent(caseId)}/closure`, body);
  }
  async correctControlledTestingClosureAnchor(
    caseId: string,
    anchor: 'stop_decision' | 'settlement_completion',
    body: ControlledTestingAnchorCorrectionBody,
  ): Promise<{ data: ControlledTestingClosureView }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/closure/anchors/${encodeURIComponent(anchor)}/correction`,
      body,
    );
  }
  async listControlledTestingDirectionRecords(
    caseId: string,
  ): Promise<{ data: ControlledTestingDirectionRecordView[]; total: number }> {
    return this.get(`/controlled-testing/cases/${encodeURIComponent(caseId)}/direction-records`);
  }
  /** Fix-sprint-20261016-1956 Finding 11 — the kinds the installed rows of the case's framework declare. */
  async listControlledTestingDirectionKinds(
    caseId: string,
  ): Promise<{ data: ControlledTestingDirectionKindOption[] }> {
    return this.get(`/controlled-testing/cases/${encodeURIComponent(caseId)}/direction-kinds`);
  }
  async recordControlledTestingDirectionRecord(
    caseId: string,
    body: ControlledTestingDirectionRecordBody,
  ): Promise<{ data: ControlledTestingDirectionRecordView }> {
    return this.post(
      `/controlled-testing/cases/${encodeURIComponent(caseId)}/direction-records`,
      body,
    );
  }

  // ── Sprint 62.38.0 — Supervisory Reporting register ──
  private supervisoryQuery(params?: Record<string, string | number | undefined>): string {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) if (v !== undefined) qs.set(k, String(v));
    return qs.toString() ? `?${qs.toString()}` : '';
  }
  /**
   * Sprint 62.45.0 — `on` (YYYY-MM-DD) reads the vocabulary in force on that
   * date; absent, the server reads it for today in the organisation's calendar.
   */
  async getSupervisoryReportingOverview(
    on?: string,
  ): Promise<{ data: SupervisoryReportingOverview }> {
    return this.get(`/supervisory-reporting/overview${this.supervisoryQuery({ on })}`);
  }
  async setSupervisoryReportingRoles(
    roles: string[],
    on?: string,
  ): Promise<{ data: SupervisoryReportingOverview }> {
    return this.put(`/supervisory-reporting/profile${this.supervisoryQuery({ on })}`, { roles });
  }
  async listSupervisoryIndicators(params?: {
    page?: number;
    page_size?: number;
    section?: string;
    lineage_state?: SupervisoryLineageState;
  }): Promise<PaginatedResponse<SupervisoryIndicatorView>> {
    return this.get(`/supervisory-reporting/indicators${this.supervisoryQuery(params)}`);
  }
  async createSupervisoryIndicator(
    body: CreateSupervisoryIndicatorBody,
  ): Promise<{ data: SupervisoryIndicatorView }> {
    return this.post('/supervisory-reporting/indicators', body);
  }
  async updateSupervisoryIndicator(
    indicatorId: string,
    body: UpdateSupervisoryIndicatorBody,
  ): Promise<{ data: SupervisoryIndicatorView }> {
    return this.patch(`/supervisory-reporting/indicators/${encodeURIComponent(indicatorId)}`, body);
  }
  /**
   * F5 — `expectedUpdatedAt` is the row's `updated_at` as the list showed it;
   * a lineage changed since is refused with `SUPERVISORY_CONCURRENT_CHANGE`.
   */
  async approveSupervisoryIndicatorLineage(
    indicatorId: string,
    expectedUpdatedAt: string,
  ): Promise<{ data: SupervisoryIndicatorView }> {
    return this.post(
      `/supervisory-reporting/indicators/${encodeURIComponent(indicatorId)}/approve-lineage`,
      { expected_updated_at: expectedUpdatedAt },
    );
  }
  async listSupervisoryChecks(params?: {
    page?: number;
    page_size?: number;
    section?: string;
  }): Promise<PaginatedResponse<SupervisorySubmissionCheckView>> {
    return this.get(`/supervisory-reporting/submission-checks${this.supervisoryQuery(params)}`);
  }
  async createSupervisoryCheck(
    body: CreateSupervisoryCheckBody,
  ): Promise<{ data: SupervisorySubmissionCheckView }> {
    return this.post('/supervisory-reporting/submission-checks', body);
  }
  async updateSupervisoryCheck(
    checkId: string,
    body: UpdateSupervisoryCheckBody,
  ): Promise<{ data: SupervisorySubmissionCheckView }> {
    return this.patch(
      `/supervisory-reporting/submission-checks/${encodeURIComponent(checkId)}`,
      body,
    );
  }
  /** F5 — approves the check as the list showed it (`expectedUpdatedAt`). */
  async approveSupervisoryCheck(
    checkId: string,
    expectedUpdatedAt: string,
  ): Promise<{ data: SupervisorySubmissionCheckView }> {
    return this.post(
      `/supervisory-reporting/submission-checks/${encodeURIComponent(checkId)}/approve`,
      { expected_updated_at: expectedUpdatedAt },
    );
  }
  /** Slice H — one check, through the restricted-form rule (the calendar's "open check"). */
  async getSupervisoryCheck(checkId: string): Promise<{ data: SupervisorySubmissionCheckView }> {
    return this.get(`/supervisory-reporting/submission-checks/${encodeURIComponent(checkId)}`);
  }
  /**
   * Slice H (AC16) — record the submission on a draft or an approved check,
   * without reopening the approval.
   */
  async recordSupervisorySubmission(
    checkId: string,
    body: RecordSupervisorySubmissionBody,
  ): Promise<{ data: SupervisorySubmissionCheckView }> {
    return this.post(
      `/supervisory-reporting/submission-checks/${encodeURIComponent(checkId)}/submission`,
      body,
    );
  }
  async listSupervisoryCorrections(params?: {
    page?: number;
    page_size?: number;
    section?: string;
    /** Walkthrough N1 — only the records the list marks overdue. */
    overdue?: boolean;
  }): Promise<PaginatedResponse<SupervisoryDataCorrectionView>> {
    const { overdue, ...rest } = params ?? {};
    return this.get(
      `/supervisory-reporting/corrections${this.supervisoryQuery({
        ...rest,
        overdue: overdue ? 'true' : undefined,
      })}`,
    );
  }
  async createSupervisoryCorrection(
    body: CreateSupervisoryCorrectionBody,
  ): Promise<{ data: SupervisoryDataCorrectionView }> {
    return this.post('/supervisory-reporting/corrections', body);
  }
  async updateSupervisoryCorrection(
    correctionId: string,
    body: UpdateSupervisoryCorrectionBody,
  ): Promise<{ data: SupervisoryDataCorrectionView }> {
    return this.patch(
      `/supervisory-reporting/corrections/${encodeURIComponent(correctionId)}`,
      body,
    );
  }
  async listSupervisoryInspections(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<SupervisoryInspectionView>> {
    return this.get(`/supervisory-reporting/inspections${this.supervisoryQuery(params)}`);
  }
  async createSupervisoryInspection(
    body: CreateSupervisoryInspectionBody,
  ): Promise<{ data: SupervisoryInspectionView }> {
    return this.post('/supervisory-reporting/inspections', body);
  }
  async updateSupervisoryInspection(
    inspectionId: string,
    body: UpdateSupervisoryInspectionBody,
  ): Promise<{ data: SupervisoryInspectionView }> {
    return this.patch(
      `/supervisory-reporting/inspections/${encodeURIComponent(inspectionId)}`,
      body,
    );
  }
  /** F5 — closes the inspection as the list showed it (`expectedUpdatedAt`). */
  async closeSupervisoryInspection(
    inspectionId: string,
    expectedUpdatedAt: string,
  ): Promise<{ data: SupervisoryInspectionView }> {
    return this.post(
      `/supervisory-reporting/inspections/${encodeURIComponent(inspectionId)}/close`,
      { expected_updated_at: expectedUpdatedAt },
    );
  }
  /** Sprint 62.45.0 — the period evidence pack, rendered by `UnifiedExportService` (R-33). */
  async exportSupervisoryPeriodPack(
    body: SupervisoryPeriodExportBody,
  ): Promise<{ data: AttachmentRef }> {
    return this.post('/supervisory-reporting/submission-checks/export', body);
  }
  /** Sprint 62.45.0 — the inspection evidence pack, rendered by `UnifiedExportService` (R-33). */
  async exportSupervisoryInspectionPack(
    inspectionId: string,
    format: RenderableExportFormat,
    locale?: SupervisoryExportLocale,
  ): Promise<{ data: AttachmentRef }> {
    return this.post(
      `/supervisory-reporting/inspections/${encodeURIComponent(inspectionId)}/export`,
      locale ? { format, locale } : { format },
    );
  }
  // Sprint 62.45.0 — service-interruption notices
  async listSupervisoryInterruptionNotices(params?: {
    page?: number;
    page_size?: number;
    incident_id?: string;
  }): Promise<PaginatedResponse<SupervisoryInterruptionNoticeView>> {
    return this.get(`/supervisory-reporting/interruption-notices${this.supervisoryQuery(params)}`);
  }
  async getSupervisoryInterruptionNotice(
    noticeId: string,
  ): Promise<{ data: SupervisoryInterruptionNoticeView }> {
    return this.get(`/supervisory-reporting/interruption-notices/${encodeURIComponent(noticeId)}`);
  }
  async createSupervisoryInterruptionNotice(
    body: CreateSupervisoryInterruptionNoticeBody,
  ): Promise<{ data: SupervisoryInterruptionNoticeView }> {
    return this.post('/supervisory-reporting/interruption-notices', body);
  }
  async updateSupervisoryInterruptionNotice(
    noticeId: string,
    body: UpdateSupervisoryInterruptionNoticeBody,
  ): Promise<{ data: SupervisoryInterruptionNoticeView }> {
    return this.patch(
      `/supervisory-reporting/interruption-notices/${encodeURIComponent(noticeId)}`,
      body,
    );
  }
  // Sprint 62.45.0 — change and contract notices
  async listSupervisoryEventNotices(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<SupervisoryEventNoticeView>> {
    return this.get(`/supervisory-reporting/event-notices${this.supervisoryQuery(params)}`);
  }
  async createSupervisoryEventNotice(
    body: CreateSupervisoryEventNoticeBody,
  ): Promise<{ data: SupervisoryEventNoticeView }> {
    return this.post('/supervisory-reporting/event-notices', body);
  }
  async updateSupervisoryEventNotice(
    noticeId: string,
    body: UpdateSupervisoryEventNoticeBody,
  ): Promise<{ data: SupervisoryEventNoticeView }> {
    return this.patch(`/supervisory-reporting/event-notices/${encodeURIComponent(noticeId)}`, body);
  }
  // Sprint 62.45.0 Slice H — the calendar, the date switch, the interruption
  // duty, letter recommendations and evidence binding.
  /** AC13–AC14, AC18 — the reporting calendar over a period-end range, derived on read. */
  async getSupervisoryCalendar(params?: {
    from?: string;
    to?: string;
    page?: number;
    page_size?: number;
    /** Walkthrough N1 — only rows in this state. */
    state?: SupervisoryCalendarRow['state'];
  }): Promise<SupervisoryCalendar> {
    return this.get(`/supervisory-reporting/calendar${this.supervisoryQuery(params)}`);
  }
  /** AC25 — what changes between two dates; `to` defaults to the next announced set. */
  async getSupervisoryVocabularyChanges(params?: {
    from?: string;
    to?: string;
  }): Promise<{ data: SupervisoryVocabularyChanges }> {
    return this.get(`/supervisory-reporting/vocabulary/changes${this.supervisoryQuery(params)}`);
  }
  /** AC20 — incidents over a held role's threshold with no notice record. */
  async listSupervisoryUnrecordedInterruptions(params?: {
    page?: number;
    page_size?: number;
  }): Promise<SupervisoryUnrecordedInterruptionList> {
    return this.get(
      `/supervisory-reporting/interruption-notices/unrecorded${this.supervisoryQuery(params)}`,
    );
  }
  /** AC20 — the duty one incident raises (the incident card's notice). */
  async getSupervisoryInterruptionDuty(
    incidentId: string,
  ): Promise<{ data: SupervisoryInterruptionDuty }> {
    return this.get(
      `/supervisory-reporting/incidents/${encodeURIComponent(incidentId)}/interruption-duty`,
    );
  }
  /** AC23 — bind the notice's evidence files (PATCH only). */
  async bindSupervisoryInterruptionEvidence(
    noticeId: string,
    body: BindSupervisoryInterruptionEvidenceBody,
  ): Promise<{ data: SupervisoryInterruptionNoticeView }> {
    return this.patch(
      `/supervisory-reporting/interruption-notices/${encodeURIComponent(noticeId)}`,
      body,
    );
  }
  /** AC22 — both kinds of recommendation: letter rows and inspection follow-ups. */
  async listSupervisoryRecommendations(params?: {
    page?: number;
    page_size?: number;
    kind?: SupervisoryRecommendationKind;
    /** Walkthrough N1 — only the rows the list marks overdue. */
    overdue?: boolean;
  }): Promise<
    PaginatedResponse<SupervisoryRecommendationRow> & {
      /** A source held more than the server reads at once; `total` is still exact. */
      truncated: boolean;
      /** How many rows the list can page through — fewer than `total` when truncated. */
      listed: number;
    }
  > {
    const { overdue, ...rest } = params ?? {};
    return this.get(
      `/supervisory-reporting/recommendations${this.supervisoryQuery({
        ...rest,
        overdue: overdue ? 'true' : undefined,
      })}`,
    );
  }
  async createSupervisoryRecommendation(
    body: CreateSupervisoryRecommendationBody,
  ): Promise<{ data: SupervisoryRecommendationView }> {
    return this.post('/supervisory-reporting/recommendations', body);
  }
  async updateSupervisoryRecommendation(
    recommendationId: string,
    body: UpdateSupervisoryRecommendationBody,
  ): Promise<{ data: SupervisoryRecommendationView }> {
    return this.patch(
      `/supervisory-reporting/recommendations/${encodeURIComponent(recommendationId)}`,
      body,
    );
  }

  // ── Sprint 62.46.0 — the AML programme register (`/api/v1/aml-programme`) ──
  //
  // Paths and body keys traced from `modules/aml-programme/routes*.ts` and the
  // `.strict()` schemas beside them (R-26). Every body is the shared contract
  // type, sent whole: a strict schema refuses an extra key and a subset
  // assertion cannot see a missing one. No method here files, transmits or
  // generates a report — `recordAmlFilingRecord` records the reference of a
  // report the organisation filed itself.
  async getAmlProgrammeOverview(): Promise<{ data: AmlProgrammeOverview }> {
    return this.get('/aml-programme/overview');
  }
  async getAmlProgrammeVocabulary(): Promise<{ data: AmlProgrammeVocabulary }> {
    return this.get('/aml-programme/vocabulary');
  }
  async getAmlScopingHistory(): Promise<{ data: AmlScopingHistory }> {
    return this.get('/aml-programme/scoping');
  }
  async recordAmlScoping(body: AmlScopingBody): Promise<{ data: AmlScopingRecord }> {
    return this.post('/aml-programme/scoping', body);
  }
  async setAmlTwoPersonPolicy(
    body: AmlTwoPersonPolicyBody,
  ): Promise<{ data: AmlTwoPersonPolicyView }> {
    return this.put('/aml-programme/settings/two-person-policy', body);
  }
  async getAmlRetentionScope(): Promise<{ data: AmlRetentionScopeView }> {
    return this.get('/aml-programme/retention-scope');
  }
  async createAmlRetentionScopeRow(
    body: AmlRetentionScopeCreateBody,
  ): Promise<{ data: AmlRetentionScopeRow }> {
    return this.post('/aml-programme/retention-scope', body);
  }
  async updateAmlRetentionScopeRow(
    rowId: string,
    body: AmlRetentionScopeUpdateBody,
  ): Promise<{ data: AmlRetentionScopeRow }> {
    return this.patch(`/aml-programme/retention-scope/${encodeURIComponent(rowId)}`, body);
  }
  async getAmlIndicatorCoverage(): Promise<{ data: AmlIndicatorCoverageView }> {
    return this.get('/aml-programme/indicator-coverage');
  }
  /** `indicatorCode` is the catalogue's `framework_indicator_code`, read from the view. */
  async putAmlIndicatorRow(
    indicatorCode: string,
    body: AmlIndicatorRowBody,
  ): Promise<{ data: AmlIndicatorRow }> {
    return this.put(
      `/aml-programme/indicator-coverage/rows/${encodeURIComponent(indicatorCode)}`,
      body,
    );
  }
  async createAmlEntityIndicator(
    body: AmlEntityIndicatorCreateBody,
  ): Promise<{ data: AmlIndicatorRow }> {
    return this.post('/aml-programme/indicator-coverage/entity-rows', body);
  }
  async updateAmlEntityIndicator(
    rowId: string,
    body: AmlEntityIndicatorUpdateBody,
  ): Promise<{ data: AmlIndicatorRow }> {
    return this.patch(
      `/aml-programme/indicator-coverage/entity-rows/${encodeURIComponent(rowId)}`,
      body,
    );
  }
  async listAmlIndicatorConfigVersions(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<AmlIndicatorConfigVersionSummary>> {
    return this.get(`/aml-programme/indicator-coverage/versions${this.supervisoryQuery(params)}`);
  }
  /** Sprint 63.31.0 (#436 S6) — the frozen rows and what changed from the version before. */
  async getAmlIndicatorConfigVersion(
    versionId: string,
  ): Promise<{ data: AmlIndicatorConfigVersionDetail }> {
    return this.get(`/aml-programme/indicator-coverage/versions/${encodeURIComponent(versionId)}`);
  }
  async approveAmlIndicatorConfigVersion(
    body: AmlConfigVersionApproveBody,
  ): Promise<{ data: AmlIndicatorConfigVersion }> {
    return this.post('/aml-programme/indicator-coverage/versions', body);
  }
  /** `aml_case:read` — 403 without it, which names no case. */
  async listAmlCases(params?: {
    page?: number;
    page_size?: number;
    status?: AmlCaseStatus;
    /** Gate E12.5 pass 1 (P3-C9) — a case number, searched on the server. */
    search?: string;
    /** `'true'` leaves out closed cases (the merge-target picker). */
    exclude_closed?: 'true' | 'false';
  }): Promise<AmlCaseList> {
    return this.get(`/aml-programme/cases${this.supervisoryQuery(params)}`);
  }
  async openAmlCase(body: AmlCaseCreateBody): Promise<{ data: AmlCase }> {
    return this.post('/aml-programme/cases', body);
  }
  /** 404 — identical for an unknown case and one the reader may not see. */
  async getAmlCase(caseId: string): Promise<{ data: AmlCaseDetail }> {
    return this.get(`/aml-programme/cases/${encodeURIComponent(caseId)}`);
  }
  async updateAmlCase(caseId: string, body: AmlCaseUpdateBody): Promise<{ data: AmlCase }> {
    return this.patch(`/aml-programme/cases/${encodeURIComponent(caseId)}`, body);
  }
  /** A person's determination; the server takes the determiner from the session. */
  async recordAmlCaseDetermination(
    caseId: string,
    body: AmlCaseDeterminationBody,
  ): Promise<{ data: AmlCase }> {
    return this.post(`/aml-programme/cases/${encodeURIComponent(caseId)}/determination`, body);
  }
  async approveAmlCase(caseId: string, body: AmlCaseApprovalBody): Promise<{ data: AmlCase }> {
    return this.post(`/aml-programme/cases/${encodeURIComponent(caseId)}/approval`, body);
  }
  /** Records the reference of a report the organisation filed itself. Files nothing. */
  async recordAmlFilingRecord(
    caseId: string,
    body: AmlFilingRecordBody,
  ): Promise<{ data: AmlCase }> {
    return this.post(`/aml-programme/cases/${encodeURIComponent(caseId)}/filing-record`, body);
  }
  async closeAmlCase(caseId: string, body: AmlCaseCloseBody): Promise<{ data: AmlCase }> {
    return this.post(`/aml-programme/cases/${encodeURIComponent(caseId)}/close`, body);
  }
  async requestAmlCddRefresh(caseId: string, body: AmlCddRefreshBody): Promise<{ data: AmlCase }> {
    return this.post(`/aml-programme/cases/${encodeURIComponent(caseId)}/cdd-refresh`, body);
  }
  /**
   * fix-sprint-20261003-0545 F5/F15 — remove a misfiled evidence file from an
   * OPEN case, with a reason kept on the case. The case's own route, never the
   * generic attachment delete: the removal is recorded on the case first, and
   * that record is what releases the case's hold on the file.
   */
  async removeAmlCaseEvidence(
    caseId: string,
    body: { file_id: string; reason: string; expected_updated_at: string },
  ): Promise<{ data: AmlCase }> {
    return this.post(`/aml-programme/cases/${encodeURIComponent(caseId)}/evidence-removals`, body);
  }
  async listAmlBeneficialOwners(params?: {
    diligence_link_id?: string;
    /** `current` lists one row per due diligence record; absent lists every version. */
    status?: 'current' | 'superseded';
    page?: number;
    limit?: number;
  }): Promise<AmlBeneficialOwnerList> {
    return this.get(`/aml-programme/beneficial-owners${this.supervisoryQuery(params)}`);
  }
  async createAmlBeneficialOwnerRecord(
    body: AmlBeneficialOwnerCreateBody,
  ): Promise<{ data: AmlBeneficialOwnerRecordView }> {
    return this.post('/aml-programme/beneficial-owners', body);
  }
  async updateAmlBeneficialOwnerRecord(
    recordId: string,
    body: AmlBeneficialOwnerUpdateBody,
  ): Promise<{ data: AmlBeneficialOwnerRecordView }> {
    return this.patch(`/aml-programme/beneficial-owners/${encodeURIComponent(recordId)}`, body);
  }
  async listAmlLaunches(params?: { page?: number; limit?: number }): Promise<AmlProductLaunchList> {
    return this.get(`/aml-programme/launches${this.supervisoryQuery(params)}`);
  }
  async getAmlLaunch(launchId: string): Promise<{ data: AmlProductLaunchView }> {
    return this.get(`/aml-programme/launches/${encodeURIComponent(launchId)}`);
  }
  async createAmlLaunch(body: AmlLaunchCreateBody): Promise<{ data: AmlProductLaunch }> {
    return this.post('/aml-programme/launches', body);
  }
  async recordAmlLaunchScopeDetermination(
    launchId: string,
    body: AmlLaunchScopeDeterminationBody,
  ): Promise<{ data: AmlProductLaunch }> {
    return this.post(
      `/aml-programme/launches/${encodeURIComponent(launchId)}/scope-determination`,
      body,
    );
  }
  async createAmlLaunchAssessment(
    launchId: string,
    body: AmlLaunchAssessmentCreateBody,
  ): Promise<{ data: AmlLaunchAssessment }> {
    return this.post(`/aml-programme/launches/${encodeURIComponent(launchId)}/assessments`, body);
  }
  async updateAmlLaunchAssessment(
    assessmentId: string,
    body: AmlLaunchAssessmentUpdateBody,
  ): Promise<{ data: AmlLaunchAssessment }> {
    return this.patch(`/aml-programme/assessments/${encodeURIComponent(assessmentId)}`, body);
  }
  async decideAmlLaunchAssessment(
    assessmentId: string,
    body: AmlLaunchAssessmentDecisionBody,
  ): Promise<{ data: AmlLaunchAssessment }> {
    return this.post(
      `/aml-programme/assessments/${encodeURIComponent(assessmentId)}/decision`,
      body,
    );
  }
  async approveAmlLaunch(
    launchId: string,
    body: AmlLaunchApproveBody,
  ): Promise<{ data: AmlProductLaunch }> {
    return this.post(`/aml-programme/launches/${encodeURIComponent(launchId)}/approve`, body);
  }
  async getAmlGuidanceTracking(): Promise<{ data: AmlGuidanceTrackingView }> {
    return this.get('/aml-programme/guidance-tracking');
  }
  // ── Sprint 63.31.0 (#435, #436) — maker-checker and the use-case steps ──
  /** 7a — one person signs a retention-scope entry as it now stands. */
  async approveAmlRetentionScopeRow(
    rowId: string,
    body: AmlRetentionScopeApprovalBody,
  ): Promise<{ data: AmlRetentionScopeRow }> {
    return this.post(`/aml-programme/retention-scope/${encodeURIComponent(rowId)}/approvals`, body);
  }
  /** S13 — records a related action on a case. The platform takes none. */
  async recordAmlCaseRelatedAction(
    caseId: string,
    body: AmlCaseRelatedActionBody,
  ): Promise<{ data: AmlCase }> {
    return this.post(`/aml-programme/cases/${encodeURIComponent(caseId)}/related-actions`, body);
  }
  /** S14 — records which installed event starts a case's retention period, and its date. */
  async recordAmlCaseRetentionStart(
    caseId: string,
    body: AmlCaseRetentionStartBody,
  ): Promise<{ data: AmlCase }> {
    return this.post(`/aml-programme/cases/${encodeURIComponent(caseId)}/retention-start`, body);
  }
  /**
   * Gate E12.5 pass 2 (P2-N1) — a second holder of `aml_case:approve` confirms
   * the retention start another person recorded under the two-person rule.
   */
  async confirmAmlCaseRetentionStart(
    caseId: string,
    body: { expected_updated_at: string },
  ): Promise<{ data: AmlCase }> {
    return this.post(
      `/aml-programme/cases/${encodeURIComponent(caseId)}/retention-start/confirm`,
      body,
    );
  }
  /** L7 — set or clear a launch's post-launch review date. */
  async setAmlLaunchPostLaunchReview(
    launchId: string,
    body: AmlLaunchPostLaunchReviewBody,
  ): Promise<{ data: AmlProductLaunch }> {
    return this.post(
      `/aml-programme/launches/${encodeURIComponent(launchId)}/post-launch-review`,
      body,
    );
  }
  /** L4 — review tasks on the indicator rows an assessment affects. */
  async raiseAmlIndicatorReviewTasks(
    assessmentId: string,
    body: AmlIndicatorReviewTaskCreateBody,
  ): Promise<{ data: AmlIndicatorReviewTaskRaise }> {
    return this.post(
      `/aml-programme/assessments/${encodeURIComponent(assessmentId)}/indicator-review-tasks`,
      body,
    );
  }
  /** L4 — a person records the review a task asked for. */
  async completeAmlIndicatorReviewTask(
    taskId: string,
    body: AmlIndicatorReviewTaskCompleteBody,
  ): Promise<{ data: AmlIndicatorReviewTask }> {
    return this.post(
      `/aml-programme/indicator-coverage/review-tasks/${encodeURIComponent(taskId)}/complete`,
      body,
    );
  }
  /**
   * The inspection pack for a period, rendered through `UnifiedExportService`
   * (R-33). Case narratives only with `include_case_narratives` AND
   * `aml_case:approve`; the server refuses otherwise.
   */
  async exportAmlInspectionPack(body: AmlInspectionPackBody): Promise<{
    data: AttachmentRef;
    counts: AmlInspectionPackCounts;
    include_case_narratives: boolean;
  }> {
    return this.post('/aml-programme/inspection-pack', body);
  }

  // ── Sprint 62.39.0 — Cross-Border Identity Exchange register ──
  async getIdentityExchangeVocabulary(): Promise<{ data: IdentityExchangeVocabulary }> {
    return this.get('/trust-evidence/identity-exchanges/kinds');
  }
  async listIdentityExchanges(params?: {
    page?: number;
    page_size?: number;
    status?: IdentityExchangeStatus;
  }): Promise<PaginatedResponse<IdentityExchangeView>> {
    return this.get(`/trust-evidence/identity-exchanges${this.supervisoryQuery(params)}`);
  }
  async getIdentityExchange(exchangeId: string): Promise<{ data: IdentityExchangeView }> {
    return this.get(`/trust-evidence/identity-exchanges/${encodeURIComponent(exchangeId)}`);
  }
  async createIdentityExchange(
    body: CreateIdentityExchangeBody,
  ): Promise<{ data: IdentityExchangeView }> {
    return this.post('/trust-evidence/identity-exchanges', body);
  }
  async updateIdentityExchange(
    exchangeId: string,
    body: UpdateIdentityExchangeBody,
  ): Promise<{ data: IdentityExchangeView }> {
    return this.patch(`/trust-evidence/identity-exchanges/${encodeURIComponent(exchangeId)}`, body);
  }
  async changeIdentityExchangeStatus(
    exchangeId: string,
    status: IdentityExchangeStatus,
  ): Promise<{ data: IdentityExchangeView }> {
    return this.post(
      `/trust-evidence/identity-exchanges/${encodeURIComponent(exchangeId)}/status`,
      { status },
    );
  }

  // ── Sprint 62.24.0: the offering scoping decision (AC8) — a person's, never derived ──
  async recordOfferingScopingDecision(
    offeringId: string,
    body: RecordOfferingScopingDecisionRequest,
  ): Promise<{ data: RegulatedOfferingRegistration }> {
    return this.put(
      `/regulatory-classification/offerings/${encodeURIComponent(offeringId)}/scoping-decision`,
      body,
    );
  }

  async listMarketingApprovals(params?: {
    page?: number;
    page_size?: number;
    status?: string;
  }): Promise<{ data: MarketingClaimApproval[]; total: number; page: number; page_size: number }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/sector-licensing/marketing-approvals${suffix}`);
  }
  async createMarketingApproval(body: unknown): Promise<{ data: MarketingClaimApproval }> {
    return this.post('/sector-licensing/marketing-approvals', body);
  }
  async getMarketingApproval(id: string): Promise<{ data: MarketingClaimApproval }> {
    return this.get(`/sector-licensing/marketing-approvals/${encodeURIComponent(id)}`);
  }
  /**
   * The gate. A refusal is HTTP 409 `MARKETING_GATE_REFUSED` whose `details`
   * carry `{ reason, citation, note, pack_code, kind_declared_by_pack,
   * attempted_by, attempted_at }` — the same facts recorded on the approval's
   * `gate_attempts`; the page renders the citation and who attempted it.
   */
  async approveMarketingApproval(id: string): Promise<{ data: MarketingClaimApproval }> {
    return this.post(`/sector-licensing/marketing-approvals/${encodeURIComponent(id)}/approve`, {});
  }
  async withdrawMarketingApproval(id: string): Promise<{ data: MarketingClaimApproval }> {
    return this.post(
      `/sector-licensing/marketing-approvals/${encodeURIComponent(id)}/withdraw`,
      {},
    );
  }

  // ── Sprint 62.19.0 — investor protection ──

  /** Installed fine areas, readable with ANY of the three read permissions. */
  async listInstalledFineAreas(): Promise<{
    data: InstalledFineArea[];
    /** Existing citation labels only; the picker uses data. */
    content?: InstalledFineArea[];
    declaration_absence?: DeclarationAbsence | null;
  }> {
    return this.get('/investor-protection/fine-areas');
  }

  async listInvestorVerifications(params?: {
    page?: number;
    page_size?: number;
    onboarding_case_reference?: string;
  }): Promise<{
    data: InvestorVerificationEvidence[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.onboarding_case_reference) {
      qs.set('onboarding_case_reference', params.onboarding_case_reference);
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/investor-protection/verifications${suffix}`);
  }
  /** `verified_at` MUST be an instant with a zone — pass `toIsoInstant(x) ?? x`. */
  async createInvestorVerification(body: {
    onboarding_case_reference: string;
    investor_type: string;
    verification_method: string;
    verification_outcome: string;
    verifier_user_id: string;
    verified_at: string;
    evidence_refs: string[];
    cdd_record_reference?: string | null;
    aml_evidence_ref?: string | null;
    fine_area_id?: string | null;
    notes?: string | null;
  }): Promise<{ data: InvestorVerificationEvidence }> {
    return this.post('/investor-protection/verifications', body);
  }
  async getInvestorVerification(id: string): Promise<{ data: InvestorVerificationEvidence }> {
    return this.get(`/investor-protection/verifications/${encodeURIComponent(id)}`);
  }
  async updateInvestorVerification(
    id: string,
    body: Partial<{
      onboarding_case_reference: string;
      investor_type: string;
      verification_method: string;
      verification_outcome: string;
      verifier_user_id: string;
      verified_at: string;
      evidence_refs: string[];
      cdd_record_reference: string | null;
      aml_evidence_ref: string | null;
      fine_area_id: string | null;
      notes: string | null;
    }>,
  ): Promise<{ data: InvestorVerificationEvidence }> {
    return this.patch(`/investor-protection/verifications/${encodeURIComponent(id)}`, body);
  }

  /** Every list and get of this register is audit-logged server-side as an access (AC4). */
  async listAccountDataDisclosures(params?: {
    page?: number;
    page_size?: number;
    operation_type?: string;
    decision?: AccountDataDisclosureDecision;
  }): Promise<{ data: AccountDataDisclosure[]; total: number; page: number; page_size: number }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.operation_type) qs.set('operation_type', params.operation_type);
    if (params?.decision) qs.set('decision', params.decision);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/investor-protection/account-data-disclosures${suffix}`);
  }
  /** `occurred_at` MUST be an instant with a zone; `justification_text` ≥ ACCOUNT_DATA_JUSTIFICATION_MIN_LENGTH after trim. */
  async createAccountDataDisclosure(body: {
    operation_type: string;
    dataset_reference: string;
    recipient?: string | null;
    /** The basis as the operator states it. */
    lawful_basis: string;
    /** The article it rests on — required by the server (Sprint 62.27.0 gate E12.5 pass 2). */
    lawful_basis_citation: string;
    justification_text: string;
    decision: AccountDataDisclosureDecision;
    decided_by: string;
    occurred_at: string;
    evidence_refs: string[];
    fine_area_id?: string | null;
  }): Promise<{ data: AccountDataDisclosure }> {
    return this.post('/investor-protection/account-data-disclosures', body);
  }
  async getAccountDataDisclosure(id: string): Promise<{ data: AccountDataDisclosure }> {
    return this.get(`/investor-protection/account-data-disclosures/${encodeURIComponent(id)}`);
  }

  async listRegulatoryDisclosures(params?: {
    page?: number;
    page_size?: number;
    status?: RegulatoryDisclosureStatus;
    kind?: RegulatoryDisclosureKind;
  }): Promise<{ data: RegulatoryDisclosure[]; total: number; page: number; page_size: number }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.status) qs.set('status', params.status);
    if (params?.kind) qs.set('kind', params.kind);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/investor-protection/disclosures${suffix}`);
  }
  async createRegulatoryDisclosure(body: {
    kind: RegulatoryDisclosureKind;
    title: string;
    summary: string;
    evidence_refs: string[];
    fine_area_ids?: string[];
  }): Promise<{ data: RegulatoryDisclosure }> {
    return this.post('/investor-protection/disclosures', body);
  }
  async getRegulatoryDisclosure(id: string): Promise<{ data: RegulatoryDisclosure }> {
    return this.get(`/investor-protection/disclosures/${encodeURIComponent(id)}`);
  }
  /** Appends version n+1; the record returns to `draft` and its approver is cleared. */
  async addRegulatoryDisclosureVersion(
    id: string,
    body: { summary: string; evidence_refs: string[] },
  ): Promise<{ data: RegulatoryDisclosure }> {
    return this.post(`/investor-protection/disclosures/${encodeURIComponent(id)}/versions`, body);
  }
  /** The caller becomes the named approver. 409 `DISCLOSURE_NOT_DRAFT` otherwise. */
  async approveRegulatoryDisclosure(id: string): Promise<{ data: RegulatoryDisclosure }> {
    return this.post(`/investor-protection/disclosures/${encodeURIComponent(id)}/approve`, {});
  }
  /** 409 `DISCLOSURE_NOT_APPROVED` without a named approver; 400 `DISCLOSURE_PUBLICATION_EVIDENCE_REQUIRED` with no refs. */
  async publishRegulatoryDisclosure(
    id: string,
    body: { publication_evidence_refs: string[] },
  ): Promise<{ data: RegulatoryDisclosure }> {
    return this.post(`/investor-protection/disclosures/${encodeURIComponent(id)}/publish`, body);
  }
  /**
   * Pack guidance for the investor-protection registers, selected by one of
   * `BOUNDARY_GUIDANCE_TAGS`. Served by the investor-protection module (any of
   * its three read permissions) so a reader of these registers need not hold
   * `sector_licence:read` for the sector-licensing copy of this endpoint.
   */
  async listInvestorProtectionBoundaryStatements(
    tag: BoundaryGuidanceTag,
  ): Promise<{ data: BoundaryStatement[] }> {
    return this.get('/investor-protection/boundary-statements?tag=' + encodeURIComponent(tag));
  }

  // ── Sprint 62.18.0 — issued records (student records): kinds, scope, records, versions, consents, sharing, evidence, exports ──

  /** Record kinds discovered from installed content, with windows (timing), retention, sharing bases and coverage options. */
  async discoverIssuedRecordKinds(): Promise<{
    data: DiscoveredIssuedRecordKind[];
    declaration_absence?: DeclarationAbsence | null;
    /**
     * Gate E12.5 pass 3 (P2-1) — `data`'s shape over EVERY installed declaration,
     * whatever its install mode. Label a record that exists from it; offer only
     * from `data` (reference mode takes away application, never content).
     */
    content?: DiscoveredIssuedRecordKind[];
  }> {
    return this.get('/issued-records/kinds');
  }
  /** Pack guidance selected by one of `ISSUED_RECORD_GUIDANCE_TAGS`; any other tag is a 400. */
  async listIssuedRecordBoundaryStatements(
    tag: IssuedRecordGuidanceTag,
  ): Promise<{ data: BoundaryStatement[] }> {
    return this.get(`/issued-records/boundary-statements?tag=${encodeURIComponent(tag)}`);
  }
  async listIssuedRecordScopeDecisions(
    recordKind: string,
  ): Promise<{ data: IssuedRecordScopeDecisionsResponse }> {
    return this.get(
      `/issued-records/scope-decisions?record_kind=${encodeURIComponent(recordKind)}`,
    );
  }
  /** 400 `ISSUED_RECORD_KIND_NOT_DECLARED` for an undeclared kind; `coverage` must be a declared option. */
  async createIssuedRecordScopeDecision(
    body: CreateIssuedRecordScopeDecisionBody,
  ): Promise<{ data: IssuedRecordScopeDecision }> {
    return this.post('/issued-records/scope-decisions', body);
  }
  async listIssuedRecords(params?: {
    page?: number;
    page_size?: number;
    record_kind?: string;
    subject_identifier?: string;
  }): Promise<{ data: IssuedRecordListRow[]; total: number; page: number; page_size: number }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.record_kind) qs.set('record_kind', params.record_kind);
    if (params?.subject_identifier) qs.set('subject_identifier', params.subject_identifier);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/issued-records/records${suffix}`);
  }
  /** 409 `ISSUED_RECORD_SCOPE_REQUIRED` while no current scope decision exists for the kind (AC2). */
  async createIssuedRecord(body: CreateIssuedRecordBody): Promise<{ data: IssuedRecord }> {
    return this.post('/issued-records/records', body);
  }
  /** Audited server-side as `ISSUED_RECORD_ACCESSED`. */
  async getIssuedRecord(id: string): Promise<{ data: IssuedRecordDetail }> {
    return this.get(`/issued-records/records/${encodeURIComponent(id)}`);
  }
  /** Audited server-side as `ISSUED_RECORD_ACCESSED`. Newest first. */
  async listIssuedRecordVersions(id: string): Promise<{ data: IssuedRecordVersion[] }> {
    return this.get(`/issued-records/records/${encodeURIComponent(id)}/versions`);
  }
  /** The FIRST version only; 409 once `current_version_id` is set — supersede instead. */
  async issueIssuedRecordVersion(
    id: string,
    body: IssueIssuedRecordVersionBody,
  ): Promise<{ data: IssuedRecordVersion }> {
    return this.post(`/issued-records/records/${encodeURIComponent(id)}/versions`, body);
  }
  /**
   * Appends a reissued or corrected version and claims the prior version's
   * `superseded_by_version_id` by compare-and-set. 400 `ISSUED_RECORD_REASON_REQUIRED`
   * without a reason; 409 `ISSUED_RECORD_VERSION_ALREADY_SUPERSEDED` when beaten to it.
   * There is deliberately NO update or delete method for a version (AC1).
   */
  async supersedeIssuedRecordVersion(
    id: string,
    versionId: string,
    body: SupersedeIssuedRecordVersionBody,
  ): Promise<{ data: IssuedRecordVersion }> {
    return this.post(
      `/issued-records/records/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/supersede`,
      body,
    );
  }
  async listIssuedRecordConsents(id: string): Promise<{ data: IssuedRecordConsent[] }> {
    return this.get(`/issued-records/records/${encodeURIComponent(id)}/consents`);
  }
  /** 400 `ISSUED_RECORD_REPRESENTATIVE_REQUIRED` for a minor without the representative and relationship. */
  async createIssuedRecordConsent(
    id: string,
    body: CreateIssuedRecordConsentBody,
  ): Promise<{ data: IssuedRecordConsent }> {
    return this.post(`/issued-records/records/${encodeURIComponent(id)}/consents`, body);
  }
  async withdrawIssuedRecordConsent(
    id: string,
    consentId: string,
  ): Promise<{ data: IssuedRecordConsent }> {
    return this.post(
      `/issued-records/records/${encodeURIComponent(id)}/consents/${encodeURIComponent(consentId)}/withdraw`,
      {},
    );
  }
  async listIssuedRecordSharingEvents(id: string): Promise<{ data: IssuedRecordSharingEvent[] }> {
    return this.get(`/issued-records/records/${encodeURIComponent(id)}/sharing-events`);
  }
  /** THE GATE (AC3): 409 `ISSUED_RECORD_SHARING_REFUSED` with `details { reason, citation, note, pack_code }`; the refusal is recorded on the record. */
  async createIssuedRecordSharingEvent(
    id: string,
    body: CreateIssuedRecordSharingEventBody,
  ): Promise<{ data: IssuedRecordSharingEvent }> {
    return this.post(`/issued-records/records/${encodeURIComponent(id)}/sharing-events`, body);
  }
  /** Latest entry per `ISSUED_RECORD_EVIDENCE_KINDS` (null = no evidence recorded) plus the entries (AC5). */
  async listIssuedRecordEvidence(
    recordKind: string,
  ): Promise<{ data: IssuedRecordEvidenceResponse }> {
    return this.get(`/issued-records/evidence?record_kind=${encodeURIComponent(recordKind)}`);
  }
  /** `attachment_ref` and `owner_user_id` required; `occurred_at` MUST be an instant with a zone. */
  async createIssuedRecordEvidence(
    body: CreateIssuedRecordEvidenceBody,
  ): Promise<{ data: IssuedRecordEvidenceEntry }> {
    return this.post('/issued-records/evidence', body);
  }
  /** Per-subject access or sharing history through `UnifiedExportService`, carrying pack code + version (AC6). The ref IS the file. */
  async exportIssuedRecordHistory(
    id: string,
    body: { kind: IssuedRecordExportKind; format: UnifiedExportFormat },
  ): Promise<{ data: AttachmentRef }> {
    return this.post(`/issued-records/records/${encodeURIComponent(id)}/export`, body);
  }
  /**
   * A retention-driven deletion request against an issued version. ALWAYS
   * refused: 409 `ISSUED_RECORD_RETENTION_BLOCKS_DELETION` (with the pack's
   * reference and note in `details`) while an installed pack states the rule,
   * 409 `ISSUED_RECORD_VERSION_IMMUTABLE` otherwise (AC7). The refusal is recorded.
   */
  async requestIssuedRecordVersionDeletion(
    id: string,
    versionId: string,
  ): Promise<{ data: unknown }> {
    return this.post(
      `/issued-records/records/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/deletion-requests`,
      {},
    );
  }
  /** Installed vendor-category pack templates and, per template, the vendor's answered instance or null (AC4). */
  async listIssuedRecordVendorQuestionnaires(
    vendorId: string,
  ): Promise<{ data: IssuedRecordVendorQuestionnaire[] }> {
    return this.get(
      `/issued-records/vendor-questionnaires?vendor_id=${encodeURIComponent(vendorId)}`,
    );
  }

  // ── Sprint 62.17.0 — publishing: gate kinds, categories, the publication register, the release gate, the appointed-representative register and the AI-assisted assessment ──

  /** Release-gate kinds discovered from installed deadline records, each with its declared windows (AC1). */
  async discoverPublicationGateKinds(): Promise<{
    data: DiscoveredPublicationGateKind[];
    declaration_absence?: DeclarationAbsence | null;
    /**
     * Gate E12.5 pass 3 (P2-1) — `data`'s shape over EVERY installed declaration,
     * whatever its install mode. Label a record that exists from it; offer only
     * from `data` (reference mode takes away application, never content).
     */
    content?: DiscoveredPublicationGateKind[];
  }> {
    return this.get('/publishing/gate-kinds');
  }
  /** The category vocabulary a person may apply to a publication (AC3); EMPTY where no pack declares one. */
  async discoverPublicationCategories(): Promise<{
    data: DiscoveredPublicationCategory[];
    declaration_absence?: DeclarationAbsence | null;
    /**
     * Gate E12.5 pass 3 (P2-1) — `data`'s shape over EVERY installed declaration,
     * whatever its install mode. Label a record that exists from it; offer only
     * from `data` (reference mode takes away application, never content).
     */
    content?: DiscoveredPublicationCategory[];
  }> {
    return this.get('/publishing/categories');
  }
  /** Pack guidance selected by one of `PUBLICATION_GUIDANCE_TAGS`; any other tag is a 400 (AC9). */
  async listPublishingBoundaryStatements(
    tag: PublicationGuidanceTag,
  ): Promise<{ data: BoundaryStatement[] }> {
    return this.get(`/publishing/boundary-statements?tag=${encodeURIComponent(tag)}`);
  }
  /**
   * The organisation's scope answer, read-only, for citation (AC5). Reachable
   * with ANY of `publication:read`, `legal_representative:read` or
   * `authority_direction:read`, so all three registers that cite it can call it.
   */
  async getPublicationScopeQuestionnaire(): Promise<{ data: PublicationScopeQuestionnaireRead }> {
    return this.get('/publishing/scope-questionnaire');
  }
  async listPublications(params?: {
    page?: number;
    page_size?: number;
    status?: PublicationStatus;
    gate_kind?: string;
  }): Promise<{ data: Publication[]; total: number; page: number; page_size: number }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.status) qs.set('status', params.status);
    if (params?.gate_kind) qs.set('gate_kind', params.gate_kind);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/publishing/publications${suffix}`);
  }
  async createPublication(body: CreatePublicationBody): Promise<{ data: Publication }> {
    return this.post('/publishing/publications', body);
  }
  async getPublication(id: string): Promise<{ data: Publication }> {
    return this.get(`/publishing/publications/${encodeURIComponent(id)}`);
  }
  async updatePublication(id: string, body: UpdatePublicationBody): Promise<{ data: Publication }> {
    return this.put(`/publishing/publications/${encodeURIComponent(id)}`, body);
  }
  /** BOTH derived dates and the declaring record they came from — never a default window (AC1). */
  async getPublicationTiming(id: string): Promise<{ data: PublicationDepositTiming }> {
    return this.get(`/publishing/publications/${encodeURIComponent(id)}/timing`);
  }
  /** AC8 — evidence attaches to the PUBLICATION; `attachment_ref` is required. */
  async recordPublicationRightsEvidence(
    id: string,
    body: RecordPublicationRightsEvidenceBody,
  ): Promise<{ data: Publication }> {
    return this.post(`/publishing/publications/${encodeURIComponent(id)}/rights-evidence`, body);
  }
  /** 400 `PUBLICATION_CATEGORY_REASON_REQUIRED` without the reason the person must give (AC3). */
  async applyPublicationCategory(
    id: string,
    body: ApplyPublicationCategoryBody,
  ): Promise<{ data: Publication }> {
    return this.post(`/publishing/publications/${encodeURIComponent(id)}/categories`, body);
  }
  /**
   * fix-sprint-20260904-2130-stream-remainders F1 (Q-SR1) — 409 `PUBLICATION_CATEGORY_NOT_APPLIED`
   * or `PUBLICATION_STEP_NOT_DECLARED`; 400 `PUBLICATION_STEP_EVIDENCE_REQUIRED`.
   */
  async recordPublicationCategoryStep(
    id: string,
    body: RecordPublicationCategoryStepBody,
  ): Promise<{ data: Publication }> {
    return this.post(`/publishing/publications/${encodeURIComponent(id)}/category-steps`, body);
  }
  async removePublicationCategory(
    id: string,
    categoryCode: string,
  ): Promise<{ data: Publication }> {
    return this.del(
      `/publishing/publications/${encodeURIComponent(id)}/categories/${encodeURIComponent(categoryCode)}`,
    );
  }
  /** Unpaged, newest submission first. */
  async listPublicationDeposits(id: string): Promise<{ data: PublicationDepositSubmission[] }> {
    return this.get(`/publishing/publications/${encodeURIComponent(id)}/deposit-submissions`);
  }
  /** `submitted_at` and any `receipt_received_at` MUST be instants with a zone. */
  async recordPublicationDeposit(
    id: string,
    body: RecordPublicationDepositBody,
  ): Promise<{ data: PublicationDepositSubmission }> {
    return this.post(
      `/publishing/publications/${encodeURIComponent(id)}/deposit-submissions`,
      body,
    );
  }
  /** The receipt the forwards count runs from; its evidence is required (AC1). */
  async recordPublicationDepositReceipt(
    id: string,
    submissionId: string,
    body: RecordPublicationReceiptBody,
  ): Promise<{ data: PublicationDepositSubmission }> {
    return this.post(
      `/publishing/publications/${encodeURIComponent(id)}/deposit-submissions/${encodeURIComponent(submissionId)}/receipt`,
      body,
    );
  }
  /** The attempt trail — the ONE paged list in this module (AC2). */
  async listPublicationReleaseAttempts(
    id: string,
    params?: { page?: number; page_size?: number },
  ): Promise<{
    data: PublicationReleaseAttempt[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/publishing/publications/${encodeURIComponent(id)}/release-attempts${suffix}`);
  }
  async getPublicationReleasePrerequisites(
    id: string,
  ): Promise<{ data: PublicationPrerequisiteRead }> {
    return this.get(`/publishing/publications/${encodeURIComponent(id)}/release-prerequisites`);
  }
  async recordPublicationReleasePrerequisites(
    id: string,
    body: RecordPublicationPrerequisitesInput,
  ): Promise<{ data: PublicationPrerequisiteRead }> {
    return this.post(
      `/publishing/publications/${encodeURIComponent(id)}/release-prerequisites`,
      body,
    );
  }
  async listPublicationReleasePrerequisiteHistory(
    id: string,
    params?: { page?: number; page_size?: number },
  ): Promise<{
    data: PublicationPrerequisiteRecord[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    return this.get(
      `/publishing/publications/${encodeURIComponent(id)}/release-prerequisites/history${qs.size ? `?${qs}` : ''}`,
    );
  }
  /**
   * THE GATE (AC2). One permission (`publication:release`), no body, no
   * additional approver: checks the window and current pack-declared prerequisites.
   * A refusal is 409 `PUBLICATION_RELEASE_REFUSED` whose `details` carry the
   * reason, the citation, the attempt's actor and time, and — because the
   * five-reason vocabulary is coarser than the fact — `derived_state`,
   * `undated_reason`, `blocked_reason` and `activation_date`.
   */
  async attemptPublicationRelease(id: string): Promise<{ data: Publication }> {
    return this.post(`/publishing/publications/${encodeURIComponent(id)}/release-attempts`, {});
  }
  /** Bookkeeping about a release the gate already permitted — never a second gate. */
  async recordPublicationRelease(
    id: string,
    body: RecordPublicationReleaseBody,
  ): Promise<{ data: Publication }> {
    return this.post(`/publishing/publications/${encodeURIComponent(id)}/release-record`, body);
  }
  async withdrawPublication(
    id: string,
    body: WithdrawPublicationBody,
  ): Promise<{ data: Publication }> {
    return this.post(`/publishing/publications/${encodeURIComponent(id)}/withdraw`, body);
  }
  /**
   * The appointed-representative register (AC4). Paged; `current_only` is the
   * string `'true'` / `'false'` on the wire, so it is sent as a string here.
   */
  async listLegalRepresentatives(params?: {
    page?: number;
    page_size?: number;
    current_only?: boolean;
  }): Promise<{
    data: LegalRepresentativeEntry[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.current_only !== undefined) qs.set('current_only', String(params.current_only));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/publishing/legal-representatives${suffix}`);
  }
  /**
   * APPEND-ONLY: a change is a new entry that supersedes the prior one, with a
   * mandatory reason. There is deliberately no update and no delete method —
   * `PUT`, `PATCH` and `DELETE` are declared server-side and refuse with
   * `LEGAL_REPRESENTATIVE_ENTRY_IMMUTABLE`, so the register cannot be edited
   * from anywhere, this client included.
   */
  async recordLegalRepresentative(
    body: RecordLegalRepresentativeBody,
  ): Promise<{ data: LegalRepresentativeEntry }> {
    return this.post('/publishing/legal-representatives', body);
  }
  /*
   * `GET /publishing/legal-representatives/:id` and
   * `GET /publishing/assessments/:id` are routed and permission-fenced
   * server-side, and this client deliberately carries NO method for either
   * (gate E12.5 pass 1, finding 4). Neither register has a detail page: an
   * entry is read from the row it is listed in, and an assessment is edited
   * from the register's own form. A client method with no call site implies a
   * capability no screen offers, and the R-26 trace then reports a surface the
   * product does not have. Add them back with the screen that needs them.
   */
  /** The AI system register the assessment reads (AC7); three states, and empty never blocks. */
  async listPublicationAiSystems(): Promise<{ data: PublicationAiSystemRegisterRead }> {
    return this.get('/publishing/ai-systems');
  }
  async listPublicationAssessments(params?: {
    page?: number;
    page_size?: number;
    publication_id?: string;
    status?: PublicationAssessmentStatus;
  }): Promise<{
    data: PublicationAssessment[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.publication_id) qs.set('publication_id', params.publication_id);
    if (params?.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/publishing/assessments${suffix}`);
  }
  async createPublicationAssessment(
    body: CreatePublicationAssessmentBody,
  ): Promise<{ data: PublicationAssessment }> {
    return this.post('/publishing/assessments', body);
  }
  /** Draft-only correction (AC7); a completed assessment is refused server-side. */
  async updatePublicationAssessment(
    id: string,
    body: PublicationAssessmentBody,
  ): Promise<{ data: PublicationAssessment }> {
    return this.put(`/publishing/assessments/${encodeURIComponent(id)}`, body);
  }
  /**
   * Completion by the NAMED approver (AC7) — its own permission
   * (`publication_assessment:approve`). It records that a human stands behind
   * the AI-assisted content; it rules on nothing.
   */
  async completePublicationAssessment(
    id: string,
    body: CompletePublicationAssessmentBody,
  ): Promise<{ data: PublicationAssessment }> {
    return this.post(`/publishing/assessments/${encodeURIComponent(id)}/complete`, body);
  }

  // ── Sprint 62.2.4: Evidence pack dispatch policy (idea-catalog #47) ──
  /**
   * Separation of duties between approving and dispatching an evidence pack.
   *
   * `require_separate_approver` defaults to **false**, so a single-persona
   * organisation — one DPO who assembles, approves and files — is never locked
   * out of filing. Turning it on is a deliberate act by an organisation that has
   * the people to separate the duties.
   *
   * The toggle UI lands with the rest of the evidence-pack surfaces in 62.2.5;
   * these methods ship here so that sprint consumes a verified client contract
   * rather than a guessed one — the same reason 62.2.1 pinned its pagination
   * envelope a sprint before anything rendered it.
   */
  /**
   * Sprint 63.30.0 (#485, owner ruling Q3) — how many days a governance
   * approval request stays decidable (read: `platform:security:read`; write:
   * `platform:security:write`). A whole number from 1 to 90; the server
   * refuses anything else with 400 `APPROVAL_SETTINGS_INVALID`.
   */
  async getOrgApprovalSettings(): Promise<{ approval_expiry_days: number }> {
    return this.get('/org-settings/approvals');
  }
  async updateOrgApprovalSettings(
    approvalExpiryDays: number,
  ): Promise<{ approval_expiry_days: number }> {
    return this.put('/org-settings/approvals', { approval_expiry_days: approvalExpiryDays });
  }
  async getOrgEvidencePackDispatchPolicy(): Promise<EvidencePackDispatchOrgSettings> {
    return this.get('/org-settings/evidence-pack-dispatch');
  }
  async updateOrgEvidencePackDispatchPolicy(
    requireSeparateApprover: boolean,
  ): Promise<EvidencePackDispatchOrgSettings> {
    // A boolean parameter rather than a payload object, because the backend
    // rejects a non-boolean rather than coercing it: `"false"` is how a form
    // posts a value it means, and coercion would switch a compliance control on
    // a serialisation detail.
    return this.put('/org-settings/evidence-pack-dispatch', {
      require_separate_approver: requireSeparateApprover,
    });
  }
  // ── Sprint 46.2.0: Sector overlay catalog + install + mode ─────
  async listSectorOverlays(): Promise<{ data: SectorOverlayCatalogEntry[] }> {
    return this.get('/localization/regulatory/sector-overlays');
  }
  /**
   * Sprint 63.43.0 (AC2) — Module 30's navigation projection: the installed
   * optional overlays, their state and their declarations
   * (`src/backend/src/modules/sector-overlays/routes.ts`, `GET /navigation`).
   * Authenticated, organisation-scoped and behind the Enterprise guard (`403
   * PACKAGE_LIMIT` on Light and Standard). Not paginated: the answer is
   * `{ entitled, overlays }`. Read through `useSectorOverlayNavigation`.
   */
  async getSectorOverlayNavigation(): Promise<SectorOverlayNavigation> {
    return this.get('/sector-overlays/navigation');
  }
  /**
   * Sprint 62.1.1 — paginated. The envelope now carries total/page/per_page;
   * callers must use `unwrapResponse`/`extractPaginationMeta` (R-23) rather
   * than treating it as an array.
   */
  async listInstalledSectorOverlays(
    page?: number,
    pageSize?: number,
  ): Promise<{
    data: InstalledSectorOverlay[];
    total: number;
    page: number;
    per_page: number;
  }> {
    const qs = new URLSearchParams();
    if (page !== undefined) qs.set('page', String(page));
    if (pageSize !== undefined) qs.set('page_size', String(pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/localization/regulatory/sector-overlays/installed${suffix}`);
  }
  /**
   * Sprint 62.1.0 — typed framework relationships, both directions.
   *
   * `outgoing` are the frameworks this one points at (its parents and the
   * frameworks it applies to). `incoming` are the frameworks that resolve
   * beneath it — the deduplicated virtual children.
   *
   * Path note: the localization router mounts at `/api/v1/localization` and
   * these routes carry a further `/regulatory` segment. The sprint spec's
   * proposed URL omitted it; this matches the real route.
   */
  async getFrameworkRelationships(
    frameworkCode: string,
  ): Promise<{ data: { outgoing: FrameworkRelationship[]; incoming: FrameworkRelationship[] } }> {
    return this.get(
      `/localization/regulatory/frameworks/${encodeURIComponent(frameworkCode)}/relationships`,
    );
  }
  async installSectorOverlay(
    packCode: string,
    installedAs?: 'direct' | 'reference',
  ): Promise<InstalledRegulatoryOverlay> {
    return this.post(
      `/localization/regulatory/sector-overlays/${encodeURIComponent(packCode)}/install`,
      {
        ...(installedAs ? { installed_as: installedAs } : {}),
      },
    );
  }
  async uninstallSectorOverlay(packCode: string): Promise<{
    pack_code: string;
    framework_code: string | null;
    frameworks_deleted: number;
    deadlines_deleted: number;
    uninstalled_at: string;
  }> {
    return this.del(
      `/localization/regulatory/sector-overlays/${encodeURIComponent(packCode)}/uninstall`,
    );
  }
  async updateInstalledFrameworkMode(
    frameworkId: string,
    installedAs: 'direct' | 'reference',
    reasonNote?: string,
  ): Promise<InstalledRegulatoryOverlay> {
    return this.patch(
      `/localization/regulatory/installed-frameworks/${encodeURIComponent(frameworkId)}`,
      {
        installed_as: installedAs,
        ...(reasonNote ? { reason_note: reasonNote } : {}),
      },
    );
  }
  // ── Sprint 46.3.0: Phased deadline engine ─────────────────────
  async listPhasedObligationEarlyImplementations(): Promise<{
    // Sprint 63.33.0 — each record carries its notice, dated by the server on read.
    data: PhasedObligationEarlyImplementationRow[];
    /** Today in the organisation's calendar (`YYYY-MM-DD`), as the server judges dates. */
    today: string;
  }> {
    return this.get('/localization/regulatory/phased-obligations/early-implementations');
  }
  async markPhasedObligationEarlyImplemented(
    deadlineCode: string,
    payload: {
      framework_code: string;
      effective_at: string;
      evidence_attachment_id?: string;
      reason_note?: string;
    },
  ): Promise<PhasedObligationEarlyImplementationRecord> {
    return this.post(
      `/localization/regulatory/phased-obligations/${encodeURIComponent(deadlineCode)}/early-implement`,
      payload,
    );
  }
  // ── Sprint 3.2.0: Session Management ───────────────────────────
  async listMySessions(): Promise<Session[]> {
    // The page fetches its own user_id from AuthContext and passes it
    // This is a convenience wrapper; the page calls listUserSessions directly
    return [];
  }
  async listUserSessions(userId: string): Promise<Session[]> {
    return this.get(`/users/${userId}/sessions`);
  }
  async terminateSession(userId: string, sessionId: string): Promise<void> {
    return this.del(`/users/${userId}/sessions/${sessionId}`);
  }
  async terminateAllSessions(userId: string): Promise<void> {
    return this.del(`/users/${userId}/sessions`);
  }
  async extendSession(): Promise<{ token: string }> {
    return this.post('/auth/extend-session', {});
  }
  // ── Sprint 3.3.0: Access Review Campaigns ─────────────────────
  async listAccessReviewCampaigns(
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<AccessReviewCampaign>> {
    return this.get(`/access-reviews${this.buildQuery(params)}`);
  }
  async createAccessReviewCampaign(data: Record<string, any>): Promise<AccessReviewCampaign> {
    return this.post('/access-reviews', data);
  }
  async getAccessReviewCampaign(id: string): Promise<AccessReviewCampaign> {
    return this.get(`/access-reviews/${id}`);
  }
  async launchAccessReviewCampaign(id: string): Promise<AccessReviewCampaign> {
    return this.post(`/access-reviews/${id}/launch`);
  }
  async completeAccessReviewCampaign(id: string): Promise<AccessReviewCampaign> {
    return this.post(`/access-reviews/${id}/complete`);
  }
  async cancelAccessReviewCampaign(id: string): Promise<AccessReviewCampaign> {
    return this.post(`/access-reviews/${id}/cancel`);
  }
  async listAccessReviewItems(
    campaignId: string,
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<AccessReviewItemResponse>> {
    return this.get(`/access-reviews/${campaignId}/items${this.buildQuery(params)}`);
  }
  async submitAccessReviewDecision(
    campaignId: string,
    itemId: string,
    data: Record<string, any>,
  ): Promise<AccessReviewItemResponse> {
    return this.patch(`/access-reviews/${campaignId}/items/${itemId}`, data);
  }
  async bulkAccessReviewDecision(
    campaignId: string,
    data: Record<string, any>,
  ): Promise<AccessReviewItemResponse[]> {
    return this.post(`/access-reviews/${campaignId}/items/bulk`, data);
  }
  async getMyReviews(params?: Record<string, any>): Promise<AccessReviewItemResponse[]> {
    return this.get(`/access-reviews/my-reviews${this.buildQuery(params)}`);
  }
  // ── Sprint 3.3.0: Access Review Analytics ─────────────────────
  async getRoleUsageStats(): Promise<RoleUsageStat[]> {
    return this.get('/access-reviews/analytics/role-usage');
  }
  async getOverPrivilegedUsers(params?: Record<string, any>): Promise<OverPrivilegedUser[]> {
    return this.get(`/access-reviews/analytics/over-privileged${this.buildQuery(params)}`);
  }
  async getTrimRecommendations(): Promise<TrimRecommendation[]> {
    return this.get('/access-reviews/analytics/recommendations');
  }
  // ── Sprint 3.3.0: Attestation Schedules ───────────────────────
  async listAttestationSchedules(
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<AttestationScheduleResponse>> {
    return this.get(`/attestation-schedules${this.buildQuery(params)}`);
  }
  async createAttestationSchedule(data: Record<string, any>): Promise<AttestationScheduleResponse> {
    return this.post('/attestation-schedules', data);
  }
  async getAttestationSchedule(id: string): Promise<AttestationScheduleResponse> {
    return this.get(`/attestation-schedules/${id}`);
  }
  async updateAttestationSchedule(
    id: string,
    data: Record<string, any>,
  ): Promise<AttestationScheduleResponse> {
    return this.patch(`/attestation-schedules/${id}`, data);
  }
  async deactivateAttestationSchedule(id: string): Promise<AttestationScheduleResponse> {
    return this.del(`/attestation-schedules/${id}`);
  }
  /**
   * The route answers the campaign it started and what the campaign model could
   * not express from the schedule (`unmapped`) - not the schedule.
   */
  async triggerAttestationCycle(id: string): Promise<{ campaign_id: string; unmapped?: unknown }> {
    return this.post(`/attestation-schedules/${id}/trigger`);
  }
  async getAttestationScheduleHistory(
    id: string,
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    return this.get(`/attestation-schedules/${id}/history${this.buildQuery(params)}`);
  }
  // ── Sprint 3.3.0: SoD Rules ───────────────────────────────────
  async listSodRules(params?: Record<string, any>): Promise<PaginatedResponse<SodRuleResponse>> {
    return this.get(`/sod-rules${this.buildQuery(params)}`);
  }
  async createSodRule(data: Record<string, any>): Promise<SodRuleResponse> {
    return this.post('/sod-rules', data);
  }
  async getSodRule(id: string): Promise<SodRuleResponse> {
    return this.get(`/sod-rules/${id}`);
  }
  async updateSodRule(id: string, data: Record<string, any>): Promise<SodRuleResponse> {
    return this.patch(`/sod-rules/${id}`, data);
  }
  async deactivateSodRule(id: string): Promise<SodRuleResponse> {
    return this.del(`/sod-rules/${id}`);
  }
  async getSodViolations(): Promise<SodViolation[]> {
    return this.get('/sod-rules/violations');
  }
  async checkSodAssignment(userId: string, roleId: string): Promise<SodCheckResult> {
    return this.get(`/sod-rules/check?user_id=${userId}&role_id=${roleId}`);
  }
  async grantSodException(
    ruleId: string,
    data: Record<string, any>,
  ): Promise<SodExceptionResponse> {
    return this.post(`/sod-rules/${ruleId}/exceptions`, data);
  }
  async listSodExceptions(ruleId: string): Promise<SodExceptionResponse[]> {
    return this.get(`/sod-rules/${ruleId}/exceptions`);
  }
  // ── Sprint 3.6.0: Webhooks ──────────────────────────────────────
  async listWebhooks(params?: Record<string, string>): Promise<PaginatedResponse<WebhookConfig>> {
    return this.get(`/webhooks${this.buildQuery(params)}`);
  }
  async createWebhook(data: Record<string, unknown>): Promise<WebhookConfig> {
    return this.post('/webhooks', data);
  }
  async getWebhook(id: string): Promise<WebhookConfig> {
    return this.get(`/webhooks/${id}`);
  }
  async updateWebhook(id: string, data: Record<string, unknown>): Promise<WebhookConfig> {
    return this.patch(`/webhooks/${id}`, data);
  }
  async deactivateWebhook(id: string): Promise<WebhookConfig> {
    return this.del(`/webhooks/${id}`);
  }
  async testWebhook(id: string): Promise<Record<string, unknown>> {
    return this.post(`/webhooks/${id}/test`, {});
  }
  async listWebhookDeliveries(
    id: string,
    params?: Record<string, string>,
  ): Promise<WebhookDelivery[]> {
    return this.get(`/webhooks/${id}/deliveries${this.buildQuery(params)}`);
  }
  async retryWebhookDelivery(webhookId: string, deliveryId: string): Promise<WebhookDelivery> {
    return this.post(`/webhooks/${webhookId}/deliveries/${deliveryId}/retry`, {});
  }
  // ── Sprint 3.6.0: Domains ──────────────────────────────────────
  async listDomains(params?: Record<string, string>): Promise<PaginatedResponse<DomainMapping>> {
    return this.get(`/domains${this.buildQuery(params)}`);
  }
  async addDomain(data: { domain: string }): Promise<DomainMapping> {
    return this.post('/domains', data);
  }
  async verifyDomain(id: string): Promise<Record<string, unknown>> {
    return this.post(`/domains/${id}/verify`, {});
  }
  async deleteDomain(id: string): Promise<void> {
    return this.del(`/domains/${id}`);
  }
  // ── Sprint 3.6.0: Export Schedules ──────────────────────────────
  async listExportSchedules(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ExportSchedule>> {
    return this.get(`/export-schedules${this.buildQuery(params)}`);
  }
  async createExportSchedule(data: Record<string, unknown>): Promise<ExportSchedule> {
    return this.post('/export-schedules', data);
  }
  async updateExportSchedule(id: string, data: Record<string, unknown>): Promise<ExportSchedule> {
    return this.patch(`/export-schedules/${id}`, data);
  }
  async deactivateExportSchedule(id: string): Promise<ExportSchedule> {
    return this.del(`/export-schedules/${id}`);
  }
  /** Sprint 63.30.0 (#584) — runs the export now; answers the finished run (completed or failed). */
  async triggerExportSchedule(id: string): Promise<ExportRun> {
    return this.post(`/export-schedules/${id}/trigger`, {});
  }
  /** Paginated `{ data, total, page, page_size }` (R-23). */
  async listExportRuns(
    id: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ExportRun>> {
    return this.get(`/export-schedules/${id}/runs${this.buildQuery(params)}`);
  }
  // ── Sprint 3.6.0: Email Log ──────────────────────────────────────
  async listEmailLog(params?: Record<string, string>): Promise<PaginatedResponse<EmailLogEntry>> {
    return this.get(`/email-log${this.buildQuery(params)}`);
  }
  async getEmailLogStats(): Promise<Record<string, unknown>> {
    return this.get('/email-log/stats');
  }
  async getEmailLogEntry(id: string): Promise<EmailLogEntry> {
    return this.get(`/email-log/${id}`);
  }
  // ── Sprint 4.0.0 -- Board Report ───────────────────────────
  async generateBoardReport(params: {
    date_from: string;
    date_to: string;
    language: string;
    sections?: string[];
  }): Promise<Record<string, unknown>> {
    return this.post('/governance/board-report', params);
  }
  // ── Sprint 4.0.0 -- Quality Gates ─────────────────────────
  async listQualityGates(
    params?: Record<string, string>,
  ): Promise<{ data: Record<string, unknown>[] }> {
    return this.get(`/rights-requests/quality-gates${this.buildQuery(params)}`);
  }
  async createQualityGate(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.post('/rights-requests/quality-gates', data);
  }
  async updateQualityGate(
    ruleId: string,
    data: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch(`/rights-requests/quality-gates/${ruleId}`, data);
  }
  async deactivateQualityGate(ruleId: string): Promise<{ message: string }> {
    return this.del(`/rights-requests/quality-gates/${ruleId}`);
  }
  async evaluateQualityGates(
    formType: string,
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.post('/rights-requests/quality-gates/evaluate', { form_type: formType, payload });
  }
  // ── Sprint 4.0.0 -- Escalation Ladder ─────────────────────
  async getEscalationLadder(): Promise<import('@vn-grc/shared').EscalationLadder> {
    return this.get('/rights-requests/escalation-ladder');
  }
  /**
   * fix-sprint #431 F1 — `expectedUpdatedAt` is the `updated_at` the ladder was
   * read at (`null` when none was saved); a save over a newer ladder is
   * refused 409 `ESCALATION_LADDER_CHANGED`. Omitted, the save is unconditional.
   */
  async replaceEscalationLadder(
    tiers: unknown[],
    expectedUpdatedAt?: string | null,
  ): Promise<import('@vn-grc/shared').EscalationLadder> {
    return this.put('/rights-requests/escalation-ladder', {
      tiers,
      ...(expectedUpdatedAt === undefined ? {} : { expected_updated_at: expectedUpdatedAt }),
    });
  }
  async getCaseEscalations(
    casePublicId: string,
    params?: Record<string, string>,
  ): Promise<{ data: Record<string, unknown>[] }> {
    return this.get(`/rights-requests/cases/${casePublicId}/escalations${this.buildQuery(params)}`);
  }
  // ── Sprint 4.0.0 -- Communications ────────────────────────
  async listCaseCommunications(
    casePublicId: string,
    params?: Record<string, string>,
  ): Promise<{ data: Record<string, unknown>[] }> {
    return this.get(
      `/rights-requests/cases/${casePublicId}/communications${this.buildQuery(params)}`,
    );
  }
  async createCaseCommunication(
    casePublicId: string,
    data: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.post(`/rights-requests/cases/${casePublicId}/communications`, data);
  }
  async getCaseCommunication(
    casePublicId: string,
    commId: string,
  ): Promise<Record<string, unknown>> {
    return this.get(`/rights-requests/cases/${casePublicId}/communications/${commId}`);
  }
  // ── Sprint 4.1.0 -- Privacy Scanner ────────────────────────
  async getMonitoringConfig(): Promise<PrivacyScannerConfig> {
    return this.get('/privacy-scanner/monitoring/config');
  }
  async updateMonitoringConfig(config: Record<string, unknown>): Promise<PrivacyScannerConfig> {
    return this.put('/privacy-scanner/monitoring/config', config);
  }
  async testMonitoringConnection(): Promise<Record<string, unknown>> {
    return this.post('/privacy-scanner/monitoring/config/test', {});
  }
  async getMonitoringDashboard(): Promise<Record<string, unknown>> {
    return this.get('/privacy-scanner/monitoring/dashboard');
  }
  async getScannerConfig(): Promise<Record<string, unknown>> {
    return this.get('/privacy-scanner/real-time/config');
  }
  async testScannerConnection(): Promise<Record<string, unknown>> {
    return this.post('/privacy-scanner/real-time/config/test', {});
  }
  async launchPrivacyScan(request: Record<string, unknown>): Promise<PrivacyScanResult> {
    return this.post('/privacy-scanner/real-time/scans', request);
  }
  async getPrivacyScanStatus(
    scanId: string,
    opts: { background?: boolean } = {},
  ): Promise<PrivacyScanProgress> {
    const path = `/privacy-scanner/real-time/scans/${scanId}/status`;
    return opts.background ? this.getInBackground(path) : this.get(path);
  }
  async getPrivacyScanResult(scanId: string): Promise<PrivacyScanResult> {
    return this.get(`/privacy-scanner/real-time/scans/${scanId}`);
  }
  async getPrivacyScanHistory(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<PrivacyScanResult>> {
    return this.get(`/privacy-scanner/real-time/scans${this.buildQuery(params)}`);
  }
  async comparePrivacyScans(scanId1: string, scanId2: string): Promise<ScanComparisonResult> {
    return this.post('/privacy-scanner/real-time/scans/compare', {
      scan_id_1: scanId1,
      scan_id_2: scanId2,
    });
  }
  // ── Sprint 4.2.0: AesirX CMP Consent Integration ──────────────
  // Webhook Config
  async getCmpWebhookConfig(): Promise<CmpWebhookConfig> {
    return this.get('/consent/cmp-webhook/config');
  }
  async createCmpWebhookConfig(payload: Record<string, unknown>): Promise<CmpWebhookConfig> {
    return this.post('/consent/cmp-webhook/config', payload);
  }
  async updateCmpWebhookConfig(payload: Record<string, unknown>): Promise<CmpWebhookConfig> {
    return this.patch('/consent/cmp-webhook/config', payload);
  }
  async deleteCmpWebhookConfig(): Promise<{ message: string }> {
    return this.del('/consent/cmp-webhook/config');
  }
  // Bulk Sync
  async startCmpBulkSync(events: any[]): Promise<BulkSyncJob> {
    return this.post('/consent/cmp-webhook/bulk-sync', { events });
  }
  async getCmpBulkSyncStatus(jobId: string): Promise<BulkSyncJob> {
    return this.get(`/consent/cmp-webhook/bulk-sync/${jobId}`);
  }
  async remapCmpRecords(): Promise<Record<string, unknown>> {
    return this.post('/consent/cmp-webhook/remap', {});
  }
  // Purpose Mappings
  async listCmpPurposeMappings(): Promise<CmpPurposeMapping[]> {
    return this.get('/consent/cmp-purpose-mappings');
  }
  async upsertCmpPurposeMapping(payload: Record<string, unknown>): Promise<CmpPurposeMapping> {
    return this.post('/consent/cmp-purpose-mappings', payload);
  }
  async deleteCmpPurposeMapping(mappingId: string): Promise<{ message: string }> {
    return this.del(`/consent/cmp-purpose-mappings/${mappingId}`);
  }
  async autoMapCmpPurposes(): Promise<{ mappings_created: number; mappings_failed: number }> {
    return this.post('/consent/cmp-purpose-mappings/auto-map', {});
  }
  // Web Consent Records
  async listWebConsentRecords(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<WebConsentRecord>> {
    return this.get(`/consent/web-records${this.buildQuery(params)}`);
  }
  async getWebConsentAnalytics(params?: Record<string, string>): Promise<WebConsentAnalytics> {
    return this.get(`/consent/web-records/analytics${this.buildQuery(params)}`);
  }
  async overrideWebConsentRecordStatus(
    recordId: string,
    body: { action: 'revoke' | 'reject'; reason?: string },
  ): Promise<{ record_id: string; admin_status: string }> {
    return this.patch(`/consent/web-records/${recordId}/status`, body);
  }
  // ── Sprint 20.6.0: Consent Governance Maturity ──────
  // Epic 1: Withdrawal Propagation
  async propagateWithdrawal(
    workflowId: string,
    linkedSystems: Array<{ system_id: string; system_name: string; owner_id: string }>,
  ): Promise<Record<string, unknown>> {
    return this.post(`/consent/propagation/${workflowId}`, {
      linked_systems: linkedSystems,
    });
  }
  async listPropagationTasks(workflowId: string): Promise<WithdrawalPropagationTask[]> {
    return this.get(`/consent/propagation/${workflowId}`);
  }
  async confirmPropagation(taskId: string): Promise<WithdrawalPropagationTask> {
    return this.post(`/consent/propagation/${taskId}/confirm`, {});
  }
  async failPropagation(taskId: string, reason: string): Promise<WithdrawalPropagationTask> {
    return this.post(`/consent/propagation/${taskId}/fail`, { reason });
  }
  async getPropagationSummary(workflowId: string): Promise<Record<string, unknown>> {
    return this.get(`/consent/propagation/${workflowId}/summary`);
  }
  // Epic 2: Sensitive Data
  async flagSensitivePolicy(policyId: string, categories: string[]): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/sensitive`, { categories });
  }
  async listSensitivePolicies(): Promise<ConsentPolicy[]> {
    return this.get('/consent/policies/sensitive');
  }
  async requireEnhancedReview(policyId: string): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/enhanced-review`, {});
  }
  // Sprint 28.1.3: Submit enhanced review decision
  async submitEnhancedReview(
    policyId: string,
    data: { action: 'approved' | 'rejected'; notes: string },
  ): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/enhanced-review/submit`, data);
  }
  // Epic 3: Mismatch Reconciliation
  async createReconciliationTask(
    data: Record<string, unknown>,
  ): Promise<MismatchReconciliationTask> {
    return this.post('/consent/reconciliation', data);
  }
  async listReconciliationTasks(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<MismatchReconciliationTask>> {
    return this.get(`/consent/reconciliation${this.buildQuery(params)}`);
  }
  async resolveReconciliationTask(
    taskId: string,
    data: { resolution: string; notes: string },
  ): Promise<MismatchReconciliationTask> {
    return this.post(`/consent/reconciliation/${taskId}/resolve`, data);
  }
  async getReconciliationDashboard(): Promise<Record<string, unknown>> {
    return this.get('/consent/reconciliation/dashboard');
  }
  // Epic 4: Bidirectional CMP Sync
  async createSyncEvent(data: Record<string, unknown>): Promise<CmpSyncEvent> {
    return this.post('/consent/sync', data);
  }
  async listSyncEvents(params?: Record<string, string>): Promise<PaginatedResponse<CmpSyncEvent>> {
    return this.get(`/consent/sync${this.buildQuery(params)}`);
  }
  async resolveSyncConflict(
    syncId: string,
    resolutionAction: 'accept_cmp' | 'keep_grc',
  ): Promise<CmpSyncEvent> {
    return this.post(`/consent/sync/${syncId}/resolve`, { resolution_action: resolutionAction });
  }
  async detectSyncDrift(): Promise<{ detected: number; skipped: number; total_compared: number }> {
    return this.post('/consent/sync/detect', {});
  }
  // ── Sprint 4.3.0 -- CMP DSAR/DSR Sync ────────────────────
  // Outbound sync config
  async getCmpDsarSyncConfig(): Promise<Record<string, unknown>> {
    return this.get('/rights-requests/cmp-sync/config');
  }
  async createCmpDsarSyncConfig(
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.post('/rights-requests/cmp-sync/config', payload);
  }
  async updateCmpDsarSyncConfig(
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch('/rights-requests/cmp-sync/config', payload);
  }
  async testCmpDsarSyncConnection(): Promise<Record<string, unknown>> {
    return this.post('/rights-requests/cmp-sync/config/test', {});
  }
  // Sync history & resync
  async getCaseSyncHistory(casePublicId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/rights-requests/cases/${casePublicId}/sync-history`);
  }
  async triggerCaseResync(casePublicId: string): Promise<Record<string, unknown>> {
    return this.post(`/rights-requests/cases/${casePublicId}/resync`, {});
  }
  // ── Sprint 20.4.0: DSR Fulfillment, Response & Extension ──────
  async assignDsrFulfillment(
    caseId: string,
    assignments: any[],
  ): Promise<DepartmentFulfillmentTask[]> {
    return this.post(`/rights-requests/cases/${caseId}/fulfillment/assign`, { assignments });
  }
  async listDsrFulfillmentTasks(caseId: string): Promise<DepartmentFulfillmentTask[]> {
    return this.get(`/rights-requests/cases/${caseId}/fulfillment`);
  }
  async updateDsrFulfillmentTask(
    taskId: string,
    updates: Record<string, unknown>,
  ): Promise<DepartmentFulfillmentTask> {
    return this.patch(`/rights-requests/fulfillment/${taskId}`, updates);
  }
  async deleteDsrFulfillmentTask(taskId: string): Promise<void> {
    return this.del(`/rights-requests/fulfillment/${taskId}`);
  }
  async dsrDepartmentSignoff(taskId: string): Promise<DepartmentFulfillmentTask> {
    return this.post(`/rights-requests/fulfillment/${taskId}/signoff`, {});
  }
  async getDsrFulfillmentSummary(caseId: string): Promise<Record<string, unknown>> {
    return this.get(`/rights-requests/cases/${caseId}/fulfillment/summary`);
  }
  async suggestDsrDataStores(caseId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/rights-requests/cases/${caseId}/data-stores`);
  }
  async generateDsrResponse(caseId: string, templateId: string): Promise<DsrResponseRecord> {
    return this.post(`/rights-requests/cases/${caseId}/response/generate`, {
      template_id: templateId,
    });
  }
  async sendDsrResponse(
    caseId: string,
    responseId: string,
    deliveryMethod: string,
  ): Promise<DsrResponseRecord> {
    return this.post(`/rights-requests/cases/${caseId}/response/${responseId}/send`, {
      delivery_method: deliveryMethod,
    });
  }
  async listDsrResponseTemplates(rightType?: string): Promise<DsrResponseTemplate[]> {
    const params = rightType ? { right_type: rightType } : undefined;
    // Backend wraps in { data: [...] }; callers (TicketDetailPage, ResponsePanel,
    // CommunicationsPanel) expect a plain array. Unwrap here so all consumers work.
    const raw = await this.get<{ data: DsrResponseTemplate[] } | DsrResponseTemplate[]>(
      `/rights-requests/response-templates${this.buildQuery(params)}`,
    );
    if (Array.isArray(raw)) return raw;
    return Array.isArray((raw as { data?: DsrResponseTemplate[] })?.data)
      ? (raw as { data: DsrResponseTemplate[] }).data
      : [];
  }
  async createDsrResponseTemplate(payload: Record<string, unknown>): Promise<DsrResponseTemplate> {
    return this.post('/rights-requests/response-templates', payload);
  }
  // ── Sprint 43.2.0 batch 3 (E1) — DSR response template CRUD ────
  async getDsrResponseTemplate(templateId: string): Promise<DsrResponseTemplate> {
    return this.get(`/rights-requests/response-templates/${templateId}`);
  }
  async updateDsrResponseTemplate(
    templateId: string,
    updates: Partial<{
      right_type: string;
      outcome: string;
      locale: string;
      subject: string;
      body_template: string;
      is_active: boolean;
      official_form_id: string | null;
    }>,
  ): Promise<DsrResponseTemplate> {
    return this.patch(`/rights-requests/response-templates/${templateId}`, updates);
  }
  async deactivateDsrResponseTemplate(templateId: string): Promise<DsrResponseTemplate> {
    return this.del(`/rights-requests/response-templates/${templateId}`);
  }
  async cloneDsrResponseTemplate(
    templateId: string,
    newLocale: string,
  ): Promise<DsrResponseTemplate> {
    return this.post(`/rights-requests/response-templates/${templateId}/clone`, {
      locale: newLocale,
    });
  }
  async listDsrExtensions(caseId: string): Promise<ExtensionRequest[]> {
    return this.get(`/rights-requests/cases/${caseId}/extension`);
  }
  async requestDsrExtension(
    caseId: string,
    reason: string,
    days: number,
  ): Promise<ExtensionRequest> {
    return this.post(`/rights-requests/cases/${caseId}/extension`, {
      reason,
      additional_days: days,
    });
  }
  async approveDsrExtension(
    caseId: string,
    extensionId: string,
    decision: string,
  ): Promise<ExtensionRequest> {
    return this.post(`/rights-requests/cases/${caseId}/extension/${extensionId}/approve`, {
      decision,
    });
  }
  // ── Sprint 4.4.0: Module Configuration ─────────────────────────
  async getModuleConfig(): Promise<ModuleConfig> {
    return this.get('/module-config');
  }
  async updateModuleConfig(payload: {
    modules: Record<string, { enabled: boolean }>;
  }): Promise<ModuleConfig> {
    return this.put('/module-config', payload);
  }
  // ── Sprint 7.6.0: Form Engine ─────────────────────────────────
  /**
   * Sprint 62.58.0 (AC-A2) — each row carries `template_not_owed` (and the
   * status that made it so), so a card can say its framework is a draft.
   */
  async listFormTemplates(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<FormTemplate & TemplateNotOwedFlag>> {
    return this.get(`/forms/templates${this.buildQuery(params)}`);
  }
  /**
   * Sprint 62.58.0 (AC-A2) — `GET /forms/templates/:id` (routes.ts) returns the
   * template with `template_framework_notices`, the declaring frameworks' draft
   * facts, so the filler says the instrument is a draft before an instance exists.
   */
  async getFormTemplate(
    id: string,
  ): Promise<FormTemplate & { template_framework_notices?: TemplateFrameworkNotice[] }> {
    return this.get(`/forms/templates/${id}`);
  }
  async createFormTemplate(data: any): Promise<FormTemplate> {
    return this.post('/forms/templates', data);
  }
  async updateFormTemplate(id: string, data: any): Promise<FormTemplate> {
    return this.put(`/forms/templates/${id}`, data);
  }
  async createFormInstance(data: any): Promise<FormInstance> {
    return this.post('/forms/instances', data);
  }
  /** Sprint 62.58.0 (AC-A2) — each row carries `template_not_owed`. */
  async listFormInstances(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<FormInstance & TemplateNotOwedFlag>> {
    return this.get(`/forms/instances${this.buildQuery(params)}`);
  }
  async getFormInstance(id: string): Promise<FormInstanceDetail> {
    return this.get(`/forms/instances/${id}`);
  }
  async updateFormInstance(id: string, data: any): Promise<FormInstance> {
    return this.put(`/forms/instances/${id}`, data);
  }
  async submitFormInstance(id: string): Promise<FormInstance> {
    return this.post(`/forms/instances/${id}/submit`, {});
  }
  async approveFormInstance(id: string): Promise<FormInstance> {
    return this.post(`/forms/instances/${id}/approve`, {});
  }
  async updateFilingStatus(id: string, filingStatus: string): Promise<FormInstance> {
    return this.patch(`/forms/instances/${id}/filing-status`, { filing_status: filingStatus });
  }
  async generateFormOutput(id: string, data: any): Promise<FormOutput> {
    return this.post(`/forms/instances/${id}/generate`, data);
  }
  async listFormOutputs(instanceId: string): Promise<FormOutput[]> {
    return this.get(`/forms/instances/${instanceId}/outputs`);
  }
  /**
   * Fix Sprint 20260418-0500 Finding 14 — R-33 migration. Replaces
   * `window.print()` on FormOutputsPage with a backend-rendered PDF
   * persisted via UnifiedExportService. Returns AttachmentRef so the
   * frontend can call `downloadPlatformFile`.
   */
  async exportFormOutputPrint(
    instanceId: string,
    // Sprint 62.45.0 gate E12.5 pass 3 — the document's locale. The route
    // prints English without it, so every caller should send the UI locale.
    params: { format: 'pdf'; locale?: string },
  ): Promise<AttachmentRef> {
    return this.get(`/forms/instances/${instanceId}/print-export${this.buildQuery(params)}`);
  }
  async downloadFormOutput(outputId: string): Promise<Blob> {
    return this.get(`/forms/outputs/${outputId}/download`);
  }
  async listFormAuditEvents(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<FormAuditEvent>> {
    return this.get(`/forms/audit${this.buildQuery(params)}`);
  }
  // ── FU2b (2026-04-21): Cover-letter preview + snapshot ─────────
  async previewCoverLetter(payload: {
    dossier_id: string;
    locale: string;
    stage?: 'initial' | 'supplement' | 'resubmission';
    body_paragraph?: string;
    additional_notes?: string | null;
    subject?: string;
  }): Promise<{ data_url: string; context: Record<string, unknown> }> {
    return this.post('/forms/cover-letter/preview', payload);
  }
  async snapshotCoverLetter(payload: {
    dossier_id: string;
    locale: string;
    stage?: 'initial' | 'supplement' | 'resubmission';
    body_paragraph?: string;
    additional_notes?: string | null;
    subject?: string;
    regenerate?: boolean;
  }): Promise<{ snapshot: Record<string, unknown> }> {
    return this.post('/forms/cover-letter/snapshot', payload);
  }
  // ── Sprint 37.1.0: Form Orchestration ──────────────────────────
  // fix-sprint-20260411-B F13 — payload/response types imported from
  // the shared barrel so the frontend, backend, and type system agree
  // on a single contract. Previously these signatures were inlined
  // both here and in the route handler.
  async orchestrationCreate(
    data: CreateWithFormPayload,
  ): Promise<{ entityId: string; formInstanceId: string }> {
    return this.post('/forms/orchestration/create', data);
  }
  async orchestrationUpdateValues(
    data: UpdateFormValuesPayload,
  ): Promise<UpdateFormValuesResponse> {
    return this.put('/forms/orchestration/update-values', data);
  }
  async orchestrationSubmit(formInstanceId: string): Promise<SubmitFormResponse> {
    return this.post('/forms/orchestration/submit', { formInstanceId });
  }
  async orchestrationFinalize(formInstanceId: string): Promise<FinalizeFormResponse> {
    return this.post('/forms/orchestration/finalize', { formInstanceId });
  }
  // ── Sprint 10.6.0: Form Catalog & Template Management ──────────
  async listFormCatalog(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<FormCatalogEntryWithStatus>> {
    return this.get(`/forms/catalog${this.buildQuery(params)}`);
  }
  async listFormCatalogCategories(
    params?: Record<string, string>,
  ): Promise<Record<string, unknown>[]> {
    return this.get(`/forms/catalog/categories${this.buildQuery(params)}`);
  }
  async getFormInstallStatus(): Promise<FormInstallStatusResponse> {
    return this.get('/forms/install-status');
  }
  async getFormCatalogEntry(schemaId: string): Promise<FormCatalogEntryWithStatus> {
    return this.get(`/forms/catalog/${schemaId}`);
  }
  async installFormFromCatalog(schemaId: string): Promise<Record<string, unknown>> {
    return this.post(`/forms/catalog/${schemaId}/install`, {});
  }
  async uninstallFormFromCatalog(schemaId: string): Promise<Record<string, unknown>> {
    return this.post(`/forms/catalog/${schemaId}/uninstall`, {});
  }
  async installFormCategory(
    category: string,
    marketCode?: string,
  ): Promise<Record<string, unknown>> {
    const qs = marketCode ? `?market_code=${encodeURIComponent(marketCode)}` : '';
    return this.post(`/forms/catalog/category/${category}/install${qs}`, {});
  }
  // ── Sprint 15.7.0: Dossier Management ─────────────────────────
  async listDossiers(params?: Record<string, string>): Promise<PaginatedResponse<Dossier>> {
    return this.get(`/forms/dossiers${this.buildQuery(params)}`);
  }
  async getDossier(id: string): Promise<Dossier> {
    return this.get(`/forms/dossiers/${id}`);
  }
  async createDossier(payload: any): Promise<Dossier> {
    return this.post('/forms/dossiers', payload);
  }
  async updateDossier(id: string, payload: any): Promise<Dossier> {
    return this.put(`/forms/dossiers/${id}`, payload);
  }
  async transitionDossier(id: string, payload: any): Promise<Dossier> {
    return this.post(`/forms/dossiers/${id}/transition`, payload);
  }
  async attachDossierForm(id: string, payload: any): Promise<Dossier> {
    return this.post(`/forms/dossiers/${id}/attachments`, payload);
  }
  async detachDossierForm(id: string, formId: string): Promise<{ message: string }> {
    return this.del(`/forms/dossiers/${id}/attachments/${formId}`);
  }
  /**
   * Sprint 63.40.0 (decision 8) — prepare the notice dossier a completed form
   * instance opens. Body `{}` (strict on the server). 201 `{ dossier, created }`
   * or 200 with `created: false` when the instance already has one.
   */
  async prepareNoticeDossier(instanceId: string): Promise<PrepareNoticeDossierResult> {
    return this.post(`/forms/instances/${encodeURIComponent(instanceId)}/notice-dossier`, {});
  }
  /**
   * Sprint 63.40.0 (decision 7) — the person's committee name and statement
   * answers on a notice dossier (`PUT /forms/dossiers/:id`, strict).
   */
  async updateNoticeDossier(
    id: string,
    payload: Pick<UpdateDossierPayload, 'addressee_name' | 'notice_statements'>,
  ): Promise<Dossier> {
    return this.put(`/forms/dossiers/${encodeURIComponent(id)}`, payload);
  }
  // ── Sprint 15.7.0: Compliance Rules ───────────────────────────
  async listComplianceRules(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ComplianceRuleDocument>> {
    return this.get(`/compliance-rules${this.buildQuery(params)}`);
  }
  async getComplianceRule(id: string): Promise<ComplianceRuleDocument> {
    return this.get(`/compliance-rules/${id}`);
  }
  async createComplianceRule(payload: any): Promise<ComplianceRuleDocument> {
    return this.post('/compliance-rules', payload);
  }
  async updateComplianceRule(id: string, payload: any): Promise<ComplianceRuleDocument> {
    return this.put(`/compliance-rules/${id}`, payload);
  }
  async listComplianceRulesByFramework(code: string): Promise<ComplianceRuleDocument[]> {
    return this.get(`/compliance-rules/framework/${code}`);
  }
  // ── Sprint 48.3.0: MCP Phase 2 pending-write queue ──────────
  /**
   * Sprint 64.124.0 — the row shape is `McpPendingWriteListItem` from
   * `@vn-grc/shared`, not a fourth hand-typed copy of it.
   *
   * It used to be spelled out inline, including a `status` union that listed
   * five members. Adding `failed` to the shared type left this declaration
   * compiling and wrong in two directions at once: `?status=failed` was not an
   * expressible argument, and a `failed` row coming back off the wire was typed
   * as something the union says it cannot be. The backend already answers with
   * exactly this interface — `toListItem` returns it — so the wire shape and the
   * client's belief about the wire shape are now one declaration.
   */
  async listMcpPendingWrites(params?: { status?: McpPendingWriteStatus }): Promise<{
    data: McpPendingWriteListItem[];
    total: number;
  }> {
    return this.get(
      `/mcp-connector/pending-writes${this.buildQuery(params as Record<string, string> | undefined)}`,
    );
  }
  /**
   * Sprint 64.124.0 — the response now distinguishes `executed` from `failed`,
   * and the caller MUST read it. Approving no longer implies the write happened:
   * the tool runner returns its failures as results rather than throwing, so a
   * 200 here can carry a dispatch that did not occur.
   */
  async approveMcpPendingWrite(id: string): Promise<{
    status: McpPendingWriteStatus;
    execution_result_excerpt: string | null;
    execution_failure_code: string | null;
  }> {
    return this.post(`/mcp-connector/pending-writes/${id}/approve`, {});
  }
  /** A decline needs the reason (1-500 characters); the server refuses it without one. */
  async declineMcpPendingWrite(id: string, reason: string): Promise<{ status: string }> {
    return this.post(`/mcp-connector/pending-writes/${id}/decline`, { reason });
  }
  // ── Sprint 48.2.9: Forseti audit walkthrough generator ──────
  /** GET /api/v1/audits — list audits available to the current org. */
  async listAudits(params?: { limit?: number }): Promise<{
    data: Array<{ _id: string; name?: string; status?: string; framework_codes?: string[] }>;
  }> {
    const q = params?.limit ? `?limit=${params.limit}` : '';
    return this.get(`/audits${q}`);
  }
  /** `offset` is the index of the first control to cover; a run covers one page of controls. */
  async generateAuditWalkthrough(
    auditId: string,
    offset = 0,
  ): Promise<{
    draft_id: string;
    audit_id: string;
    audit_name?: string;
    offset?: number;
    total_controls?: number;
    next_offset?: number | null;
    controls: Array<{
      control_id: string;
      control_label: string;
      framework_code?: string;
      requirement_id?: string;
      opening_question: string;
      expected_evidence: string[];
      followup_probes: string[];
      potential_gaps: string[];
      evidence_count: number;
    }>;
    citations: Array<{ framework_code: string; requirement_id: string; article_ref?: string }>;
    billable_units: number;
  }> {
    return this.post(`/audits/${auditId}/generate-walkthrough`, { offset });
  }
  async resolveAuditWalkthrough(
    draftId: string,
    decision: 'accepted' | 'rejected',
  ): Promise<{ status: string }> {
    return this.post(`/audits/walkthrough/${draftId}/resolve`, { decision });
  }
  async exportAuditWalkthroughDocx(draftId: string): Promise<unknown> {
    return this.post(`/audits/walkthrough/${draftId}/export-docx`, {});
  }
  // ── Sprint 48.2.9: Forseti privilege creep detection ────────
  async scanPrivilegeCreep(): Promise<{
    scan_id: string;
    scanned_user_count: number;
    findings: Array<{
      id: string;
      user_id: string;
      user_email?: string;
      job_function_title?: string;
      role_ids: string[];
      unexpected_permissions: string[];
      expected_permissions_summary: string[];
      severity: 'critical' | 'high' | 'medium' | 'low';
      rationale: string;
      suggested_remediation: string;
    }>;
    billable_units: number;
  }> {
    return this.post('/access-accountability/scan-privilege-creep', {});
  }
  async listPrivilegeCreepFindings(params?: {
    status?: 'open' | 'dismissed' | 'remediated';
  }): Promise<{
    data: Array<{
      _id: string;
      user_id: string;
      user_email?: string;
      job_function_title?: string;
      severity: 'critical' | 'high' | 'medium' | 'low';
      unexpected_permissions: string[];
      suggested_remediation: string;
      status: 'open' | 'dismissed' | 'remediated';
      produced_at: string;
    }>;
  }> {
    return this.get(
      `/access-accountability/privilege-creep/findings${this.buildQuery(
        params as Record<string, string> | undefined,
      )}`,
    );
  }
  async resolvePrivilegeCreepFinding(
    findingId: string,
    decision: 'dismissed' | 'remediated',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/access-accountability/privilege-creep/findings/${findingId}/resolve`, {
      decision,
      note,
    });
  }
  // ── Sprint 48.2.8: Compliance rule auto-mapping ─────────────
  /** `offset` is where in the pack's ranked passages the run starts reading (default: the first). */
  async runComplianceRuleMapping(
    packCode: string,
    offset?: number,
  ): Promise<{
    draft_id: string;
    pack_code: string;
    /** Passages this run read, where its page began, how many the pack search returns, how many it left unread. */
    articles_examined?: number;
    passages_offset?: number;
    passages_total?: number;
    passages_remaining?: number;
    suggestions: Array<{
      id: string;
      kind: 'new_rule' | 'refresh_existing' | 'no_change';
      framework_code: string;
      requirement_id: string;
      article_ref?: string;
      existing_rule_id?: string;
      existing_rule_name?: string;
      proposed_title: string;
      proposed_body: string;
      source_quote: string;
    }>;
    billable_units: number;
  }> {
    return this.post(
      `/compliance-rules/map-from-pack/${encodeURIComponent(packCode)}`,
      offset && offset > 0 ? { offset } : {},
    );
  }
  async resolveComplianceRuleMapping(
    draftId: string,
    acceptedSuggestionIds: string[],
  ): Promise<{ status: string; created_rule_ids: string[] }> {
    return this.post(`/compliance-rules/map-from-pack/${draftId}/resolve`, {
      accepted_suggestion_ids: acceptedSuggestionIds,
    });
  }
  // ── Sprint 11.5.0: Org Groups (Phase 9) ─────────────────────
  async createOrgGroup(data: {
    name: string;
    description: string;
    settings?: any;
  }): Promise<OrgGroup> {
    return this.post('/org-groups', data);
  }
  async listOrgGroups(params?: Record<string, string>): Promise<PaginatedResponse<OrgGroup>> {
    return this.get(`/org-groups${this.buildQuery(params)}`);
  }
  async getOrgGroup(groupId: string): Promise<OrgGroup> {
    return this.get(`/org-groups/${groupId}`);
  }
  async updateOrgGroup(groupId: string, data: any): Promise<OrgGroup> {
    return this.patch(`/org-groups/${groupId}`, data);
  }
  async dissolveOrgGroup(groupId: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}`);
  }
  async addGroupMember(groupId: string, data: { org_id: string }): Promise<OrgGroupMembership> {
    return this.post(`/org-groups/${groupId}/members`, data);
  }
  async removeGroupMember(groupId: string, orgId: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/members/${orgId}`);
  }
  async listGroupMembers(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: OrgGroupMembership[] }> {
    return this.get(`/org-groups/${groupId}/members${this.buildQuery(params)}`);
  }
  async getGroupHierarchy(groupId: string): Promise<Record<string, unknown>> {
    return this.get(`/org-groups/${groupId}/hierarchy`);
  }
  async getGroupAudit(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<GroupAuditEntry>> {
    return this.get(`/org-groups/${groupId}/audit${this.buildQuery(params)}`);
  }
  // ── Sprint 11.6.0: Group Shared Data & Settings Inheritance ──
  async publishSharedVendor(groupId: string, data: any): Promise<GroupSharedVendor> {
    return this.post(`/org-groups/${groupId}/shared/vendors`, data);
  }
  async listSharedVendors(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: GroupSharedVendor[] }> {
    return this.get(`/org-groups/${groupId}/shared/vendors${this.buildQuery(params)}`);
  }
  async updateSharedVendor(
    groupId: string,
    vendorId: string,
    data: any,
  ): Promise<GroupSharedVendor> {
    return this.patch(`/org-groups/${groupId}/shared/vendors/${vendorId}`, data);
  }
  async retireSharedVendor(groupId: string, vendorId: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/shared/vendors/${vendorId}`);
  }
  async publishSharedFramework(groupId: string, data: any): Promise<GroupSharedFramework> {
    return this.post(`/org-groups/${groupId}/shared/frameworks`, data);
  }
  async listSharedFrameworks(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: GroupSharedFramework[] }> {
    return this.get(`/org-groups/${groupId}/shared/frameworks${this.buildQuery(params)}`);
  }
  async updateSharedFramework(
    groupId: string,
    frameworkId: string,
    data: any,
  ): Promise<GroupSharedFramework> {
    return this.patch(`/org-groups/${groupId}/shared/frameworks/${frameworkId}`, data);
  }
  async retireSharedFramework(groupId: string, frameworkId: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/shared/frameworks/${frameworkId}`);
  }
  async publishSharedFormTemplate(groupId: string, data: any): Promise<Record<string, unknown>> {
    return this.post(`/org-groups/${groupId}/shared/form-templates`, data);
  }
  async retireSharedFormTemplate(
    groupId: string,
    templateId: string,
  ): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/shared/form-templates/${templateId}`);
  }
  async listSharedFormTemplates(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: Record<string, unknown>[] }> {
    return this.get(`/org-groups/${groupId}/shared/form-templates${this.buildQuery(params)}`);
  }
  async getMergedVendors(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: GroupSharedVendor[] }> {
    return this.get(`/org-groups/${groupId}/merged/vendors${this.buildQuery(params)}`);
  }
  async getMergedFrameworks(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: GroupSharedFramework[] }> {
    return this.get(`/org-groups/${groupId}/merged/frameworks${this.buildQuery(params)}`);
  }
  async getInheritanceStatus(groupId: string): Promise<OrgSettingsInheritance> {
    return this.get(`/org-groups/${groupId}/settings/inheritance`);
  }
  async setInheritanceMode(
    groupId: string,
    category: string,
    data: { mode: string; local_override?: any },
  ): Promise<OrgSettingsInheritance> {
    return this.put(`/org-groups/${groupId}/settings/inheritance/${category}`, data);
  }
  async clearInheritanceOverride(groupId: string, category: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/settings/inheritance/${category}/override`);
  }
  async getGroupDefaults(groupId: string, category?: string): Promise<Record<string, unknown>> {
    const query = category ? `?category=${category}` : '';
    return this.get(`/org-groups/${groupId}/settings/defaults${query}`);
  }
  // ── Sprint 11.7.0: Cross-Org User Roles & Group Audit ──────
  async assignGroupRole(
    groupId: string,
    data: { user_id: string; group_role: string },
  ): Promise<GroupUserAssignment> {
    return this.post(`/org-groups/${groupId}/users`, data);
  }
  async revokeGroupRole(groupId: string, userId: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/users/${userId}`);
  }
  async listGroupUsers(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: GroupUserAssignment[] }> {
    return this.get(`/org-groups/${groupId}/users${this.buildQuery(params)}`);
  }
  async getMyMemberships(): Promise<{ data: UserOrgMembership[] }> {
    return this.get('/org-groups/my-memberships');
  }
  async switchOrg(data: { target_org_id: string }): Promise<OrgSwitchResponse> {
    return this.post('/org-groups/switch-org', data);
  }
  async getAccessibleOrgs(groupId: string): Promise<{ data: Record<string, unknown>[] }> {
    return this.get(`/org-groups/${groupId}/accessible-orgs`);
  }
  async getGroupAuditConsolidated(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<GroupAuditEntry>> {
    return this.get(`/org-groups/${groupId}/audit/consolidated${this.buildQuery(params)}`);
  }
  async getGroupAuditSummary(groupId: string): Promise<GroupAuditSummary> {
    return this.get(`/org-groups/${groupId}/audit/summary`);
  }
  // Sprint 42.2.3 E2 — returns AttachmentRef when format='csv'; legacy
  // format='json' still returns the raw {data} envelope.
  async exportGroupAudit(
    groupId: string,
    data: any,
  ): Promise<AttachmentRef | Record<string, unknown>> {
    return this.post(`/org-groups/${groupId}/audit/export`, data);
  }
  async searchGroupAudit(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<GroupAuditEntry>> {
    return this.get(`/org-groups/${groupId}/audit/search${this.buildQuery(params)}`);
  }
  // ── Sprint 11.8.0: Group Dashboard & Reporting ─────────────
  async getGroupDashboard(groupId: string): Promise<GroupDashboardData> {
    return this.get(`/org-groups/${groupId}/dashboard`);
  }
  async getGroupDashboardWidget(
    groupId: string,
    widgetType: string,
  ): Promise<GroupDashboardWidget> {
    return this.get(`/org-groups/${groupId}/dashboard/${widgetType}`);
  }
  async refreshGroupDashboard(groupId: string): Promise<Record<string, unknown>> {
    return this.post(`/org-groups/${groupId}/dashboard/refresh`, {});
  }
  async requestGroupReport(groupId: string, data: any): Promise<GroupReport> {
    return this.post(`/org-groups/${groupId}/reports`, data);
  }
  async listGroupReports(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: GroupReport[] }> {
    return this.get(`/org-groups/${groupId}/reports${this.buildQuery(params)}`);
  }
  async getGroupReport(groupId: string, reportId: string): Promise<GroupReport> {
    return this.get(`/org-groups/${groupId}/reports/${reportId}`);
  }
  async deleteGroupReport(groupId: string, reportId: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/reports/${reportId}`);
  }
  async downloadGroupReport(groupId: string, reportId: string): Promise<Record<string, unknown>> {
    return this.get(`/org-groups/${groupId}/reports/${reportId}/download`);
  }
  // ── Phase 9 Extension: Organization Management ──────────────────
  async listOrganizations(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    return this.get(`/organizations${this.buildQuery(params)}`);
  }
  async getOrganization(orgId: string): Promise<Record<string, unknown>> {
    return this.get(`/organizations/${orgId}`);
  }
  async createOrganization(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.post('/organizations', payload);
  }
  async updateOrganization(
    orgId: string,
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch(`/organizations/${orgId}`, payload);
  }
  async deactivateOrganization(orgId: string): Promise<{ message: string }> {
    return this.del(`/organizations/${orgId}`);
  }
  // ── Phase 9 Extension: Intake Config Management ────────────────

  /**
   * Which request types this organisation may record, and which of them an
   * installed clock dates.
   *
   * Sprint 65.15.0 (AC6) first wired this read into `CreateCasePage`, then
   * against `409 REQUEST_TYPE_NOT_CLOCKABLE`. Sprint 63.31.0 withdrew that
   * refusal (owner ruling OD-A): a class no installed clock dates is accepted
   * and dated from the organisation's SLA target or the platform default. What
   * is still refused is a type no installed framework grants
   * (`422 REQUEST_TYPE_NOT_OFFERED`, #438), so the operator's form offers
   * `offered`, the public form `offerable` (enabled ∩ offered), and
   * `unclockable` names the types a case of which is dated by no statute.
   * The server computes every list; the page names no instrument (R-46).
   */
  async getClockableRequestTypes(): Promise<{
    data: {
      has_capable_content: boolean;
      enabled: string[];
      offerable: string[];
      unclockable: string[];
      clockable_not_offered: string[];
      /** Sprint 63.31.0 (#438) — whether an installed framework declares its request types. */
      declared_by_frameworks?: boolean;
      /** The request types the installed frameworks grant (or the platform set when none declares). */
      offered?: string[];
      /** Enabled on the public form and not a right under the installed frameworks. */
      enabled_not_offered?: string[];
      /** A right the installed frameworks grant that the public form does not enable. */
      offered_not_enabled?: string[];
      /**
       * Sprint 63.31.0 (OD-E) — offered types handled in Legal Matters, with the
       * installed rights a request of each may name.
       */
      legal_matter_rights?: Record<string, LegalMatterRightOption[]>;
    };
  }> {
    return this.get('/organizations/intake-config/clockable-request-types');
  }

  async listIntakeConfigs(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    return this.get(`/organizations/intake-configs/list${this.buildQuery(params)}`);
  }
  async getIntakeConfig(configId: string): Promise<Record<string, unknown>> {
    return this.get(`/organizations/intake-configs/${configId}`);
  }
  async createIntakeConfig(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.post('/organizations/intake-configs', payload);
  }
  async updateIntakeConfig(
    configId: string,
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch(`/organizations/intake-configs/${configId}`, payload);
  }
  async deleteIntakeConfig(configId: string): Promise<{ message: string }> {
    return this.del(`/organizations/intake-configs/${configId}`);
  }

  // ── Sprint 22.3.0: Organizations Module Maturity ────────────
  async createDepartment(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.post('/organizations/departments', payload);
  }
  async listDepartments(): Promise<Record<string, unknown>[]> {
    return this.get('/organizations/departments');
  }
  async getDepartmentHierarchy(): Promise<Record<string, unknown>> {
    return this.get('/organizations/departments/hierarchy');
  }
  async mapUserToDepartment(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.post('/organizations/departments/user-mappings', payload);
  }
  async listDepartmentUsers(departmentId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/organizations/departments/${departmentId}/users`);
  }
  async removeDepartmentUser(mappingId: string): Promise<void> {
    return this.del(`/organizations/departments/user-mappings/${mappingId}`);
  }
  async assignFrameworkToEntity(
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.post('/organizations/entity-framework-assignments', payload);
  }
  async listEntityFrameworkAssignments(entityId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/organizations/entities/${entityId}/framework-assignments`);
  }
  async removeEntityFrameworkAssignment(assignmentId: string): Promise<void> {
    return this.del(`/organizations/entity-framework-assignments/${assignmentId}`);
  }
  async getComplianceRollup(params?: Record<string, string>): Promise<Record<string, unknown>> {
    const qs =
      params && Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
    return this.get(`/organizations/compliance-rollup${qs}`);
  }
  async getEntityComplianceSummary(entityId: string): Promise<Record<string, unknown>> {
    return this.get(`/organizations/entities/${entityId}/compliance-summary`);
  }
  async listDepartmentsByUser(userId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/organizations/departments/by-user/${userId}`);
  }

  // ── Sprint 44.0.7 Wave 3: Organizations + Org Groups — multi-format list exports ──
  //
  // R-33 migration replaces 1-format `downloadClientCSV` / `downloadClientExport`
  // (and the new-Blob JSON fallback on GroupAuditPage) with 4-format
  // (csv/xlsx/pdf/docx) backend routes that call
  // UnifiedExportService.generateFromDefinition. Each method returns an
  // `AttachmentRef` that can be handed to `downloadPlatformFile`.
  async exportOrganizationsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/organizations/export${this.buildQuery(params)}`);
  }
  async exportOrganizationIntakeConfigsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/organizations/intake-configs/export${this.buildQuery(params)}`);
  }
  async exportOrganizationDepartmentsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/organizations/departments/export${this.buildQuery(params)}`);
  }
  async exportOrganizationFrameworkAssignmentsList(
    entityId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/organizations/entities/${encodeURIComponent(entityId)}/framework-assignments/export${this.buildQuery(params)}`,
    );
  }
  async exportOrganizationComplianceRollupList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/organizations/compliance-rollup/export${this.buildQuery(params)}`);
  }
  async exportOrgGroupsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/org-groups/export${this.buildQuery(params)}`);
  }
  async exportOrgGroupDashboard(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/dashboard/export${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupReportsList(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/reports/export${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupConsolidatedAudit(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/audit/export-list${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupPerOrgAudit(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/audit/export-per-org${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupUsersList(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/users/export${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupSharedVendorsList(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/shared/vendors/export${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupSharedFrameworksList(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/shared/frameworks/export${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupSharedFormTemplatesList(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/shared/form-templates/export${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupMergedVendorsList(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/merged/vendors/export${this.buildQuery(params)}`,
    );
  }

  // ── Sprint 43.2.0 batch 3 (E2) — Department CRUD extensions ────
  async updateDepartment(
    departmentId: string,
    updates: {
      name?: string;
      description?: string;
      head_user_id?: string | null;
      /** `true` reactivates a deactivated department. */
      is_active?: boolean;
    },
  ): Promise<Record<string, unknown>> {
    return this.patch(`/organizations/departments/${departmentId}`, updates);
  }
  async deactivateDepartment(departmentId: string): Promise<Record<string, unknown>> {
    return this.post(`/organizations/departments/${departmentId}/deactivate`, {});
  }
  async deleteDepartment(
    departmentId: string,
    reassignTo: string | null,
  ): Promise<{ cases_reassigned: number; department_deleted: boolean }> {
    return this.del(`/organizations/departments/${departmentId}`, { reassignTo });
  }
  async getDepartmentActiveCaseCount(departmentId: string): Promise<{ count: number }> {
    return this.get(`/organizations/departments/${departmentId}/active-case-count`);
  }

  // ── Sprint 43.2.0 batch 3 (E4) — Retention policies ────────────
  async listOrgRetentionPolicies(): Promise<OrgRetentionPolicy[]> {
    return this.get('/organizations/retention-policies');
  }
  async createOrgRetentionPolicy(payload: {
    target_type: string;
    retention_days: number;
    policy_owner: string;
    effective_from?: string;
  }): Promise<OrgRetentionPolicy> {
    return this.post('/organizations/retention-policies', payload);
  }
  async updateOrgRetentionPolicy(
    policyId: string,
    updates: Partial<{
      target_type: string;
      retention_days: number;
      policy_owner: string;
      effective_from: string;
    }>,
  ): Promise<OrgRetentionPolicy> {
    return this.patch(`/organizations/retention-policies/${policyId}`, updates);
  }
  async deactivateOrgRetentionPolicy(policyId: string): Promise<void> {
    return this.post(`/organizations/retention-policies/${policyId}/deactivate`, {});
  }

  // ── Sprint 17.3.0: Notification System ──────────────────────

  async listNotifications(params?: Record<string, string>): Promise<Record<string, unknown>> {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.get(`/notifications${qs}`);
  }

  /** Polled by the notification bell every 30 s: a background read (it renews no idle clock). */
  async getNotificationStats(): Promise<Record<string, unknown>> {
    return this.getInBackground('/notifications/stats');
  }

  async markNotificationRead(notificationId: string): Promise<Record<string, unknown>> {
    return this.patch(`/notifications/${notificationId}/read`);
  }

  async markAllNotificationsRead(category?: string): Promise<Record<string, unknown>> {
    return this.post('/notifications/read-all', category ? { category } : {});
  }

  async archiveNotification(notificationId: string): Promise<Record<string, unknown>> {
    return this.patch(`/notifications/${notificationId}/archive`);
  }

  async bulkArchiveNotifications(olderThan: string): Promise<Record<string, unknown>> {
    return this.post('/notifications/archive-bulk', { older_than: olderThan });
  }

  async getNotificationPreferences(): Promise<Record<string, unknown>> {
    return this.get('/notifications/preferences');
  }

  async updateNotificationPreferences(
    updates: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch('/notifications/preferences', updates);
  }

  async watchEntity(
    module: string,
    entityId: string,
    entityType: string,
  ): Promise<Record<string, unknown>> {
    return this.post('/notifications/watch', {
      module,
      entity_id: entityId,
      entity_type: entityType,
    });
  }

  async unwatchEntity(module: string, entityId: string): Promise<void> {
    return this.del(`/notifications/watch/${module}/${entityId}`);
  }

  async getEmailHistory(params?: Record<string, string>): Promise<Record<string, unknown>> {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.get(`/notifications/email-history${qs}`);
  }

  async getAdminEmailQueue(): Promise<Record<string, unknown>> {
    return this.get('/notifications/admin/queue');
  }

  async getAdminEmailQueueList(params?: Record<string, string>): Promise<Record<string, unknown>> {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.get(`/notifications/admin/queue/list${qs}`);
  }

  async retryFailedEmail(emailId: string): Promise<Record<string, unknown>> {
    return this.post(`/notifications/admin/retry/${emailId}`);
  }

  async processEmailQueue(): Promise<{ processed: number }> {
    return this.post('/notifications/admin/queue/process') as Promise<{ processed: number }>;
  }

  // ── Sprint 17.4.0: Task Management ─────────────────────────
  async getTasks(params?: Record<string, any>): Promise<PaginatedResponse<TaskListItem>> {
    return this.get(`/tasks${this.buildQuery(params)}`);
  }
  async getTask(id: string): Promise<TaskRecord> {
    return this.get(`/tasks/${id}`);
  }
  async createTask(payload: Record<string, unknown>): Promise<TaskRecord> {
    return this.post('/tasks', payload);
  }
  async updateTask(id: string, payload: Record<string, unknown>): Promise<TaskRecord> {
    return this.patch(`/tasks/${id}`, payload);
  }
  async updateTmTaskStatus(id: string, status: string): Promise<TaskRecord> {
    return this.patch(`/tasks/${id}/status`, { status });
  }
  /** Sprint 63.30.0 (E10, #574) — escalate with a reason; needs `tm:task:escalate`. */
  async escalateTask(id: string, reason: string): Promise<TaskRecord> {
    return this.post(`/tasks/${id}/escalate`, { reason });
  }
  async deleteTask(id: string): Promise<void> {
    return this.del(`/tasks/${id}`);
  }
  async bulkUpdateTaskStatus(taskIds: string[], status: string): Promise<Record<string, unknown>> {
    return this.patch('/tasks/bulk/status', { task_ids: taskIds, status });
  }
  // Sprint 47.5.8.5 — convert a task into a Forseti AI Workflow Run.
  // Reuses the existing /api/v1/ai-workflows run pipeline; the route on
  // /tasks/:id stamps source.linked_workflow_run_ids and emits the
  // ai_advisor_task_converted_to_workflow_run audit event.
  async convertTaskToWorkflowRun(
    taskId: string,
    body: {
      workflow_id: string;
      attachment_ids?: string[];
      persona_slug_override?: import('@vn-grc/shared').ForsetiPersonaSlug;
    },
  ): Promise<{ run_id: string }> {
    return this.post(`/tasks/${taskId}/convert-to-workflow-run`, body);
  }
  async assignTmTask(id: string, userIds: string[]): Promise<TaskRecord> {
    return this.post(`/tasks/${id}/assign`, { user_ids: userIds });
  }
  async unassignTask(id: string, userId: string): Promise<void> {
    return this.del(`/tasks/${id}/assign/${userId}`);
  }
  async getMyTasks(params?: Record<string, any>): Promise<PaginatedResponse<TaskListItem>> {
    return this.get(`/tasks/my${this.buildQuery(params)}`);
  }
  async getOverdueTasks(params?: Record<string, any>): Promise<PaginatedResponse<TaskListItem>> {
    return this.get(`/tasks/overdue${this.buildQuery(params)}`);
  }
  async getTaskStats(): Promise<Record<string, unknown>> {
    return this.get('/tasks/stats');
  }
  async getCalendarTasks(params: Record<string, any>): Promise<{ data: TaskListItem[] }> {
    return this.get(`/tasks/calendar${this.buildQuery(params)}`);
  }
  async getTaskComments(
    id: string,
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<TaskComment>> {
    return this.get(`/tasks/${id}/comments${this.buildQuery(params)}`);
  }
  async addTaskComment(id: string, content: string): Promise<TaskComment> {
    return this.post(`/tasks/${id}/comments`, { content });
  }
  async updateTaskComment(id: string, commentId: string, content: string): Promise<TaskComment> {
    return this.patch(`/tasks/${id}/comments/${commentId}`, { content });
  }
  async deleteTaskComment(id: string, commentId: string): Promise<void> {
    return this.del(`/tasks/${id}/comments/${commentId}`);
  }
  async updateTmTaskChecklist(
    id: string,
    itemId: string,
    completed: boolean,
  ): Promise<Record<string, unknown>> {
    return this.patch(`/tasks/${id}/checklist/${itemId}`, { completed });
  }
  async addTaskChecklistItem(id: string, label: string): Promise<Record<string, unknown>> {
    return this.post(`/tasks/${id}/checklist`, { label });
  }
  async getTasksByEntity(module: string, entityId: string): Promise<TaskListItem[]> {
    return this.get(`/tasks/by-entity/${module}/${entityId}`);
  }
  async addTaskDependency(
    id: string,
    taskId: string,
    type: string,
    description?: string,
  ): Promise<Record<string, unknown>> {
    return this.post(`/tasks/${id}/dependencies`, {
      task_id: taskId,
      dependency_type: type,
      description,
    });
  }
  async removeTaskDependency(id: string, depId: string): Promise<void> {
    return this.del(`/tasks/${id}/dependencies/${depId}`);
  }
  async getTaskAudit(
    id: string,
    params?: Record<string, any>,
  ): Promise<{ data: Record<string, unknown>[] }> {
    return this.get(`/tasks/${id}/audit${this.buildQuery(params)}`);
  }
  // Workflow instance methods (task-scoped)
  async getTaskWorkflow(taskId: string): Promise<WorkflowInstance> {
    return this.get(`/tasks/${taskId}/workflow`);
  }
  async getTaskWorkflowTransitions(taskId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/tasks/${taskId}/workflow/transitions`);
  }
  async transitionTaskWorkflow(
    taskId: string,
    toStepId: string,
    notes?: string,
  ): Promise<WorkflowInstance> {
    return this.post(`/tasks/${taskId}/workflow/transition`, { to_step_id: toStepId, notes });
  }
  async getTaskWorkflowHistory(taskId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/tasks/${taskId}/workflow/history`);
  }
  async getTaskWorkflowSla(taskId: string): Promise<Record<string, unknown>> {
    return this.get(`/tasks/${taskId}/workflow/sla`);
  }
  // --- Workflow definition CRUD (Sprint 17.5.0) ---
  async getWorkflows(params?: Record<string, any>): Promise<PaginatedResponse<WorkflowDefinition>> {
    return this.get(`/workflows${this.buildQuery(params)}`);
  }
  async getWorkflow(id: string): Promise<WorkflowDefinition> {
    return this.get(`/workflows/${id}`);
  }
  async createWorkflow(payload: Record<string, any>): Promise<WorkflowDefinition> {
    return this.post('/workflows', payload);
  }
  async updateWorkflow(id: string, payload: Record<string, any>): Promise<WorkflowDefinition> {
    return this.patch(`/workflows/${id}`, payload);
  }
  async activateWorkflow(id: string): Promise<WorkflowDefinition> {
    return this.post(`/workflows/${id}/activate`, {});
  }
  async archiveWorkflow(id: string): Promise<WorkflowDefinition> {
    return this.post(`/workflows/${id}/archive`, {});
  }
  async deleteWorkflow(id: string): Promise<void> {
    return this.del(`/workflows/${id}`);
  }
  async validateWorkflow(id: string): Promise<Record<string, unknown>> {
    return this.get(`/workflows/${id}/validate`);
  }
  async getWorkflowTemplates(): Promise<WorkflowTemplate[]> {
    return this.get('/workflows/templates');
  }
  async createWorkflowFromTemplate(templateKey: string): Promise<WorkflowDefinition> {
    return this.post(`/workflows/from-template/${encodeURIComponent(templateKey)}`, {});
  }
  // ── Sprint 44.0.9 Wave 3 — 12 Task Management list/detail exports ─
  //
  // 4-format (csv/xlsx/pdf/docx) backend routes via UnifiedExportService.
  // Each returns an AttachmentRef ({ file_id, filename, ... }) consumed
  // by `downloadPlatformFile(file_id, filename)`. The pre-existing
  // `GET /tasks/export` (Sprint 17.0.0, TM_EXPORT-gated, returns a flat
  // JSON array) is kept for programmatic consumers; the new
  // `GET /tasks/export-list` drives the UI. Sprint 44.0.9 CLOSES the
  // 44.0.x Standard/Enterprise QA finalization series.
  async exportTaskList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/export-list${this.buildQuery(params)}`);
  }
  async exportTaskBoard(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/board/export${this.buildQuery(params)}`);
  }
  async exportTaskDetail(
    taskId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/${taskId}/export${this.buildQuery(params)}`);
  }
  async exportMyTasksList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/my/export${this.buildQuery(params)}`);
  }
  async exportOverdueTasksList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/overdue/export${this.buildQuery(params)}`);
  }
  async exportCalendarTasks(
    params: Record<string, unknown> & {
      format: 'csv' | 'xlsx' | 'pdf' | 'docx';
      start_date: string;
      end_date: string;
    },
  ): Promise<any> {
    return this.get(`/tasks/calendar/export${this.buildQuery(params)}`);
  }
  async exportWorkflowList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/workflows/export${this.buildQuery(params)}`);
  }
  async exportWorkflowDetail(
    workflowId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/workflows/${workflowId}/export${this.buildQuery(params)}`);
  }
  async exportWorkflowTemplateCatalog(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/workflows/templates/export${this.buildQuery(params)}`);
  }
  async exportTaskConflicts(
    masterTaskId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/${masterTaskId}/conflicts/export${this.buildQuery(params)}`);
  }
  async exportTaskConsolidation(
    masterTaskId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/${masterTaskId}/consolidation/export${this.buildQuery(params)}`);
  }
  async exportTaskSectionAssignments(
    taskId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/${taskId}/sections/export${this.buildQuery(params)}`);
  }
  // --- Team workload (Sprint 17.5.0) ---
  async getTeamWorkload(params?: Record<string, any>): Promise<Record<string, unknown>> {
    return this.get(`/tasks/team-workload${this.buildQuery(params)}`);
  }
  // --- Task search for dependency modal (Sprint 17.5.0) ---
  // There is no dedicated `/tasks/search` route — searching is handled by the
  // list endpoint via the `search` filter (matched against title/description).
  // Calling `/tasks/search` previously fell through to `GET /:id` with
  // id="search" and returned TASK_NOT_FOUND.
  async searchTasks(
    query: string,
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<TaskListItem>> {
    return this.get(`/tasks${this.buildQuery({ search: query, ...params })}`);
  }
  // --- Task dependency graph (Sprint 17.5.0) ---
  async getTaskDependencyGraph(id: string): Promise<Record<string, unknown>> {
    return this.get(`/tasks/${id}/dependency-graph`);
  }

  // ── Sprint 17.6.0: Dashboard task widgets + Cross-org ──────────
  async getDashboardTaskSummary(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/tasks/summary');
  }
  async getDashboardUpcomingTasks(days = 7): Promise<Record<string, unknown>> {
    return this.get(`/dashboard/tasks/upcoming?days=${days}`);
  }
  async getDashboardOverdueTasks(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/tasks/overdue');
  }
  // ── Sprint 20.2.0: Persona-aware dashboard widgets ──────────
  async getDashboardPersonaSummary(frameworkCode?: string): Promise<Record<string, unknown>> {
    const q = frameworkCode ? `?framework_code=${encodeURIComponent(frameworkCode)}` : '';
    return this.get(`/dashboard/persona-summary${q}`);
  }
  async getDashboardFilingLifecycle(frameworkCode?: string): Promise<Record<string, unknown>> {
    const q = frameworkCode ? `?framework_code=${encodeURIComponent(frameworkCode)}` : '';
    return this.get(`/dashboard/filing-lifecycle${q}`);
  }
  async getDashboardDepartmentConsolidation(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/department-consolidation');
  }
  async getDashboardVendorRiskSummary(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/vendor-risk-summary');
  }
  async getDashboardDiscoverySummary(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/discovery-summary');
  }
  async getDashboardAiGovernanceSummary(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/ai-governance-summary');
  }
  async getDashboardBreachSummary(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/breach-summary');
  }
  async getDashboardAuditReadiness(frameworkCode?: string): Promise<Record<string, unknown>> {
    const q = frameworkCode ? `?framework_code=${encodeURIComponent(frameworkCode)}` : '';
    return this.get(`/dashboard/audit-readiness${q}`);
  }
  async getDashboardMyDepartment(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/my-department');
  }
  // Sprint 22.7.3: Department-scoped widget endpoints
  async getDashboardMyTasks(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/my-tasks');
  }
  async getDashboardMyDepartmentStatus(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/my-department-status');
  }
  // Sprint 22.7.1: Dashboard user preferences
  async getDashboardPreferences(): Promise<UserDashboardPreferences> {
    return this.get('/dashboard/preferences');
  }
  async saveDashboardPreferences(
    payload: UpdateDashboardPreferencesPayload,
  ): Promise<UserDashboardPreferences> {
    return this.put('/dashboard/preferences', payload);
  }
  async getDashboardPreferenceDefaults(): Promise<UserDashboardPreferences> {
    return this.get('/dashboard/preferences/defaults');
  }
  async getCrossOrgTasks(
    orgGroupId: string,
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<TaskListItem>> {
    return this.get(`/tasks/cross-org/${orgGroupId}${this.buildQuery(params)}`);
  }
  async getCrossOrgCalendarTasks(
    orgGroupId: string,
    params: Record<string, any>,
  ): Promise<{ data: TaskListItem[] }> {
    return this.get(`/tasks/cross-org/${orgGroupId}/calendar${this.buildQuery(params)}`);
  }
  async getCrossOrgTaskStats(orgGroupId: string): Promise<Record<string, unknown>> {
    return this.get(`/tasks/cross-org/${orgGroupId}/stats`);
  }
  async getOrgGroups(params?: Record<string, any>): Promise<PaginatedResponse<OrgGroup>> {
    return this.get(`/org-groups${this.buildQuery(params)}`);
  }

  // ── Sprint 18.0.0: License & Package Management ──────────────

  async getLicense(): Promise<License> {
    return this.get('/license');
  }

  async checkLicense(payload: { license_key: string }): Promise<Record<string, unknown>> {
    return this.post('/license/check', payload);
  }

  async activateLicense(payload: { license_key: string }): Promise<License> {
    return this.post('/license/activate', payload);
  }

  async deactivateLicense(): Promise<{ message: string }> {
    return this.post('/license/deactivate', {});
  }

  async validateLicense(): Promise<Record<string, unknown>> {
    return this.post('/license/validate', {});
  }

  async getPackageInfo(): Promise<PackageInfo> {
    return this.get('/package');
  }

  async changePackage(request: {
    target_tier: string;
    license_key: string;
  }): Promise<PackageChangeResult> {
    return this.post('/package/change', request);
  }

  // ── Sprint 18.3.0: Settings Defaults (public, no auth) ──────

  async getDefaults(): Promise<Record<string, unknown>> {
    return this.get('/settings/defaults');
  }

  // ── Sprint 20.3.0: Assessments -- Dossier Filings ──────────
  async listDossierFilings(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<DossierFiling>> {
    return this.get(`/assessments/dossier-filings${this.buildQuery(params)}`);
  }
  async createDossierFiling(payload: Record<string, unknown>): Promise<DossierFiling> {
    return this.post('/assessments/dossier-filings', payload);
  }
  /** Sprint 63.37.0 — before a first filing: one dossier per organisation, then updates. */
  async getFirstFilingGuidance(
    assessmentKind: 'dpia' | 'otia',
  ): Promise<DossierFirstFilingGuidance> {
    return this.get(
      `/assessments/dossier-filings/first-filing-guidance${this.buildQuery({ assessment_kind: assessmentKind })}`,
    );
  }
  async getDossierFiling(filingId: string): Promise<DossierFiling> {
    return this.get(`/assessments/dossier-filings/${filingId}`);
  }
  async updateDossierFiling(
    filingId: string,
    payload: Record<string, unknown>,
  ): Promise<DossierFiling> {
    return this.patch(`/assessments/dossier-filings/${filingId}`, payload);
  }
  async assignDossierSections(filingId: string, assignments: any[]): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/assign-sections`, { assignments });
  }
  async submitDossierForInternalReview(filingId: string): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/submit-internal-review`);
  }
  async dossierLegalSignoff(filingId: string): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/legal-signoff`);
  }
  async approveDossierFiling(filingId: string): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/approve`);
  }
  // Sprint 44.4.0 — submission artifact hardening
  async retryDossierMaterialisation(filingId: string): Promise<{
    filing_id: string;
    submission_dossier_id: string;
    materialisation_status: 'pending' | 'success' | 'failed';
    created: boolean;
  }> {
    return this.post(`/assessments/dossier-filings/${filingId}/retry-materialisation`);
  }
  async getDossierMaterialisationStatus(filingId: string): Promise<{
    filing_id: string;
    status: 'pending' | 'success' | 'failed' | null;
    dossier_id: string | null;
    failure_reason: string | null;
    last_attempt_at: string | null;
    failed_at: string | null;
    retry_count: number;
    eligible_to_retry: boolean;
  }> {
    return this.get(`/assessments/dossier-filings/${filingId}/materialisation-status`);
  }
  async getDossierMaterialisationHealth(): Promise<{
    approved_not_materialised: Array<{
      filing_id: string;
      title: string;
      form_type: string;
      approved_at: string | null;
    }>;
    failed_materialisations: Array<{
      filing_id: string;
      title: string;
      form_type: string;
      failure_reason: string | null;
      failed_at: string | null;
      retry_count: number;
    }>;
    orphan_dossiers: Array<{
      dossier_id: string;
      source_filing_id: string;
      dossier_type: string;
      created_at: string;
    }>;
  }> {
    return this.get(`/assessments/admin/dossier-materialisation-health`);
  }
  async submitDossierToAuthority(
    filingId: string,
    submission: Record<string, unknown>,
  ): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/submit-mps`, submission);
  }
  /**
   * Fix sprint #447 (round 2) — the channels `submit-mps` accepts, read from the
   * installed filing framework's declaration; `refusal` states why there are none.
   */
  async listDossierFilingChannels(filingId?: string): Promise<{
    framework_code: string | null;
    channels: Array<{ code: string; name: Record<string, string> }>;
    refusal: FilingChannelRefusal | null;
  }> {
    // fix-sprint-20260808-2111 (#36) — for one filing, the channels of the
    // framework its `submit-mps` will validate against.
    return this.get(
      `/assessments/dossier-filings/filing-channels${this.buildQuery(
        filingId ? { filing_id: filingId } : {},
      )}`,
    );
  }
  /**
   * Sprint 62.57.0 (Slice C, D28) — the pre-result supplement request cites the
   * communication (`communication_id`) and may state it asks for completion
   * (`completion_requested`); a typed `received_at` with no communication opens
   * nothing. 422 `DOSSIER_COMPLETION_REQUEST_COMMUNICATION_INVALID`.
   */
  async recordDossierSupplementRequest(
    filingId: string,
    request: RecordPreResultSupplementRequestBody | Record<string, unknown>,
  ): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/supplement-request`, request);
  }
  /**
   * Sprint 62.57.0 (Slice C, D30) — the supplement or completion submitted.
   * From `result_recorded` with an open completion period it moves the filing
   * to `result_completion_submitted`. 409 `INVALID_STATUS`; 422
   * `DOSSIER_COMPLETION_DATE_INVALID`. A string is the legacy call (notes only).
   */
  async submitDossierSupplement(
    filingId: string,
    body: SubmitDossierSupplementBody | string,
  ): Promise<DossierFiling> {
    return this.post(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/submit-supplement`,
      typeof body === 'string' ? { response_notes: body } : body,
    );
  }
  /**
   * Sprint 62.57.0 (Slice C, D25) — a person's statement that one recorded
   * communication asks the organisation to complete the dossier. Append-only (a
   * correction names `correction_of`). 201 `{ data: detail }`; 409
   * `DOSSIER_COMPLETION_REQUEST_NOT_OPEN`, `DOSSIER_COMPLETION_REQUEST_ALREADY_CORRECTED`,
   * `DOSSIER_FILING_CHANGED`; 422 `DOSSIER_COMPLETION_REQUEST_COMMUNICATION_INVALID`;
   * 404 `DOSSIER_COMPLETION_REQUEST_NOT_FOUND`. A withdrawal (BE-5) goes to the
   * same route with `withdrawn: true` and no `communication_id`.
   */
  async recordCompletionRequest(
    filingId: string,
    body: RecordCompletionRequestBody | WithdrawCompletionRequestBody,
  ): Promise<{ data?: DossierFiling }> {
    return this.post(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/completion-requests`,
      body,
    );
  }
  /**
   * Sprint 62.57.0 (Slice D, D36) — the update's recipient and competent
   * authority, recorded by a person. 409 `DOSSIER_UPDATE_ADDRESSEE_LOCKED`,
   * `DOSSIER_FILING_CHANGED`; 422 `DOSSIER_UPDATE_BODY_INVALID`,
   * `DOSSIER_UPDATE_REASON_REQUIRED`; 404 for `updates_filing_id`.
   */
  async updateDossierUpdateAddressee(
    filingId: string,
    body: UpdateAddresseeBody,
  ): Promise<{ data?: DossierFiling }> {
    return this.put(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/update-addressee`,
      body,
    );
  }
  /**
   * Sprint 62.57.0 (AC-F5, D38, D41) — for each requirement id given, how many
   * filings an installed declaration naming it governs. Pages over the ids
   * given (1–200 per call); only ids some declaration names are answered.
   */
  async listDossierRequirementLinks(
    requirementIds: readonly string[],
    paging: { page: number; page_size: number } = { page: 1, page_size: 200 },
  ): Promise<{ data: DossierRequirementLink[]; total: number; page: number; page_size: number }> {
    const res = await this.get<{
      data?: DossierRequirementLink[];
      total?: number;
      page?: number;
      page_size?: number;
    }>(
      `/assessments/dossier-filings/requirement-links${this.buildQuery({
        requirement_ids: requirementIds.join(','),
        page: String(paging.page),
        page_size: String(paging.page_size),
      })}`,
    );
    const data = (res?.data ?? []) as DossierRequirementLink[];
    return {
      data,
      total: typeof res?.total === 'number' ? res.total : data.length,
      page: typeof res?.page === 'number' ? res.page : paging.page,
      page_size: typeof res?.page_size === 'number' ? res.page_size : paging.page_size,
    };
  }
  async resubmitDossier(filingId: string): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/resubmit`);
  }
  /**
   * Sprint 62.57.0 (Slice B, AC-B1, AC-B7) — record the authority's result from
   * one of the options the governing text offers (`appraisal.result_control`).
   * Strict body; 409 `INVALID_STATUS`; 422 `DOSSIER_GOVERNING_TEXT_NOT_DETERMINED`,
   * `DOSSIER_RESULT_DECLARATION_ABSENT`, `DOSSIER_RESULT_OPTION_NOT_OFFERED`,
   * `DOSSIER_RESULT_DATE_INVALID`, `DOSSIER_RESULT_COMMUNICATION_INVALID`.
   */
  async recordDossierResult(
    filingId: string,
    body: RecordDossierResultBody,
  ): Promise<{ data?: DossierFiling } & Partial<DossierFiling>> {
    return this.post(`/assessments/dossier-filings/${encodeURIComponent(filingId)}/outcome`, body);
  }
  /**
   * Sprint 62.57.0 (Slice B) — close the filing, with the reason a person gives
   * where one is needed. 409 `DOSSIER_RESULT_SUPPLEMENT_OPEN`,
   * `DOSSIER_FAILURE_FOLLOW_UP_OPEN`; 422 `DOSSIER_CLOSE_REASON_REQUIRED`.
   */
  async closeDossierFiling(
    filingId: string,
    body: { closing_reason?: string } = {},
  ): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${encodeURIComponent(filingId)}/close`, body);
  }
  /**
   * Sprint 62.57.0 (Slice F, AC-F1, AC-F3, D16, D17, D19) — append one receipt
   * evidence entry (a correction names `correction_of`); re-stamps the governing
   * text. 409 `DOSSIER_NOT_SUBMITTED`, `DOSSIER_FILING_CHANGED`; 422
   * `DOSSIER_RECEIPT_DATE_INVALID`; 404.
   */
  async recordDossierReceiptEvidence(
    filingId: string,
    body: RecordDossierReceiptEvidenceBody,
  ): Promise<{ data?: unknown }> {
    return this.post(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/receipt-evidence`,
      body,
    );
  }
  /**
   * Sprint 62.57.0 (Slice A, AC-A2) — a person's statement of the body that
   * handles the dossier, citing a recorded communication. Append-only.
   */
  async recordHandlingAuthorityStatement(
    filingId: string,
    body: RecordHandlingAuthorityStatementBody,
  ): Promise<{ data: DossierHandlingAuthorityStatement }> {
    return this.post(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/handling-authority-statements`,
      body,
    );
  }
  /** Sprint 62.57.0 (Slice A, AC-A6) — the statement history, one page at a time (R-23). */
  async listHandlingAuthorityStatements(
    filingId: string,
    paging: { page: number; page_size: number },
  ): Promise<{
    data: DossierHandlingAuthorityStatement[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const res = await this.get<{
      data?: DossierHandlingAuthorityStatement[];
      total?: number;
      page?: number;
      page_size?: number;
    }>(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/handling-authority-statements${this.buildQuery(
        { page: String(paging.page), page_size: String(paging.page_size) },
      )}`,
    );
    const data = (res?.data ?? []) as DossierHandlingAuthorityStatement[];
    return {
      data,
      total: typeof res?.total === 'number' ? res.total : data.length,
      page: typeof res?.page === 'number' ? res.page : paging.page,
      page_size: typeof res?.page_size === 'number' ? res.page_size : paging.page_size,
    };
  }
  /**
   * Sprint 62.57.0 (Slice B, AC-B3, AC-B4, B-7) — the concrete supplement
   * request after a result whose effect requires one. 409
   * `DOSSIER_RESULT_SUPPLEMENT_NOT_OPEN`.
   */
  async recordResultSupplementRequest(
    filingId: string,
    body: RecordResultSupplementRequestBody,
  ): Promise<{ data?: unknown }> {
    return this.post(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/result-supplement-requests`,
      body,
    );
  }
  /**
   * Sprint 62.57.0 (Slice B, AC-B6, B-8) — the follow-up a failed result opens,
   * as an append-only revision. 409 `DOSSIER_FAILURE_FOLLOW_UP_NOT_OPEN`.
   */
  async recordFailureFollowUp(
    filingId: string,
    body: RecordFailureFollowUpBody,
  ): Promise<{ data?: unknown }> {
    return this.post(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/failure-follow-up`,
      body,
    );
  }
  /**
   * Sprint 62.57.0 (Slice B, D20) — a result date the authority itself states;
   * it wins over the provisional date. Append-only (correction like D17).
   */
  async recordAuthorityStatedResultDate(
    filingId: string,
    body: RecordAuthorityStatedResultDateBody,
  ): Promise<{ data?: unknown }> {
    return this.post(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/authority-stated-result-date`,
      body,
    );
  }
  /**
   * `POST /dossier-filings/:id/change-filing` — a revision (`{title?, reason?}`,
   * the raw new filing) or, Sprint 62.57.0 (Slice D, D35), an update
   * (`{mode: 'update', form_instance_id, sent_to, competent_authority, …}`,
   * 201 `{ data: <update filing detail> }`). 409 `DOSSIER_NOT_SUBMITTED`,
   * `DOSSIER_UPDATE_TARGET_INVALID`; 422 `DOSSIER_UPDATE_ADDRESSEE_NOT_DECLARED`,
   * `DOSSIER_UPDATE_FORM_INVALID`, `DOSSIER_UPDATE_BODY_INVALID`,
   * `DOSSIER_UPDATE_REASON_REQUIRED`.
   */
  async createChangeFiling(
    filingId: string,
    payload: ChangeFilingRevisionBody | ChangeFilingUpdateBody | Record<string, unknown>,
  ): Promise<DossierFiling & { data?: DossierFiling }> {
    return this.post(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/change-filing`,
      payload,
    );
  }
  async getDossierLineage(filingId: string): Promise<Record<string, unknown>> {
    return this.get(`/assessments/dossier-filings/${filingId}/lineage`);
  }
  async getDossierTimers(filingId: string): Promise<Record<string, unknown>> {
    return this.get(`/assessments/dossier-filings/${filingId}/timers`);
  }
  /**
   * Sprint 62.57.0 (Slice R, AC-R1) — record one written communication the
   * organisation received from an authority about this filing. Append-only:
   * a correction is a new entry naming `correction_of`. The body carries no
   * evidence — files bind afterwards under the attachments owner type
   * `dossier_authority_communication` with the communication as owner (D3).
   * `201 { data }`; 409 `DOSSIER_NOT_SUBMITTED`; 422
   * `DOSSIER_COMMUNICATION_RECEIPT_INVALID`; 400 with field errors.
   */
  async recordAuthorityCommunication(
    filingId: string,
    body: RecordDossierAuthorityCommunicationRequest,
  ): Promise<{ data: DossierAuthorityCommunication }> {
    return this.post(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/authority-communications`,
      body,
    );
  }
  /**
   * Sprint 62.57.0 (Slice R, AC-R5) — the filing's communication history,
   * newest first by receipt, one page at a time (R-23). The rows are unwrapped
   * from `data` with `total` and `page_size` kept, so a caller can page.
   */
  async listAuthorityCommunications(
    filingId: string,
    paging: { page: number; page_size: number },
  ): Promise<{
    data: DossierAuthorityCommunication[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const res = await this.get<{
      data?: DossierAuthorityCommunication[];
      total?: number;
      page?: number;
      page_size?: number;
    }>(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/authority-communications${this.buildQuery(
        { page: String(paging.page), page_size: String(paging.page_size) },
      )}`,
    );
    const data = (res?.data ?? []) as DossierAuthorityCommunication[];
    return {
      data,
      total: typeof res?.total === 'number' ? res.total : data.length,
      page: typeof res?.page === 'number' ? res.page : paging.page,
      page_size: typeof res?.page_size === 'number' ? res.page_size : paging.page_size,
    };
  }
  /**
   * Sprint 62.57.0 (Slice R, AC-R5, D5) — the filing's communication history
   * as a document, through `UnifiedExportService` (R-33), under the heading
   * *Communications recorded by the organisation*. Returns the export ref; the
   * caller downloads it through the checked platform-file route.
   */
  async exportAuthorityCommunications(
    filingId: string,
    format: 'pdf' | 'xlsx',
  ): Promise<{ data: AttachmentRef }> {
    return this.get(
      `/assessments/dossier-filings/${encodeURIComponent(filingId)}/authority-communications/export${this.buildQuery(
        { format },
      )}`,
    );
  }
  // ── Sprint 21.9.0: Compliance Forms -- Filing Case Links ──
  async linkFormToFilingCase(data: Record<string, any>) {
    return this.post('/forms/filing-case-links', data);
  }
  async listFilingCaseLinks(params?: Record<string, any>) {
    return this.get(`/forms/filing-case-links${this.buildQuery(params)}`);
  }
  async deleteFilingCaseLink(linkId: string) {
    return this.del(`/forms/filing-case-links/${linkId}`);
  }
  // Sprint 42.2.3 E2 — returns AttachmentRef.
  async exportFilingCaseLinksCSV(): Promise<AttachmentRef> {
    return this.get('/forms/filing-case-links/export/csv');
  }
  // ── Sprint 21.9.0: Compliance Forms -- Official Labels ──
  /**
   * Sprint 63.37.0, D5 — a label is display names, a date and (on an internal
   * form) a provisional disclosure. It carries no `classification`: a form's
   * standing comes from its pack, and the server refuses one with 400
   * `OFFICIAL_STANDING_FROM_PACK`, so the type does not offer it.
   */
  async setOfficialLabel(data: {
    template_id: string;
    official_label_vi: string;
    official_label_en: string;
    effective_date: string;
    provisional_disclosure?: {
      reason: ProvisionalArtefactReason;
      issuing_body?: string | null;
      prescribed_template_ref?: string | null;
    } | null;
  }): Promise<OfficialLabel> {
    return this.put('/forms/official-labels', data);
  }
  async listOfficialLabels(params?: { page?: string; page_size?: string }) {
    return this.get(`/forms/official-labels${this.buildQuery(params)}`);
  }
  // ── Sprint 63.37.0 (E3/E5): Fill from record ──
  // Fills the instance's EMPTY declared fields from its record. `refresh` only
  // reports which declared fields differ from the record now; it writes none.
  async autoPopulateFormData(instanceId: string, options: { refresh?: boolean } = {}) {
    return this.post<FormPrefillResult>(
      `/forms/${encodeURIComponent(instanceId)}/auto-populate`,
      options.refresh ? { refresh: true } : {},
    );
  }
  // ── Sprint 25.4.5.1: Template Sections for Section Routing ──
  async listTemplateSections(templateId: string) {
    return this.get<{ sections: Array<{ section_id: string; label: string }> }>(
      `/forms/templates/${templateId}/sections`,
    );
  }
  // ── Sprint 21.9.0: Compliance Forms -- Section Routing ──
  async assignFormSection(data: Record<string, any>) {
    return this.post('/forms/section-assignments', data);
  }
  async listFormSectionAssignments(instanceId: string, params?: Record<string, any>) {
    return this.get(`/forms/${instanceId}/section-assignments${this.buildQuery(params)}`);
  }
  async updateFormSectionStatus(assignmentId: string, status: string) {
    return this.patch(`/forms/section-assignments/${assignmentId}/status`, { status });
  }
  async deleteSectionAssignment(assignmentId: string) {
    return this.del(`/forms/section-assignments/${assignmentId}`);
  }
  // ── Sprint 21.9.0: Compliance Forms -- Completeness Validation ──
  async validateFormCompleteness(instanceId: string) {
    return this.get(`/forms/${instanceId}/completeness`);
  }
  // ── Sprint 21.9.0: Compliance Forms -- Submission Packages ──
  async assembleSubmissionPackage(data: Record<string, any>) {
    return this.post('/forms/submission-packages', data);
  }
  async listSubmissionPackages(params?: Record<string, any>) {
    return this.get(`/forms/submission-packages${this.buildQuery(params)}`);
  }
  async getSubmissionPackage(packageId: string) {
    return this.get(`/forms/submission-packages/${packageId}`);
  }
  async updateSubmissionPackage(packageId: string, data: Record<string, any>) {
    return this.patch(`/forms/submission-packages/${packageId}`, data);
  }
  async deleteSubmissionPackage(packageId: string) {
    return this.del(`/forms/submission-packages/${packageId}`);
  }
  // Sprint 27.0.1 — Form template changelog
  async getFormTemplateChangelog(templateId: string) {
    return this.get(`/forms/templates/${templateId}/changelog`);
  }
  // Sprint 27.1.0 — Assessment routing
  async routeFormToAssessment(instanceId: string) {
    return this.post(`/forms/instances/${instanceId}/route`, {});
  }
  // Sprint 27.0.2 — Document generation
  async generatePackageDocument(packageId: string, component: string) {
    return this.post(`/forms/submission-packages/${packageId}/generate/${component}`, {});
  }
  async listPackageDocuments(packageId: string) {
    return this.get(`/forms/submission-packages/${packageId}/documents`);
  }
  // Sprint 63.37.0 (E0) — the organisation's identity (Settings › Organisation
  // identity). `updateOrgBranding` is gone: it called
  // `PATCH /organizations/:id/branding`, a route that never existed, and no page
  // called it.
  async getOrgIdentity(orgId: string) {
    return this.get<{ identity: OrgIdentity }>(
      `/organizations/${encodeURIComponent(orgId)}/identity`,
    );
  }
  async updateOrgIdentity(orgId: string, data: UpdateOrgIdentityPayload) {
    return this.patch<{ identity: OrgIdentity }>(
      `/organizations/${encodeURIComponent(orgId)}/identity`,
      data,
    );
  }
  // ── Sprint 22.0.0: Audit Trail -- Unified Audit ──
  async queryUnifiedAudit(params?: Record<string, any>) {
    return this.get(`/audit/unified${this.buildQuery(params)}`);
  }
  async getContributorLineage(entityType: string, entityId: string) {
    return this.get(`/audit/lineage/${entityType}/${entityId}`);
  }
  async getAuditSummaryByModule(params?: Record<string, any>) {
    return this.get(`/audit/summary-by-module${this.buildQuery(params)}`);
  }
  /**
   * Sprint 63.29.0 (#585, AC6) — the WHOLE filtered Unified Audit set as a
   * server export (UnifiedExportService), in `locale`. The filters are the
   * list's; empty ones are dropped, the server refuses an unknown key.
   */
  async exportUnifiedAudit(
    filters: Record<string, string>,
    format: RenderableExportFormat,
    locale?: string,
  ): Promise<AttachmentRef> {
    const body: Record<string, string> = { format };
    for (const [k, v] of Object.entries(filters)) if (v) body[k] = v;
    const res = (await this.post(`/audit/unified/export${this.buildQuery({ locale })}`, body)) as {
      data: AttachmentRef;
    };
    return res.data;
  }
  // ── Sprint 63.25.0: Audit Trail -- versioned Evidence Packs ──
  //
  // Traced against `registerEvidencePackRoutes` (`audit/routes.ts`), mounted at
  // `/api/v1/audit`. The retired `PATCH /status` and `GET /export` answer 410
  // and have no client method: every read of a pack's events goes through
  // `versions/:version/items`, and every file through `export-file`.
  async createAuditEvidencePack(data: AuditEvidencePackCreateBody) {
    return this.post<{ data: AuditEvidencePack }>('/audit/evidence-packs', data);
  }
  async listAuditEvidencePacks(params?: { page?: number; page_size?: number; status?: string }) {
    return this.get<{
      data: AuditEvidencePack[];
      total: number;
      page: number;
      page_size: number;
    }>(`/audit/evidence-packs${this.buildQuery(params)}`);
  }
  async getAuditEvidencePack(packId: string) {
    return this.get<{ data: { pack: AuditEvidencePack; versions: AuditEvidencePackVersion[] } }>(
      `/audit/evidence-packs/${encodeURIComponent(packId)}`,
    );
  }
  async listAuditEvidencePackItems(
    packId: string,
    version: number,
    params?: { page?: number; page_size?: number },
  ) {
    return this.get<{
      data: AuditEvidencePackItem[];
      total: number;
      page: number;
      page_size: number;
      source_outcomes: AuditEvidencePackSourceOutcome[];
    }>(
      `/audit/evidence-packs/${encodeURIComponent(packId)}/versions/${version}/items${this.buildQuery(params)}`,
    );
  }
  async getAuditEvidencePackCompleteness(packId: string, version?: number) {
    return this.get<{ data: AuditEvidencePackCoverage & { version: number } }>(
      `/audit/evidence-packs/${encodeURIComponent(packId)}/completeness${this.buildQuery(
        version === undefined ? undefined : { version },
      )}`,
    );
  }
  async checkAuditEvidencePackDrift(packId: string) {
    return this.get<{ data: AuditEvidencePackDrift }>(
      `/audit/evidence-packs/${encodeURIComponent(packId)}/drift`,
    );
  }
  async reassembleAuditEvidencePack(packId: string) {
    return this.post<{ data: AuditEvidencePack }>(
      `/audit/evidence-packs/${encodeURIComponent(packId)}/reassemble`,
      {},
    );
  }
  async reviewAuditEvidencePackVersion(
    packId: string,
    version: number,
    body: { decision: 'verified' | 'returned'; note?: string },
  ) {
    return this.post<{ data: AuditEvidencePackVersion }>(
      `/audit/evidence-packs/${encodeURIComponent(packId)}/versions/${version}/review`,
      body,
    );
  }
  async recordAuditEvidencePackSubmission(
    packId: string,
    version: number,
    body: {
      recipient: string;
      sent_on: string;
      channel: string;
      reference?: string | null;
      proof_file_id?: string | null;
    },
  ) {
    return this.post<{ data: AuditEvidencePackVersion }>(
      `/audit/evidence-packs/${encodeURIComponent(packId)}/versions/${version}/submission`,
      body,
    );
  }
  // ── Sprint 22.0.0: Audit Trail -- Periods & Integrity ──
  async createAuditPeriod(data: Record<string, any>) {
    return this.post('/audit/audit-periods', data);
  }
  async listAuditPeriods(params?: Record<string, any>) {
    return this.get(`/audit/audit-periods${this.buildQuery(params)}`);
  }
  async deleteAuditPeriod(periodId: string) {
    return this.del(`/audit/audit-periods/${periodId}`);
  }
  async verifyAuditIntegrity(entityId: string) {
    return this.get(`/audit/integrity/${entityId}`);
  }

  // ── Sprint 22.1.0: Task Management Module Maturity ──────────────

  // Section-Level Assignment
  async assignTaskSection(taskId: string, data: Record<string, any>) {
    return this.post(`/tasks/${taskId}/sections`, data);
  }
  async listTaskSectionAssignments(taskId: string) {
    return this.get(`/tasks/${taskId}/sections`);
  }
  async updateTaskSectionStatus(assignmentId: string, status: string, completionPct: number) {
    return this.patch(`/tasks/sections/${assignmentId}/status`, {
      status,
      completion_pct: completionPct,
    });
  }

  // Parent-Child Task Model
  async createChildTask(masterTaskId: string, childTaskId: string, departmentId: string) {
    return this.post(`/tasks/${masterTaskId}/children`, {
      child_task_id: childTaskId,
      department_id: departmentId,
    });
  }
  async listChildTasks(masterTaskId: string) {
    return this.get(`/tasks/${masterTaskId}/children`);
  }
  async deleteChildTask(masterTaskId: string, childId: string) {
    return this.del(`/tasks/${masterTaskId}/children/${childId}`);
  }
  async getConsolidationProgress(masterTaskId: string) {
    return this.get(`/tasks/${masterTaskId}/consolidation`);
  }

  // Department-Scoped Views
  async listDepartmentTasks(departmentId: string, params?: Record<string, any>) {
    return this.get(`/tasks/department/${departmentId}${this.buildQuery(params)}`);
  }
  async getDepartmentTaskSummary(departmentId: string) {
    return this.get(`/tasks/department/${departmentId}/summary`);
  }

  // Conflict Detection & Resolution
  async detectTaskConflicts(masterTaskId: string, sectionRef: string, submissions: any[]) {
    return this.post(`/tasks/${masterTaskId}/conflicts/detect`, {
      section_ref: sectionRef,
      submissions,
    });
  }
  async listTaskConflicts(masterTaskId: string) {
    return this.get(`/tasks/${masterTaskId}/conflicts`);
  }
  async resolveTaskConflict(conflictId: string) {
    return this.patch(`/tasks/conflicts/${conflictId}/resolve`, {});
  }

  // Section-Level Revision
  async requestSectionRevision(taskId: string, sectionRef: string, data: Record<string, any>) {
    return this.post(`/tasks/${taskId}/sections/${sectionRef}/revision`, data);
  }

  // ── Sprint 22.2.0: Localization Module Maturity ──────────────

  // Framework-Module & Persona Linkage
  async linkModuleToFramework(frameworkId: string, data: Record<string, any>) {
    return this.post(`/localization/frameworks/${frameworkId}/modules`, data);
  }
  async listFrameworkModuleLinks(frameworkId: string) {
    return this.get(`/localization/frameworks/${frameworkId}/modules`);
  }
  async linkPersonaToFramework(frameworkId: string, data: Record<string, any>) {
    return this.post(`/localization/frameworks/${frameworkId}/personas`, data);
  }
  async listFrameworkPersonaLinks(frameworkId: string) {
    return this.get(`/localization/frameworks/${frameworkId}/personas`);
  }

  // Submission Channels & Deadlines
  async addSubmissionChannel(frameworkId: string, data: Record<string, any>) {
    return this.post(`/localization/frameworks/${frameworkId}/channels`, data);
  }
  async listSubmissionChannels(frameworkId: string) {
    return this.get(`/localization/frameworks/${frameworkId}/channels`);
  }
  async addRegulatoryDeadline(frameworkId: string, data: Record<string, any>) {
    return this.post(`/localization/frameworks/${frameworkId}/deadlines`, data);
  }
  /**
   * One page of a framework's deadlines; the envelope is `{ data, pagination }`.
   * Sprint 63.33.0 — a caller that needs every row passes the page bounds (the
   * route used to ignore them, so every caller got the service's first 25).
   * fix-sprint-20261025-2133 F6 — the bounds are required: an unbounded call
   * was a caller reading one default page as the whole list. Read every page
   * with `readAllPages`.
   */
  async listRegulatoryDeadlines(frameworkId: string, params: { page: string; page_size: string }) {
    const qs = new URLSearchParams({ page: params.page, page_size: params.page_size });
    return this.get(`/localization/frameworks/${frameworkId}/deadlines?${qs.toString()}`);
  }
  /**
   * Sprint 63.33.0 (#702) — the organisation's installed deadlines across every
   * framework, in one paginated read, behind the same install-mode gate and
   * activation as {@link listRegulatoryDeadlines}. Read every page with
   * `readAllPages`; `framework_codes` and `event_type` narrow the read.
   */
  async listOrganisationRegulatoryDeadlines(params: {
    page?: string;
    page_size?: string;
    framework_codes?: readonly string[];
    event_type?: string;
  }): Promise<PaginatedResponse<OrganisationRegulatoryDeadline>> {
    const qs = new URLSearchParams();
    if (params.page !== undefined) qs.set('page', params.page);
    if (params.page_size !== undefined) qs.set('page_size', params.page_size);
    if (params.framework_codes !== undefined && params.framework_codes.length > 0)
      qs.set('framework_codes', params.framework_codes.join(','));
    if (params.event_type !== undefined) qs.set('event_type', params.event_type);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/localization/deadlines${suffix}`);
  }

  // Framework Lifecycle Dashboard & Updates
  async getFrameworkCoverageDashboard() {
    return this.get('/localization/framework-dashboard');
  }
  async createRegulatoryUpdate(data: Record<string, any>) {
    return this.post('/localization/regulatory-updates', data);
  }
  async listRegulatoryUpdates(params?: Record<string, any>) {
    return this.get(`/localization/regulatory-updates${this.buildQuery(params)}`);
  }
  async assessRegulatoryUpdate(updateId: string, impactAssessment: string) {
    return this.patch(`/localization/regulatory-updates/${updateId}/assess`, {
      impact_assessment: impactAssessment,
    });
  }
  /**
   * Sprint 62.58.0 (Slice F, AC-F3, AC-F8) — what changed between the two texts
   * of a regulatory update, requirement by requirement, with the organisation's
   * reviews and the records filled under the replaced text. `change` filters the
   * rows; `page`/`page_size` page them, `records_page`/`records_page_size` the
   * re-read list and `reviews_page`/`reviews_page_size` the reviews (gate E12.5
   * pass 1, P2-3). `include_records: false` skips the re-read list (the review
   * form's paged reads, P3-10). `{ data }`; 404 `NOT_FOUND` for an unknown or
   * foreign id.
   */
  async getRegulatoryUpdateComparison(
    updateId: string,
    params?: {
      change?: string;
      page?: number;
      page_size?: number;
      records_page?: number;
      records_page_size?: number;
      reviews_page?: number;
      reviews_page_size?: number;
      include_records?: boolean;
    },
  ): Promise<{ data: RegulatoryUpdateComparisonView }> {
    const query: Record<string, string> = {};
    for (const [key, value] of Object.entries(params ?? {})) {
      if (value !== undefined && value !== '') query[key] = String(value);
    }
    return this.get(
      `/localization/regulatory-updates/${encodeURIComponent(updateId)}/comparison${this.buildQuery(query)}`,
    );
  }
  /**
   * Sprint 62.58.0 (Slice F, AC-F4) — the organisation's reading of a
   * comparison, append-only. `201 { data }`; 400 `validation_failed` with
   * `details.field_errors`; 409 `REGULATORY_UPDATE_NO_COMPARISON`.
   */
  async recordComparisonReview(
    updateId: string,
    body: CreateComparisonReviewPayload,
  ): Promise<{ data: ComparisonReview }> {
    return this.post(
      `/localization/regulatory-updates/${encodeURIComponent(updateId)}/comparison-reviews`,
      body,
    );
  }
  /**
   * Sprint 62.58.0 (Slice C, AC-C1) — the framework summary through
   * `UnifiedExportService` (R-33). Returns the export ref with its `redaction`
   * summary, which `ExportFormatMenu` reads; the caller downloads it through the
   * checked attachment route (owner type `framework_summary_export`).
   */
  async exportFrameworkSummary(
    frameworkId: string,
    format: FrameworkSummaryExportFormat,
    locale?: string,
  ): Promise<{ data: AttachmentRef & { redaction?: ExportRedactionSummary } }> {
    return this.post(
      `/localization/frameworks/${encodeURIComponent(frameworkId)}/summary-export`,
      locale ? { format, locale } : { format },
    );
  }
  /**
   * Sprint 62.58.0 (Slice C, AC-C3, AC-C4) — the framework's briefings and
   * consultation comments, one page at a time (R-23), newest first as served.
   */
  async listFrameworkBriefings(
    frameworkId: string,
    paging: { page: number; page_size: number },
  ): Promise<FrameworkBriefingPage> {
    const res = await this.get<Partial<FrameworkBriefingPage>>(
      `/localization/frameworks/${encodeURIComponent(frameworkId)}/briefings${this.buildQuery({
        page: String(paging.page),
        page_size: String(paging.page_size),
      })}`,
    );
    const data = (res?.data ?? []) as FrameworkBriefing[];
    return {
      data,
      total: typeof res?.total === 'number' ? res.total : data.length,
      page: typeof res?.page === 'number' ? res.page : paging.page,
      page_size: typeof res?.page_size === 'number' ? res.page_size : paging.page_size,
    };
  }
  /**
   * Sprint 62.58.0 (Slice C, AC-C3, AC-C5) — record a briefing or a consultation
   * comment. Append-only: a correction is a new entry naming `correction_of`.
   * `201 { data }`; 400 `validation_failed` (field errors) or
   * `FRAMEWORK_BRIEFING_APPROVER_NOT_FOUND`; 422 `FRAMEWORK_BRIEFING_HELD_ON_INVALID`.
   */
  async recordFrameworkBriefing(
    frameworkId: string,
    body: RecordFrameworkBriefingInput,
  ): Promise<{ data: FrameworkBriefing }> {
    return this.post(`/localization/frameworks/${encodeURIComponent(frameworkId)}/briefings`, body);
  }
  /**
   * Sprint 62.58.0 (Slice C, AC-C3) — the named approver's act, once. 403
   * `FRAMEWORK_BRIEFING_NOT_APPROVER`; 409 `FRAMEWORK_BRIEFING_ALREADY_APPROVED`.
   */
  async approveFrameworkBriefing(briefingId: string): Promise<{ data: FrameworkBriefing }> {
    return this.post(`/localization/briefings/${encodeURIComponent(briefingId)}/approve`, {});
  }

  // ── Sprint 25.3.5.1: Dossier section status ──────────────
  async updateDossierSectionStatus(filingId: string, sectionId: string, status: string) {
    return this.patch(`/assessments/dossier-filings/${filingId}/sections/${sectionId}/status`, {
      status,
    });
  }

  // ── Sprint 25.3.7.1: Bulk label review ────────────────────
  async bulkReviewLabels(labelIds: string[], action: 'approve' | 'reject') {
    return this.post('/classification/labels/bulk-review', { labelIds, action });
  }

  async bulkApproveLabels(labelIds: string[]) {
    return this.post('/classification/labels/bulk-approve', { label_ids: labelIds });
  }

  async bulkRejectLabels(labelIds: string[]) {
    return this.post('/classification/labels/bulk-reject', { label_ids: labelIds });
  }

  // ── Sprint 25.4.1.1: Bulk discovery operations ───────────
  async bulkCreateCycles(templateId: string, scopeSystemIds: string[], count: number) {
    return this.post('/discovery/cycles/bulk-create', {
      template_id: templateId,
      scope_system_ids: scopeSystemIds,
      count,
    });
  }
  async bulkAssignCycleScope(cycleIds: string[], scopeSystemIds: string[]) {
    return this.post('/discovery/cycles/bulk-scope', {
      cycle_ids: cycleIds,
      scope_system_ids: scopeSystemIds,
    });
  }
  async bulkAdvanceCycles(cycleIds: string[], targetStatus: string) {
    return this.post('/discovery/cycles/bulk-advance', {
      cycle_ids: cycleIds,
      target_status: targetStatus,
    });
  }

  // ── Sprint 27.9.1: Discovery Export + Downstream Flagging ─
  // Sprint 42.2.3 E2 — returns AttachmentRef (was Blob).
  async discoveryExport(
    exportType: string,
    params?: Record<string, string>,
  ): Promise<AttachmentRef> {
    return this.get(`/discovery/export/${exportType}${this.buildQuery(params)}`);
  }
  /**
   * Sprint 44.0.2 Wave 3 — R-33 list-export migration for 8 Data Discovery
   * list/dashboard pages that previously used `downloadClientExport()`.
   *
   * Each method routes the page's "export list" action through the backend
   * UnifiedExportService (csv / xlsx / pdf / docx) and returns an
   * AttachmentRef. The caller invokes `downloadPlatformFile(ref.file_id,
   * ref.filename)` to trigger the browser download. The 4 other Discovery
   * pages (Dashboard, Cycles, OwnerReview, Lineage) already route through
   * the legacy `discoveryExport()` above (Sprint 42.2.3 E2).
   */
  async exportDiscoveryClassificationRulesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/classification-rules/list-export${this.buildQuery(params)}`);
  }
  async exportDiscoveryDedupRulesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/dedup-rules/list-export${this.buildQuery(params)}`);
  }
  async exportDiscoveryImportTemplatesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/import-templates/list-export${this.buildQuery(params)}`);
  }
  async exportDiscoveryConnectorsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/connectors/export${this.buildQuery(params)}`);
  }
  async exportDiscoveryScansList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/scans/list-export${this.buildQuery(params)}`);
  }
  async exportDiscoveryStagingList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/staging/export${this.buildQuery(params)}`);
  }
  async exportDiscoverySuggestionsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/suggestions/list-export${this.buildQuery(params)}`);
  }
  async exportDiscoveryAnalyticsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/analytics/export${this.buildQuery(params)}`);
  }
  async listDiscoveryFlags(params?: Record<string, any>) {
    return this.get(`/discovery/flags${this.buildQuery(params)}`);
  }
  async acknowledgeDiscoveryFlag(flagId: string) {
    return this.post(`/discovery/flags/${flagId}/acknowledge`, {});
  }
  async resolveDiscoveryFlag(flagId: string) {
    return this.post(`/discovery/flags/${flagId}/resolve`, {});
  }
  async getDiscoveryDeltaSummary(cycleId: string) {
    return this.get(`/discovery/cycles/${cycleId}/delta-summary`);
  }
  async getRecentDeltaAlerts(params?: Record<string, any>) {
    return this.get(`/discovery/delta-alerts${this.buildQuery(params)}`);
  }

  // ── Sprint 25.4.2.1: Security scan import ─────────────────
  async importSecurityScan(
    connectorId: string,
    format: 'sarif' | 'json',
    data: Record<string, unknown>,
  ) {
    return this.post('/connectors/security-reviews/import-scan', {
      connector_id: connectorId,
      format,
      data,
    });
  }

  // ── Sprint 25.4.6.1: Evidence pack export ─────────────────
  async exportEvidencePack(packId: string, format: 'csv' | 'pdf' = 'csv') {
    return this.get(`/audit/evidence-packs/${packId}/export?format=${format}`);
  }

  /**
   * Sprint 65.4.3 — the R-33 evidence-pack export.
   *
   * `exportEvidencePack` above returns raw JSON that the page then turned into
   * a file in the browser. This one returns the `AttachmentRef` of an artefact
   * `UnifiedExportService` has already built, redacted and persisted to the
   * File Component; the caller hands it to `downloadPlatformFile`.
   */
  /**
   * fix-sprint-20260915-2128 F10 — `locale` is what makes the artefact
   * Vietnamese.
   *
   * There is no locale on the session (see `exportLocale` in
   * `data-mapping/registry-routes.ts`), so the acting locale travels as an
   * explicit query parameter and the backend honours it ahead of
   * `Accept-Language`. `RegistryPage.handleExport` sends it the same way, for
   * the reason recorded there: a Vietnamese DPO running the product in
   * Vietnamese on an English-installed browser must not get an English
   * document from the button whose whole purpose is the opposite.
   *
   * Optional so a caller that genuinely has no locale still compiles to the
   * header floor; `EvidencePacksPage` always passes one.
   */
  async exportEvidencePackFile(
    packId: string,
    // Sprint 64.45.0's shared vocabulary, not an inline union — the
    // `shared-vocabulary-census` guard flags a re-spelling of an existing one,
    // and it flagged this method's first draft.
    //
    // Sprint 63.25.0 — `zip` is the dossier (G3), and `version` names the
    // snapshot the file is built from; absent, the route reads the pack's
    // current version.
    format: RenderableExportFormat | 'zip',
    locale?: string,
    version?: number,
  ): Promise<AttachmentRef> {
    const query = `?format=${format}${locale ? `&locale=${encodeURIComponent(locale)}` : ''}${
      version === undefined ? '' : `&version=${version}`
    }`;
    const res = (await this.get(
      `/audit/evidence-packs/${encodeURIComponent(packId)}/export-file${query}`,
    )) as { data: AttachmentRef };
    return res.data;
  }

  // ── Sprint 28.3.1: Vendor Onboarding Lifecycle ──────────────
  async listOnboardingCases(params?: Record<string, any>) {
    return this.get(`/vendor-risk/onboarding-cases${this.buildQuery(params)}`);
  }
  async createOnboardingCase(payload: Record<string, unknown>) {
    return this.post('/vendor-risk/onboarding-cases', payload);
  }
  async getOnboardingCase(caseId: string) {
    return this.get(`/vendor-risk/onboarding-cases/${caseId}`);
  }
  async updateOnboardingCase(caseId: string, payload: Record<string, unknown>) {
    return this.patch(`/vendor-risk/onboarding-cases/${caseId}`, payload);
  }
  /**
   * Mark one onboarding tab complete through the gate route. `PATCH` above
   * does not persist `tab_completion` (it is not an updatable field), so the
   * gate is enforced, and `tab_gate_not_met` raised, only here.
   */
  async markOnboardingCaseTabComplete(caseId: string, tab: string) {
    return this.post(
      `/vendor-risk/onboarding-cases/${encodeURIComponent(caseId)}/tabs/${encodeURIComponent(tab)}/complete`,
      {},
    );
  }
  async transitionOnboardingCaseStatus(caseId: string, status: string) {
    return this.post(`/vendor-risk/onboarding-cases/${caseId}/status`, { status });
  }
  async listOnboardingApprovals(caseId: string) {
    return this.get(`/vendor-risk/onboarding-cases/${caseId}/approvals`);
  }
  async decideOnboardingApproval(
    caseId: string,
    approvalId: string,
    payload: Record<string, unknown>,
  ) {
    return this.post(
      `/vendor-risk/onboarding-cases/${caseId}/approvals/${approvalId}/decide`,
      payload,
    );
  }
  /**
   * Sprint 63.30.0 (#592) — reopen a REJECTED gate with a documented
   * resolution; the rejection is archived and the gate returns to undecided.
   * 409 `GATE_NOT_REJECTED` for any other gate.
   */
  async resolveOnboardingApproval(
    caseId: string,
    approvalId: string,
    payload: { resolution_notes: string },
  ) {
    return this.post(
      `/vendor-risk/onboarding-cases/${encodeURIComponent(caseId)}/approvals/${encodeURIComponent(approvalId)}/resolve`,
      payload,
    );
  }
  async exportOnboardingCase(caseId: string) {
    return this.get(`/vendor-risk/onboarding-cases/${caseId}/export`);
  }
  /**
   * Sprint 41.2.0 E8 — UC-VN-14 Step 10 MPS workflow direct integration.
   *
   * Creates a new MPS dossier filing from the vendor onboarding case
   * context and links it back onto the case. Server-side requires
   * dpia_id + title + form_type ('phu_luc_i' | 'phu_luc_ii').
   */
  async createDossierFilingFromCase(
    caseId: string,
    payload: {
      dpia_id: string;
      title: string;
      form_type: 'phu_luc_i' | 'phu_luc_ii';
    },
  ): Promise<{ filing_id: string; title: string; form_type: string; status: string }> {
    return this.post(`/vendor-risk/onboarding-cases/${caseId}/dossier-filing`, payload);
  }
  // Sprint 41.2.0 E7 — Sub-Processor Register canonical store.
  //
  // Backed by the real SubProcessorRegisterService (was a phantom
  // endpoint in Sprint 41.1.0 V7; retrofitted in 41.2.0).
  async listSubProcessorRegister(params?: Record<string, string>): Promise<{
    data: SubProcessorRegisterRecord[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/vendor-risk/sub-processor-register${this.buildQuery(params)}`);
  }
  async createSubProcessorRegister(
    payload: Record<string, unknown>,
  ): Promise<SubProcessorRegisterRecord> {
    return this.post('/vendor-risk/sub-processor-register', payload);
  }
  async updateSubProcessorRegister(
    subProcessorId: string,
    payload: Record<string, unknown>,
  ): Promise<SubProcessorRegisterRecord> {
    return this.patch(`/vendor-risk/sub-processor-register/${subProcessorId}`, payload);
  }
  // fix-sprint-20260411-A F9 — backend now returns a paginated
  // envelope; caller can override page/page_size when needed.
  async getSubProcessorHistory(
    subProcessorId: string,
    params: { page?: number; page_size?: number } = {},
  ): Promise<{
    data: SubProcessorRegisterChange[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const search = new URLSearchParams();
    if (params.page) search.set('page', String(params.page));
    if (params.page_size) search.set('page_size', String(params.page_size));
    const q = search.toString();
    return this.get(
      `/vendor-risk/sub-processor-register/${subProcessorId}/history${q ? `?${q}` : ''}`,
    );
  }
  /**
   * Sprint 41.2.0 E7 — CSV export performed client-side from the list
   * response. No backend export endpoint needed; keeps scope tight and
   * the caller controls the exact column layout.
   */
  exportSubProcessorsCsv(rows: SubProcessorRegisterRecord[]): string {
    if (rows.length === 0) return '';
    const headers = [
      'sub_processor_id',
      'name',
      'services',
      'data_categories',
      'processing_countries',
      'dpa_chain_status',
      'certifications',
      'last_assessment_date',
      'next_assessment_date',
      'onboarding_case_id',
      'created_at',
      'updated_at',
    ];
    const lines = [headers.join(',')];
    for (const r of rows) {
      const cells = headers.map((h) => {
        const v = (r as unknown as Record<string, unknown>)[h];
        if (Array.isArray(v)) return JSON.stringify(v.join('; '));
        return JSON.stringify(v ?? '');
      });
      lines.push(cells.join(','));
    }
    return lines.join('\n');
  }
  // ── Sprint 28.7.1: Multi-Department Report Consolidation ────

  async createConsolidationCase(payload: Record<string, unknown>): Promise<any> {
    return this.post('/consolidation', payload);
  }
  async listConsolidationCases(params?: Record<string, string>): Promise<any> {
    return this.get(`/consolidation${this.buildQuery(params)}`);
  }
  async getConsolidationCase(caseId: string): Promise<any> {
    return this.get(`/consolidation/${caseId}`);
  }
  async updateConsolidationCase(caseId: string, payload: Record<string, unknown>): Promise<any> {
    return this.patch(`/consolidation/${caseId}`, payload);
  }
  async deleteConsolidationCase(caseId: string): Promise<void> {
    return this.del(`/consolidation/${caseId}`);
  }
  async transitionConsolidationCase(caseId: string, status: string): Promise<any> {
    return this.post(`/consolidation/${caseId}/transition`, { status });
  }
  async closeConsolidationCase(caseId: string, dispositionNote: string): Promise<any> {
    return this.post(`/consolidation/${caseId}/close`, { disposition_note: dispositionNote });
  }
  async assignConsolidationSection(caseId: string, payload: Record<string, unknown>): Promise<any> {
    return this.post(`/consolidation/${caseId}/sections`, payload);
  }
  async autoAssignConsolidationSections(caseId: string): Promise<any> {
    return this.post(`/consolidation/${caseId}/sections/auto-assign`, {});
  }
  async listConsolidationSections(caseId: string, mySections?: boolean): Promise<any> {
    const params: Record<string, string> = {};
    if (mySections) params.my_sections = 'true';
    return this.get(`/consolidation/${caseId}/sections${this.buildQuery(params)}`);
  }
  async getConsolidationSection(sectionId: string): Promise<any> {
    return this.get(`/consolidation/sections/${sectionId}`);
  }
  async submitConsolidationSection(
    sectionId: string,
    payload: Record<string, unknown>,
  ): Promise<any> {
    return this.post(`/consolidation/sections/${sectionId}/submit`, payload);
  }
  async resubmitConsolidationSection(
    sectionId: string,
    payload: Record<string, unknown>,
  ): Promise<any> {
    return this.post(`/consolidation/sections/${sectionId}/resubmit`, payload);
  }
  async saveSectionProgress(sectionId: string, data: Record<string, unknown>): Promise<any> {
    return this.patch(`/consolidation/sections/${sectionId}/progress`, { data });
  }
  async acceptConsolidationSection(sectionId: string): Promise<any> {
    return this.post(`/consolidation/sections/${sectionId}/accept`, {});
  }
  async returnConsolidationSection(sectionId: string, comment: string): Promise<any> {
    return this.post(`/consolidation/sections/${sectionId}/return`, { comment });
  }
  async addConsolidationComment(
    sectionId: string,
    text: string,
    parentCommentId?: string,
  ): Promise<any> {
    return this.post(`/consolidation/sections/${sectionId}/comments`, {
      text,
      parent_comment_id: parentCommentId,
    });
  }
  async addConsolidationEvidence(
    sectionId: string,
    evidence: Record<string, unknown>,
  ): Promise<any> {
    return this.post(`/consolidation/sections/${sectionId}/evidence`, evidence);
  }
  async consolidateCase(caseId: string): Promise<any> {
    return this.post(`/consolidation/${caseId}/consolidate`, {});
  }
  async listConsolidationFlags(caseId: string, status?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    return this.get(`/consolidation/${caseId}/flags${this.buildQuery(params)}`);
  }
  async resolveConsolidationFlag(
    flagId: string,
    action: 'resolve' | 'dismiss',
    notes: string,
  ): Promise<any> {
    return this.post(`/consolidation/flags/${flagId}/resolve`, { action, notes });
  }
  async decideConsolidationApproval(
    caseId: string,
    stepId: string,
    decision: string,
    notes?: string,
  ): Promise<any> {
    return this.post(`/consolidation/${caseId}/approvals/${stepId}/decide`, { decision, notes });
  }
  async generateConsolidationExport(caseId: string): Promise<any> {
    return this.post(`/consolidation/${caseId}/export`, {});
  }
  async getConsolidationExportArtifact(caseId: string, artifactType: string): Promise<any> {
    return this.get(`/consolidation/${caseId}/export/${artifactType}`);
  }
  async getConsolidationFormWizard(caseId: string): Promise<any> {
    return this.get(`/consolidation/${caseId}/form-wizard`);
  }
  async getConsolidationAudit(caseId: string): Promise<any> {
    return this.get(`/consolidation/${caseId}/audit`);
  }

  // ── Sprint 28.6.1: Connector artifact export ────────────────
  // Sprint 44.0.3 Waves 2+3 — R-33 migration. The route now renders
  // through UnifiedExportService and returns an AttachmentRef. Callers
  // must follow with `downloadPlatformFile(ref.file_id, ref.filename)`
  // to trigger the download. Previously the route returned raw JSON
  // and callers discarded the response (no download ever happened).
  async exportConnectorArtifact(
    type: string,
    format: 'csv' | 'xlsx' | 'pdf' | 'docx' = 'csv',
  ): Promise<any> {
    return this.get(
      `/connectors/export/${encodeURIComponent(type)}?format=${encodeURIComponent(format)}`,
    );
  }

  // ── Sprint 28.6.1: Automation Planning CRUD ──────────────────
  // Backend route + DB collection still use the "planning-programs" name;
  // the user-facing "automation" rename only landed on the page, the
  // method names, and the export-artifact alias.
  async listAutomationPrograms(params?: Record<string, any>) {
    return this.get(`/connectors/planning-programs${this.buildQuery(params)}`);
  }
  async getAutomationProgram(id: string) {
    return this.get(`/connectors/planning-programs/${id}`);
  }
  async createAutomationProgram(payload: Record<string, any>) {
    return this.post('/connectors/planning-programs', payload);
  }
  async updateAutomationProgram(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/planning-programs/${id}`, payload);
  }
  async updateAutomationProgramStatus(id: string, status: string) {
    return this.patch(`/connectors/planning-programs/${id}/status`, { status });
  }

  // ── Sprint 28.6.1: System Inventory CRUD ─────────────────────
  async listSystemInventory(params?: Record<string, any>) {
    return this.get(`/connectors/system-inventory${this.buildQuery(params)}`);
  }
  async getSystemInventoryItem(id: string) {
    return this.get(`/connectors/system-inventory/${id}`);
  }
  async createSystemInventoryItem(payload: Record<string, any>) {
    return this.post('/connectors/system-inventory', payload);
  }
  async updateSystemInventoryItem(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/system-inventory/${id}`, payload);
  }

  // ── Sprint 28.6.1: Traceability Matrix CRUD ──────────────────
  async listTraceabilityEntries(params?: Record<string, any>) {
    return this.get(`/connectors/traceability-matrix${this.buildQuery(params)}`);
  }
  async getTraceabilityEntry(id: string) {
    return this.get(`/connectors/traceability-matrix/${id}`);
  }
  async createTraceabilityEntry(payload: Record<string, any>) {
    return this.post('/connectors/traceability-matrix', payload);
  }
  async updateTraceabilityEntry(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/traceability-matrix/${id}`, payload);
  }

  // ── Sprint 40.1.0: Assessment Trigger Config ─────────────────
  async getAssessmentTriggerConfig(): Promise<AssessmentTriggerConfig> {
    return this.get('/assessments/trigger-config');
  }
  async updateAssessmentTriggerConfig(
    config: Partial<AssessmentTriggerConfig>,
  ): Promise<AssessmentTriggerConfig> {
    return this.put('/assessments/trigger-config', config);
  }

  // ── Sprint 28.6.1: Gap Register CRUD ─────────────────────────
  async listGapRegisterEntries(params?: Record<string, any>) {
    return this.get(`/connectors/gap-register${this.buildQuery(params)}`);
  }
  async getGapRegisterEntry(id: string) {
    return this.get(`/connectors/gap-register/${id}`);
  }
  async createGapRegisterEntry(payload: Record<string, any>) {
    return this.post('/connectors/gap-register', payload);
  }
  async updateGapRegisterEntry(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/gap-register/${id}`, payload);
  }

  // ── Sprint 40.2.0: Data Subject Profiles ────────────────────
  async listDataSubjects(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<DataSubjectProfile>> {
    return this.get(`/data-subjects${this.buildQuery(params)}`);
  }
  async createDataSubject(payload: Record<string, unknown>): Promise<DataSubjectProfile> {
    return this.post('/data-subjects', payload);
  }
  async getDataSubject(profileId: string): Promise<any> {
    return this.get(`/data-subjects/${profileId}`);
  }
  /**
   * Sprint 63.31.0 (#415, owner ruling Q3 (a)) — the external identifier fields
   * a profile offers: each key an installed record kind declares, with its
   * label. Registered before `/:profileId` on the server. `{ data: [...] }`.
   */
  async getDataSubjectExternalIdentifierKeys(): Promise<{
    data: DataSubjectIdentifierKeyDeclaration[];
  }> {
    return this.get('/data-subjects/external-identifier-keys');
  }
  async updateDataSubject(profileId: string, payload: Record<string, unknown>): Promise<any> {
    return this.patch(`/data-subjects/${profileId}`, payload);
  }
  async deleteDataSubject(profileId: string): Promise<void> {
    return this.del(`/data-subjects/${profileId}`);
  }
  async linkCaseToProfile(profileId: string, caseId: string): Promise<any> {
    return this.post(`/data-subjects/${profileId}/link-case`, { case_id: caseId });
  }
  async linkWorkflowToProfile(profileId: string, workflowId: string): Promise<any> {
    return this.post(`/data-subjects/${profileId}/link-workflow`, { workflow_id: workflowId });
  }
  async getDataSubjectTimeline(profileId: string): Promise<DataSubjectTimelineEntry[]> {
    const res: any = await this.get(`/data-subjects/${profileId}/timeline`);
    return res.data ?? res;
  }
  // Sprint 43.6.0 — Web consent records for a data subject profile
  async listDataSubjectWebConsentRecords(
    profileId: string,
    params?: Record<string, string>,
  ): Promise<{ data: any[]; total: number; page: number; page_size: number }> {
    return this.get(`/data-subjects/${profileId}/web-consent-records${this.buildQuery(params)}`);
  }

  // ── Sprint 41.6.0 V4 — create onboarding approval ──────────────
  async createOnboardingApproval(
    caseId: string,
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.post(`/vendor-risk/onboarding-cases/${caseId}/approvals`, payload);
  }

  // ── Sprint 41.6.0 V6.7 — evidence bundle export ────────────────
  async exportVendorEvidenceBundle(caseId: string): Promise<AttachmentRef> {
    return this.post(`/vendor-risk/onboarding-cases/${caseId}/export/evidence-bundle`);
  }

  // ── Sprint 43.1.0 batch 3 E6 — Rights Request export + auto-response ───
  // The legacy `exportCaseEvidencePack` (Sprint 41.6.0 V7.3) pointed at
  // `/cases/:id/export/evidence-pack` which produced a 7-field stub. The
  // assembly batch (43.1.0) splits this into two artefacts:
  //   - Case File PDF       (POST /export/pdf)
  //   - Phu luc VI ZIP pack (POST /export/zip)
  // and adds the auto-generate + cross-border endpoints below.
  async exportCasePdf(casePublicId: string): Promise<AttachmentRef> {
    const r = await this.post<{ data: AttachmentRef }>(
      `/rights-requests/cases/${casePublicId}/export/pdf`,
    );
    return r.data;
  }

  async exportCaseZip(casePublicId: string): Promise<AttachmentRef> {
    const r = await this.post<{ data: AttachmentRef }>(
      `/rights-requests/cases/${casePublicId}/export/zip`,
    );
    return r.data;
  }

  async autoGenerateResponse(
    casePublicId: string,
    opts: {
      template_type: string;
      locale: 'vi' | 'en';
      method: 'email' | 'portal' | 'postal' | 'in_person';
      recipient: { email?: string; portal_user_id?: string; postal_address?: string };
      additional_attachments?: Array<{ file_id: string; filename: string }>;
    },
  ): Promise<{
    subject: string;
    body: string;
    delivered_at: string;
    response_ref: AttachmentRef;
    delivery_evidence_ref?: AttachmentRef;
    placeholders_missing: string[];
    effective_method: 'email' | 'portal' | 'postal' | 'in_person';
  }> {
    const r = await this.post<{ data: any }>(
      `/rights-requests/cases/${casePublicId}/responses/auto-generate`,
      opts,
    );
    return r.data;
  }

  /**
   * fix-sprint #431 (owner ruling 2026-10-03) — the case's cross-border state,
   * READ only: the rule's assessment and the dossier already recorded, if any.
   * Opens nothing; the case page calls it on load.
   */
  async getCrossBorderStatus(casePublicId: string): Promise<CrossBorderCaseStatus> {
    const r = await this.get<{ data: CrossBorderCaseStatus }>(
      `/rights-requests/cases/${casePublicId}/cross-border-check`,
    );
    return r.data;
  }

  /**
   * fix-sprint #431 — a person opens the cross-border dossier for a case, and
   * the statutory filing where an installed form declares one. Requires
   * `gov:crossborder:filing:write`. Never called on load.
   */
  async openCrossBorderDossier(casePublicId: string): Promise<CrossBorderDossierOpenResult> {
    const r = await this.post<{ data: CrossBorderDossierOpenResult }>(
      `/rights-requests/cases/${casePublicId}/cross-border-dossier`,
    );
    return r.data;
  }

  /**
   * @deprecated Use `exportCasePdf` (richer 13-section PDF) and
   * `exportCaseZip` (Phu luc VI evidence pack) instead. Retained as a
   * thin alias so legacy call sites keep working until batch 4 rewires
   * `TicketDetailPage.handleExportPDF` / `handleExportZIP`.
   */
  async exportCaseEvidencePack(casePublicId: string): Promise<AttachmentRef> {
    return this.exportCasePdf(casePublicId);
  }

  // ── Sprint 41.6.0 V8.4 — incident report PDF export ────────────
  async exportIncidentReport(incidentId: string): Promise<AttachmentRef> {
    return this.post(`/incidents/${incidentId}/export/pdf`);
  }

  // ── Sprint 43.2.0 batch 4 (E5) — Breach-trigger actions ─────────
  //
  // `flagCaseAsBreach` flags a DSR case as breach-causing and starts
  // whichever notification clock the organisation's installed content
  // declares — the earliest, where several duties run against the one
  // discovery. Permission-gated at BREACH_FLAG (DPO tier).
  //
  // fix-sprint-20260907-2133 — this said "the 72h MPS filing clock (Mau so 08
  // / vn-mops-filing-rules.md Rule 6)", which had already stopped being true:
  // Sprint 65.4.0 resolved the form from `serves_capability`, 65.9.0 moved the
  // period into installed content, and this fix removed the last two literals
  // (the filing's `jurisdiction` and its note). A comment that names a period
  // and an authority the code no longer chooses is the same defect as the
  // banner F2 corrected, one layer down.
  //
  // `listCaseBreaches` drives the red banner at the top of
  // TicketDetailPage — non-empty list means the case has been flagged.
  async flagCaseAsBreach(
    casePublicId: string,
    breachType: 'misdirected_response' | 'unauthorised_disclosure' | 'identity_mismatch' | 'other',
    description?: string,
  ): Promise<{ breach_id: string; filing_deadline: string; filing_id?: string }> {
    const r = await this.post<{
      data: { breach_id: string; filing_deadline: string; filing_id?: string };
    }>(`/rights-requests/cases/${casePublicId}/flag-breach`, {
      breach_type: breachType,
      ...(description ? { description } : {}),
    });
    return r.data;
  }

  async listCaseBreaches(casePublicId: string): Promise<DataBreach[]> {
    const r = await this.get<{ data: DataBreach[] }>(
      `/rights-requests/cases/${casePublicId}/breaches`,
    );
    return r.data ?? [];
  }

  // ── Sprint 47.5.1 — Forseti Provider + Token Billing ─────────────
  // Family plan §6.2; engineering: docs/engineering/ai-legal-advisor.md §3+§4
  async forsetiProviderHealth(): Promise<{
    ok: boolean;
    provider?: string;
    last_checked?: string;
    reason?: string;
    help_url?: string;
  }> {
    return this.post('/llm-relay/health', {});
  }

  async forsetiListUsage(params?: {
    feature?: string;
    user_id?: string;
    from_date?: string;
    to_date?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: unknown[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.feature) qs.set('feature', params.feature);
    if (params?.user_id) qs.set('user_id', params.user_id);
    if (params?.from_date) qs.set('from_date', params.from_date);
    if (params?.to_date) qs.set('to_date', params.to_date);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.get(`/llm-relay/usage${suffix}`);
  }

  async forsetiGetQuota(): Promise<{
    organization_id: string;
    period: string;
    bucket_total: number;
    bucket_consumed: number;
    bucket_remaining: number;
    last_reconciled_at: string;
    bundle_history: unknown[];
  }> {
    return this.get('/llm-relay/quota');
  }

  async forsetiQuotaHistory(): Promise<{ data: unknown[] }> {
    return this.get('/llm-relay/quota/history');
  }

  // ── Forseti → AI Model (owner ruling 2026-10-10) ────────────────
  // Each organisation configures its own provider (Anthropic, OpenAI or an
  // OpenAI-compatible endpoint), key, model and effort. The prepaid token
  // bundles are gone: the purchase-intent, redeem and top-up routes were
  // removed with them, and Forseti → Usage reports consumption instead.
  async forsetiGetAiModelSettings(): Promise<import('@vn-grc/shared').AiModelSettingsView> {
    return this.get('/llm-relay/settings');
  }

  async forsetiSaveAiModelGeneral(
    body: import('@vn-grc/shared').AiModelGeneralSaveRequest,
  ): Promise<import('@vn-grc/shared').AiModelSettingsView> {
    return this.put('/llm-relay/settings', body);
  }

  /** A blank or omitted `api_key` keeps the saved key. */
  async forsetiSaveAiProvider(
    provider: import('@vn-grc/shared').AiConfigurableProvider,
    body: import('@vn-grc/shared').AiProviderSaveRequest,
  ): Promise<import('@vn-grc/shared').AiModelSettingsView> {
    return this.put(`/llm-relay/settings/providers/${encodeURIComponent(provider)}`, body);
  }

  async forsetiClearAiProviderKey(
    provider: import('@vn-grc/shared').AiConfigurableProvider,
  ): Promise<import('@vn-grc/shared').AiModelSettingsView> {
    return this.del(`/llm-relay/settings/providers/${encodeURIComponent(provider)}/key`);
  }

  /** Tests the provider's SAVED settings with one bounded request. */
  async forsetiTestAiProvider(provider: import('@vn-grc/shared').AiConfigurableProvider): Promise<{
    result: import('@vn-grc/shared').AiProviderTestResult;
    settings: import('@vn-grc/shared').AiModelSettingsView;
  }> {
    return this.post(`/llm-relay/settings/providers/${encodeURIComponent(provider)}/test`, {});
  }

  async forsetiGetRouting(): Promise<{
    active_provider: import('@vn-grc/shared').AiActiveProvider;
    routes: Record<import('@vn-grc/shared').LlmFeature, import('@vn-grc/shared').AiRouting>;
  }> {
    return this.get('/llm-relay/routing');
  }

  async forsetiUsageSummary(params: { from_date: string; to_date: string }): Promise<{
    from_date: string;
    to_date: string;
    totals: {
      calls: number;
      input_tokens: number;
      output_tokens: number;
      cached_input_tokens: number;
      billable_units: number;
    };
    groups: Array<{
      provider: string;
      model: string;
      effort: string;
      feature: string;
      calls: number;
      input_tokens: number;
      output_tokens: number;
      cached_input_tokens: number;
      billable_units: number;
    }>;
  }> {
    const qs = new URLSearchParams({ from_date: params.from_date, to_date: params.to_date });
    return this.get(`/llm-relay/usage/summary?${qs}`);
  }

  // Sprint 47.5.8 — Advisor audit lineage (cross-module Forseti graph)
  async getAdvisorLineage(params: LineageQuery): Promise<{
    data: import('@vn-grc/shared').LineageGraph;
    total_nodes: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    qs.set('record_type', params.record_type);
    qs.set('record_id', params.record_id);
    if (params.date_start) qs.set('date_start', params.date_start);
    if (params.date_end) qs.set('date_end', params.date_end);
    if (params.actor_user_id) qs.set('actor_user_id', params.actor_user_id);
    if (params.page) qs.set('page', String(params.page));
    if (params.page_size) qs.set('page_size', String(params.page_size));
    return this.get(`/audit/advisor-lineage?${qs}`);
  }

  async exportAdvisorLineage(
    /** Sprint 47.9.1 — XLSX added alongside PDF/CSV. */
    params: LineageQuery & { format: 'pdf' | 'csv' | 'xlsx' },
  ): Promise<{ data: import('@vn-grc/shared').AttachmentRef }> {
    const qs = new URLSearchParams();
    qs.set('record_type', params.record_type);
    qs.set('record_id', params.record_id);
    qs.set('format', params.format);
    // The export holds what the page shows: the same date range and actor.
    if (params.date_start) qs.set('date_start', params.date_start);
    if (params.date_end) qs.set('date_end', params.date_end);
    if (params.actor_user_id) qs.set('actor_user_id', params.actor_user_id);
    return this.get(`/audit/advisor-lineage/export?${qs}`);
  }

  /**
   * Sprint 47.5.8.6 — list AI-generated evidence in actionable states.
   * Defaults to draft_ai_generated + pending_review.
   */
  async listEvidenceDrafts(params?: {
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<{ data: unknown[]; total: number; page: number; page_size: number }> {
    return this.get(`/evidence/drafts${this.buildQuery(params ?? {})}`);
  }

  async evidenceMoveToReview(evidenceId: string): Promise<{ data: unknown }> {
    return this.post(`/evidence/${evidenceId}/move-to-review`);
  }

  async evidenceApprove(evidenceId: string): Promise<{ data: unknown }> {
    return this.post(`/evidence/${evidenceId}/approve`);
  }

  async evidenceReject(evidenceId: string, reason: string): Promise<{ data: unknown }> {
    return this.post(`/evidence/${evidenceId}/reject`, { reason });
  }

  async forsetiQuotaOverride(targetId: string, reason: string): Promise<{ ok: boolean }> {
    return this.post('/llm-relay/quota/override', { target_id: targetId, reason });
  }

  async forsetiRunCostEstimate(
    feature: 'chat' | 'workflow' | 'tabular' | 'redline',
    targetId: string,
    inputCount: number,
  ): Promise<{
    target_id: string;
    estimated_billable_units: number;
    confidence: 'low' | 'medium' | 'high';
    input_count: number;
  }> {
    const qs = new URLSearchParams({
      feature,
      target_id: targetId,
      input_count: String(inputCount),
    });
    return this.get(`/llm-relay/run-cost-estimate?${qs}`);
  }

  // ── Sprint 47.9.7 — Forseti MCP connector (Phase 1, read-only) ───
  async mcpKeysList(): Promise<{ data: McpApiKeyListItem[]; total: number }> {
    return this.get('/mcp-connector/api-keys');
  }
  async mcpKeysIssue(payload: McpApiKeyIssueRequest): Promise<McpApiKeyIssueResponse> {
    return this.post('/mcp-connector/api-keys', payload);
  }
  async mcpKeysRevoke(keyId: string): Promise<void> {
    return this.del(`/mcp-connector/api-keys/${keyId}`);
  }
  async mcpKeysRotate(keyId: string): Promise<McpApiKeyRotateResponse> {
    return this.post(`/mcp-connector/api-keys/${keyId}/rotate`, {});
  }

  // ── Sprint 63.23.0 — MCP remote server: connection, OAuth clients, grants,
  // consent. Routes are the C.1 contract (work file); every response is the
  // `{ data }` envelope, unwrapped here so callers never read the wrapper.
  // Lists are paginated `{ data, total, page, page_size }` (R-23) and are
  // returned whole with `data` normalised to an array.
  async mcpConnectionGet(): Promise<McpConnectionDetails> {
    return envelopeData<McpConnectionDetails>(await this.get('/mcp-connector/connection'));
  }
  async mcpSettingsUpdate(body: { dcr_enabled: boolean }): Promise<{ dcr_enabled: boolean }> {
    return envelopeData<{ dcr_enabled: boolean }>(
      await this.patch('/mcp-connector/settings', body),
    );
  }
  async mcpOAuthClientsList(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<OAuthClientView>> {
    const res = await this.get<Partial<PaginatedResponse<OAuthClientView>>>(
      `/mcp-connector/oauth-clients${this.buildQuery(pageQuery(params))}`,
    );
    return normalisePage(res);
  }
  async mcpOAuthClientsCreate(
    body: McpOAuthClientCreateRequest,
  ): Promise<McpOAuthClientCreateResponse> {
    return envelopeData<McpOAuthClientCreateResponse>(
      await this.post('/mcp-connector/oauth-clients', body),
    );
  }
  async mcpOAuthClientsRotateSecret(id: string): Promise<{ client_secret: string }> {
    return envelopeData<{ client_secret: string }>(
      await this.post(`/mcp-connector/oauth-clients/${encodeURIComponent(id)}/rotate-secret`, {}),
    );
  }
  async mcpOAuthClientsRevoke(id: string): Promise<{ revoked: boolean }> {
    return envelopeData<{ revoked: boolean }>(
      await this.del(`/mcp-connector/oauth-clients/${encodeURIComponent(id)}`),
    );
  }
  async mcpGrantsList(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<GrantView>> {
    const res = await this.get<Partial<PaginatedResponse<GrantView>>>(
      `/mcp-connector/grants${this.buildQuery(pageQuery(params))}`,
    );
    return normalisePage(res);
  }
  async mcpGrantsMine(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<GrantView>> {
    return normalisePage(
      await this.get<Partial<PaginatedResponse<GrantView>>>(
        `/mcp-connector/grants/mine${this.buildQuery(pageQuery(params))}`,
      ),
    );
  }
  async mcpGrantsRevoke(id: string): Promise<{ revoked: boolean }> {
    return envelopeData<{ revoked: boolean }>(
      await this.del(`/mcp-connector/grants/${encodeURIComponent(id)}`),
    );
  }
  async mcpConsentGet(requestId: string): Promise<McpOAuthConsentDetails> {
    return envelopeData<McpOAuthConsentDetails>(
      await this.get(`/mcp-oauth/consent/${encodeURIComponent(requestId)}`),
    );
  }
  async mcpConsentDecide(
    requestId: string,
    decision: McpOAuthConsentDecision,
  ): Promise<{ redirect_to: string }> {
    return envelopeData<{ redirect_to: string }>(
      await this.post(`/mcp-oauth/consent/${encodeURIComponent(requestId)}`, { decision }),
    );
  }

  // ── Sprint 47.5.2 — Forseti Projects ─────────────────────────────
  // Family plan §3.1 + §6.3; engineering: docs/engineering/ai-legal-advisor.md §8
  async forsetiListProjects(params?: {
    shared_with_me?: boolean;
    owner_user_id?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: Array<import('@vn-grc/shared').Project>;
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.shared_with_me) qs.set('shared_with_me', '1');
    if (params?.owner_user_id) qs.set('owner_user_id', params.owner_user_id);
    if (params?.q) qs.set('q', params.q);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.get(`/projects${suffix}`);
  }

  async forsetiCreateProject(body: {
    name: string;
    description?: string;
    visibility?: import('@vn-grc/shared').ProjectVisibility;
    cm_number?: string;
  }): Promise<import('@vn-grc/shared').Project> {
    return this.post('/projects', body);
  }

  async forsetiGetProject(id: string): Promise<{
    project: import('@vn-grc/shared').Project;
    folders: Array<import('@vn-grc/shared').ProjectFolder>;
  }> {
    return this.get(`/projects/${id}`);
  }

  async forsetiUpdateProject(
    id: string,
    body: {
      name?: string;
      description?: string;
      visibility?: import('@vn-grc/shared').ProjectVisibility;
      cm_number?: string;
    },
  ): Promise<import('@vn-grc/shared').Project> {
    return this.patch(`/projects/${id}`, body);
  }

  async forsetiDeleteProject(id: string): Promise<void> {
    await this.del(`/projects/${id}`);
  }

  async forsetiCreateFolder(
    projectId: string,
    body: { name: string; parent_folder_id?: string },
  ): Promise<import('@vn-grc/shared').ProjectFolder> {
    return this.post(`/projects/${projectId}/folders`, body);
  }

  async forsetiUpdateFolder(
    projectId: string,
    folderId: string,
    body: { name?: string; parent_folder_id?: string | null },
  ): Promise<import('@vn-grc/shared').ProjectFolder> {
    return this.patch(`/projects/${projectId}/folders/${folderId}`, body);
  }

  async forsetiDeleteFolder(projectId: string, folderId: string): Promise<void> {
    await this.del(`/projects/${projectId}/folders/${folderId}`);
  }

  async forsetiListProjectDocuments(
    projectId: string,
    folderId?: string,
  ): Promise<{
    data: Array<import('@vn-grc/shared').ProjectDocumentMembership>;
    total: number;
    page: number;
    page_size: number;
  }> {
    const suffix = folderId !== undefined ? `?folder_id=${encodeURIComponent(folderId)}` : '';
    return this.get(`/projects/${projectId}/documents${suffix}`);
  }

  async forsetiAttachDocument(
    projectId: string,
    body: { attachment_id: string; folder_id?: string; filename_cached: string },
  ): Promise<import('@vn-grc/shared').ProjectDocumentMembership> {
    return this.post(`/projects/${projectId}/documents`, body);
  }

  async forsetiUnattachDocument(projectId: string, attachmentId: string): Promise<void> {
    await this.del(`/projects/${projectId}/documents/${attachmentId}`);
  }

  async forsetiListProjectMembers(projectId: string): Promise<{
    data: Array<import('@vn-grc/shared').ProjectMember>;
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/projects/${projectId}/members`);
  }

  async forsetiAddProjectMember(
    projectId: string,
    body: {
      user_id?: string;
      org_group_id?: string;
      role: 'admin' | 'editor' | 'viewer';
    },
  ): Promise<import('@vn-grc/shared').ProjectMember> {
    return this.post(`/projects/${projectId}/members`, body);
  }

  async forsetiRemoveProjectMember(projectId: string, userId: string): Promise<void> {
    await this.del(`/projects/${projectId}/members/${userId}`);
  }

  // ── Sprint 47.5.3a — Forseti Document Review ─────────────────────
  // Family plan §6.4; engineering: docs/engineering/ai-legal-advisor.md §5
  // Sprint 47.8.0a — index of documents under Forseti review.
  async forsetiListDocumentsUnderReview(params?: { page?: number; page_size?: number }): Promise<{
    data: Array<{
      attachment_id: string;
      display_name: string;
      filename?: string;
      linked_record_type: string | null;
      linked_record_id: string | null;
      /**
       * Sprint 47.5.9.3.3 (T-3) — backend-resolved display label
       * (DPIA name, etc.). Optional — falls back to type · id.
       */
      linked_record_label?: string;
      pending_edits_count: number;
      last_activity_at: string;
    }>;
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.get(`/document-review/documents${suffix}`);
  }

  async forsetiListDocumentVersions(
    attachmentId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<{
    data: Array<import('@vn-grc/shared').DocumentVersion>;
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.get(`/document-review/${attachmentId}/versions${suffix}`);
  }

  async forsetiCreateDocumentVersion(
    attachmentId: string,
    body: {
      // Sprint 47.9.3 — text_body is optional for the PDF / DOCX upload
      // path. When omitted, the backend extracts text from the
      // attachment bytes via PdfTextExtractor / extractDocxText. For
      // plaintext substrates (.txt / .md), the frontend continues to
      // pre-compute and submit text_body.
      text_body?: string;
      source?: import('@vn-grc/shared').DocumentVersionSource;
      display_name?: string;
      chat_message_id?: string;
    },
  ): Promise<import('@vn-grc/shared').DocumentVersion> {
    // 47.9.3 PDF/DOCX path: the backend extracts text synchronously from the
    // attachment bytes before responding, so allow far more than the default
    // 30s request timeout. Without this, a legitimate multi-MB PDF aborts in
    // the browser with "signal timed out" while extraction is still running.
    return this.post(`/document-review/${attachmentId}/versions`, body, true, undefined, 120_000);
  }

  async forsetiPromoteDocumentVersion(
    attachmentId: string,
    versionId: string,
  ): Promise<{ ok: true }> {
    return this.post(`/document-review/${attachmentId}/versions/${versionId}/promote`, {});
  }

  async forsetiListDocumentEdits(
    attachmentId: string,
    params?: {
      status?: 'pending' | 'accepted' | 'rejected';
      page?: number;
      page_size?: number;
    },
  ): Promise<{
    data: Array<import('@vn-grc/shared').DocumentEdit>;
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.get(`/document-review/${attachmentId}/edits${suffix}`);
  }

  async forsetiProposeEdit(body: {
    attachment_id: string;
    version_id: string;
    change_id: string;
    deleted_text: string;
    inserted_text: string;
    context_before?: string;
    context_after?: string;
    chat_message_id?: string;
  }): Promise<{ edit_id: string }> {
    return this.post('/document-review/edits', body);
  }

  async forsetiAcceptEdit(editId: string): Promise<import('@vn-grc/shared').DocumentVersion> {
    return this.post(`/document-review/edits/${editId}/accept`, {});
  }

  async forsetiRejectEdit(editId: string, reason?: string): Promise<{ ok: true }> {
    return this.post(`/document-review/edits/${editId}/reject`, reason ? { reason } : {});
  }

  // ── Sprint 47.4.5: Immutable Audit Trails (frontend bindings) ──────
  async getImmutableAuditSettings(): Promise<{ settings: ImmutableAuditTrailsOrgSettings }> {
    return this.get('/platform/immutable-audit-trails/settings');
  }
  async updateImmutableAuditSettings(
    patch: Partial<ImmutableAuditTrailsOrgSettings> & { tier_a_off_acknowledged?: boolean },
  ): Promise<{ settings: ImmutableAuditTrailsOrgSettings }> {
    return this.patch('/platform/immutable-audit-trails/settings', patch);
  }
  /**
   * Sprint 47.4.7 — rotate the AesirX credential. Server generates the
   * new credential_id + signing_secret; the signing_secret never reaches
   * the frontend.
   */
  async rotateImmutableAuditCredential(): Promise<{
    data: {
      credential_id: string;
      credential_id_display: string;
      rotated_at: string;
      grace_window_minutes: number;
    };
  }> {
    return this.post('/platform/immutable-audit-trails/credentials/rotate', {});
  }
  async listProofLinks(params?: Record<string, string>): Promise<{
    data: ProofLink[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/platform/immutable-audit-trails/proof-links${this.buildQuery(params)}`);
  }
  async getProofLink(
    proofLinkId: string,
  ): Promise<{ data: ProofLink; receipt: ProofReceipt | null }> {
    return this.get(`/platform/immutable-audit-trails/proof-links/${proofLinkId}`);
  }
  /**
   * Sprint 47.4.5 — batch lookup used by the Proof column on every audit
   * page (the global /audit + /audit/unified endpoints also support
   * `?with_proof=true` server-side; this batch is for per-module audit
   * pages and timelines that never had server-side joins).
   */
  async lookupProofLinksBySource(
    events: Array<{ source_collection?: string; source_event_id: string }>,
  ): Promise<{ data: Record<string, ProofLink> }> {
    return this.post('/platform/immutable-audit-trails/proof-links/lookup', { events });
  }
  /** Release this organization's proofs parked at the monthly cap. */
  async resumeIatCappedProofs(): Promise<{ resumed: number }> {
    return this.post('/platform/immutable-audit-trails/cap/resume', {});
  }
  async getIatUsageSummary(): Promise<{
    balance: number;
    current_month: {
      consumed: number;
      refunded: number;
      granted: number;
      adjustment: number;
      net: number;
    };
    period_start: string;
    period_end: string;
  }> {
    return this.get('/platform/immutable-audit-trails/usage/summary');
  }
  async getIatUsageHistory(params?: Record<string, string>): Promise<{
    data: ProofUsageRecord[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/platform/immutable-audit-trails/usage/history${this.buildQuery(params)}`);
  }
  async redeemIatCredits(body: {
    purchase_id: string;
    package: IatCreditPackageCode;
    units?: number;
    price_paid_usd?: number;
  }): Promise<{
    credit_package: ProofCreditPackage;
    balance: number;
    pricing_card: { units: number; price_per_unit_usd: number; total_usd: number };
  }> {
    return this.post('/platform/immutable-audit-trails/credits/redeem', body);
  }

  // Sprint 48.5.x — IAT self-service "Buy More" billing (mirrors Forseti).
  // Flow A: open a Stripe checkout session via aesirx.io.
  async iatCreatePurchaseIntent(
    packageCode: IatCreditPackageCode,
    returnUrl?: string,
  ): Promise<{ checkout_url: string }> {
    return this.post('/platform/immutable-audit-trails/credits/purchase-intent', {
      package: packageCode,
      ...(returnUrl ? { return_url: returnUrl } : {}),
    });
  }

  // Flow B: verify a customer-supplied invoice against aesirx.io and, if
  // paid + org + package match, credit the selected package into the ledger.
  async iatRedeemInvoice(body: { package: IatCreditPackageCode; invoice_id: string }): Promise<{
    credit_package: ProofCreditPackage;
    balance: number;
    pricing_card: { units: number; price_per_unit_usd: number; total_usd: number };
  }> {
    return this.post('/platform/immutable-audit-trails/credits/redeem-invoice', body);
  }
  async listIatAlerts(status?: 'active' | 'dismissed'): Promise<{ data: ProofAlert[] }> {
    const qs = status ? `?status=${status}` : '';
    return this.get(`/platform/immutable-audit-trails/alerts${qs}`);
  }
  async dismissIatAlert(alertId: string): Promise<{ data: ProofAlert }> {
    return this.post(`/platform/immutable-audit-trails/alerts/${alertId}/dismiss`, {});
  }

  // ── Sprint 47.4.6: IAT Integrity Center + per-link verification ────
  async verifyProofLink(proofLinkId: string): Promise<{
    data: {
      proof_link_id: string;
      proof_reference_id: string | null;
      module: string;
      event_type: string;
      proof_mode: 'direct' | 'batch';
      verdict: string;
      local: { verdict: string; expected_hash: string; recomputed_hash: string };
      service: { verdict: string; verification_ref: unknown; checked_at: string | null };
    };
  }> {
    return this.post(`/platform/immutable-audit-trails/proof-links/${proofLinkId}/verify`, {});
  }
  async startIntegrityRun(body: {
    period_start: string;
    period_end: string;
    modules?: string[];
  }): Promise<{ data: IntegrityRunRow }> {
    return this.post('/platform/immutable-audit-trails/integrity-center/runs', body);
  }
  async getIntegrityRun(
    runId: string,
    opts: { background?: boolean } = {},
  ): Promise<{ data: IntegrityRunRow }> {
    const path = `/platform/immutable-audit-trails/integrity-center/runs/${runId}`;
    return opts.background ? this.getInBackground(path) : this.get(path);
  }
  async listIntegrityIssues(
    runId: string,
    params?: { page?: string; page_size?: string; verdict?: string },
  ): Promise<{ data: IntegrityIssueRow[]; total: number; page: number; page_size: number }> {
    return this.get(
      `/platform/immutable-audit-trails/integrity-center/runs/${runId}/issues${this.buildQuery(params)}`,
    );
  }
  async exportIntegrityPack(body: {
    period_start: string;
    period_end: string;
    modules?: string[];
    framework_code?: string;
  }): Promise<{
    data: {
      pack_id: string;
      run: IntegrityRunRow;
      self_reference_audit_id: string | null;
      artifacts: {
        manifest: Record<string, unknown>;
        summary_html: string;
        summary_filename: string;
        manifest_filename: string;
      };
    };
  }> {
    return this.post(
      '/platform/immutable-audit-trails/integrity-center/integrity-pack/export',
      body,
    );
  }

  // ── Sprint 47.5.4a — Forseti chat shell ──────────────────────────
  // Family plan §6.5; engineering: docs/engineering/ai-legal-advisor.md §5–§9
  async forsetiListChats(params?: {
    all_users?: boolean;
    project_id?: string;
    parent_record_type?: string;
    parent_record_id?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: Array<import('@vn-grc/shared').ChatSession>;
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.all_users) qs.set('all_users', '1');
    if (params?.project_id) qs.set('project_id', params.project_id);
    if (params?.parent_record_type) qs.set('parent_record_type', params.parent_record_type);
    if (params?.parent_record_id) qs.set('parent_record_id', params.parent_record_id);
    if (params?.q) qs.set('q', params.q);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.get(`/ai-advisor/chats${suffix}`);
  }

  async forsetiCreateChat(body: {
    persona_slug?: import('@vn-grc/shared').ForsetiPersonaSlug;
    project_id?: string;
    parent_record?: import('@vn-grc/shared').ParentRecordRef;
    advisor_context?: import('@vn-grc/shared').AdvisorChatContext;
    title?: string;
  }): Promise<import('@vn-grc/shared').ChatSession> {
    return this.post('/ai-advisor/chats', body);
  }

  async forsetiGetChat(id: string): Promise<{
    chat: import('@vn-grc/shared').ChatSession;
    messages: Array<import('@vn-grc/shared').ChatMessage>;
  }> {
    return this.get(`/ai-advisor/chats/${id}`);
  }

  async forsetiUpdateChat(
    id: string,
    body: {
      title?: string;
      persona_slug?: import('@vn-grc/shared').ForsetiPersonaSlug;
    },
  ): Promise<import('@vn-grc/shared').ChatSession> {
    return this.patch(`/ai-advisor/chats/${id}`, body);
  }

  async forsetiDeleteChat(id: string): Promise<void> {
    await this.del(`/ai-advisor/chats/${id}`);
  }

  async forsetiListPersonas(): Promise<{
    data: Array<{
      slug: import('@vn-grc/shared').ForsetiPersonaSlug;
      display_name: string;
      scope: string;
      equipped_skills: string[];
      preloaded_regulatory_packs?: string[];
      avatar_svg: string;
      greetings: Record<string, string>;
    }>;
  }> {
    return this.get('/ai-advisor/personas');
  }

  async forsetiReloadRegistry(): Promise<{
    personas_loaded: number;
    skills_loaded: number;
  }> {
    return this.post('/ai-advisor/registry/reload', {});
  }

  // ── Sprint 47.5.4c — transcript export + non-LLM create-task ────
  async forsetiExportChat(chatId: string): Promise<import('@vn-grc/shared').AttachmentRef> {
    return this.post(`/ai-advisor/chats/${chatId}/export`, {});
  }

  async forsetiCreateTaskFromChat(
    chatId: string,
    body: {
      title: string;
      description?: string;
      due_date?: string;
      assignee_user_id?: string;
      assignee_org_group_id?: string;
      priority?: 'low' | 'normal' | 'high' | 'urgent';
      linked_workflow_id?: string;
    },
  ): Promise<{ task_id: string }> {
    return this.post(`/ai-advisor/chats/${chatId}/tasks`, body);
  }

  // ── Sprint 48.0.0b — Persona handoff (sub-agent orchestration) ──
  async forsetiSuggestHandoff(
    chatId: string,
    body: { question: string; locale?: string },
  ): Promise<{
    suggested_persona_slug: import('@vn-grc/shared').ForsetiPersonaSlug | null;
    reason: string | null;
    matched_rule_index: number;
  }> {
    return this.post(`/ai-advisor/chats/${chatId}/handoff/suggest`, body);
  }

  async forsetiHandoffChat(
    chatId: string,
    body: import('@vn-grc/shared').HandoffPayload,
  ): Promise<import('@vn-grc/shared').HandoffResponse> {
    return this.post(`/ai-advisor/chats/${chatId}/handoff`, body);
  }

  // ── Sprint 48.1.1 (finish) — DSR batch autocomplete ──────────────
  async dsrBatchAutocomplete(
    rows: Array<{
      request_id: string;
      requester_email: string;
      request_type: PdplRightType;
      deadline?: string;
      notes?: string;
      /** fix-sprint-20260506-2121-followup F4 — the drafted response's language. */
      requester_locale?: string;
    }>,
  ): Promise<{
    drafted_count: number;
    failed_count: number;
    drafts: Array<{
      request_id: string;
      draft_id: string;
      status: 'drafted' | 'failed';
      response_template?: string;
      frameworks_cited?: string[];
      verification_task_id?: string;
      billable_units?: number;
      locale?: string;
      synthesis_status?: import('@vn-grc/shared').DraftSynthesisStatus;
      error?: { code: string; message: string };
    }>;
  }> {
    return this.post('/cases/batch-autocomplete', { rows });
  }

  async dsrBatchAutocompleteDecision(
    draftId: string,
    accept: boolean,
  ): Promise<{ status: 'accepted' | 'rejected' }> {
    return this.post(`/cases/batch-autocomplete/${draftId}/decision`, { accept });
  }

  // ── Sprint 48.1.2 (finish) — RoPA hygiene autofix ──────────────
  async ropaAutofixDraft(
    activityId: string,
    // fix-sprint-20260506-2121-followup verifier round 1 — read from the one
    // list in `@vn-grc/shared` (`ROPA_AUTOFIX_FIELDS`) rather than written out.
    fieldKey: import('@vn-grc/shared').RopaAutofixField,
    // Sprint 64.57.0 gate E12.5 pass 2, finding F3 — this was the family
    // vocabulary's fifth transcription. Derived from the declared families in
    // `@vn-grc/shared` so a family added there is accepted here without an edit.
    frameworkCode: import('@vn-grc/shared').RopaCompletenessFrameworkSlug,
  ): Promise<{
    draft_id: string;
    activity_id: string;
    field_key: string;
    framework_code: string;
    suggested_value: string;
    framework_citation: string;
    needs_human_input: boolean;
    human_input_message?: string;
  }> {
    return this.post(`/data-mapping/ropa-completeness/${activityId}/autofix/${fieldKey}`, {
      framework_code: frameworkCode,
    });
  }

  async ropaAutofixDecision(
    draftId: string,
    accept: boolean,
  ): Promise<{ status: 'accepted' | 'rejected' }> {
    return this.post(`/data-mapping/ropa-completeness/autofix/${draftId}/decision`, { accept });
  }

  // ── Sprint 48.0.0c — Forseti memory browse + curate ─────────────
  async forsetiMemoryList(params?: {
    category?: import('@vn-grc/shared').MemoryCategory;
    scope?: import('@vn-grc/shared').MemoryScope;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: import('@vn-grc/shared').OrgMemory[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    if (params?.scope) q.set('scope', params.scope);
    if (params?.page) q.set('page', String(params.page));
    if (params?.page_size) q.set('page_size', String(params.page_size));
    const query = q.toString();
    return this.get(`/ai-advisor/memory${query ? `?${query}` : ''}`);
  }

  async forsetiMemoryUpdate(
    memoryId: string,
    body: {
      content?: string;
      category?: import('@vn-grc/shared').MemoryCategory;
      anchor?: string;
    },
  ): Promise<import('@vn-grc/shared').OrgMemory> {
    return this.patch(`/ai-advisor/memory/${memoryId}`, body);
  }

  async forsetiMemoryDelete(memoryId: string): Promise<void> {
    await this.del(`/ai-advisor/memory/${memoryId}`);
  }

  async forsetiMemoryPromote(memoryId: string): Promise<import('@vn-grc/shared').OrgMemory> {
    return this.post(`/ai-advisor/memory/${memoryId}/promote`, {});
  }

  // ── Sprint 47.5.5 — AI Workflows ────────────────────────────────
  async forsetiWorkflowsList(params?: {
    type?: 'assistant' | 'tabular';
    q?: string;
    page?: number;
    page_size?: number;
    /** Also return the workflows the viewer hid (the Workflows page greys them and offers Unhide). */
    include_hidden?: boolean;
  }): Promise<{
    data: import('@vn-grc/shared').Workflow[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.type) qs.set('type', params.type);
    if (params?.q) qs.set('q', params.q);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    if (params?.include_hidden) qs.set('include_hidden', 'true');
    return this.get(`/ai-workflows?${qs.toString()}`);
  }

  // Sprint 47.5.9.3.4 — closes finding 3.5; was previously achieved by
  // calling forsetiWorkflowsList() and finding by id, which scaled
  // poorly with workflow count.
  async forsetiWorkflowsGet(id: string): Promise<import('@vn-grc/shared').Workflow> {
    return this.get(`/ai-workflows/${encodeURIComponent(id)}`);
  }

  async forsetiWorkflowsCreate(body: {
    title: string;
    description?: string;
    type: import('@vn-grc/shared').WorkflowType;
    prompt_md: string;
    columns_config?: Record<string, unknown>[];
    persona_affinity?: import('@vn-grc/shared').ForsetiPersonaSlug;
    practice?: string;
    produces_tasks?: boolean;
    produces_evidence?: boolean;
  }): Promise<import('@vn-grc/shared').Workflow> {
    return this.post('/ai-workflows', body);
  }

  async forsetiWorkflowsUpdate(
    id: string,
    body: {
      title?: string;
      description?: string;
      prompt_md?: string;
      persona_affinity?: import('@vn-grc/shared').ForsetiPersonaSlug;
      practice?: string;
      produces_tasks?: boolean;
      produces_evidence?: boolean;
    },
  ): Promise<import('@vn-grc/shared').Workflow> {
    return this.patch(`/ai-workflows/${id}`, body);
  }

  async forsetiWorkflowsDelete(id: string): Promise<void> {
    await this.del(`/ai-workflows/${id}`);
  }

  async forsetiWorkflowsRun(
    id: string,
    body: {
      project_id?: string;
      attachment_ids?: string[];
      parent_record?: { type: import('@vn-grc/shared').ParentRecordRef['type']; id: string };
      persona_slug_override?: import('@vn-grc/shared').ForsetiPersonaSlug;
    },
  ): Promise<{ run_id: string }> {
    return this.post(`/ai-workflows/${id}/run`, body);
  }

  async forsetiWorkflowsGetRun(runId: string): Promise<import('@vn-grc/shared').WorkflowRun> {
    return this.get(`/ai-workflows/runs/${runId}`);
  }

  /** Ask for a pending or running workflow run to stop. 409 once it has finished. */
  async forsetiWorkflowsCancelRun(runId: string): Promise<import('@vn-grc/shared').WorkflowRun> {
    return this.post(`/ai-workflows/runs/${encodeURIComponent(runId)}/cancel`, {});
  }

  async forsetiWorkflowsShare(
    id: string,
    body: {
      shared_with_user_ids?: string[];
      shared_with_org_group_ids?: string[];
      allow_edit: boolean;
    },
  ): Promise<void> {
    await this.post(`/ai-workflows/${id}/share`, body);
  }

  async forsetiWorkflowsListHidden(): Promise<{
    data: import('@vn-grc/shared').WorkflowHiddenEntry[];
  }> {
    return this.get('/ai-workflows/hidden');
  }

  async forsetiWorkflowsHide(workflowId: string): Promise<void> {
    await this.post('/ai-workflows/hidden', { workflow_id: workflowId });
  }

  async forsetiWorkflowsUnhide(workflowId: string): Promise<void> {
    await this.del(`/ai-workflows/hidden/${workflowId}`);
  }

  async forsetiWorkflowsReloadBuiltin(): Promise<{ loaded: number }> {
    return this.post('/ai-workflows/reload-builtin', {});
  }

  // ── Sprint 47.5.6 — Tabular Reviews ─────────────────────────────
  async forsetiTabularReviewsList(params?: {
    project_id?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: import('@vn-grc/shared').TabularReview[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.project_id) qs.set('project_id', params.project_id);
    if (params?.q) qs.set('q', params.q);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    return this.get(`/tabular-reviews?${qs.toString()}`);
  }

  async forsetiTabularReviewsCreate(body: {
    title: string;
    project_id?: string;
    attachment_ids?: string[];
  }): Promise<import('@vn-grc/shared').TabularReview> {
    return this.post('/tabular-reviews', body);
  }

  async forsetiTabularReviewsGet(id: string): Promise<{
    review: import('@vn-grc/shared').TabularReview;
    cells: import('@vn-grc/shared').TabularCell[];
    /**
     * Sprint 47.5.9.3.3 (T-3) — partial map of attachment_id → filename
     * projected by the backend so the TR table row header can render
     * filenames instead of raw ObjectIds. Optional + partial.
     */
    attachment_filenames?: Record<string, string>;
  }> {
    return this.get(`/tabular-reviews/${id}`);
  }

  async forsetiTabularReviewsUpdate(
    id: string,
    body: { title?: string; columns_config?: import('@vn-grc/shared').TabularColumn[] },
  ): Promise<import('@vn-grc/shared').TabularReview> {
    return this.patch(`/tabular-reviews/${id}`, body);
  }

  async forsetiTabularReviewsDelete(id: string): Promise<void> {
    await this.del(`/tabular-reviews/${id}`);
  }

  async forsetiTabularReviewsAddColumn(
    id: string,
    body: {
      name: string;
      prompt: string;
      return_format: import('@vn-grc/shared').TabularColumnReturnFormat;
      presets?: string[];
      citations_required?: boolean;
    },
  ): Promise<import('@vn-grc/shared').TabularColumn> {
    return this.post(`/tabular-reviews/${id}/columns`, body);
  }

  async forsetiTabularReviewsRemoveColumn(id: string, index: number): Promise<void> {
    await this.del(`/tabular-reviews/${id}/columns/${index}`);
  }

  async forsetiTabularReviewsAttachDocs(id: string, attachmentIds: string[]): Promise<void> {
    await this.post(`/tabular-reviews/${id}/documents`, { attachment_ids: attachmentIds });
  }

  async forsetiTabularReviewsDetachDoc(id: string, attachmentId: string): Promise<void> {
    await this.del(`/tabular-reviews/${id}/documents/${attachmentId}`);
  }

  async forsetiTabularReviewsRun(id: string): Promise<{ review_id: string; cell_count: number }> {
    return this.post(`/tabular-reviews/${id}/run`, {});
  }

  async forsetiTabularReviewsRunCell(
    id: string,
    documentId: string,
    columnIndex: number,
  ): Promise<{ review_id: string }> {
    return this.post(`/tabular-reviews/${id}/cells/${documentId}/${columnIndex}/run`, {});
  }

  async forsetiTabularReviewsCancel(id: string): Promise<{ ok: boolean }> {
    return this.post(`/tabular-reviews/${id}/cancel`, {});
  }

  async forsetiTabularReviewsGetCostEstimate(id: string): Promise<{
    estimated_billable_units: number;
    confidence: 'low' | 'medium' | 'high';
    target_id: string;
    organization_id: string;
    feature: string;
    input_count: number;
    created_at: string;
  }> {
    return this.get(`/tabular-reviews/${id}/export-cost-estimate`);
  }

  async forsetiTabularReviewsExport(
    id: string,
    format: 'xlsx' | 'csv' = 'xlsx',
  ): Promise<import('@vn-grc/shared').AttachmentRef> {
    return this.get(`/tabular-reviews/${id}/export?format=${format}`);
  }

  // ── Sprint 47.7.2 — Tabular cell discussion threads ─────────────
  async forsetiTabularReviewsThreadsSummary(reviewId: string): Promise<{
    data: import('@vn-grc/shared').TabularCellThreadSummary[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/tabular-reviews/${reviewId}/threads-summary`);
  }

  async forsetiTabularReviewsCellThreadGet(
    reviewId: string,
    documentId: string,
    columnIndex: number,
  ): Promise<{ thread: import('@vn-grc/shared').TabularCellThread | null }> {
    return this.get(
      `/tabular-reviews/${reviewId}/cells/${encodeURIComponent(documentId)}/${columnIndex}/thread`,
    );
  }

  async forsetiTabularReviewsCellThreadPost(
    reviewId: string,
    documentId: string,
    columnIndex: number,
    body_md: string,
  ): Promise<{ thread: import('@vn-grc/shared').TabularCellThread }> {
    return this.post(
      `/tabular-reviews/${reviewId}/cells/${encodeURIComponent(documentId)}/${columnIndex}/thread/posts`,
      { body_md },
    );
  }

  async forsetiTabularReviewsCellThreadEdit(
    reviewId: string,
    documentId: string,
    columnIndex: number,
    postId: string,
    body_md: string,
  ): Promise<{ thread: import('@vn-grc/shared').TabularCellThread }> {
    return this.patch(
      `/tabular-reviews/${reviewId}/cells/${encodeURIComponent(documentId)}/${columnIndex}/thread/posts/${postId}`,
      { body_md },
    );
  }

  async forsetiTabularReviewsCellThreadDelete(
    reviewId: string,
    documentId: string,
    columnIndex: number,
    postId: string,
  ): Promise<void> {
    await this.del(
      `/tabular-reviews/${reviewId}/cells/${encodeURIComponent(documentId)}/${columnIndex}/thread/posts/${postId}`,
    );
  }

  // ── Sprint 47.7.2 — Workflow schedules ──────────────────────────
  async forsetiWorkflowSchedulesList(
    workflowId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<{
    data: import('@vn-grc/shared').WorkflowSchedule[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/ai-workflows/${workflowId}/schedules${tail}`);
  }

  async forsetiWorkflowSchedulesCreate(
    workflowId: string,
    body: {
      frequency: import('@vn-grc/shared').WorkflowScheduleFrequency;
      time_utc: string;
      timezone: string;
      day_of_week?: number;
      day_of_month?: number;
      project_id?: string;
      default_attachment_ids?: string[];
      default_parent_record?: import('@vn-grc/shared').ParentRecordRef;
      persona_slug_override?: import('@vn-grc/shared').ForsetiPersonaSlug;
    },
  ): Promise<import('@vn-grc/shared').WorkflowSchedule> {
    return this.post(`/ai-workflows/${workflowId}/schedules`, body);
  }

  async forsetiWorkflowSchedulesUpdate(
    scheduleId: string,
    body: Partial<{
      frequency: import('@vn-grc/shared').WorkflowScheduleFrequency;
      time_utc: string;
      timezone: string;
      day_of_week: number;
      day_of_month: number;
      status: import('@vn-grc/shared').WorkflowScheduleStatus;
      project_id: string;
      default_attachment_ids: string[];
    }>,
  ): Promise<import('@vn-grc/shared').WorkflowSchedule> {
    return this.patch(`/ai-workflows/schedules/${scheduleId}`, body);
  }

  async forsetiWorkflowSchedulesDelete(scheduleId: string): Promise<void> {
    await this.del(`/ai-workflows/schedules/${scheduleId}`);
  }

  async forsetiWorkflowSchedulesTrigger(scheduleId: string): Promise<{ run_id: string }> {
    return this.post(`/ai-workflows/schedules/${scheduleId}/trigger`, {});
  }

  // ── Sprint 47.7.3 — Cross-framework conflicts ──────────────────
  async forsetiCrossFrameworkConflictsList(params?: {
    framework_code?: string;
    topic?: string;
    severity?: import('@vn-grc/shared').FrameworkConflictSeverity;
    status?:
      | 'acknowledged'
      | 'unacknowledged'
      | import('@vn-grc/shared').FrameworkConflictAcknowledgementStatus;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: import('@vn-grc/shared').FrameworkConflictWithAcknowledgement[];
    total: number;
    page: number;
    page_size: number;
    // fix-sprint-20260925-1400 F15 (#107) — `installed_frameworks` is gone:
    // the server stopped sending it in Sprint 64.51.0 (it carried framework
    // codes while the filter compares pack slugs), and this type still
    // declared it as always present.
  }> {
    const qs = new URLSearchParams();
    if (params?.framework_code) qs.set('framework_code', params.framework_code);
    if (params?.topic) qs.set('topic', params.topic);
    if (params?.severity) qs.set('severity', params.severity);
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/ai-advisor/cross-framework-conflicts${tail}`);
  }

  async forsetiCrossFrameworkConflictsGet(
    conflictId: string,
  ): Promise<import('@vn-grc/shared').FrameworkConflictWithAcknowledgement> {
    return this.get(`/ai-advisor/cross-framework-conflicts/${encodeURIComponent(conflictId)}`);
  }

  async forsetiCrossFrameworkConflictsAcknowledge(
    conflictId: string,
    body: {
      status: import('@vn-grc/shared').FrameworkConflictAcknowledgementStatus;
      assigned_to_user_id?: string;
      due_date?: string;
      notes?: string;
      resolution_md?: string;
    },
  ): Promise<import('@vn-grc/shared').FrameworkConflictAcknowledgement> {
    return this.post(
      `/ai-advisor/cross-framework-conflicts/${encodeURIComponent(conflictId)}/acknowledge`,
      body,
    );
  }

  async forsetiCrossFrameworkConflictsUpdateAcknowledgement(
    conflictId: string,
    body: {
      status: import('@vn-grc/shared').FrameworkConflictAcknowledgementStatus;
      assigned_to_user_id?: string;
      due_date?: string;
      notes?: string;
      resolution_md?: string;
    },
  ): Promise<import('@vn-grc/shared').FrameworkConflictAcknowledgement> {
    return this.patch(
      `/ai-advisor/cross-framework-conflicts/${encodeURIComponent(conflictId)}/acknowledge`,
      body,
    );
  }

  async forsetiCrossFrameworkConflictsExport(params?: {
    framework_code?: string;
    topic?: string;
    severity?: import('@vn-grc/shared').FrameworkConflictSeverity;
    status?:
      | 'acknowledged'
      | 'unacknowledged'
      | import('@vn-grc/shared').FrameworkConflictAcknowledgementStatus;
    q?: string;
    /** Sprint 47.9.1 — XLSX added alongside CSV. */
    format?: 'csv' | 'xlsx';
  }): Promise<import('@vn-grc/shared').AttachmentRef> {
    const qs = new URLSearchParams();
    qs.set('format', params?.format ?? 'csv');
    if (params?.framework_code) qs.set('framework_code', params.framework_code);
    if (params?.topic) qs.set('topic', params.topic);
    if (params?.severity) qs.set('severity', params.severity);
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    return this.get(`/ai-advisor/cross-framework-conflicts/export?${qs.toString()}`);
  }

  async forsetiCrossFrameworkConflictsUnacknowledge(conflictId: string): Promise<void> {
    await this.del(
      `/ai-advisor/cross-framework-conflicts/${encodeURIComponent(conflictId)}/acknowledge`,
    );
  }

  // ── Sprint 58.x.x (Phase 25) — PDPL Evidence Pack frontend (Sprint E1) ──
  // Routes shipped in Sprints A–C: pack assembly + email draft + manual
  // sent-proof + customer-controlled provider config. Send/timeline land
  // in Sprint E2 (separate PR).

  async assemblePdplEvidencePack(
    input: PdplEvidencePackAssembleInput,
  ): Promise<PdplEvidencePackAssembleResult> {
    return this.post('/pdpl-evidence-packs/assemble', input);
  }
  async getPdplEvidencePackEligibility(
    filingId: string,
  ): Promise<PdplEvidencePackEligibilityContext> {
    return this.get(
      `/pdpl-evidence-packs/eligibility/dossier-filings/${encodeURIComponent(filingId)}`,
    );
  }
  async getPdplEvidencePack(packId: string): Promise<PdplEvidencePackAssemblyDoc> {
    return this.get(`/pdpl-evidence-packs/${encodeURIComponent(packId)}`);
  }

  async composePdplEmailDraft(
    packId: string,
    input: PdplEmailDraftComposeInputPayload,
  ): Promise<PdplEmailDraftComposeResult> {
    return this.post(`/pdpl-evidence-packs/${encodeURIComponent(packId)}/email-drafts`, input);
  }
  async editPdplEmailDraft(
    packId: string,
    draftId: string,
    changes: PdplEmailDraftEditChanges,
  ): Promise<PdplEmailDraftComposeResult> {
    return this.patch(
      `/pdpl-evidence-packs/${encodeURIComponent(packId)}/email-drafts/${encodeURIComponent(draftId)}`,
      { changes },
    );
  }
  async listPdplEmailDrafts(packId: string): Promise<{ items: PdplEmailDraftDoc[] }> {
    return this.get(`/pdpl-evidence-packs/${encodeURIComponent(packId)}/email-drafts`);
  }
  async uploadPdplSentProof(
    packId: string,
    input: PdplSentProofUploadPayload,
  ): Promise<PdplEmailSentProofUploadResult> {
    return this.post(`/pdpl-evidence-packs/${encodeURIComponent(packId)}/sent-proof`, input);
  }
  async listPdplSentProofs(packId: string): Promise<{ items: PdplEmailSentProofDoc[] }> {
    return this.get(`/pdpl-evidence-packs/${encodeURIComponent(packId)}/sent-proofs`);
  }

  // ── Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + send-record timeline ──

  async sendPdplEvidencePackEmail(
    input: PdplEmailSendRequestPayload,
  ): Promise<PdplEmailSendRecord> {
    return this.post('/pdpl-evidence-packs/send', input);
  }
  async listPdplSendRecords(packId: string): Promise<{ items: PdplEmailSendRecord[] }> {
    return this.get(`/pdpl-evidence-packs/send-records?pack_id=${encodeURIComponent(packId)}`);
  }
  async getPdplSendRecord(sendId: string): Promise<PdplEmailSendRecord> {
    return this.get(`/pdpl-evidence-packs/send-records/${encodeURIComponent(sendId)}`);
  }
  async recordPdplAuthorityResponse(
    sendId: string,
    input: PdplAuthorityResponseRequestPayload,
  ): Promise<PdplEmailSendRecord> {
    return this.post(
      `/pdpl-evidence-packs/send-records/${encodeURIComponent(sendId)}/authority-response`,
      input,
    );
  }
  async reclassifyPdplAuthorityResponse(
    sendId: string,
    input: PdplAuthorityResponseRequestPayload,
  ): Promise<PdplEmailSendRecord> {
    return this.patch(
      `/pdpl-evidence-packs/send-records/${encodeURIComponent(sendId)}/authority-response`,
      input,
    );
  }
  async recordPdplHardCopyFollowup(
    sendId: string,
    input: PdplHardCopyFollowupRequestPayload,
  ): Promise<PdplEmailSendRecord> {
    return this.post(
      `/pdpl-evidence-packs/send-records/${encodeURIComponent(sendId)}/hard-copy-followup`,
      input,
    );
  }
  async updatePdplHardCopyFollowup(
    sendId: string,
    input: PdplHardCopyFollowupUpdatePayload,
  ): Promise<PdplEmailSendRecord> {
    return this.patch(
      `/pdpl-evidence-packs/send-records/${encodeURIComponent(sendId)}/hard-copy-followup`,
      input,
    );
  }

  async listPdplEmailProviders(
    status?: PdplEmailProviderStatus,
  ): Promise<{ items: PdplEmailProviderConfigRedacted[] }> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.get(`/pdpl-evidence-packs/email-providers${query}`);
  }
  async getPdplEmailProvider(configId: string): Promise<PdplEmailProviderConfigRedacted> {
    return this.get(`/pdpl-evidence-packs/email-providers/${encodeURIComponent(configId)}`);
  }
  async createPdplEmailProvider(
    input: PdplEmailProviderCreateInput,
  ): Promise<PdplEmailProviderConfigRedacted> {
    return this.post('/pdpl-evidence-packs/email-providers', input);
  }
  async acceptPdplEmailProviderConsent(
    configId: string,
    payload: { verbatim_text: string; locale: 'en' | 'vi' },
  ): Promise<PdplEmailProviderConfigRedacted> {
    return this.post(
      `/pdpl-evidence-packs/email-providers/${encodeURIComponent(configId)}/consent`,
      payload,
    );
  }
  async testPdplEmailProviderConnection(
    configId: string,
  ): Promise<PdplEmailProviderConfigRedacted> {
    return this.post(
      `/pdpl-evidence-packs/email-providers/${encodeURIComponent(configId)}/test-connection`,
      {},
    );
  }
  async disablePdplEmailProvider(configId: string): Promise<PdplEmailProviderConfigRedacted> {
    return this.post(
      `/pdpl-evidence-packs/email-providers/${encodeURIComponent(configId)}/disable`,
      {},
    );
  }

  // ── Trust evidence (Sprint 62.5.0) ────────────────────────────────
  //
  // Every path below is relative to the module's single mount,
  // `/api/v1/trust-evidence`, which composes three routers. The envelopes are
  // the pair 62.2.5 pinned — `{ data }` for one entity, `{ data, total, page,
  // page_size }` for a list — so callers read lists through `unwrapResponse` +
  // `extractPaginationMeta` and single entities through `unwrapSingleResponse`.
  //
  // `page_size`, never `per_page`. 62.2.5 found `extractPaginationMeta` reading
  // a key 560 backend sites do not emit, so every paginated surface silently
  // showed the default while the server had said something else.
  //
  // `getTrustRegimeCatalogue` is the deliberate exception to the list shape: it
  // returns **one object holding two arrays**, because a catalogue carries its
  // own diagnostics and has no page that would mean anything. It must be
  // unwrapped with `unwrapSingleResponse`; `unwrapResponse` would return `[]`
  // for every tenant, since `data` is an object.

  async getTrustRegimeCatalogue(): Promise<{
    data: {
      regimes: Array<{ source: TrustRegimeSource; regime: TrustRegime }>;
      diagnostics: Array<{
        framework_code: string;
        regime_id: string | null;
        reason: string;
        detail: string;
      }>;
    };
  }> {
    return this.get('/trust-evidence/regimes');
  }

  // ── Identity ──────────────────────────────────────────────────────

  async listIdentityProfiles(params?: {
    page?: number;
    page_size?: number;
    subject_kind?: string;
    status?: string;
    regime_id?: string;
  }): Promise<{ data: IdentityProfile[]; total: number; page: number; page_size: number }> {
    return this.get(`/trust-evidence/identity-profiles${this.buildQuery(params)}`);
  }

  async createIdentityProfile(
    input: CreateIdentityProfileRequest,
  ): Promise<{ data: IdentityProfile }> {
    return this.post('/trust-evidence/identity-profiles', input);
  }

  async getIdentityProfile(profileId: string): Promise<{ data: IdentityProfile }> {
    return this.get(`/trust-evidence/identity-profiles/${encodeURIComponent(profileId)}`);
  }

  async updateIdentityProfile(
    profileId: string,
    input: UpdateIdentityProfileRequest,
  ): Promise<{ data: IdentityProfile }> {
    return this.patch(`/trust-evidence/identity-profiles/${encodeURIComponent(profileId)}`, input);
  }

  /**
   * An identity's attributes.
   *
   * Unpaginated at the server — it returns every attribute with
   * `page: 1, page_size: <count>` — because the set is a property of the one
   * identity and a page boundary through it would make "which attributes are
   * verified?" unanswerable from a single request. The envelope is still the
   * list pair so it reads like every other list.
   */
  async listIdentityAttributes(
    profileId: string,
  ): Promise<{ data: IdentityAttribute[]; total: number; page: number; page_size: number }> {
    return this.get(
      `/trust-evidence/identity-profiles/${encodeURIComponent(profileId)}/attributes`,
    );
  }

  async createIdentityAttribute(
    profileId: string,
    input: CreateIdentityAttributeRequest,
  ): Promise<{ data: IdentityAttribute }> {
    return this.post(
      `/trust-evidence/identity-profiles/${encodeURIComponent(profileId)}/attributes`,
      input,
    );
  }

  /**
   * Nested under the profile, and the profile id is not decoration: the service
   * filters on **both** ids, so a mismatch is a 404 rather than an edit applied
   * to the wrong subject.
   */
  async updateIdentityAttribute(
    profileId: string,
    attributeId: string,
    input: UpdateIdentityAttributeRequest,
  ): Promise<{ data: IdentityAttribute }> {
    return this.patch(
      `/trust-evidence/identity-profiles/${encodeURIComponent(profileId)}/attributes/${encodeURIComponent(attributeId)}`,
      input,
    );
  }

  async listIdentityEvents(
    profileId: string,
    params?: { page?: number; page_size?: number; event_kind?: string },
  ): Promise<{ data: IdentityEvent[]; total: number; page: number; page_size: number }> {
    return this.get(
      `/trust-evidence/identity-profiles/${encodeURIComponent(profileId)}/events${this.buildQuery(params)}`,
    );
  }

  async recordIdentityEvent(
    profileId: string,
    input: RecordIdentityEventRequest,
  ): Promise<{ data: IdentityEvent }> {
    return this.post(
      `/trust-evidence/identity-profiles/${encodeURIComponent(profileId)}/events`,
      input,
    );
  }

  // ── Trust services ────────────────────────────────────────────────

  async listTrustProviders(params?: {
    page?: number;
    page_size?: number;
    recognition_status?: string;
    service_type_code?: string;
  }): Promise<{ data: TrustServiceProvider[]; total: number; page: number; page_size: number }> {
    return this.get(`/trust-evidence/providers${this.buildQuery(params)}`);
  }

  async createTrustProvider(
    input: CreateTrustProviderRequest,
  ): Promise<{ data: TrustServiceProvider }> {
    return this.post('/trust-evidence/providers', input);
  }

  async getTrustProvider(providerId: string): Promise<{ data: TrustServiceProvider }> {
    return this.get(`/trust-evidence/providers/${encodeURIComponent(providerId)}`);
  }

  async updateTrustProvider(
    providerId: string,
    input: UpdateTrustProviderRequest,
  ): Promise<{ data: TrustServiceProvider }> {
    return this.patch(`/trust-evidence/providers/${encodeURIComponent(providerId)}`, input);
  }

  /**
   * A provider's timeline, across every credential it issued.
   *
   * `event_kind` narrows to one kind **at the server**. Gate E12.5 pass 2 (F3)
   * found the signature-evidence picker fetching the newest page of *any* kind
   * and filtering to verifications in the client: a provider with a hundred
   * recent lifecycle events and older verifications yielded zero candidates, and
   * the interface then reported an org-wide negative the fetch could not
   * support. Filtering after the server has already truncated is not filtering.
   */
  async listTrustProviderEvents(
    providerId: string,
    params?: { page?: number; page_size?: number; event_kind?: TrustEventKind },
  ): Promise<{ data: TrustEvent[]; total: number; page: number; page_size: number }> {
    return this.get(
      `/trust-evidence/providers/${encodeURIComponent(providerId)}/events${this.buildQuery(params)}`,
    );
  }

  async listTrustCredentials(params?: {
    page?: number;
    page_size?: number;
    provider_id?: string;
    status?: string;
    identity_profile_id?: string;
  }): Promise<{ data: TrustCredential[]; total: number; page: number; page_size: number }> {
    return this.get(`/trust-evidence/credentials${this.buildQuery(params)}`);
  }

  async createTrustCredential(
    input: CreateTrustCredentialRequest,
  ): Promise<{ data: TrustCredential }> {
    return this.post('/trust-evidence/credentials', input);
  }

  async getTrustCredential(credentialId: string): Promise<{ data: TrustCredential }> {
    return this.get(`/trust-evidence/credentials/${encodeURIComponent(credentialId)}`);
  }

  async updateTrustCredential(
    credentialId: string,
    input: Partial<
      Omit<
        CreateTrustCredentialRequest,
        'provider_id' | 'service_type_code' | 'serial_reference' | 'renewal_of_credential_id'
      >
    >,
  ): Promise<{ data: TrustCredential }> {
    return this.patch(`/trust-evidence/credentials/${encodeURIComponent(credentialId)}`, input);
  }

  /**
   * A credential lifecycle transition — `trust_evidence:lifecycle`, not `:write`.
   *
   * Returns `{ data: { credential, event } }`: one transition produces exactly
   * two facts, and returning only the credential would leave the caller unable
   * to link to the event it just created.
   */
  async transitionTrustCredential(
    credentialId: string,
    input: TransitionTrustCredentialRequest,
  ): Promise<{ data: { credential: TrustCredential; event: TrustEvent } }> {
    return this.post(
      `/trust-evidence/credentials/${encodeURIComponent(credentialId)}/transitions`,
      input,
    );
  }

  async listTrustCredentialEvents(
    credentialId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<{ data: TrustEvent[]; total: number; page: number; page_size: number }> {
    return this.get(
      `/trust-evidence/credentials/${encodeURIComponent(credentialId)}/events${this.buildQuery(params)}`,
    );
  }

  /** `:write`, not `:lifecycle` — recording a check changes no credential. */
  async recordTrustVerification(
    input: RecordTrustVerificationRequest,
  ): Promise<{ data: TrustEvent }> {
    return this.post('/trust-evidence/verifications', input);
  }

  /**
   * Export a credential's audited lifecycle as a versioned evidence pack.
   *
   * Declared on the module composer rather than the trust sub-router, and it is
   * the one route in the module gated on **two** permissions:
   * `trust_evidence:read` **and** `framework_evidence_pack:create`. It composes
   * 62.2.2's assembler, which mints an immutable hash-bearing artefact on disk,
   * and `:read` alone would hand pack creation to the auditor role — which holds
   * every `:read` permission by suffix.
   *
   * Refuses with 409 when the credential has no recorded event: a credential
   * with no lifecycle has no lifecycle to attest to.
   */
  async exportCredentialLifecycle(credentialId: string): Promise<{
    data: {
      credential_id: string;
      root_framework_code: string;
      regime_id: string;
      event_count: number;
      assembly: {
        pack_id: string;
        version: number;
        manifest_hash: string;
        completeness: 'complete' | 'partial';
        file_count: number;
        missing_item_count: number;
      };
      export: {
        pack_id: string;
        version: number;
        manifest_hash: string;
        archive: { filename: string; size: number };
        manifest_json: { filename: string; size: number };
      };
    };
  }> {
    return this.post(
      `/trust-evidence/credentials/${encodeURIComponent(credentialId)}/evidence-export`,
      {},
    );
  }

  // ── Electronic submissions ────────────────────────────────────────

  async listElectronicSubmissions(params?: {
    page?: number;
    page_size?: number;
    status?: string;
    framework_code?: string;
  }): Promise<{ data: ElectronicSubmission[]; total: number; page: number; page_size: number }> {
    return this.get(`/trust-evidence/submissions${this.buildQuery(params)}`);
  }

  async createElectronicSubmission(
    input: CreateElectronicSubmissionRequest,
  ): Promise<{ data: ElectronicSubmission }> {
    return this.post('/trust-evidence/submissions', input);
  }

  async getElectronicSubmission(submissionId: string): Promise<{ data: ElectronicSubmission }> {
    return this.get(`/trust-evidence/submissions/${encodeURIComponent(submissionId)}`);
  }

  async updateElectronicSubmission(
    submissionId: string,
    input: UpdateElectronicSubmissionRequest,
  ): Promise<{ data: ElectronicSubmission }> {
    return this.patch(`/trust-evidence/submissions/${encodeURIComponent(submissionId)}`, input);
  }

  // The three regulator-facing calls. `trust_evidence:submit`, not `:write`:
  // preparing a filing and sending one are different acts, and only one of them
  // reaches an authority.

  async submitElectronicSubmission(
    submissionId: string,
    input: SubmitElectronicSubmissionRequest,
  ): Promise<{ data: ElectronicSubmission }> {
    return this.post(
      `/trust-evidence/submissions/${encodeURIComponent(submissionId)}/submit`,
      input,
    );
  }

  async recordSubmissionReceipt(
    submissionId: string,
    input: RecordSubmissionReceiptRequest,
  ): Promise<{ data: ElectronicSubmission }> {
    return this.post(
      `/trust-evidence/submissions/${encodeURIComponent(submissionId)}/receipt`,
      input,
    );
  }

  async recordSubmissionAmendment(
    submissionId: string,
    input: RecordSubmissionAmendmentRequest,
  ): Promise<{ data: ElectronicSubmission }> {
    return this.post(
      `/trust-evidence/submissions/${encodeURIComponent(submissionId)}/amendments`,
      input,
    );
  }

  // ── Electronic agreements ─────────────────────────────────────────

  async listElectronicAgreements(params?: {
    page?: number;
    page_size?: number;
    agreement_type_code?: string;
    status?: string;
    /**
     * The server parses this as the literal string `'true'` / `'false'` rather
     * than coercing: `z.coerce.boolean()` is truthiness, so `"false"` would
     * coerce to `true` and the filter would invert.
     */
    sync_outstanding?: 'true' | 'false';
  }): Promise<{ data: ElectronicAgreement[]; total: number; page: number; page_size: number }> {
    return this.get(`/trust-evidence/agreements${this.buildQuery(params)}`);
  }

  /**
   * Open an electronic agreement.
   *
   * ## The parties are typed to the wire shape, not to `AgreementParty`
   *
   * `CreateElectronicAgreementRequest.parties` is declared as `AgreementParty[]`
   * in the shared contract, and `AgreementParty` carries a **required `label`**.
   * The route's `PartySchema` is `.strict()` and explicitly refuses `label`,
   * because the display string is resolved server-side from whichever reference
   * is set — a typed name cannot be joined back to the signature evidence that
   * proves who signed.
   *
   * So a client typed against the shared request type is told to send exactly
   * the field that produces a 400 naming `parties.0.label`. The backend has the
   * correct shape (`AgreementPartyInput` in `transaction-service.ts`), but it
   * lives in a backend module and is not exported to the frontend. It is
   * restated here as `TrustAgreementPartyInput` so this client's callers are
   * typed against what the server actually accepts.
   */
  async createElectronicAgreement(
    input: Omit<CreateElectronicAgreementRequest, 'parties'> & {
      parties: TrustAgreementPartyInput[];
    },
  ): Promise<{ data: ElectronicAgreement }> {
    return this.post('/trust-evidence/agreements', input);
  }

  async getElectronicAgreement(agreementId: string): Promise<{ data: ElectronicAgreement }> {
    return this.get(`/trust-evidence/agreements/${encodeURIComponent(agreementId)}`);
  }

  async updateElectronicAgreement(
    agreementId: string,
    input: Omit<UpdateElectronicAgreementRequest, 'parties'> & {
      parties?: TrustAgreementPartyInput[];
    },
  ): Promise<{ data: ElectronicAgreement }> {
    return this.patch(`/trust-evidence/agreements/${encodeURIComponent(agreementId)}`, input);
  }

  async recordAgreementSyncEvent(
    agreementId: string,
    input: RecordAgreementSyncRequest,
  ): Promise<{ data: ElectronicAgreement }> {
    return this.post(
      `/trust-evidence/agreements/${encodeURIComponent(agreementId)}/sync-events`,
      input,
    );
  }

  async recordAgreementException(
    agreementId: string,
    input: RecordAgreementExceptionRequest,
  ): Promise<{ data: ElectronicAgreement }> {
    return this.post(
      `/trust-evidence/agreements/${encodeURIComponent(agreementId)}/exceptions`,
      input,
    );
  }

  /**
   * The sequence travels in the path and **only** in the path — the route
   * refuses it in the body, because one fact with two sources is how a record
   * gets updated against the wrong entry.
   */
  async resolveAgreementException(
    agreementId: string,
    sequence: number,
    input: { resolution: string },
  ): Promise<{ data: ElectronicAgreement }> {
    return this.patch(
      `/trust-evidence/agreements/${encodeURIComponent(agreementId)}/exceptions/${encodeURIComponent(String(sequence))}`,
      input,
    );
  }

  // ── Regulatory artefacts (Sprint 62.6.0) ──────────────────────────
  //
  // Every path below is relative to the module's single mount,
  // `/api/v1/regulatory-artifacts`. The envelopes are the pair 62.2.5 pinned —
  // `{ data }` for one entity, `{ data, total, page, page_size }` for a list —
  // so callers read lists through `unwrapResponse` + `extractPaginationMeta`
  // and single entities through `unwrapSingleResponse`.
  //
  // `page_size`, never `per_page`. 62.2.5 found `extractPaginationMeta` reading
  // a key 560 backend sites do not emit, so every paginated surface silently
  // showed the default while the server had said something else.
  //
  // Two deliberate departures from the list shape, both because the route is
  // not a page of records:
  //
  // - `exportRegulatoryArtifacts` returns `{ data, total }` with **no page** —
  //   it is the whole set the current filter selects, and a page boundary
  //   through an export would make "what would leave" unanswerable.
  // - `requestArtifactAccess` returns a decision and the event it wrote, not an
  //   entity. It creates no grant, by design.

  async listRegulatoryArtifacts(params?: {
    page?: number;
    page_size?: number;
    kind?: RegulatoryArtifactKind;
    access_tier?: ArtifactAccessTier;
    framework_code?: string;
    verification_status?: ArtifactVerificationStatus;
    fillable?: 'true' | 'false';
    search?: string;
  }): Promise<{ data: RegulatoryArtifact[]; total: number; page: number; page_size: number }> {
    return this.get(`/regulatory-artifacts${this.buildQuery(params)}`);
  }

  /** The custody-gated Restricted artifact catalog used when issuing grants. */
  async listGrantsCatalog(params?: {
    page?: number;
    page_size?: number;
    search?: string;
  }): Promise<{ data: RegulatoryArtifact[]; total: number; page: number; page_size: number }> {
    return this.get(`/regulatory-artifacts/grants/catalog${this.buildQuery(params)}`);
  }

  /**
   * The unclassified backlog — what the migration could not classify from the
   * evidence each record already carried, and therefore what a person still
   * owes a decision on.
   *
   * `verification_status` is not accepted: the server drops it, because a queue
   * of unclassified artefacts filtered to `verified` is a query that can only
   * ever return nothing and would read as an empty backlog.
   */
  async listArtifactVerificationQueue(params?: {
    page?: number;
    page_size?: number;
    kind?: RegulatoryArtifactKind;
    access_tier?: ArtifactAccessTier;
    framework_code?: string;
    fillable?: 'true' | 'false';
    search?: string;
  }): Promise<{
    /**
     * Sprint 64.29.0 — each row carries the disposition the surface groups it
     * by: resolvable by upgrading the pack, or genuinely a person's call.
     */
    data: DispositionedArtifact[];
    total: number;
    page: number;
    page_size: number;
    /**
     * The install drift the queue's size is a consequence of.
     *
     * On the list envelope rather than fetched separately, so the count and the
     * explanation for it come from one request and cannot describe two different
     * moments.
     *
     * **There is no `GET /regulatory-artifacts/pack-currency`, and there is no
     * client method for one.** This docblock advertised that route for two
     * passes; `routes.ts` deliberately did not create it and
     * `route-coverage.test.ts` records it as deleted. A standalone route was
     * written first, and the coverage guard immediately reported its api-client
     * method as called by no surface — a capability with no interface. The
     * honest fix was to delete it rather than invent a caller, because serving
     * the drift apart from the queue count it explains would let the two come
     * from two requests and therefore two moments. Gate E12.5, F9: a frontend
     * developer reads this file, not the backend route file, so a route named
     * only here is a route they will spend an afternoon failing to call.
     */
    pack_currency: PackCurrencyReport;
  }> {
    return this.get(`/regulatory-artifacts/verification-queue${this.buildQuery(params)}`);
  }

  /**
   * The rows an export would contain, selected by the same filter as the list.
   *
   * Every row above open tier that enters the set writes an `exported` access
   * event against the caller, so this is a recorded act rather than a preview.
   */
  async exportRegulatoryArtifacts(params?: {
    kind?: RegulatoryArtifactKind;
    access_tier?: ArtifactAccessTier;
    framework_code?: string;
    verification_status?: ArtifactVerificationStatus;
    fillable?: 'true' | 'false';
    search?: string;
  }): Promise<{
    // Sprint 64.39.0 — all four of R-23's keys. The route used to emit two, so
    // `extractPaginationMeta` handed any consumer a hard-coded `per_page: 25`
    // instead of saying it did not know. An export is one page containing
    // everything: `page` is 1 and `page_size` is `total`.
    data: RegulatoryArtifact[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/regulatory-artifacts/export${this.buildQuery(params)}`);
  }

  /** The grant register. Custody, not read — see `RestrictedAccessPage`. */
  async listArtifactAccessGrants(params?: {
    page?: number;
    page_size?: number;
    artifact_id?: string;
    grantee_user_id?: string;
    status?: ArtifactGrantStatus;
  }): Promise<{ data: ArtifactAccessGrant[]; total: number; page: number; page_size: number }> {
    return this.get(`/regulatory-artifacts/grants${this.buildQuery(params)}`);
  }

  /** Every attempt, allowed or refused. */
  async listArtifactAccessEvents(params?: {
    page?: number;
    page_size?: number;
    artifact_id?: string;
    actor_user_id?: string;
    kind?: ArtifactAccessEventKind;
  }): Promise<{ data: ArtifactAccessEvent[]; total: number; page: number; page_size: number }> {
    return this.get(`/regulatory-artifacts/access-events${this.buildQuery(params)}`);
  }

  async revokeArtifactAccessGrant(
    grantId: string,
    input: RevokeArtifactAccessRequest,
  ): Promise<{ data: ArtifactAccessGrant }> {
    return this.post(`/regulatory-artifacts/grants/${encodeURIComponent(grantId)}/revoke`, input);
  }

  /**
   * One artefact.
   *
   * A denial and a genuine miss are indistinguishable: same status, same code,
   * same message. A 403 would confirm the record exists, and for a restricted
   * artefact its existence is the disclosure.
   */
  async getRegulatoryArtifact(artifactId: string): Promise<{ data: RegulatoryArtifact }> {
    return this.get(`/regulatory-artifacts/${encodeURIComponent(artifactId)}`);
  }

  /** The same access decision as a read, recorded as a different event. */
  async downloadRegulatoryArtifact(artifactId: string): Promise<{ data: RegulatoryArtifact }> {
    return this.post(`/regulatory-artifacts/${encodeURIComponent(artifactId)}/download`, {});
  }

  async updateArtifactClassification(
    artifactId: string,
    input: UpdateArtifactClassificationRequest,
  ): Promise<{ data: RegulatoryArtifact }> {
    return this.patch(
      `/regulatory-artifacts/${encodeURIComponent(artifactId)}/classification`,
      input,
    );
  }

  /**
   * State why you need it. Creates **no grant** — a request that granted its own
   * access would produce a trail in which every access is self-authorised.
   */
  async requestArtifactAccess(
    artifactId: string,
    input: RequestArtifactAccessRequest,
  ): Promise<{
    data: {
      decision: ArtifactAccessDecision;
      event: ArtifactAccessEvent;
      max_grant_hours: number;
    };
  }> {
    return this.post(
      `/regulatory-artifacts/${encodeURIComponent(artifactId)}/access-request`,
      input,
    );
  }

  async grantArtifactAccess(
    artifactId: string,
    input: GrantArtifactAccessRequest,
  ): Promise<{ data: ArtifactAccessGrant }> {
    return this.post(`/regulatory-artifacts/${encodeURIComponent(artifactId)}/grants`, input);
  }

  /**
   * Whether this organisation permits restricted handling at all.
   *
   * On `/org-settings`, not on `/regulatory-artifacts`, and gated on
   * `platform:security:*` rather than on artifact custody — a custodian issues
   * grants, an administrator decides whether the organisation holds restricted
   * records in the first place.
   *
   * Returns the settings object bare, not in a `{ data }` envelope, matching
   * `getOrgEvidencePackDispatchPolicy` and every other org-settings route.
   */
  async getOrgRestrictedArtifactsPolicy(): Promise<RestrictedArtifactsOrgSettings> {
    return this.get('/org-settings/restricted-artifacts');
  }

  async updateOrgRestrictedArtifactsPolicy(
    enabled: boolean,
  ): Promise<RestrictedArtifactsOrgSettings> {
    // A boolean parameter rather than a payload object, because the backend
    // rejects a non-boolean rather than coercing it: `"false"` is how a form
    // posts a value it means, and coercion would switch a compliance control on
    // a serialisation detail.
    return this.put('/org-settings/restricted-artifacts', { enabled });
  }

  // ── Public data / payments / platform evidence (Sprint 62.7.0) ────
  //
  // Three modules, three mounts, one envelope pair: `{ data }` for a single
  // entity and `{ data, total, page, page_size }` for a list — `page_size`,
  // never `per_page`.
  //
  // **`GET /regimes` is NOT shaped like `getTrustRegimeCatalogue`.** 62.5.0
  // answers `{ data: { regimes, diagnostics } }`; all three routers here answer
  // `{ data: <regimes array>, diagnostics }`, with the diagnostics on the
  // envelope beside `data`. So these are read with `unwrapResponse` (the array
  // unwrapper) and the diagnostics off the envelope — copying 62.5.0's
  // `unwrapSingleResponse` would return `[]` for every tenant and every surface
  // would say "no regime installed".
  //
  // The regime entry shape differs too: 62.5.0 returns `{ source, regime }`,
  // these return `ResolvedEvidenceRegime` — `{ regime, framework_code,
  // pack_code, pack_version, framework_version }`, provenance flattened onto the
  // entry. `r.source.framework_code` does not exist here.

  async listPublicDataRegimes(): Promise<EvidenceRegimeListResponse> {
    return this.get('/public-data/regimes');
  }

  async listPublicDatasets(params?: {
    page?: number;
    page_size?: number;
    status?: PublicDatasetStatus;
    steward_user_id?: string;
    registry_item_id?: string;
  }): Promise<PagedResponse<PublicDataset>> {
    return this.get(`/public-data/datasets${this.buildQuery(params)}`);
  }

  async createPublicDataset(input: CreatePublicDatasetInput): Promise<{ data: PublicDataset }> {
    return this.post('/public-data/datasets', input);
  }

  async getPublicDataset(datasetId: string): Promise<{ data: PublicDataset }> {
    return this.get(`/public-data/datasets/${encodeURIComponent(datasetId)}`);
  }

  /**
   * `dataset_code` and `regime_id` are absent from the patch by design — the
   * route's `.strict()` schema turns a body carrying either into a 400 naming
   * the field, because the codes a dataset records mean something under one
   * regime's vocabulary and nothing under another.
   */
  async updatePublicDataset(
    datasetId: string,
    input: UpdatePublicDatasetInput,
  ): Promise<{ data: PublicDataset }> {
    return this.patch(`/public-data/datasets/${encodeURIComponent(datasetId)}`, input);
  }

  async listDatasetDictionaryVersions(
    datasetId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<PagedResponse<PublicDatasetDictionaryVersion>> {
    return this.get(
      `/public-data/datasets/${encodeURIComponent(datasetId)}/dictionary${this.buildQuery(params)}`,
    );
  }

  async createDatasetDictionaryVersion(
    datasetId: string,
    input: { fields: PublicDataDictionaryField[]; reference_mapping_note?: string | null },
  ): Promise<{ data: PublicDatasetDictionaryVersion }> {
    return this.post(`/public-data/datasets/${encodeURIComponent(datasetId)}/dictionary`, input);
  }

  /**
   * The data owner's confirmation. Two facts change, so the response carries
   * both — returning only the version would leave the caller unable to see that
   * the dataset's `current_dictionary_version_id` moved.
   */
  async confirmDatasetDictionaryVersion(
    datasetId: string,
    versionId: string,
  ): Promise<{ data: { version: PublicDatasetDictionaryVersion; dataset: PublicDataset } }> {
    return this.post(
      `/public-data/datasets/${encodeURIComponent(datasetId)}/dictionary/${encodeURIComponent(versionId)}/confirm`,
      {},
    );
  }

  async listSharingArrangements(params?: {
    page?: number;
    page_size?: number;
    dataset_id?: string;
    status?: SharingArrangementStatus;
    channel_code?: string;
  }): Promise<PagedResponse<PublicDataSharingArrangement>> {
    return this.get(`/public-data/arrangements${this.buildQuery(params)}`);
  }

  async createSharingArrangement(
    input: CreateSharingArrangementInput,
  ): Promise<{ data: PublicDataSharingArrangement }> {
    return this.post('/public-data/arrangements', input);
  }

  async getSharingArrangement(
    arrangementId: string,
  ): Promise<{ data: PublicDataSharingArrangement }> {
    return this.get(`/public-data/arrangements/${encodeURIComponent(arrangementId)}`);
  }

  async updateSharingArrangement(
    arrangementId: string,
    input: Partial<Omit<CreateSharingArrangementInput, 'dataset_id'>>,
  ): Promise<{ data: PublicDataSharingArrangement }> {
    return this.patch(`/public-data/arrangements/${encodeURIComponent(arrangementId)}`, input);
  }

  async approveSharingArrangement(
    arrangementId: string,
  ): Promise<{ data: PublicDataSharingArrangement }> {
    return this.post(`/public-data/arrangements/${encodeURIComponent(arrangementId)}/approve`, {});
  }

  async reviewSharingArrangement(
    arrangementId: string,
    input: {
      reviewed_at: string;
      review_due_at?: string | null;
      status?: 'active' | 'under_review' | 'terminated';
    },
  ): Promise<{ data: PublicDataSharingArrangement }> {
    return this.post(
      `/public-data/arrangements/${encodeURIComponent(arrangementId)}/review`,
      input,
    );
  }

  async listShareReceipts(params?: {
    page?: number;
    page_size?: number;
    arrangement_id?: string;
    dataset_id?: string;
    status?: ShareReceiptStatus;
  }): Promise<PagedResponse<PublicDataShareReceipt>> {
    return this.get(`/public-data/receipts${this.buildQuery(params)}`);
  }

  /**
   * `dataset_id` and `dictionary_version_id` are absent by design: both are
   * derived from the arrangement, and a receipt naming a description the
   * arrangement was not made against would be a transfer whose provenance
   * disagrees with its own authority to exist.
   */
  async createShareReceipt(input: {
    arrangement_id: string;
    transferred_at: string;
    transfer_reference?: string | null;
  }): Promise<{ data: PublicDataShareReceipt }> {
    return this.post('/public-data/receipts', input);
  }

  async acknowledgeShareReceipt(
    receiptId: string,
    input: {
      acknowledged_at: string;
      acknowledgement_reference?: string | null;
      acknowledgement_evidence?: AttachmentRef | null;
    },
  ): Promise<{ data: PublicDataShareReceipt }> {
    return this.post(`/public-data/receipts/${encodeURIComponent(receiptId)}/acknowledge`, input);
  }

  async refuseShareReceipt(
    receiptId: string,
    input: { refusal_reason: string },
  ): Promise<{ data: PublicDataShareReceipt }> {
    return this.post(`/public-data/receipts/${encodeURIComponent(receiptId)}/refuse`, input);
  }

  async listReleaseReadiness(params?: {
    page?: number;
    page_size?: number;
    dataset_id?: string;
    outcome?: ReleaseReadinessOutcome;
  }): Promise<PagedResponse<OpenDataReleaseReadiness>> {
    return this.get(`/public-data/readiness${this.buildQuery(params)}`);
  }

  /**
   * `outcome` is absent, and was here.
   *
   * The route's body is `.strict()` and does not accept it, so declaring it
   * optional here described a request the server answers with a 400 naming the
   * field — which is what `OpenDataReleasesPage` sent on every attempt to
   * record an assessment. Every assessment begins `pending`; the outcome has
   * one writer, `concludeReleaseReadiness` below.
   */
  async createReleaseReadiness(input: {
    dataset_id: string;
    dictionary_version_id: string;
    personal_data_present: boolean;
    personal_data_rationale: string;
    mitigation_note?: string | null;
    reassess_due_at?: string | null;
  }): Promise<{ data: OpenDataReleaseReadiness }> {
    return this.post('/public-data/readiness', input);
  }

  /**
   * The second act on an assessment, and the only writer of `outcome`.
   *
   * `pending` is excluded by the route: concluding is what ENDS that state, so
   * a request to conclude something as unfinished has no meaning. `blocked`
   * with no `mitigation_note` is refused with `READINESS_RATIONALE_REQUIRED`,
   * and an assessment some release already names is refused 409 — take the
   * release down instead.
   *
   * It had no method here at all, so nothing in the product could move an
   * assessment off `pending`, and publishing — which requires a cleared and
   * signed-off assessment — was unreachable by construction.
   */
  async concludeReleaseReadiness(
    readinessId: string,
    input: {
      outcome: Exclude<ReleaseReadinessOutcome, 'pending'>;
      mitigation_note?: string | null;
    },
  ): Promise<{ data: OpenDataReleaseReadiness }> {
    return this.post(`/public-data/readiness/${encodeURIComponent(readinessId)}/conclude`, input);
  }

  /**
   * `signed_off_by_user_id` is absent: the signatory is the acting session,
   * never a name the caller types. A sign-off somebody can attribute to a
   * colleague is not a sign-off.
   *
   * `public_data:write`, not `:release` — signing off the personal-data test is
   * an assessment act and authorises nothing on its own.
   */
  async signOffReleaseReadiness(
    readinessId: string,
    input?: { signed_off_at?: string | null },
  ): Promise<{ data: OpenDataReleaseReadiness }> {
    return this.post(
      `/public-data/readiness/${encodeURIComponent(readinessId)}/sign-off`,
      input ?? {},
    );
  }

  async listOpenDataReleases(params?: {
    page?: number;
    page_size?: number;
    dataset_id?: string;
    status?: OpenDataReleaseStatus;
  }): Promise<PagedResponse<OpenDataRelease>> {
    return this.get(`/public-data/releases${this.buildQuery(params)}`);
  }

  /** `public_data:release`. The effect leaves the organisation. */
  async createOpenDataRelease(input: {
    dataset_id: string;
    readiness_id: string;
    channel_code: string;
    location_reference?: string | null;
    published_at?: string | null;
  }): Promise<{ data: OpenDataRelease }> {
    return this.post('/public-data/releases', input);
  }

  /** `public_data:release` as well — an operator who may not publish may not unpublish. */
  async takedownOpenDataRelease(
    releaseId: string,
    input: { takedown_reason: string; taken_down_at?: string | null },
  ): Promise<{ data: OpenDataRelease }> {
    return this.post(`/public-data/releases/${encodeURIComponent(releaseId)}/takedown`, input);
  }

  async listPublicDataQualityIssues(params?: {
    page?: number;
    page_size?: number;
    dataset_id?: string;
    status?: QualityIssueStatus;
  }): Promise<PagedResponse<PublicDataQualityIssue>> {
    return this.get(`/public-data/quality-issues${this.buildQuery(params)}`);
  }

  async createPublicDataQualityIssue(input: {
    dataset_id: string;
    issue_kind_code?: string | null;
    summary: string;
    detail?: string | null;
    reported_by_label?: string | null;
    reported_at: string;
  }): Promise<{ data: PublicDataQualityIssue }> {
    return this.post('/public-data/quality-issues', input);
  }

  async listPublicDataCorrections(params?: {
    page?: number;
    page_size?: number;
    dataset_id?: string;
    status?: CorrectionStatus;
  }): Promise<PagedResponse<PublicDataCorrection>> {
    return this.get(`/public-data/corrections${this.buildQuery(params)}`);
  }

  /**
   * `notified_recipients` and `recipient_snapshot_at` are absent and the route
   * rejects a body carrying either with a 400 that names the field. The
   * recipient list is **derived** by the service from the receipts and releases
   * that exist at correction time — propagation is computed, never remembered.
   */
  async createPublicDataCorrection(input: {
    dataset_id: string;
    quality_issue_id?: string | null;
    description: string;
    corrected_at: string;
    confirmed_by_user_id?: string | null;
  }): Promise<{ data: PublicDataCorrection }> {
    return this.post('/public-data/corrections', input);
  }

  async notifyPublicDataCorrection(
    correctionId: string,
    input: {
      source_kind: 'share_receipt' | 'open_data_release';
      source_id: string;
      notified_at?: string | null;
      notification_reference?: string | null;
    },
  ): Promise<{ data: PublicDataCorrection }> {
    return this.post(`/public-data/corrections/${encodeURIComponent(correctionId)}/notify`, input);
  }

  /** R-33 — the export is rendered by `UnifiedExportService` and returns its reference. */
  async exportPublicData(params?: {
    kind?: 'datasets' | 'arrangements' | 'receipts' | 'releases' | 'corrections';
    format?: 'csv' | 'xlsx' | 'pdf' | 'docx';
    dataset_id?: string;
    status?: string;
  }): Promise<{ data: AttachmentRef }> {
    return this.get(`/public-data/export${this.buildQuery(params)}`);
  }

  // ── Payments evidence ────────────────────────────────────────────

  async listPaymentsEvidenceRegimes(): Promise<EvidenceRegimeListResponse> {
    return this.get('/payments-evidence/regimes');
  }

  async listPaymentServices(params?: {
    page?: number;
    page_size?: number;
    status?: PaymentServiceStatus;
    service_type_code?: string;
    regime_id?: string;
  }): Promise<PagedResponse<PaymentService>> {
    return this.get(`/payments-evidence/services${this.buildQuery(params)}`);
  }

  /**
   * `obligations` carries the two identifying fields only. `requirement_title`
   * is resolved server-side from the installed framework's own requirements and
   * the route rejects a body carrying it — otherwise a `:write` holder could
   * register a service displaying obligation text that appears in no instrument.
   */
  async createPaymentService(input: {
    regime_id: string;
    service_code: string;
    name: string;
    description?: string | null;
    service_type_code: string;
    obligations?: PaymentObligationRequest[];
    owner_user_id?: string | null;
    status?: PaymentServiceStatus;
  }): Promise<{ data: PaymentService }> {
    return this.post('/payments-evidence/services', input);
  }

  async getPaymentService(serviceId: string): Promise<{ data: PaymentService }> {
    return this.get(`/payments-evidence/services/${encodeURIComponent(serviceId)}`);
  }

  async updatePaymentService(
    serviceId: string,
    input: {
      name?: string;
      description?: string | null;
      service_type_code?: string;
      obligations?: PaymentObligationRequest[];
      owner_user_id?: string | null;
      status?: PaymentServiceStatus;
    },
  ): Promise<{ data: PaymentService }> {
    return this.patch(`/payments-evidence/services/${encodeURIComponent(serviceId)}`, input);
  }

  /** `data` is null until the service has a mapping recorded. */
  async getPaymentLogMapping(
    serviceId: string,
  ): Promise<{ data: PaymentTransactionLogMapping | null }> {
    return this.get(`/payments-evidence/services/${encodeURIComponent(serviceId)}/log-mapping`);
  }

  async putPaymentLogMapping(
    serviceId: string,
    input: {
      logged_elements: string[];
      retention_location: string;
      retention_days?: number | null;
      retention_duty_code?: string | null;
      notes?: string | null;
    },
  ): Promise<{ data: PaymentTransactionLogMapping }> {
    return this.put(
      `/payments-evidence/services/${encodeURIComponent(serviceId)}/log-mapping`,
      input,
    );
  }

  async listEkycVerifications(params?: {
    page?: number;
    page_size?: number;
    payment_service_id?: string;
    customer_reference?: string;
    outcome?: EkycOutcome;
    retention_state?: RetentionState;
  }): Promise<PagedResponse<EkycVerificationView>> {
    return this.get(`/payments-evidence/verifications${this.buildQuery(params)}`);
  }

  async createEkycVerification(
    input: CreateEkycVerificationInput,
  ): Promise<{ data: EkycVerificationView }> {
    return this.post('/payments-evidence/verifications', input);
  }

  async getEkycVerification(verificationId: string): Promise<{ data: EkycVerificationView }> {
    return this.get(`/payments-evidence/verifications/${encodeURIComponent(verificationId)}`);
  }

  /**
   * The handles, in full.
   *
   * `payments_evidence:read` **and** `payments_evidence:unmask` — a separate
   * route rather than a `?unmask=true` flag, so the audit row cannot be made
   * conditional on a query parameter the caller sets. `:unmask` is held by no
   * system role by default.
   */
  async readEkycProtectedReferences(
    verificationId: string,
  ): Promise<{ data: ProtectedReferenceView[] }> {
    return this.get(
      `/payments-evidence/verifications/${encodeURIComponent(verificationId)}/references`,
    );
  }

  async reviewEkycVerification(
    verificationId: string,
    input: { approve_lawful_basis: boolean },
  ): Promise<{ data: EkycVerificationView }> {
    return this.post(
      `/payments-evidence/verifications/${encodeURIComponent(verificationId)}/review`,
      input,
    );
  }

  /** `payments_evidence:reconcile` — disposing and extending both settle a duty conflict. */
  async executeEkycRetention(
    verificationId: string,
    input: { action: 'dispose' | 'extend'; note: string; disposal_evidence?: AttachmentRef | null },
  ): Promise<{ data: EkycVerificationView }> {
    return this.post(
      `/payments-evidence/verifications/${encodeURIComponent(verificationId)}/retention`,
      input,
    );
  }

  async listPaymentDataSecurityRecords(params?: {
    page?: number;
    page_size?: number;
    payment_service_id?: string;
    status?: CardEvidenceStatus;
  }): Promise<PagedResponse<PaymentDataSecurityRecordView>> {
    return this.get(`/payments-evidence/data-security${this.buildQuery(params)}`);
  }

  async createPaymentDataSecurityRecord(input: {
    payment_service_id: string;
    control_code?: string | null;
    control_description: string;
    evidence_summary: string;
    evidence?: AttachmentRef | null;
    protected_references?: ProtectedReferenceInput[];
    last_reviewed_at?: string | null;
    next_review_due_at?: string | null;
    owner_user_id?: string | null;
  }): Promise<{ data: PaymentDataSecurityRecordView }> {
    return this.post('/payments-evidence/data-security', input);
  }

  /**
   * The completeness gap: due diligence records that link to no obligation.
   *
   * Its own route, registered before `/diligence-links/:id` on the server — the
   * other way round `gaps` would be read as an id and this report would answer
   * 404 forever.
   */
  async listDiligenceGaps(params?: {
    page?: number;
    page_size?: number;
  }): Promise<PagedResponse<AmlDueDiligenceLink>> {
    return this.get(`/payments-evidence/diligence-links/gaps${this.buildQuery(params)}`);
  }

  async listDiligenceLinks(params?: {
    page?: number;
    page_size?: number;
    payment_service_id?: string;
    customer_reference?: string;
    status?: CddLinkStatus;
    /** Comma-separated, deduplicated, 1–100 record ids (fix-sprint-20261003-0545 F3). */
    ids?: string;
  }): Promise<PagedResponse<AmlDueDiligenceLink>> {
    return this.get(`/payments-evidence/diligence-links${this.buildQuery(params)}`);
  }

  /** `status` is computed from `obligations.length` and the route rejects a body sending one. */
  async createDiligenceLink(input: {
    payment_service_id: string;
    customer_reference: string;
    diligence_reference: string;
    performed_at: string;
    diligence_level_code?: string | null;
    obligations?: PaymentObligationRequest[];
    refresh_due_at?: string | null;
    ekyc_verification_ids?: string[];
    notes?: string | null;
  }): Promise<{ data: AmlDueDiligenceLink }> {
    return this.post('/payments-evidence/diligence-links', input);
  }

  async confirmDiligenceSufficiency(
    linkId: string,
    input?: { note?: string | null },
  ): Promise<{ data: AmlDueDiligenceLink }> {
    return this.post(
      `/payments-evidence/diligence-links/${encodeURIComponent(linkId)}/confirm`,
      input ?? {},
    );
  }

  /**
   * Sprint 62.46.0 gate E12.5 pass 2 (P2-B) — open a due-diligence refresh on
   * a link directly from the diligence register, without a case. The same
   * record write and audit row the case path makes; the diligence link never
   * names a case. Needs `payments_evidence:write`.
   */
  async requestDiligenceRefresh(
    linkId: string,
    body: { refresh_due_on: string },
  ): Promise<{ data: AmlDueDiligenceLink }> {
    return this.post(
      `/payments-evidence/diligence-links/${encodeURIComponent(linkId)}/refresh`,
      body,
    );
  }

  async listRetentionReconciliations(params?: {
    page?: number;
    page_size?: number;
    payment_service_id?: string;
    status?: RetentionReconciliationStatus;
  }): Promise<PagedResponse<RetentionReconciliation>> {
    return this.get(`/payments-evidence/reconciliations${this.buildQuery(params)}`);
  }

  /**
   * No `status` and no approval object in the body. A record that could be
   * created `complete` would need no second signature at all.
   */
  async createRetentionReconciliation(input: {
    payment_service_id: string;
    regime_id: string;
    data_element: string;
    retention_duty_code: string;
    retention_position: string;
    minimisation_position: string;
    reconciliation_statement: string;
    review_due_at?: string | null;
    /**
     * Sprint 62.46.0 (AC13) — a record class of the installed AML
     * records-retention scope; the server refuses an undeclared one with 400
     * `unknown_option` and joins the scope for it onto every read.
     */
    record_class?: string | null;
  }): Promise<{ data: RetentionReconciliation }> {
    return this.post('/payments-evidence/reconciliations', input);
  }

  /**
   * One function signs. `payments_evidence:reconcile`, because this is the
   * signature itself — and `status` moves to `complete` only when **both**
   * named functions have signed.
   */
  async approveRetentionReconciliation(
    reconciliationId: string,
    input: { approval_function: 'financial_crime' | 'privacy'; note?: string | null },
  ): Promise<{ data: RetentionReconciliation }> {
    return this.post(
      `/payments-evidence/reconciliations/${encodeURIComponent(reconciliationId)}/approve`,
      input,
    );
  }

  async listPaymentExceptions(params?: {
    page?: number;
    page_size?: number;
    status?: PaymentExceptionStatus;
    payment_service_id?: string;
    raised_by_system?: 'true' | 'false';
  }): Promise<PagedResponse<PaymentEvidenceException>> {
    return this.get(`/payments-evidence/exceptions${this.buildQuery(params)}`);
  }

  /**
   * `raised_by_system` is absent and rejected: it is the difference between
   * "the platform detected a conflict" and "somebody wrote one down", and a
   * client able to set it could dress its own observation as a machine finding.
   */
  async createPaymentException(input: {
    payment_service_id: string;
    exception_kind_code?: string | null;
    summary: string;
    detail?: string | null;
    ekyc_verification_id?: string | null;
    due_diligence_link_id?: string | null;
    retention_reconciliation_id?: string | null;
    assigned_to_user_id?: string | null;
    linked_incident_ids?: string[];
  }): Promise<{ data: PaymentEvidenceException }> {
    return this.post('/payments-evidence/exceptions', input);
  }

  /** `payments_evidence:reconcile` — deciding an exception is not an ordinary write. */
  async resolvePaymentException(
    exceptionId: string,
    input: {
      status: 'resolved' | 'accepted';
      resolution_decision: string;
      linked_incident_ids?: string[];
    },
  ): Promise<{ data: PaymentEvidenceException }> {
    return this.post(
      `/payments-evidence/exceptions/${encodeURIComponent(exceptionId)}/resolve`,
      input,
    );
  }

  /**
   * The register, redacted for this viewer.
   *
   * `:read` alone, deliberately — the export carries what its list surfaces
   * carry and its protected handles are redacted for a caller with no `:unmask`
   * grant. The response is not a `{ data }` envelope alone: `totals` and
   * `unmasked` ride beside it.
   */
  async exportPaymentsEvidence(params?: {
    format?: 'csv' | 'xlsx' | 'pdf' | 'docx';
  }): Promise<{ data: AttachmentRef; totals: Record<string, number>; unmasked: boolean }> {
    return this.get(`/payments-evidence/export${this.buildQuery(params)}`);
  }

  // ── Platform evidence ────────────────────────────────────────────

  async listPlatformEvidenceRegimes(): Promise<EvidenceRegimeListResponse> {
    return this.get('/platform-evidence/regimes');
  }

  async listPlatformSellers(params?: {
    page?: number;
    page_size?: number;
    status?: string;
    state_code?: string;
    vendor_id?: string;
    seller_code?: string;
  }): Promise<PagedResponse<PlatformSeller>> {
    return this.get(`/platform-evidence/sellers${this.buildQuery(params)}`);
  }

  /**
   * `verified` is absent by design — a seller that could be created already
   * verified would make the decision that stamps who decided optional.
   */
  async createPlatformSeller(input: {
    regime_id: string;
    seller_code: string;
    display_name: string;
    state_code: string;
    tax_reference?: string | null;
    vendor_id?: string | null;
    registration_evidence?: AttachmentRef | null;
    status?: 'draft' | 'submitted';
  }): Promise<{ data: PlatformSeller }> {
    return this.post('/platform-evidence/sellers', input);
  }

  async getPlatformSeller(sellerId: string): Promise<{ data: PlatformSeller }> {
    return this.get(`/platform-evidence/sellers/${encodeURIComponent(sellerId)}`);
  }

  /**
   * `status` is not in the patch and the route rejects a body carrying it.
   * `state_code` **is**: the regime's view of a seller changes without the
   * platform's own registration decision changing, and that is the distinction
   * this module preserves.
   */
  async updatePlatformSeller(
    sellerId: string,
    input: {
      display_name?: string;
      state_code?: string;
      tax_reference?: string | null;
      vendor_id?: string | null;
      registration_evidence?: AttachmentRef | null;
    },
  ): Promise<{ data: PlatformSeller }> {
    return this.patch(`/platform-evidence/sellers/${encodeURIComponent(sellerId)}`, input);
  }

  async verifyPlatformSeller(sellerId: string): Promise<{ data: PlatformSeller }> {
    return this.post(`/platform-evidence/sellers/${encodeURIComponent(sellerId)}/verify`, {});
  }

  async rejectPlatformSeller(
    sellerId: string,
    input: { reason: string },
  ): Promise<{ data: PlatformSeller }> {
    return this.post(`/platform-evidence/sellers/${encodeURIComponent(sellerId)}/reject`, input);
  }

  async listPlatformTransactions(params?: {
    page?: number;
    page_size?: number;
    seller_id?: string;
    transaction_reference?: string;
    occurred_from?: string;
    occurred_to?: string;
  }): Promise<PagedResponse<PlatformTransactionRecord>> {
    return this.get(`/platform-evidence/transactions${this.buildQuery(params)}`);
  }

  /**
   * `declared_amount` and `declared_currency` are **strings**, not numbers.
   * The value is what a source system stated, carried verbatim; coercing it
   * would be the platform deciding what a figure means before anybody compares
   * it.
   */
  async createPlatformTransaction(input: {
    seller_id: string;
    transaction_reference: string;
    occurred_at: string;
    declared_amount: string;
    declared_currency: string;
    source_system: string;
    transaction_kind_code?: string | null;
    notes?: string | null;
  }): Promise<{ data: PlatformTransactionRecord }> {
    return this.post('/platform-evidence/transactions', input);
  }

  async listPlatformDocuments(params?: {
    page?: number;
    page_size?: number;
    seller_id?: string;
    transaction_record_id?: string;
    document_kind_code?: string;
    status?: string;
  }): Promise<PagedResponse<PlatformDocumentEvidence>> {
    return this.get(`/platform-evidence/documents${this.buildQuery(params)}`);
  }

  async createPlatformDocument(input: {
    seller_id: string;
    transaction_record_id?: string | null;
    document_kind_code: string;
    document_reference: string;
    issued_at?: string | null;
    declared_amount?: string | null;
    declared_currency?: string | null;
    lodgement_reference?: string | null;
    lodged_at?: string | null;
    evidence?: AttachmentRef | null;
    replaces_document_id?: string | null;
  }): Promise<{ data: PlatformDocumentEvidence }> {
    return this.post('/platform-evidence/documents', input);
  }

  async cancelPlatformDocument(
    documentId: string,
    input: { reason: string },
  ): Promise<{ data: PlatformDocumentEvidence }> {
    return this.post(
      `/platform-evidence/documents/${encodeURIComponent(documentId)}/cancel`,
      input,
    );
  }

  /**
   * Runs and exceptions carry `determination_caveats` **on the envelope**,
   * beside `data` — one entry per regime the rows were recorded under, holding
   * the pack's own statement of what the organisation's records are and are
   * not. It is resolved live rather than stored, and a regime that no longer
   * resolves produces a null caveat with a reason naming the framework to
   * repair, so "no caveat" and "the caveat could not be found" never render the
   * same.
   */
  async listReconciliationRuns(params?: {
    page?: number;
    page_size?: number;
    status?: string;
    seller_id?: string;
  }): Promise<PagedResponse<ReconciliationRun> & { determination_caveats: DeterminationCaveat[] }> {
    return this.get(`/platform-evidence/runs${this.buildQuery(params)}`);
  }

  /**
   * `exception_kind_map` is required and every condition must be present. The
   * platform detects the condition and the **regime** names it — a run that
   * could not name a condition would find it and say nothing.
   *
   * ## `platform_role_code` was missing from this type, and the run form
   *
   * `StartRunBody` in `platform-evidence/routes.ts` is `.strict()` and has
   * required `platform_role_code` since the backend stream added it. This type
   * did not declare it and no frontend file mentioned it, so **every** attempt to
   * start a reconciliation run from the UI was a 400 on a required-field
   * violation — the module's headline action, unusable on any tenant.
   *
   * It was invisible to the suite because every request-shape assertion in these
   * modules is `toMatchObject`, which asserts a **subset** and cannot detect a
   * missing key. The tests are now pinned to the exact key set for the mutations
   * the acceptance criteria name.
   *
   * The value comes from the regime's `platform_role` vocabulary — a required
   * vocabulary of the `platform` domain profile, so any usable regime declares
   * it — and is stated, never inferred: a platform acting as intermediary for
   * some sellers and as direct seller for others reconciles those populations
   * under different duties, and a run that does not say which role it was
   * reconciling cannot be read back.
   */
  async startReconciliationRun(input: {
    regime_id: string;
    platform_role_code: string;
    period_start: string;
    period_end: string;
    seller_id?: string | null;
    exception_kind_map: Record<ReconciliationCondition, string>;
  }): Promise<{
    data: { run: ReconciliationRun; exceptions: ReconciliationException[] };
    determination_caveats: DeterminationCaveat[];
  }> {
    return this.post('/platform-evidence/runs', input);
  }

  async getReconciliationRun(
    runId: string,
  ): Promise<{ data: ReconciliationRun; determination_caveats: DeterminationCaveat[] }> {
    return this.get(`/platform-evidence/runs/${encodeURIComponent(runId)}`);
  }

  async listReconciliationExceptions(params?: {
    page?: number;
    page_size?: number;
    run_id?: string;
    seller_id?: string;
    status?: string;
    exception_kind_code?: string;
  }): Promise<
    PagedResponse<ReconciliationException> & { determination_caveats: DeterminationCaveat[] }
  > {
    return this.get(`/platform-evidence/exceptions${this.buildQuery(params)}`);
  }

  async resolveReconciliationException(
    exceptionId: string,
    input: {
      status: ReconciliationExceptionStatus;
      resolution_note?: string | null;
      assigned_to_user_id?: string | null;
    },
  ): Promise<{ data: ReconciliationException; determination_caveats: DeterminationCaveat[] }> {
    return this.post(
      `/platform-evidence/exceptions/${encodeURIComponent(exceptionId)}/resolve`,
      input,
    );
  }

  async listAuthorityCorrespondence(params?: {
    page?: number;
    page_size?: number;
    status?: string;
    authority_request_id?: string;
  }): Promise<PagedResponse<AuthorityCorrespondence>> {
    return this.get(`/platform-evidence/correspondence${this.buildQuery(params)}`);
  }

  /**
   * `platform_evidence:respond`. Opening, assembling and answering are the only
   * acts in this module whose effect an authority reads — reading the register
   * stays `:read`, because seeing what was answered is not answering.
   */
  async createAuthorityCorrespondence(input: {
    authority_request_id: string;
    regime_id: string;
    seller_ids?: string[];
    reconciliation_exception_ids?: string[];
    document_evidence_ids?: string[];
  }): Promise<{ data: AuthorityCorrespondence }> {
    return this.post('/platform-evidence/correspondence', input);
  }

  async assembleAuthorityCorrespondence(
    correspondenceId: string,
  ): Promise<{ data: AuthorityCorrespondence }> {
    return this.post(
      `/platform-evidence/correspondence/${encodeURIComponent(correspondenceId)}/assemble`,
      {},
    );
  }

  async respondAuthorityCorrespondence(
    correspondenceId: string,
    input: { response_note: string },
  ): Promise<{ data: AuthorityCorrespondence }> {
    return this.post(
      `/platform-evidence/correspondence/${encodeURIComponent(correspondenceId)}/respond`,
      input,
    );
  }

  async exportPlatformEvidence(params?: {
    kind?: 'sellers' | 'transactions' | 'documents' | 'runs' | 'exceptions';
    format?: 'csv' | 'xlsx' | 'pdf' | 'docx';
    seller_id?: string;
    run_id?: string;
    status?: string;
  }): Promise<{ data: AttachmentRef }> {
    return this.get(`/platform-evidence/export${this.buildQuery(params)}`);
  }

  // ── Sprint 62.8.0 — manufacturing operations ─────────────────────
  //
  // Mounted at `/api/v1/manufacturing`. One namespace for five sub-domains,
  // because the product master is shared by all of them.
  //
  // Three domains declare `determination_caveat_required`, so their list and
  // detail responses carry `determination_caveats` **beside** `data` — the return
  // types below say so, rather than leaving a caller to discover it. The
  // traceability domain carries none, and that is a statement rather than an
  // omission: a trace ledger records events and determines nothing.

  async listManufacturingRegimes(params?: {
    domain?:
      | 'product_conformity'
      | 'product_traceability'
      | 'producer_responsibility'
      | 'substance_control';
  }): Promise<EvidenceRegimeListResponse> {
    return this.get(`/manufacturing/regimes${this.buildQuery(params)}`);
  }

  /**
   * Sprint 62.49.0 gate E12.5 pass 2 (contract b) — whether an installed,
   * operative framework declares the food registers, read under
   * `manufacturing_product:read`, so the product and batch registers decide
   * whether to show their food fields without holding the food read.
   */
  async getManufacturingFoodSurfaces(): Promise<{ data: ManufacturingFoodSurfacesView }> {
    return this.get('/manufacturing/food-surfaces');
  }

  /**
   * Sprint 62.49.0 gate E12.5 pass 3 (P2-C) — the recall trigger sources the
   * installed recall templates declare, with their EN/VI labels, under
   * `manufacturing_recall:read` (never the food read).
   */
  async listManufacturingFoodTriggerSources(): Promise<{
    data: ManufacturingRecallTriggerSourceView[];
    /** The whole vocabulary on one page: `total` equals `data.length`, `page` is 1. */
    total?: number;
    page?: number;
    page_size?: number;
  }> {
    return this.get('/manufacturing/food-trigger-sources');
  }

  async listManufacturingProducts(params?: {
    page?: number;
    page_size?: number;
    lifecycle_state?: ProductLifecycleState;
    commercial_role?: ProductCommercialRole;
    q?: string;
  }): Promise<PagedResponse<ManufacturingProduct>> {
    return this.get(`/manufacturing/products${this.buildQuery(params)}`);
  }

  async getManufacturingProduct(productId: string): Promise<{ data: ManufacturingProduct }> {
    return this.get(`/manufacturing/products/${encodeURIComponent(productId)}`);
  }

  /**
   * `regime_id` is required whenever `risk_group_codes` is non-empty.
   *
   * A group code with no regime cannot be validated, and an unvalidated code is a
   * value nothing declares — rendering later as a blank row in a control that
   * looks finished (62.0.11, 120 times).
   */
  async createManufacturingProduct(input: {
    product_code: string;
    name: string;
    description?: string;
    commercial_role: ProductCommercialRole;
    lifecycle_state: ProductLifecycleState;
    tariff_classification?: string;
    risk_group_codes?: string[];
    regime_id?: string;
    identifiers?: TraceIdentifier[];
    supplier_vendor_ids?: string[];
  }): Promise<{ data: ManufacturingProduct }> {
    return this.post('/manufacturing/products', input);
  }

  /**
   * No `product_code`, and that is the contract rather than an omission.
   *
   * `PRODUCT_PATCH_FIELDS` in `product-service.ts` is a whitelist and
   * `product_code` is not on it, so the service **drops** the field instead of
   * refusing the request. `UpdateProductBody` is `CreateProductBody.partial()`,
   * so the route's Zod schema accepts the key and the caller gets a 200 holding
   * the unchanged record — which is exactly how the product register came to
   * offer an editable code field, send it, and raise a success toast over a
   * rename that never happened.
   *
   * Declaring the key here would restore that: the type is the only place a
   * caller can learn the field is unwritable before shipping. A rename is a new
   * product, because the code is what every batch, conformity assessment and
   * corrective action already recorded points at.
   */
  async updateManufacturingProduct(
    productId: string,
    input: Partial<{
      name: string;
      /** `null` unsets a recorded description; an absent key leaves it. */
      description: string | null;
      commercial_role: ProductCommercialRole;
      lifecycle_state: ProductLifecycleState;
      /** `null` unsets a recorded classification; an absent key leaves it. */
      tariff_classification: string | null;
      risk_group_codes: string[];
      regime_id: string;
      identifiers: TraceIdentifier[];
      supplier_vendor_ids: string[];
      /**
       * Sprint 62.49.0 (AC6, AC21) — the product's food facts, each nullable; an
       * absent key keeps what is recorded and `food: null` clears the whole
       * object. The expiry-date statement's author is the session (no
       * `*_stated_by` key exists to send).
       */
      food: Partial<
        Pick<
          ProductFoodFacts,
          | 'brand'
          | 'packaging'
          | 'ingredients'
          | 'shelf_life_rule'
          | 'standards_applied'
          | 'dossier_attachment_ids'
          | 'has_expiry_date'
          | 'expiry_date_not_required'
        >
      > | null;
    }>,
  ): Promise<{ data: ManufacturingProduct }> {
    return this.patch(`/manufacturing/products/${encodeURIComponent(productId)}`, input);
  }

  async listProductBatches(
    productId: string,
    params?: { page?: number; page_size?: number; granularity?: BatchGranularity },
  ): Promise<PagedResponse<ProductBatch>> {
    return this.get(
      `/manufacturing/products/${encodeURIComponent(productId)}/batches${this.buildQuery(params)}`,
    );
  }

  async getProductBatch(batchId: string): Promise<{ data: ProductBatch }> {
    return this.get(`/manufacturing/batches/${encodeURIComponent(batchId)}`);
  }

  /**
   * `quantity` and `quantity_unit` travel together or not at all — the route
   * rejects one without the other, naming the field.
   *
   * A number with no unit is read as whatever unit the reader expects, and here
   * the reader may be a producer-responsibility reconciliation.
   */
  async createProductBatch(
    productId: string,
    input: {
      granularity: BatchGranularity;
      batch_code: string;
      identifiers?: TraceIdentifier[];
      manufactured_at?: string;
      expires_at?: string;
      quantity?: number;
      quantity_unit?: string;
    },
  ): Promise<{ data: ProductBatch }> {
    return this.post(`/manufacturing/products/${encodeURIComponent(productId)}/batches`, input);
  }

  /** No `batch_code`, for the reason given on `updateManufacturingProduct`. */
  async updateProductBatch(
    batchId: string,
    input: Partial<{
      granularity: BatchGranularity;
      identifiers: TraceIdentifier[];
      /** `null` unsets a recorded date; an absent key leaves it. */
      manufactured_at: string | null;
      expires_at: string | null;
      /** `null` unsets the pair, and only together with `quantity_unit: null`. */
      quantity: number | null;
      quantity_unit: string | null;
      /**
       * Sprint 62.49.0 (AC8) — each list replaced whole; `[]` records none, and
       * the batch then reads *input lot link missing*. An input is one of this
       * organisation's batches or a counterparty's lot reference; a recipient is
       * a vendor or a name as written.
       */
      food_inputs: BatchFoodInput[];
      food_recipients: BatchFoodRecipient[];
    }>,
  ): Promise<{ data: ProductBatch }> {
    return this.patch(`/manufacturing/batches/${encodeURIComponent(batchId)}`, input);
  }

  /**
   * The trace ledger is append-only, and there is no update or delete method
   * here because there is no route to call.
   *
   * `include_superseded` defaults to false: the default is the live chain, which
   * is what an operator reading a timeline means. The option exists because the
   * question an authority asks after a recall is *"what did you believe then, and
   * when did that change"*.
   */
  async listTraceEvents(params?: {
    product_id?: string;
    batch_id?: string;
    page?: number;
    page_size?: number;
    include_superseded?: boolean;
  }): Promise<PagedResponse<StoredTraceEvent>> {
    return this.get(`/manufacturing/trace-events${this.buildQuery(params)}`);
  }

  /**
   * The whole chain for a product or batch, **including superseded events**.
   *
   * Unlike `listTraceEvents`, this never hides history: a chain walk that dropped
   * corrected events would answer "what do you believe now" when the question a
   * recall asks is "what did you believe then".
   *
   * `diagnostics` is the half that matters and the reason this return type is
   * wider than the timeline's. A chain can contain a **cycle** — A upstream of B
   * upstream of A, entered by a data-entry error or a genuinely circular rework
   * loop — and a bounded walk over one terminates *by construction*, which is
   * exactly how a truncated chain gets reported as a complete one. So the walk is
   * bounded **and** says when it stopped early and why, carrying the path. An
   * empty array means the chain was walked to its end.
   */
  async getProductTraceChain(
    productId: string,
    params?: { batch_id?: string },
  ): Promise<{
    data: {
      events: StoredTraceEvent[];
      upstream: string[];
      downstream: string[];
      diagnostics: Array<{
        reason: 'reference_cycle' | 'expansion_bound_reached';
        direction: string;
        path: string[];
        detail: string;
      }>;
      /**
       * Sprint 62.49.0 (AC12) — each reference the walk reached that nothing
       * recorded resolves, with the path that reached it. A gap in what is
       * recorded, never a finding that the chain is broken. Additive: the
       * cycle and bound diagnostics are unchanged.
       */
      unresolved_refs?: TraceChainUnresolvedRef[];
    };
  }> {
    return this.get(
      `/manufacturing/products/${encodeURIComponent(productId)}/trace-chain${this.buildQuery(params)}`,
    );
  }

  async appendTraceEvent(input: {
    product_id: string;
    batch_id?: string;
    event_type_code: string;
    regime_id: string;
    occurred_at: string;
    actor: string;
    location?: string;
    upstream_refs?: string[];
    downstream_refs?: string[];
    observed_identifier?: TraceIdentifier;
    evidence_attachment_ids?: string[];
    notes?: string;
  }): Promise<{ data: StoredTraceEvent }> {
    return this.post('/manufacturing/trace-events', input);
  }

  /**
   * `correction_reason` is **required**, matching the discriminated shape in the
   * shared types.
   *
   * A supersession nobody can explain is exactly what an inspector asks about,
   * and it is the record that ships when the reason is optional and left for
   * later.
   */
  /**
   * A correction carries **no `product_id`, `batch_id` or `regime_id`** — all
   * three are inherited from the event being corrected, and the route rejects a
   * body carrying any of them.
   *
   * A correction of an event about product A cannot be about product B: it would
   * silently move a fact from one product's chain to another's. The regime is
   * re-resolved server-side so the correction's provenance records the pack
   * version it was made under.
   */
  async appendTraceCorrection(
    eventId: string,
    input: {
      event_type_code: string;
      occurred_at: string;
      actor: string;
      location?: string;
      upstream_refs?: string[];
      downstream_refs?: string[];
      observed_identifier?: TraceIdentifier;
      evidence_attachment_ids?: string[];
      notes?: string;
      correction_reason: string;
    },
  ): Promise<{ data: StoredTraceEvent }> {
    return this.post(
      `/manufacturing/trace-events/${encodeURIComponent(eventId)}/corrections`,
      input,
    );
  }

  async listConformityRecords(params?: {
    product_id?: string;
    state?: ConformityState;
    technical_regulation_code?: string;
    page?: number;
    page_size?: number;
  }): Promise<PagedResponse<ConformityRecord> & { determination_caveats: DeterminationCaveat[] }> {
    return this.get(`/manufacturing/conformity${this.buildQuery(params)}`);
  }

  async getConformityRecord(
    recordId: string,
  ): Promise<{ data: ConformityRecord; determination_caveats: DeterminationCaveat[] }> {
    return this.get(`/manufacturing/conformity/${encodeURIComponent(recordId)}`);
  }

  async createConformityRecord(input: {
    product_id: string;
    technical_regulation_code: string;
    conformity_route_code: string;
    regime_id: string;
    state?: ConformityState;
    route_rationale?: string;
  }): Promise<{ data: ConformityRecord }> {
    return this.post('/manufacturing/conformity', input);
  }

  async updateConformityRecord(
    recordId: string,
    input: Partial<{
      conformity_route_code: string;
      state: ConformityState;
      route_rationale: string;
    }>,
  ): Promise<{ data: ConformityRecord }> {
    return this.patch(`/manufacturing/conformity/${encodeURIComponent(recordId)}`, input);
  }

  async addConformityEvidence(
    recordId: string,
    input: {
      evidence_kind_code: string;
      reference: string;
      issued_by?: string;
      issued_at?: string;
      valid_until?: string;
      attachment_ids?: string[];
    },
  ): Promise<{ data: ConformityRecord }> {
    return this.post(`/manufacturing/conformity/${encodeURIComponent(recordId)}/evidence`, input);
  }

  async raiseConformityFinding(
    recordId: string,
    input: { summary: string },
  ): Promise<{ data: ConformityRecord }> {
    return this.post(`/manufacturing/conformity/${encodeURIComponent(recordId)}/findings`, input);
  }

  async resolveConformityFinding(
    recordId: string,
    findingId: string,
    input: { resolution: string },
  ): Promise<{ data: ConformityRecord }> {
    return this.post(
      `/manufacturing/conformity/${encodeURIComponent(recordId)}/findings/${encodeURIComponent(findingId)}/resolve`,
      input,
    );
  }

  /**
   * `manufacturing_conformity:declare` — the one act in this domain that is a
   * legal statement by the organisation rather than a record about one.
   *
   * **There is no `declared_by`.** The signatory is the authenticated caller, taken
   * from the session server-side; the request body is `.strict()`, so sending one is
   * a 400 naming the key. Whoever calls this signs it.
   *
   * `declared_at` is optional and defaults to now.
   *
   * The service refuses while any finding is unresolved, while the record holds no
   * evidence, once superseded, once already declared, and when the caller is the
   * person who raised the record — so holding the permission is necessary and not
   * sufficient. That last refusal is the separation of duties; it used to be listed
   * here as two, one of which compared `declared_by` to the session. **This comment
   * enumerating four refusals is why the 62.8.0 declarant contract change never
   * reached the page** — it was the reference the frontend stream read, and it was
   * not updated with the service. A client-side enumeration of server refusals is a
   * copy of a contract, so it is a place a contract can go stale.
   */
  async declareConformity(
    recordId: string,
    input: { declared_at?: string } = {},
  ): Promise<{ data: ConformityRecord }> {
    return this.post(`/manufacturing/conformity/${encodeURIComponent(recordId)}/declare`, input);
  }

  /**
   * `:write`, not `:declare`. Superseding records that an assessment is
   * historical, which is record-keeping; it makes no statement about the product.
   */
  async supersedeConformityRecord(recordId: string): Promise<{ data: ConformityRecord }> {
    return this.post(`/manufacturing/conformity/${encodeURIComponent(recordId)}/supersede`, {});
  }

  async listCapaRecords(params?: {
    state?: CapaState;
    product_id?: string;
    recall_scope?: RecallScope;
    page?: number;
    page_size?: number;
  }): Promise<PagedResponse<CapaRecord>> {
    return this.get(`/manufacturing/capa-recall${this.buildQuery(params)}`);
  }

  async getCapaRecord(recordId: string): Promise<{ data: CapaRecord }> {
    return this.get(`/manufacturing/capa-recall/${encodeURIComponent(recordId)}`);
  }

  /**
   * The recall blast radius, computed and never remembered.
   *
   * `derived_at` travels with it because "which batches are affected" is only
   * ever true as of a moment — 62.7.0's rule for correction recipients, applied
   * to a recall. A stored list would be a claim about the present made in the
   * past.
   */
  async getCapaAffectedScope(recordId: string): Promise<{
    data: {
      product_ids: string[];
      batch_ids: string[];
      derived_batch_ids: string[];
      derived_at: string;
      /** Sprint 62.49.0 (G3) — the links the derivation could not follow, with their paths. */
      unresolved_refs?: TraceChainUnresolvedRef[];
    };
  }> {
    return this.get(`/manufacturing/capa-recall/${encodeURIComponent(recordId)}/affected-scope`);
  }

  async createCapaRecord(input: {
    product_ids: string[];
    batch_ids?: string[];
    title: string;
    description: string;
    incident_ids?: string[];
    task_ids?: string[];
    authority_request_ids?: string[];
    conformity_record_ids?: string[];
    root_cause?: string;
  }): Promise<{ data: CapaRecord }> {
    return this.post('/manufacturing/capa-recall', input);
  }

  async updateCapaRecord(
    recordId: string,
    input: Partial<{
      product_ids: string[];
      batch_ids: string[];
      title: string;
      description: string;
      incident_ids: string[];
      task_ids: string[];
      authority_request_ids: string[];
      conformity_record_ids: string[];
      root_cause: string;
      state: CapaState;
    }>,
  ): Promise<{ data: CapaRecord }> {
    return this.patch(`/manufacturing/capa-recall/${encodeURIComponent(recordId)}`, input);
  }

  async escalateCapaToRecall(
    recordId: string,
    input: { recall_scope: RecallScope; batch_ids?: string[] },
  ): Promise<{ data: CapaRecord }> {
    return this.post(`/manufacturing/capa-recall/${encodeURIComponent(recordId)}/escalate`, input);
  }

  /**
   * `manufacturing_recall:approve_closure`, and the service refuses an approver
   * equal to the person who raised the action.
   *
   * Both are required: the permission lets a tenant separate the roles, and the
   * service stops the separation being defeated by one person who holds both.
   */
  async approveCapaClosure(
    recordId: string,
    input: { closure_summary: string },
  ): Promise<{ data: CapaRecord }> {
    return this.post(
      `/manufacturing/capa-recall/${encodeURIComponent(recordId)}/approve-closure`,
      input,
    );
  }

  async listEprScopes(params?: {
    in_scope?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<PagedResponse<EprScope> & { determination_caveats: DeterminationCaveat[] }> {
    return this.get(`/manufacturing/epr/scopes${this.buildQuery(params)}`);
  }

  /**
   * `in_scope` is a required boolean with **no third value**.
   *
   * An unanswered applicability test looks exactly like no record at all, so
   * offering "not assessed" would let a tenant record the absence of a decision
   * as though it were one. `determination_rationale` is required either way — an
   * exclusion needs grounds.
   */
  async upsertEprScope(input: {
    material_category_code: string;
    regime_id: string;
    in_scope: boolean;
    determination_rationale: string;
    determined_at?: string;
  }): Promise<{ data: EprScope }> {
    return this.put('/manufacturing/epr/scopes', input);
  }

  async listEprVolumes(params?: {
    material_category_code?: string;
    product_id?: string;
    period_start?: string;
    period_end?: string;
    page?: number;
    page_size?: number;
  }): Promise<PagedResponse<EprMaterialVolume>> {
    return this.get(`/manufacturing/epr/volumes${this.buildQuery(params)}`);
  }

  /**
   * `quantity_unit` is required here, unlike on a batch.
   *
   * A volume with no unit cannot be reconciled against a declared quantity at
   * all, and the reconciliation's honest answer would be `unit_mismatch` on every
   * row. **No conversion is ever applied** — a normalised figure is a figure
   * nobody declared.
   */
  async createEprVolume(input: {
    product_id: string;
    material_category_code: string;
    regime_id: string;
    period_start: string;
    period_end: string;
    quantity: number;
    quantity_unit: string;
    source_note: string;
  }): Promise<{ data: EprMaterialVolume }> {
    return this.post('/manufacturing/epr/volumes', input);
  }

  async updateEprVolume(
    volumeId: string,
    input: Partial<{
      product_id: string;
      material_category_code: string;
      regime_id: string;
      period_start: string;
      period_end: string;
      quantity: number;
      quantity_unit: string;
      source_note: string;
    }>,
  ): Promise<{ data: EprMaterialVolume }> {
    return this.patch(`/manufacturing/epr/volumes/${encodeURIComponent(volumeId)}`, input);
  }

  async listEprFulfilments(params?: {
    material_category_code?: string;
    page?: number;
    page_size?: number;
  }): Promise<PagedResponse<EprFulfilment> & { determination_caveats: DeterminationCaveat[] }> {
    return this.get(`/manufacturing/epr/fulfilments${this.buildQuery(params)}`);
  }

  async createEprFulfilment(input: {
    material_category_code: string;
    fulfilment_route_code: string;
    regime_id: string;
    period_start: string;
    period_end: string;
    planned_quantity?: number;
    planned_quantity_unit?: string;
    achieved_quantity?: number;
    achieved_quantity_unit?: string;
    provider_vendor_id?: string;
    evidence_attachment_ids?: string[];
  }): Promise<{ data: EprFulfilment }> {
    return this.post('/manufacturing/epr/fulfilments', input);
  }

  async updateEprFulfilment(
    fulfilmentId: string,
    input: Partial<{
      material_category_code: string;
      fulfilment_route_code: string;
      regime_id: string;
      period_start: string;
      period_end: string;
      planned_quantity: number;
      planned_quantity_unit: string;
      achieved_quantity: number;
      achieved_quantity_unit: string;
      provider_vendor_id: string;
      evidence_attachment_ids: string[];
    }>,
  ): Promise<{ data: EprFulfilment }> {
    return this.patch(`/manufacturing/epr/fulfilments/${encodeURIComponent(fulfilmentId)}`, input);
  }

  async listEprSubmissions(params?: {
    submission_kind_code?: string;
    page?: number;
    page_size?: number;
  }): Promise<PagedResponse<EprSubmission> & { determination_caveats: DeterminationCaveat[] }> {
    return this.get(`/manufacturing/epr/submissions${this.buildQuery(params)}`);
  }

  async createEprSubmission(input: {
    submission_kind_code: string;
    regime_id: string;
    period_start: string;
    period_end: string;
  }): Promise<{ data: EprSubmission }> {
    return this.post('/manufacturing/epr/submissions', input);
  }

  /**
   * `manufacturing_epr:submit` — recording that the organisation filed.
   *
   * A `declared_contribution_amount` arrives only with `declared_by`,
   * `declared_source` **and** `declared_contribution_currency`, and the route
   * rejects it otherwise naming the field. **The platform computes no
   * contribution**: it holds no rate, no coefficient and no unit charge, so an
   * amount with no author would read as one it produced.
   */
  async recordEprSubmissionFiled(
    submissionId: string,
    input: {
      /** Required. Not defaulted — the filing date is what a deadline is measured against. */
      submitted_at: string;
      submission_reference?: string;
      /**
       * The quantity the submission declared, and the unit it was declared in.
       *
       * Without this the reconciliation's `declared_quantity_mismatch` condition
       * could never fire, because nothing could set the value it compares against.
       */
      declared_quantity?: number;
      declared_quantity_unit?: string;
      declared_contribution_amount?: number;
      declared_contribution_currency?: string;
      declared_by?: string;
      declared_source?: string;
      proof_attachment_ids?: string[];
      authority_response_note?: string;
      authority_request_ids?: string[];
    },
  ): Promise<{ data: EprSubmission }> {
    return this.post(
      `/manufacturing/epr/submissions/${encodeURIComponent(submissionId)}/filed`,
      input,
    );
  }

  /**
   * The reconciliation. A GET, because it stores nothing and a repeated call has
   * no effect — a caller may safely retry.
   *
   * Each exception says that two of the organisation's own statements differ. **No
   * field names a correct value**, because naming one would be assessing the
   * organisation's position. `label` is the regime's own word for the condition;
   * `null` there is a pack defect, not a blank to render.
   */
  async reconcileEpr(params: {
    material_category_code?: string;
    period_start: string;
    period_end: string;
    regime_id: string;
  }): Promise<{
    data: {
      exceptions: Array<EprReconciliationException & { label: Record<string, string> | null }>;
      determination_caveat: Record<string, string> | null;
      caveat_unresolved_reason: string | null;
      reconciled_at: string;
    };
  }> {
    return this.get(`/manufacturing/epr/reconciliation${this.buildQuery(params)}`);
  }

  async listChemicalSubstances(params?: {
    control_status_code?: string;
    hazard_class_code?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<PagedResponse<ChemicalSubstance> & { determination_caveats: DeterminationCaveat[] }> {
    return this.get(`/manufacturing/chemicals/substances${this.buildQuery(params)}`);
  }

  async getChemicalSubstance(
    substanceId: string,
  ): Promise<{ data: ChemicalSubstance; determination_caveats: DeterminationCaveat[] }> {
    return this.get(`/manufacturing/chemicals/substances/${encodeURIComponent(substanceId)}`);
  }

  /**
   * A limit is a value, a unit **and** a basis, or none of the three.
   *
   * The same figure measured against a whole article and against one homogeneous
   * component is a different limit, so a comparison missing the basis would
   * answer a question nobody asked. The route rejects a partial triple naming the
   * field.
   */
  async createChemicalSubstance(input: {
    name: string;
    identifier?: string;
    hazard_class_codes?: string[];
    control_status_code: string;
    regime_id: string;
    limit_value?: number;
    limit_unit?: string;
    limit_basis?: string;
  }): Promise<{ data: ChemicalSubstance }> {
    return this.post('/manufacturing/chemicals/substances', input);
  }

  async updateChemicalSubstance(
    substanceId: string,
    input: Partial<{
      name: string;
      identifier: string;
      hazard_class_codes: string[];
      control_status_code: string;
      regime_id: string;
      limit_value: number;
      limit_unit: string;
      limit_basis: string;
    }>,
  ): Promise<{ data: ChemicalSubstance }> {
    return this.patch(
      `/manufacturing/chemicals/substances/${encodeURIComponent(substanceId)}`,
      input,
    );
  }

  /**
   * Sprint 63.31.0 (#444, #445) — the chemical traceability declarations view:
   * one page of declarations (or code-termination records) read from the form
   * instances an installed pack's deadline rows link, with the clocks, both
   * anchors of the declaration clock and the boundary statement.
   */
  async listChemicalTraceabilityRecords(params?: {
    kind?: import('@vn-grc/shared').ChemicalTraceabilityRecordKind;
    page?: number;
    page_size?: number;
  }): Promise<import('@vn-grc/shared').ChemicalTraceabilityListResponse> {
    return this.get(`/manufacturing/chemical-traceability/declarations${this.buildQuery(params)}`);
  }

  /** Links to incidents and licences, or (declarations) the recorded times. 409 on a stale save. */
  async updateChemicalTraceabilityRecord(
    recordId: string,
    input: import('@vn-grc/shared').UpdateChemicalTraceabilityRecordInput,
  ): Promise<{ data: import('@vn-grc/shared').ChemicalTraceabilityRecord }> {
    return this.patch(
      `/manufacturing/chemical-traceability/declarations/${encodeURIComponent(recordId)}`,
      input,
    );
  }

  /**
   * "Which products contain this substance?" — the query a new restriction runs,
   * and the one an authority asks after a substance is banned.
   *
   * `exceeds_limit` is `boolean | null`: **three states, not two.** `null` means
   * the comparison could not be made — no limit, no concentration, or units that
   * do not match. Rendering it as "within limit" is a confident answer to a
   * comparison that never happened.
   */
  async listProductsContainingSubstance(
    substanceId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<
    PagedResponse<{
      product_id: string;
      product_code: string;
      name: string;
      exceeds_limit: boolean | null;
    }> & { determination_caveats: DeterminationCaveat[] }
  > {
    return this.get(
      `/manufacturing/chemicals/substances/${encodeURIComponent(substanceId)}/products${this.buildQuery(params)}`,
    );
  }

  async listProductComposition(
    productId: string,
  ): Promise<{ data: ProductCompositionView[]; total: number }> {
    return this.get(`/manufacturing/products/${encodeURIComponent(productId)}/composition`);
  }

  async upsertProductComposition(
    productId: string,
    input: {
      substance_id: string;
      concentration_value?: number;
      concentration_unit?: string;
      concentration_basis?: string;
      evidence_kind_code?: string;
      evidence_reference?: string;
      supplier_vendor_id?: string;
      supplier_declared_at?: string;
      attachment_ids?: string[];
      regime_id?: string;
    },
  ): Promise<{ data: ProductComposition }> {
    return this.put(`/manufacturing/products/${encodeURIComponent(productId)}/composition`, input);
  }

  async deleteProductComposition(compositionId: string): Promise<void> {
    return this.del(`/manufacturing/chemicals/composition/${encodeURIComponent(compositionId)}`);
  }

  /**
   * An OT profile **extends** a canonical system: `system_id` is required
   * because it is non-nullable on the type, and non-nullable because the locked
   * architecture decision is that OT does not create a disconnected asset
   * inventory.
   *
   * The consequence for a user is real and is stated on the surface: recording an
   * OT asset requires the system to be in the data inventory first.
   */
  async listOtAssetProfiles(params?: {
    plant?: string;
    line?: string;
    processes_worker_data?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<PagedResponse<OtAssetProfileView>> {
    return this.get(`/manufacturing/ot-assets${this.buildQuery(params)}`);
  }

  async getOtAssetProfile(profileId: string): Promise<{ data: OtAssetProfileView }> {
    return this.get(`/manufacturing/ot-assets/${encodeURIComponent(profileId)}`);
  }

  /**
   * `processes_worker_data` is a required boolean with no third value, and
   * `worker_data_mapping_ref` is required when it is true.
   *
   * A `true` pointing at nothing records the personal-data mapping duty as met
   * and points at nothing.
   */
  async createOtAssetProfile(input: {
    system_id: string;
    location: { plant: string; line?: string; zone?: string };
    integration_kinds?: OtIntegrationKind[];
    processes_worker_data: boolean;
    worker_data_mapping_ref?: string;
    ai_system_profile_ids?: string[];
    incident_ids?: string[];
  }): Promise<{ data: OtAssetProfile }> {
    return this.post('/manufacturing/ot-assets', input);
  }

  async updateOtAssetProfile(
    profileId: string,
    input: Partial<{
      location: { plant: string; line?: string; zone?: string };
      integration_kinds: OtIntegrationKind[];
      processes_worker_data: boolean;
      worker_data_mapping_ref: string;
      ai_system_profile_ids: string[];
      incident_ids: string[];
    }>,
  ): Promise<{ data: OtAssetProfile }> {
    return this.patch(`/manufacturing/ot-assets/${encodeURIComponent(profileId)}`, input);
  }

  /**
   * `state` is **derived at read time from the clock**, never stored — a stored
   * flag is wrong the moment the clock passes `expires_at` and nothing runs to
   * correct it. `revoked` wins over `expired`, because a revocation is a decision
   * somebody made and an expiry is not.
   */
  /**
   * Export one manufacturing register through `UnifiedExportService` (R-33).
   *
   * **One method for all eight kinds, not eight methods.** The kind is the route
   * segment and the backend loops its route table from `MANUFACTURING_EXPORT_KINDS`,
   * so a ninth kind is reachable here the moment it is mounted — where eight
   * hand-written methods would leave the ninth with no client and nobody would notice,
   * which is exactly how the eight routes came to ship with no user at all (Gate E12.5
   * pass 2, P1-2: the service and routes were correct, tested, and reachable by
   * nothing — `ExportFormatMenu` appeared on 0 of the module's nine pages).
   *
   * Each kind is gated on its own sub-domain read permission server-side, so a caller
   * holding one register's permission cannot export another's.
   *
   * Returns the export's own statement about what it produced: the row count, the
   * determination caveats for the regimes the rows were recorded under, and one line
   * per framework naming whether it is installed directly or for reference only. A
   * caller that shows the file and drops those lines is publishing an artefact whose
   * own provenance it discarded.
   */
  async exportManufacturingRegister(
    kind: ManufacturingExportKind,
    params: { format: 'csv' | 'xlsx' | 'pdf' | 'docx' } & Record<string, unknown>,
  ): Promise<{ data: ManufacturingExportResult }> {
    // **GET with a query string, not POST.** The eight routes are mounted with
    // `router.get` and read `req.query` under `.strict()`. This method's first version
    // posted a body, so **every export 404ed** — found at gate E12.5 pass 3, and it is
    // the same class as that pass's subject: a client and a route disagreeing about a
    // contract with no assertion joining them. `export-surface-62.8.0.test.ts` checked
    // that the path appeared in this file and not the verb, so it passed; it now pins
    // the verb, because a path without a method is half a contract.
    return this.get(`/manufacturing/export/${encodeURIComponent(kind)}${this.buildQuery(params)}`);
  }

  async listRemoteAccessGrants(params?: {
    ot_asset_profile_id?: string;
    vendor_id?: string;
    state?: RemoteAccessGrantState;
    page?: number;
    page_size?: number;
  }): Promise<PagedResponse<RemoteAccessGrantView>> {
    return this.get(`/manufacturing/ot-assets/remote-access/grants${this.buildQuery(params)}`);
  }

  async getRemoteAccessGrant(grantId: string): Promise<{ data: RemoteAccessGrantView }> {
    return this.get(`/manufacturing/ot-assets/remote-access/grants/${encodeURIComponent(grantId)}`);
  }

  /**
   * `expires_at` is **required** and must be in the future.
   *
   * An open-ended grant is the thing the control exists to prevent, and a
   * nullable expiry would make permanent access the easiest record to create.
   */
  async requestRemoteAccessGrant(input: {
    ot_asset_profile_id: string;
    vendor_id: string;
    purpose: string;
    granted_at?: string;
    expires_at: string;
    evidence_attachment_ids?: string[];
  }): Promise<{ data: RemoteAccessGrant }> {
    return this.post('/manufacturing/ot-assets/remote-access/grants', input);
  }

  /**
   * `manufacturing_ot:approve_remote_access` — letting a vendor into a system
   * that moves physical machinery.
   *
   * **There is no `approved_by`.** The approver is the authenticated caller. The
   * service refuses an approver equal to the requester, a vendor offboarded since
   * the request, and a system decommissioned since the request; approving "on behalf
   * of" somebody is not refused but unrepresentable — the body accepts no field for
   * one, and is `.strict()`.
   */
  async approveRemoteAccessGrant(grantId: string): Promise<{ data: RemoteAccessGrant }> {
    return this.post(
      `/manufacturing/ot-assets/remote-access/grants/${encodeURIComponent(grantId)}/approve`,
      {},
    );
  }

  /**
   * Revocation is `:write`, not `:approve_remote_access`.
   *
   * Closing an access route is the safe direction, and requiring the scarcer
   * permission to shut a tunnel is how a tunnel stays open while somebody looks
   * for whoever holds it. Revoking an already-expired grant is allowed: an expiry
   * is not a decision and a revocation is.
   */
  async revokeRemoteAccessGrant(
    grantId: string,
    input?: { reason?: string },
  ): Promise<{ data: RemoteAccessGrant }> {
    return this.post(
      `/manufacturing/ot-assets/remote-access/grants/${encodeURIComponent(grantId)}/revoke`,
      input ?? {},
    );
  }

  // ── Sprint 62.9.0 — logistics, customs, parcel and delivery ──────
  //
  // Mounted **once** at `/api/v1/logistics`. One namespace for five registers,
  // because the shipment aggregate is shared: a customs record, a clearance
  // event, an authority annotation and a parcel all point at one `shipment_id`,
  // and five routers would need five copies of "does this shipment exist in this
  // org" — which is how two of them come to disagree.
  //
  // Every method below is traced from `src/backend/src/modules/logistics/routes.ts`
  // (R-26). No URL, body shape or envelope here is guessed.
  //
  // **Envelopes.** `{ data }` for a single entity, `{ data, total, page, page_size }`
  // for a list — `page_size`, never `per_page`. **One route departs**, and it is
  // named at its own method: `GET /parcels/:parcelId/events` is cursor-paginated
  // and returns `{ data, next_cursor, has_more }`, so `extractPaginationMeta` must
  // not be pointed at it.
  //
  // **Two reads carry `determination_caveats` beside `data`**, and only two:
  // customs records and postal authorisations, because they are the only rows in
  // this module that own a `regime_source`. The return types say so rather than
  // leaving a caller to discover it.

  async listLogisticsCustomsRegimes(): Promise<EvidenceRegimeListResponse> {
    return this.get('/logistics/customs/regimes');
  }

  /**
   * Sprint 62.25.0 AC11 (idea-catalog #142) — the filing channels the customs
   * draft form may offer for one framework.
   *
   * `data` is exactly the set the channel validator accepts for that framework
   * (the backend filters the one shared filing profile through
   * `resolveFilingChannel`), so a selector built from it can never hand the
   * operator an option that produces a 400. **Not paginated**, and always a 200:
   * an uninstalled framework answers `data: []` with
   * `diagnostics.state: 'framework_not_installed'`, which is what lets the page
   * tell "not installed" from "declares none" from "has no filing authority".
   */
  async listLogisticsCustomsChannels(frameworkCode: string): Promise<{
    data: InstalledFilingChannelOption[];
    diagnostics: FilingChannelDiscoveryDiagnostics;
  }> {
    return this.get(
      `/logistics/customs/channels${this.buildQuery({ framework_code: frameworkCode })}`,
    );
  }

  async listLogisticsAuthorisationRegimes(): Promise<EvidenceRegimeListResponse> {
    return this.get('/logistics/authorisations/regimes');
  }

  /**
   * `processes_personal_data` is the literal string `'true'` / `'false'`.
   *
   * The route parses it with `z.enum(['true','false'])` and **not**
   * `z.coerce.boolean()`, because coercion applies JavaScript truthiness to the
   * raw string: `"false"` becomes `true`. Three query parameters shipped that way
   * last sprint and each returned the exact inverse of what the caller asked for.
   */
  async listShipments(params?: {
    page?: number;
    page_size?: number;
    status?: ShipmentStatus;
    direction?: ShipmentDirection;
    processes_personal_data?: 'true' | 'false';
    party_vendor_id?: string;
    party_data_subject_profile_id?: string;
    q?: string;
  }): Promise<PagedResponse<Shipment>> {
    return this.get(`/logistics/shipments${this.buildQuery(params)}`);
  }

  /**
   * One shipment, by any identity it is known by (AC1).
   *
   * It can **refuse rather than answer**: a house bill and a booking reference
   * legitimately repeat across consolidations, so two shipments can carry one
   * number, and the service raises `logistics_identifier_ambiguous` naming both
   * rather than returning "the first" — the quiet wrong answer a broker acts on.
   */
  async lookupShipmentByIdentifier(params: {
    value: string;
    scheme?: ShipmentIdentifierScheme;
  }): Promise<{ data: Shipment }> {
    return this.get(`/logistics/shipments/lookup${this.buildQuery(params)}`);
  }

  async getShipment(shipmentId: string): Promise<{ data: Shipment }> {
    return this.get(`/logistics/shipments/${encodeURIComponent(shipmentId)}`);
  }

  /**
   * `processes_personal_data` is a **required boolean** with no third value, and
   * a `true` must be accompanied by `processing_activity_id` — the cross-border
   * duty is met by pointing at Data Mapping, never by a second register here. The
   * other direction is enforced too: `false` requires the activity to be absent,
   * because a shipment declaring no personal data while pointing at a processing
   * activity contradicts itself and either half could be the wrong one.
   */
  async createShipment(input: {
    shipment_code: string;
    direction: ShipmentDirection;
    transport_mode: string;
    identifiers?: Array<{ scheme: string; value: string; issued_by: string | null }>;
    parties?: Array<Record<string, unknown>>;
    origin_country?: string | null;
    destination_country?: string | null;
    warehouse_system_id?: string | null;
    processes_personal_data: boolean;
    processing_activity_id?: string | null;
    document_attachment_ids?: string[];
    notes?: string | null;
  }): Promise<{ data: Shipment }> {
    return this.post('/logistics/shipments', input);
  }

  /**
   * No `shipment_code` and no `status`.
   *
   * `shipment_code` is half a unique index and the value every other record joins
   * on; `status` has its own routes, because `close` and `cancel` audit under
   * their own codes and a generic patch reaching that column would take a shipment
   * out of service with an `..._UPDATED` row on the trail.
   */
  async updateShipment(
    shipmentId: string,
    input: Partial<{
      direction: ShipmentDirection;
      transport_mode: string;
      identifiers: Array<{ scheme: string; value: string; issued_by: string | null }>;
      parties: Array<Record<string, unknown>>;
      origin_country: string | null;
      destination_country: string | null;
      warehouse_system_id: string | null;
      processes_personal_data: boolean;
      processing_activity_id: string | null;
      document_attachment_ids: string[];
      notes: string | null;
    }>,
  ): Promise<{ data: Shipment }> {
    return this.patch(`/logistics/shipments/${encodeURIComponent(shipmentId)}`, input);
  }

  async closeShipment(shipmentId: string): Promise<{ data: Shipment }> {
    return this.post(`/logistics/shipments/${encodeURIComponent(shipmentId)}/close`, {});
  }

  async cancelShipment(shipmentId: string): Promise<{ data: Shipment }> {
    return this.post(`/logistics/shipments/${encodeURIComponent(shipmentId)}/cancel`, {});
  }

  async listCargoItems(
    shipmentId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<PagedResponse<CargoItem>> {
    return this.get(
      `/logistics/shipments/${encodeURIComponent(shipmentId)}/cargo-items${this.buildQuery(params)}`,
    );
  }

  /**
   * Every commercial figure is `declared_`-prefixed and carries the party that
   * stated it. There is no duty rate, no tariff lookup, no customs-value
   * computation and no total across lines, here or anywhere in this module.
   */
  async addCargoItem(
    shipmentId: string,
    input: {
      line_number: number;
      description: string;
      hs_code?: string | null;
      declared_quantity?: number | null;
      declared_quantity_unit?: string | null;
      declared_value_amount?: number | null;
      declared_value_currency?: string | null;
      declared_by?: string | null;
      origin_country?: string | null;
    },
  ): Promise<{ data: CargoItem }> {
    return this.post(`/logistics/shipments/${encodeURIComponent(shipmentId)}/cargo-items`, input);
  }

  async getCargoItem(cargoItemId: string): Promise<{ data: CargoItem }> {
    return this.get(`/logistics/cargo-items/${encodeURIComponent(cargoItemId)}`);
  }

  /** No `line_number` and no `shipment_id`: re-parenting moves a declared figure. */
  async updateCargoItem(
    cargoItemId: string,
    input: Partial<{
      description: string;
      hs_code: string | null;
      declared_quantity: number | null;
      declared_quantity_unit: string | null;
      declared_value_amount: number | null;
      declared_value_currency: string | null;
      declared_by: string | null;
      origin_country: string | null;
    }>,
  ): Promise<{ data: CargoItem }> {
    return this.patch(`/logistics/cargo-items/${encodeURIComponent(cargoItemId)}`, input);
  }

  async removeCargoItem(cargoItemId: string): Promise<void> {
    return this.del(`/logistics/cargo-items/${encodeURIComponent(cargoItemId)}`);
  }

  /**
   * Every clearance event across a shipment's customs records.
   *
   * Gated on `logistics_customs:read` rather than the shipment read: what the
   * authority did about a consignment is customs evidence, and it sits on this
   * path only because that is the question an operator asks.
   */
  async listShipmentClearanceEvents(
    shipmentId: string,
    params?: { page?: number; page_size?: number; outcome?: ClearanceOutcome },
  ): Promise<PagedResponse<ClearanceEvent>> {
    return this.get(
      `/logistics/shipments/${encodeURIComponent(shipmentId)}/clearance-events${this.buildQuery(params)}`,
    );
  }

  async listCustomsRecords(params?: {
    page?: number;
    page_size?: number;
    shipment_id?: string;
    status?: CustomsRecordStatus;
    artifact_kind_code?: string;
  }): Promise<PagedResponse<CustomsRecord> & { determination_caveats: DeterminationCaveat[] }> {
    return this.get(`/logistics/customs-records${this.buildQuery(params)}`);
  }

  async getCustomsRecord(
    customsRecordId: string,
  ): Promise<{ data: CustomsRecord; determination_caveats: DeterminationCaveat[] }> {
    return this.get(`/logistics/customs-records/${encodeURIComponent(customsRecordId)}`);
  }

  /**
   * A customs record is evidence **about** a lodgement.
   *
   * There is no declaration body, no line-item schema and no field that could
   * carry either — the packs record that no official customs, manifest,
   * value-declaration, inspection or single-window template has been verified
   * against its source, and a field for one would contradict the content this
   * module serves.
   */
  async createCustomsRecord(input: {
    shipment_id: string;
    regime_id: string;
    artifact_kind_code: string;
    procedure_reference?: string | null;
    channel_code?: string | null;
    lodged_by_role?: ShipmentPartyRole | null;
    /**
     * Sprint 62.25.0 AC8 — the organisation's own regulated role, a term of the
     * chosen regime's `logistics_service_role` vocabulary. Not a shipment party's
     * role: that is `lodged_by_role`.
     */
    service_role_code?: string | null;
    evidence_attachment_ids?: string[];
    notes?: string | null;
  }): Promise<{ data: CustomsRecord }> {
    return this.post('/logistics/customs-records', input);
  }

  /**
   * No `shipment_id`, no `regime_id`, and none of the three lifecycle columns.
   *
   * `status`, `submission_id` and `lodged_at` each have their own route with its
   * own precondition and audit code. `regime_id` is absent because
   * `artifact_kind_code` is a term of one regime's vocabulary and means nothing
   * under another, so repointing would leave the recorded kind describing
   * something else.
   */
  async updateCustomsRecord(
    customsRecordId: string,
    input: Partial<{
      artifact_kind_code: string;
      procedure_reference: string | null;
      channel_code: string | null;
      lodged_by_role: ShipmentPartyRole | null;
      /** Sprint 62.25.0 AC8 — `draft`-writable only, as `channel_code` is. */
      service_role_code: string | null;
      evidence_attachment_ids: string[];
      notes: string | null;
    }>,
  ): Promise<{ data: CustomsRecord }> {
    return this.patch(`/logistics/customs-records/${encodeURIComponent(customsRecordId)}`, input);
  }

  /**
   * Lodging, amending and withdrawing each require **both** `logistics_customs:write`
   * and `logistics_customs:lodge` — their effect is a statement to an authority.
   * Closing is bookkeeping about a matter that has ended and carries `:write` alone.
   */
  async lodgeCustomsRecord(
    customsRecordId: string,
    input: {
      submission_id: string;
      lodged_at?: string;
      channel_code?: string | null;
      lodged_by_role?: ShipmentPartyRole | null;
    },
  ): Promise<{ data: CustomsRecord }> {
    return this.post(
      `/logistics/customs-records/${encodeURIComponent(customsRecordId)}/lodge`,
      input,
    );
  }

  async amendCustomsRecord(
    customsRecordId: string,
    input: { reason: string; submission_id?: string | null; amended_at?: string },
  ): Promise<{ data: CustomsRecord }> {
    return this.post(
      `/logistics/customs-records/${encodeURIComponent(customsRecordId)}/amend`,
      input,
    );
  }

  async withdrawCustomsRecord(
    customsRecordId: string,
    input: { reason: string | null },
  ): Promise<{ data: CustomsRecord }> {
    return this.post(
      `/logistics/customs-records/${encodeURIComponent(customsRecordId)}/withdraw`,
      input,
    );
  }

  async closeCustomsRecord(
    customsRecordId: string,
    input: { reason: string | null },
  ): Promise<{ data: CustomsRecord }> {
    return this.post(
      `/logistics/customs-records/${encodeURIComponent(customsRecordId)}/close`,
      input,
    );
  }

  /**
   * The clearance ledger: a GET and a POST, and **no PATCH, PUT or DELETE
   * anywhere** in the router. The absence is the guarantee — a clearance history
   * that could be edited is one an inspector cannot rely on.
   */
  async listClearanceEvents(
    customsRecordId: string,
    params?: { page?: number; page_size?: number; outcome?: ClearanceOutcome },
  ): Promise<PagedResponse<ClearanceEvent>> {
    return this.get(
      `/logistics/customs-records/${encodeURIComponent(customsRecordId)}/clearance-events${this.buildQuery(params)}`,
    );
  }

  async appendClearanceEvent(
    customsRecordId: string,
    input: {
      outcome: ClearanceOutcome;
      occurred_at: string;
      authority_statement?: string | null;
      statement_source?: string | null;
      evidence_attachment_ids?: string[];
    },
  ): Promise<{ data: ClearanceEvent }> {
    return this.post(
      `/logistics/customs-records/${encodeURIComponent(customsRecordId)}/clearance-events`,
      input,
    );
  }

  async listShipmentCorrespondence(params?: {
    page?: number;
    page_size?: number;
    authority_request_id?: string;
    shipment_id?: string;
    customs_record_id?: string;
  }): Promise<PagedResponse<ShipmentAuthorityCorrespondence>> {
    return this.get(`/logistics/authority-correspondence${this.buildQuery(params)}`);
  }

  /**
   * This **annotates** an authority request that already exists.
   *
   * `AuthorityRequest` has modelled authority correspondence since Sprint 44.5.0
   * — the eight-state lifecycle, the deadline, the channel, the checklist — so no
   * second register is minted here, `authority_request_id` is required and
   * existence-checked, and `(org_id, authority_request_id)` is unique because two
   * annotations of one question would be two answers to it.
   */
  async linkShipmentCorrespondence(input: {
    authority_request_id: string;
    shipment_ids?: string[];
    customs_record_ids?: string[];
    clearance_event_ids?: string[];
  }): Promise<{ data: ShipmentAuthorityCorrespondence }> {
    return this.post('/logistics/authority-correspondence', input);
  }

  /** No `authority_request_id`: the annotation is of one request, for its life. */
  async updateShipmentCorrespondence(
    correspondenceId: string,
    input: Partial<{
      shipment_ids: string[];
      customs_record_ids: string[];
      clearance_event_ids: string[];
    }>,
  ): Promise<{ data: ShipmentAuthorityCorrespondence }> {
    return this.patch(
      `/logistics/authority-correspondence/${encodeURIComponent(correspondenceId)}`,
      input,
    );
  }

  /** `is_international` is the literal string, for `processes_personal_data`'s reason. */
  async listParcels(params?: {
    page?: number;
    page_size?: number;
    status?: ParcelStatus;
    is_international?: 'true' | 'false';
    shipment_id?: string;
  }): Promise<PagedResponse<Parcel>> {
    return this.get(`/logistics/parcels${this.buildQuery(params)}`);
  }

  async getParcel(parcelId: string): Promise<{ data: Parcel }> {
    return this.get(`/logistics/parcels/${encodeURIComponent(parcelId)}`);
  }

  async createParcel(input: {
    parcel_code: string;
    shipment_id?: string | null;
    parties?: Array<Record<string, unknown>>;
    origin_country?: string | null;
    destination_country?: string | null;
    is_international: boolean;
    processing_activity_id?: string | null;
    service_agreement_id?: string | null;
  }): Promise<{ data: Parcel }> {
    return this.post('/logistics/parcels', input);
  }

  /** No `parcel_code` (half a unique index) and no `status` (`close` owns it). */
  async updateParcel(
    parcelId: string,
    input: Partial<{
      shipment_id: string | null;
      parties: Array<Record<string, unknown>>;
      origin_country: string | null;
      destination_country: string | null;
      is_international: boolean;
      processing_activity_id: string | null;
      service_agreement_id: string | null;
    }>,
  ): Promise<{ data: Parcel }> {
    return this.patch(`/logistics/parcels/${encodeURIComponent(parcelId)}`, input);
  }

  /** A closed parcel still accepts events: a complaint and a remedy follow closure. */
  async closeParcel(parcelId: string): Promise<{ data: Parcel }> {
    return this.post(`/logistics/parcels/${encodeURIComponent(parcelId)}/close`, {});
  }

  /**
   * **The one cursor-paginated read in this module**, and the one response
   * `extractPaginationMeta` must never be pointed at.
   *
   * It answers `{ data, next_cursor, has_more }` rather than
   * `{ data, total, page, page_size }`. The reason is the ledger: it is
   * append-only and read while it is being appended to, so an offset page
   * silently skips an event that lands below the reader — on the one register
   * whose purpose is that a parcel's history can be reconstructed. The cursor is
   * keyed on `sequence`, unique per parcel by index and monotonic by
   * construction, rather than on `_id`, which is a ULID whose random component is
   * re-rolled every call.
   *
   * `cursor` is **opaque**: it is produced by a previous page and never
   * constructed by a caller.
   */
  async listParcelEvents(
    parcelId: string,
    params?: { cursor?: string; page_size?: number; include_superseded?: 'true' | 'false' },
  ): Promise<CursorPage<ParcelEvent>> {
    return this.get(
      `/logistics/parcels/${encodeURIComponent(parcelId)}/events${this.buildQuery(params)}`,
    );
  }

  async appendParcelEvent(
    parcelId: string,
    input: {
      kind: ParcelEventKind;
      occurred_at: string;
      location?: string | null;
      actor_label?: string | null;
      detail?: string | null;
      evidence_attachment_ids?: string[];
    },
  ): Promise<{ data: ParcelEvent }> {
    return this.post(`/logistics/parcels/${encodeURIComponent(parcelId)}/events`, input);
  }

  /**
   * A correction is a **new event**, and it carries no `parcel_id`.
   *
   * The parcel is inherited from the event being corrected, which is a
   * correctness property rather than a convenience: a correction of an event about
   * parcel A must not be able to move that fact into parcel B's history — the
   * quiet re-attribution a loss investigation would never find. The body omits the
   * field, so a client sending one gets a 400 naming it rather than a silent no-op.
   */
  async appendParcelEventCorrection(
    eventId: string,
    input: {
      kind: ParcelEventKind;
      occurred_at: string;
      reason: string;
      location?: string | null;
      actor_label?: string | null;
      detail?: string | null;
      evidence_attachment_ids?: string[];
    },
  ): Promise<{ data: ParcelEvent }> {
    return this.post(`/logistics/parcel-events/${encodeURIComponent(eventId)}/corrections`, input);
  }

  async listDeliveryProofs(params?: {
    page?: number;
    page_size?: number;
    parcel_id?: string;
  }): Promise<PagedResponse<DeliveryProof>> {
    return this.get(`/logistics/delivery-proofs${this.buildQuery(params)}`);
  }

  /**
   * What has passed its own deletion date under the rule the organisation
   * recorded.
   *
   * `as_of` is a parameter rather than a hard-wired clock, because the question a
   * privacy lead asks before a sweep is *"what will be deletable at the end of the
   * quarter"*.
   */
  async listDeletableDeliveryProofs(params?: {
    page?: number;
    page_size?: number;
    as_of?: string;
  }): Promise<PagedResponse<DeliveryProof>> {
    return this.get(`/logistics/delivery-proofs/deletable${this.buildQuery(params)}`);
  }

  async getDeliveryProof(proofId: string): Promise<{ data: DeliveryProof }> {
    return this.get(`/logistics/delivery-proofs/${encodeURIComponent(proofId)}`);
  }

  /**
   * `retention` is **required**, and its `set_by` / `set_at` are absent by design.
   *
   * There is no proof of delivery without a deletion position — the courier
   * persona's first pain point made unrepresentable when unanswered — and the
   * attribution is stamped from the session and the clock, because a client that
   * submits one the server silently drops believes it took effect, and *"who
   * extended the retention on this material"* is precisely the question the silent
   * drop would make unanswerable.
   */
  async captureDeliveryProof(input: {
    parcel_id: string;
    parcel_event_id?: string | null;
    proof_kind: string;
    captured_at: string;
    received_by_label?: string | null;
    signature_trust_event_id?: string | null;
    evidence_attachment_ids?: string[];
    retention: { basis: string; delete_after: string };
  }): Promise<{ data: DeliveryProof }> {
    return this.post('/logistics/delivery-proofs', input);
  }

  async setDeliveryProofRetention(
    proofId: string,
    input: { basis: string; delete_after: string },
  ): Promise<{ data: DeliveryProof }> {
    return this.put(`/logistics/delivery-proofs/${encodeURIComponent(proofId)}/retention`, input);
  }

  async listParcelClaims(params?: {
    page?: number;
    page_size?: number;
    parcel_id?: string;
    status?: ClaimStatus;
    claimant_data_subject_profile_id?: string;
  }): Promise<PagedResponse<ParcelClaim>> {
    return this.get(`/logistics/claims${this.buildQuery(params)}`);
  }

  async getParcelClaim(claimId: string): Promise<{ data: ParcelClaim }> {
    return this.get(`/logistics/claims/${encodeURIComponent(claimId)}`);
  }

  /** No `status`: every claim is raised in the status the service creates it in. */
  async raiseParcelClaim(input: {
    parcel_id: string;
    /**
     * Sprint 64.128.0 — the postal regime the claim is handled under, optional.
     *
     * Absent from `updateParcelClaim` below, and deliberately: the claim's kind
     * and its outcome are read against this regime, so repointing it would leave
     * both describing something else.
     */
    regime_id?: string | null;
    claim_kind: string;
    detail: string;
    raised_at?: string;
    claimant_data_subject_profile_id?: string | null;
    claimant_label?: string | null;
    relied_on_event_ids?: string[];
    evidence_attachment_ids?: string[];
  }): Promise<{ data: ParcelClaim }> {
    return this.post('/logistics/claims', input);
  }

  /**
   * No `parcel_id`, no `raised_at`, and none of the three decision columns.
   *
   * `outcome_statement`, `decided_by` and `decided_at` belong to `decide`, and
   * their absence is what makes that gate unbypassable: a PATCH that could write
   * `decided_by` would let one person record another as the decider. `status` is
   * patchable to the working statuses only; the service refuses a decision status
   * here, which is the other half of the same gate.
   */
  async updateParcelClaim(
    claimId: string,
    input: Partial<{
      claim_kind: string;
      detail: string;
      claimant_data_subject_profile_id: string | null;
      claimant_label: string | null;
      relied_on_event_ids: string[];
      evidence_attachment_ids: string[];
      status: ClaimStatus;
    }>,
  ): Promise<{ data: ParcelClaim }> {
    return this.patch(`/logistics/claims/${encodeURIComponent(claimId)}`, input);
  }

  /**
   * No `decided_by`. The decider is the session.
   *
   * A decision attributed by the client is a decision attributable to somebody who
   * did not take it, and the service additionally refuses a decider equal to the
   * person who raised the claim — so holding `logistics_claim:decide` is necessary
   * and not sufficient. There is **no amount field**, here or anywhere in this
   * module: postal law prescribes compensation, the platform computes none.
   */
  async decideParcelClaim(
    claimId: string,
    input: { status: ClaimStatus; outcome_statement: string },
  ): Promise<{ data: ParcelClaim }> {
    return this.post(`/logistics/claims/${encodeURIComponent(claimId)}/decide`, input);
  }

  async listPostalAuthorisations(params?: {
    page?: number;
    page_size?: number;
    status?: AuthorisationStatus;
    authorisation_kind_code?: string;
  }): Promise<
    PagedResponse<PostalAuthorisation> & { determination_caveats: DeterminationCaveat[] }
  > {
    return this.get(`/logistics/authorisations${this.buildQuery(params)}`);
  }

  async getPostalAuthorisation(
    authorisationId: string,
  ): Promise<{ data: PostalAuthorisation; determination_caveats: DeterminationCaveat[] }> {
    return this.get(`/logistics/authorisations/${encodeURIComponent(authorisationId)}`);
  }

  async createPostalAuthorisation(input: {
    regime_id: string;
    authorisation_kind_code: string;
    scope_statement: string;
    authority_reference?: string | null;
    effective_from?: string | null;
    effective_until?: string | null;
    evidence_attachment_ids?: string[];
  }): Promise<{ data: PostalAuthorisation }> {
    return this.post('/logistics/authorisations', input);
  }

  /** No `regime_id`: `authorisation_kind_code` is a term of one regime's vocabulary. */
  async updatePostalAuthorisation(
    authorisationId: string,
    input: Partial<{
      authorisation_kind_code: string;
      scope_statement: string;
      authority_reference: string | null;
      effective_from: string | null;
      effective_until: string | null;
      evidence_attachment_ids: string[];
    }>,
  ): Promise<{ data: PostalAuthorisation }> {
    return this.patch(`/logistics/authorisations/${encodeURIComponent(authorisationId)}`, input);
  }

  /**
   * Filing carries **both** `logistics_authorisation:write` and
   * `logistics_authorisation:submit`, for `lodge`'s reason. The remaining three
   * record what happened to an authorisation the organisation already filed —
   * bookkeeping about the register rather than a fresh statement to the authority
   * — so each carries `:write` alone.
   */
  async submitPostalAuthorisation(
    authorisationId: string,
    input: { submission_id: string },
  ): Promise<{ data: PostalAuthorisation }> {
    return this.post(
      `/logistics/authorisations/${encodeURIComponent(authorisationId)}/submit`,
      input,
    );
  }

  /** `effective_from` is required: an in-force record with no start date cannot
   *  answer *"is our authorisation current?"* either way, and the wrong answer to
   *  that question is operating unlicensed. */
  async markPostalAuthorisationInForce(
    authorisationId: string,
    input: {
      effective_from: string;
      effective_until?: string | null;
      authority_reference?: string | null;
    },
  ): Promise<{ data: PostalAuthorisation }> {
    return this.post(
      `/logistics/authorisations/${encodeURIComponent(authorisationId)}/in-force`,
      input,
    );
  }

  async supersedePostalAuthorisation(
    authorisationId: string,
    input: { supersedes_authorisation_id: string },
  ): Promise<{ data: PostalAuthorisation }> {
    return this.post(
      `/logistics/authorisations/${encodeURIComponent(authorisationId)}/supersede`,
      input,
    );
  }

  async withdrawPostalAuthorisation(
    authorisationId: string,
  ): Promise<{ data: PostalAuthorisation }> {
    return this.post(
      `/logistics/authorisations/${encodeURIComponent(authorisationId)}/withdraw`,
      {},
    );
  }

  /**
   * Export one logistics register through `UnifiedExportService` (R-33).
   *
   * **One method for all ten kinds, not ten methods.** The kind is the route
   * segment and the backend loops its route table from `LOGISTICS_EXPORT_KINDS` in
   * `@vn-grc/shared`, so an eleventh kind is reachable here the moment it is
   * mounted — where ten hand-written methods would leave the eleventh with no
   * client and nobody would notice, which is exactly how 62.8.0's eight routes
   * came to ship with no user at all.
   *
   * **GET with a query string, not POST.** The ten routes are mounted with
   * `router.get` and read `req.query` under `.strict()`; the equivalent method
   * shipped as a POST once and **every export 404ed**. `export-surface-62.9.0.test.ts`
   * pins the verb by reading it out of `routes.ts` rather than asserting a
   * constant — a path without a method is half a contract.
   *
   * Each kind is gated server-side on its own register's read permission, so a
   * caller holding one register's grant cannot export another's.
   */
  async exportLogisticsRegister(
    kind: LogisticsExportKind,
    params: { format: 'csv' | 'xlsx' | 'pdf' | 'docx' } & Record<string, unknown>,
  ): Promise<{ data: LogisticsExportResult }> {
    return this.get(`/logistics/export/${encodeURIComponent(kind)}${this.buildQuery(params)}`);
  }
  // ── Sprint 62.57.0 Slice E (stream F): the processing-service certificate ──
  // register, mounted at `/api/v1/service-certificates` (work file §12.5.3).
  // Every response type is a shared read-side view (coordinator ruling (f),
  // §12.4.1); only the request bodies and the declaration list are typed here.

  /** `GET /service-certificates/availability` — D51; `{ data }` unwrapped. */
  async getServiceCertificateAvailability(): Promise<ServiceCertificateAvailability> {
    const res = await this.get<{ data: ServiceCertificateAvailability }>(
      '/service-certificates/availability',
    );
    return res.data;
  }
  /** `GET /service-certificates/declarations` — the installed declarations, or `[]` and why. */
  async listServiceCertificateDeclarations(): Promise<{
    data: ServiceCertificateDeclarationReadView[];
    absence: DeclarationAbsence | null;
  }> {
    const res = await this.get<{
      data?: ServiceCertificateDeclarationReadView[];
      absence?: DeclarationAbsence | null;
    }>('/service-certificates/declarations');
    return { data: Array.isArray(res?.data) ? res.data : [], absence: res?.absence ?? null };
  }
  /** `GET /service-certificates/certificates` (R-23). */
  async listServiceCertificates(params: {
    page: number;
    page_size: number;
    status?: string;
    certificate_kind?: string;
  }): Promise<PaginatedResponse<ServiceCertificateListRow>> {
    return normalisePage(
      await this.get(`/service-certificates/certificates${this.buildQuery(params)}`),
    );
  }
  /** `POST /service-certificates/certificates` — 201 `{ data }`; 409 `…_SCOPE_NOT_STATED`. */
  async createServiceCertificate(
    body: CreateServiceCertificateBody,
  ): Promise<ServiceCertificateDetailView> {
    const res = await this.post<{ data: ServiceCertificateDetailView }>(
      '/service-certificates/certificates',
      body,
    );
    return res.data;
  }
  /** `GET /service-certificates/certificates/:id`. */
  async getServiceCertificate(id: string): Promise<ServiceCertificateDetailView> {
    const res = await this.get<{ data: ServiceCertificateDetailView }>(
      `/service-certificates/certificates/${encodeURIComponent(id)}`,
    );
    return res.data;
  }
  /** `PATCH /service-certificates/certificates/:id` — a correction, with its required note. */
  async correctServiceCertificate(
    id: string,
    body: CorrectServiceCertificateBody,
  ): Promise<ServiceCertificateDetailView> {
    const res = await this.patch<{ data: ServiceCertificateDetailView }>(
      `/service-certificates/certificates/${encodeURIComponent(id)}`,
      body,
    );
    return res.data;
  }
  /**
   * `POST /service-certificates/certificates/:id/documents` — names a file of a
   * kind on the certificate. The file is uploaded first through the generic
   * attachments route (owner type `service_certificate_document`, owner id the
   * certificate), as `routes.ts` reads it.
   */
  async addServiceCertificateDocument(
    id: string,
    body: {
      document_kind: ServiceCertificateDocumentKind;
      file_id: string;
      expected_updated_at: string;
    },
  ): Promise<{ data: ServiceCertificateDocument }> {
    return this.post(
      `/service-certificates/certificates/${encodeURIComponent(id)}/documents`,
      body,
    );
  }
  /** `GET /service-certificates/certificates/:id/history` (R-23). */
  async listServiceCertificateHistory(
    id: string,
    params: { page: number; page_size: number },
  ): Promise<PaginatedResponse<ServiceCertificateHistoryEntry>> {
    return normalisePage(
      await this.get(
        `/service-certificates/certificates/${encodeURIComponent(id)}/history${this.buildQuery(params)}`,
      ),
    );
  }
  /** `POST /service-certificates/certificates/:id/revocations` (append-only, D61). */
  async recordServiceCertificateRevocation(
    id: string,
    body: RecordServiceCertificateRevocationBody,
  ): Promise<{ data: unknown }> {
    return this.post(
      `/service-certificates/certificates/${encodeURIComponent(id)}/revocations`,
      body,
    );
  }
  /** `POST /service-certificates/certificates/:id/returns` (D61). */
  async recordServiceCertificateReturn(
    id: string,
    body: RecordServiceCertificateReturnBody,
  ): Promise<{ data: unknown }> {
    return this.post(`/service-certificates/certificates/${encodeURIComponent(id)}/returns`, body);
  }
  /** `POST /service-certificates/certificates/:id/publications` (D61). */
  async recordServiceCertificatePublication(
    id: string,
    body: { seen_on: string; reference_text?: string; expected_updated_at: string },
  ): Promise<{ data: unknown }> {
    return this.post(
      `/service-certificates/certificates/${encodeURIComponent(id)}/publications`,
      body,
    );
  }
  /** `GET|POST /service-certificates/{certificates|applications}/:id/communications` (D57). */
  async listServiceCertificateCommunications(
    owner: ServiceCertificateCommunicationOwner,
    paging: { page: number; page_size: number },
  ): Promise<PaginatedResponse<ServiceCertificateCommunicationView>> {
    return normalisePage(
      await this.get(
        `/service-certificates/${owner.type}s/${encodeURIComponent(owner.id)}/communications${this.buildQuery(
          paging,
        )}`,
      ),
    );
  }
  async recordServiceCertificateCommunication(
    owner: ServiceCertificateCommunicationOwner,
    body: RecordServiceCertificateCommunicationBody,
  ): Promise<{ data: ServiceCertificateCommunicationView }> {
    return this.post(
      `/service-certificates/${owner.type}s/${encodeURIComponent(owner.id)}/communications`,
      body,
    );
  }
  /** `GET /service-certificates/certificates/:id/export?format=` — the export ref (R-33). */
  async exportServiceCertificate(
    id: string,
    format: 'pdf' | 'xlsx',
    locale?: 'en' | 'vi',
  ): Promise<{ data: AttachmentRef }> {
    return this.get(
      `/service-certificates/certificates/${encodeURIComponent(id)}/export${this.buildQuery({ format, locale })}`,
    );
  }
  /** `GET /service-certificates/applications` (R-23). */
  async listServiceCertificateApplications(params: {
    page: number;
    page_size: number;
    kind?: string;
    status?: string;
    certificate_id?: string;
    governing_text?: string;
  }): Promise<PaginatedResponse<ServiceCertificateApplicationListRow>> {
    return normalisePage(
      await this.get(`/service-certificates/applications${this.buildQuery(params)}`),
    );
  }
  /** `POST /service-certificates/applications` — 201 `{ data: detail }`. */
  async createServiceCertificateApplication(
    body: CreateServiceCertificateApplicationBody,
  ): Promise<ServiceCertificateApplicationDetailView> {
    const res = await this.post<{ data: ServiceCertificateApplicationDetailView }>(
      '/service-certificates/applications',
      body,
    );
    return res.data;
  }
  /** `GET /service-certificates/applications/:id`. */
  async getServiceCertificateApplication(
    id: string,
  ): Promise<ServiceCertificateApplicationDetailView> {
    const res = await this.get<{ data: ServiceCertificateApplicationDetailView }>(
      `/service-certificates/applications/${encodeURIComponent(id)}`,
    );
    return res.data;
  }
  /** `PATCH /service-certificates/applications/:id`. */
  async updateServiceCertificateApplication(
    id: string,
    body: UpdateServiceCertificateApplicationBody,
  ): Promise<ServiceCertificateApplicationDetailView> {
    const res = await this.patch<{ data: ServiceCertificateApplicationDetailView }>(
      `/service-certificates/applications/${encodeURIComponent(id)}`,
      body,
    );
    return res.data;
  }
  /** `PUT /service-certificates/applications/:id/parts/:partId` (D59). */
  async recordServiceCertificateApplicationPart(
    id: string,
    partId: string,
    body: RecordServiceCertificatePartBody,
  ): Promise<{ data: unknown }> {
    return this.put(
      `/service-certificates/applications/${encodeURIComponent(id)}/parts/${encodeURIComponent(partId)}`,
      body,
    );
  }
  /** `PUT /service-certificates/applications/:id/condition-links` (D59). */
  async recordServiceCertificateConditionLinks(
    id: string,
    body: {
      links: Array<{ link_id: string; filing_ids: string[] }>;
      expected_updated_at: string;
    },
  ): Promise<{ data: unknown }> {
    return this.put(
      `/service-certificates/applications/${encodeURIComponent(id)}/condition-links`,
      body,
    );
  }
  /** `POST /service-certificates/applications/:id/filing`. */
  async recordServiceCertificateApplicationFiling(
    id: string,
    body: RecordServiceCertificateFilingBody,
  ): Promise<{ data: unknown }> {
    return this.post(`/service-certificates/applications/${encodeURIComponent(id)}/filing`, body);
  }
  /** `POST /service-certificates/applications/:id/receipt-evidence` — re-stamps (D58, D54). */
  async recordServiceCertificateReceiptEvidence(
    id: string,
    body: RecordServiceCertificateReceiptBody,
  ): Promise<{ data: unknown }> {
    return this.post(
      `/service-certificates/applications/${encodeURIComponent(id)}/receipt-evidence`,
      body,
    );
  }
  /** `POST /service-certificates/applications/:id/supplement-requests` (D56). */
  async recordServiceCertificateSupplementRequest(
    id: string,
    body: RecordServiceCertificateSupplementRequestBody,
  ): Promise<{ data: unknown }> {
    return this.post(
      `/service-certificates/applications/${encodeURIComponent(id)}/supplement-requests`,
      body,
    );
  }
  /** `POST /service-certificates/applications/:id/supplements` (D56). */
  async recordServiceCertificateSupplementSent(
    id: string,
    body: { request_id: string; sent_on: string; note?: string; expected_updated_at: string },
  ): Promise<{ data: unknown }> {
    return this.post(
      `/service-certificates/applications/${encodeURIComponent(id)}/supplements`,
      body,
    );
  }
  /** `POST /service-certificates/applications/:id/evaluation` (D55). */
  async recordServiceCertificateEvaluation(
    id: string,
    body: RecordServiceCertificateEvaluationBody,
  ): Promise<{ data: unknown }> {
    return this.post(
      `/service-certificates/applications/${encodeURIComponent(id)}/evaluation`,
      body,
    );
  }
  /** `POST /service-certificates/applications/:id/decision` (D55). */
  async recordServiceCertificateDecision(
    id: string,
    body: RecordServiceCertificateDecisionBody,
  ): Promise<{
    data: ServiceCertificateApplicationDetailView & { issued_certificate_id?: string | null };
  }> {
    return this.post(`/service-certificates/applications/${encodeURIComponent(id)}/decision`, body);
  }
  /** `POST /service-certificates/applications/:id/withdraw`. */
  async withdrawServiceCertificateApplication(
    id: string,
    body: { withdrawn_on: string; reason?: string; expected_updated_at: string },
  ): Promise<{ data: unknown }> {
    return this.post(`/service-certificates/applications/${encodeURIComponent(id)}/withdraw`, body);
  }
  /** `GET /service-certificates/applications/:id/history` (R-23). */
  async listServiceCertificateApplicationHistory(
    id: string,
    params: { page: number; page_size: number },
  ): Promise<PaginatedResponse<ServiceCertificateHistoryEntry>> {
    return normalisePage(
      await this.get(
        `/service-certificates/applications/${encodeURIComponent(id)}/history${this.buildQuery(params)}`,
      ),
    );
  }
  /** `GET /service-certificates/applications/:id/export?format=` (R-33). */
  async exportServiceCertificateApplication(
    id: string,
    format: 'pdf' | 'xlsx',
    locale?: 'en' | 'vi',
  ): Promise<{ data: AttachmentRef }> {
    return this.get(
      `/service-certificates/applications/${encodeURIComponent(id)}/export${this.buildQuery({ format, locale })}`,
    );
  }
  /** `GET /service-certificates/export?format=` — the register (D66); 409 `…_NOT_OFFERED`. */
  async exportServiceCertificateRegister(
    format: 'pdf' | 'xlsx',
    locale?: 'en' | 'vi',
  ): Promise<{ data: AttachmentRef }> {
    return this.get(`/service-certificates/export${this.buildQuery({ format, locale })}`);
  }

  // ── Sprint 62.57.0 Slice E (§12.13.4): the controller-side vendor certificate record ──
  /** `GET /governance/vendors/:vendorId/service-certificate` (`gov:vendor:read`). */
  async getVendorServiceCertificate(vendorId: string): Promise<VendorServiceCertificateView> {
    const res = await this.get<{ data: VendorServiceCertificateView }>(
      `/governance/vendors/${encodeURIComponent(vendorId)}/service-certificate`,
    );
    return res.data;
  }
  /** `GET /governance/vendors/:vendorId/processing-service-statements` — newest revision first. */
  async listVendorProcessingServiceStatements(
    vendorId: string,
    params: { page: number; page_size: number },
  ): Promise<PaginatedResponse<VendorProcessingServiceStatement>> {
    return normalisePage(
      await this.get(
        `/governance/vendors/${encodeURIComponent(vendorId)}/processing-service-statements${this.buildQuery(params)}`,
      ),
    );
  }
  /** `POST /governance/vendors/:vendorId/processing-service-statements` (append-only revision). */
  async recordVendorProcessingServiceStatement(
    vendorId: string,
    body: RecordVendorProcessingServiceStatementBody,
  ): Promise<VendorServiceCertificateView> {
    const res = await this.post<{ data: VendorServiceCertificateView }>(
      `/governance/vendors/${encodeURIComponent(vendorId)}/processing-service-statements`,
      body,
    );
    return res.data;
  }
  /** `GET /governance/vendors/:vendorId/service-certificate-entries` — newest first. */
  async listVendorCertificateEntries(
    vendorId: string,
    params: { page: number; page_size: number },
  ): Promise<PaginatedResponse<VendorCertificateEntry>> {
    return normalisePage(
      await this.get(
        `/governance/vendors/${encodeURIComponent(vendorId)}/service-certificate-entries${this.buildQuery(params)}`,
      ),
    );
  }
  /** `POST /governance/vendors/:vendorId/service-certificate-entries` (append-only; a correction names `correction_of`). */
  async recordVendorCertificateEntry(
    vendorId: string,
    body: RecordVendorCertificateEntryBody,
  ): Promise<VendorServiceCertificateView> {
    const res = await this.post<{ data: VendorServiceCertificateView }>(
      `/governance/vendors/${encodeURIComponent(vendorId)}/service-certificate-entries`,
      body,
    );
    return res.data;
  }
}

// ── Sprint 62.57.0 Slice E (stream F) — request bodies of the certificate ──
// register and the vendor record (work file §12.5.3, §12.13.4). The read
// views are the shared ones (coordinator ruling (f)).

/** Which record a certificate communication is on (D57). */
export interface ServiceCertificateCommunicationOwner {
  type: 'certificate' | 'application';
  id: string;
}

/**
 * One installed declaration as `GET /service-certificates/declarations`
 * returns it (§12.5.3). Pack text throughout (R-46); the reservations are the
 * server's `LegalReservation` where it projects one.
 */
export interface ServiceCertificateDeclarationReadView {
  framework_code?: string;
  certificate_kind: string;
  label: BilingualText;
  issuing_authority?: ServiceCertificateIssuingAuthorityDeclaration | null;
  scope_text?: BilingualText | null;
  out_of_scope_items?: Array<
    ServiceCertificateOutOfScopeItem & { reservation?: LegalReservation | null }
  >;
  service_list: ServiceListItem[];
  procedures_by_window?: CertificateProceduresByWindow[];
  windows?: Array<{
    window_id: string;
    label: BilingualText;
    reservation?: LegalReservation | null;
  }>;
  revocation?: ServiceCertificateRevocationDeclaration | null;
  validity?: ServiceCertificateValidityDeclaration | null;
  boundary_statement?: BilingualText | null;
}

export interface CreateServiceCertificateBody {
  certificate_kind: string;
  certificate_number: string;
  signed_on: string;
  issuing_authority_as_named: string;
  covered_services: PersonalDataProcessingService[];
  end_date_stated: boolean;
  stated_end_date?: string;
  forms_held: ServiceCertificateFormsHeld;
  accountable_owner_user_id: string;
  notes?: string;
}

export type CorrectServiceCertificateBody = Partial<
  Omit<CreateServiceCertificateBody, 'certificate_kind'>
> & { correction_note: string; expected_updated_at: string };

export interface RecordServiceCertificateRevocationBody {
  decision_number: string;
  decision_date: string;
  ground_option_id: string;
  effective_on?: string;
  decision_received_on: string;
  communication_id?: string;
  voluntary_request?: { requested_on: string; note?: string; file_ids?: string[] };
  correction_of?: string;
  correction_note?: string;
  expected_updated_at: string;
}

export interface RecordServiceCertificateReturnBody {
  returned_on: string;
  note?: string;
  file_ids?: string[];
  correction_of?: string;
  correction_note?: string;
  expected_updated_at: string;
}

/** The Slice R body with the D57 kinds. */
export type RecordServiceCertificateCommunicationBody = Omit<
  RecordDossierAuthorityCommunicationRequest,
  'kind'
> & { kind: ServiceCertificateCommunicationKind };

export interface CreateServiceCertificateApplicationBody {
  certificate_kind: string;
  kind: ServiceCertificateProcedureKind;
  replaces_certificate_id?: string;
  services_applied_for?: PersonalDataProcessingService[];
  ground_option_ids?: string[];
  application_reference?: string;
  accountable_owner_user_id: string;
  notes?: string;
}

export interface UpdateServiceCertificateApplicationBody {
  application_reference?: string;
  services_applied_for?: PersonalDataProcessingService[];
  ground_option_ids?: string[];
  accountable_owner_user_id?: string;
  notes?: string;
  expected_updated_at: string;
}

export interface RecordServiceCertificatePartBody {
  state: ServiceCertificatePartState;
  form_instance_id?: string;
  owner_user_id?: string;
  internal_due_on?: string;
  note?: string;
  expected_updated_at: string;
}

export interface RecordServiceCertificateFilingBody {
  filed_on: string;
  channel_option_id: string;
  reviewed_by_user_id?: string;
  signed_by_name?: string;
  signed_on?: string;
  expected_updated_at: string;
}

export interface RecordServiceCertificateReceiptBody {
  event: ServiceCertificateReceiptEvent;
  received_on: string;
  postmarked_on?: string;
  communication_id?: string;
  correction_of?: string;
  correction_note?: string;
  expected_updated_at: string;
  /** D54 (L-13) — only with `complete_dossier_received`, on the `dossier_received` date. */
  no_deficiency_notice?: true;
}

export interface RecordServiceCertificateSupplementRequestBody {
  communication_id: string;
  items: Array<{ text: string; owner_user_id?: string }>;
  stated_period_text?: string;
  stated_deadline?: string;
  correction_of?: string;
  correction_note?: string;
  expected_updated_at: string;
}

export interface RecordServiceCertificateEvaluationBody {
  option_id: string;
  notice_date?: string;
  notice_received_on: string;
  communication_id: string;
  correction_of?: string;
  correction_note?: string;
  expected_updated_at: string;
}

export interface RecordServiceCertificateDecisionBody {
  outcome: ServiceCertificateDecisionOutcome;
  decision_number?: string;
  decision_date: string;
  decision_received_on: string;
  communication_id: string;
  stated_reasons?: string;
  certificate?: CertificateDecisionIssuedFacts;
  correction_of?: string;
  correction_note?: string;
  expected_updated_at: string;
}

export interface RecordVendorProcessingServiceStatementBody {
  certificate_kind: string;
  state: VendorProcessingServiceStatementState;
  activities: PersonalDataProcessingService[];
  note?: string;
  evidence_file_ids: string[];
  expected_revision: number;
}

export interface RecordVendorCertificateEntryBody {
  certificate_kind: string;
  stated_position: VendorCertificateStatedPosition;
  source: VendorCertificateStatementSource;
  stated_on: string;
  certificate_number_as_stated?: string;
  issuing_body_as_stated?: string;
  issue_date_as_stated?: string;
  services_covered_as_stated: PersonalDataProcessingService[];
  services_text_as_stated?: string;
  end_date_stated: boolean;
  stated_end_date?: string;
  note?: string;
  evidence_file_ids: string[];
  correction_of?: string;
  correction_note?: string;
}

// ── Sprint 62.7.0 — wire shapes for the three evidence modules ──────

/** The list envelope all three modules emit. `page_size`, never `per_page`. */
export interface PagedResponse<T> {
  data: T[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * Sprint 62.9.0 — the envelope of the one cursor-paginated read in the product.
 *
 * `GET /logistics/parcels/:parcelId/events` answers this and **not**
 * `PagedResponse`, so `extractPaginationMeta` must not be pointed at it. It is a
 * separate interface rather than an optional widening of `PagedResponse` for
 * exactly that reason: a caller who reaches for `total` on a parcel timeline
 * should get a compile error, not `undefined` rendered as a page count.
 *
 * The split is deliberate and narrow. Offset paging is wrong on a ledger that is
 * appended to while a reader is paging it — a row inserted between page 1 and page
 * 2 shifts every subsequent offset and silently duplicates or skips rows — and
 * this is the one register in the module whose purpose is that a parcel's history
 * can be reconstructed. Every other list here keeps the pinned offset envelope.
 */
export interface CursorPage<T> {
  data: T[];
  /** Opaque. Produced by the previous page, never constructed by a caller. */
  next_cursor: string | null;
  has_more: boolean;
}

/**
 * A regime as the registry resolves it — the instrument's own declaration plus
 * the provenance of the install it was read from.
 *
 * Provenance is flattened onto the entry rather than nested under `source`,
 * which is where 62.5.0 puts it. Both shapes exist in the product; this is the
 * one these three modules emit.
 */
export interface ResolvedEvidenceRegimeEntry {
  regime: EvidenceRegime;
  framework_code: string;
  pack_code: string;
  pack_version: string;
  framework_version: string;
}

/**
 * `GET /regimes` for all three modules.
 *
 * `data` is the **array**; the diagnostics ride on the envelope beside it. A
 * tenant whose only regime is malformed gets `data: []` — indistinguishable on
 * its own from having installed nothing — and the diagnostics are what turn
 * that into "this pack needs repairing" rather than "nothing is here".
 */
export interface EvidenceRegimeListResponse {
  data: ResolvedEvidenceRegimeEntry[];
  diagnostics: EvidenceRegimeDiagnostic[];
}

/**
 * What a caller actually receives for a protected handle.
 *
 * A service never returns a `ProtectedReference`; it returns this. The kind,
 * the custodian and the restricted flag are never redacted, because an operator
 * with no grant still needs to tell "we hold nothing" from "you may not see
 * what we hold" — different compliance positions.
 */
export type EkycVerificationView = Omit<EkycVerification, 'protected_references'> & {
  protected_references: ProtectedReferenceView[];
};

export type PaymentDataSecurityRecordView = Omit<
  PaymentDataSecurityRecord,
  'protected_references'
> & {
  protected_references: ProtectedReferenceView[];
};

/**
 * A protected reference **as a client may send it**: three fields.
 *
 * `restricted` is absent and rejected — it is the regime's statement about the
 * kind, copied at write time, and a client able to send it could turn off the
 * redaction its own record needs. `external_reference` is a handle in someone
 * else's custody and never a value or a fragment of one.
 */
export interface ProtectedReferenceInput {
  kind_code: string;
  custodian: string;
  external_reference: string;
}

export interface CreatePublicDatasetInput {
  regime_id: string;
  dataset_code: string;
  name: string;
  description?: string | null;
  registry_item_ids?: string[];
  data_flow_ids?: string[];
  system_ids?: string[];
  steward_user_id?: string | null;
  restricted_artifact_id?: string | null;
}

export interface UpdatePublicDatasetInput {
  name?: string;
  description?: string | null;
  registry_item_ids?: string[];
  data_flow_ids?: string[];
  system_ids?: string[];
  steward_user_id?: string | null;
  restricted_artifact_id?: string | null;
  status?: PublicDatasetStatus;
}

export interface CreateSharingArrangementInput {
  dataset_id: string;
  /** Non-nullable: the dictionary is a precondition of sharing, structurally. */
  dictionary_version_id: string;
  counterparty_name: string;
  counterparty_reference?: string | null;
  basis_code: string;
  channel_code: string;
  scope_description: string;
  review_due_at?: string | null;
  agreement?: AttachmentRef | null;
}

export interface CreateEkycVerificationInput {
  payment_service_id: string;
  regime_id: string;
  customer_reference: string;
  verification_method_code: string;
  outcome: EkycOutcome;
  outcome_detail?: string | null;
  performed_at: string;
  performed_by_label: string;
  identity_profile_id?: string | null;
  /** `null` not assessed, `false` not suspected, `true` suspected (note required). */
  impersonation_suspected?: boolean | null;
  impersonation_note?: string | null;
  protected_references?: ProtectedReferenceInput[];
  lawful_basis: string;
  /** Bound at capture — required by the schema and by the service. */
  retention_rule: { duty_code: string; retain_until: string; rationale: string };
}

/** The four conditions a reconciliation run detects. The regime names each one. */
export type ReconciliationCondition =
  | 'amount_disagreement'
  | 'currency_disagreement'
  | 'document_absent'
  | 'transaction_absent';

/**
 * One regime's statement of what the organisation's records are and are not.
 *
 * `caveat` and `unavailable_reason` are never both null and never both set: a
 * regime that no longer resolves carries a reason naming the framework to
 * repair rather than a silently empty statement.
 */
/**
 * What one manufacturing export produced, as the client reads it.
 *
 * Declared here rather than imported from the backend service, matching this file's
 * existing convention for response envelopes (`DeterminationCaveat` below is the same
 * shape of decision). The **kind union** is imported from `@vn-grc/shared` rather than
 * restated, because that is the part whose drift has a consequence: a ninth register
 * added to the union appears here with no edit, and Gate E12.5 pass 2 found the eight
 * existing ones had shipped with no client at all.
 */
export interface ManufacturingExportResult {
  kind: ManufacturingExportKind;
  format: 'csv' | 'xlsx' | 'pdf' | 'docx';
  row_count: number;
  determination_caveats: DeterminationCaveat[];
  install_modes: Array<{
    framework_code: string;
    install_mode: 'direct' | 'reference';
    is_operational: boolean;
    reason: string;
    statement: string;
  }>;
  attachment: { file_id: string; filename: string; mime_type: string; size: number };
}

export interface DeterminationCaveat {
  regime_id: string;
  framework_code: string;
  caveat: Record<string, string> | null;
  unavailable_reason: string | null;
}

/**
 * Sprint 62.9.0 — what one logistics export produced, as the client reads it.
 *
 * The **kind union** is imported from `@vn-grc/shared` rather than restated,
 * because that is the part whose drift has a consequence: an eleventh register
 * added to the union appears here with no edit.
 *
 * `lineage_unresolved_reason` is the field 62.7.0's settlement makes necessary and
 * the one a careless caller drops. Four of the ten kinds reach a regime only
 * through a parent record, so a file can legitimately carry no caveat *and* a
 * reason naming the record the organisation must hold for the pack's own sentence
 * to appear. "No caveat" and "the caveat could not be found" must never render
 * identically.
 */
export interface LogisticsExportResult {
  kind: LogisticsExportKind;
  format: 'csv' | 'xlsx' | 'pdf' | 'docx';
  row_count: number;
  determination_caveats: DeterminationCaveat[];
  lineage_unresolved_reason: string | null;
  install_modes: Array<{
    framework_code: string;
    install_mode: 'direct' | 'reference';
    is_operational: boolean;
    reason: string;
    statement: string;
  }>;
  attachment: { file_id: string; filename: string; mime_type: string; size: number };
}

// ── Sprint 58.4.0 — PDPL frontend payload shapes ────────────────────
// Wire-format types the frontend uses for the PDPL endpoints. They mirror
// the backend shared types but adapt binary fields to base64 strings for
// JSON transmission.

export interface PdplEvidencePackAssembleInput {
  dossier_id: string;
  org_slug?: string;
  pack_type: 'dpia' | 'otia';
  parts: {
    cover_letter_pdf_base64: string;
    authority_form_pdf_base64: string;
    supporting_evidence: PdplPartFilePayload[];
    safeguards: PdplPartFilePayload[];
    prior_correspondence?: PdplPartFilePayload[];
    signature_evidence: PdplPartFilePayload[];
  };
  ai_assistance?: {
    ai_run_id: string;
    ai_workflow_template: string;
    ai_evidence_status: 'draft_ai_generated';
  };
}

export interface PdplPartFilePayload {
  filename: string;
  mime_type: string;
  base64: string;
}

export interface PdplEmailDraftComposeInputPayload {
  org_slug?: string;
  dossier_id: string;
  pack_sha256: string;
  pack_type: 'dpia' | 'otia';
  locale: 'en' | 'vi';
  overrides?: {
    subject?: string;
    body?: string;
    cover_letter_body_paragraph?: string;
    cover_letter_additional_notes?: string;
  };
}

export interface PdplEmailDraftEditChanges {
  subject?: string;
  body?: string;
  cover_letter_body_paragraph?: string | null;
  cover_letter_additional_notes?: string | null;
}

export interface PdplSentProofUploadPayload {
  proof_file: { filename: string; mime_type: string; base64: string };
  draft_id?: string | null;
  draft_version_at_send?: number | null;
  envelope: {
    from: string;
    to: string;
    subject_at_send: string;
    sent_at: string;
    attachment_sha256_at_send: string;
  };
}

// ── Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send wire payloads ──

export interface PdplEmailSendRequestPayload {
  pack_id: string;
  draft_id: string;
  provider_config_id: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  /** send_id of an earlier failed send this transmission is a retry of.
   *  When supplied, the backend verifies the referenced record belongs to
   *  the same org + pack + draft and had `outcome.status === 'failed'`
   *  before chaining the retry via `PdplEmailSendRecord.retry_of`. */
  retry_of_send_id?: string;
}

export interface PdplAuthorityResponseRequestPayload {
  /*
   * The shared type, not a fifth transcription of its members — Sprint 65.4.0.
   * This file's own neighbours record what a second spelling on the wire costs:
   * `TaskSourceModule` shipped as a hand-copied duplicate here, and adding a
   * member type-checked everywhere while the API went on refusing it, silently.
   */
  classification: PdplAuthorityResponseClassification;
  received_at?: string | null;
  free_text?: string | null;
  evidence_file?: { filename: string; mime_type: string; base64: string };
  /** Only used on PATCH (reclassification). */
  reason?: string | null;
}

export interface PdplHardCopyFollowupRequestPayload {
  posted_at: string;
  courier: string;
  tracking_number: string;
  recipient: string;
  receipt_file?: { filename: string; mime_type: string; base64: string };
}

export interface PdplHardCopyFollowupUpdatePayload {
  delivered_at?: string | null;
  delivery_notes?: string | null;
  receipt_file?: { filename: string; mime_type: string; base64: string };
}

// ── Sprint 47.4.6: response shapes for the new IAT integrity routes ──
export interface IntegrityRunRow {
  run_id: string;
  org_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  period_start: string;
  period_end: string;
  modules: string[] | null;
  total_links: number;
  processed_links: number;
  summary: {
    total: number;
    verified: number;
    drifted: number;
    hash_mismatch: number;
    source_missing: number;
    merkle_mismatch: number;
    not_found: number;
    chain_unreachable: number;
    service_unreachable: number;
    pending: number;
  };
  started_at: string;
  finished_at: string | null;
  error_message: string | null;
  triggered_by: string;
}

export interface IntegrityIssueRow {
  run_id: string;
  org_id: string;
  proof_link_id: string;
  proof_reference_id: string | null;
  module: string;
  event_type: string;
  proof_mode: 'direct' | 'batch';
  verdict: string;
  expected_hash: string;
  recomputed_hash: string;
  raised_at: string;
}
/** Singleton API client instance. */
export const apiClient = new ApiClient();
